// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { deModuleTips } from './de-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
import { enVehicleMonitoring, enRecordLinks } from './en-extras.js';
export const de = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Melden Sie sich bei Ihrem Konto an',
        email: 'E-Mail',
        password: 'Passwort',
        sign_in: 'Anmelden',
        signing_in: 'Anmeldung läuft...',
        mfa_code: 'MFA-Code',
        mfa_placeholder: '6-stelligen Code eingeben',
        mfa_instruction: 'Geben Sie den 6-stelligen Code aus Ihrer Authentifizierungs-App ein.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Bestätigen',
        verifying: 'Wird bestätigt...',
        forgot_password: 'Passwort vergessen?',
        account_locked: 'Das Konto ist gesperrt. Bitte versuchen Sie es später erneut.',
        account_deactivated: 'Das Konto wurde deaktiviert. Kontaktieren Sie Ihren Administrator.',
        invalid_mfa: 'Ungültiger MFA-Code. Bitte versuchen Sie es erneut.',
        invalid_credentials: 'Ungültige E-Mail oder ungültiges Passwort.',
        login_failed: 'Anmeldung fehlgeschlagen',
        session_limit_reached: 'Ihr Konto hat das Limit aktiver Sitzungen erreicht. Melden Sie sich auf einem anderen Gerät ab oder bitten Sie Ihren Administrator, eine bestehende Sitzung zu widerrufen, und versuchen Sie es erneut.',
        session_expired: 'Ihre Sitzung ist abgelaufen. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Passwort vergessen',
            subtitle: 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.',
            send: 'Link zum Zurücksetzen senden',
            sending: 'Wird gesendet...',
            success: 'Falls diese E-Mail-Adresse existiert, wurde ein Link zum Zurücksetzen des Passworts gesendet.',
            back_to_login: 'Zurück zur Anmeldung',
        },
        reset_password: {
            title: 'Passwort zurücksetzen',
            subtitle: 'Geben Sie unten Ihr neues Passwort ein.',
            new_password: 'Neues Passwort',
            confirm_password: 'Passwort bestätigen',
            reset: 'Passwort zurücksetzen',
            resetting: 'Wird zurückgesetzt...',
            success: 'Ihr Passwort wurde erfolgreich zurückgesetzt.',
            go_to_login: 'Zur Anmeldung',
            password_mismatch: 'Die Passwörter stimmen nicht überein.',
            invalid_token: 'Ungültiger oder fehlender Zurücksetzungstoken. Bitte fordern Sie einen neuen Link zum Zurücksetzen des Passworts an.',
            requirements_title: 'Passwortanforderungen:',
            password_requirements: 'Das Passwort muss die folgenden Anforderungen erfüllen:',
            rules: {
                min_length: 'Mindestens 12 Zeichen',
                uppercase: 'Mindestens ein Großbuchstabe',
                lowercase: 'Mindestens ein Kleinbuchstabe',
                digit: 'Mindestens eine Ziffer',
                special: 'Mindestens ein Sonderzeichen',
            },
        },
        verify_email: {
            title: 'E-Mail-Verifizierung',
            verifying: 'Ihre E-Mail-Adresse wird verifiziert...',
            success: 'Ihre E-Mail-Adresse wurde erfolgreich verifiziert.',
            invalid_token: 'Ungültiger oder fehlender Verifizierungstoken.',
            failed: 'E-Mail-Verifizierung fehlgeschlagen. Der Token ist möglicherweise ungültig oder abgelaufen.',
            go_to_login: 'Zur Anmeldung',
        },
        offline: {
            title: 'Sie sind offline',
            message: 'Es scheint, als hätten Sie Ihre Internetverbindung verloren. Bitte überprüfen Sie Ihre Netzwerkverbindung und versuchen Sie es erneut.',
        },
    },
    intake: {
        title: 'Auskunftsersuchen',
        subtitle: 'Reichen Sie ein Auskunftsersuchen bei unserer Organisation ein',
        fields: {
            full_name: 'Vollständiger Name',
            email: 'E-Mail-Adresse',
            phone: 'Telefonnummer (optional)',
            request_type: 'Art des Ersuchens',
            description: 'Beschreiben Sie Ihr Ersuchen',
            preferred_language: 'Bevorzugte Sprache',
            consent: 'Ich willige in die Verarbeitung dieses Ersuchens ein',
        },
        submit: 'Ersuchen einreichen',
        validation: {
            name_required: 'Der vollständige Name ist erforderlich',
            email_required: 'Eine gültige E-Mail-Adresse ist erforderlich',
            type_required: 'Bitte wählen Sie eine Art des Ersuchens',
            description_required: 'Bitte beschreiben Sie Ihr Ersuchen',
            consent_required: 'Sie müssen der Verarbeitung zustimmen, um fortzufahren',
        },
        error_title: 'Fehler',
        config_load_error: 'Konfiguration konnte nicht geladen werden. Bitte überprüfen Sie die URL.',
        organization_label: 'Organisation',
        customer_reference: 'Kundenreferenznummer',
        country: 'Land / Rechtsgebiet',
        preferred_channel: 'Bevorzugter Antwortkanal',
        channels: {
            email: 'E-Mail',
            postal: 'Post',
            phone: 'Telefon',
            portal: 'Portalbereich',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Ersuchen eingegangen',
            message: 'Ihr Ersuchen wurde erfolgreich erfasst. Ihre Referenznummer lautet:',
            next_steps: 'Wir werden Ihr Ersuchen so schnell wie möglich gemäß den geltenden Gesetzen bearbeiten. Die voraussichtliche Bearbeitungszeit beträgt 30 Werktage.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Auskunft (DSAR)',
        deletion: 'Löschung',
        correction: 'Berichtigung / Aktualisierung',
        objection: 'Widerspruch',
        portability: 'Datenübertragbarkeit',
        restriction: 'Einschränkung der Verarbeitung',
        consent_withdrawal: 'Widerruf der Einwilligung',
        protective_measures: 'Maßnahmen und Lösungen zum Schutz personenbezogener Daten anfordern',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Neu',
        in_review: 'In Prüfung',
        waiting_on_requester: 'Warten auf Antragsteller',
        waiting_on_internal_data: 'Warten auf interne Daten',
        approved: 'Genehmigt',
        closed: 'Geschlossen',
        rejected: 'Abgelehnt',
    },
    admin: {
        nav: {
            rights_requests: 'Auskunftsersuchen',
            templates: 'Vorlagen',
            response_templates: 'Antwortvorlagen',
            data_mapping: 'Datenverzeichnis',
            governance: 'Steuerung',
            consent: 'Einwilligung',
            assessments: 'Bewertungen',
            classification: 'Klassifizierung',
            monitoring: 'Überwachung',
            incidents: 'Vorfälle',
            onboarding: 'Einführung',
            connectors: 'Konnektoren',
            vendor_risk: 'Dienstleisterrisiko',
            deletion_orchestration: 'Löschorchestrierung',
            discovery: 'Datenerkennung',
            incident_ops: 'Vorfallmanagement',
            program_governance: 'Programmsteuerung',
            access_accountability: 'Zugriffskontrolle',
            consent_properties: 'Einwilligungseigenschaften',
            localization: 'Lokalisierung',
            regulatory: 'Regulatorisch',
            partner_workspace: 'Partner-Arbeitsbereich',
            audit_trail: 'Prüfpfad',
            forseti: 'Forseti',
            settings: 'Einstellungen',
            help_center: 'Hilfezentrum',
            dashboard: 'Übersicht',
            platform: 'Plattform',
            integrations: 'Integrationen',
            cmp_dsar_sync: 'CMP-DSAR-Synchronisierung',
            cmp_integration: 'CMP-Integration',
            purpose_mapping: 'Zweckzuordnung',
            webhook_config: 'Webhook-Konfiguration',
            bulk_sync: 'Massenabgleich',
            privacy_scanner: 'Datenschutz-Scanner',
            privacy_monitoring: 'Datenschutzüberwachung',
            real_time_scanner: 'Echtzeit-Scanner',
            compliance_forms: 'Compliance-Formulare',
            forms_dashboard: 'Formularübersicht',
            form_templates: 'Vorlagen',
            form_catalog: 'Formularkatalog',
            pack_catalog: 'Paketkatalog',
            org_groups: 'Organisationsgruppen',
            consolidation: 'Konsolidierung',
            organizations: 'Organisationen',
            org_list: 'Alle Organisationen',
            intake_configs: 'Eingangskonfigurationen',
            task_management: 'Aufgabenverwaltung',
            unified_audit: 'Gesamtprüfung',
            evidence_packs: 'Nachweispakete',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Prüfungszeiträume',
            regulatory_updates: 'Regulatorische Aktualisierungen',
            departments: 'Abteilungen',
        },
        sidebar_category_data_privacy_rights: 'Datenschutz & Rechte',
        sidebar_category_vendor_third_party: 'Lieferanten- & Drittanbieter-Management',
        sidebar_category_risk_assessment: 'Risiko & Bewertung',
        sidebar_category_operations_automation: 'Betrieb & Automatisierung',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Aufgaben- & Workflow-Management',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP & Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'Verarbeitungsverzeichnis',
            data_mapping_classification: 'Klassifizierung',
            data_mapping_governance: 'Regierungsführung',
            access_live_monitoring: 'Echtzeit-Überwachung',
            access_reviews_investigations: 'Prüfungen & Untersuchungen',
            access_evidence_policies: 'Nachweise & Richtlinien',
            classification_taxonomies: 'Taxonomien & Labels',
            classification_workflow: 'Arbeitsablauf',
            classification_insights: 'Auswertungen',
            connectors_runtime: 'Laufzeit',
            connectors_due_diligence: 'Sorgfaltsprüfung',
            connectors_coverage: 'Abdeckung',
            cp_definitions: 'Definitionen',
            cp_lifecycle: 'Lebenszyklus',
            cp_quality: 'Qualität & Nachweise',
            deletion_execution: 'Ausführung',
            deletion_vendors: 'Dienstleister',
            deletion_evidence: 'Nachweise & Integration',
            discovery_rules: 'Regeln & Vorlagen',
            discovery_scanning: 'Scannen',
            discovery_review: 'Prüf-Workflow',
            discovery_insights: 'Auswertungen',
            governance_oversight: 'Aufsicht',
            governance_reporting: 'Berichtswesen',
            governance_vendors: 'Dienstleister & Drittlandtransfers',
            governance_lifecycle: 'Lebenszyklus',
            incident_ops_dashboards: 'Dashboards',
            incident_ops_sla: 'SLA-Konfiguration',
            incident_ops_playbooks: 'Spielbücher',
            incident_ops_vendor_regulator: 'Dienstleister & Aufsichtsbehörde',
            incident_ops_evidence: 'Nachweise & Prüfung',
            incidents_workload: 'Arbeitslast',
            incidents_regulatory: 'Regulatorisch',
            incidents_reporting: 'Berichtswesen',
            localization_content: 'Inhalte',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Beobachtbarkeit',
            monitoring_metrics: 'Kennzahlen',
            monitoring_programs: 'Programme',
            monitoring_tasks: 'Aufgaben',
            monitoring_controls: 'Kontrollen',
            org_entities: 'Entitäten',
            org_config: 'Konfiguration',
            org_frameworks: 'Rahmenwerke & Rollup',
            partner_overview: 'Übersicht',
            partner_partners: 'Partner',
            partner_delivery: 'Lieferung',
            partner_support: 'Unterstützung',
            platform_identity: 'Identität & Rollen',
            platform_access_control: 'Zugriffssteuerung',
            platform_auth_session: 'Authentifizierung & Sitzung',
            platform_access_reviews: 'Zugriffsprüfungen',
            platform_files: 'Dateien',
            platform_notifications: 'Benachrichtigungen',
            platform_module_config: 'Modulkonfiguration',
            platform_ops: 'Betrieb & Logs',
            pg_health: 'Programmzustand',
            pg_maturity: 'Reifegrad',
            pg_audit: 'Audit & Nachweise',
            pg_training: 'Schulung',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Pflichten',
            pg_documents: 'Dokumente',
            vr_overview: 'Übersicht',
            vr_onboarding: 'Onboarding',
            vr_assessment: 'Bewertung',
            vr_oversight: 'Aufsicht',
        },
        tickets: {
            title: 'Auskunftsersuchen',
            create: 'Ersuchen erstellen',
            filters: 'Filtern',
            search_placeholder: 'Search tickets...',
            export_csv: 'Exportieren CSV',
            exporting: 'Exportieren...',
            bulk_export_failed: 'Der Massenexport ist fehlgeschlagen',
            bulk_export: {
                label: 'Exportieren',
                format: {
                    pdf: 'PDF Zusammenfassung',
                    zip: 'ZIP (Beweispakete pro Fall)',
                    csv: 'CSV',
                },
                warning_over_50: 'Dadurch werden etwa {count} Kartonpackungen generiert – dies kann mehrere Minuten dauern. Weitermachen?',
                blocked_over_500: 'Schränken Sie den Filter auf 500 oder weniger Fälle ein, bevor Sie ZIP exportieren (aktuell: {count}).',
                success: 'Massenexport im Dateibrowser gespeichert ({count} Fälle)',
                failed: 'Der Massenexport ist fehlgeschlagen',
                continue: 'Weiter',
                cancel: 'Abbrechen',
            },
            no_results: 'Keine Ersuchen gefunden',
            ticket_id: 'Ticket-ID',
            request_type: 'Antragsart',
            requester: 'Antragsteller',
            source: 'Quelle',
            all_sources: 'Alle Quellen',
            assigned_to: 'Zugewiesen an',
            created: 'Erstellt',
            load_error: 'Fehler beim Laden der Fälle. Bitte versuchen Sie es erneut.',
            case_creation_failed: 'Fallerstellung fehlgeschlagen',
            business_unit: 'Geschäftseinheit',
            reference: 'Referenz',
            sla: {
                title: 'SLA-Status',
                on_track: '{days} Tage verbleibend',
                approaching: 'Frist nähert sich',
                overdue: '{days} Tage überfällig',
                ack_overdue: '{hours}h über Bestätigungsfrist',
                ack_warning: 'Bestätigung bald fällig',
                closed: 'Geschlossen',
                completed_in: 'Abgeschlossen in {days} Tagen',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Antwortvorlagen',
            new: 'Neue Vorlage',
            edit: 'Vorlage bearbeiten',
            all_types: 'Alle Typen',
            all_locales: 'Alle Sprachen',
            type_access: 'Auskunft/DSAR',
            type_deletion: 'Löschung',
            type_correction: 'Berichtigung',
            type_objection: 'Widerspruch',
            type_portability: 'Datenübertragbarkeit',
            type_other: 'Sonstiges',
            type_restriction: 'Einschränkung',
            type_all: 'Alle',
            locale_en: 'Englisch',
            locale_vi: 'Vietnamesisch',
            locale_all: 'Beide',
            name: 'Bezeichnung',
            request_type: 'Antragsart',
            locale: 'Sprache',
            subject: 'Betreff',
            body: 'Text',
            name_placeholder: 'Vorlagenname...',
            subject_placeholder: 'E-Mail-/Briefbetreff...',
            body_placeholder: 'Vorlagentext. Verwenden Sie {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} als Platzhalter.',
            available_placeholders: 'Verfügbare Platzhalter:',
            create_template: 'Vorlage erstellen',
            preview: 'Vorschau',
            close_preview: 'Vorschau schließen',
            default_badge: 'Standard',
            no_templates: 'Keine Vorlagen gefunden',
            confirm_delete: 'Diese Vorlage löschen?',
            load_error: 'Fehler beim Laden der Vorlagen. Bitte versuchen Sie es erneut.',
            create_error: 'Fehler beim Erstellen der Vorlage',
            update_error: 'Fehler beim Aktualisieren der Vorlage',
            delete_error: 'Fehler beim Löschen der Vorlage',
            preview_error: 'Fehler bei der Vorschau der Vorlage',
            response_templates_badge: 'Antwortvorlagen',
            browse_form_templates: 'Compliance-Formularvorlagen durchsuchen',
            templates_loaded: 'Vorlagen geladen',
            sample_organization: 'Beispielorganisation',
        },
        detail: {
            requester_info: 'Antragstellerinformationen',
            status: 'Zustand',
            assignment: 'Zuweisung',
            notes: 'Notizen',
            audit_trail: 'Prüfpfad',
            add_note: 'Notiz hinzufügen',
            case_not_found: 'Fall nicht gefunden',
            load_error: 'Fehler beim Laden des Falls. Bitte versuchen Sie es erneut.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Fehler beim Hinzufügen der Notiz. Bitte versuchen Sie es erneut.',
            submitted_from: 'Eingereicht von',
            via_cmp: 'über AesirX CMP',
            cmp_ticket: 'CMP-Ticket:',
            select_template: 'Vorlage auswählen',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Einzelheiten',
                evidence: 'Nachweise',
                communications: 'Kommunikation',
                escalations: 'Eskalationen',
                audit_trail: 'Prüfpfad',
                fulfillment: 'Erfüllung',
                response: 'Antwort',
                extension: 'Verlängerung',
            },
            requester: {
                name: 'Bezeichnung',
                email: 'E-Mail',
                phone: 'Telefon',
                language: 'Sprache',
                source: 'Quelle',
                customer_ref: 'Kundenreferenz',
                country: 'Land',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Bevorzugter Kanal',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Im Plan',
                due_soon: 'Bald fällig',
                overdue: 'Überfällig',
                days_remaining: 'Tage verbleibend',
                due_date: 'Fälligkeitsdatum',
                breached: 'Verletzt',
                paused: 'Pausiert',
                total_paused_days: 'Pausierte Tage gesamt',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Verstrichene Tage',
                progress: 'Fortschritt',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identitätsprüfung',
                verified: 'Verifiziert',
                not_verified: 'Nicht verifiziert',
                method: 'Methode',
                notes: 'Notizen',
                verified_by: 'Verifiziert von',
                verified_at: 'Verifiziert am',
                warning: 'Identität noch nicht verifiziert.',
                verify_button: 'Identität verifizieren',
                select_method: '-- Auswählen --',
                methods: {
                    email_confirmation: 'E-Mail-Bestätigung',
                    id_document: 'Ausweisdokument',
                    phone_call: 'Telefonanruf',
                    in_person: 'Persönlich',
                    other: 'Sonstiges',
                },
            },
            closure: {
                reason: 'Abschlussgrund',
                summary: 'Abschlusszusammenfassung',
                summary_placeholder: 'Zusammenfassung der Bearbeitung des Ersuchens...',
                reason_required: 'Abschlusszusammenfassung und Grund sind erforderlich',
                response_not_sent: 'Eine formelle Antwort muss an die betroffene Person gesendet werden, bevor der Fall geschlossen werden kann',
                reasons: {
                    fulfilled: 'Erfüllt',
                    partially_fulfilled: 'Teilweise erfüllt',
                    unfounded: 'Unbegründet',
                    excessive: 'Übermäßig',
                    withdrawn: 'Zurückgezogen',
                    duplicate: 'Duplikat',
                    identity_unverified: 'Identität nicht verifiziert',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Exportieren',
            export_pdf: 'PDF exportieren',
            export_zip: 'ZIP exportieren',
            case_file_pdf: 'Exportieren PDF (Falldatei)',
            evidence_pack_zip: 'ZIP exportieren (Beweispaket)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Als verletzungsverursachend kennzeichnen',
                modal: {
                    title: 'Kennzeichnen Sie den Fall als verletzungsverursachend',
                    type_label: 'Art des Verstoßes',
                    type: {
                        misdirected_response: 'Fehlgeleitete Antwort',
                        unauthorised_disclosure: 'Unbefugte Offenlegung',
                        identity_mismatch: 'Identitätskonflikt',
                        other: 'Andere',
                    },
                    description: 'Beschreibung (optional)',
                    description_placeholder: 'Was ist passiert? (maximal 1000 Zeichen)',
                    confirm: 'Markieren und die Melduhr starten',
                    cancel: 'Abbrechen',
                    flagging: 'Melden...',
                },
                banner: {
                    title: 'Verstoß gemeldet: {type}',
                    detected_at: 'Erkannt am {timestamp}',
                    filing_deadline: 'Meldefrist: in {hours} Stunden',
                    overdue: 'Meldefrist: ÜBERFÄLLIG',
                },
                toasts: {
                    flagged: 'Fall wurde als verletzungsverursachend gekennzeichnet – 72-Stunden-Uhr gestartet',
                    flag_failed: 'Der Fall konnte nicht als verletzungsverursachend gekennzeichnet werden',
                },
            },
            export: {
                generating: 'Generiert... bitte warten',
                success: 'Im Dateibrowser gespeicherter Export',
                failed: 'Der Export ist fehlgeschlagen. Überprüfen Sie das Audit-Protokoll',
            },
            no_notes: 'Noch keine Notizen',
            assign_placeholder: 'Benutzer-ID zur Zuweisung...',
            reassign: 'Neu zuweisen',
            evidence: {
                checklist_title: 'Nachweis-Checkliste',
                complete_pct: '% abgeschlossen',
                no_checklist: 'Keine Checklisten-Elemente',
                attachments: 'Anhänge',
                upload_attachment: 'Anhang hochladen',
                upload_button: 'Hochladen',
                uploading: 'Wird hochgeladen...',
                attachment_uploaded: 'Anhang erfolgreich hochgeladen',
                upload_error: 'Hochladen fehlgeschlagen',
                category: 'Kategorie',
                categories: {
                    identity_proof: 'Identitätsnachweis',
                    correspondence: 'Korrespondenz',
                    internal_data: 'Interne Daten',
                    legal: 'Rechtlich',
                    other: 'Sonstige',
                },
                no_attachments: 'Keine Anhänge',
                references_title: 'Nachweisreferenzen',
                no_references: 'Keine Referenzen',
                add_reference: 'Referenz hinzufügen',
                ref_type: 'Typ',
                ref_value: 'Wert',
                ref_desc: 'Beschreibung',
                ref_value_placeholder: 'URL, E-Mail-ID oder Referenz...',
                ref_desc_placeholder: 'Beschreibung...',
                ref_types: {
                    url: 'URL',
                    email_id: 'E-Mail-ID',
                    crm_record: 'CRM-Datensatz',
                    ticket_ref: 'Ticket-Referenz',
                    other: 'Sonstiges',
                },
            },
            communications: {
                title: 'Kommunikation',
                add: 'Nachricht hinzufügen',
                no_records: 'Keine Kommunikation erfasst',
                direction: 'Richtung',
                channel: 'Kanal',
                visibility: 'Sichtbarkeit',
                subject: 'Betreff',
                body: 'Text',
                send: 'Senden',
                inbound: 'Eingehend',
                outbound: 'Ausgehend',
                internal: 'Intern',
                external: 'Extern',
                tab_all: 'All',
                tab_internal: 'Intern',
                tab_external: 'Extern',
                subject_placeholder: 'Betreff (optional)',
                body_placeholder: 'Nachrichtentext...',
                channels: {
                    email: 'E-Mail',
                    portal: 'Portalbereich',
                    phone: 'Telefon',
                    postal: 'Post',
                    in_person: 'Persönlich',
                },
                apply_template: 'Vorlage Anwenden',
                select_template: 'Vorlage auswählen...',
                no_templates: 'Keine Vorlagen verfügbar',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Vorlage auf Antwort angewendet.',
                template_apply_error: 'Vorlage konnte nicht angewendet werden.',
            },
            escalations: {
                title: 'Eskalationsverlauf',
                no_events: 'Keine Eskalationsereignisse',
                tier: 'Stufe',
                threshold: 'Schwellenwert',
                action: 'Aktion',
                target: 'Ziel',
                triggered_at: 'Ausgelöst am',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP-Synchronisierung',
                sync_status: 'Synchronisierungsstatus',
                last_synced: 'Zuletzt synchronisiert',
                not_synced: 'Nicht synchronisiert',
                resync: 'Jetzt synchronisieren',
                syncing: 'Synchronisierung...',
                sync_history: 'Synchronisierungsverlauf (Letzte 10)',
                direction: 'Richtung',
                time: 'Zeit',
                retries: 'Wiederholungen',
                error_col: 'Fehler',
            },
            audit: {
                filter_event_placeholder: 'Nach Ereignistyp filtern...',
                filter_actor_placeholder: 'Nach Akteur filtern...',
            },
            fulfillment: {
                title: 'Abteilungserfüllung',
                assign_departments: 'Abteilungen zuweisen',
                department: 'Abteilung',
                assignee: 'Zugewiesene Person',
                deadline: 'Frist',
                evidence_count: 'Nachweise',
                signoff: 'Abzeichnung',
                signed_off: 'Abgezeichnet',
                pending_signoff: 'Ausstehend',
                start_button: 'Starten',
                complete_button: 'Als erledigt markieren',
                signoff_button: 'Abmelden',
                no_tasks: 'Noch keine Erfüllungsaufgaben zugewiesen.',
                tasks_table: 'Erfüllungsaufgaben',
                progress_label: 'Erfüllungsfortschritt',
                overdue: 'überfällig',
                assigned_success: 'Abteilungserfüllungsaufgabe erfolgreich zugewiesen.',
                assign_error: 'Erfüllungsaufgabe konnte nicht zugewiesen werden.',
                signoff_success: 'Abteilungsabzeichnung erfasst.',
                signoff_error: 'Abzeichnung konnte nicht erfasst werden.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Beweise hinzufügen',
                evidence_uploaded: 'Beweise hochgeladen und verknüpft',
                evidence_upload_error: 'Hochladen fehlgeschlagen',
                suggest_data_stores: 'Datenspeicher vorschlagen',
                suggest_error: 'Datenspeichervorschläge konnten nicht geladen werden.',
                suggest_stores_title: 'Empfohlene Datenspeicher',
                suggest_stores_none: 'Für diesen Fall gibt es keinen Datenspeicher. Konfigurieren Sie Systeme in Data Mapping, um Vorschläge zu verbessern.',
                suggest_stores_add: 'Zum Fall hinzufügen',
                suggest_stores_added: 'Datenspeicher zum Fall hinzugefügt',
                no_departments_title: 'Keine Abteilungen konfiguriert',
                no_departments_body: 'Mit Abteilungen können Sie DSR-Erfüllungsaufgaben an eine Funktion weiterleiten (Datenschutz, Recht, Personalwesen, IT-Betrieb). Installieren Sie Beispielabteilungen oder bitten Sie Ihren Administrator, Abteilungen in den Organisationseinstellungen zu konfigurieren.',
                no_departments_cta: 'Beispieldaten installieren',
                department_placeholder: 'Abteilungsname...',
                assignee_placeholder: 'Benutzer-ID zum Zuweisen...',
                assign_button: 'Zuweisen',
                status_pending: 'Ausstehend',
                status_in_progress: 'In Bearbeitung',
                status_completed: 'Abgeschlossen',
                status_verified: 'Überprüft',
                confirm_approve: 'Möchten Sie diese Verlängerung wirklich genehmigen?',
                confirm_deny: 'Möchten Sie diese Verlängerung wirklich ablehnen?',
            },
            response: {
                title: 'Antwortgenerierung',
                select_template: 'Antwortvorlage',
                choose_template: 'Vorlage auswählen...',
                generate: 'Antwort generieren',
                preview: 'Antwortvorschau',
                send: 'Antwort senden',
                delivery_method: {
                    label: 'Zustellmethode',
                    email: 'E-Mail',
                    portal: 'Portal',
                    postal: 'Post',
                    in_person: 'Persönlich',
                },
                recipient: {
                    email: 'Empfänger-E-Mail',
                    portal: 'Portalbenutzer-ID',
                    address: 'Postanschrift',
                },
                auto_generate_btn: 'Antwort generieren und senden',
                auto_generate_success: 'Antwort generiert und übermittelt',
                auto_generate_missing_recipient: 'Bitte geben Sie vor dem Absenden einen Empfänger an.',
                auto_fields: 'Automatisch ausgefüllte Felder',
                case_id: 'Fall-ID',
                requester_name: 'Name des Antragstellers',
                rights_type: 'Rechtstyp',
                history: 'Gesendete Antworten',
                generated_at: 'Generiert',
                confirmation: 'Bestätigung',
                sent_label: 'Gesendet',
                draft_label: 'Entwurf',
                generated_success: 'Antwort erfolgreich generiert.',
                generate_error: 'Antwort konnte nicht generiert werden.',
                sent_success: 'Antwort erfolgreich gesendet.',
                send_error: 'Antwort konnte nicht gesendet werden.',
                load_error: 'Antwortvorlagen konnten nicht geladen werden.',
                pdf_title: 'Antwortschreiben an betroffene Person',
                download_pdf: 'PDF herunterladen',
                download_all: 'Alle herunterladen',
                download_error: 'Antwort konnte nicht heruntergeladen werden',
            },
            extension: {
                title: 'Fristverlängerungen',
                acknowledgment_deadline: 'Bestätigungsfrist',
                fulfillment_deadline: 'Erfüllungsfrist',
                request_extension: 'Verlängerung beantragen',
                reason: 'Begründung',
                reason_placeholder: 'Begründung für die Beantragung zusätzlicher Zeit...',
                additional_days: 'Zusätzliche Tage',
                submit_request: 'Verlängerungsantrag einreichen',
                history: 'Verlängerungshistorie',
                requested_at: 'Beantragt',
                approve: 'Genehmigen',
                deny: 'Ablehnen',
                request_success: 'Verlängerungsantrag eingereicht.',
                request_error: 'Verlängerungsantrag konnte nicht eingereicht werden.',
                approved_success: 'Verlängerung genehmigt.',
                denied_success: 'Verlängerung abgelehnt.',
                decision_error: 'Verlängerungsentscheidung konnte nicht verarbeitet werden.',
                status_pending: 'Ausstehend',
                status_approved: 'Genehmigt',
                status_denied: 'Abgelehnt',
                confirm_approve: 'Möchten Sie diese Verlängerungsanfrage wirklich genehmigen?',
                confirm_deny: 'Möchten Sie diese Verlängerungsanfrage wirklich ablehnen?',
                decided_by: 'Entschieden von',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'CSV exportieren',
            exporting: 'Wird exportiert...',
            search_placeholder: 'Prüfdetails durchsuchen...',
            filter_by_actor: 'Nach Akteur filtern...',
            all_event_types: 'Alle Ereignistypen',
            seq: '#',
            timestamp: 'Zeitstempel',
            event: 'Ereignis',
            case_col: 'Fall',
            actor: 'Akteur',
            summary: 'Zusammenfassung',
            no_events: 'Keine Prüfereignisse gefunden',
            export_success: 'Audit-Trail erfolgreich exportiert.',
            export_error: 'Fehler beim CSV-Export',
            load_error: 'Fehler beim Laden des Prüfpfads. Bitte versuchen Sie es erneut.',
            announce_loaded: 'Prüfpfad geladen',
            start_date: 'Startdatum',
            end_date: 'Enddatum',
            per_page: '{count} / Seite',
            unknown: 'Unbekannt',
            source_filter: {
                label: 'Quelle',
                all: 'Alle',
                module: 'Modul',
                forseti: 'Forseti',
                aria_label: 'Audit-Ereignisse nach Quelle filtern',
            },
            event_types: {
                case_created: 'Fall erstellt',
                status_changed: 'Status geändert',
                case_assigned: 'Fall zugewiesen',
                note_added: 'Notiz hinzugefügt',
                attachment_added: 'Anhang hinzugefügt',
                attachment_deleted: 'Anhang gelöscht',
                reference_added: 'Referenz hinzugefügt',
                checklist_updated: 'Checkliste aktualisiert',
                identity_verified: 'Identität verifiziert',
                closure_summary_added: 'Abschlusszusammenfassung',
                approval_granted: 'Genehmigung erteilt',
                template_applied: 'Vorlage angewendet',
                export_generated: 'Export erstellt',
                sla_warning: 'SLA-Warnung',
                sla_overdue: 'SLA überfällig',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Gesamtprüfprotokoll',
                module_filter: 'Modul',
                framework_filter: 'Rahmenwerk',
                select_framework: 'Rahmenwerk auswählen...',
                entity_type_filter: 'Entitätstyp',
                action_filter: 'Aktion',
                actor_filter: 'Akteur',
                select_actor: 'Akteur auswählen...',
                search_filter: 'Suche',
                search_placeholder: 'Audit-Ereignisse durchsuchen...',
                filters_heading: 'Filter',
                date_range: 'Zeitraum',
                no_events_found: 'Keine Prüfereignisse gefunden.',
                event_count: 'Anzahl Ereignisse',
                action: 'Aktion',
                actor: 'Akteur',
                date_range_start: 'Anfang des Datumsbereichs',
                date_range_end: 'Ende des Datumsbereichs',
                filtering_events: 'Audit-Ereignisse werden gefiltert',
                framework_codes: 'Rahmenwerk-Codes',
                entity_type_col: 'Entitätstyp',
                aria_table: 'Vereinheitlichte Audit-Ereignisse',
                page_of: 'Seite {page} von {total}',
                entity_types: {
                    user: 'Benutzer',
                    case: 'Fall',
                    incident: 'Vorfall',
                    assessment: 'Bewertung',
                    vendor: 'Anbieter',
                    consent: 'Zustimmung',
                    policy: 'Politik',
                    data_map: 'Datenkarte',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Erstellen',
                    update: 'Aktualisieren',
                    delete: 'Löschen',
                    view: 'Ansicht',
                    export: 'Exportieren',
                    status_change: 'Statusänderung',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Prüfungsnachweispakete',
                assemble_pack: 'Nachweispaket zusammenstellen',
                framework: 'Rahmenwerk',
                select_framework: 'Rahmenwerk auswählen...',
                framework_code_label: 'Framework-Code',
                entity_types: 'Entitätstypen',
                select_entity_types: 'Entitätstypen auswählen...',
                period: 'Zeitraum',
                period_start: 'Beginn des Zeitraums',
                period_end: 'Ende des Zeitraums',
                completeness: 'Vollständigkeit',
                no_packs_found: 'Keine Nachweispakete gefunden.',
                verify: 'Überprüfen',
                export_pack: 'Exportieren',
                exporting: 'Wird exportiert...',
                pack_assembled: 'Beweispaket zusammengestellt',
                pack_exported: 'Beweispaket exportiert',
                aria_table: 'Audit-Bewispakete',
                aria_export_pack: 'Paket exportieren',
                export_format: 'Exportformat',
                export_in_progress: 'Export wird durchgeführt...',
                status_draft: 'Entwurf',
                status_assembled: 'Zusammengestellt',
                status_verified: 'Überprüft',
                status_submitted: 'Eingereicht',
                entity_type_labels: {
                    user: 'Benutzer',
                    case: 'Fall',
                    incident: 'Vorfall',
                    assessment: 'Bewertung',
                    vendor: 'Anbieter',
                    consent: 'Zustimmung',
                    policy: 'Politik',
                    data_map: 'Datenkarte',
                },
                template_id: 'Vorlagen-ID',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Modul',
                    action: 'Aktion',
                    actor_id: 'Schauspieler',
                    actor_role: 'Actor Role',
                    entity_type: 'Entitätstyp',
                    entity_id: 'Entitäts-ID',
                    summary: 'Zusammenfassung',
                    timestamp: 'Zeitstempel',
                    record_type: 'Record Type',
                    framework_code: 'Rahmen',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Evidenzpaket — Rahmenwerk: {framework}',
                export_header_period: 'Zeitraum: {start} bis {end}',
                export_header_completeness: 'Vollständigkeit: {score}%',
                export_header_status: 'Status: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Prüfungszeiträume',
                create_period: 'Prüfungszeitraum erstellen',
                framework_code_label: 'Framework-Code',
                select_framework: 'Rahmenwerk auswählen...',
                period_label: 'Zeitraumbezeichnung',
                period_start: 'Beginn des Zeitraums',
                period_end: 'Ende des Zeitraums',
                start: 'Starten',
                end: 'Ende',
                framework: 'Rahmen',
                retention_days: 'Aufbewahrungstage',
                section_period_dates: 'Zeitraumdaten',
                no_periods_found: 'Keine Prüfungszeiträume gefunden.',
                period_created: 'Audit-Zeitraum erstellt',
                aria_table: 'Audit-Zeiträume',
                suggested_boundaries: 'Vorgeschlagene Grenzen',
                suggested_boundaries_info: 'Vorgeschlagene Periodengrenzen werden aus dem regulatorischen Berichtszyklus jedes Rahmenwerks abgeleitet. Passen Sie diese nach Bedarf für Ihre Organisation an.',
                reporting_cycle: 'Berichtszyklus',
                suggested_start: 'Vorgeschlagener Beginn',
                suggested_end: 'Vorgeschlagenes Ende',
                suggested_next: 'Empfohlene nächste Periode',
                cycle_monthly: 'Monatlich',
                cycle_quarterly: 'Vierteljährlich',
                cycle_semi_annual: 'Halbjährlich',
                cycle_annual: 'Jährlich',
                cycle_custom: 'Benutzerdefiniert',
                period_deleted: 'Prüfzeitraum gelöscht',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Kundenreferenz',
            country: 'Land / Rechtsordnung',
            preferred_channel: 'Bevorzugter Kanal',
            channels: {
                email: 'E-Mail',
                postal: 'Post',
                phone: 'Telefon',
                portal: 'Portalbereich',
            },
            source: 'Quelle',
            sources: {
                admin_manual: 'Admin-Handbuch',
                public_form: 'Öffentliche Form',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telefon erhalten',
                postal_received: 'Posteingang erhalten',
                email_received: 'E-Mail erhalten',
                in_person: 'Persönlich',
                legacy_import: 'Legacy-Import',
            },
            required_fields: 'Antragsart, Name und E-Mail sind erforderlich',
            create_error: 'Fehler beim Erstellen des Falls',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Einstellungen',
            request_types: 'Antragsarten',
            request_types_desc: 'Konfigurieren Sie, welche Antragsarten für das öffentliche Eingangsformular aktiviert sind.',
            notifications: 'Benachrichtigungen',
            ticket_numbering: 'Ticket-Nummerierung',
            ticket_format: 'Aktuelles Format',
            ticket_prefix: 'Präfix',
            ticket_prefix_note: 'nicht konfigurierbar',
            sla_deadlines: 'SLA-Fristen',
            sla_desc: 'Legen Sie die maximale Antwortzeit (in Kalendertagen) für jede Antragsart fest. Fälle, die sich dem Schwellenwert nähern, werden mit Warnungen angezeigt.',
            days: 'Tage',
            warning_threshold: 'Warnschwelle',
            escalation_label: 'Automatische Eskalation bei SLA-Verletzung aktivieren',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'SLA-Konfiguration speichern',
            saving: 'Speichern...',
            saved: 'Erfolgreich gespeichert',
            announce_loaded: 'Einstellungen geladen',
            email_on_creation: 'E-Mail-Benachrichtigung bei Ticket-Erstellung',
            send_confirmation: 'Bestätigungs-E-Mail an Antragsteller senden',
            users: 'Benutzer',
            roles: 'Rollen',
            api_keys: 'API Schlüssel',
            mfa: 'MFA',
            sessions: 'Sitzungen',
            webhooks: 'Webhooks',
            modules: 'Module',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Auskunft (DSAR)',
                deletion: 'Löschung',
                correction: 'Berichtigung',
                portability: 'Datenübertragbarkeit',
                objection: 'Widerspruch',
                restriction: 'Einschränkung',
                consent_withdrawal: 'Widerruf der Einwilligung',
                protective_measures: 'Schutzmaßnahmen',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Abschnittszuweisungen',
            assign_section: 'Abschnitt zuweisen',
            section_ref: 'Abschnittsreferenz',
            section_label: 'Abschnittsbezeichnung',
            department_id: 'Abteilung',
            assignee_id: 'Zugewiesene Person',
            mandatory: 'Pflichtfeld',
            assign: 'Zuweisen',
            no_assignments: 'Keine Abschnittszuweisungen',
            status: 'Status',
            completion: 'Fertigstellung',
            mark_complete: 'Abschließen',
        },
        consolidation: {
            title: 'Konsolidierungsfortschritt',
            add_child: 'Unteraufgabe hinzufügen',
            child_task_id: 'Unteraufgaben-ID',
            department_id: 'Abteilung',
            link: 'Verknüpfung',
            overall_completion: 'Gesamtfertigstellung',
            departments_complete: 'Abgeschlossene Abteilungen',
            overdue: 'Überfällig',
            ready: 'Bereit',
            not_ready: 'Nicht bereit',
            master_status: 'Gesamtstatus',
            department_breakdown: 'Abteilungsübersicht',
            department: 'Abteilung',
            status: 'Status',
            completion: 'Fertigstellung',
            due_date: 'Fälligkeitsdatum',
            overdue_flag: 'Überfällig',
            child_tasks: 'Unteraufgaben',
            task: 'Aufgabe',
        },
        conflict_detection: {
            title: 'Konflikterkennung',
            unresolved: 'ungelöst',
            no_conflicts: 'Keine Konflikte erkannt',
            section: 'Abschnitt',
            field: 'Feld',
            dept_a: 'Abteilung A',
            value_a: 'Wert A',
            dept_b: 'Abteilung B',
            value_b: 'Wert B',
            severity: 'Schweregrad',
            status: 'Status',
            resolved: 'Gelöst',
            open: 'Offen',
            resolve: 'Lösen',
        },
        org_maturity: {
            title: 'Organisationsreife',
            departments: 'Abteilungen',
            department_hierarchy: 'Abteilungshierarchie',
            create_department: 'Abteilung erstellen',
            department_name: 'Abteilungsname',
            department_code: 'Code',
            level: 'Ebene',
            division: 'Bereich',
            department: 'Abteilung',
            team: 'Team',
            parent_department: 'Übergeordnete Abteilung',
            head_user: 'Abteilungsleiter',
            no_departments: 'Keine Abteilungen konfiguriert.',
            user_mappings: 'Benutzerzuordnungen',
            map_user: 'Benutzer zuordnen',
            user_id: 'Benutzer-ID',
            role: 'Rolle',
            no_users: 'Keine Benutzer dieser Abteilung zugeordnet.',
            remove: 'Entfernen',
            framework_assignments: 'Rahmenwerk-Zuweisungen',
            assign_framework: 'Rahmenwerk zuweisen',
            entity_id: 'Entitäts-ID',
            entity_type: 'Entitätstyp',
            framework_id: 'Rahmenwerk-ID',
            framework_code: 'Rahmenwerk-Code',
            no_assignments: 'Keine Rahmenwerk-Zuweisungen.',
            compliance_rollup: 'Compliance-Zusammenfassung',
            entity_name: 'Entität',
            total_assessments: 'Bewertungen gesamt',
            completed: 'Abgeschlossen',
            completion_pct: 'Abschluss %',
            open_gaps: 'Offene Lücken',
            critical_gaps: 'Kritisch',
            compliance_score: 'Punktzahl',
            no_rollup: 'Keine Daten zur Compliance-Zusammenfassung verfügbar.',
            entity_summary: 'Compliance-Zusammenfassung der Entität',
            frameworks_assigned: 'Rahmenwerke',
            overall_score: 'Gesamtpunktzahl',
            load_departments_error: 'Fehler beim Laden der Abteilungen',
            load_users_error: 'Fehler beim Laden der Benutzer',
            department_created: 'Abteilung erstellt',
            department_created_announce: 'Abteilung erstellt',
            create_department_error: 'Fehler beim Erstellen der Abteilung',
            user_mapped: 'Benutzer zugeordnet',
            user_mapped_announce: 'Benutzer der Abteilung zugeordnet',
            map_user_error: 'Fehler beim Zuordnen des Benutzers',
            user_removed: 'Benutzer entfernt',
            load_rollup_error: 'Fehler beim Laden der Zusammenfassung',
            load_assignments_error: 'Fehler beim Laden der Zuweisungen',
            framework_assigned: 'Framework zugewiesen',
            framework_assigned_announce: 'Framework der Entität zugewiesen',
            assign_framework_error: 'Fehler beim Zuweisen des Rahmenwerks',
            assignment_removed: 'Zuweisung entfernt',
            enter_entity_id_placeholder: 'Entitäts-ID eingeben um Zuweisungen anzuzeigen',
            entity_type_organization: 'Organisation',
            entity_type_department: 'Abteilung',
        },
        onboarding_maturity: {
            title: 'Onboarding-Reife',
            quick_starts: 'Schnellstarts',
            progress: 'Onboarding-Fortschritt',
            personas: 'Persona-Pfade',
            persona_onboarding: 'Persona-Onboarding',
            quick_start_scenarios: 'Schnellstart-Szenarien',
            start: 'Starten',
            restart: 'Neustart',
            estimated_minutes: 'Geschätzte Minuten',
            steps: 'Schritte',
            scenario: 'Szenario',
            no_scenarios: 'Keine Schnellstartszenarien verfügbar.',
            persona_id: 'Persona-ID',
            persona_label: 'Persona',
            role_description: 'Rollenbeschreibung',
            primary_modules: 'Primäre Module',
            recommended_tours: 'Empfohlene Touren',
            sample_data_modules: 'Beispieldatenmodule',
            quick_start_scenario: 'Schnellstart-Szenario',
            no_personas: 'Keine Persona-Pfade verfügbar.',
            framework_setup: 'Regulatorische Rahmenwerke',
            framework_code: 'Rahmencode',
            framework_name: 'Framework-Name',
            related_modules: 'Verwandte Module',
            persona_relevance: 'Relevante Personas',
            no_frameworks: 'Keine Framework-Setup-Optionen verfügbar.',
            tier_config: 'Konfiguration des Ebenenmoduls',
            tier: 'Stufe',
            available_modules: 'Verfügbare Module',
            module_count: 'Modulanzahl',
            overall_completion: 'Gesamtabschluss',
            wizard_completed: 'Assistent abgeschlossen',
            tours_completed: 'Touren abgeschlossen',
            quick_starts_completed: 'Schnellstarts abgeschlossen',
            selected_frameworks: 'Ausgewählte Frameworks',
            no_progress: 'Es sind keine Daten zum Onboarding-Fortschritt verfügbar.',
            detected_persona: 'Erkannte Persona',
            current_tier: 'Aktuelle Stufe',
            completion_pct: 'Abschluss',
            progress_page: {
                loaded: 'Fortschritt geladen',
                no_progress: 'Keine Onboarding-Fortschrittsdaten verfügbar.',
                overall: 'Gesamtfortschritt',
                detected_persona: 'Erkannte Persona',
                tier: 'Stufe',
                modules: 'Module',
                wizard: 'Assistent',
                completed: 'Abgeschlossen',
                pending: 'Ausstehend',
                tours: 'Rundgänge',
                quick_starts: 'Schnellstarts',
                frameworks: 'Ausgewählte Rahmenwerke',
                no_frameworks: 'Keine Rahmenwerke ausgewählt.',
                go_to_wizard: 'Einrichtungsassistenten abschließen',
                framework_selection: 'Rahmenwerk-Auswahl',
                framework_toggle: 'Rahmenwerke für Ihr Compliance-Programm aktivieren oder deaktivieren.',
                framework_updated: 'Rahmenwerk-Auswahl aktualisiert.',
                view_tours: 'Rundgänge anzeigen',
                view_quick_starts: 'Schnellstarts anzeigen',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Wählen Sie eine Persona, um empfohlene Module, Rundgänge und Schnellstartszenarien anzuzeigen.',
                no_personas: 'Keine Persona-Pfade verfügbar.',
                modules: 'Module',
                tours: 'Rundgänge',
                quick_start: 'Schnellstart',
                details: 'Einzelheiten',
                primary_modules: 'Primäre Module',
                recommended_tours: 'Empfohlene Rundgänge',
                framework_options: 'Regulatorische Rahmenwerke',
                no_frameworks: 'Keine Rahmenwerk-Einrichtungsoptionen verfügbar.',
                no_frameworks_explain: 'Für Ihre Organisation sind noch keine regulatorischen Rahmenwerke installiert, daher gibt es hier nichts einzurichten.',
                no_frameworks_action: 'Installieren Sie ein Marktpaket, um die für Sie geltenden Rahmenwerke hinzuzufügen',
                start_tour: 'Starten',
                no_tours: 'Keine Rundgänge verfügbar.',
                help_center: 'Hilfecenter',
                no_articles: 'Keine Artikel gefunden.',
                primary: 'Primär',
                framework_code: 'Rahmenwerkkode',
                framework_label: 'Bezeichnung',
                region: 'Region',
                category: 'Kategorie',
                enabled: 'Aktiviert',
                disabled: 'Deaktiviert',
                tier_modules: 'Stufenmodulkonfiguration',
                tiers_loaded: 'Stufenkonfigurationen geladen',
                no_tier_config: 'Keine Stufenkonfiguration verfügbar.',
                view_tours: 'Modul-Rundgänge anzeigen',
                view_quick_start: 'Schnellstart beginnen',
                toggle_framework: 'Rahmenwerk umschalten',
                framework_updated: 'Rahmenwerk-Auswahl aktualisiert.',
                tier_desc_light: 'Grundlegende Datenschutz-Essentials — PDPL-Compliance-Grundlagen',
                tier_desc_standard: 'Vollständige Compliance-Suite — alle Module mit Workflows',
                tier_desc_enterprise: 'Erweiterte Governance und Automatisierung — Multi-Entität, Gruppenberichte',
                persona_highlight: 'Ihre Module',
                aria_go_to_module: '{name} – gehe zum Modul',
                aria_start_tour: 'Starten Sie {title}',
                aria_quick_start: 'Schnellstart: {title}',
                aria_help_article: 'Hilfeartikel: {title}',
                aria_view_framework: '{label}{primary} – Framework-Details anzeigen',
                aria_primary_for_persona: '{name} (primär für diese Persona)',
                primary_for_persona_suffix: ' (primär für diese Persona)',
                aria_view_in_persona_paths: '{name} – Ansicht in Persona-Pfaden',
                progress_of_total: '{completed} von {total} abgeschlossen',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Üben Sie gängige Arbeitsabläufe mit geführten Schnellstartszenarien.',
                no_scenarios: 'Keine Schnellstartszenarien verfügbar.',
                started: 'Schnellstart gestartet',
                status_completed: 'Abgeschlossen',
                status_in_progress: 'In Bearbeitung',
                status_not_started: 'Nicht gestartet',
                minutes: 'Min.',
                steps: 'Schritte',
                step: 'Schritt',
                restart: 'Neustart',
                start: 'Starten',
                go_to_module: 'Zum Modul gehen',
                next_step: 'Nächster Schritt',
                complete: 'Abschließen',
                reset: 'Zurücksetzen',
                step_of: 'von',
                action_navigate: 'Navigieren',
                action_form_fill: 'Formular ausfüllen',
                action_review: 'Überprüfen',
                action_confirm: 'Bestätigen',
                step_changed: 'Schritt {current} von {total}: {title}',
                completed_message: 'Schnellstart erfolgreich abgeschlossen!',
                reset_message: 'Schnellstart wurde zurückgesetzt.',
            },
            persona_p_vn_01_label: 'Unternehmen DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Abteilungsinhaber',
            persona_p_vn_02_description: 'Abteilungsleiter, verantwortlich für die bereichsspezifische Compliance-Berichterstattung',
            persona_p_vn_03_label: 'E-Commerce-Compliance',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Leiter der Daten-Governance',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cybersicherheit und rechtliche Operationen',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Governance-Leiter',
            persona_p_vn_07_description: 'Leitet die Überwachung von AI System-Governance, Risikobewertungen und Klassifizierung',
            persona_p_vn_08_label: 'Grenzüberschreitender und Vendor-Gouverneur',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Leiter der Reaktion auf Sicherheitsverletzungen',
            persona_p_vn_09_description: 'Leiter der Koordinierung der Meldung von Verstößen und der Reaktion auf Vorfälle',
            persona_p_vn_10_label: 'Leiter der Internen Revision',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'HR- & Mitarbeiterdaten-Verantwortlicher',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Compliance-Leiter multinationale Tochtergesellschaft',
            persona_p_vn_12_description: 'DSB in einer vietnamesischen Tochtergesellschaft eines ausländischen Unternehmens mit lokaler Compliance-Ebene',
            persona_p_vn_13_label: 'Compliance-Leiter Bildungswesen',
            persona_p_vn_13_description: 'Compliance-Leiter an einer ausländischen Schule für den Datenschutz von Schülern und Eltern',
            qs_hr_consent_title: 'Einwilligungsunterlagen von Mitarbeitern registrieren',
            qs_hr_consent_description: 'Durchlauf der Registrierung von Mitarbeitereinwilligungen und Erstellung eines Abdeckungsberichts.',
            qs_subsidiary_dpia_title: 'DSFA-Bewertung für Tochtergesellschaft erstellen',
            qs_subsidiary_dpia_description: 'Durchlauf der Erstellung einer DSFA für eine Verarbeitungstätigkeit der Tochtergesellschaft.',
            qs_student_consent_title: 'Einwilligungserfassung für Schüler einrichten',
            qs_student_consent_description: 'Durchlauf der Erstellung von Einwilligungsrichtlinien für Eltern/Erziehungsberechtigte für Schülerdaten.',
            persona_p_vn_14_label: 'Externer Rechtsberater',
            persona_p_vn_14_description: 'Senior-Partner einer Anwaltskanzlei, der mehrere Kundenorganisationen zu PDPL-Compliance berät',
            persona_p_vn_15_label: 'Betriebsleiter einer Anwaltskanzlei',
            persona_p_vn_15_description: 'Koordinator des Kundenengagements, der die Nachverfolgung der Lieferungen und die Terminkoordination verwaltet',
            persona_p_vn_16_label: 'Geschäftsinhaber / CEO',
            persona_p_vn_16_description: 'CEO oder GM, der rechtlich der Datenverantwortliche gemäß PDPL ist und für Compliance-Investitionen verantwortlich ist',
            qs_dpia_filing_title: 'Schließen Sie Ihre erste DPIA-Einreichung ab',
            qs_dept_compliance_title: 'Reichen Sie Ihren ersten Abteilungs-Compliance-Abschnitt ein',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Richten Sie die Datenverarbeitung für Telekommunikationsteilnehmer ein',
            qs_data_flow_title: 'Ordnen Sie Ihren ersten Datenfluss zu',
            qs_incident_response_title: 'Konfigurieren Sie den Workflow zur Reaktion auf Vorfälle',
            qs_ai_risk_title: 'Führen Sie eine AI Systemrisikobewertung durch',
            qs_cross_border_title: 'Bewerten Sie einen grenzüberschreitenden Datentransfer',
            qs_breach_notification_title: 'Simulieren Sie einen Arbeitsablauf für die Meldung von Verstößen',
            qs_audit_trail_title: 'Überprüfen Sie einen Compliance-Audit-Trail',
            qs_legal_advisor_portfolio_title: 'Überprüfen Sie das Multi-Client-Compliance-Portfolio',
            qs_legal_advisor_portfolio_description: 'Überprüfen Sie den Compliance-Status mehrerer Kundenorganisationen.',
            qs_law_firm_ops_title: 'Verfolgen Sie den Lieferstatus des Kunden',
            qs_law_firm_ops_description: 'Überwachen Sie den Fortschritt der Beratungsleistungen und das Kundenengagement.',
            qs_ceo_dashboard_title: 'Sehen Sie sich das Compliance-Dashboard für Führungskräfte an',
            qs_ceo_dashboard_description: 'Überprüfen Sie den Compliance-Status und die Investitionskennzahlen auf hoher Ebene.',
        },
        rights_requests: {
            response_templates: {
                title: 'Antwortvorlagen',
                subtitle: 'Verwalten Sie die DSR-Antwortbriefvorlagen, die ResponseGenerator zum Verfassen von Antworten an betroffene Personen verwendet.',
                create_btn: 'Neue Vorlage',
                edit_btn: 'Vorlage bearbeiten',
                filter_right_type: 'Filtern Sie nach dem richtigen Typ',
                filter_locale: 'Nach Gebietsschema filtern',
                filter_status: 'Nach Status filtern',
                all_right_types: 'Alle richtigen Typen',
                any_right_type: 'Beliebig (gilt für alle Typen)',
                all_locales: 'Alle Orte',
                all_statuses: 'Alle',
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                empty_state: 'Keine Vorlage entspricht den aktuellen Filtern.',
                columns: {
                    template_id: 'Vorlagen-ID',
                    outcome: 'Ergebnis',
                    locale: 'Gebietsschema',
                    right_type: 'Richtiger Typ',
                    is_active: 'Status',
                    last_updated: 'Zuletzt aktualisiert',
                    actions: 'Aktionen',
                },
                actions: {
                    edit: 'Bearbeiten',
                    clone: 'Klon',
                    deactivate: 'Deaktivieren',
                    reactivate: 'Reaktivieren',
                    preview: 'Vorschau',
                },
                form: {
                    template_id: 'Vorlagen-ID',
                    template_id_help: 'Bezeichner in Kleinbuchstaben, z. B. dsr_access_fulfilled_vi',
                    outcome: 'Ergebnis',
                    locale: 'Gebietsschema',
                    right_type: 'Richtiger Typ',
                    subject: 'Betreff',
                    body: 'Körpervorlage',
                    body_help: 'Verwenden Sie Platzhalter wie {case_reference}, {data_subject_name}, {fulfilled_at}. Mindestens 15 Zeilen für mehrteilige Briefe.',
                    is_active: 'Aktiv',
                    official_form_id: 'Offizielle Formular-ID (optional)',
                },
                outcomes: {
                    fulfilled: 'Erfüllt',
                    partially_fulfilled: 'Teilweise erfüllt',
                    denied: 'Abgelehnt',
                },
                clone_modal_title: 'Klonvorlage',
                clone_target_locale: 'Zielgebietsschema',
                clone_help: 'Im Zielgebietsschema wird eine neue Vorlage erstellt, wobei diese Vorlage als Ausgangspunkt dient.',
                preview_modal_title: 'Vorlagenvorschau',
                preview_note: 'Die Vorschau verwendet Platzhalter für Demo-Fälle DSR-DEMO-0020. Echte Lieferung ersetzt Live-Falldaten.',
                preview_unavailable: 'Für die Vorschau ist ein vorhandener Fall erforderlich. Hängen Sie diese Vorlage an einen Fall an und verwenden Sie die Aktion „Antwortentwurf“ auf der Falldetailseite.',
                deactivate_confirm: 'Diese Vorlage deaktivieren? Es gilt weiterhin für bestehende Fälle, die darauf verweisen.',
                toasts: {
                    created: 'Vorlage erstellt',
                    updated: 'Vorlage aktualisiert',
                    deactivated: 'Vorlage deaktiviert',
                    reactivated: 'Vorlage reaktiviert',
                    cloned: 'Vorlage geklont',
                    load_error: 'Vorlagen konnten nicht geladen werden',
                    save_error: 'Vorlage konnte nicht gespeichert werden',
                    clone_error: 'Vorlage konnte nicht geklont werden',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Abteilungen',
                subtitle: 'Verwalten Sie die Abteilungen, die DSR-Fälle, DPIA-Aufgaben und Compliance-Workflows weiterleiten.',
                create_btn: 'Neue Abteilung',
                edit_btn: 'Abteilung bearbeiten',
                columns: {
                    name: 'Name',
                    description: 'Beschreibung',
                    active_cases: 'Aktive Fälle',
                    members: 'Mitglieder',
                    status: 'Status',
                    actions: 'Aktionen',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    name: 'Name',
                    description: 'Beschreibung',
                },
                actions: {
                    edit: 'Bearbeiten',
                    deactivate: 'Deaktivieren',
                    reactivate: 'Reaktivieren',
                    delete: 'Löschen',
                    manage_members: 'Mitglieder verwalten',
                },
                delete_modal_title: 'Abteilung löschen',
                delete_warning: 'Diese Abteilung hat noch aktive Fälle. Wählen Sie eine Abteilung aus, der Sie sie vor dem Löschen neu zuweisen möchten.',
                delete_no_cases: 'Diese Abteilung hat keine aktiven Fälle und kann bedenkenlos gelöscht werden.',
                reassign_prompt: 'Aktive Fälle neu zuweisen an:',
                reassign_none_available: 'Keine weiteren aktiven Abteilungen verfügbar. Erstellen Sie zunächst eine andere Abteilung.',
                member_section_title: 'Mitglieder',
                add_member: 'Mitglied hinzufügen',
                remove_member: 'Entfernen',
                member_role: 'Rolle',
                no_members: 'Dieser Abteilung sind keine Mitglieder zugeordnet.',
                toasts: {
                    created: 'Abteilung erstellt',
                    updated: 'Abteilung aktualisiert',
                    deactivated: 'Abteilung deaktiviert',
                    reactivated: 'Abteilung reaktiviert',
                    deleted: 'Abteilung gelöscht',
                    reassigned_count: '{count} Fall(e) neu zugewiesen',
                    member_added: 'Mitglied hinzugefügt',
                    member_removed: 'Mitglied entfernt',
                    load_error: 'Die Abteilungen konnten nicht geladen werden',
                    save_error: 'Die Abteilung konnte nicht gespeichert werden',
                    delete_error: 'Die Abteilung konnte nicht gelöscht werden',
                },
            },
            retention_policies: {
                title: 'Aufbewahrungsrichtlinien',
                subtitle: 'Konfigurieren Sie Aufbewahrungsfenster pro Zieltyp. Dokumente zur Identitätsprüfung verwenden dies, um zukünftige Uploads mit dem Stempel retention_expires_at zu versehen.',
                create_btn: 'Neue Richtlinie',
                edit_btn: 'Richtlinie bearbeiten',
                columns: {
                    target_type: 'Zieltyp',
                    retention_days: 'Aufbewahrung (Tage)',
                    owner: 'Versicherungsnehmer',
                    effective_from: 'Gültig ab',
                    last_reviewed: 'Zuletzt überprüft',
                    status: 'Status',
                    actions: 'Aktionen',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    target_type: 'Zieltyp',
                    target_type_help: 'Für welche Datenkategorie gilt das Aufbewahrungsfenster? Derzeit werden nur DSR-Dokumente zur Identitätsprüfung unterstützt.',
                    retention_days: 'Aufbewahrungszeitraum (Tage)',
                    retention_days_help: 'Zwischen 1 und 3650 Tagen (maximal 10 Jahre).',
                    owner: 'Versicherungsnehmer',
                    owner_help: 'Normalerweise das DPO. Verantwortlich für die Aufbewahrungsentscheidung gemäß DG-2.',
                    effective_from: 'Gültig ab',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR-Dokumente zur Identitätsprüfung',
                },
                deactivate_notice: 'Die Deaktivierung wirkt sich nicht auf bereits gestempelte Anhänge aus – lediglich zukünftige Uploads erhalten keine Aufbewahrungsfrist mehr.',
                deactivate_confirm: 'Diese Aufbewahrungsrichtlinie deaktivieren?',
                toasts: {
                    created: 'Aufbewahrungsrichtlinie erstellt',
                    updated: 'Aufbewahrungsrichtlinie aktualisiert',
                    deactivated: 'Aufbewahrungsrichtlinie deaktiviert',
                    load_error: 'Aufbewahrungsrichtlinien konnten nicht geladen werden',
                    save_error: 'Aufbewahrungsrichtlinie konnte nicht gespeichert werden',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Systeme',
            categories: 'Datenkategorien',
            registry: 'Datenregister',
            purposes: 'Verarbeitungszwecke',
            retention: 'Aufbewahrung',
            flows: 'Datenflüsse',
            activities: 'Verarbeitungstätigkeiten',
            evidence: 'Nachweise',
            exports: 'Exporte',
            attestation: 'Bescheinigungszyklen',
            governance: 'Regierungsführung',
            review: 'Prüfaufgaben',
            inventory: 'Inventar',
            exceptions: 'Ausnahmen',
            history: 'Verlauf',
            ropa_completeness: 'ROPA Vollständigkeit',
        },
        ropa_completeness_autofix: {
            ask_button: 'Fragen Sie Forseti',
            drafting: 'Ausarbeitung…',
            accept: 'Akzeptiere',
            reject: 'Ablehnen',
            accepted: 'Akzeptiert – bewerben Sie sich über den Aktivitätseditor.',
            rejected: 'Abgelehnt.',
            needs_human_input: 'Forseti benötigt mehr Aktivitätskontext (z. B. einen dokumentierten Zweck), bevor er dieses Feld entwerfen kann.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA-Vollständigkeitsbericht',
            subtitle: 'Feldbezogenes Hygieneaudit Ihrer Aufzeichnungen über Verarbeitungsaktivitäten, abhängig vom gesetzlichen Rahmen.',
            framework_label: 'Rahmen',
            kpi_total: 'Bewertete Aktivitäten',
            kpi_critical: 'Kritisch',
            kpi_high: 'Hoch',
            kpi_conditional: 'Bedingt',
            field_chart_heading: 'Fehlende Felder nach Häufigkeit',
            drilldown_heading: 'Aktivitäten mit Lücken',
            col_activity: 'Aktivität',
            col_field: 'Feld',
            col_severity: 'Schweregrad',
            col_condition: 'Zustand',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Kritisch',
            severity_high: 'Hoch',
            severity_conditional: 'Bedingt',
            severity_informational: 'Informativ',
            include_informational: 'Fügen Sie Informationen hinzu',
            export_csv: 'Herunterladen CSV',
            exporting: 'Exportieren...',
            loading: 'Wird geladen…',
            empty_state: 'Alle aktiven Aktivitäten verfügen über vollständige ROPA-Datensätze für dieses Framework.',
            error_fetch: 'Der Vollständigkeitsbericht konnte nicht geladen werden.',
            error_unsupported_framework: 'Dieses Framework wird nicht unterstützt.',
            error_framework_not_installed: 'Dieses Framework ist für Ihre Organisation nicht installiert.',
            no_misses_for_field: 'In diesem Feld fehlen keine Aktivitäten.',
            framework_PDPL: 'PDPL (Gesetz 91/2025 + Dekret 356)',
            framework_GDPR: 'GDPR Artikel 30',
            framework_DECREE_165: 'Dekret 165 (vietnamesisches Datengesetz)',
            framework_ISO_27701: 'ISO 27701 (informativ)',
        },
        registry: {
            // Sprint 64.72.0 — Spaltenüberschriften des Datenregister-Exports.
            export_col: {
                registry_item_id: 'Registereintrags-ID',
                name: 'Name',
                description: 'Beschreibung',
                field_identifiers: 'Feldbezeichner',
                category_id: 'Kategorie-ID',
                category_name: 'Kategoriename',
                data_type: 'Datentyp',
                effective_sensitivity: 'Geltende Vertraulichkeitsstufe',
                sensitivity_override: 'Abweichende Vertraulichkeitsstufe',
                data_subjects: 'Betroffene Personen',
                volume_band: 'Mengenbereich',
                source_system_ids: 'Quellsystem-IDs',
                processing_activity_ids: 'Verarbeitungstätigkeits-IDs',
                retention_policy_id: 'Aufbewahrungsrichtlinien-ID',
                effective_retention_policy_id: 'Geltende Aufbewahrungsrichtlinien-ID',
                purposes: 'Zwecke',
                collection_method: 'Erhebungsart',
                legal_basis: 'Rechtsgrundlage',
                data_tier: 'Datenstufe',
                data_tier_source: 'Herkunft der Datenstufe',
                storage_locations: 'Speicherorte',
                cross_border: 'Grenzüberschreitend',
                status: 'Status',
                source_form_submission_id: 'Einreichungs-ID des Quellformulars',
                source_form_template_id: 'Vorlagen-ID des Quellformulars',
                last_reviewed_at: 'Zuletzt überprüft',
                next_review_at: 'Nächste Überprüfung',
                created_at: 'Erstellt am',
                updated_at: 'Aktualisiert am',
                data_element: 'Datenelement',
                data_category: 'Datenkategorie',
                systems: 'Systeme',
                retention_policy: 'Aufbewahrungsrichtlinie',
                name_of_processing: 'Bezeichnung der Verarbeitung',
                categories_of_personal_data: 'Kategorien personenbezogener Daten',
                categories_of_data_subjects: 'Kategorien betroffener Personen',
                recipients: 'Empfänger',
                third_country_transfers: 'Drittlandsübermittlungen',
                retention_period: 'Aufbewahrungsfrist',
                doc_title: 'Datenregister',
                doc_generated: 'Export erstellt {timestamp}',
                doc_item_count: '{count} Registereintrag/-einträge',
                doc_template_full: 'Vollständig',
            },
            export_vocab: {
                data_type: {
                    text: 'Text',
                    number: 'Zahl',
                    date: 'Datum',
                    datetime: 'Datum und Uhrzeit',
                    boolean: 'Ja/Nein',
                    binary: 'Binär',
                    structured: 'Strukturiert',
                    file: 'Datei',
                    identifier: 'Kennung',
                    geolocation: 'Standortdaten',
                },
                collection_method: {
                    direct_from_subject: 'Unmittelbar bei der betroffenen Person',
                    third_party: 'Von einem Dritten',
                    derived: 'Aus anderen Daten abgeleitet',
                    observed: 'Beobachtet',
                    inferred: 'Erschlossen',
                    public_source: 'Öffentlich zugängliche Quelle',
                },
                data_subject_type: {
                    customer: 'Kunde',
                    employee: 'Beschäftigter',
                    contractor: 'Auftragnehmer',
                    prospect: 'Interessent',
                    minor: 'Minderjähriger',
                    patient: 'Patient',
                    student: 'Studierender',
                    beneficiary: 'Begünstigter',
                    vendor_contact: 'Ansprechpartner des Lieferanten',
                    website_visitor: 'Website-Besucher',
                    other: 'Sonstige',
                },
                volume_band: {
                    lt_1k: 'Weniger als 1.000 Datensätze',
                    '1k_to_10k': '1.000 bis 10.000 Datensätze',
                    '10k_to_100k': '10.000 bis 100.000 Datensätze',
                    '100k_to_1m': '100.000 bis 1 Million Datensätze',
                    gt_1m: 'Mehr als 1 Million Datensätze',
                    unknown: 'Nicht bekannt',
                },
                tier_source: {
                    assessed_and_approved: 'Bewertet und freigegeben',
                    manual_legacy: 'Aus einem Altwert übernommen',
                    legacy_platform_field: 'Geerbter Plattformwert',
                    scheme_scoped_only: 'Nach schemaspezifischen Stufen bewertet — kein einzelner Code deckt mehrere gesetzliche Stufenskalen ab',
                },
            },
            page: {
                title: 'Datenregister',
                subtitle: 'Persönliche Datenelemente, die Ihre Organisation speichert, organisiert nach Datenkategorien.',
                empty_state: 'Noch keine Registrierungselemente. Fügen Sie einen Artikel manuell hinzu oder importieren Sie ihn aus einem Regulierungsregister.',
                empty_filter_state: 'Keine Artikel entsprechen Ihren Filtern.',
            },
            filter: {
                category: 'Kategorie',
                sensitivity: 'Empfindlichkeit',
                data_subject: 'Betroffene Person',
                status: 'Status',
                data_tier: 'Datenebene',
                cross_border: 'Grenzüberschreitend',
                has_owner: 'Hat Besitzer',
                has_form_source: 'Aus Formular importiert',
                search_placeholder: 'Suchen Sie nach Name, Beschreibung oder Feldkennung …',
                clear: 'Filter löschen',
                option_all: 'Alle',
            },
            col: {
                name: 'Name',
                category: 'Kategorie',
                sensitivity: 'Empfindlichkeit',
                data_subjects: 'Betroffene Personen',
                systems: 'Systeme',
                activities: 'Aktivitäten',
                owner: 'Besitzer',
                retention: 'Aufbewahrung',
                data_tier: 'Datenebene',
                status: 'Status',
                last_reviewed: 'Zuletzt überprüft',
                actions: 'Aktionen',
            },
            action: {
                add_manual: 'Artikel hinzufügen',
                add_from_form: 'Aus behördlichem Register hinzufügen',
                import_history: 'Importieren Sie vergangene Einsendungen',
                export: 'Exportieren',
                archive: 'Archiv',
                restore: 'Wiederherstellen',
                edit: 'Bearbeiten',
                assign_owner: 'Besitzer zuweisen',
                attach_evidence: 'Beweise beifügen',
                open_lineage: 'Offene Abstammung',
            },
            modal_editor: {
                section_identity: 'Identität',
                section_classification: 'Klassifizierung',
                section_subjects: 'Themen & Band',
                section_location: 'Standort & Transfer',
                section_systems: 'Systeme und Aktivitäten',
                section_retention: 'Aufbewahrung und Recht',
                section_governance: 'Regierungsführung',
                section_notes: 'Notizen',
                hint_inherited: '(geerbt von der Kategorie)',
                hint_overridden: '(überschrieben)',
                placeholder_field_identifiers: 'z. B. Kunden.primary_email, Hris.employee_id',
                validation_name_required: 'Name ist erforderlich',
                validation_category_required: 'Kategorie ist erforderlich',
                validation_data_subjects_required: 'Es ist mindestens ein Datensubjekttyp erforderlich',
                label_data_type: 'Datentyp',
                label_field_identifiers: 'Feldbezeichner (durch Kommas getrennt)',
                label_data_subject_types: 'Arten von Datensubjekten',
                label_volume_band: 'Lautstärkeband',
                label_storage_locations: 'Speicherorte (durch Kommas getrennt)',
                placeholder_storage_locations: 'ISO-3166 Codes oder Freitext (z. B. VN, Hetzner EU)',
                label_cross_border: 'Grenzüberschreitende Überweisung',
                label_source_systems: 'Quellsysteme',
                label_processing_activities: 'Verarbeitungstätigkeiten',
                label_collection_method: 'Sammelmethode',
                label_purposes: 'Zwecke',
                hint_multi_select: 'Halten Sie die Befehls-/Strg-Taste gedrückt, um mehrere auszuwählen.',
                option_inherit_category: '— (von Kategorie erben)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Vorschau importieren',
                label_to_create: 'Zu erschaffen',
                label_to_update: 'Zum Aktualisieren',
                label_skipped_archived: 'Übersprungen (archiviert)',
                label_conflicts: 'Konflikte',
                commit: 'Importieren',
                cancel: 'Abbrechen',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Kategorie',
                section_systems: 'Quellsysteme',
                section_activities: 'Verarbeitungstätigkeiten',
                section_flows: 'Datenflüsse',
                section_retention: 'Effektive Aufbewahrung',
                section_governance: 'Regierungsführung',
                section_evidence: 'Beweise',
                section_audit: 'Aktueller Prüfpfad',
                section_source_form: 'Einreichung des Quellformulars',
                badge_inherited: 'Geerbt',
                badge_overridden: 'Überschrieben',
            },
            toast: {
                created: 'Registrierungselement erstellt.',
                updated: 'Registrierungselement aktualisiert.',
                archived: 'Registrierungselement archiviert.',
                imported: '{count} Elemente importiert.',
                conflict_warning: '{count} Feld(er) haben Ihre Änderungen beibehalten – siehe Konfliktliste.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Systeminventar',
            new: 'Neues System',
            edit: 'System bearbeiten',
            name: 'Bezeichnung',
            owner: 'Verantwortlicher',
            criticality: 'Kritikalität',
            dpa_status: 'AVV-Status',
            location: 'Standort',
            system_type: 'Systemtyp',
            metadata: 'Metadaten (JSON)',
            department: 'Abteilung',
            hosting_location: 'Hosting-Standort',
            hosting_provider: 'Hosting-Anbieter',
            data_residency_country: 'Land der Datenspeicherung',
            hosting: 'Hostingdienst',
            no_systems: 'Keine Systeme gefunden',
            confirm_archive: 'System „{name}" archivieren?',
            count: 'Systeme',
            count_one: 'System',
            type_internal: 'Intern',
            type_external: 'Extern',
            type_cloud_service: 'Cloud-Dienst',
            type_on_premise: 'Vor Ort',
            type_hybrid: 'Hybridtyp',
            status_active: 'Aktiv',
            status_decommissioning: 'Wird stillgelegt',
            status_decommissioned: 'Stillgelegt',
            status_planned: 'Geplant',
            dpa_not_required: 'Nicht erforderlich',
            dpa_required_missing: 'Erforderlich fehlend',
            dpa_in_progress: 'In Bearbeitung',
            dpa_signed: 'Unterzeichnet',
            all_criticalities: 'Alle Kritikalitätsstufen',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Datenkategorien',
            new: 'Neue Kategorie',
            edit: 'Kategorie bearbeiten',
            name: 'Bezeichnung',
            sensitivity: 'Sensitivität',
            tags: 'Schlagwörter',
            used_in: 'Verwendet in',
            examples: 'Beispiele',
            examples_hint: 'Beispiele (kommagetrennt)',
            default: 'Standard',
            all_sensitivities: 'Alle Sensitivitätsstufen',
            all_statuses: 'Alle Status',
            no_categories: 'Keine Kategorien gefunden',
            confirm_delete: 'Kategorie „{name}" löschen?',
            count: 'Kategorien',
            count_one: 'Kategorie',
            status_draft: 'Entwurf',
            status_proposed: 'Vorgeschlagen',
            status_under_review: 'In Prüfung',
            status_approved: 'Genehmigt',
            status_deprecated: 'Veraltet',
            status_updated: 'Kategoriestatus aktualisiert',
            propose: 'Vorschlagen',
            approve: 'Genehmigen',
            deprecate: 'Veraltung',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Verarbeitungszwecke',
            new: 'Neuer Zweck',
            edit: 'Zweck bearbeiten',
            name: 'Bezeichnung',
            legal_basis: 'Rechtsgrundlage',
            retention_period: 'Aufbewahrungsfrist (Tage)',
            justification: 'Begründung des berechtigten Interesses',
            legal_basis_detail: 'Details zur Rechtsgrundlage',
            all_legal_bases: 'Alle Rechtsgrundlagen',
            no_purposes: 'Keine Zwecke gefunden',
            confirm_delete: '„{name}" löschen?',
            default: 'Standard',
            count: '{count} Zwecke',
            count_one: '{count} Zweck',
        },
        retention: {
            title: 'Aufbewahrungsrichtlinien',
            new: 'Neue Richtlinie',
            edit: 'Richtlinie bearbeiten',
            name: 'Bezeichnung',
            retention_days: 'Aufbewahrungstage',
            disposal_method: 'Entsorgungsmethode',
            data_categories: 'Datenkategorien',
            activities: 'Tätigkeiten',
            review_frequency: 'Überprüfungshäufigkeit',
            review_frequency_days: 'Überprüfungshäufigkeit (Tage)',
            no_policies: 'Keine Aufbewahrungsrichtlinien gefunden',
            confirm_delete: '„{name}" löschen?',
            count: '{count} Richtlinien',
            count_one: '{count} Richtlinie',
            days: 'Tage',
            disposal_deletion: 'Löschung',
            disposal_anonymization: 'Anonymisierung',
            disposal_archival: 'Archivierung',
            review_overdue: 'Überprüfung überfällig',
            review_status: 'Überprüfungsstatus',
            review_ok: 'Im Plan',
            department: 'Abteilung',
            all_departments: 'Alle Abteilungen',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Datenfluss-Ansicht',
            view_list: 'Liste',
            view_map: 'Karte',
            title: 'Datenflüsse',
            new: 'Neuer Fluss',
            edit: 'Fluss bearbeiten',
            name: 'Bezeichnung',
            from_system: 'Quellsystem',
            to_system: 'Zielsystem',
            flow_type: 'Flusstyp',
            cross_border: 'Grenzüberschreitend',
            risk_level: 'Risikostufe',
            mechanism: 'Mechanismus',
            transfer_mechanism: 'Übertragungsmechanismus',
            frequency: 'Häufigkeit',
            source_country: 'Herkunftsland',
            destination_country: 'Zielland',
            transfer_safeguard: 'Übertragungsschutzmaßnahme',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Alle Risikostufen',
            no_flows: 'Keine Datenflüsse gefunden',
            confirm_archive: '„{name}" archivieren?',
            count: '{count} Flüsse',
            count_one: '{count} Fluss',
            status_active: 'Aktiv',
            status_inactive: 'Inaktiv',
            status_under_review: 'In Überprüfung',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Dateiübertragung',
            mechanism_database_replication: 'Datenbankreplikation',
            mechanism_manual: 'Manuell',
            mechanism_email: 'E-Mail',
            mechanism_other: 'Sonstiges',
            frequency_real_time: 'Echtzeit',
            frequency_hourly: 'Stündlich',
            frequency_daily: 'Täglich',
            frequency_weekly: 'Wöchentlich',
            frequency_monthly: 'Monatlich',
            frequency_on_demand: 'Auf Anfrage',
            frequency_one_time: 'Einmalig',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Rechtsordnung',
        },
        activities: {
            title: 'Verarbeitungstätigkeiten',
            new: 'Neue Tätigkeit',
            edit: 'Tätigkeit bearbeiten',
            activity_title: 'Titel',
            category: 'Kategorie',
            legal_basis: 'Rechtsgrundlage',
            dpa_required: 'DPIA Required',
            ropa_generated: 'VVT erstellt',
            version: 'Fassung',
            generate_ropa: 'VVT erstellen',
            controller: 'Verantwortlicher',
            controller_name: 'Name des Verantwortlichen',
            controller_contact: 'Kontakt des Verantwortlichen',
            legal_basis_detail: 'Details zur Rechtsgrundlage',
            organization_info: 'Organization Information',
            org_address: 'Organisationsadresse',
            tax_code: 'Steuernummer',
            contact_person: 'Ansprechpartner',
            contact_email: 'Kontakt-E-Mail',
            contact_phone: 'Kontakttelefon',
            select_entity: 'Wählen Sie…',
            no_entities: 'Keine Artikel gefunden',
            dpo_name: 'Name des DSB',
            dpo_contact: 'Kontakt des DSB',
            processor_name: 'Name des Auftragsverarbeiters',
            processor_contact: 'Kontakt des Auftragsverarbeiters',
            data_subjects: 'Betroffene Personen',
            data_subjects_hint: 'Betroffene Personen (kommagetrennt)',
            recipients: 'Empfänger',
            recipients_hint: 'Empfänger (kommagetrennt)',
            security_measures: 'Sicherheitsmaßnahmen',
            dpia_reference: 'DSFA-Referenz',
            no_activities: 'Keine Verarbeitungstätigkeiten gefunden',
            confirm_archive: '„{name}" archivieren?',
            count: '{count} Tätigkeiten',
            count_one: '{count} Tätigkeit',
            ropa_success: 'VVT-Eintrag erstellt. Gehen Sie zu Exporte, um es herunterzuladen.',
            ropa_error: 'VVT konnte nicht erstellt werden',
            required_label: 'Erforderlich',
            status_draft: 'Entwurf',
            status_active: 'Aktiv',
            status_under_review: 'In Überprüfung',
            status_archived: 'Archiviert',
            decree_165_heading: 'Dekret 165 Datenebene (vietnamesisches Datengesetz)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Datenebene',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Datenebene',
            data_tier_regular: 'Regelmäßig',
            data_tier_important: 'Wichtig',
            data_tier_core: 'Kern',
            transfer_mechanism_label: 'Grenzüberschreitender Übertragungsmechanismus',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Referenz zur Sicherheitsbewertung',
            security_assessment_reference_placeholder: 'z.B. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Nachweisarchiv',
            new: 'Neuer Nachweis',
            edit: 'Nachweis bearbeiten',
            name: 'Bezeichnung',
            evidence_type: 'Nachweistyp',
            linked_entities: 'Verknüpfte Objekte',
            uploaded: 'Hochgeladen',
            expiry: 'Ablaufdatum',
            file_url: 'Datei-URL',
            source_mode_label: 'Nachweisquelle',
            source_mode_file: 'Datei hochladen',
            source_mode_url: 'Externe URL verknüpfen',
            attachment_label: 'Anhang',
            link_to: 'Nachweis verknüpfen mit',
            entity_type: 'Objekttyp',
            entity_id: 'Objekt-ID',
            no_evidence: 'Keine Nachweise gefunden',
            manage_links: 'Links verwalten',
            unlink: 'Verknüpfung aufheben',
            confirm_delete: 'Nachweis „{name}" löschen?',
            count: '{count} Einträge',
            count_one: '{count} Eintrag',
            type_document: 'Dokument',
            type_screenshot: 'Bildschirmfoto',
            type_policy: 'Richtlinie',
            type_contract: 'Vertrag',
            type_assessment: 'Bewertung',
            type_correspondence: 'Korrespondenz',
            type_other: 'Sonstiges',
            linked_type_system: 'System',
            linked_type_data_flow: 'Datenfluss',
            linked_type_processing_activity: 'Verarbeitungsaktivität',
            linked_type_data_category: 'Datenkategorie',
            linked_type_purpose: 'Zweck',
            linked_type_retention_policy: 'Aufbewahrungsrichtlinie',
            link_entity: 'Link zur Entität',
            link_success: 'Entität erfolgreich verknüpft',
            confirm_unlink: 'Sind Sie sicher, dass Sie die Verknüpfung dieser Entität aufheben möchten?',
            unlink_success: 'Die Verknüpfung der Entität wurde erfolgreich aufgehoben',
            no_linked_entities: 'Keine verknüpften Einheiten',
            select_entity: 'Wählen Sie…',
            no_entities: 'Keine Artikel',
        },
        exports: {
            title: 'Datenexporte',
            recent: 'Letzte Exporte',
            export_type: 'Exporttyp',
            format: 'Dateiformat',
            requested_by: 'Angefordert von',
            requested_at: 'Angefordert am',
            download: 'Herunterladen',
            csv_export: 'CSV-Export',
            csv_description: 'Daten im CSV-Format für die Tabellenkalkulationsanalyse herunterladen.',
            pdf_reports: 'PDF-Berichte',
            pdf_description: 'Formatierte Berichte für die Compliance-Dokumentation erstellen.',
            csv_systems: 'Systeminventar',
            csv_categories: 'Datenkategorien',
            csv_flows: 'Datenflüsse',
            csv_activities: 'Verarbeitungstätigkeiten',
            csv_cross_border: 'Grenzüberschreitende Flüsse',
            csv_retention_policies: 'Aufbewahrungsrichtlinien',
            csv_evidence: 'Beweisstücke',
            csv_governance_assignments: 'Governance-Aufgaben',
            pdf_ropa: 'VVT-Register',
            pdf_systems: 'Systembericht',
            pdf_cross_border: 'Grenzüberschreitender Bericht',
            pdf_flow_map: 'Flusszuordnungsdaten',
            export_failed: 'Export fehlgeschlagen',
            pdf_generated: 'PDF erfolgreich erstellt.',
            ropa: 'RoPA-Register',
            retention_policies: 'Aufbewahrungsrichtlinien',
            governance_assignments: 'Governance-Aufgaben',
            col_entity: 'Entität exportieren',
            col_description: 'Beschreibung',
            desc_ropa: 'Vollständiges ROPA-Register aller Verarbeitungsaktivitäten mit Rechtsgrundlage, DPIA-Status und Kontrollen.',
            desc_systems: 'Systeminventur mit Eigentümer, Datenkategorie-Tags und Konformitätsstatus.',
            desc_activities: 'Einzelne Verarbeitungstätigkeiten mit Verantwortlicher, Rechtsgrundlage und DPIA-Klassifizierung.',
            desc_categories: 'Datenkategorien und Vertraulichkeitsstufen, die bei Klassifizierungen und Prüfungen verwendet werden.',
            desc_purposes: 'Verarbeitungszwecke mit der für jeden Zweck erfassten Rechtsgrundlage.',
            desc_flows: 'Datenflüsse zwischen Systemen, einschließlich interner und grenzüberschreitender Routen.',
            desc_cross_border: 'Nur grenzüberschreitende Ströme, mit Übertragungsmechanismus und Risikobewertung.',
            desc_retention_policies: 'Aufbewahrungsrichtlinien, Entsorgungsmethoden und Überprüfungsrhythmus.',
            desc_evidence: 'Den Verarbeitungstätigkeiten beigefügte Nachweise: Zertifikate, DPA-Verträge, DPIA-Berichte.',
            desc_governance_assignments: 'Governance-Zuweisungen: Eigentümer, Genehmiger und ISO 27001-Referenzen.',
            unified_heading: 'Entitäten exportieren',
            unified_description: 'Exportieren Sie jede Datenzuordnungseinheit im Format CSV, XLSX, PDF oder DOCX. Alle Exporte werden im Dateibrowser gespeichert.',
            export_button_label: 'Exportieren',
            csv_download_success: 'CSV erfolgreich heruntergeladen.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Attestierungszyklen',
            create: 'Neuer Zyklus',
            name: 'Zyklusname',
            year: 'Jahr',
            deadline: 'Frist',
            scope: 'Umfang',
            status: 'Status',
            tasks: 'Abteilungsaufgaben',
            assign_tasks: 'Aufgaben zuweisen',
            department: 'Abteilung',
            assignee: 'Zugewiesener',
            progress: 'Fortschritt',
            stale_entries: 'Veraltete Einträge',
            detect_stale: 'Veraltete erkennen',
            export_bundle: 'Paket exportieren',
            no_cycles: 'Noch keine Attestierungszyklen.',
            create_success: 'Attestierungszyklus erstellt.',
            assign_success: 'Aufgaben zugewiesen.',
            complete_success: 'Aufgabe abgeschlossen.',
            stale_detected: 'Veraltete Einträge erkannt.',
            stale_systems: 'Veraltete Systeme',
            stale_flows: 'Veraltete Flüsse',
            no_stale: 'Keine veralteten Einträge erkannt.',
            stale_name: 'Name',
            stale_announcement: '{0} veraltete Systeme und {1} veraltete Flüsse gefunden',
            last_updated: 'Zuletzt aktualisiert',
            status_draft: 'Entwurf',
            status_active: 'Aktiv',
            status_completed: 'Abgeschlossen',
            task_status_pending: 'Ausstehend',
            task_status_in_progress: 'In Bearbeitung',
            task_status_completed: 'Abgeschlossen',
            scope_systems: 'Systeme',
            scope_flows: 'Datenflüsse',
            scope_activities: 'Aktivitäten',
            scope_categories: 'Kategorien',
            scope_purposes: 'Zwecke',
            scope_retention: 'Aufbewahrung',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Governance-Zuweisungen',
            assign: 'Eigentümerschaft zuweisen',
            entity_type: 'Entitätstyp',
            entity: 'Entität',
            role: 'Rolle',
            assigned_by: 'Zugewiesen von',
            assigned_at: 'Zugewiesen am',
            remove: 'Entfernen',
            no_assignments: 'Keine Governance-Zuweisungen.',
            assign_success: 'Eigentümerschaft zugewiesen.',
            remove_success: 'Zuweisung entfernt.',
            assignee: 'Zugewiesene Person',
            department: 'Abteilung',
            all_departments: 'Alle Abteilungen',
            roles: {
                data_owner: 'Dateneigentümer',
                data_steward: 'Datenverwalter',
                data_custodian: 'Datenhüter',
                informed: 'Informiert',
            },
            entity_types: {
                system: 'System',
                flow: 'Datenfluss',
                activity: 'Aktivität',
                category: 'Kategorie',
                purpose: 'Zweck',
                retention_policy: 'Aufbewahrungsrichtlinie',
            },
        },
        risk_assessment: {
            title: 'Risikobewertung',
            create: 'Neue Bewertung',
            safeguard_type: 'Schutzmaßnahmentyp',
            adequacy_status: 'Angemessenheitsstatus',
            risk_factors: 'Risikofaktoren',
            mitigations: 'Gegenmaßnahmen',
            reviewer: 'Prüfer',
            decision: 'Entscheidung',
            approve: 'Genehmigen',
            reject: 'Ablehnen',
            no_assessments: 'Keine Risikobewertungen.',
            create_success: 'Risikobewertung erstellt.',
            approve_success: 'Bewertung genehmigt.',
            reject_success: 'Bewertung abgelehnt.',
            one_per_line: 'Eins pro Zeile',
            adequacy_adequate: 'Angemessen',
            adequacy_inadequate: 'Unzureichend',
            adequacy_under_review: 'In Prüfung',
            adequacy_not_assessed: 'Nicht bewertet',
            decision_approved: 'Genehmigt',
            decision_rejected: 'Abgelehnt',
        },
        review: {
            title: 'Prüfaufgaben',
            description: 'Prüfaufgaben werden automatisch erstellt, wenn eine Datenkategorie vorgeschlagen wird. Um eine neue Aufgabe zu erstellen, gehen Sie zu Datenkategorien und klicken Sie bei einer Entwurfskategorie auf Vorschlagen.',
            count: 'Prüfaufgaben',
            summary: 'Prüfungsübersicht',
            pending: 'Ausstehend',
            resolved_today: 'Heute gelöst',
            escalated: 'Eskaliert',
            overdue: 'Überfällig (>5 Tage)',
            all_statuses: 'Alle Status',
            status_pending: 'Ausstehend',
            status_resolved: 'Gelöst',
            status_escalated: 'Eskaliert',
            category_name: 'Kategorie',
            system_name: 'System',
            owner: 'Verantwortlicher',
            resolve: 'Lösen',
            resolve_task: 'Prüfaufgabe lösen',
            resolution: 'Lösung',
            resolution_approve: 'Genehmigen',
            resolution_modify: 'Ändern und genehmigen',
            resolution_reject: 'Ablehnen',
            resolution_escalate: 'Eskalieren',
            notes: 'Lösungshinweise',
            notes_required: 'Hinweise erforderlich (mind. 10 Zeichen) für Ändern, Ablehnen oder Eskalieren',
            resolved_success: 'Prüfaufgabe gelöst',
            bulk_approve: 'Massengenehmigung',
            bulk_reject: 'Massenablehnung',
            bulk_success: '{count} Aufgaben verarbeitet',
            no_tasks: 'Keine Prüfaufgaben gefunden',
        },
        inventory: {
            title: 'Klassifizierungsinventar',
            count: 'Systeme',
            summary: 'Inventarübersicht',
            total_systems: 'Systeme gesamt',
            total_labels: 'Bezeichnungen gesamt',
            system_name: 'System',
            label_count: 'Bezeichnungen',
            approved_count: 'Genehmigt',
            pending_count: 'Ausstehend',
            no_items: 'Keine Inventareinträge gefunden',
        },
        exceptions: {
            title: 'Klassifizierungsausnahmen',
            count: 'Ausnahmen',
            all_severities: 'Alle Schweregrade',
            all_statuses: 'Alle Status',
            system_name: 'System',
            severity_label: 'Schweregrad',
            status_open: 'Offen',
            status_resolved: 'Gelöst',
            status_accepted_risk: 'Akzeptiertes Risiko',
            resolve: 'Lösen',
            resolve_exception: 'Ausnahme lösen',
            resolution_type: 'Lösungstyp',
            notes: 'Hinweise',
            justification_required: 'Begründung erforderlich (mind. 10 Zeichen) für akzeptiertes Risiko',
            resolved_success: 'Ausnahme gelöst',
            no_exceptions: 'Keine Ausnahmen gefunden',
        },
        history: {
            title: 'Klassifizierungsverlauf',
            count: 'Ereignisse',
            actor: 'Akteur',
            action: 'Aktion',
            entity_type: 'Entitätstyp',
            changes: 'Änderungen',
            view_lineage: 'Herkunft anzeigen',
            lineage_title: 'Klassifizierungsherkunft',
            no_events: 'Keine Verlaufsereignisse gefunden',
            no_lineage: 'Keine Herkunftsdaten verfügbar',
            modified: 'Geändert',
            created: 'Erstellt',
            removed: 'Entfernt',
            action_created: 'Erstellt',
            action_updated: 'Aktualisiert',
            action_archived: 'Archiviert',
            action_approved: 'Genehmigt',
            action_deprecated: 'Veraltet',
            entity_label: 'Bezeichnung',
            entity_taxonomy: 'Taxonomie',
            entity_review_task: 'Prüfaufgabe',
            entity_exception: 'Ausnahme',
            before: 'Vorher',
            after: 'Nachher',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: 'Governance-Audit-Protokoll',
        audit_trail_subtitle: 'Übergreifender Audit-Stream für Governance-Aktionen — Freigaben, Playbook-Ausführungen, Nachweisverknüpfungen und mehr.',
        audit_trail_item_count: 'Audit-Ereignisse',
        audit_trail_fetch_error: 'Audit-Ereignisse konnten nicht geladen werden.',
        audit_trail_no_events: 'Keine Audit-Ereignisse für die aktuellen Filter gefunden.',
        audit_trail_entity_type: 'Entitätstyp',
        audit_trail_entity_id: 'Entitäts-ID',
        audit_trail_action: 'Aktion',
        audit_trail_actor: 'Akteur',
        audit_trail_timestamp: 'Zeitstempel',
        audit_trail_date_from: 'Von',
        audit_trail_date_to: 'Bis',
        audit_trail_no_rows_to_export: 'Keine Zeilen zum Exportieren.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Dienstleister',
            subprocessors: 'Unterauftragsverarbeiter',
            cross_border: 'Grenzüberschreitend',
            drift: 'Abweichungsmonitor',
            scores: 'Compliance-Bewertung',
            playbooks: 'Spielbücher',
            approvals: 'Genehmigungen',
            audit_trail: 'Audit-Trail',
            board_report: 'Vorstandsbericht',
            evidence: 'Nachweise',
            onboarding: 'Onboarding-Pipeline',
            dpa_reviews: 'DPA-Prüfungen',
            periodic_reviews: 'Periodische Prüfungen',
            offboarding: 'Offboarding',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Dienstleisterverzeichnis',
            new: 'Neuer Dienstleister',
            edit: 'Dienstleister bearbeiten',
            name: 'Bezeichnung',
            jurisdiction: 'Rechtsordnung',
            dpa_status: 'AVV-Status',
            risk_level: 'Risikostufe',
            onboarding_status: 'Onboarding-Status',
            contact_email: 'Kontakt-E-Mail',
            contact_name: 'Kontaktname',
            contact_phone: 'Kontakttelefon',
            website: 'Webseite',
            services_csv: 'Dienstleistungen (kommagetrennt)',
            country: 'Land',
            country_placeholder: 'z.B. DE',
            last_reviewed: 'Zuletzt überprüft',
            offboard: 'Beenden',
            offboard_vendor: 'Dienstleister beenden',
            offboard_reason: 'Grund für Beendigung',
            no_vendors: 'Keine Dienstleister gefunden',
            vendor_count: '{count} Dienstleister',
            all_dpa_status: 'Alle AVV-Status',
            all_risk_levels: 'Alle Risikostufen',
            all_onboarding: 'Alle Onboarding-Status',
            fetch_error: 'Fehlgeschlagen. Bitte erneut versuchen.',
            save_error: 'Dienstleister konnte nicht gespeichert werden',
            offboard_error: 'Beendigung fehlgeschlagen',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Auftragsverarbeiter',
            sub_processor: 'Unterauftragsverarbeiter',
            controller: 'Verantwortlicher',
            joint_controller: 'Gemeinsam Verantwortlicher',
        },
        onboarding_statuses: {
            pending_review: 'Überprüfung Ausstehend',
            approved: 'Genehmigt',
            active: 'Aktiv',
            suspended: 'Ausgesetzt',
            offboarded: 'Beendet',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Nicht Erforderlich',
            required_missing: 'Erforderlich Fehlend',
            draft: 'Entwurf',
            under_review: 'In Überprüfung',
            signed: 'Unterzeichnet',
            expired: 'Abgelaufen',
        },
        risk_levels: {
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Niedrig',
        },
        approval_statuses: {
            pending: 'Ausstehend',
            approved: 'Genehmigt',
            rejected: 'Abgelehnt',
            expired: 'Abgelaufen',
        },
        approval_action_types: {
            vendor_onboarding: 'Lieferantenanbindung',
            vendor_offboarding: 'Lieferantenabwicklung',
            cross_border_acceptance: 'Grenzüberschreitende Genehmigung',
            playbook_activation: 'Playbook-Aktivierung',
            drift_accept_risk: 'Abweichung Risiko Akzeptiert',
            activity_activation: 'Aktivitätsaktivierung',
        },
        drift_check_types: {
            dpa_expiry: 'DPA-Ablauf',
            dpa_missing: 'DPA Fehlend',
            review_overdue: 'Überprüfung Überfällig',
            cross_border_gap: 'Grenzüberschreitende Lücke',
            sub_processor_chain: 'Unterauftragsverarbeiterkette',
            risk_assessment_overdue: 'Risikobewertung Überfällig',
            retention_policy_missing: 'Aufbewahrungsrichtlinie Fehlend',
            evidence_expiring: 'Nachweis Läuft Ab',
        },
        drift_statuses: {
            open: 'Offen',
            acknowledged: 'Bestätigt',
            in_progress: 'In Bearbeitung',
            resolved: 'Gelöst',
            accepted_risk: 'Akzeptiertes Risiko',
        },
        drift_entity_types: {
            vendor: 'Lieferant',
            system: 'IT-System',
            data_flow: 'Datenfluss',
            processing_activity: 'Verarbeitungsaktivität',
            evidence_item: 'Nachweis',
        },
        playbook_statuses: {
            draft: 'Entwurf',
            active: 'Aktiv',
            archived: 'Archiviert',
        },
        playbook_action_types: {
            delete_data: 'Daten Löschen',
            verify_deletion: 'Löschung Verifizieren',
            notify_vendor: 'Lieferant Benachrichtigen',
            confirm_receipt: 'Empfang Bestätigen',
            document_evidence: 'Dokumentieren',
            other: 'Sonstiges',
        },
        playbook_target_types: {
            system: 'IT-System',
            vendor: 'Lieferant',
        },
        dpa_chain_statuses: {
            complete: 'Vollständig',
            incomplete: 'Unvollständig',
            unknown: 'Unbekannt',
        },
        sub_processor_statuses: {
            active: 'Aktiv',
            terminated: 'Beendet',
        },
        evidence_types: {
            dpa_agreement: 'DPA-Vereinbarung',
            risk_assessment: 'Risikobewertung',
            audit_report: 'Prüfbericht',
            certification: 'Zertifizierung',
            deletion_confirmation: 'Löschbestätigung',
            training_record: 'Schulungsnachweis',
            incident_report: 'Vorfallbericht',
            document: 'Dokument',
            screenshot: 'Bildschirmfoto',
            policy: 'Richtlinie',
            contract: 'Vertrag',
            assessment: 'Bewertung',
            correspondence: 'Korrespondenz',
            other: 'Sonstiges',
        },
        linkable_entity_types: {
            vendor: 'Lieferant',
            transfer_mechanism: 'Übertragungsmechanismus',
            playbook: 'Leitfaden',
            execution: 'Ausführung',
            system: 'IT-System',
            activity: 'Aktivität',
        },
        subprocessors: {
            title: 'Unterauftragsverarbeiter-Beziehungen',
            new: 'Neue Verknüpfung',
            vendor: 'Dienstleister',
            subprocessor: 'Unterauftragsverarbeiter',
            relationship_type: 'Beziehungstyp',
            countries: 'Länder',
            countries_csv: 'Länder (kommagetrennt)',
            dpa_chain: 'DPA-Kette',
            dpa_chain_status: 'DPA-Kettenstatus',
            no_processors: 'Keine Auftragsverarbeiter gefunden.',
            no_subprocessors: 'Keine Unterauftragsverarbeiter gefunden.',
            processor_vendor: 'Auftragsverarbeiter-Dienstleister',
            select_processor: 'Auftragsverarbeiter auswählen',
            select_vendor: 'Dienstleister auswählen',
            services: 'Dienstleistungen',
            sub_processor_vendor_id: 'Unterauftragsverarbeiter-ID',
            terminate: 'Beenden',
            confirm_terminate: 'Sind Sie sicher, dass Sie diese Beziehung beenden möchten?',
            create_error: 'Verknüpfung konnte nicht erstellt werden.',
            fetch_error: 'Unterauftragsverarbeiter konnten nicht abgerufen werden.',
            processor: 'Auftragsverarbeiter',
            services_csv: 'Dienstleistungen (kommagetrennt)',
            terminate_error: 'Beendigung fehlgeschlagen.',
            version_history_heading: 'Unterprozessor-Änderungsversionierung',
            change_tracking_note: 'Alle Änderungen der Unterauftragsverarbeiterbeziehung werden versioniert. Bei jeder Änderung wird der Vorher-/Nachher-Status für den Prüfpfad aufgezeichnet.',
            history_retention_note: 'Der Änderungsverlauf wird für den gesamten Datenaufbewahrungszeitraum aufbewahrt. Überprüfen Sie Änderungsprotokolle über die Detailansicht des Anbieters.',
            vendor_risk_cross_view_heading: 'Unterauftragsverarbeiter-Register (Vendor Risk)',
            vendor_risk_cross_view_source: 'Quelle: Vendor Risk Register',
            vendor_risk_cross_view_empty: 'Keine Einträge im Vendor Risk-Register.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Grenzüberschreitender Steuerungsbericht',
            flows: 'Grenzüberschreitende Flüsse',
            transfers: 'Grenzüberschreitende Übermittlungen',
            detected_gaps: 'Erkannte Lücken',
            safeguards: 'Schutzmaßnahmen',
            cross_border_vendors: 'Grenzüberschreitende Dienstleister',
            destination: 'Ziel',
            no_cross_border_vendors: 'Keine grenzüberschreitenden Dienstleister gefunden.',
            no_safeguard: 'Keine Schutzmaßnahme',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Verarbeitungsstandorte',
            export_error: 'Bericht konnte nicht exportiert werden.',
            export_pdf: 'PDF exportieren',
            export_success: 'Bericht erfolgreich exportiert.',
            exporting: 'Wird exportiert...',
            fetch_error: 'Grenzüberschreitende Daten konnten nicht abgerufen werden.',
            source: 'Quelle',
            pdpl_p2_heading: 'PDPL-P2 Einhaltung grenzüberschreitender Übermittlungen',
            dossier_prep_note: 'Dossiervorbereitung: Stellen Sie vor der Einreichung eine Folgenabschätzung, eine Rechtsgrundlage und eine Schutzdokumentation zusammen.',
            filing_tracking_note: 'Einreichungsverfolgung: Überwachen Sie den Einreichungsstatus bei der Behörde (PDPC) vom ersten Antrag bis zur Genehmigung.',
            supplement_loop_note: 'Ergänzungsschleife: Reagieren Sie auf Informationsanfragen der Behörde und reichen Sie bei Bedarf geänderte Dossiers erneut ein.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Anbietersuche',
            vendor_search_placeholder: 'Nach Anbieternamen suchen…',
            jurisdiction_filter_label: 'Gerichtsbarkeit',
            filings_section_heading: 'Grenzüberschreitende Einreichungen',
            filings_empty: 'Noch keine Einreichungen. Erstellen Sie eines, um ein Dossier zu erstellen.',
            filings_create_button: 'Neue Einreichung',
            filings_table_id: 'Anmelde-ID',
            filings_table_jurisdiction: 'Gerichtsstand',
            filings_table_type: 'Formulartyp',
            filings_table_status: 'Status',
            filings_table_updated: 'Aktualisiert',
            filings_table_open: 'Offen',
            filings_detail_title: 'Grenzüberschreitende Einreichung',
            filings_back_to_list: 'Zurück zu Cross-Border',
            filings_status_heading: 'Lebenszyklus',
            filings_evidence_heading: 'Beweise',
            filings_history_heading: 'Audit-Trail',
            filings_field_filing_id: 'Anmelde-ID',
            filings_field_jurisdiction: 'Gerichtsstand',
            filings_field_type: 'Formulartyp',
            filings_field_status: 'Status',
            filings_field_reference: 'Autoritätsreferenz',
            filings_field_mps_receipt: 'MPS Quittung',
            filings_field_submitted_at: 'Eingereicht bei',
            filings_field_acknowledged_at: 'Bestätigt bei',
            filings_field_archived_at: 'Archiviert unter',
            filings_field_version: 'Version',
            filings_field_notes: 'Notizen',
            filings_action_advance: 'Vorabstatus',
            filings_action_reject: 'Ablehnen',
            filings_action_archive: 'Archiv',
            filings_action_submit: 'Mark eingereicht',
            filings_action_acknowledge: 'Mark bestätigte',
            filings_action_resume_draft: 'Lebenslauf als Entwurf',
            filings_status_draft: 'Entwurf',
            filings_status_assessed: 'Bewertet',
            filings_status_pending_review: 'Ausstehende Überprüfung',
            filings_status_ready_to_file: 'Bereit zur Einreichung',
            filings_status_submitted: 'Eingereicht',
            filings_status_acknowledged: 'Anerkannt',
            filings_status_rejected: 'Abgelehnt',
            filings_status_archived: 'Archiviert',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Neue grenzüberschreitende Anmeldung',
            filings_new_form_jurisdiction: 'Gerichtsstand',
            filings_new_form_type: 'Formulartyp',
            filings_new_form_notes: 'Notizen (optional)',
            filings_new_submit: 'Erstellen',
            filings_new_cancel: 'Abbrechen',
            filings_reason_label: 'Grund',
            filings_reference_label: 'Referenznummer der Behörde',
            filings_mps_receipt_label: 'MPS Belegreferenz',
            filings_create_error: 'Die Datei konnte nicht erstellt werden.',
            filings_create_success: 'Ablage erstellt.',
            filings_update_error: 'Die Einreichung konnte nicht aktualisiert werden.',
            filings_update_success: 'Einreichung aktualisiert.',
            filings_fetch_error: 'Die Einreichungen konnten nicht abgerufen werden.',
            filings_archive_confirm: 'Diese Einreichung archivieren? Dies kann nicht rückgängig gemacht werden.',
            filings_status_rejected_detour: 'Die Einreichung wurde nach Prüfung abgelehnt. Als Entwurf fortsetzen, um den Flow neu zu starten.',
            recent_exports_heading: 'Aktuelle Exporte',
            recent_exports_empty: 'Noch keine Exporte. Klicken Sie auf „PDF exportieren“, um einen zu generieren.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Konfigurationsabweichungsmonitor',
            drift_type: 'Abweichungstyp',
            severity: 'Schweregrad',
            detected_at: 'Erkannt am',
            resolve: 'Beheben',
            all_check_types: 'Alle Prüfungstypen',
            all_entity_types: 'Alle Objekttypen',
            event_count: 'Ereignisanzahl',
            accept_risk: 'Risiko akzeptieren',
            acknowledge: 'Bestätigen',
            assign_error: 'Zuweisung fehlgeschlagen.',
            assign_event: 'Zuweisen',
            assigned: 'Zugewiesen',
            enter_resolution_notes: 'Lösungshinweise eingeben...',
            enter_user_id: 'Benutzer-ID eingeben...',
            entity: 'Objekt',
            fetch_error: 'Abweichungsereignisse konnten nicht abgerufen werden.',
            no_events: 'Keine Abweichungsereignisse gefunden.',
            resolution_notes: 'Lösungshinweise',
            update_status_error: 'Status konnte nicht aktualisiert werden.',
        },
        scores: {
            title: 'Compliance-Bewertungsübersicht',
            overall: 'Gesamt-Compliance-Bewertung',
            breakdown: 'Bewertung nach Modul',
            trend: 'Historischer Trend',
            module: 'Modul',
            score: 'Bewertung',
            grade: 'Note',
            calculated_at: 'Berechnet am',
            generate_board_report: 'Vorstandsbericht erstellen',
            lang_english: 'Englisch',
            lang_vietnamese: 'Vietnamesisch',
            fetch_error: 'Compliance-Bewertungen konnten nicht abgerufen werden.',
            generate_report: 'Bericht erstellen',
            generating: 'Wird erstellt...',
            recommendations: 'Empfehlungen',
            report_error: 'Bericht konnte nicht erstellt werden.',
            factor_owner_assigned: 'Eigentümer zugewiesen',
            factor_data_categories_linked: 'Datenkategorien verknüpft',
            factor_purposes_linked: 'Zwecke verknüpft',
            factor_data_flows_documented: 'Datenflüsse dokumentiert',
            factor_processing_activity_linked: 'Verarbeitungstätigkeit verknüpft',
            factor_dpa_status: 'DPA-Status',
            factor_retention_policy_linked: 'Aufbewahrungsrichtlinie verknüpft',
            factor_last_reviewed: 'Letzte Überprüfung innerhalb der Frist',
            factor_risk_assessment_current: 'Risikobewertung aktuell',
            factor_sub_processors_documented: 'Unterauftragsverarbeiter dokumentiert',
            factor_cross_border_safeguards: 'Grenzüberschreitende Schutzmaßnahmen',
            factor_contact_info_complete: 'Kontaktinformationen vollständig',
            factor_onboarding_approved: 'Onboarding genehmigt',
            factor_avg_system_score: 'Durchschnittlicher Systempunktwert',
            factor_avg_vendor_score: 'Durchschnittlicher Anbieterpunktwert',
            factor_processing_activity_coverage: 'Abdeckung der Verarbeitungstätigkeiten',
            factor_ropa_completeness: 'RoPA-Vollständigkeit',
            factor_open_drift_events: 'Offene Abweichungsereignisse',
        },
        playbooks: {
            title: 'Lösch-Playbooks',
            new: 'Neues Playbook',
            edit: 'Playbook bearbeiten',
            steps: 'Schritte',
            add_step: 'Schritt hinzufügen',
            remove_step: 'Schritt entfernen',
            data_categories_csv: 'Datenkategorien (kommagetrennt)',
            estimated_duration: 'Geschätzte Dauer',
            estimated_duration_label: 'Geschätzte Dauer',
            evidence_description: 'Nachweisbeschreibung',
            no_playbooks: 'Keine Playbooks gefunden.',
            playbook_count: 'Playbook-Anzahl',
            requires_evidence: 'Nachweis erforderlich',
            step_label: 'Schrittbezeichnung',
            target_name: 'Zielname',
            target_type: 'Zieltyp',
            action_type: 'Aktionstyp',
            archive_error: 'Archivierung fehlgeschlagen.',
            confirm_archive: 'Sind Sie sicher, dass Sie dieses Playbook archivieren möchten?',
            data_categories: 'Datenkategorien',
            days: 'Tage',
            fetch_error: 'Playbooks konnten nicht abgerufen werden.',
            sla_days: 'SLA (Tage)',
            target_id: 'Ziel-ID',
            template_class_heading: 'Vorlagenklassifizierung',
            official: 'Offiziell',
            internal: 'Intern',
            classification: 'Klassifizierung',
            official_note: 'Offiziell: behördlich vorgeschriebene Vorlagen, abgestimmt auf PDPL, GDPR oder branchenspezifische Anforderungen.',
            internal_note: 'Intern: Organisationsspezifische Verfahren, die nicht aus regulatorischen Vorlagen abgeleitet sind.',
            active_executions_heading: 'Aktive Ausführungen',
            no_active_executions: 'Keine aktiven Ausführungen.',
            playbook_label: 'Spielbuch',
            ticket_id_label: 'Ticket',
            progress_label: 'Fortschritt',
            actions_label: 'Aktionen',
            view_steps_label: 'Schritte anzeigen',
            execution_detail_title: 'Ausführungsdetails',
            playbook_missing: 'Playbook nicht gefunden.',
            evidence_required_label: 'Nachweis erforderlich',
            upload_evidence_label: 'Nachweis hochladen',
            mark_complete_label: 'Als abgeschlossen markieren',
            evidence_required_toast: 'Bitte Nachweis anhängen, bevor Schritt abgeschlossen wird.',
            evidence_blocking_message: 'Eine Nachweisdatei anhängen, um fortzufahren.',
            step_completed_toast: 'Schritt abgeschlossen.',
            executions_fetch_error: 'Ausführungen konnten nicht geladen werden.',
            step_evidence_default_name: 'Nachweis für Ausführungsschritt',
            attached_evidence_label: 'Angehängte Nachweise',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Genehmigungsworkflows',
            new: 'Neue Genehmigung',
            edit: 'Genehmigung bearbeiten',
            request_type: 'Antragsart',
            requester: 'Antragsteller',
            approve: 'Genehmigen',
            reject: 'Ablehnen',
            justification: 'Begründung',
            all_action_types: 'Alle Aktionstypen',
            decided_by: 'Entschieden von',
            no_approvals: 'Keine Genehmigungen gefunden.',
            approval_count: 'Genehmigungsanzahl',
            approval_notes_optional: 'Anmerkungen (optional)',
            approve_error: 'Genehmigung fehlgeschlagen.',
            approve_request: 'Antrag genehmigen',
            decision: 'Entscheidung',
            entity: 'Objekt',
            fetch_error: 'Genehmigungen konnten nicht abgerufen werden.',
            reject_error: 'Ablehnung fehlgeschlagen.',
            reject_request: 'Antrag ablehnen',
            rejection_notes_required: 'Ablehnungsbegründung erforderlich',
            rejection_notes_required_msg: 'Bitte geben Sie einen Grund für die Ablehnung an.',
            requested_at: 'Angefordert am',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Unterstützender Nachweis (optional)',
            decision_evidence_name: 'Entscheidungsnachweis',
        },
        evidence: {
            title: 'Steuerungsnachweise',
            new: 'Neuer Nachweis',
            link_to: 'Verknüpfen mit',
            entity_id_placeholder: 'Objekt-ID',
            file_path_placeholder: '/pfad/zur/datei',
            evidence_label: 'Nachweisbezeichnung',
            file_path: 'Dateipfad',
            source_mode_label: 'Nachweisquelle',
            source_mode_file: 'Datei hochladen',
            source_mode_url: 'Externe URL verknüpfen',
            attachment_label: 'Anhang',
            item_count: 'Anzahl Einträge',
            link_button: 'Nachweis verknüpfen',
            confirm_delete: 'Sind Sie sicher, dass Sie diesen Nachweis löschen möchten?',
            enter_entity_id: 'Objekt-ID eingeben...',
            entity_id: 'Objekt-ID',
            entity_type: 'Objekttyp',
            expires: 'Läuft ab',
            expires_at: 'Läuft ab am',
            fetch_error: 'Nachweise konnten nicht abgerufen werden.',
            link_error: 'Nachweis konnte nicht verknüpft werden.',
            no_evidence: 'Keine Nachweise gefunden.',
            tags_csv: 'Tags (kommagetrennt)',
            uploaded: 'Hochgeladen',
            url: 'URL',
            framework_tag_heading: 'Mit Framework markierte Evidenz',
            framework_tag_note: 'Jeder Beweisdatensatz erfasst Framework-Code, Akteur und Zeitstempel. Filtern Sie nach Framework, um prüfungsrelevante Datensätze zu isolieren.',
            framework_code: 'Rahmencode',
            filter_by_framework: 'Nach Framework-Code filtern...',
            actor: 'Schauspieler',
        },
        certification: {
            not_applicable: 'Nicht zutreffend',
            not_certified: 'Nicht zertifiziert',
            pending: 'Ausstehend',
            certified: 'Zertifiziert',
            revoked: 'Widerrufen',
        },
        onboarding_pipeline: {
            title: 'Onboarding-Pipeline',
            new_workflow: 'Neuer Onboarding-Workflow',
            vendor_id: 'Lieferanten-ID',
            stages: 'Phasen',
            current_stage: 'Aktuelle Phase',
            advance: 'Phase vorantreiben',
            advance_success: 'Phase erfolgreich vorangetrieben',
            workflow_status: 'Workflow-Status',
            initiated: 'Eingeleitet',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            cancelled: 'Abgebrochen',
            no_workflows: 'Keine Onboarding-Workflows gefunden',
            stages_placeholder: 'Phase 1, Phase 2, Phase 3',
            assigned_to: 'Zugewiesen an',
            notes: 'Notizen',
            pdpl_lifecycle_heading: 'PDPL Lieferantenlebenszyklus – Onboarding',
            due_diligence_note: 'Vor der Einbindung eines Auftragsverarbeiters gemäß PDPL ist eine Due-Diligence-Phase obligatorisch. Überprüfen Sie den Umfang der Datenverarbeitung, die Rechtsgrundlage und die Sicherheitsmaßnahmen.',
            contract_review_note: 'Bei der Vertragsprüfung müssen DPA-Klauseln, Beschränkungen der Datenaufbewahrung und Beschränkungen für Unterauftragsverarbeiter bestätigt werden.',
            create_dpa_review_cta: 'DPA-Prüfung erstellen',
            dpa_review_linked: 'DPA-Prüfung verknüpft',
        },
        dpa_review: {
            title: 'DPA-Prüfungen',
            new_review: 'Neue DPA-Prüfung',
            review_type: 'Prüfungstyp',
            findings: 'Ergebnisse',
            outcome: 'Ergebnis',
            complete_review: 'Prüfung abschließen',
            complete_success: 'Prüfung erfolgreich abgeschlossen',
            link_assessment: 'Sicherheitsbewertung verknüpfen',
            link_success: 'Bewertung erfolgreich verknüpft',
            assessment_id: 'Bewertungs-ID',
            combined_title: 'Kombinierte Bewertung',
            no_reviews: 'Keine DPA-Prüfungen gefunden',
            telecom_disclosure_heading: 'Offenlegungs-Governance im Telekommunikationsbereich',
            disclosure_trigger_note: 'Durch Offenlegung ausgelöste Überprüfungen erfassen Ereignisse zur Weitergabe von Daten von Telekommunikationsteilnehmern gemäß dem VN-Telekommunikationsgesetz.',
            telecom_t2_note: 'Vor der Offenlegung von Abonnentendaten an Behörden ist eine TELECOM-T2-Stufengenehmigung erforderlich.',
            quarterly_log_note: 'Nutzen Sie das vierteljährliche Protokoll, um alle Offenlegungsereignisse zu dokumentieren und einen kontinuierlichen Prüfpfad zu führen.',
        },
        periodic_review: {
            title: 'Periodische Prüfungen',
            new_review: 'Neue periodische Prüfung',
            review_type: 'Prüfungstyp',
            due_date: 'Fälligkeitsdatum',
            next_review: 'Nächster Prüfungstermin',
            trigger_reassessment: 'Neubewertung auslösen',
            reassessment_reason: 'Grund für Neubewertung',
            reassessment_success: 'Neubewertung erfolgreich ausgelöst',
            complete_success: 'Prüfung erfolgreich abgeschlossen',
            scheduled: 'Geplant',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            overdue: 'Überfällig',
            material_change: 'Wesentliche Änderung',
            no_reviews: 'Keine periodischen Prüfungen gefunden',
            mark_complete: 'Als abgeschlossen markieren',
            pdpl_periodic_heading: 'PDPL Lieferantenlebenszyklus – Regelmäßige Überprüfungen',
            scheduled_review_note: 'Regelmäßige Überprüfungen müssen mindestens einmal im Jahr für alle aktiven Auftragsverarbeiter angesetzt werden. Überfällige Überprüfungen lösen Compliance-Warnungen aus.',
            material_change_note: 'Wesentliche Änderungen in der Datenverarbeitung erfordern unabhängig vom Zeitplan eine sofortige Neubewertung.',
            overdue_badge: 'Überfällig',
            overdue_aria: 'Überfällige Prüfung',
            only_overdue_filter: 'Nur überfällige',
        },
        offboarding: {
            title: 'Lieferanten-Offboarding',
            initiate: 'Offboarding einleiten',
            initiate_success: 'Offboarding erfolgreich eingeleitet',
            reason: 'Offboarding-Grund',
            data_return_required: 'Datenrückgabe erforderlich',
            verify_data: 'Datenrückgabe überprüfen',
            verify_success: 'Datenrückgabe erfolgreich überprüft',
            sign_off: 'Abzeichnen',
            sign_off_success: 'Offboarding erfolgreich abgezeichnet',
            status_initiated: 'Eingeleitet',
            status_data_pending: 'Datenrückgabe ausstehend',
            status_data_verified: 'Datenrückgabe überprüft',
            status_completed: 'Abgeschlossen',
            status_cancelled: 'Abgebrochen',
            task_id: 'Aufgaben-ID',
            no_tasks: 'Keine Offboarding-Aufgaben gefunden',
            evidence_id: 'Nachweis-ID',
            pdpl_offboarding_heading: 'PDPL Lieferantenlebenszyklus – Offboarding und Löschung',
            deletion_capture_note: 'Beim Offboarding muss die Bestätigung der Datenlöschung vom Anbieter eingeholt werden. Überprüfen Sie die Datenrückgabe vor der Freigabe.',
            evidence_attachment_note: 'Fügen Sie Löschnachweise (Zertifikate, Screenshots, Lieferantenbestätigungen) bei, um den Offboarding-Datensatz zu vervollständigen.',
            verify_mode_label: 'Verifizierungsmethode',
            verify_mode_upload: 'Neue Datei hochladen',
            verify_mode_link: 'Vorhandenen Nachweis verknüpfen',
            verify_mode_none: 'Keine Daten zurückzugeben',
            upload_data_return_evidence: 'Nachweis für Datenrückgabe hochladen',
            no_data_justification_label: 'Begründung',
            no_data_justification_placeholder: 'Erkläre, warum keine Daten zurückzugeben sind',
            no_data_justification_hint: 'Mindestens 20 Zeichen',
            upload_required_toast: 'Bitte Datei anhängen, um fortzufahren',
            evidence_link_required_toast: 'Bitte Nachweis auswählen, um fortzufahren',
            no_data_justification_required_toast: 'Bitte Begründung mit mindestens 20 Zeichen',
            data_return_evidence_name: 'Nachweis für Datenrückgabe',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Erstbewertung',
            annual: 'Jährlich',
            renewal: 'Erneuerung',
            incident_triggered: 'Vorfallsbedingt',
        },
        periodic_review_types: {
            initial: 'Erstprüfung',
            annual: 'Jährlich',
            renewal: 'Erneuerung',
            incident_triggered: 'Vorfallsbedingt',
            material_change: 'Wesentliche Änderung',
            disclosure_triggered: 'Offenlegungsbedingt',
            quarterly_log: 'Quartalsbericht',
        },
        dpa_review_statuses: {
            pending: 'Ausstehend',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            cancelled: 'Storniert',
        },
        onboarding_stage_names: {
            due_diligence: 'Sorgfaltsprüfung',
            contract_review: 'Vertragsprüfung',
            security_assessment: 'Sicherheitsbewertung',
            data_mapping: 'Datenzuordnung',
            approval: 'Genehmigung',
            integration: 'Integration',
        },
        access_denied_title: 'Zugriff verweigert',
        access_denied: 'Sie haben keine Berechtigung, diese Seite anzuzeigen. Bitte kontaktieren Sie Ihren Administrator.',
    },
    consent: {
        nav: {
            policies: 'Richtlinien',
            purposes: 'Zweckkatalog',
            workflows: 'Arbeitsabläufe',
            snapshots: 'CMP-Snapshots',
            change_awareness: 'Änderungserkennung',
            audit: 'Einwilligungs-Audit',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Lückenanalyse',
            page_title: 'Analyse der Einwilligungslücke',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Rahmenwerke',
            org_flags_label: 'Organisationskontext',
            flag_withdrawal: 'Verfügt über einen dokumentierten globalen Auszahlungsmechanismus',
            flag_parental: 'Hat die Zustimmung der Eltern für unter 16-Jährige',
            flag_cross_border: 'Hat grenzüberschreitende Einreichungen registriert',
            run_label: 'Lauflückenanalyse',
            running_label: 'Laufen…',
            requires_advisor_label: 'Erfordert ai_advisor:Chat-Berechtigung.',
            results_heading: 'Lücken aufgetaucht ({count})',
            tokens_label: '≈ {count} Token',
            empty_label: 'Für die ausgewählten Frameworks wurden keine Lücken erkannt.',
            remediation_label: 'Empfohlene Abhilfe:',
            dismiss_label: 'Als nicht zutreffend ablehnen',
            resolve_label: 'Lösen',
            dismissed_label: 'Als nicht zutreffend abgewiesen',
            resolved_label: 'Gelöst',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Einwilligungs-Workflows importieren',
            nav_title: 'Massenimport',
            description: 'Laden Sie eine CSV-Datei hoch, um Einwilligungs-Workflows in Massen zu importieren.',
            select_file: 'CSV-Datei auswählen',
            file_help: 'CSV mit Überschriften, die den Workflow-Feldern entsprechen.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Erforderliche Spalten: Workflow-Typ, Betroffene Person, Kanal.',
            file_too_large: 'Datei zu groß. Maximale Größe ist 2MB.',
            preview: 'Vorschau',
            columns_detected: 'Erkannte Spalten: {count}',
            column_mapping: 'Spaltenzuordnung',
            import_button: 'Importieren',
            import_complete: 'Import abgeschlossen',
            import_error: 'Import fehlgeschlagen',
            result: 'Importergebnis',
            total: 'Gesamt',
            imported: 'Importiert',
            skipped: 'Übersprungen',
            failed: 'Fehlgeschlagen',
            errors: 'Fehler',
            error_row: 'Zeile',
            error_field: 'Feld',
            error_message: 'Nachricht',
            history: 'Importverlauf',
            no_history: 'Kein Importverlauf.',
            history_file: 'Dateiname',
            history_date: 'Datum',
            create_profiles_label: 'Profile für Betroffene Personen für neue Subjekte erstellen',
            create_profiles_help: 'Importe ohne passendes Profil erstellen automatisch eines. Deaktivieren, um nur Workflows zu importieren.',
            profiles_created: 'Profile erstellt',
            profiles_matched: 'Profile zugeordnet',
            subject_kind_help: 'Optionale Spalten: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Ungültige E-Mail-Adresse in Spalte subject_email.',
            error_subject_unknown_kind: 'Unbekannter Wert für subject_kind. Erwartet: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key ist erforderlich, wenn subject_kind=external_ref',
        },
        policies: {
            title: 'Einwilligungsrichtlinien',
            new: 'Neue Richtlinie',
            edit: 'Richtlinie bearbeiten',
            name: 'Bezeichnung',
            version: 'Fassung',
            policy_text: 'Richtlinientext',
            publish: 'Veröffentlichen',
            versions: 'Versionen',
            hide_versions: 'Versionen ausblenden',
            submit_for_review: 'Zur Prüfung einreichen',
            suspend: 'Aussetzen',
            retire: 'Zurückziehen',
            jurisdictions: 'Rechtsordnungen',
            jurisdictions_csv: 'Rechtsordnungen (kommagetrennt)',
            approval_mode: 'Genehmigungsmodus',
            reviewer_ids: 'Prüfer-IDs (kommagetrennt)',
            tags_csv: 'Tags (kommagetrennt)',
            effective_date: 'Gültigkeitsdatum',
            review_date: 'Überprüfungsdatum',
            consent_model_statement: 'Einwilligungsmodell-Erklärung',
            consent_model_placeholder: 'Optionale Einwilligungsmodell-Erklärung...',
            default_settings: 'Standardeinstellungen (JSON)',
            changes: 'Änderungen',
            fetch_error: 'Fehler beim Abrufen der Einwilligungsrichtlinien. Bitte versuchen Sie es erneut.',
            save_error: 'Fehler beim Speichern der Einwilligungsrichtlinie. Bitte versuchen Sie es erneut.',
            submit_review_error: 'Fehler beim Einreichen der Richtlinie zur Prüfung. Bitte versuchen Sie es erneut.',
            activate_error: 'Fehler beim Aktivieren der Richtlinie. Bitte versuchen Sie es erneut.',
            suspend_error: 'Fehler beim Aussetzen der Richtlinie. Bitte versuchen Sie es erneut.',
            retire_error: 'Fehler beim Zurückziehen der Richtlinie. Bitte versuchen Sie es erneut.',
            submit_review_success: 'Richtlinie zur Überprüfung eingereicht.',
            activate_success: 'Richtlinie aktiviert.',
            suspend_success: 'Richtlinie ausgesetzt.',
            retire_success: 'Die Police wurde zurückgezogen.',
            versions_error: 'Fehler beim Abrufen der Richtlinienversionen. Bitte versuchen Sie es erneut.',
            invalid_json: 'Ungültiges JSON im Feld Standardeinstellungen. Bitte versuchen Sie es erneut.',
            page_title: 'Einwilligungsrichtlinien',
            version_updated: 'Aktualisiert',
            version_initial: 'Erste Version',
            json_format: 'JSON-Format',
            sensitive: 'Empfindlich',
            flag_sensitive: 'Sensibel kennzeichnen',
            flag_sensitive_title: 'Markieren Sie die Richtlinie als vertraulich',
            require_enhanced_review: 'Erfordern eine erweiterte Überprüfung',
            enhanced_review_success: 'Erweiterte Überprüfungsanforderung aktiviert.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Wählen Sie die anwendbaren PDPL Kategorien sensibler Daten gemäß Artikel 2 aus:',
            no_policies: 'Keine Einwilligungsrichtlinien gefunden. Erstellen Sie Ihre erste Richtlinie, um zu beginnen.',
            approval_success: 'Richtlinie genehmigt.',
            reviewers_label: 'Prüfer',
            enhanced_review_title: 'Erweiterte Überprüfung erforderlich',
            enhanced_review_badge: 'Erweiterte Überprüfung erforderlich',
            enhanced_review_status_pending: 'Überprüfung ausstehend',
            enhanced_review_status_approved: 'Überprüfung genehmigt',
            enhanced_review_status_rejected: 'Überprüfung abgelehnt',
            enhanced_review_reviewed_by: 'Überprüft von',
            enhanced_review_notes: 'Überprüfungsnotizen',
            enhanced_review_approve: 'Überprüfung genehmigen',
            enhanced_review_reject: 'Überprüfung ablehnen',
            enhanced_review_notes_required: 'Überprüfungsnotizen sind erforderlich',
            enhanced_review_approved: 'Erweiterte Überprüfung genehmigt',
            enhanced_review_rejected: 'Erweiterte Überprüfung abgelehnt — Richtlinie auf Entwurf zurückgesetzt',
            enhanced_review_reset_to_draft: 'Erweiterte Überprüfung abgelehnt — Richtlinie auf Entwurf zurückgesetzt',
            enhanced_review_reject_warning: 'Die Ablehnung setzt diese Richtlinie auf den Status Entwurf zurück. Der DSB muss sie nach Behebung der Probleme erneut einreichen.',
            submitted_modal_title: 'Zur erweiterten Überprüfung eingereicht',
            submitted_modal_body: 'Diese Richtlinie wurde eingereicht. Ein Rechts-/Datenschutzprüfer muss sie genehmigen, bevor sie aktiviert werden kann.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Gesundheitsdaten',
            biometric: 'Biometrische Daten',
            political: 'Politische Meinungen',
            ethnic: 'Ethnische Herkunft',
            genetic: 'Genetische Daten',
            criminal: 'Strafregister',
        },
        purposes: {
            title: 'Zweckkatalog',
            new: 'Neuer Zweck',
            edit: 'Zweck bearbeiten',
            name: 'Bezeichnung',
            legal_basis: 'Rechtsgrundlage',
            retention_days: 'Aufbewahrungsfrist (Tage)',
            is_active: 'Aktiv',
            filter_by_category: 'Nach Kategorie filtern',
            policy_ids: 'Richtlinien-IDs (kommagetrennt)',
            ropa_activity_ids: 'ROPA-Tätigkeits-IDs (kommagetrennt)',
            policies: 'Richtlinien',
            deprecate: 'Veralten',
            fetch_error: 'Fehler beim Abrufen der Einwilligungszwecke. Bitte versuchen Sie es erneut.',
            save_error: 'Fehler beim Speichern des Einwilligungszwecks. Bitte versuchen Sie es erneut.',
            deprecate_error: 'Fehler beim Veralten des Einwilligungszwecks. Bitte versuchen Sie es erneut.',
            page_title: 'Zweckkatalog',
            no_purposes: 'Keine Einwilligungszwecke gefunden. Erstellen Sie Ihren ersten Zweck, um zu beginnen.',
            category_personal_data: 'Persönliche Daten',
            category_sensitive_data: 'Sensible Daten',
            category_health_data: 'Gesundheitsdaten',
            category_financial_data: 'Finanzdaten',
            category_biometric_data: 'Biometrische Daten',
            category_children_data: 'Kinderdaten',
            category_location_data: 'Standortdaten',
            category_communications_data: 'Kommunikationsdaten',
            category_other: 'Andere',
        },
        workflows: {
            title: 'Einwilligungs-Workflows',
            new: 'Neuer Workflow',
            edit: 'Workflow bearbeiten',
            workflow_type: 'Workflow-Typ',
            user_id: 'Benutzer-ID',
            purpose_id: 'Zweck-ID',
            purpose_ids_csv: 'Zweck-ID (kommagetrennt)',
            reason: 'Grund',
            process: 'Verarbeiten',
            data_subject_ref: 'Betroffenenreferenz',
            data_subject: 'Betroffene Person',
            channel: 'Kanal',
            policy_id: 'Richtlinien-ID',
            scope: 'Umfang',
            legal_grounds: 'Rechtsgrundlagen',
            assessment: 'Bewertung',
            select_outcome: 'Ergebnis auswählen',
            partially_approved: 'Teilweise genehmigt',
            denied: 'Abgelehnt',
            outcome_upheld: 'Bestätigt',
            outcome_pending: 'Ausstehend',
            outcome_denied: 'Abgelehnt',
            status_count: 'Statusanzahl',
            channel_filter: 'Nach Kanal filtern',
            add_action: '+ Aktion',
            by_type: 'Nach Typ',
            by_status: 'Nach Status',
            downstream_actions: 'Folgemaßnahmen',
            downstream_actions_for: 'Folgemaßnahmen für {subject} ({type})',
            add_downstream_action: 'Folgemaßnahme hinzufügen',
            assigned_to: 'Zugewiesen an',
            fetch_error: 'Fehler beim Abrufen der Einwilligungs-Workflows. Bitte versuchen Sie es erneut.',
            dashboard_error: 'Fehler beim Abrufen der Workflow-Übersicht. Bitte versuchen Sie es erneut.',
            save_error: 'Fehler beim Speichern des Einwilligungs-Workflows. Bitte versuchen Sie es erneut.',
            status_update_error: 'Fehler beim Aktualisieren des Workflow-Status. Bitte versuchen Sie es erneut.',
            assessment_error: 'Fehler beim Aktualisieren der Workflow-Bewertung. Bitte versuchen Sie es erneut.',
            add_action_error: 'Fehler beim Hinzufügen der Workflow-Aktion. Bitte versuchen Sie es erneut.',
            update_action_error: 'Fehler beim Aktualisieren der Workflow-Aktion. Bitte versuchen Sie es erneut.',
            type_refusal: 'Verweigerung',
            type_withdrawal: 'Widerruf',
            type_restriction: 'Einschränkung',
            type_objection: 'Widerspruch',
            status_received: 'Eingegangen',
            status_processing: 'In Bearbeitung',
            status_under_assessment: 'In Bewertung',
            status_implemented: 'Umgesetzt',
            evidence_button: 'Beweise',
            evidence_modal_title: 'Workflow-Beweise',
            no_evidence: 'Diesem Arbeitsablauf sind keine Beweise beigefügt.',
            upload_evidence_label: 'Laden Sie Workflow-Beweise hoch',
            completed_at: 'Abgeschlossen am',
            effective_date: 'Datum des Inkrafttretens',
            page_title: 'Einwilligungsworkflows',
            no_workflows: 'Keine Einwilligungs-Workflows gefunden. Workflows werden erstellt, wenn Betroffene ihre Rechte ausüben.',
            type_grant: 'Erteilung',
            propagation_none: 'Keine',
            propagation_pending: 'Ausstehend',
            propagation_pending_warning: 'Abschluss nicht möglich — Widerrufsweitergabe noch ausstehend',
            propagation_completed: 'Abgeschlossen',
            propagation_partial: 'Teilweise',
            propagation_incomplete_error: 'Workflow kann nicht abgeschlossen werden, solange die Widerrufsweitergabe noch aussteht',
            propagation_status: 'Weitergabestatus',
        },
        snapshots: {
            title: 'CMP-Snapshots',
            new: 'Neuer Snapshot',
            label: 'Bezeichnung',
            snapshot_date: 'Snapshot-Datum',
            purposes_count: 'Anzahl Zwecke',
            compare: 'Vergleichen',
            snapshot_data: 'Snapshot-Daten (JSON)',
            select_left: 'Linken Snapshot auswählen',
            select_right: 'Rechten Snapshot auswählen',
            comparison_results: 'Vergleichsergebnisse',
            field: 'Feld',
            left: 'Links',
            right: 'Rechts',
            close_comparison: 'Vergleich schließen',
            trigger: 'Auslöser',
            trigger_manual: 'Manuell',
            trigger_policy_approval: 'Richtliniengenehmigung',
            cmp_version: 'CMP-Version',
            policy_version_id: 'Richtlinienversions-ID',
            banner_config: 'Banner-Konfiguration (JSON)',
            active_purposes: 'Aktive Zwecke (JSON)',
            active_legal_bases: 'Aktive Rechtsgrundlagen (JSON)',
            domain_scope: 'Domain-Umfang',
            domain_scope_csv: 'Domain-Umfang (kommagetrennt)',
            raw_config: 'Rohkonfiguration (JSON)',
            checksum: 'Prüfsumme',
            created: 'Erstellt',
            fetch_error: 'Fehler beim Abrufen der CMP-Snapshots. Bitte versuchen Sie es erneut.',
            save_error: 'Fehler beim Speichern des CMP-Snapshots. Bitte versuchen Sie es erneut.',
            compare_error: 'Fehler beim Vergleichen der Snapshots. Bitte versuchen Sie es erneut.',
            page_title: 'Einwilligungs-Snapshots',
            json_format: 'JSON-Format',
            json_array_format: 'JSON Array-Format',
            manual_snapshot_note: 'Nur für Administratoren/Entwickler. Im normalen Betrieb werden Snapshots automatisch über den CMP-Webhook oder bei Richtliniengenehmigung erstellt. Fügen Sie den JSON direkt aus Ihrem CMP-Anbieter ein (z.B. OneTrust, Cookiebot).',
            banner_config_hint: 'Fügen Sie das Banner-Konfigurationsobjekt aus Ihrem CMP-Export ein.',
            active_purposes_hint: 'JSON-Array der aktiven Zweck-Objekte aus Ihrem CMP-Export.',
            active_legal_bases_hint: 'JSON-Array der aktiven Rechtsgrundlagen-Objekte aus Ihrem CMP-Export.',
            raw_config_hint: 'Vollständige rohe Konfigurations-JSON von Ihrem CMP. Andere JSON-Felder werden daraus extrahiert. Eine SHA-256-Prüfsumme wird automatisch berechnet.',
            changed_field: 'Geändertes Feld: ',
            no_snapshots: 'Keine CMP-Snapshots gefunden. Snapshots werden erstellt, wenn Richtlinien genehmigt oder manuell ausgelöst werden.',
            detail_title: 'Snapshot-Detail',
            view_snapshot_detail: 'Snapshot-Detail anzeigen',
            policy_version: 'Richtlinienversion',
            removed: 'Vorher',
            added: 'Nachher',
        },
        change_awareness: {
            title: 'Einwilligungsänderungserkennung',
            change_type: 'Änderungstyp',
            entity_type: 'Objekttyp',
            entity_id: 'Objekt-ID',
            detected_at: 'Erkannt am',
            acknowledge: 'Bestätigen',
            new_alert: 'Neue Warnung',
            new_alert_form: 'Neue Änderungserkennungswarnung',
            total_open: 'Gesamt offen',
            by_severity: 'Nach Schweregrad',
            snapshot_id: 'Snapshot-ID',
            update_status: 'Status aktualisieren',
            resolution_notes: 'Lösungshinweise',
            resolution_required: 'Lösungshinweise (erforderlich)',
            resolution_required_msg: 'Lösungshinweise sind bei der Behebung eines Elements erforderlich.',
            status_open: 'Offen',
            status_under_review: 'In Überprüfung',
            status_resolved: 'Behoben',
            status_accepted: 'Akzeptiert',
            severity_informational: 'Informativ',
            severity_requires_review: 'Überprüfung erforderlich',
            severity_policy_violation: 'Richtlinienverstoß',
            create_reconciliation: 'Abstimmungsaufgabe erstellen',
            reconciliation_created: 'Abstimmungsaufgabe erfolgreich erstellt.',
            reconciliation_error: 'Die Abgleichsaufgabe konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
            fetch_error: 'Fehler beim Abrufen der Änderungserkennungselemente. Bitte versuchen Sie es erneut.',
            dashboard_error: 'Fehler beim Abrufen der Änderungserkennungsübersicht. Bitte versuchen Sie es erneut.',
            create_error: 'Fehler beim Erstellen des Änderungserkennungselements. Bitte versuchen Sie es erneut.',
            status_update_error: 'Fehler beim Aktualisieren des Änderungserkennungsstatus. Bitte versuchen Sie es erneut.',
            page_title: 'Bewusstsein ändern',
            no_items: 'Keine Änderungsbewusstseinselemente gefunden. Elemente werden erstellt, wenn Änderungen in der Einwilligungskonfiguration erkannt werden.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Einwilligungs-Prüfpfad',
            event_type: 'Ereignistyp',
            user_id: 'Benutzer-ID',
            timestamp: 'Zeitstempel',
            details: 'Einzelheiten',
            actor: 'Akteur',
            filter_by_entity_type: 'Nach Objekttyp filtern',
            filter_by_action: 'Nach Aktion filtern',
            fetch_error: 'Fehler beim Abrufen des Einwilligungs-Prüfpfads. Bitte versuchen Sie es erneut.',
            page_title: 'Einwilligungsprüfung',
            export_csv: 'Exportieren CSV',
            exporting: 'Exportieren…',
            export_success: 'Audit-Trail erfolgreich exportiert.',
            export_error: 'Der Export des Einwilligungs-Prüfprotokolls ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
            no_entries: 'Keine Audit-Einträge gefunden. Audit-Ereignisse werden automatisch aufgezeichnet, wenn sich Einwilligungsdaten ändern.',
            entity_policy: 'Richtlinie',
            entity_purpose: 'Zweck',
            entity_workflow: 'Arbeitsablauf',
            entity_consent_record: 'Einwilligungsdatensatz',
            entity_change_awareness: 'Änderungsbewusstsein',
            entity_cmp_snapshot: 'CMP-Schnappschuss',
            action_created: 'Erstellt',
            action_updated: 'Aktualisiert',
            action_status_changed: 'Status geändert',
            action_drift_detected: 'Abweichung erkannt',
            action_snapshot_created: 'Schnappschuss erstellt',
            action_approval_submitted: 'Genehmigung eingereicht',
            action_archived: 'Archiviert',
            action_purpose_auto_mapped: 'Zweck automatisch zugeordnet',
            action_purpose_mapping_updated: 'Zweckzuordnung aktualisiert',
            action_webhook_configured: 'Webhook konfiguriert',
            entity_propagation: 'Vermehrung',
            entity_reconciliation: 'Versöhnung',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Ausbreitung bestätigt',
            action_propagation_failed: 'Die Weitergabe ist fehlgeschlagen',
            action_consent_captured: 'Einwilligung erfasst',
            action_reconciliation_created: 'Versöhnung erstellt',
            action_reconciliation_resolved: 'Versöhnung gelöst',
            action_submitted_for_review: 'Zur Überprüfung eingereicht',
            action_activated: 'Aktiviert',
            action_suspended: 'Ausgesetzt',
            action_retired: 'Außer Betrieb genommen',
            action_enhanced_review_required: 'Erweiterte Überprüfung erforderlich',
            action_enhanced_review_approved: 'Erweiterte Überprüfung genehmigt',
            action_enhanced_review_rejected: 'Erweiterte Überprüfung abgelehnt — Richtlinie auf Entwurf zurückgesetzt',
            action_consented: 'Zugestimmt',
            action_consented_customize: 'Einwilligung angepasst',
            action_revoked: 'Widerrufen',
            action_rejected: 'Abgelehnt',
        },
        web_records: {
            title: 'Aufzeichnungen zur Web-Einwilligung',
            page_title: 'Aufzeichnungen zur Web-Einwilligung',
            domain: 'Domäne',
            visitor_id: 'Besucherausweis',
            consent_action: 'Einwilligungsaktion',
            received_at: 'Erhalten am',
            admin_status: 'Admin-Status',
            revoke: 'Widerrufen',
            reject: 'Ablehnen',
            reason: 'Grund',
            reason_placeholder: 'Grund für diese Aktion (optional)',
            revoke_confirm: 'Diesen Einwilligungsdatensatz widerrufen?',
            reject_confirm: 'Diesen Einwilligungsdatensatz ablehnen?',
            override_success: 'Datensatzstatus erfolgreich aktualisiert.',
            override_error: 'Datensatzstatus konnte nicht aktualisiert werden.',
            no_records: 'Es wurden keine Web-Einwilligungsdatensätze gefunden.',
            fetch_error: 'Web-Zustimmungsdatensätze konnten nicht geladen werden.',
            filter_by_domain: 'Nach Domäne filtern',
            filter_by_action: 'Nach Aktion filtern',
            active: 'Aktiv',
        },
        legal_bases: {
            all: 'Alle Rechtsgrundlagen',
        },
        common_statuses: {
            suspended: 'Ausgesetzt',
            retired: 'Zurückgezogen',
            deprecated: 'Veraltet',
        },
        approval_modes: {
            all: 'Alle',
            any: 'Beliebig',
        },
        cmp_integration: {
            title: 'AesirX CMP-Integration',
            back_to_consent: 'Back zu Einwilligung',
            back_to_integration: 'Back zu Integration',
            webhook_status: 'Webhook-Status',
            events_received: 'Ereignisse Empfangen',
            web_consent_records: 'Web Einwilligung Datensätze',
            unmapped_purposes: 'Nicht zugeordnet Zwecke',
            integration_management: 'Integration Verwaltung',
            webhook_configuration: 'Webhook Konfiguration',
            webhook_config_desc: 'Konfigurieren der CMP Webhook endpoint und manage der HMAC secret.',
            purpose_mapping: 'Zweck Zuordnung',
            purpose_mapping_desc: 'Zuordnen CMP purpose identifiers zu GRC Einwilligung catalog purposes.',
            bulk_sync: 'Massen Synchronisierung',
            bulk_sync_desc: 'Importieren historical Einwilligung Daten und view Synchronisierung history.',
            consent_analytics: 'Web Einwilligung Analytics (Last 30 Days)',
            consent_grant_rate: 'Einwilligung Grant Rate',
            top_domain: 'Top Domäne',
            records: 'Datensätze',
            banner_interactions: 'Banner-Interaktionen',
            consent_actions: 'Einwilligungsaktionen',
            opt_in_trend: 'Einwilligung/Abmeldung-Trend',
            records_by_domain: 'Datensätze nach Domäne',
            date: 'Datum',
            grants: 'Gewährungen',
            denials: 'Ablehnungen',
            not_configured: 'Nicht Configured',
            load_error: 'Fehler beim load Integration Daten',
            webhook_not_configured: 'Webhook nicht konfiguriert.',
            webhook_not_configured_msg: 'Bitte {link} first.',
            configure_webhook_link: 'konfigurieren der Webhook',
            setup_wizard: 'Einrichten Wizard',
            step_configure_webhook: 'Konfigurieren Webhook',
            step_map_purposes: 'Zuordnen Zwecke',
            step_run_sync: 'Ausführen Erstmalig Massen Synchronisierung',
            step_verify_data: 'Verifizieren Daten in Dashboard',
            configure: 'Konfigurieren',
            view_dashboard: 'Ansehen Dashboard',
            syncing: 'Synchronisiere...',
            sync_now: 'Synchronisierung Now',
            sync_completed: 'Synchronisierung completed: {imported} imported, {skipped} skipped, {failed} fehlgeschlagen.',
            sync_failed: 'Synchronisierung fehlgeschlagen',
            last_sync_result: 'Letzter Synchronisierung Result',
            imported: 'Importiert',
            skipped: 'Übersprungen',
            errors: 'Fehler',
            index: 'Verzeichnis',
            error: 'Fehler',
            sync_history: 'Synchronisierung Verlauf',
            no_sync_jobs: 'No Synchronisierung jobs recorded yet.',
            load_sync_error: 'Fehler beim load Synchronisierung Daten',
            purpose_mapping_title: 'CMP Zweck Zuordnung',
            auto_mapping: 'Automatische Zuordnung...',
            auto_map_purposes: 'Auto-Map Zwecke',
            unmapped_warning: '{count} nicht zugeordnete(r) Zweck(e) – diese CMP Zwecke müssen GRC-Zwecken zugeordnet werden.',
            mapping_saved: 'Zuordnung saved.',
            mapping_deleted: 'Zuordnung deleted.',
            auto_map_complete: 'Auto-map abgeschlossen: {mapped} mapped, {unmatched} unmatched.',
            auto_map_failed: 'Auto-map fehlgeschlagen',
            save_mapping_error: 'Fehler beim save Zuordnung',
            delete_mapping_error: 'Fehler beim delete Zuordnung',
            load_data_error: 'Fehler beim load Daten',
            cmp_purpose: 'CMP Zweck',
            grc_purpose: 'GRC Zweck',
            select_grc_purpose: '-- Auswählen GRC Zweck --',
            auto_mapped: 'Automatisch zugeordnet',
            mapped: 'Zugeordnet',
            unmapped: 'Nicht zugeordnet',
            no_mappings: 'Keine Zweckzuordnungen gefunden. Zuordnungen werden automatisch erstellt, wenn Einwilligungsereignisse empfangen werden.',
            total_mappings: 'Gesamt: {count} mapping(s)',
            webhook_config_title: 'Webhook Konfiguration',
            current_configuration: 'Aktuell Konfiguration',
            setup_webhook: 'Einrichten Webhook',
            setup_webhook_desc: 'Konfigurieren der AesirX CMP Webhook zu receive Einwilligung Ereignisse.',
            allowed_domains: 'Erlaubt Domänen (one per line)',
            webhook_secret_warning: 'Webhook Geheimnis (save dieses now -- it will not be shown again):',
            last_event: 'Letzter Ereignis',
            none_received: 'Keine',
            update_domains: 'Aktualisieren Domänen',
            pause_webhook: 'Pausieren Webhook',
            resume_webhook: 'Fortsetzen Webhook',
            regenerate_secret: 'Regenerate Geheimnis',
            configuring: 'Konfiguriere...',
            create_webhook_config: 'Erstellen Webhook Konfiguration',
            webhook_endpoint: 'Webhook Endpunkt',
            webhook_endpoint_desc: 'Konfigurieren your AesirX CMP zu send Einwilligung Ereignisse zu:',
            webhook_signature_note: 'Einschließen der HMAC-SHA256 signature in der {header} header.',
            configured_success: 'Webhook erfolgreich konfiguriert.',
            config_updated: 'Konfiguration updated.',
            config_error: 'Fehler beim konfigurieren Webhook',
            update_error: 'Fehler beim update Konfiguration',
            toggle_error: 'Fehler beim toggle Webhook',
            deactivated: 'Webhook deaktiviert.',
            deactivate_error: 'Fehler beim deactivate Webhook',
            page_title: 'CMP Integration',
            webhook_config_page_title: 'CMP Webhook-Konfiguration',
            bulk_sync_page_title: 'Datensätze neu zuordnen',
            purpose_mapping_page_title: 'Zweckzuordnung',
            data_loaded: 'CMP Integrationsdaten geladen',
            sync_all: 'Alle synchronisieren',
            sync_selected: 'Ausgewählte synchronisieren',
            sync_status: 'Synchronisierungsstatus',
            last_sync: 'Letzte Synchronisierung',
            no_records: 'Keine Datensätze gefunden.',
            sync_in_progress: 'Synchronisierung läuft...',
            sync_complete: 'Synchronisierung abgeschlossen.',
            sync_error: 'Synchronisierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
            map_purpose: 'Zweck zuordnen',
            unmap: 'Zuordnung aufheben',
            mapping_status: 'Zuordnungsstatus',
            save_mapping: 'Zuordnung speichern',
            remap_unmapped_records: 'Nicht zugeordnete Datensätze neu zuordnen',
            remap_records: 'Datensätze neu zuordnen',
            add_mappings_first: 'Zuerst Zweckzuordnungen hinzufügen (Schritt 2)',
            remap_result: 'Ergebnis der Neuzuordnung',
            remap_result_desc: 'Verarbeitet alle Einwilligungsdatensätze mit nicht oder teilweise zugeordneten Zwecken.',
            total_processed: 'Insgesamt verarbeitet',
            newly_mapped: 'Neu kartiert',
            still_unmapped_label: 'Immer noch nicht zugeordnet',
            still_unmapped_msg: '{count} Datensätze haben noch nicht zugeordnete Zwecke. {link} und klicken Sie dann erneut auf Neu zuordnen.',
            add_more_mappings: 'Fügen Sie weitere Zuordnungen hinzu',
            remap_complete: 'Neuzuordnung abgeschlossen: {remapped} neu zugeordnet, {still_unmapped} noch nicht zugeordnet.',
            add_mapping: 'Zuordnung hinzufügen',
            cmp_purpose_id_label: 'CMP Zweck-ID *',
            cmp_purpose_id_required: 'CMP Zweck-ID ist erforderlich',
            cmp_purpose_name_label: 'CMP Zweckname',
            grc_purpose_label: 'GRC-Zweck',
        },
        propagation: {
            title: 'Widerrufsausbreitung',
            systems: 'Verknüpfte Systeme',
            status: 'Status',
            confirmed: 'Bestätigt',
            pending: 'Ausstehend',
            failed: 'Fehlgeschlagen',
            confirm: 'Widerruf bestätigen',
            fail: 'Als fehlgeschlagen markieren',
            failure_reason: 'Fehlergrund',
            can_close: 'Alle Systeme bestätigt — Workflow kann geschlossen werden',
            cannot_close: 'Warten auf Systembestätigungen',
            no_tasks: 'Keine Ausbreitungsaufgaben.',
            confirm_success: 'Systemwiderruf bestätigt.',
            fail_success: 'System als fehlgeschlagen markiert.',
            propagate_success: 'Widerruf an verknüpfte Systeme weitergeleitet.',
            propagate: 'An Systeme weitergeben',
            add_system: 'System hinzufügen',
            remove_system: 'Entfernen',
            system_id: 'System-ID',
            system_name: 'Systemname',
            owner_id: 'Besitzer-ID',
            propagate_error: 'Die Auszahlung konnte nicht verbreitet werden. Bitte versuchen Sie es erneut.',
            no_systems: 'Fügen Sie mindestens ein System hinzu.',
            page_title: 'Auszahlungsausbreitung',
            workflow_id: 'Wählen Sie Auszahlungsworkflow',
            system_id_hint: 'z.B. crm-001, erp-prod',
            owner_id_hint: 'Systemeigentümer',
            elapsed: 'Vergangen',
        },
        sensitive: {
            title: 'Richtlinien für sensible Daten',
            flag: 'Als sensibel markieren',
            categories: 'Sensible Kategorien',
            enhanced_review: 'Erweiterte Überprüfung erforderlich',
            enhanced_review_success: 'Erweiterte Überprüfung angefordert.',
            flag_success: 'Richtlinie als sensibel markiert.',
            no_sensitive: 'Keine Richtlinien für sensible Daten.',
        },
        reconciliation: {
            title: 'Abweichungsabstimmung',
            create: 'Neue Abstimmungsaufgabe',
            system: 'System',
            owner: 'Eigentümer',
            description: 'Beschreibung',
            resolution: 'Lösung',
            resolve: 'Lösen',
            update_grc: 'GRC aktualisieren',
            update_cmp: 'CMP aktualisieren',
            accept_difference: 'Unterschied akzeptieren',
            escalate: 'Eskalieren',
            resolution_notes: 'Lösungshinweise',
            no_tasks: 'Keine Abstimmungsaufgaben.',
            resolve_success: 'Abstimmungsaufgabe gelöst.',
            create_success: 'Abstimmungsaufgabe erstellt.',
            open: 'Offen',
            resolved: 'Gelöst',
            escalated: 'Eskaliert',
            dashboard_open: 'Offene Aufgaben',
            dashboard_resolved: 'Gelöste Aufgaben',
            dashboard_escalated: 'Eskalierte Aufgaben',
            page_title: 'Nichtübereinstimmungsabgleich',
            my_tasks: 'Meine Aufgaben',
            all_tasks: 'Alle Aufgaben',
            severity: 'Schweregrad',
        },
        sync: {
            title: 'CMP-Synchronisierung',
            direction: 'Richtung',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Konflikt',
            resolve_conflict: 'Konflikt lösen',
            no_events: 'Keine Synchronisierungsereignisse.',
            resolve_success: 'Synchronisierungskonflikt gelöst.',
            create_success: 'Synchronisierungsereignis aufgezeichnet.',
            page_title: 'CMP Synchronisierung',
            detect_drift: 'Konflikte erkennen',
            detecting: 'Erkennen...',
            drift_result: '{detected} Konflikte wurden bei {total} verglichenen Zweck(en) erkannt.',
            no_drift: 'Keine neuen Konflikte erkannt.',
            drift_error: 'Die Konflikterkennung konnte nicht ausgeführt werden.',
            resolve_modal_title: 'Konflikte lösen',
            resolve_field: 'Feld',
            resolve_grc_value: 'GRC-Wert',
            resolve_cmp_value: 'CMP Wert',
            accept_cmp: 'Akzeptieren Sie den Wert CMP.',
            accept_cmp_desc: 'Aktualisieren Sie GRC so, dass er mit dem CMP übereinstimmt – der CMP-Wert wird zum neuen GRC-Wert.',
            keep_grc: 'Behalten Sie den GRC-Wert bei',
            keep_grc_desc: 'Schließen Sie diesen Konflikt, ohne GRC zu ändern – benachrichtigen Sie den CMP-Eigentümer zur Ausrichtung.',
            resolve_applied: 'Konflikt gelöst. GRC-Zweck aktualisiert, um mit CMP übereinzustimmen.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Dieser Bereitschaftsbericht ist lediglich beratend. Er genehmigt, erstellt, materialisiert, reicht oder versendet das Dossier nicht.',
            run_label: 'Vor dem Flug ausführen',
            running: 'Laufen vor dem Flug…',
            assemble_label: 'Paket automatisch zusammenstellen',
            assembling: 'Zusammenbau…',
            verdict_label: 'Urteil vor dem Flug',
            tokens_label: '≈ {count} Token',
            remediation_label: 'Sanierung',
            no_gaps: 'Es wurden keine Lücken vor dem Flug festgestellt. Die Ablage ist montagefertig.',
            override_label: 'Blockurteil außer Kraft setzen – die Versammlung wird mit einem Prüfstempel versehen.',
            accept_label: 'Bericht annehmen',
            reject_label: 'Bericht ablehnen',
            assembled_label: 'Einreichungspaket zusammengestellt',
            package_id_label: 'Paket-ID',
            package_filename_label: 'Dateiname',
            package_hash_label: 'Paket-Hash',
            evidence_count_label: '{count} Beweisstücke gebündelt',
            error_generic: 'Der Preflight konnte nicht ausgeführt oder das Paket nicht zusammengestellt werden. Versuchen Sie es erneut oder überprüfen Sie den Anmeldestatus.',
        },
        errors: {
            fetch: 'Fehler beim load assessments..',
            save: 'Fehler beim save Bewertung..',
            fetch_gap_report_failed: 'Fehler beim fetch Lücke report..',
            fetch_transfers_failed: 'Fehler beim fetch Übertragungen..',
            save_transfer_failed: 'Fehler beim save Übertragung..',
            submit_review_failed: 'Fehler beim submit für Überprüfung..',
            process_decision_failed: 'Fehler beim process Entscheidung..',
            deprecate_failed: 'Fehler beim deprecate..',
            evaluate_risk_failed: 'Fehler beim evaluate Risiko..',
            fetch_safeguards_failed: 'Fehler beim fetch Schutzmaßnahmen..',
            save_safeguard_failed: 'Fehler beim save Schutzmaßnahme..',
            update_safeguard_status_failed: 'Fehler beim update Schutzmaßnahme Status..',
        },
        nav: {
            dpias: 'DSFAs',
            transfers: 'Übermittlungsbewertungen',
            dashboard: 'Übersicht',
            gap_report: 'Lückenbericht',
        },
        risk_level_critical: 'Kritisches Risiko',
        risk_level_high: 'Hohes Risiko',
        risk_level_medium: 'Mittleres Risiko',
        risk_level_low: 'Niedriges Risiko',
        severity_critical: 'Kritischer Schweregrad',
        severity_high: 'Hoher Schweregrad',
        severity_medium: 'Mittlerer Schweregrad',
        severity_low: 'Niedriger Schweregrad',
        overdue: 'Überfällig',
        framework_filter_label: 'Rahmen',
        aria_filter_by_framework: 'Nach Framework filtern',
        dashboard: {
            title: 'Bewertungsübersicht',
            page_title: 'Bewertungs-Dashboard',
            announce_loaded: 'Bewertungs-Dashboard geladen',
            announce_chart_view: 'Diagrammansicht anzeigen',
            announce_table_view: 'Datentabellenansicht anzeigen',
            aria_metrics_table: 'Bewertungs-Dashboard-Kennzahlen',
            metric_col: 'Kennzahl',
            value_col: 'Wert',
            dpia_prefix: 'DSFA',
            transfer_prefix: 'Übermittlung',
            risk_prefix: 'Risiko',
            aria_dpias_by_status: 'DSFAs nach Status',
            aria_transfers_by_status: 'Übermittlungen nach Status',
            aria_top_risks: 'Top-Risiken',
            aria_top_risks_table: 'Top-Risiken über DSFAs',
            overdue_dpias_attention: '{count} überfällige DSFA-Bewertungen erfordern Aufmerksamkeit',
            no_overdue_dpias: 'Keine überfälligen DSFA-Bewertungen',
            overdue_transfers_attention: '{count} überfällige Übermittlungsbewertungen erfordern Aufmerksamkeit',
            no_overdue_transfers: 'Keine überfälligen Übermittlungsbewertungen',
            dpias_overview: 'DSFA-Übersicht',
            transfers_overview: 'Übermittlungsbewertungen – Übersicht',
            overdue_items: 'Überfällige Einträge',
            overdue_dpias: 'Überfällige DSFAs',
            overdue_transfers: 'Überfällige Übermittlungsbewertungen',
            count: 'Anzahl',
            no_data: 'No Daten available.',
            no_risks: 'Keine Risiken gefunden.',
            no_dashboard_data: 'No Dashboard Daten available.',
            fetch_error: 'Fehler beim fetch Dashboard Daten. Bitte versuchen Sie es erneut.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DSFAs',
            announce_submitted_for_review: 'DSFA zur Prüfung eingereicht',
            announce_deprecated: 'DSFA veraltet',
            announce_risks_loaded: '{count} Risiko(en) geladen',
            announce_mitigations_loaded: '{count} Maßnahme(n) geladen',
            announce_risk_deleted: 'Risiko gelöscht',
            announce_mitigation_status_updated: 'Maßnahmenstatus aktualisiert',
            aria_filter: 'DSFAs filtern',
            aria_filter_by_status: 'Nach Status filtern',
            aria_filter_by_lead: 'Nach Bewertungsleiter filtern',
            aria_table: 'Datenschutz-Folgenabschätzungen',
            announce_found: '{count} DSFA(s) gefunden',
            announce_decision: 'DSFA {decision}',
            mitigation_start: 'Starten',
            mitigation_mark_implemented: 'Als umgesetzt markieren',
            mitigation_verify: 'Verifizieren',
            title: 'Datenschutz-Folgenabschätzungen',
            new: 'Neue DSFA',
            edit: 'DSFA bearbeiten',
            dpia_title: 'Titel',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Tätigkeit',
            risk_score: 'Risikobewertung',
            risks: 'Risiken',
            mitigations: 'Maßnahmen',
            add_risk: 'Risiko hinzufügen',
            add_mitigation: 'Maßnahme hinzufügen',
            necessity: 'Erforderlichkeit / Verhältnismäßigkeit',
            likelihood: 'Eintrittswahrscheinlichkeit',
            impact: 'Auswirkung',
            severity_label: 'Schweregrad',
            purpose: 'Zweck',
            consultation_notes: 'Beratung Hinweise',
            dpo_opinion: 'DPO-Stellungnahme',
            processing_activity_ids: 'Verarbeitung Aktivität IDs (comma-separated)',
            system_ids: 'System-IDs (kommagetrennt)',
            vendor_ids: 'Anbieter IDs (comma-separated)',
            data_types: 'Daten Typen (comma-separated)',
            data_types_short: 'Daten Typen',
            data_subjects: 'Daten Subjects (comma-separated)',
            assessment_lead_id: 'Bewertung Leitung ID',
            assessment_lead: 'Bewertung Leitung',
            dpo_id: 'DSB-ID',
            reviewer_ids: 'Prüfer IDs (comma-separated)',
            approval_mode: 'Genehmigung Modus',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'DPO-Abzeichnung erforderlich',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Überprüfen Datum',
            submit_for_review: 'Absenden für Überprüfen',
            approve_reject: 'Genehmigen/Ablehnen',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Veralten',
            deprecated: 'Veraltet',
            hide_risks: 'Ausblenden Risiken',
            hide_mitigations: 'Ausblenden Maßnahmen',
            comments: 'Kommentare',
            submit_decision: 'Absenden Entscheidung',
            category_label: 'Kategorie',
            category_data_breach: 'Daten Verletzung',
            category_unauthorized_access: 'Unbefugt Zugriff',
            category_function_creep: 'Funktion Creep',
            category_re_identification: 'Re-Identifizierung',
            category_discrimination: 'Diskriminierung',
            category_surveillance: 'Überwachung',
            category_other: 'Andere',
            affected_rights: 'Betroffene Rechte (kommagetrennt)',
            no_risks: 'Keine Risiken gefunden.',
            no_mitigations: 'Keine Maßnahmen gefunden.',
            no_dpias: 'Keine DPIAs gefunden.',
            mitigation_type: 'Typ',
            mitigation_technical: 'Technisch',
            mitigation_organizational: 'Organisatorisch',
            mitigation_contractual: 'Vertraglich',
            risk_ids: 'Risiko IDs (comma-separated)',
            responsible_party: 'Verantwortlich Party',
            responsible_party_placeholder: 'Oder Name manuell eingeben',
            affected_rights_placeholder: 'Ausgewählte Rechte werden hier angezeigt',
            affected_rights_freetext_placeholder: 'Betroffene Rechte eingeben (kommagetrennt)',
            responsible: 'Verantwortlich',
            target_date: 'Ziel Datum',
            created: 'Erstellt',
            fetch_error: 'Fehler beim fetch DPIAs. Bitte versuchen Sie es erneut.',
            save_error: 'Fehler beim save DPIA. Bitte versuchen Sie es erneut.',
            submit_review_error: 'Fehler beim submit DPIA für Überprüfung. Bitte versuchen Sie es erneut.',
            decision_error: 'Fehler beim process Entscheidung. Bitte versuchen Sie es erneut.',
            deprecate_error: 'Fehler beim deprecate DPIA. Bitte versuchen Sie es erneut.',
            fetch_risks_error: 'Fehler beim fetch Risiken. Bitte versuchen Sie es erneut.',
            save_risk_error: 'Fehler beim save Risiko. Bitte versuchen Sie es erneut.',
            delete_risk_error: 'Fehler beim delete Risiko. Bitte versuchen Sie es erneut.',
            fetch_mitigations_error: 'Fehler beim fetch Maßnahmen. Bitte versuchen Sie es erneut.',
            save_mitigation_error: 'Fehler beim save Maßnahme. Bitte versuchen Sie es erneut.',
            update_mitigation_status_error: 'Fehler beim Aktualisieren des Maßnahmenstatus. Bitte versuchen Sie es erneut.',
            link_form_template: 'Vorlage für ein Link-Regulierungsformular',
            link_form_template_placeholder: 'Wählen Sie eine DPIA-Formularvorlage aus (optional)',
            link_form_template_none: 'Keine',
            linked_form: 'Verknüpftes Formular',
            linked_form_creating: 'Verlinktes Formular wird erstellt...',
            linked_form_error: 'Die verknüpfte Formularinstanz konnte nicht erstellt werden. Das DPIA wurde ohne verknüpftes Formular erstellt.',
            view_linked_form: 'Formular anzeigen',
            announce_created_with_form: 'DPIA mit verknüpfter Formularinstanz erstellt',
            creation_mode: 'DPIA Erstellungsmodus',
            mode_standard: 'Standard DPIA',
            mode_template: 'Verwenden Sie die Vorlage für ein Regulierungsformular',
            select_form_template: 'Wählen Sie Formularvorlage aus',
            create_and_fill_form: 'Formular erstellen und ausfüllen',
            template_sections_title: 'Vorlagenabschnitte',
            field_singular: 'Feld',
            field_plural: 'Felder',
            no_sections: 'In dieser Vorlage wurden keine Abschnitte gefunden.',
            template_mode_hint: 'Nach der Erstellung werden Sie weitergeleitet, um das vollständige Regulierungsformular auszufüllen.',
            linked_dossier_filings: 'Verknüpfte Dossiereinreichungen',
            no_linked_dossier_filings: 'Keine Dossiereinreichungen mit dieser DSFA verknüpft.',
            view_dossier_filing: 'Einreichung anzeigen',
            hide_dossier_filings: 'Dossiereinreichungen ausblenden',
            supporting_evidence: 'Unterstützende Beweise',
            hide_supporting_evidence: 'Beweise verbergen',
            no_supporting_evidence: 'Keine unterstützenden Beweise beigefügt.',
            upload_supporting_evidence: 'Laden Sie unterstützende Beweise hoch',
            view_form_responses: 'Formularantworten anzeigen',
            regulatory_framework: 'Regulatorischer Rahmen',
            all_frameworks: 'Alle Rahmenwerke',
            no_templates_for_market: 'Keine Vorlagen für den ausgewählten Markt verfügbar. Bitte wählen Sie einen anderen Markt oder erstellen Sie eine Vorlage.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Datenübermittlungen',
            announce_found: '{count} Übermittlungsbewertung(en) gefunden',
            announce_submitted_for_review: 'Übermittlung zur Prüfung eingereicht',
            announce_deprecated: 'Übermittlung veraltet',
            announce_decision: 'Übermittlung {decision}',
            announce_risk_eval_completed: 'Risikobewertung abgeschlossen',
            announce_safeguard_status_updated: 'Schutzmaßnahmenstatus aktualisiert',
            announce_safeguards_loaded: '{count} Schutzmaßnahme(n) geladen',
            aria_filter: 'Übermittlungsbewertungen filtern',
            aria_filter_by_status: 'Nach Status filtern',
            aria_filter_by_mechanism: 'Nach Übermittlungsmechanismus filtern',
            aria_table: 'Datenübermittlungsbewertungen',
            title: 'Grenzüberschreitende Übermittlungsbewertungen',
            new: 'Neue Übermittlung',
            edit: 'Übermittlung bearbeiten',
            name: 'Bezeichnung',
            from_country: 'Herkunftsland',
            to_country: 'Zielland',
            safeguards: 'Schutzmaßnahmen',
            risk_level: 'Risikostufe',
            assessment_notes: 'Bewertungsnotizen',
            mechanism: 'Mechanismus',
            transfer_mechanism: 'Übertragung Mechanismus',
            transfer_mechanism_detail: 'Übertragung Mechanismus Detail',
            all_mechanisms: 'Alle Mechanisms',
            mechanism_other: 'Andere',
            data_types: 'Daten Typen (comma-separated)',
            data_subjects: 'Daten Subjects (comma-separated)',
            purpose: 'Zweck',
            recipient_name: 'Empfänger Name',
            create_success: 'Übermittlungsbewertung erstellt.',
            update_success: 'Übermittlungsbewertung aktualisiert.',
            recipient_details: 'Empfängerdetails',
            recipient_details_placeholder: 'Rolle, Standort und Systeme des Empfängers sowie Details zur Weiterübermittlung',
            vendor_id: 'Anbieter ID',
            processing_activity_ids: 'Verarbeitung Aktivität IDs (comma-separated)',
            reviewer_ids: 'Prüfer IDs (comma-separated)',
            approval_mode: 'Genehmigung Modus',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'DPO-Abzeichnung erforderlich',
            review_date: 'Überprüfen Datum',
            created: 'Erstellt',
            deprecated: 'Veraltet',
            submit_for_review: 'Absenden für Überprüfen',
            approve_reject: 'Genehmigen/Ablehnen',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Veralten',
            risk_eval: 'Risiko Eval',
            hide_safeguards: 'Ausblenden Schutzmaßnahmen',
            mechanism_proof: 'Mechanismusbeweis',
            hide_mechanism_proof: 'Mechanismusbeweis ausblenden',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Kein Mechanismusnachweis beigefügt.',
            upload_mechanism_proof: 'Mechanismusbeweis hochladen',
            comments: 'Kommentare',
            submit_decision: 'Absenden Entscheidung',
            data_sensitivity: 'Daten Sensitivity (1-5)',
            country_risk: 'Land Risiko (1-5)',
            safeguard_adequacy: 'Schutzmaßnahme Angemessenheit (1-5)',
            volume_factor: 'Volumen Factor (1-5)',
            evaluate: 'Bewerten',
            add_safeguard: 'Hinzufügen Schutzmaßnahme',
            responsible_party: 'Verantwortlich Party',
            responsible_party_placeholder: 'Oder Name manuell eingeben',
            responsible: 'Verantwortlich',
            implementation_date: 'Implementierung Datum',
            implementation: 'Implementierung',
            expiry_date: 'Ablauf Datum',
            expiry: 'Ablauf',
            expire: 'Ablaufen',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Keine Schutzmaßnahmen gefunden.',
            no_transfers: 'Keine Übertragung assessments gefunden.',
            fetch_error: 'Fehler beim fetch Übertragung assessments. Bitte versuchen Sie es erneut.',
            save_error: 'Fehler beim save Übertragung Bewertung. Bitte versuchen Sie es erneut.',
            submit_review_error: 'Fehler beim submit für Überprüfung. Bitte versuchen Sie es erneut.',
            decision_error: 'Fehler beim process Entscheidung. Bitte versuchen Sie es erneut.',
            deprecate_error: 'Fehler beim deprecate Übertragung. Bitte versuchen Sie es erneut.',
            risk_eval_error: 'Fehler beim evaluate Risiko. Bitte versuchen Sie es erneut.',
            fetch_safeguards_error: 'Fehler beim fetch Schutzmaßnahmen. Bitte versuchen Sie es erneut.',
            save_safeguard_error: 'Fehler beim save Schutzmaßnahme. Bitte versuchen Sie es erneut.',
            update_safeguard_status_error: 'Fehler beim Aktualisieren des Schutzmaßnahmenstatus. Bitte versuchen Sie es erneut.',
            safeguard_type_encryption_in_transit: 'Verschlüsselung in Transit',
            safeguard_type_encryption_at_rest: 'Verschlüsselung bei Rest',
            safeguard_type_access_controls: 'Zugriff Kontrollen',
            safeguard_type_contractual_clauses: 'Vertraglich Klauseln',
            safeguard_type_data_minimization: 'Daten Minimization',
            safeguard_type_pseudonymization: 'Pseudonymisierung',
            safeguard_type_audit_rights: 'Prüfungsrechte',
            safeguard_type_breach_notification: 'Verletzung Benachrichtigung',
            safeguard_type_data_localization: 'Daten Lokalisierung',
            safeguard_type_other: 'Andere',
            activate: 'Aktivieren',
            all_statuses: 'Alle Status',
            any: 'Any',
            mechanism_detail: 'Mechanismus Detail',
            safeguard_types: {
                encryption_in_transit: 'Verschlüsselung bei der Übertragung',
                encryption_at_rest: 'Verschlüsselung im Ruhezustand',
                access_controls: 'Zugriffskontrollen',
                contractual_clauses: 'Vertragliche Klauseln',
                data_minimization: 'Datensparsamkeit',
                pseudonymization: 'Pseudonymisierung',
                audit_rights: 'Prüfungsrechte',
                breach_notification: 'Verletzungsmeldung',
                data_localization: 'Datenlokalisierung',
                other: 'Andere',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Gap-Bericht für Bewertungen',
            announce_loaded: 'Gap-Bericht geladen. {count} Lücke(n) identifiziert',
            aria_activities_without_dpia: 'Aktivitäten ohne DSFA-Bewertung',
            aria_activities_unclassified: 'Aktivitäten mit nicht klassifiziertem DSFA-Erfordernis',
            activities_unclassified_section_title: 'Aktivitäten mit nicht klassifiziertem DSFA-Erfordernis',
            activities_unclassified_description: 'Diese Aktivitäten wurden nicht geprüft, ob eine DSFA gesetzlich erforderlich ist. Klassifizieren Sie jede einzelne, um den Hinweis zu entfernen.',
            activities_unclassified_empty: 'Alle aktiven Aktivitäten wurden klassifiziert.',
            aria_unmitigated_risks: 'Nicht behandelte hohe und kritische Risiken',
            aria_transfers_without_safeguards: 'Übermittlungen ohne ausreichende Schutzmaßnahmen',
            aria_expired_assessments: 'Abgelaufene Bewertungen die Erneuerung erfordern',
            title: 'Bewertungslückenbericht',
            no_dpia: 'Tätigkeiten ohne DSFA',
            no_assessment: 'Übermittlungen ohne Bewertung',
            unmitigated: 'DSFAs mit offenen Risiken',
            activity_id: 'Aktivität ID',
            dpia_id: 'DSFA-ID',
            assessment_id: 'Bewertung ID',
            expired_assessments: 'Abgelaufen Bewertungen',
            no_gaps_activities: 'Keine Lücken gefunden Alle activities have DPIAs..',
            no_unmitigated_risks: 'Keine unmitigated high/critical Risiken gefunden.',
            no_gaps_safeguards: 'Keine Lücken gefunden Alle Übertragungen have adequate Schutzmaßnahmen..',
            no_expired: 'Keine expired assessments gefunden.',
            no_report_data: 'No Lücke report Daten available.',
            fetch_error: 'Fehler beim fetch Lücke report. Bitte versuchen Sie es erneut.',
            no_expired_assessments: 'Keine abgelaufenen Bewertungen.',
            no_gaps_dpia: 'Keine DPIA Lücken gefunden.',
            no_data: 'No Daten available.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dossiereinreichungsdetail',
            page_title_list: 'Dossiereinreichungsfälle',
            announce_filings_found: '{count} Einreichung(en) gefunden',
            announce_results: '{count} Ergebnisse',
            aria_filter_by_status: 'Nach Status filtern',
            title: 'Fälle zur Dossiereinreichung',
            nav_label: 'Dossier-Einreichungen',
            create: 'Erstellen Sie eine Dossier-Einreichung',
            form_type: 'Formulartyp',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Referenz',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Datum der Einreichung',
            outcome: 'Ergebnis',
            parent_filing: 'Eltern',
            no_filings: 'Keine Dossiereinreichungen gefunden.',
            fetch_error: 'Dossiereinreichungen konnten nicht abgerufen werden. Bitte versuchen Sie es erneut.',
            save_error: 'Die Dossier-Einreichung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
            created: 'Dossierablage erstellt.',
            loaded: 'Dossier-Einreichung geladen.',
            not_found: 'Dossier-Einreichung nicht gefunden.',
            back_to_list: 'Zurück zur Liste',
            section_assignments: 'Abschnittsaufgaben',
            assign_sections: 'Abschnitte zuweisen',
            section_id: 'Abschnitt',
            department: 'Abteilung',
            assignee: 'Bevollmächtigter',
            deadline: 'Frist',
            no_assignments: 'Noch keine Abschnittszuweisungen.',
            sections_assigned_msg: 'Abschnitte zugewiesen.',
            assign_error: 'Abschnitte konnten nicht zugewiesen werden. Bitte versuchen Sie es erneut.',
            lifecycle: 'Lebenszyklus der Einreichung',
            submission: 'Einreichungsdetails',
            submission_channel: 'Einreichungskanal',
            confirmation_number: 'Bestätigungsnummer',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Zur Überprüfung einreichen',
            submitted_for_review: 'Zur internen Überprüfung eingereicht.',
            submit_review_error: 'Fehler beim Einreichen zur Überprüfung. Bitte versuchen Sie es erneut.',
            legal_signoff: 'Rechtliche Freigabe',
            legal_signoff_done: 'Die rechtliche Freigabe ist abgeschlossen.',
            legal_signoff_error: 'Die rechtliche Freigabe konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Die Einreichung konnte nicht genehmigt werden. Bitte versuchen Sie es erneut.',
            materialisation_status_column: 'Einreichungsartefakt',
            materialisation_na: 'Nicht zutreffend',
            materialisation_pending: 'Ausstehend',
            materialisation_success: 'Materialisiert',
            materialisation_failed: 'Fehlgeschlagen',
            materialisation_banner_title: 'Das Übermittlungsartefakt konnte nicht erstellt werden',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Versuchen Sie es noch einmal',
            materialisation_retry_count: 'Frühere Wiederholungsversuche:',
            materialisation_retry_success: 'Das Übermittlungsartefakt wurde erfolgreich umgesetzt.',
            materialisation_retry_announce: 'Der erneute Versuch war erfolgreich',
            filter_materialisation_failed: 'Nur Materialisierungsfehler anzeigen',
            column_title: 'Titel',
            column_form_type: 'Formular',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Ergänzungsanfrage',
            supplement_requests: 'Ergänzungswünsche',
            supplement_response: 'Ergänzungsantwort',
            record_supplement: 'Antrag auf Ergänzung des Datensatzes',
            supplement_recorded: 'Ergänzungsanfrage erfasst.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Ergänzungsanfrage konnte nicht erfasst werden. Bitte versuchen Sie es erneut.',
            submit_supplement: 'Senden Sie eine ergänzende Antwort',
            supplement_submitted_msg: 'Ergänzungsantwort eingereicht.',
            supplement_submit_error: 'Die Ergänzungsantwort konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.',
            response_status: 'Antwortstatus',
            response_notes: 'Antwortnotizen',
            resubmit: 'Erneut einreichen',
            resubmitted_msg: 'Dossier erneut eingereicht.',
            resubmit_error: 'Das erneute Einreichen des Dossiers ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
            outcome_error: 'Das Ergebnis konnte nicht aufgezeichnet werden. Bitte versuchen Sie es erneut.',
            accepted: 'Akzeptiert',
            rejected: 'Abgelehnt',
            close: 'Schließen',
            closed_msg: 'Einreichung geschlossen.',
            close_error: 'Die Archivierung konnte nicht geschlossen werden. Bitte versuchen Sie es erneut.',
            change_filing: 'Änderungsablage erstellen',
            change_filing_created: 'Änderungsablage erstellt.',
            change_filing_error: 'Änderungsarchiv konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
            change_reason: 'Grund für die Änderung',
            filing_lineage: 'Abstammung einreichen',
            timers: 'Aktive Timer',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Frist für Ergänzungen',
            timer_resubmission_review: 'Frist für die erneute Einreichung',
            timer_department_input: 'Frist der Abteilung',
            days_remaining: 'Tage verbleibend',
            days_overdue: 'Tage überfällig',
            cover_letter: 'Anschreiben',
            checklist: 'Checkliste',
            evidence_index: 'Beweisindex',
            status_draft: 'Entwurf',
            status_internal_review: 'Interne Prüfung',
            status_legal_signoff: 'Rechtliche Freigabe',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Ergänzung angefordert',
            status_supplement_submitted: 'Ergänzung eingereicht',
            status_resubmitted: 'Erneut eingereicht',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Geschlossen',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Entwurf',
            step_internal_review: 'Interne Prüfung',
            step_legal_signoff: 'Rechtliche Freigabe',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Ergänzung angefordert',
            step_supplement_submitted: 'Ergänzung eingereicht',
            step_resubmitted: 'Erneut eingereicht',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Geschlossen',
            linked_dpia: 'Verknüpfte DSFA',
            linked_dpia_none: 'Keine DSFA mit dieser Einreichung verknüpft.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'DSFA anzeigen',
            linked_dpia_label: 'Diese Dossiereinreichung ist mit folgender DSFA verknüpft:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Zustand des Einreichungsartefakts',
            page_title: 'Einreichung von Artefaktgesundheit',
            page_description: 'Erkennen und beheben Sie Probleme bei der Materialisierung von Dossiers. Listen sind auf Ihre Organisation beschränkt.',
            failed_title: 'Fehlgeschlagene Materialisierungen',
            failed_empty: 'Keine fehlgeschlagenen Materialisierungen – gut.',
            pending_title: 'Genehmigt, aber nicht umgesetzt',
            pending_empty: 'Alle genehmigten Anträge verfügen über ein Begleitdossier.',
            orphan_title: 'Verwaiste Dossiers',
            orphan_description: 'Durch Materialisierung erstellte Dossiers, auf die in keiner Akte mehr verwiesen wird – typischerweise der Verlierer eines gleichzeitigen Rennens. Zur Untersuchung gelistet; Die Bereinigung erfolgt manuell.',
            orphan_empty: 'Es wurden keine verwaisten Dossiers entdeckt.',
            materialise_button: 'Jetzt materialisieren',
            column_failure_reason: 'Fehlergrund',
            column_failed_at: 'Fehlgeschlagen bei',
            column_retries: 'Wiederholungen',
            column_approved_at: 'Genehmigt bei',
            column_dossier_id: 'Dossier-ID',
            column_source_filing_id: 'Quellenablage',
            column_dossier_type: 'Typ',
            column_created_at: 'Erstellt bei',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Zugriff verweigert',
        access_denied: 'Sie verfügen nicht über die erforderlichen Berechtigungen, um auf das Klassifizierungsmodul zuzugreifen. Kontaktieren Sie Ihren Administrator.',
        nav: {
            taxonomies: 'Taxonomien',
            labeling: 'Kennzeichnung',
            lifecycle: 'Lebenszyklus',
            gaps: 'Lückenbericht',
            exports: 'Exporte',
        },
        exports: {
            title: 'Klassifizierungsexporte',
            csv_section: 'CSV-Exporte',
            pdf_section: 'PDF-Berichte',
            inventory: 'Inventar',
            classification_register: 'Klassifizierungsregister',
            raci: 'RACI-Matrix',
            exceptions: 'Ausnahmen',
            evidence_pack: 'Nachweispaket',
            delta_report: 'Delta-Bericht',
            executive_summary: 'Zusammenfassung',
            exporting: 'Wird exportiert…',
            export_success: 'Export erfolgreich abgeschlossen.',
            export_error: 'Export fehlgeschlagen. Bitte versuchen Sie es erneut.',
            csv_download: 'CSV-Download bereit.',
            pdf_download: 'PDF-Erstellung gestartet.',
            no_data: 'Keine Daten zum Exportieren verfügbar.',
        },
        delta: {
            title: 'Delta-Details',
            added: 'Hinzugefügt',
            removed: 'Entfernt',
            changed: 'Geändert',
            added_count: 'Hinzugefügt ({count})',
            removed_count: 'Entfernt ({count})',
            changed_count: 'Geändert ({count})',
            object_id: 'Objekt-ID',
            object_type: 'Objekttyp',
            taxonomy: 'Taxonomie',
            level: 'Stufe',
            previous: 'Vorherig',
            current: 'Aktuell',
            no_records: 'Keine Einträge in diesem Abschnitt.',
            empty_section: 'Dieser Abschnitt ist leer.',
        },
        schedule: {
            title: 'Erkennungszyklus planen',
            enabled: 'Aktiviert',
            interval_days: 'Intervall (Tage)',
            interval_label: 'Alle N Tage ausführen',
            manual_only: 'Nur manuell',
            every_n_days: 'Alle {n} Tage',
            save: 'Zeitplan speichern',
            cancel: 'Abbrechen',
            schedule_updated: 'Zeitplan erfolgreich aktualisiert.',
        },
        taxonomies: {
            title: 'Klassifizierungstaxonomien',
            new: 'Neue Taxonomie',
            edit: 'Taxonomie bearbeiten',
            name: 'Bezeichnung',
            levels: 'Klassifizierungsstufen',
            seed_defaults: 'Initialisieren Standardwerte',
            scope: 'Geltungsbereich',
            scope_organization: 'Organisation',
            scope_module_specific: 'Modul Specific',
            deprecate: 'Veralten',
            hide_levels: 'Ausblenden Stufen',
            versions: 'Versionen',
            hide_versions: 'Ausblenden Versionen',
            add_level: 'Hinzufügen Stufe',
            new_level: 'Neu Stufe',
            edit_level: 'Bearbeiten Stufe',
            code: 'Kennung',
            label: 'Bezeichnung',
            color: 'Farbe',
            ordinal: 'Ordinalzahl',
            handling_requirements: 'Handhabung Anforderungen',
            version_history: 'Version Verlauf',
            changes: 'Änderungen',
            failed_fetch: 'Fehler beim fetch Taxonomien. Bitte versuchen Sie es erneut.',
            failed_save: 'Fehler beim save Taxonomie. Bitte versuchen Sie es erneut.',
            failed_activate: 'Fehler beim activate Taxonomie. Bitte versuchen Sie es erneut.',
            failed_deprecate: 'Fehler beim deprecate Taxonomie. Bitte versuchen Sie es erneut.',
            failed_seed: 'Fehler beim seed Standard Taxonomien. Bitte versuchen Sie es erneut.',
            failed_fetch_levels: 'Fehler beim fetch Taxonomie Stufen. Bitte versuchen Sie es erneut.',
            failed_save_level: 'Fehler beim save Taxonomie Stufe. Bitte versuchen Sie es erneut.',
            failed_delete_level: 'Fehler beim delete Taxonomie Stufe. Bitte versuchen Sie es erneut.',
            failed_fetch_versions: 'Fehler beim fetch Taxonomie Versionen. Bitte versuchen Sie es erneut.',
            loaded_count: '{count} Taxonomien geladen',
            defaults_seeded: 'Standard-Taxonomien initialisiert.',
            no_levels: 'Keine Klassifikationsstufen definiert — fügen Sie Stufen hinzu, um Ihre Klassifikationshierarchie aufzubauen.',
            no_versions: 'Noch keine Versionshistorie — Versionen werden erstellt, wenn eine aktive Taxonomie aktualisiert wird.',
        },
        labeling: {
            title: 'Datenkennzeichnung',
            new: 'Neue Kennzeichnung',
            edit: 'Kennzeichnung bearbeiten',
            entity_type: 'Objekttyp',
            entity_id: 'Objekt-ID',
            taxonomy: 'Taxonomie',
            classification_level: 'Klassifizierungsstufe',
            applied_by: 'Angewendet von',
            applied_at: 'Angewendet am',
            bulk_apply: 'Massenweise anwenden',
            bulk_remove: 'Massenweise entfernen',
            all_taxonomies: 'Alle Taxonomien',
            system: 'IT-System',
            dataset: 'Datensatz',
            level_code_placeholder: 'Stufe Code...',
            select_taxonomy: 'Auswählen Taxonomie...',
            select_level: 'Auswählen Stufe...',
            rationale: 'Begründung',
            review_date: 'Überprüfen Datum',
            review: 'Überprüfen',
            selected: 'ausgewählt',
            select_all_labels: 'Alle Bezeichnungen auswählen',
            loaded_count: '{count} Bezeichnungen geladen',
            hide_stale_labels: 'Ausblenden Stale Bezeichnungen',
            show_conflicts: 'Anzeigen Konflikte',
            hide_conflicts: 'Ausblenden Konflikte',
            label_conflicts: 'Bezeichnung Konflikte',
            conflict_reason: 'Konflikt Grund',
            labels: 'Bezeichnungen',
            failed_fetch: 'Fehler beim fetch Bezeichnungen. Bitte versuchen Sie es erneut.',
            failed_fetch_taxonomies: 'Fehler beim fetch Taxonomien. Bitte versuchen Sie es erneut.',
            failed_fetch_levels: 'Fehler beim fetch Taxonomie Stufen. Bitte versuchen Sie es erneut.',
            failed_save: 'Fehler beim save Bezeichnung. Bitte versuchen Sie es erneut.',
            failed_review: 'Fehler beim Überprüfung Bezeichnung. Bitte versuchen Sie es erneut.',
            failed_bulk: 'Fehler beim bulk Bezeichnung. Bitte versuchen Sie es erneut.',
            failed_stale: 'Fehler beim fetch stale Bezeichnungen. Bitte versuchen Sie es erneut.',
            failed_conflicts: 'Fehler beim fetch Bezeichnung conflicts. Bitte versuchen Sie es erneut.',
            review_permission_denied: 'Sie haben keine Berechtigung, dieses Label zu überprüfen.',
            review_not_found: 'Label nicht gefunden — es wurde möglicherweise gelöscht.',
            approve_selected: 'Ausgewählte genehmigen',
            reject_selected: 'Ausgewählte ablehnen',
            approved: 'Genehmigt',
            rejected: 'Abgelehnt',
            labels_count: 'Labels',
            failed_count: 'fehlgeschlagen',
            confirm_approve: 'Genehmigung bestätigen',
            confirm_reject: 'Ablehnung bestätigen',
            approve_n_labels: '{count} Labels genehmigen?',
            reject_n_labels: '{count} Labels ablehnen?',
            submit_for_review: 'Zur Überprüfung einreichen',
            approve: 'Genehmigen',
            reject: 'Ablehnen',
            parent_system: 'Uebergeordnetes System',
            parent_system_placeholder: 'System auswaehlen...',
            parent_system_help: 'Fuer Konflikterkennung verwendet.',
            dataset_level: 'Datensatzebene',
            parent_level: 'Uebergeordnete Ebene',
            bulk_created: '{created} Beschriftung(en) erstellt',
            bulk_skipped: '{skipped} uebersprungen',
            bulk_all_skipped: 'Alle {skipped} uebersprungen',
            ids_hint: 'IDs durch Kommas getrennt eingeben',
            delete_confirm: 'Beschriftung fuer "{id}" loeschen?',
        },
        lifecycle: {
            title: 'Datenlebenszyklus-Überwachung',
            new: 'Neuer Eintrag',
            current_stage: 'Aktuelle Phase',
            last_transition: 'Letzter Übergang',
            transition: 'Übergang',
            view_history: 'Verlauf anzeigen',
            stages: {
                creation: 'Erstellung',
                collection: 'Erhebung',
                processing: 'Verarbeitung',
                storage: 'Speicherung',
                retention: 'Aufbewahrung',
                disposal: 'Entsorgung',
                archived: 'Archiviert',
                active: 'Aktiv',
                pending_deletion: 'Ausstehend Deletion',
                deleted: 'Gelöscht',
                on_hold: 'On Zurückstellen',
            },
            all_stages: 'Alle Stufen',
            object_id: 'Objekt ID',
            object_type: 'Objekt Typ',
            policy_reference: 'Richtlinie Referenz',
            retention_period_days: 'Aufbewahrung Zeitraum (days)',
            retention_basis: 'Aufbewahrung Grundlage',
            deletion_method: 'Deletion Methode',
            deletion_secure_erase: 'Sicher Erase',
            deletion_anonymization: 'Anonymisierung',
            deletion_physical_destruction: 'Physisch Destruction',
            deletion_other: 'Andere',
            responsible_party: 'Verantwortlich Party',
            responsible_user_id: 'Verantwortlich Benutzer ID',
            retention_expiry: 'Aufbewahrung Ablauf',
            hold_reason: 'Zurückstellen Grund',
            hold: 'Zurückstellen',
            release: 'Freigabe',
            hide_events: 'Ausblenden Ereignisse',
            events_timeline: 'Ereignisse Zeitverlauf',
            add_event: 'Hinzufügen Ereignis',
            event_type: 'Ereignis Typ',
            create_event: 'Erstellen Ereignis',
            from_stage: 'Von Stufe',
            to_stage: 'Zu Stufe',
            actor: 'Akteur',
            reason_placeholder: 'Grund...',
            go: 'Go',
            show_expired_retentions: 'Anzeigen Abgelaufen Retentions',
            hide_expired_retentions: 'Ausblenden Abgelaufen Retentions',
            expired_retentions_alert: 'Abgelaufen Retentions Warnung',
            failed_fetch: 'Fehler beim fetch Lebenszyklus Datensätze. Bitte versuchen Sie es erneut.',
            failed_save: 'Fehler beim save Lebenszyklus Datensatz. Bitte versuchen Sie es erneut.',
            failed_transition: 'Fehler beim transition Lebenszyklus Stufe. Bitte versuchen Sie es erneut.',
            failed_hold: 'Fehler beim place Zurückstellung. Bitte versuchen Sie es erneut.',
            failed_release: 'Fehler beim release Zurückstellung. Bitte versuchen Sie es erneut.',
            failed_fetch_events: 'Fehler beim fetch Lebenszyklus Ereignisse. Bitte versuchen Sie es erneut.',
            failed_create_event: 'Fehler beim create Lebenszyklus Ereignis. Bitte versuchen Sie es erneut.',
            failed_expired: 'Fehler beim fetch expired retentions. Bitte versuchen Sie es erneut.',
            hold_reason_prompt: 'Eingeben Zurückstellung reason:',
            loaded_count: '{count} Lebenszyklus-Datensätze geladen',
            stage_transition_for: 'Phasenübergang für {id}',
            event_created: 'Erstellt',
            event_updated: 'Aktualisiert',
            event_approved: 'Genehmigt',
            event_deprecated: 'Veraltet',
            event_proposed: 'Vorgeschlagen',
            event_reviewed: 'Überprüft',
            event_archived: 'Archiviert',
            event_restored: 'Wiederhergestellt',
        },
        gap_report: {
            title: 'Klassifizierungslückenbericht',
            unlabeled: 'Nicht gekennzeichnete Daten',
            missing_lifecycle: 'Fehlender Lebenszyklus',
            stale_labels: 'Veraltete Kennzeichnungen',
            run_detection: 'Ausführen Lücke Erkennung',
            detecting: 'Erkenne...',
            detection_complete: 'Lücke detection abgeschlossen. {count} gap(s) detected.',
            detection_failed: 'Lücke detection fehlgeschlagen.',
            by_severity: 'By Schweregrad',
            by_gap_type: 'By Lücke Typ',
            all_gap_types: 'Alle Lücke Typen',
            conflicting_labels: 'Conflicting Bezeichnungen',
            expired_retention: 'Abgelaufen Aufbewahrung',
            missing_handling: 'Fehlend Handhabung',
            info: 'Infos',
            gap_type: 'Lücke Typ',
            object_id: 'Objekt ID',
            object_type: 'Objekt Typ',
            detected_at: 'Erkannt At',
            open: 'Offen',
            acknowledged: 'Bestätigt',
            remediated: 'Behoben',
            accepted: 'Akzeptiert',
            acknowledge: 'Bestätigen',
            remediate: 'Beheben',
            accept_risk: 'Akzeptieren Risiko',
            accept_risk_prompt: 'Eingeben Risiko acceptance Begründung:',
            failed_fetch: 'Fehler beim fetch Lücken. Bitte versuchen Sie es erneut.',
            failed_acknowledge: 'Fehler beim acknowledge Lücke. Bitte versuchen Sie es erneut.',
            failed_remediate: 'Fehler beim remediate Lücke. Bitte versuchen Sie es erneut.',
            failed_accept_risk: 'Fehler beim accept Lücke Risiko. Bitte versuchen Sie es erneut.',
            failed_detect: 'Fehler beim Durchlauf Lücke detection. Bitte versuchen Sie es erneut.',
            loaded_count: '{count} Klassifizierungslücken geladen',
            related_object_id: 'Zugehörige Objekt-ID',
            related_object_type: 'Zugehöriger Objekttyp',
            gap_types: {
                unlabeled_asset: 'Nicht beschriftetes Objekt',
                missing_lifecycle: 'Fehlender Lebenszyklus',
                expired_retention: 'Abgelaufene Aufbewahrung',
                extended_pending_deletion: 'Aussstehende Loeschung',
            },
        },
        label_workflow: {
            title: 'Etikett-Workflow',
            proposed: 'Vorgeschlagen',
            under_review: 'In Prüfung',
            approved: 'Genehmigt',
            deprecated: 'Veraltet',
            submit_review: 'Zur Prüfung einreichen',
            approve: 'Etikett genehmigen',
            deprecate: 'Als veraltet markieren',
            bulk_approve: 'Massengenehmigung',
            bulk_approve_success: 'Etiketten erfolgreich genehmigt.',
            submit_success: 'Etikett zur Prüfung eingereicht.',
            approve_success: 'Etikett genehmigt.',
            deprecate_success: 'Etikett als veraltet markiert.',
        },
        review_tasks: {
            title: 'Prüfaufgaben',
            create: 'Neue Prüfaufgabe',
            system: 'System',
            owner: 'Eigentümer',
            description: 'Beschreibung',
            resolution: 'Lösung',
            resolve: 'Lösen',
            approve: 'Genehmigen',
            modify: 'Ändern',
            reject: 'Ablehnen',
            escalate: 'Eskalieren',
            escalated: 'Eskalierte Aufgaben',
            no_tasks: 'Keine Prüfaufgaben.',
            resolve_success: 'Prüfaufgabe gelöst.',
            status_pending: 'Ausstehend',
            status_resolved: 'Gelöst',
            status_escalated: 'Eskaliert',
            notes: 'Notizen',
            create_success: 'Überprüfungsaufgabe erstellt.',
            no_tasks_detail: 'Noch keine Überprüfungsaufgaben — erstellen Sie eine Aufgabe, um Label-Überprüfungen an Systemverantwortliche zuzuweisen.',
            label_id: 'Label-ID',
            system_id: 'System-ID',
            system_name: 'Systemname',
            owner_id: 'Eigentümer-ID',
            resolution_col: 'Loesung',
            notes_col: 'Notizen',
        },
        exceptions: {
            title: 'Ausnahmewarteschlange',
            create: 'Neue Ausnahme',
            severity: 'Schweregrad',
            system: 'System',
            owner: 'Eigentümer',
            resolve: 'Lösen',
            no_exceptions: 'Keine Ausnahmen gefunden.',
            resolve_success: 'Ausnahme gelöst.',
            dashboard_open: 'Offene Ausnahmen',
            dashboard_resolved: 'Gelöste Ausnahmen',
            dashboard_total: 'Gesamt',
            dashboard_by_severity: 'Nach Schweregrad',
            status_open: 'Offen',
            status_resolved: 'Gelöst',
            status_accepted_risk: 'Akzeptiertes Risiko',
            create_success: 'Ausnahme erstellt.',
            no_exceptions_detail: 'Noch keine Ausnahmen — Ausnahmen werden aus schwerwiegenden Lücken erstellt, die besondere Behandlung erfordern.',
            gap_id: 'Lücken-ID',
            system_id: 'System-ID',
            system_name: 'Systemname',
            owner_id: 'Eigentümer-ID',
            resolution_notes: 'Lösungshinweise',
            resolution_notes_placeholder: 'Beschreiben Sie, wie diese Ausnahme gelöst wurde...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Unternehmensdatenkarte',
            inventory: 'Dateninventar',
            total_systems: 'Systeme gesamt',
            total_labels: 'Etiketten gesamt',
            approved_count: 'Genehmigte Etiketten',
            proposed_count: 'Vorgeschlagene Etiketten',
            no_data: 'Keine Klassifizierungsdaten verfügbar.',
            no_approved_labels: 'Noch keine gekennzeichneten Objekte — ein Objekt erscheint auf der Datenkarte, sobald es ein Klassifizierungslabel hat, unabhängig davon, ob es vorgeschlagen, in Prüfung oder genehmigt ist.',
            loaded_count: 'Geladene Unternehmensdatenkarte',
        },
        discovery: {
            title: 'Entdeckungszyklen',
            create: 'Neuer Zyklus',
            cycle_name: 'Zyklusname',
            run_delta: 'Deltaerkennung ausführen',
            delta_success: 'Deltaerkennung abgeschlossen.',
            create_success: 'Entdeckungszyklus erstellt.',
            no_cycles: 'Keine Entdeckungszyklen.',
            status_created: 'Erstellt',
            status_scanning: 'Scannen',
            status_review: 'Rezension',
            status_approved: 'Genehmigt',
            status_published: 'Veröffentlicht',
            status_draft: 'Entwurf',
            running: 'Läuft',
            completed: 'Abgeschlossen',
            cancelled: 'Abgebrochen',
            delta_result_summary: '{added} hinzugefügt, {removed} entfernt, {changed} geänderte Labels gefunden.',
            delta_results: 'Delta-Ergebnisse',
            view_results: 'Ergebnisse anzeigen',
            scope_type: 'Bereichstyp',
            scope_type_all: 'Alle',
            scope_type_systems: 'Systeme',
            scope_type_datasets: 'Datensätze',
            scope_ids: 'Bereichs-IDs',
            scope_ids_placeholder: 'Geben Sie die IDs durch Kommas getrennt ein',
            cycle_explanation: 'Ein Erkennungszyklus scannt Ihre Dateninfrastruktur, um Datenbestände zu identifizieren, zu klassifizieren und zu verfolgen. Konfigurieren Sie den Bereich so, dass er auf bestimmte Systeme oder Datensätze abzielt.',
            empty_state_title: 'Noch keine Entdeckungszyklen',
            empty_state_description: 'Erstellen Sie Ihren ersten Erkennungszyklus, um mit dem Scannen und Klassifizieren Ihrer Datenbestände zu beginnen.',
            empty_state_cta: 'Erstellen Sie den ersten Zyklus',
            validation_name_required: 'Zyklusname ist erforderlich',
            validation_scope_type_required: 'Bereichstyp ist erforderlich',
            validation_scope_ids_required: 'Es ist mindestens eine Bereichs-ID erforderlich',
        },
        regulatory: {
            sensitivity_importance_info: 'Das Datenrecht (Gesetz 60/2024/QH15, Anforderung 18e4bfd1) erfordert eine Klassifizierung sowohl nach Sensibilität als auch nach Wichtigkeit als separate Dimensionen. Erstellen Sie für jede Dimension eine Taxonomie, um diese Anforderung zu erfüllen.',
            handling_rules_info: 'Jede Klassifizierungsebene sollte je nach Bedarf Handhabungsregeln definieren. b89981ac („Handhabungsregeln nach Klassifizierungsstufe definieren“).',
            template_internal_ids_info: 'Alle Klassifizierungsvorlagen verwenden interne IDs (z. B. vn_data_law_data_classification_record). Es wird keine Vorlage als offizielles Ministeriumsformular vorgelegt.',
            ai_risk_tiers_info: 'Das AI-Gesetz (Gesetz 134/2025/QH15, Anforderung 3a87ad63) definiert vier AI-Systemrisikostufen: minimal, begrenzt, hoch und inakzeptabel. Erstellen Sie eine AI-Risikotaxonomie mit diesen Ebenen.',
            ai_risk_tier_minimal: 'Minimal',
            ai_risk_tier_limited: 'Begrenzt',
            ai_risk_tier_high: 'Hoch',
            ai_risk_tier_unacceptable: 'Inakzeptabel',
            ai_register_info: 'AI-Klassifizierungsdatensätze sollten mit einem AI-Systemregistereintrag (Anforderung 0f4475d3) verknüpft sein, der Folgendes erfasst: Systemname, Eigentümer, Zweck, Bereitstellungskontext, Risikostufe, Anbieterbeteiligung.',
            ai_template_internal_info: 'AI-Klassifizierungsvorlagen verwenden interne IDs (z. B. AI-RISK-01). Es werden keine offiziellen AI-Platzhalter-IDs (AI-HC-01 bis AI-HC-08) verwendet.',
            ai_audit_trail_info: 'AI Klassifizierungsänderungen erzeugen einen versionierten Prüfpfad gemäß UC-VN-06: Akteur, Zeitstempel, vorheriger Wert, neuer Wert und Begründung.',
            lifecycle_data_d1_info: 'Lebenszyklusübergänge sind den Schritten des Datengesetzverfahrens DATA-D1 zugeordnet: Katalogisieren, klassifizieren, Handhabungsregeln definieren, Beweise erfassen, Überprüfungen planen.',
            lifecycle_deletion_link_info: 'Ein klassifizierter Datenbestand, der das Ende seiner Lebensdauer erreicht, sollte über das Modul „Deletion Orchestration“ einen Löschplandatensatz auslösen.',
            lifecycle_evidence_info: 'Bei jeder Änderung des Lebenszyklusstatus (Entwurf, aktiv, in Prüfung, archiviert, gelöscht) werden Akteur, Zeitstempel und Begründung als Audit-Trail-Eintrag erfasst.',
            escalation_timers_info: 'UC-VN-10 Eskalationsfristen: 5 Werktage für die Überprüfung durch den Eigentümer, 48 Stunden für die Ausnahmeeskalation, 72 Stunden für risikoreiche/sensible Elemente.',
            escalation_owner_review: '5 Werktage',
            escalation_exception: '48 Stunden',
            escalation_high_risk: '72 Stunden',
            gap_risk_areas_info: 'Datengesetzanforderung. f6c3060a („Datenrisikobewertung“) erfordert eine Lückenanalyse in vier Risikobereichen: Datenschutz, Cybersicherheit, Zugangskontrolle und Betriebsrisiko.',
            gap_risk_area_privacy: 'Datenschutz',
            gap_risk_area_cybersecurity: 'Cybersicherheit',
            gap_risk_area_access: 'Zugriffskontrolle',
            gap_risk_area_operational: 'Betrieblich',
            gap_framework_filter_info: 'Filtern Sie Lücken nach Regulierungsrahmen (VN_DATA_LAW oder VN_AI_LAW_2026), um relevante Lücken für jeden Regulierungsbereich zu sehen.',
            framework_vn_data_law: 'VN Datengesetz',
            framework_vn_ai_law: 'VN KI-Gesetz 2026',
            enterprise_map_approved_info: 'Diese Datenkarte zeigt nur genehmigte Klassifizierungsdatensätze gemäß UC-VN-10 Workflow-Schritt 10 („Genehmigte Labels werden zur aktuellen Unternehmenswahrheit“). Entwurfs- und vorgeschlagene Aufzeichnungen sind ausgeschlossen.',
            exports_bundle_info: 'Zu den Exportpaketen gehören: Klassifizierungsregister, Handhabungsregeln, Eigentum/RACI und Beweispaket – entsprechend UC-VN-10 Exportanforderungen und DATA-EVD-01 Beweisblattformat.',
        },
    },
    monitoring: {
        no_permission_title: 'Unzureichende Berechtigungen',
        no_permission_message: 'Sie haben keine Berechtigung auf dieses Uberwachungsmodul zuzugreifen.',
        errors: {
            fetch: 'Fehler beim load..',
            save: 'Fehler beim save..',
            fetch_programs_failed: 'Fehler beim fetch Programme..',
            fetch_kpi_failed: 'Fehler beim fetch KPI Daten..',
            create_snapshot_failed: 'Fehler beim create Snapshot..',
            fetch_periods_failed: 'Fehler beim fetch Zeiträume..',
            create_period_failed: 'Fehler beim create Zeitraum..',
            auto_generate_failed: 'Fehler beim auto-generate Zeiträume..',
            start_period_failed: 'Fehler beim start Zeitraum..',
            close_period_failed: 'Fehler beim close Zeitraum..',
            save_program_failed: 'Fehler beim save Programm..',
            activate_program_failed: 'Fehler beim activate Programm..',
            pause_program_failed: 'Fehler beim pause Programm..',
            complete_program_failed: 'Fehler beim abgeschlossen Programm..',
            archive_program_failed: 'Fehler beim archive Programm..',
            fetch_ric_templates_failed: 'Fehler beim fetch RIC Vorlagen..',
            fetch_ric_cases_failed: 'Fehler beim fetch RIC cases..',
            create_ric_case_failed: 'Fehler beim create RIC case..',
            transition_ric_failed: 'Fehler beim transition RIC case..',
            add_follow_up_failed: 'Fehler beim add follow-up..',
            respond_follow_up_failed: 'Fehler beim respond zu follow-up..',
            link_evidence_failed: 'Fehler beim link Nachweis..',
            fetch_task_templates_failed: 'Fehler beim fetch Aufgabe Vorlagen..',
            save_task_template_failed: 'Fehler beim save Aufgabe Vorlage..',
            delete_task_template_failed: 'Fehler beim delete Aufgabe Vorlage..',
            delete_framework_link_failed: 'Fehler beim Löschen der Framework-Verknüpfung.',
            seed_defaults_failed: 'Fehler beim seed Standardwerte..',
            fetch_tasks_failed: 'Fehler beim fetch Aufgaben..',
            create_task_failed: 'Fehler beim create Aufgabe..',
            instantiate_tasks_failed: 'Fehler beim instantiate Aufgaben..',
            start_task_failed: 'Fehler beim start Aufgabe..',
            complete_task_failed: 'Fehler beim abgeschlossen Aufgabe..',
            defer_task_failed: 'Fehler beim defer Aufgabe..',
            block_task_failed: 'Fehler beim block Aufgabe..',
            override_complete_failed: 'Fehler beim override abgeschlossen..',
            assign_task_failed: 'Fehler beim assign Aufgabe..',
            update_checklist_failed: 'Fehler beim update checklist..',
        },
        nav: {
            programs: 'Programme',
            periods: 'Zeiträume',
            task_templates: 'Aufgabenvorlagen',
            tasks: 'Aufgaben',
            kpi: 'KPI-Übersicht',
            ric: 'Behördenanfragen',
            framework_link: 'Rahmenüberwachung',
            authority_readiness: 'Behördenbereitschaft',
            change_review: 'Änderungsüberprüfung',
            raci: 'RACI-Matrix',
        },
        programs: {
            page_title: 'Überwachungsprogramme',
            title: 'Überwachungsprogramme',
            new: 'Neues Programm',
            edit: 'Programm bearbeiten',
            name: 'Bezeichnung',
            scope: 'Umfang',
            frequency: 'Häufigkeit',
            owner: 'Verantwortlicher',
            activate: 'Aktivieren',
            pause: 'Pausieren',
            resume: 'Fortsetzen',
            complete: 'Abschließen',
            archive: 'Archivieren',
            custom_frequency_days: 'Benutzerdefiniert Häufigkeit (days)',
            select_user: 'Benutzer auswählen…',
            assign_training: 'Schulung zuweisen',
            linked_forms: 'Verknüpfte Formulare',
            linked_forms_none: 'Noch keine verknüpften Formulare',
            loaded_count: '{0} Programme geladen',
            scope_labels: {
                full: 'Vollständig',
                partial: 'Teilweise',
                targeted: 'Gezielt',
                consent: 'Einwilligung',
                dpia: 'DPIA',
                vendors: 'Lieferanten',
                classification: 'Klassifizierung',
                rights_requests: 'Betroffenenanfragen',
                data_mapping: 'Datenkartierung',
                transfers: 'Übertragungen',
                security_monitoring: 'Sicherheitsüberwachung',
                authority_response: 'Antwort der Behörde',
                evidence_preservation: 'Beweissicherung',
                localization_review: 'Überprüfung der Lokalisierung',
                ai_system_registration: 'AI Systemregistrierung',
                ai_pre_deployment: 'AI Bewertung vor der Bereitstellung',
                ai_transparency: 'AI Transparenzprüfung',
                ai_incident_review: 'AI Vorfallüberprüfung',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Überwachungszeiträume',
            title: 'Prüfungszeiträume',
            new: 'Neuer Zeitraum',
            label: 'Zeitraumbezeichnung',
            start_date: 'Startdatum',
            end_date: 'Enddatum',
            reviewers: 'Prüfer',
            auto_generate: 'Zeiträume automatisch generieren',
            start: 'Starten',
            close: 'Schließen',
            change_digest: 'Änderungsübersicht',
            no_digest: 'Kein Digest verfügbar.',
            reviewers_count: 'Prüfer Anzahl',
            select_program: 'Auswählen Programm',
            select_program_prompt: 'Auswählen ein Programm zu view Zeiträume.',
            hide_digest: 'Ausblenden Digest',
            loaded_count: '{0} Zeiträume geladen',
            periods_auto_generated: 'Zeiträume automatisch generiert',
            label_placeholder: 'z.B. 2026-Q1',
        },
        task_templates: {
            page_title: 'Aufgabenvorlagen',
            title: 'Aufgabenvorlagen',
            new: 'Neue Vorlage',
            edit: 'Vorlage bearbeiten',
            category: 'Kategorie',
            priority: 'Priorität',
            checklist_items: 'Checklisten-Elemente',
            effort: 'Geschätzter Aufwand (Stunden)',
            pre_seeded: 'Vorgefüllt',
            seed_defaults: 'Standardwerte laden',
            add_checklist: 'Checklisten-Element hinzufügen',
            guidance: 'Hinweise',
            all_categories: 'Alle Kategorien',
            loaded_count: '{0} Aufgabenvorlagen geladen',
            defaults_seeded: 'Standard-Aufgabenvorlagen erstellt',
            checklist_item_label: 'Checklisten-Element {0}',
            remove_checklist_item: 'Checklisten-Element {0} entfernen',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Prüfungsaufgaben',
            title: 'Prüfungsaufgaben',
            new: 'Neue Aufgabe',
            assigned_to: 'Zugewiesen an',
            due_date: 'Fälligkeitsdatum',
            checklist: 'Checkliste',
            start: 'Starten',
            complete: 'Abschließen',
            defer: 'Zurückstellen',
            block: 'Blockieren',
            override_complete: 'Abschluss erzwingen',
            link_evidence: 'Nachweis verknüpfen',
            direct_upload_label: 'Nachweisdatei hochladen',
            instantiate: 'Aus Vorlagen erstellen',
            defer_reason: 'Grund für die Zurückstellung',
            override_justification: 'Begründung für die Erzwingung',
            filter_assignee: 'Filtern nach assignee',
            select_period: 'Auswählen Zeitraum',
            select_program: 'Auswählen Programm',
            select_program_period_prompt: 'Auswählen ein Programm und Zeitraum zu view Aufgaben.',
            assign_placeholder: 'Zuweisen zu...',
            loaded_count: '{0} Aufgaben geladen',
            tasks_instantiated: 'Aufgaben aus Vorlagen erstellt',
            overdue: 'Überfällig',
            checklist_for: 'Checkliste für',
            manage_checklist: 'Checkliste verwalten',
            add_checklist_item: 'Element hinzufügen',
            checklist_item_label: 'Element',
            checklist_item_description: 'Beschreibung',
            remove_checklist_item: 'Element entfernen',
            save_checklist: 'Checkliste speichern',
            no_checklist_items: 'Noch keine Checklistenelemente.',
            confirm_delete_attachment: 'Möchten Sie diese Nachweisdatei wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
        },
        kpi: {
            page_title: 'KPI-Übersicht',
            title: 'KPI-Übersicht',
            kpi_name: 'KPI-Name',
            current_value: 'Aktueller Wert',
            target: 'Zielwert',
            trends: 'Entwicklungen',
            capture_snapshot: 'Erfassen Snapshot',
            select_program: 'Auswählen Programm',
            select_program_prompt: 'Auswählen ein Programm zu view KPIs.',
            snapshot: 'Momentaufnahme',
            kpi_labels: {
                on_track: 'On Verfolgen',
                at_risk: 'At Risiko',
                behind: 'Zurückliegend',
                rights_request_sla_rate: 'SLA-Rate für Betroffenenanfragen',
                vendor_review_currency: 'Lieferantenprüfung Aktualität',
                dpia_completion_rate: 'DPIA-Abschlussrate',
                classification_coverage: 'Klassifizierungsabdeckung',
                consent_policy_currency: 'Einwilligungsrichtlinie Aktualität',
                security_event_detection_rate: 'Erkennungsrate von Sicherheitsereignissen',
                evidence_preservation_compliance_rate: 'Compliance-Rate bei der Beweissicherung',
                authority_response_timeliness: 'Rechtzeitige Reaktion der Behörden',
                localization_readiness_score: 'Bewertung der Lokalisierungsbereitschaft',
                ai_register_completeness: 'AI Vollständigkeit des Systemregisters',
                pre_deployment_gate_pass_rate: 'Gate-Pass-Rate vor der Bereitstellung',
                transparency_disclosure_coverage: 'Abdeckung der Transparenzoffenlegung',
                ai_incident_readiness_score: 'AI Incident Readiness Score',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'KPIs zum Cybersicherheitsrecht: Erkennungsrate von Sicherheitsereignissen, Compliance-Rate bei der Beweissicherung, Pünktlichkeit der behördlichen Reaktion, Bewertung der Lokalisierungsbereitschaft.',
            ai_law_kpi_info: 'AI Gesetzes-KPIs: AI Systemregister-Vollständigkeit, Pre-Deployment-Gate-Pass-Rate, Transparenz-Offenlegungsabdeckung, AI Incident Readiness Score.',
            no_data: 'No Daten available.',
            snapshot_date: 'Snapshot Datum',
            loaded_count: '{0} KPI-Momentaufnahmen geladen',
            snapshot_captured: 'KPI-Momentaufnahme erstellt',
            showing_chart: 'Diagrammansicht',
            showing_table: 'Datentabellenansicht',
            aria_current_values: 'KPI aktuelle Werte',
            kpi_label: 'KPI',
            actual: 'Ist',
            trend: 'Trend',
            notes: 'Anmerkungen',
            trend_up: 'Steigend',
            trend_down: 'Fallend',
            trend_stable: 'Stabil',
        },
        ric: {
            page_title: 'RIC-Fälle',
            title: 'Behördenanfragen',
            new: 'Neue Behördenanfrage',
            reference: 'Referenz',
            regulator: 'Aufsichtsbehörde',
            inquiry_type: 'Art der Anfrage',
            deadline: 'Antwortfrist',
            lead: 'Verantwortlicher',
            follow_ups: 'Nachverfolgungen',
            add_follow_up: 'Nachverfolgung hinzufügen',
            respond: 'Antworten',
            question: 'Frage',
            response: 'Antwort',
            asked_at: 'Gefragt am',
            no_follow_ups: 'Noch keine Nachverfolgungen.',
            received_date: 'Empfangen Datum',
            responded_at: 'Reagiert At',
            hide_follow_ups: 'Ausblenden Follow-ups',
            inquiry_types: {
                general: 'Allgemein',
                investigation: 'Untersuchung',
                audit: 'Prüfung',
                complaint: 'Beschwerde',
                general_audit: 'Allgemeine Prüfung',
                rights_requests_review: 'Prüfung der Betroffenenanfragen',
                cross_border_transfer_review: 'Prüfung grenzüberschreitender Übertragungen',
                vendor_governance_review: 'Prüfung der Lieferantensteuerung',
                consent_management_review: 'Prüfung des Einwilligungsmanagements',
                data_mapping_ropa_review: 'Prüfung der Datenkartierung / ROPA',
                incident_readiness_review: 'Prüfung der Vorfallbereitschaft',
                security_access_control_review: 'Prüfung von Sicherheit und Zugriffskontrolle',
                targeted_processing_review: 'Prüfung gezielter Verarbeitung',
                bank_readiness_pack: 'Bank-Bereitschaftspaket',
            },
            link_evidence: 'Verknüpfen Nachweis',
            loaded_count: '{0} RIC-Fälle geladen',
            to_follow_up: 'zur Nachverfolgungsfrage',
            overdue: 'Überfällig',
            overdue_indicator: 'Antwortfrist überschritten',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Erhalten',
                in_triage: 'In Sichtung',
                evidence_gathering: 'Beweissammlung',
                response_drafting: 'Antwortentwurf',
                internal_review: 'Interne Prüfung',
                submitted: 'Eingereicht',
                follow_up: 'Nachverfolgung',
                closed: 'Geschlossen',
            },
        },
        framework_link: {
            title: 'Rahmenverknüpfte Überwachung',
            link_program: 'Programm mit Rahmen verknüpfen',
            framework_code: 'Rahmencode',
            select_framework: 'Rahmenwerk auswählen...',
            select_program: 'Programm auswählen...',
            readiness_score: 'Bereitschaftsbewertung',
            no_links: 'Keine rahmenverknüpften Programme gefunden',
            seed_defaults: 'Standardvorlagen erstellen',
            program_id: 'Programm-ID',
            program: 'Programm',
        },
        authority_readiness: {
            title: 'Behördenanfragenbereitschaft',
            create_entry: 'Neuer Bereitschaftseintrag',
            select_framework: 'Rahmenwerk auswählen...',
            framework_code: 'Rahmenwerk',
            readiness_status: 'Bereitschaftsstatus',
            readiness_package: 'Bereitschaftspaket',
            run_drill: 'Bereitschaftsübung durchführen',
            drill_success: 'Bereitschaftsübung erfasst',
            last_drill: 'Letzte Übung',
            never_drilled: 'Nie',
            no_readiness: 'Keine Behördenbereitschaftsdaten gefunden',
            entry_created: 'Bereitschaftseintrag erstellt',
            ready: 'Bereit',
            partial: 'Teilweise',
            not_ready: 'Nicht bereit',
            items: 'Elemente',
        },
        change_review: {
            title: 'Regulatorische Änderungsüberprüfung',
            new_review: 'Neue Änderungsüberprüfung',
            change_id: 'Änderungs-ID',
            select_change: 'Änderung auswählen...',
            select_framework: 'Rahmenwerk auswählen...',
            impact_assessment: 'Auswirkungsbewertung',
            decision: 'Entscheidung',
            no_action: 'Keine Maßnahme',
            internal_update: 'Interne Aktualisierung',
            authority_filing: 'Behördenmeldung',
            no_reviews: 'Keine Änderungsüberprüfungen gefunden',
            auto_cycle: 'Automatischer Überprüfungszyklus',
            cadence: 'Überprüfungsrhythmus',
            framework: 'Rahmenwerk',
        },
        export_csv: 'Exportieren CSV',
        exporting: 'Exportieren…',
        export_success: 'CSV erfolgreich exportiert.',
        export_failed: 'CSV Export fehlgeschlagen.',
        export_attestation_pack: 'Attestierungspaket exportieren',
        staleness: {
            badge_label: '{0} veraltete Elemente',
            days_since: '{0} Tage seit der letzten Aktualisierung',
            detail_title: 'Veraltete Gegenstände',
            accept_justification: 'Akzeptieren Sie mit Begründung',
            justification_label: 'Begründung',
            no_stale: 'Keine veralteten Artikel gefunden.',
            threshold_label: 'Verjährungsschwelle (Tage)',
            threshold_min: 'Mindestens 30 Tage',
            threshold_max: 'Maximal 730 Tage',
            threshold_saved: 'Schwellenwert für Veraltung aktualisiert.',
        },
        csv_columns: {
            name: 'Name',
            status: 'Status',
            owner: 'Besitzer',
            created_at: 'Erstellt am',
            updated_at: 'Aktualisiert am',
            scope: 'Umfang',
            frameworks: 'Rahmenwerke',
            period_label: 'Periodenbeschriftung',
            start_date: 'Startdatum',
            end_date: 'Enddatum',
            task_count: 'Aufgabenanzahl',
            completion_rate: 'Abschlussrate',
            title: 'Titel',
            assigned_to: 'Zugewiesen an',
            due_date: 'Fälligkeitsdatum',
            priority: 'Priorität',
            completed_at: 'Abgeschlossen am',
            kpi_code: 'KPI Code',
            kpi_label: 'KPI Beschriftung',
            target: 'Ziel',
            actual: 'Tatsächlich',
            trend: 'Trend',
            reference: 'Referenz',
            regulator: 'Regulator',
            inquiry_type: 'Anfragetyp',
            lead: 'Führen',
            received_date: 'Empfangsdatum',
            deadline: 'Frist',
            framework_code: 'Rahmencode',
            readiness_status: 'Bereitschaftsstatus',
            package_count: 'Paketanzahl',
            change_id: 'ID ändern',
            impact_assessment: 'Folgenabschätzung',
            decision: 'Entscheidung',
            program: 'Programm',
            readiness_score: 'Bereitschaftsbewertung',
            category: 'Kategorie',
            checklist_count: 'Anzahl der Checklisten',
            effort_hours: 'Aufwand (Stunden)',
            version: 'Version',
            department: 'Abteilung',
            function_area: 'Funktionsbereich',
            role: 'Rolle',
            assignee: 'Bevollmächtigter',
            frequency: 'Häufigkeit',
        },
        raci: {
            page_title: 'Governance RACI-Matrix',
            title: 'Governance RACI-Matrix',
            department: 'Abteilung',
            function_area: 'Funktionsbereich',
            responsible: 'Verantwortlich',
            accountable: 'Verantwortlich',
            consulted: 'Konsultiert',
            informed: 'Informiert',
            assign_role: 'RACI-Rolle zuweisen',
            select_program: 'Wählen Sie Programm',
            select_program_prompt: 'Wählen Sie ein Programm aus, um die RACI-Matrix anzuzeigen.',
            no_assignments: 'Keine RACI-Zuweisungen gefunden.',
            role_updated: 'RACI-Zuweisung aktualisiert.',
            role_labels: {
                R: 'Verantwortlich',
                A: 'Verantwortlich',
                C: 'Konsultiert',
                I: 'Informiert',
            },
            loaded_count: '{0} RACI-Zuweisungen geladen',
            single_accountability_warning: 'Pro Funktionsbereich ist nur eine verantwortliche Rolle (A) zulässig. Durch die Zuweisung einer neuen verantwortlichen Rolle wird die bestehende ersetzt.',
            cybersec_function_areas: 'Funktionsbereiche des Cybersicherheitsrechts: Reaktion auf Vorfälle, Beweissicherung, Zusammenarbeit mit Behörden, Überprüfung der Lokalisierung.',
            ai_law_function_areas: 'AI Rechtsfunktionsbereiche: Systemregistrierung, Bewertung vor der Bereitstellung, Transparenz, Behandlung von Vorfällen.',
            suggested_function_areas: 'Empfohlene Funktionsbereiche',
        },
        prior_cycle: {
            link_label: 'Link zum vorherigen Zyklus',
            select_prior: 'Vorherigen Zyklus auswählen…',
            linked: 'Jahr für Jahr',
            no_prior: 'Keine früheren Zyklen verfügbar.',
            linked_success: 'Vorheriger Zyklus verknüpft.',
        },
        digest_table: {
            module: 'Modul',
            entity_type: 'Entitätstyp',
            change_type: 'Typ ändern',
            description: 'Beschreibung',
            changed_at: 'Geändert am',
        },
        view_related_ric: 'Verwandte RIC-Fälle anzeigen',
        linked_program: 'Verknüpftes Programm',
        nav_raci: 'RACI-Matrix',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Antwortentwurf',
            drafting: 'Ausarbeitung…',
            results_heading: 'Vorgefasste Antworten ({count})',
            tokens_label: '≈ {count} Token',
            counsel_label: 'Beratungsprüfung erforderlich:',
            counsel_default: 'Mindestens eine Antwort muss vor dem Absenden von einem Berater überprüft werden.',
            requires_counsel_label: 'Erfordert eine anwaltliche Überprüfung.',
            accept_label: 'Entwurf akzeptieren',
            reject_label: 'Entwurf ablehnen',
            error_generic: 'Die Antwort konnte nicht verfasst werden. Versuchen Sie es erneut oder verfeinern Sie den Anfragetext.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Berechnen Sie den Zeitplan für die Benachrichtigung bei Compute-Verstößen',
            computing: 'Computer…',
            results_heading: 'Fristen für die Meldung von Verstößen ({count})',
            tokens_label: '≈ {count} Token',
            handoff_label: 'Forseti schlägt vor:',
            empty_label: 'Kein installiertes Rahmenwerk erklärt eine Meldepflicht für diesen Vorfall.',
            mandatory_label: 'Pflichtfelder:',
            draft_label: 'Notifizierungsentwurf',
            remaining_label: 'Noch {count}h',
            overdue_label: 'ÜBERFÄLLIG von {count}h',
            acknowledge_label: 'Zeitplan bestätigen',
            acknowledging: 'Anerkennung…',
            error_generic: 'Der Zeitrahmen für den Verstoß konnte nicht berechnet werden. Versuchen Sie es erneut.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Bestätigt Verletzung',
            no: 'Nicht ein Verletzung',
            uncertain: 'Ausstehend',
        },
        errors: {
            fetch: 'Fehler beim load Vorfälle..',
            save: 'Fehler beim save Vorfall..',
            fetch_failed: 'Fehler beim fetch Vorfälle..',
            save_failed: 'Fehler beim save Vorfall..',
            transition_failed: 'Fehler beim transition Vorfall..',
            close_failed: 'Fehler beim close Vorfall..',
            fetch_timeline_failed: 'Fehler beim fetch Zeitverlauf..',
            fetch_decisions_failed: 'Fehler beim fetch decisions..',
            triage_failed: 'Fehler beim update triage..',
            impact_update_failed: 'Fehler beim update impact Bewertung..',
            breach_update_failed: 'Fehler beim update breach Bewertung..',
            create_timeline_failed: 'Fehler beim create Zeitverlauf Eintrag..',
            create_decision_failed: 'Fehler beim create Entscheidung..',
            notification_update_failed: 'Fehler beim update Benachrichtigung..',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Fehler beim fetch Aufgaben..',
            create_task_failed: 'Fehler beim create Aufgabe..',
            update_task_status_failed: 'Fehler beim Aktualisieren des Aufgabenstatus.',
            update_checklist_failed: 'Fehler beim update checklist..',
            assign_task_failed: 'Fehler beim assign Aufgabe..',
            escalate_task_failed: 'Fehler beim escalate Aufgabe..',
            fetch_templates_failed: 'Fehler beim fetch Vorlagen..',
            permission_denied: 'Sie haben keine Berechtigung auf diesen Bereich zuzugreifen. Wenden Sie sich an Ihren Administrator.',
        },
        harm_likelihood: {
            unlikely: 'Unwahrscheinlich',
            possible: 'Möglich',
            likely: 'Wahrscheinlich',
            very_likely: 'Sehr Wahrscheinlich',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'E-Mail',
            phone: 'Telefon',
            internal_system: 'Intern System',
            third_party: 'Dritt Party',
            automated_detection: 'Automatisiert Erkennung',
        },
        task_transition_labels: {
            start: 'Starten',
            complete: 'Abschließen',
            block: 'Blockieren',
            defer: 'Aufschieben',
            cancel: 'Abbrechen',
            unblock: 'Blockierung aufheben',
        },
        transition_labels: {
            start: 'Starten',
            complete: 'Abschließen',
            block: 'Blockieren',
            defer: 'Aufschieben',
        },
        nav: {
            all: 'Alle Vorfälle',
            tasks: 'Reaktionsaufgaben',
            reports: 'Berichte',
            breach_notifications: 'Verletzungsmeldungen',
            supplements: 'Ergänzungen',
            remediation: 'Behebung',
            authority_requests: 'Behördenanfragen',
            disclosure_register: 'Offenlegungsregister',
        },
        list: {
            page_title: 'Vorfälle',
            title: 'Vorfälle',
            new: 'Neuer Vorfall',
            edit: 'Vorfall bearbeiten',
            incident_title: 'Titel',
            severity: 'Schweregrad',
            reported_at: 'Gemeldet am',
            reported_by: 'Gemeldet von',
            assigned_to: 'Zugewiesen an',
            view_detail: 'Details anzeigen',
            reference: 'Referenz',
            lead: 'Leitung',
            reported_via: 'Gemeldet Via',
            discovery_date: 'Erkennung Datum',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Ausblenden Details',
            close_incident: 'Schließen Vorfall',
            close_justification_prompt: 'Bitte provide ein Begründung für closing dieses Vorfall.',
            justification: 'Begründung',
            lead_id: 'Leitung ID',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Kontakt',
            category: 'Kategorie',
            not_assessed: 'Nicht assessed',
        },
        detail: {
            page_title: 'Details zum Vorfall',
            title: 'Vorfalldetails',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Übersicht',
                triage: 'Sichtung',
                impact: 'Auswirkungsbewertung',
                breach: 'Datenschutzverletzungsbewertung',
                timeline: 'Zeitverlauf',
                decisions: 'Entscheidungen',
                evidence: 'Nachweise',
                audit_trail: 'Prüfpfad',
            },
            evidence_upload_label: 'Nachweisdatei anhängen',
            evidence_uploaded: 'Nachweis hochgeladen',
            evidence_deleted: 'Nachweis gelöscht',
            evidence_delete_error: 'Nachweis konnte nicht gelöscht werden',
            evidence_uploaded_at: 'Hochgeladen',
            evidence_empty: 'Keine Nachweisdateien an diesen Vorfall angehängt.',
            evidence_size: 'Größe',
            triage_notes: 'Triage-Notizen',
            initial_classification: 'Erstklassifizierung',
            individuals_affected: 'Anzahl betroffener Personen',
            data_categories: 'Betroffene Datenkategorien',
            systems_compromised: 'Kompromittierte Systeme',
            geographic_scope: 'Geografischer Umfang',
            breach_determination: 'Feststellung einer Datenschutzverletzung',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Meldung erforderlich',
            notification_deadline: 'Meldefrist',
            pdpc_reportable: 'Meldepflicht an Aufsichtsbehörde',
            add_timeline: 'Zeitverlaufseintrag hinzufügen',
            add_decision: 'Entscheidung hinzufügen',
            not_found: 'Vorfall not gefunden.',
            team_members: 'Team Mitglieder',
            checklist: 'Checkliste',
            completed: 'Abgeschlossen',
            impact_severity_score: 'Calculated Auswirkung Schweregrad Bewertung',
            records_affected: 'Datensätze Affected',
            potential_consequences: 'Potentiell Konsequenzen',
            harm_likelihood: 'Schaden Wahrscheinlichkeit',
            legal_basis: 'Rechtlich Grundlage',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Gerichtsbarkeiten',
            jurisdictions_comma: 'Gerichtsbarkeiten (comma-separated)',
            jurisdiction: 'Gerichtsbarkeit',
            authority_status: 'Behörde Status',
            subject_status: 'Betreff Status',
            authority_deadline: 'Behörde Frist',
            subject_deadline: 'Betreff Frist',
            mark_authority_done: 'Markieren Behörde Done',
            mark_subject_done: 'Markieren Betreff Done',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Nicht assessed',
            event_type: 'Ereignis Typ',
            timestamp: 'Zeitstempel',
            actor: 'Akteur',
            no_timeline_events: 'No Zeitverlauf Ereignisse yet.',
            decision_type: 'Entscheidung Typ',
            decision: 'Entscheidung',
            rationale: 'Begründung',
            decision_made: 'Entscheidung Made',
            no_decisions: 'Noch keine Entscheidungen erfasst.',
            no_audit_events: 'Es wurden noch keine Audit-Ereignisse erfasst.',
            export_pdf: 'PDF exportieren',
            pdf_title: 'Vorfallzusammenfassung',
            description: 'Beschreibung',
            linked_records: 'Verknüpfte Datensätze',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Aufgaben zur Reaktion auf Vorfälle',
            title: 'Vorfallreaktionsaufgaben',
            new: 'Neue Aufgabe',
            edit: 'Aufgabe bearbeiten',
            task_type: 'Aufgabentyp',
            assignee: 'Zugewiesene Person',
            due_date: 'Fälligkeitsdatum',
            dependencies: 'Abhängigkeiten',
            mark_complete: 'Als erledigt markieren',
            templates: 'Aufgabenvorlagen',
            select_incident: 'Vorfall auswählen',
            all_statuses: 'Alle Status',
            all_categories: 'Alle Kategorien',
            new_response_task: 'Neue Reaktionsaufgabe',
            category: 'Kategorie',
            priority: 'Priorität',
            depends_on: 'Abhängig von (kommagetrennte Aufgaben-IDs)',
            checklist_items: 'Checklisten-Elemente',
            remove: 'Entfernen',
            add: 'Hinzufügen',
            new_checklist_item: 'Neues Checklisten-Element',
            no_templates: 'Keine Vorlagen verfügbar.',
            assign_task: 'Aufgabe zuweisen',
            assign_to: 'Zuweisen an',
            assign: 'Zuweisen',
            select_incident_prompt: 'Bitte wählen Sie einen Vorfall aus, um dessen Reaktionsaufgaben anzuzeigen.',
            checklist: 'Checkliste',
            hide_checklist: 'Checkliste ausblenden',
            escalate: 'Eskalieren',
            escalations: 'Eskalationen',
            no_checklist_items: 'Keine Checklisten-Elemente.',
        },
        reports: {
            page_title: 'Vorfallberichte',
            title: 'Vorfallberichte',
            summary: 'Zusammenfassende Statistiken',
            by_severity: 'Nach Schweregrad',
            by_status: 'Nach Status',
            recent: 'Aktuelle Vorfälle',
            total_incidents: 'Vorfälle gesamt',
            open: 'Offen',
            closed: 'Geschlossen',
            export_csv: 'CSV exportieren',
            export_pdf: 'PDF exportieren',
            export_success: 'Bericht erfolgreich exportiert.',
            export_error: 'Bericht konnte nicht exportiert werden.',
            reference: 'Referenz',
            view_report: 'Bericht anzeigen',
        },
        statuses: {
            reported: 'Gemeldet',
            triage: 'Sichtung',
            contained: 'Eingedämmt',
            investigation: 'Untersuchung',
            remediation: 'Behebung',
            closed: 'Geschlossen',
            false_positive: 'Fehlalarm',
        },
        task_statuses: {
            pending: 'Ausstehend',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            blocked: 'Blockiert',
            cancelled: 'Abgebrochen',
        },
        severity: {
            low: 'Niedrig',
            medium: 'Mittel',
            high: 'Hoch',
            critical: 'Kritisch',
        },
        task_types: {
            containment: 'Eindämmung',
            investigation: 'Untersuchung',
            remediation: 'Behebung',
            communication: 'Kommunikation',
            documentation: 'Dokumentation',
            notification: 'Benachrichtigung',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Daten Verletzung',
            unauthorized_access: 'Unbefugt Zugriff',
            data_loss: 'Daten Loss',
            system_compromise: 'Systemkompromittierung',
            malware: 'Schadsoftware',
            phishing: 'Phishing-Angriff',
            insider_threat: 'Insider-Bedrohung',
            other: 'Andere',
        },
        reported_via_options: {
            email: 'E-Mail',
            phone: 'Telefon',
            internal_system: 'Intern System',
            third_party: 'Dritt Party',
            automated_detection: 'Automatisiert Erkennung',
        },
        harm_likelihood_options: {
            unlikely: 'Unwahrscheinlich',
            possible: 'Möglich',
            likely: 'Wahrscheinlich',
            very_likely: 'Sehr Wahrscheinlich',
        },
        breach_determination_options: {
            confirmed_breach: 'Bestätigt Verletzung',
            suspected_breach: 'Verdächtig Verletzung',
            not_a_breach: 'Nicht ein Verletzung',
            pending: 'Ausstehend',
        },
        timeline_event_types: {
            detection: 'Erkennung',
            escalation: 'Eskalation',
            containment: 'Eindämmung',
            investigation_start: 'Untersuchung Starten',
            evidence_collected: 'Nachweis Collected',
            notification_sent: 'Benachrichtigung Sent',
            remediation_action: 'Behebung Aktion',
            status_change: 'Status Ändern',
            communication: 'Kommunikation',
            other: 'Andere',
        },
        decision_types: {
            containment: 'Eindämmung',
            notification: 'Benachrichtigung',
            remediation: 'Behebung',
            escalation: 'Eskalation',
            closure: 'Abschluss',
            other: 'Andere',
        },
        task_transitions: {
            start: 'Starten',
            complete: 'Abschließen',
            block: 'Blockieren',
            cancel: 'Abbrechen',
            unblock: 'Entsperren',
        },
        announce: {
            loaded: '{count} Vorfälle geladen',
            reports_loaded: '{count} Vorfallberichte geladen',
            tasks_loaded: '{count} Aufgaben geladen',
            created: 'Vorfall erfolgreich erstellt',
            updated: 'Vorfall erfolgreich aktualisiert',
            closed: 'Vorfall erfolgreich abgeschlossen',
            status_changed: 'Vorfallstatus geändert',
            triage_completed: 'Triage erfolgreich abgeschlossen',
            impact_updated: 'Folgenabschätzung aktualisiert',
            breach_updated: 'Bewertung des Verstoßes aktualisiert',
            timeline_added: 'Timeline-Ereignis hinzugefügt',
            decision_recorded: 'Entscheidung protokolliert',
            notification_updated: 'Benachrichtigungsstatus aktualisiert',
            task_created: 'Aufgabe erfolgreich erstellt',
            task_status_changed: 'Aufgabenstatus geändert',
            checklist_completed: 'Checklistenpunkt abgeschlossen',
            checklist_unchecked: 'Checklistenelement deaktiviert',
            task_assigned: 'Aufgabe erfolgreich zugewiesen',
            task_escalated: 'Aufgabe eskaliert',
            export_initiated: 'Export eingeleitet',
        },
        breach_notifications: {
            page_title: 'Benachrichtigungen über Verstöße',
            title: 'Benachrichtigungen über Verstöße',
            new_notification: 'Neue Benachrichtigung',
            no_notifications: 'Es wurden keine Benachrichtigungen über Verstöße gefunden',
            status: 'Status',
            submit_to_mps: 'Senden an MPS',
            decision: 'Entscheidung',
            decision_maker: 'Entscheidungsträger',
            notify: 'Benachrichtigen',
            do_not_notify: 'Nicht benachrichtigen',
            rationale: 'Begründung',
            filing_deadline: 'Anmeldeschluss',
            overdue: 'Überfällig',
            due_soon: 'Bald fällig',
            on_track: 'Im Zeitplan',
            legal_review: 'Rechtliche Überprüfung',
            incident_id: 'Vorfall-ID',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Bewertung',
                decision_to_notify: 'Entscheidung zur Benachrichtigung',
                notification_drafted: 'Benachrichtigung erstellt',
                legal_review: 'Rechtliche Überprüfung',
                submitted_to_authority: 'Bei Behörde eingereicht',
                supplement_requested: 'Ergänzung angefordert',
                supplement_submitted: 'Ergänzung eingereicht',
                acknowledged: 'Anerkannt',
                closed: 'Geschlossen',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Ergänzungswünsche',
            title: 'Ergänzungswünsche',
            new_supplement: 'Neue Ergänzung',
            no_supplements: 'Keine Ergänzungswünsche gefunden',
            request_details: 'Details anfordern',
            response_package: 'Antwortpaket',
            submit_response: 'Antwort senden',
            deadline: 'Frist',
            notification_id: 'Benachrichtigungs-ID',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Geben Sie eine Benachrichtigungs-ID ein, um Ergänzungen anzuzeigen.',
            supplement_statuses: {
                requested: 'Angefordert',
                drafting: 'Ausarbeitung',
                submitted: 'Eingereicht',
                accepted: 'Akzeptiert',
                rejected: 'Abgelehnt',
            },
        },
        remediation: {
            page_title: 'Abhilfemaßnahmen',
            title: 'Abhilfemaßnahmen',
            new_action: 'Neue Aktion',
            no_actions: 'Keine Abhilfemaßnahmen gefunden',
            description: 'Beschreibung',
            owner: 'Besitzer',
            department: 'Abteilung',
            deadline: 'Frist',
            statuses: {
                planned: 'Geplant',
                in_progress: 'In Bearbeitung',
                completed: 'Abgeschlossen',
                verified: 'Verifiziert',
            },
            verify: 'Überprüfen',
            incident_id: 'Vorfall-ID',
            enter_incident_id: 'Geben Sie eine Vorfall-ID ein, um Abhilfemaßnahmen anzuzeigen.',
            start: 'Starten',
            complete_action: 'Abschließen',
        },
        authority_requests: {
            page_title: 'Behördenanfragen',
            title: 'Behördenanfragen',
            new_request: 'Neue Anfrage',
            no_requests: 'Keine Autoritätsanfragen gefunden',
            authority_name: 'Name der Behörde',
            request_details: 'Details anfordern',
            response_package: 'Antwortpaket',
            submission_channel: 'Einreichungskanal',
            submit_response: 'Antwort senden',
            deadline: 'Frist',
            confirmation: 'Bestätigung',
            incident_id_optional: 'Vorfall-ID (optional)',
            response_context_label: 'Antwortkontext (für Materialisierung des Einreichungsdokuments)',
            response_context_none: '(Materialisierung überspringen)',
            response_context_supplement: 'Ergänzungsantwort',
            response_context_information: 'Antwort auf Auskunftsersuchen',
            response_context_inspection: 'Antwort auf Prüfung',
            materialisation_status_column: 'Einreichungsdokument',
            materialisation_pending: 'Ausstehend',
            materialisation_success: 'Materialisiert',
            materialisation_failed: 'Fehlgeschlagen',
            materialisation_retry_button: 'Wiederholen',
            ar_statuses: {
                received: 'Erhalten',
                verified: 'Verifiziert',
                assembling: 'Zusammenbau',
                submitted: 'Eingereicht',
                closed: 'Geschlossen',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Rechtliche Überprüfung',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Checkliste',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Offenlegungsregister',
            title: 'Offenlegungsregister',
            new_entry: 'Neuer Eintrag',
            no_entries: 'Keine Offenlegungsregistereinträge gefunden',
            disclosure_type: 'Offenlegungstyp',
            disclosure_date: 'Offenlegungsdatum',
            recipient: 'Empfänger',
            authority_name: 'Name der Behörde',
            channel: 'Kanal',
            content_summary: 'Inhaltszusammenfassung',
            incident_id_optional: 'Vorfall-ID (optional)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Einrichtungsassistent',
            tours: 'Modulführungen',
            sample_data: 'Beispieldaten',
        },
        wizard: {
            title: 'Einrichtungsassistent',
            welcome: 'Willkommen bei AesirX ComplianceOne',
            welcome_message: 'Lassen Sie uns Ihre Organisation einrichten. Dieser Assistent führt Sie durch die Erstkonfiguration.',
            setup_complete: 'Einrichtung abgeschlossen',
            skipped_message: 'Der Einrichtungsassistent wurde übersprungen. Sie können ihn jederzeit ausführen, um Ihre Organisation zu konfigurieren.',
            start_setup: 'Einrichtung starten',
            skip_wizard: 'Assistenten überspringen',
            go_to_admin: 'Gehen Sie zu Admin',
            skip_for_now: 'Vorerst überspringen',
            next_step: 'Nächster Schritt',
            previous_step: 'Vorheriger Schritt',
            finish_setup: 'Einrichtung abschließen',
            rerun: 'Einrichtungsassistenten erneut ausführen',
            step_label: 'Schritt {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Organisationsname ist erforderlich.',
            validation_admin_email_required: 'Administrator-E-Mail ist erforderlich.',
            validation_admin_email_invalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
            validation_cmp_endpoint_required: 'CMP-Endpunkt ist erforderlich, wenn CMP aktiviert ist.',
            steps: {
                license_activation: 'Lizenzaktivierung',
                org_profile: 'Organisationsprofil',
                module_selection: 'Modulauswahl',
                cmp_integration: 'CMP-Integration',
                notification_preferences: 'Benachrichtigungen',
                review: 'Überprüfen & Bestätigen',
            },
            step_descriptions: {
                license_activation: 'Geben Sie Ihren Lizenzschlüssel ein, um Ihr Paket zu aktivieren und festzustellen, welche Module verfügbar sind.',
                org_profile: 'Richten Sie den Organisationsnamen, die Rechtsordnung, die Branche, die Größe und die bevorzugte Sprache ein.',
                module_selection: 'Wählen Sie aus, welche Compliance-Module für Ihre Organisation aktiviert werden sollen.',
                cmp_integration: 'Konfigurieren Sie die Integration der Consent Management Platform (optional).',
                notification_preferences: 'Konfigurieren Sie, wie Sie Benachrichtigungen über Compliance-Ereignisse erhalten.',
                review: 'Überprüfen Sie Ihre Konfiguration und schließen Sie die Einrichtung ab.',
            },
            license_step: {
                validate_button: 'Lizenz validieren',
                skip_warning: 'Der Modulzugriff ist auf die Standardpaketstufe beschränkt. Sie können eine Lizenz später unter Plattform > Lizenzeinstellungen aktivieren.',
            },
            module_descriptions: {
                rights_requests: 'Bearbeiten Sie Anfragen auf Zugang und Löschung von Personendaten.',
                data_mapping: 'Kartieren Sie Systeme, Datenflüsse und Verarbeitungsaktivitäten.',
                governance: 'Überwachen Sie Lieferanten, Genehmigungen, Abweichungen und die Nachweisregister.',
                consent: 'Verwalten Sie Einwilligungsrichtlinien und Zweckkataloge.',
                consent_properties: 'Verwalten Sie Einwilligungen über mehrere digitale Eigenschaften hinweg.',
                assessments: 'Führen Sie Folgenabschätzungen und grenzüberschreitende Datenübermittlungen durch.',
                classification: 'Klassifizieren und kennzeichnen Sie Datenbestände mit Lebenszyklusverfolgung.',
                monitoring: 'Geplante Compliance-Überprüfungen und KPI-Verfolgung.',
                incidents: 'Melden und verwalten Sie Sicherheits- und Datenschutzvorfälle.',
                connectors: 'Verbinden Sie sich mit externen Systemen für automatisierten Datenaustausch.',
                vendor_risk: 'Bewerten Sie Lieferanten, verfolgen Sie Verträge und verwalten Sie Due Diligence.',
                deletion_orchestration: 'Koordinieren Sie Datenlöschung über Systeme und Lieferanten hinweg.',
                discovery: 'Entdecken und klassifizieren Sie Daten in Ihrer gesamten Infrastruktur.',
                incident_ops: 'Erweiterte Vorfallsplaybooks, SLA-Verfolgung und Eskalationen.',
                program_governance: 'Reifegradbeurteilungen, Managementberichte und Auditbereitschaft.',
                access_accountability: 'Überwachen Sie Benutzerzugriff, Sitzungen und Begründungsworkflows.',
                localization: 'Marktspezifische Compliance-Pakete und regulatorische Rahmenwerke.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Partner- und Händlerarbeitsbereich.',
                privacy_scanner: 'Echtzeit-Datenschutzüberwachung digitaler Eigenschaften.',
                onboarding: 'Einrichtungsassistent, geführte Touren und Verwaltung von Beispieldaten.',
                compliance_forms: 'Interaktive Compliance-Vorlagen und Dokumentenerstellung.',
                audit_trail: 'Umfassende unveränderliche Auditprotokollierung und Aufbewahrung.',
                organizations: 'Multi-Organisations- und Gruppenverwaltung.',
                task_management: 'Einheitliche Aufgabenplanung, Workflow-Engine und Kalenderansichten.',
            },
            module_categories: {
                data_privacy_rights: 'Datenschutz und Rechte',
                vendor_third_party: 'Lieferanten- und Drittanbietermanagement',
                risk_assessment: 'Risiko & Bewertung',
                operations_automation: 'Betrieb und Automatisierung',
                platform_admin: 'Plattform und Verwaltung',
            },
            package_tier_label: 'Ihr Paket',
            package_module_unavailable: 'Nicht in Ihrem Paket enthalten',
            select_all: 'Alle auswählen',
            deselect_all: 'Alle abwählen',
            load_sample_data: 'Beispieldaten nach Einrichtung laden',
            load_sample_data_description: 'Installieren Sie realistische Demodaten, um die Plattform zu erkunden. Kann später unter Onboarding > Beispieldaten entfernt werden.',
            language_label: 'Bevorzugte Sprache',
            language_description: 'Wählen Sie die Standardsprache für Ihre Organisation.',
            admin_account_title: 'Administratorkonto',
            admin_account_created: 'Administratorkonto erstellt',
            admin_full_name: 'Vollständiger Name',
            admin_email: 'E-Mail-Adresse',
            admin_password: 'Passwort',
            admin_confirm_password: 'Passwort bestätigen',
            admin_show_password: 'Passwort anzeigen',
            admin_hide_password: 'Passwort verbergen',
            admin_name_placeholder: 'z.B. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Mindestens 8 Zeichen',
            admin_password_confirm_placeholder: 'Passwort erneut eingeben',
            validation_admin_name_required: 'Administratorname ist erforderlich',
            validation_admin_password_required: 'Passwort ist erforderlich',
            validation_admin_password_min_length: 'Passwort muss mindestens 8 Zeichen lang sein',
            validation_passwords_mismatch: 'Passwörter stimmen nicht überein',
            notification_email_enabled: 'E-Mail-Benachrichtigungen aktivieren',
            notification_email_description: 'Erhalten Sie E-Mail-Benachrichtigungen über Aufgabenzuweisungen, SLA-Verstöße und Compliance-Ereignisse.',
            notification_digest_frequency: 'Zusammenfassungshäufigkeit',
            notification_digest_daily: 'Täglich',
            notification_digest_weekly: 'Wöchentlich',
            notification_digest_none: 'Keine Zusammenfassung',
            notification_in_app_enabled: 'Plattform-Benachrichtigungen aktivieren',
            notification_in_app_description: 'Zeigen Sie Benachrichtigungsabzeichen und -schublade für Echtzeitwarnungen an.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/EWR',
                global: 'Global',
            },
            languages: {
                en: 'Englisch',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk Bokmål',
                nn: 'Norsk Nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Niederlande',
                fr: 'Französisch',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Spanisch',
                ca: 'Katalonien',
                eu: 'Euskara',
                pt: 'Portugiesisch',
            },
            industries: {
                financial_services: 'Finanzdienstleistungen',
                healthcare: 'Gesundheitswesen',
                technology: 'Technologie',
                telecommunications: 'Telekommunikation',
                government: 'Öffentlicher Sektor',
                education: 'Bildung',
                manufacturing: 'Fertigung',
                retail: 'Einzelhandel',
                other: 'Sonstige',
            },
            placeholders: {
                org_name: 'z. B. ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'user1@company.vn, user2@company.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organisationsname',
                jurisdiction: 'Rechtsordnung',
                industry: 'Branche',
                org_size: 'Organisationsgröße',
                language: 'Bevorzugte Sprache',
                admin_email: 'Administrator-E-Mail',
                team_emails: 'E-Mail-Adressen der Teammitglieder (kommagetrennt)',
                enable_cmp: 'CMP-Integration aktivieren',
                cmp_endpoint: 'CMP-API-Endpunkt',
                email_notifications: 'E-Mail-Benachrichtigungen',
                in_app_notifications: 'Plattform-Benachrichtigungen',
                digest_frequency: 'Zusammenfassungshäufigkeit',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Modul-Einführungstouren',
            start: 'Tour starten',
            continue_tour: 'Tour fortsetzen',
            replay: 'Tour wiederholen',
            exit: 'Tour beenden',
            skip: 'Tour überspringen',
            next: 'Weiter',
            previous: 'Zurück',
            finish: 'Tour abschließen',
            step_of: 'Schritt {current} von {total}',
            info: 'Nehmen Sie an geführten Touren durch die einzelnen Module teil, um die wichtigsten Funktionen und Workflows kennenzulernen. Touren können jederzeit wiederholt werden.',
            autoplay: 'Autoplay',
            interactive: 'Interaktiv',
            play_all: 'Alle abspielen',
            search_tours: 'Touren suchen...',
            duration: 'Dauer',
            steps_label: '{count} Schritte',
            mode: 'Modus',
            pause: 'Pause',
            resume: 'Lebenslauf',
            speed: 'Geschwindigkeit',
            restart: 'Neustart',
            reset: 'Zurücksetzen',
            reset_success: 'Der Tourfortschritt wurde zurückgesetzt.',
            category_core_privacy: 'Kerndatenschutz',
            category_governance: 'Regierungsführung',
            category_operations: 'Operationen',
            category_platform: 'Plattform',
            category_advanced: 'Fortgeschritten',
            category_data_privacy_rights: 'Datenschutz und Rechte',
            category_vendor_third_party: 'Lieferanten- und Drittanbietermanagement',
            category_risk_assessment: 'Risiko & Bewertung',
            category_operations_automation: 'Betrieb und Automatisierung',
            category_platform_admin: 'Plattform und Verwaltung',
            steps_count: '{count} Schritte',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Beispieldatenverwaltung',
            select_modules: 'Module auswählen',
            current_status: 'Aktueller Status',
            operation_history: 'Vorgangshistorie',
            load: 'Beispieldaten laden',
            clear: 'Beispieldaten löschen',
            processing: 'Verarbeitung...',
            loaded: 'Geladen',
            not_loaded: 'Nicht geladen',
            records: 'Beispieldatensätze',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Sind Sie sicher, dass Sie alle Beispieldaten für die ausgewählten Module löschen möchten?',
            confirm_clear_title: 'Beispieldaten löschen',
            confirm_load_title: 'Beispieldaten laden',
            confirm_load: 'Sind Sie sicher, dass Sie Beispieldaten für die ausgewählten Module laden möchten?',
            action_load: 'Laden',
            action_clear: 'Löschen',
            info: 'Laden Sie realistische Demodaten für Schulungen und Vorführungen. Beispieldaten sind gekennzeichnet und können ohne Auswirkung auf echte Daten sicher entfernt werden.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Hilfezentrum',
            search: 'Suche',
            search_placeholder: 'Hilfeartikel durchsuchen...',
            clear: 'Löschen',
            all_categories: 'Alle Kategorien',
            all_modules: 'Alle Module',
            back: 'Zurück zum Hilfezentrum',
            callout_important: 'Wichtig',
            callout_tip: 'Tipp',
            no_results: 'Keine Artikel gefunden, die Ihrer Suche entsprechen.',
            no_articles: 'Noch keine Hilfeartikel verfügbar. Artikel werden hier angezeigt, sobald Inhalte hinzugefügt werden.',
            results_for: '{count} Ergebnis(se) für „{query}"',
            quick_links: {
                getting_started: 'Erste Schritte',
                getting_started_desc: 'Installation, Einrichtung und Schnellstartanleitung.',
                user_guide: 'Benutzerhandbuch',
                user_guide_desc: 'Modulweise Nutzungsdokumentation.',
                admin_guide: 'Administratorhandbuch',
                admin_guide_desc: 'Konfiguration, Wartung und Fehlerbehebung.',
                faq: 'FAQ',
                faq_desc: 'Häufig gestellte Fragen und Antworten.',
            },
            search_articles_aria: 'Hilfeartikel suchen',
            article_metadata_aria: 'Artikel-Metadaten',
            faq_editor_title: 'FAQ-Editor des Hilfecenters',
            faq_editor_description: 'Fügen Sie FAQ-Frage-Antwort-Paare in einem beliebigen Hilfecenter-Artikel hinzu, bearbeiten oder entfernen Sie sie. Der Editor ist noch nicht verfügbar; diese Adresse ist dafür reserviert.',
            faq_editor_empty: 'Der FAQ-Editor ist noch nicht verfügbar. Bitten Sie bis dahin Ihre Administratorin oder Ihren Administrator, FAQ-Abschnitte für Sie zu einem Hilfecenter-Artikel hinzuzufügen.',
            articles_list_aria: 'Hilfeartikel',
            category_nav_aria: 'Kategorienavigation',
            quick_links_aria: 'Schnelllinks zu Hilfekategorien',
            returned_to_list: 'Zurück zur Artikelliste',
            opening_article: 'Artikel wird geöffnet: {title}',
            read_article_aria: 'Artikel lesen: {title}',
            pagination_nav_aria: 'Artikel-Seitennavigation',
            error_unavailable: 'Das Hilfecenter ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.',
            retry: 'Erneut versuchen',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'z. B. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Speichern',
        cancel: 'Abbrechen',
        system_actor: 'System',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        create: 'Erstellen',
        update: 'Aktualisieren',
        loading: 'Wird geladen...',
        error: 'Ein Fehler ist aufgetreten',
        success: 'Erfolg',
        logout: 'Abmelden',
        actions: 'Aktionen',
        retrying: 'Erneuter Versuch…',
        permission_denied: 'Sie haben keinen Zugriff auf diese Seite.',
        status: 'Zustand',
        description: 'Beschreibung',
        name: 'Bezeichnung',
        created_at: 'Erstellt am',
        updated_at: 'Aktualisiert am',
        yes: 'Ja',
        no: 'Nein',
        confirm: 'Bestätigen',
        confirm_dialog_title: 'Confirm action',
        back: 'Zurück',
        next: 'Weiter',
        previous: 'Zurück',
        search: 'Suche',
        filter: 'Filtern',
        page_x_of_y: 'Seite {page} von {total}',
        pagination_label: 'Paginierung',
        view_mode_label: 'Ansichtsmodus',
        export: 'Exportieren',
        exporting: 'Wird exportiert…',
        sidebar_navigation: 'Admin-Seitennavigation',
        items_count: '{count} Einträge',
        items_count_one: '{count} Eintrag',
        export_success: 'Dokument erfolgreich generiert.',
        generate_pdf: 'PDF erstellen',
        import: 'Importieren',
        download: 'Herunterladen',
        upload: 'Hochladen',
        view: 'Anzeigen',
        close: 'Schließen',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Absenden',
        saved: 'Erfolgreich gespeichert',
        saving: 'Speichern...',
        submitting: 'Wird gesendet...',
        created: 'Erfolgreich erstellt',
        all: 'Alle',
        none: 'Keine',
        select: 'Auswählen',
        select_user: 'Benutzer auswählen...',
        custom_input: 'Manuell eingeben',
        required: 'Erforderlich',
        optional: 'Wahlweise',
        notes: 'Notizen',
        tags: 'Schlagwörter',
        archive: 'Archivieren',
        clone: 'Klonen',
        refresh: 'Aktualisieren',
        retry: 'Erneut versuchen',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Zurücksetzen',
        add: 'Hinzufügen',
        remove: 'Entfernen',
        removing: 'Wird entfernt…',
        attachment: 'Anhang',
        upload_file: 'Datei hochladen',
        upload_file_required: 'Datei hochladen (erforderlich)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Ziehen und ablegen oder zum Durchsuchen klicken',
        upload_max_size: 'Maximale Größe: {size} MB',
        upload_accepted_types: 'Akzeptiert: {types}',
        upload_in_progress: '{name} wird hochgeladen...',
        upload_size_exceeded: 'Die Dateigröße überschreitet die maximal zulässige Größe von {size} MB.',
        upload_type_not_accepted: 'Dateityp nicht akzeptiert. Akzeptierte Typen: {types}',
        upload_succeeded: 'Datei "{name}" wurde erfolgreich hochgeladen.',
        upload_failed: 'Datei konnte nicht hochgeladen werden.',
        select_entity_type_first: 'Bitte zuerst einen Entitätstyp auswählen',
        failed_to_remove_file: 'Datei konnte nicht entfernt werden.',
        entity_type_label: 'Entitätstyp',
        entity_label: 'Entität',
        entity_reference: 'Entitätsreferenz',
        failed_to_load_entities: 'Entitäten konnten nicht geladen werden',
        enable: 'Aktivieren',
        disable: 'Deaktivieren',
        no_data: 'Keine Daten gefunden',
        confirm_delete: 'Sind Sie sicher, dass Sie dieses Element löschen möchten?',
        confirm_action: 'Sind Sie sicher, dass Sie fortfahren möchten?',
        save_success: 'Erfolgreich gespeichert',
        save_error: 'Speichern fehlgeschlagen',
        delete_success: 'Erfolgreich gelöscht',
        delete_error: 'Löschen fehlgeschlagen',
        hour: 'Stunde',
        hours: 'Stunden',
        load_error: 'Daten konnten nicht geladen werden',
        operation_error: 'Vorgang fehlgeschlagen',
        something_went_wrong: 'Etwas ist schiefgelaufen',
        go_home: 'Zur Übersicht',
        access_denied: 'Zugriff verweigert',
        no_permission: 'Sie haben keine Berechtigung, auf diese Seite zuzugreifen.',
        no_permission_title: 'Unzureichende Berechtigungen',
        no_permission_message: 'Sie haben keine Berechtigung, auf diese Seite zuzugreifen.',
        unknown: 'Unbekannt',
        back_to_dashboard: 'Zurück zur Übersicht',
        id: 'ID',
        type: 'Typ',
        date: 'Datum',
        created_by: 'Erstellt von',
        updated_by: 'Aktualisiert von',
        page_of: 'Seite {page} von {total}',
        showing_items: '{count} Einträge angezeigt',
        items_per_page: 'Einträge pro Seite',
        no_results: 'Keine Datensätze entsprechen Ihren Filtern',
        no_items: 'Keine Einträge gefunden',
        sort_by: 'Sortieren nach',
        ascending: 'Aufsteigend',
        descending: 'Absteigend',
        all_statuses: 'Alle Status',
        all_types: 'Alle Typen',
        all_categories: 'Alle Kategorien',
        all_severities: 'Alle Schweregrade',
        date_range: 'Zeitraum',
        from_date: 'Von',
        to_date: 'Bis',
        clear_filters: 'Filter zurücksetzen',
        apply_filters: 'Filter anwenden',
        save_changes: 'Änderungen speichern',
        discard_changes: 'Änderungen verwerfen',
        confirm_discard: 'Sie haben ungespeicherte Änderungen. Möchten Sie diese verwerfen?',
        copy: 'Kopieren',
        duplicate: 'Duplizieren',
        move: 'Verschieben',
        assign: 'Zuweisen',
        unassign: 'Zuweisung aufheben',
        approve: 'Genehmigen',
        reject: 'Ablehnen',
        deleting: 'Löschen...',
        processing: 'Verarbeitung...',
        loading_page: 'Seite wird geladen...',
        operation_success: 'Vorgang erfolgreich abgeschlossen',
        operation_failed: 'Vorgang fehlgeschlagen. Bitte versuchen Sie es erneut.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
            gov_vendor_read: 'read vendors',
            vendor_questionnaire_read: 'read vendor questionnaires',
            ai_governance_read: 'read AI systems',
            dpia_read: 'read data processing impact assessments',
            incident_read: 'read incidents',
            dm_evidence_read: 'read Data Mapping evidence',
            vehicle_monitoring_read: 'read vehicle monitoring devices',
            forms_instance_read: 'read form records',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Netzwerkfehler. Überprüfen Sie Ihre Verbindung.',
        session_expired: 'Sitzung abgelaufen. Bitte melden Sie sich erneut an.',
        test: 'Testen',
        pause: 'Pausieren',
        resume: 'Fortsetzen',
        activate: 'Aktivieren',
        last_sync: 'Letzte Synchronisierung',
        health: 'Zustand',
        email: 'E-Mail',
        role: 'Rolle',
        title: 'Titel',
        owner: 'Verantwortlicher',
        priority: 'Priorität',
        category: 'Kategorie',
        details: 'Einzelheiten',
        overview: 'Übersicht',
        configuration: 'Konfiguration',
        history: 'Verlauf',
        new_item: 'Neu',
        edit_item: 'Bearbeiten',
        select_option: 'Auswählen...',
        total: 'Gesamt',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        enabled: 'Aktiviert',
        disabled: 'Deaktiviert',
        last_login: 'Letzte Anmeldung',
        never: 'Nie',
        password_changed: 'Passwort geändert. Sie werden abgemeldet.',
        password_change_failed: 'Passwortänderung fehlgeschlagen',
        passwords_mismatch: 'Die Passwörter stimmen nicht überein',
        install: 'Installieren',
        install_named: 'Installieren {name}',
        uninstall: 'Deinstallieren',
        installed: 'Installiert',
        available: 'Verfügbar',
        catalog: 'Katalog',
        published: 'Veröffentlicht',
        version: 'Fassung',
        market: 'Markt',
        language: 'Sprache',
        framework: 'Rahmenwerk',
        template: 'Vorlage',
        connector_type: 'Konnektortyp',
        polling_interval: 'Abfrageintervall (Minuten)',
        new_instance: 'Neue Instanz',
        change_password: 'Passwort ändern',
        current_password: 'Aktuelles Passwort',
        new_password: 'Neues Passwort',
        confirm_password: 'Passwort bestätigen',
        comments: 'Kommentare',
        completed: 'Abgeschlossen',
        contact: 'Kontakt',
        hide: 'Ausblenden',
        hide_details: 'Ausblenden Details',
        label: 'Bezeichnung',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Anzeige',
        page: 'Seite',
        preview: 'Vorschau',
        print: 'Drucken',
        show: 'Anzeigen',
        status_active: 'Aktiv',
        status_completed: 'Abgeschlossen',
        status_draft: 'Entwurf',
        status_in_progress: 'In Fortschritt',
        status_pending: 'Ausstehend',
        view_details: 'Ansehen Details',
        all_priorities: 'Alle Priorities',
        pipeline: 'Ablaufkette',
        all_requests: 'Alle Anfragen',
        schedule: 'Planen',
        deploy: 'Bereitstellen',
        rollback: 'Zurücksetzen',
        advance_stage: 'Voranschreiten Stufe',
        submit_for_approval: 'Absenden für Genehmigung',
        approval_log: 'Genehmigung Protokoll',
        approver: 'Genehmiger',
        decision: 'Entscheidung',
        decided_at: 'Entschieden am',
        rollout_stages: 'Einführung Stufen',
        approval_comment: 'Genehmigung Kommentar',
        optional_comment: 'Optional Kommentar...',
        impact_summary: 'Auswirkung Zusammenfassung',
        target_property_ids: 'Ziel Eigenschaft IDs (comma-separated)',
        policy_id: 'Richtlinie ID',
        target_version: 'Ziel Version',
        target_version_placeholder: 'z. B. 2.0.0',
        scheduled_label: 'Geplant',
        scheduled_start: 'Geplant Starten',
        scheduled_end: 'Geplant Ende',
        properties_count: '{count} Eigenschaften',
        results: 'Ergebnisse',
        comparison_results: 'Vergleich Ergebnisse',
        past_comparison_reports: 'Vergangene Vergleich Berichte',
        scanning: 'Scanne...',
        run_inconsistency_scan: 'Ausführen Inkonsistenz Scannen',
        property_ids_label: 'Eigenschaft IDs (comma-separated, min 2)',
        comparison_type: 'Vergleich Typ',
        golden_template_id: 'Golden Vorlage ID (optional)',
        leave_blank_compare: 'Leave blank zu compare Eigenschaften against each other',
        add_notes_placeholder: 'Hinzufügen your analysis Hinweise here...',
        covered_of_active: '{covered} von {active} aktiv Eigenschaften covered',
        fetch_error: 'Fehler beim fetch Daten. Bitte versuchen Sie es erneut.',
        run_comparison_error: 'Fehler beim Durchlauf comparison. Bitte versuchen Sie es erneut.',
        save_notes_error: 'Fehler beim save Hinweise. Bitte versuchen Sie es erneut.',
        scan_complete: 'Inkonsistenz Scan completed successfully.',
        scan_error: 'Fehler beim Durchlauf inconsistency Scan. Bitte versuchen Sie es erneut.',
        min_two_properties: 'Bitte provide bei least 2 property IDs für comparison.',
        comma_separated: 'kommagetrennt',
        language_english: 'Englisch',
        language_vietnamese: 'Vietnamesisch',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Autor',
        dependencies: 'Abhängigkeiten',
        checksum: 'Prüfsumme',
        changelog: 'Änderungsprotokoll',
        tier: 'Stufe',
        phone: 'Telefon',
        launch: 'Starten',
        complete: 'Abschließen',
        back_to_list: 'Back zu Liste',
        clear_selection: 'Löschen Auswahl',
        deactivate: 'Deaktivieren',
        scope: 'Geltungsbereich',
        deadline: 'Frist',
        progress: 'Fortschritt',
        frequency: 'Häufigkeit',
        reviewer: 'Prüfer',
        user: 'Benutzer',
        campaign: 'Kampagne',
        items: 'Elemente',
        next_run: 'Nächster Ausführen',
        last_run: 'Letzter Ausführen',
        overdue: 'ÜBERFÄLLIG',
        creating: 'Erstelle...',
        launching: 'Starte...',
        loaded: 'loaded',
        page_size: 'Seitengröße',
        per_page: '/ Seite',
        urgent: 'Dringend',
        pagination: 'Paginierung',
        metric: 'Metrisch',
        value: 'Wert',
        country: 'Land',
        showing_chart_view: 'Diagrammansicht wird angezeigt',
        showing_table_view: 'Datentabellenansicht wird angezeigt',
        policies: 'Richtlinien',
        compliant: 'Konform',
        non_compliant_status: 'Nicht konform',
        all_data: 'Alle Daten',
        sr_urgent: 'Dringend',
        n_per_page: '{n} / Seite',
        select_user_placeholder: '-- Benutzer auswählen --',
        field_required: 'Dieses Feld ist erforderlich',
        results_found: 'Ergebnisse gefunden',
        select_placeholder: '-- Auswählen --',
        unknown_user: 'Unbekannter Benutzer',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'CSV exportieren',
        not_applicable: 'N/A',
        aria_overdue_since: 'Überfällig seit {date}',
        placeholders: {
            enter_justification: 'Eingeben Begründung',
            justification: 'Begründung',
            campaign_name_example: 'Q1 2026 Zugriff Überprüfen',
            optional_description: 'Optional Beschreibung',
            enter_user_id: 'Eingeben der user ID von der reviewer',
            enter_user_id_short: 'Eingeben user ID',
            schedule_name_example: 'Vierteljährlich Zugriff Attestierung',
            enter_property_id: 'Eingeben property ID',
            enter_policy_id: 'Eingeben Richtlinie ID',
            property_or_group_id: 'Eigenschaft oder Gruppe ID',
            describe_template_purpose: 'Beschreiben der Vorlage purpose...',
            step_title: 'Schritt title...',
            detailed_instructions: 'Detaillierte Anweisungen...',
            filter_by_taxonomy_id: 'Filtern nach Taxonomie ID...',
            describe_classification_rule: 'Beschreiben der classification Regel...',
            describe_dedup_rule: 'Beschreiben der Deduplizierung Regel...',
            describe_import_template: 'Beschreiben der Import Vorlage...',
            reason_for_rejection: 'Grund für rejection...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Filtern nach locale...',
            filter_by_event_type: 'Filtern nach Ereignis type',
            filter_by_actor_id: 'Filtern nach actor ID',
            filter_by_market_code: 'Filtern nach market Code...',
            filter_by_framework_id: 'Filtern nach Rahmenwerk ID...',
            add_comment: 'Hinzufügen ein Kommentar...',
            provide_rejection_reason: 'Angeben ein reason für rejecting dieses Einreichung...',
            filter_by_jurisdiction: 'Filtern nach jurisdiction Code...',
            enter_report_title: 'Eingeben report title',
            enter_name: 'Eingeben Name',
            enter_description: 'Eingeben Beschreibung',
            enter_org_id: 'Eingeben Org ID zu add',
            describe_evidence_gap: 'Beschreiben der Nachweis Lücke...',
            expected_evidence: 'What Nachweis is expected',
            entity_identifier: 'Entität identifier',
            remediation_steps: 'Schritte zu remediate dieses Lücke',
            user_id_or_name: 'Benutzer ID oder Name',
            reporting_period_id: 'Berichterstattung Zeitraum ID',
            maturity_model_id: 'Reife Modell ID',
            describe_maturity_model: 'Beschreiben dieses Reife Modell...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Angeben ein reason für denying dieses request',
            describe_emergency: 'Beschreiben der emergency situation requiring elevated Zugriff (minimum 20 characters)',
            enter_delegate_id: 'Eingeben user ID von der delegate',
            provide_delegation_reason: 'Angeben ein reason für dieses delegation',
            client_identifier: 'Client-Kennung',
            client_secret: 'Client-Geheimnis',
            time_format: 'HH:MM',
            describe_sod_rule: 'Beschreiben der purpose von dieses SoD Regel...',
            enter_role_id: 'Eingeben role ID',
            explain_exception: 'Explain why dieses exception is needed...',
            describe_compensating_controls: 'Beschreiben compensating Kontrollen in place...',
            full_name: 'Vollständig Name',
            min_chars: 'Min. 12 chars',
            enter_override_justification: 'Eingeben Begründung für overriding dieses Gate Bewertung',
            enter_evidence_id: 'Geben Sie die Beweis-ID ein, um Folgendes zu verknüpfen:',
            date_format: 'TT/MM/JJJJ',
            describe_pack_contents: 'Beschreiben Sie den Paketinhalt und Zweck',
            select_reviewers: 'Prüfer auswählen...',
        },
        aria_previous_page: 'Vorherige Seite',
        aria_next_page: 'Nächste Seite',
        aria_results_per_page: 'Ergebnisse pro Seite',
        per_page_25: '25 / Seite',
        per_page_50: '50 / Seite',
        per_page_100: '100 / Seite',
        select_language: 'Sprache auswählen',
        today: 'Heute',
        tomorrow: 'Morgen',
        dashboard_load_error: 'Dashboard konnte nicht geladen werden. Bitte erneut versuchen.',
        enum_grade: {
            red: 'Rot',
            amber: 'Orange',
            yellow: 'Gelb',
            green: 'Grün',
        },
        enum_severity: {
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Niedrig',
        },
        enum_priority: {
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Niedrig',
        },
        enum_status: {
            pending: 'Ausstehend',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            blocked: 'Blockiert',
            escalated: 'Eskaliert',
            cancelled: 'Abgebrochen',
            overdue: 'Überfällig',
            not_started: 'Nicht begonnen',
            new: 'Neu',
            open: 'Offen',
            closed: 'Geschlossen',
            acknowledged: 'Bestätigt',
            resolved: 'Behoben',
        },
        enum_department: {
            unassigned: 'Nicht zugewiesen',
        },
        enum_form_category: {
            assessment: 'Bewertung',
            assessment_report: 'Bewertungsbericht',
            dpia: 'DPIA',
            authority_submission: 'Einreichung bei der Behörde',
            cross_border_transfer: 'Grenzüberschreitende Übertragung',
            vendor: 'Anbieter',
            breach: 'Verstoß',
            consent: 'Zustimmung',
            governance: 'Regierungsführung',
            register: 'Registrieren',
            monitoring: 'Überwachung',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Privatsphäre',
            incident: 'Vorfall',
            org: 'Organisation',
            sector: 'Sektor',
            classification: 'Klassifizierung',
            discovery: 'Entdeckung',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Rechteanfragen',
            assessments: 'Beurteilungen',
            incidents: 'Vorfälle',
            incident_ops: 'Vorfall-Operationen',
            monitoring: 'Überwachung',
            vendor_risk: 'Lieferantenrisiko',
            governance: 'Regierungsführung',
            deletion_orchestration: 'Löschung',
            access_review: 'Zugriffsüberprüfung',
            consent: 'Zustimmung',
            consent_properties: 'Einwilligungseigenschaften',
            forms: 'Formulare',
            localization: 'Lokalisierung',
            program_governance: 'Programm-Governance',
            privacy_scanner: 'Datenschutzscanner',
            classification: 'Klassifizierung',
            discovery: 'Entdeckung',
            manual: 'Handbuch',
            data_mapping: 'Datenzuordnung',
        },
        enum_risk_level: {
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Niedrig',
        },
        enum_request_type: {
            all: 'All',
            access: 'Zugriff (DSAR)',
            deletion: 'Löschung',
            correction: 'Korrektur',
            objection: 'Einspruch',
            portability: 'Portabilität',
            restriction: 'Einschränkung',
            consent_withdrawal: 'Widerruf der Einwilligung',
            protective_measures: 'Schutzmaßnahmen',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'Englisch',
            vi: 'Vietnamesisch',
            all: 'Beides',
        },
        enum_event_type: {
            case_created: 'Fall erstellt',
            status_changed: 'Status geändert',
            case_assigned: 'Fall zugewiesen',
            note_added: 'Hinweis hinzugefügt',
            attachment_added: 'Anhang hinzugefügt',
            attachment_deleted: 'Anhang gelöscht',
            reference_added: 'Referenz hinzugefügt',
            checklist_updated: 'Checkliste aktualisiert',
            identity_verified: 'Identität überprüft',
            closure_summary_added: 'Abschlusszusammenfassung',
            approval_granted: 'Genehmigung erteilt',
            template_applied: 'Vorlage angewendet',
            export_generated: 'Export generiert',
            sla_warning: 'SLA Warnung',
            sla_overdue: 'SLA Überfällig',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Export fehlgeschlagen',
        you: 'You',
        export_failed: 'Export fehlgeschlagen. Bitte versuchen Sie es erneut.',
        filters: 'Filtern',
        failed: 'Fehlgeschlagen',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Entfernt',
        draft: 'Entwurf',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        pending: 'Ausstehend',
        in_progress: 'In Bearbeitung',
        completed: 'Abgeschlossen',
        archived: 'Archiviert',
        published: 'Veröffentlicht',
        approved: 'Genehmigt',
        rejected: 'Abgelehnt',
        cancelled: 'Abgebrochen',
        blocked: 'Blockiert',
        deferred: 'Zurückgestellt',
        not_started: 'Nicht gestartet',
        dismissed: 'Verworfen',
        failed: 'Fehlgeschlagen',
        in_review: 'In Überprüfen',
        deprecated: 'Veraltet',
        scheduled: 'Geplant',
        deploying: 'Bereitstellung',
        deployed: 'Bereitgestellt',
        paused: 'Pausiert',
        rolled_back: 'Zurückgesetzt',
        received: 'Empfangen',
        processing: 'Verarbeitung',
        under_assessment: 'Unter Bewertung',
        implemented: 'Implementiert',
        pending_approval: 'Ausstehend Genehmigung',
        pending_deletion: 'Ausstehend Deletion',
        on_hold: 'On Zurückstellen',
        deleted: 'Gelöscht',
        expired: 'Abgelaufen',
        outdated: 'Veraltet',
        drifted: 'Abgewichen',
        decommissioned: 'Außer Betrieb',
        match: 'Abgleichen',
        different: 'Unterschiedlich',
        missing: 'Fehlend',
        confirmed: 'Bestätigt',
        revoked: 'Widerrufen',
        escalated: 'Eskaliert',
        system: 'IT-System',
        custom: 'Benutzerdefiniert',
        warning: 'Warnung',
        blocking: 'Blockierend',
        analyst: 'Analytiker',
        viewer: 'Betrachter',
        daily: 'Täglich',
        weekly: 'Wöchentlich',
        policies: 'Richtlinien',
        denied: 'Abgelehnt',
        verified: 'Verifiziert',
        detected: 'Erkannt',
        ignored: 'Ignoriert',
        open: 'Offen',
        resolved: 'Gelöst',
        suspended: 'Suspendiert',
        retired: 'Im Ruhestand',
        review: 'Rezension',
        generated: 'Generiert',
        exported: 'Exportiert',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Niedrig',
        medium: 'Mittel',
        high: 'Hoch',
        critical: 'Kritisch',
    },
    sensitivity: {
        standard: 'Standardstufe',
        sensitive: 'Sensibel',
        special_category: 'Besondere Kategorie',
        badge_sensitive: 'Sensibel',
    },
    change_types: {
        policy_update: 'Richtlinie Aktualisieren',
        new_binding: 'Neu Bindung',
        remove_binding: 'Entfernen Bindung',
        configuration_change: 'Konfiguration Ändern',
        banner_update: 'Banner Aktualisieren',
    },
    comparison_types: {
        full: 'Vollständig',
        banner_only: 'Nur Banner',
        purposes_only: 'Zwecke Only',
        legal_bases_only: 'Rechtlich Bases Only',
        consent_settings: 'Einwilligung Einstellungen',
    },
    lifecycle_stages: {
        active: 'Aktiv',
        archived: 'Archiviert',
        pending_deletion: 'Ausstehend Deletion',
        deleted: 'Gelöscht',
        on_hold: 'On Zurückstellen',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Personenbezogen Daten',
        consent_record: 'Einwilligung Datensatz',
        user_account: 'Benutzer Konto',
        audit_log: 'Audit Protokoll',
        document: 'Dokument',
        backup: 'Sicherung',
    },
    risk_categories: {
        data_breach: 'Daten Verletzung',
        unauthorized_access: 'Unbefugt Zugriff',
        data_loss: 'Daten Loss',
        regulatory_non_compliance: 'Regulatorisch Non-Compliance',
        third_party_risk: 'Third-Party Risiko',
        operational: 'Betriebsbereit',
        technical: 'Technisch',
        legal: 'Rechtlich',
    },
    mitigation_types: {
        technical: 'Technisch',
        organizational: 'Organisatorisch',
        contractual: 'Vertraglich',
        procedural: 'Prozedural',
    },
    safeguard_types: {
        encryption_in_transit: 'Verschlüsselung bei der Übertragung',
        encryption_at_rest: 'Verschlüsselung im Ruhezustand',
        access_controls: 'Zugangskontrollen',
        contractual_clauses: 'Vertragsklauseln',
        data_minimization: 'Datenminimierung',
        pseudonymization: 'Pseudonymisierung',
        audit_rights: 'Prüfungsrechte',
        breach_notification: 'Benachrichtigung über Verstöße',
        data_localization: 'Datenlokalisierung',
        other: 'Andere',
    },
    scoring_methods: {
        weighted_average: 'Gewichtet Durchschnitt',
        simple_average: 'Einfach Durchschnitt',
        maximum: 'Maximalwert',
        manual: 'Manuell',
    },
    dpa_status: {
        not_required: 'Nicht erforderlich',
        required: 'Erforderlich',
        in_progress: 'In Bearbeitung',
        completed: 'Abgeschlossen',
        signed: 'Unterzeichnet',
    },
    disposal_methods: {
        anonymize: 'Anonymisieren',
        pseudonymize: 'Pseudonymisieren',
        aggregate: 'Aggregieren',
        delete: 'Löschen',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Übersicht',
        no_data: 'Keine Dashboard-Daten verfügbar.',
        no_data_heading: 'Noch keine Live-Daten',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Einwilligungsabdeckung',
        ctia_filings: 'CTIA-Meldungen',
        next_dpia_review: 'Nächste DPIA-Überprüfung',
        next_review_none: 'Keine geplant',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'MPS-Meldungen zu Vorfällen',
        compliance_score: 'Compliance-Bewertung',
        grade: 'Note',
        no_score: 'Noch keine Compliance-Bewertung berechnet.',
        rights_requests: 'Auskunftsersuchen',
        open: 'Offen',
        sla_breached: 'SLA verletzt',
        sla_approaching: 'SLA bald fällig',
        closed_month: 'Geschlossen (Monat)',
        incidents: 'Vorfälle & Datenschutzverletzungen',
        active: 'Aktiv',
        breaches: 'Datenschutzverletzungen',
        overdue_tasks: 'Überfällige Aufgaben',
        breach_72h_notice: 'Für aktive Verstöße gilt eine behördliche Benachrichtigungsfrist von 72 Stunden',
        recommendations: 'Empfehlungen',
        take_action: 'Werden Sie aktiv',
        drift_events: 'Abweichungen & Genehmigungen',
        open_events: 'Offene Ereignisse',
        pending_approvals: 'Ausstehende Genehmigungen',
        monitoring: 'Überwachungsaufgaben',
        programs: 'Programme',
        overdue: 'Überfällig',
        pending: 'Ausstehend',
        ric_cases: 'Behördenanfragen',
        coverage: 'Datenabdeckung',
        systems: 'Systeme',
        activities: 'Tätigkeiten',
        vendors: 'Dienstleister',
        dpias: 'DSFAs',
        open_gaps: 'offene Lücken',
        view_as_table: 'Als Tabelle anzeigen',
        view_as_chart: 'Als Diagramm anzeigen',
        table_metric_header: 'Kennzahl',
        table_value_header: 'Wert',
        announce_chart_view: 'Diagrammansicht wird angezeigt',
        announce_table_view: 'Tabellenansicht wird angezeigt',
        section_core_metrics: 'Kernkennzahlen',
        section_task_management: 'Aufgabenverwaltung',
        section_compliance_insights: 'Compliance-Einblicke',
        configure_dashboard: 'Dashboard konfigurieren',
        config_col_active: 'Aktiv',
        config_col_widget: 'Widget',
        collapse_section: 'Einklappen',
        expand_section: 'Ausklappen',
        reset_to_defaults: 'Auf Standard zurücksetzen',
        preferences_saved: 'Dashboard-Einstellungen gespeichert',
        preferences_reset: 'Dashboard auf Rollenstandard zurückgesetzt',
        preferences_load_error: 'Dashboard-Einstellungen konnten nicht geladen werden',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Durchschnittliche Systembewertung',
        factor_avg_vendor_score: 'Durchschnittliche Anbieterbewertung',
        factor_processing_coverage: 'Abdeckung der Verarbeitungsaktivitäten',
        factor_ropa_completeness: 'RoPA-Vollständigkeit',
        factor_drift_penalty: 'Strafe für Drift-Ereignisse',
        rec_improve_system_gov: 'Verbessern Sie die Dokumentation der Systemgovernance',
        rec_improve_vendor_gov: 'Verbessern Sie die Dokumentation zur Lieferanten-Governance',
        rec_document_processing: 'Dokumentenverarbeitungsaktivitäten für nicht abgedeckte Systeme',
        rec_generate_ropa: 'Generieren Sie RoPA-Datensätze für aktive Verarbeitungsaktivitäten',
        rec_resolve_drift: 'Beheben Sie offene kritische und schwerwiegende Abweichungsereignisse',
        score_good: 'Gut',
        score_fair: 'Befriedigend',
        score_poor: 'Schlecht',
        score_critical: 'Kritisch',
        drag_to_reorder: 'Ziehen zum Umordnen',
        widget_moved: 'Widget verschoben',
        press_space_to_grab: 'Leertaste zum Greifen, Pfeiltasten zum Verschieben, Leertaste zum Ablegen',
        reorder_instructions: 'Verwenden Sie Drag-and-Drop oder Tastenkürzel, um Widgets umzuordnen',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Aufgabenübersicht',
            pending: 'Ausstehend',
            in_progress: 'In Bearbeitung',
            overdue: 'Überfällig',
            done_7d: 'Fertig (7d)',
            by_priority: 'Nach Priorität',
            view_all_tasks: 'Alle Aufgaben anzeigen',
            upcoming_tasks: 'Anstehende Aufgaben (nächste 7 Tage)',
            no_upcoming: 'In den nächsten 7 Tagen sind keine Aufgaben fällig.',
            view_calendar: 'Kalender anzeigen',
            overdue_alerts: 'Überfällig',
            no_overdue: 'Keine überfälligen Aufgaben. Alles ist auf dem richtigen Weg.',
            assigned_to: 'Zugewiesen an',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Alle überfälligen anzeigen',
            notifications: 'Aktuelle Benachrichtigungen',
            unread: 'unread',
            no_notifications: 'Keine Benachrichtigungen. Ihr seid alle beschäftigt!',
            view_all_notifications: 'Alle Benachrichtigungen anzeigen',
            load_error: 'Daten konnten nicht geladen werden.',
            day_late: 'Tag zu spät',
            days_late_plural: 'Tage zu spät',
            due_today: 'Heute fällig',
            just_now: 'gerade jetzt',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Organisationsübergreifende Statistiken',
            by_organization: 'Nach Organisation',
            overdue_across_orgs: 'überfällige Aufgaben in allen Organisationen',
            overdue_count_label: '{count} overdue tasks',
            total: 'Insgesamt',
            tasks: 'tasks',
            organization: 'Organisation',
            current_org: 'Aktuelle Organisation',
            all_orgs: 'Alle Organisationen',
            all_organizations: 'Alle Organisationen',
            filter_by_org: 'Nach Organisation filtern',
            cross_org: 'Orgübergreifend',
            empty_calendar: 'Keine Aufgaben für diesen Zeitraum. Erstellen Sie Ihre erste Aufgabe oder überprüfen Sie Ihre Filter.',
            empty_my_tasks: 'Ihnen sind keine Aufgaben zugewiesen. Tolle Arbeit!',
            empty_overdue: 'Keine überfälligen Aufgaben. Alles ist auf dem richtigen Weg.',
            empty_notifications: 'Keine Benachrichtigungen. Ihr seid alle beschäftigt!',
            empty_workflows: 'Keine Workflows konfiguriert. Durchsuchen Sie Vorlagen, um loszulegen.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Meine Aufgaben',
            no_tasks: 'Keine Aufgaben an Sie zugewiesen.',
            load_error: 'Aufgaben konnten nicht geladen werden.',
            overdue: 'überfällig',
            remaining: 'übrig',
            view_all: 'Alle Aufgaben anzeigen',
        },
        my_section_status_widget: {
            title: 'Mein Abschnittsstatus',
            overall_completion: 'Gesamt',
            sections_count: 'Abschnitte',
            no_sections: 'Keine Abschnitte Ihrer Abteilung zugewiesen.',
            load_error: 'Abschnittsstatus konnte nicht geladen werden.',
            department_tasks_title: 'Aufgaben der Abteilung',
            no_department_tasks: 'Ihre Abteilung hat keine Aufgaben.',
            department_tasks_error: 'Aufgaben der Abteilung konnten nicht geladen werden.',
            no_department: 'Sie sind keiner Abteilung zugeordnet.',
        },
        revision_requests_widget: {
            title: 'Revisionsanfragen',
            no_revisions: 'Keine Revisionsanfragen. Alle Abschnitte sind auf Kurs.',
            load_error: 'Revisionsanfragen konnten nicht geladen werden.',
        },
        consent_summary: {
            title: 'Zustimmung',
            active: 'Aktiv',
            withdrawn_30d: 'Zurückgezogen (30 Tage)',
            expiring_7d: 'Auslaufend (7 Tage)',
        },
        transfer_assessments: {
            title: 'Transferbewertungen',
            open: 'Offen',
            approved: 'Genehmigt',
            gaps_flagged: 'Lücken gekennzeichnet',
        },
        breach_notifications: {
            title: 'Benachrichtigungen über Verstöße',
            deadline_72h: '72-Stunden-Frist',
            submitted: 'Eingereicht',
            pending: 'Ausstehend',
        },
        authority_requests: {
            title: 'Behördenanfragen',
            pending: 'Ausstehend',
            overdue: 'Überfällig',
            responded_30d: 'Beantwortet (30 Tage)',
        },
        data_subjects_widget: {
            title: 'Betroffene Personen',
            total: 'Insgesamt',
            high_risk: 'Hohes Risiko',
            active_30d: 'Aktiv (30 Tage)',
        },
        audit_activity_summary: {
            title: 'Audit-Aktivität',
            events_today: 'Veranstaltungen heute',
            events_7d: 'Ereignisse (7 Tage)',
            unique_actors_30d: 'Einzigartige Schauspieler (30 Tage)',
        },
        onboarding_progress: {
            title: 'Onboarding-Fortschritt',
            completion_pct: 'Komplett',
            status_label: 'Status',
            current_step: 'Aktueller Schritt',
            status: {
                in_progress: 'In Bearbeitung',
                completed: 'Abgeschlossen',
                skipped: 'Übersprungen',
                not_started: 'Nicht gestartet',
            },
        },
        sector_overlay_reports_due: {
            title: 'Fällige Berichte für Sektor-Overlays',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Nach Rahmenwerk filtern',
            all_frameworks: 'Alle Rahmenwerke',
            filing_lifecycle: 'Einreichungslebenszyklus',
            filing_pipeline: 'Einreichungspipeline',
            draft: 'Entwurf',
            internal_review: 'Interne Prüfung',
            approved: 'Genehmigt',
            submitted: 'Eingereicht',
            supplement_requested: 'Ergänzung angefordert',
            resubmitted: 'Erneut eingereicht',
            accepted: 'Akzeptiert',
            rejected: 'Abgelehnt',
            closed: 'Geschlossen',
            deadline: 'Frist',
            days_remaining: 'verbleibende Tage',
            days_overdue: 'Tage überfällig',
            no_filings: 'Keine aktiven Einreichungen.',
            department_consolidation: 'Abteilungskonsolidierung',
            departments_completed: 'Abteilungen abgeschlossen',
            departments_pending: 'Abteilungen ausstehend',
            departments_overdue: 'Abteilungen überfällig',
            no_consolidation_cases: 'Keine abteilungsübergreifenden Fälle in Bearbeitung.',
            vendor_risk: 'Lieferanten- und Drittparteirisiko',
            pending_assessment: 'Ausstehende Bewertung',
            overdue_questionnaires: 'Überfällige Fragebögen',
            dpa_review_queue: 'DPA-Prüfungswarteschlange',
            risk_distribution: 'Risikoverteilung',
            total_vendors: 'Gesamtlieferanten',
            by_risk_level: 'Nach Risikoniveau',
            no_vendor_data: 'Keine Lieferantenrisikodaten verfügbar.',
            discovery_summary: 'Datenermittlung',
            discovery_classification: 'Datenentdeckung und -klassifizierung',
            active_scans: 'Aktive Scans',
            labels_pending: 'Etiketten stehen zur Überprüfung aus',
            exceptions_unresolved: 'Ausnahmen ungelöst',
            pending_review: 'Ausstehende Prüfung',
            exceptions: 'Ausnahmen',
            approved_labels: 'Genehmigte Labels',
            total_classified: 'Gesamt klassifiziert',
            no_discovery_data: 'Keine Entdeckungsdaten verfügbar.',
            ai_governance: 'KI-Systemsteuerung',
            systems_inventory: 'Systeme im Inventar',
            systems_in_inventory: 'Systeme im Inventar',
            classification_breakdown: 'Klassifizierungsaufschlüsselung',
            dossier_completion: 'Dossier-Fertigstellung',
            by_classification: 'Nach Klassifizierung',
            labeling_status: 'Kennzeichnungsstatus',
            not_started: 'Nicht gestartet',
            no_ai_data: 'Keine KI-Steuerungsdaten verfügbar.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Verletzungs- und Vorfallsreaktion',
            active_breaches: 'Aktive Verletzungen',
            notification_status: 'Benachrichtigungsstatus',
            remediation_progress: 'Behebungsfortschritt',
            supplement_loops: 'Ergänzungsschleifen',
            no_breach_data: 'Keine aktiven Verletzungsfälle.',
            audit_readiness: 'Prüfungs- und Bewertungsbereitschaft',
            assessments: 'Bewertungen',
            gap_findings: 'Lückenbefunde',
            evidence_completion: 'Nachweisfertigstellung',
            export_ready: 'Exportbereit',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Nicht bereit',
            no_audit_data: 'Keine Prüfungsbereitschaftsdaten verfügbar.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cybersicherheit 2018',
            framework_label_vn_ecommerce: 'VN E-Commerce 2023',
            framework_label_vn_telecom: 'VN Telekommunikation 2023',
            framework_label_vn_ai: 'VN KI-Gesetz 2025',
            framework_label_gdpr: 'DSGVO',
            framework_label_unspecified: 'Nicht spezifiziert',
            my_department: 'Meine Abteilung',
            my_tasks: 'Meine Aufgaben',
            section_status: 'Abschnittsstatus',
            revision_requests: 'Revisionsanfragen',
            completion: 'Fertigstellung',
            reason: 'Grund',
            requested_at: 'Angefordert am',
            no_tasks_assigned: 'Keine Aufgaben zugewiesen.',
            no_sections: 'Keine Abschnitte Ihrer Abteilung zugewiesen.',
            no_revisions: 'Keine Revisionsanfragen.',
            load_error: 'Daten konnten nicht geladen werden.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instanzen',
            credentials: 'Anmeldedatentresor',
            event_bus: 'Ereignisbus',
            definitions: 'Definitionen',
            brds: 'BRD-Erfassung',
            security_reviews: 'Sicherheitsprüfungen',
            poc_scorecards: 'POC-Bewertungskarten',
            automation_planning: 'Automatisierungsplanung',
            system_inventory: 'Systeminventar',
            traceability_matrix: 'Rückverfolgbarkeitsmatrix',
            gap_register: 'Lückenregister',
        },
        instances: {
            title: 'Konnektor-Instanzen',
            page_title: 'Konnektoren',
            add: 'Instanz hinzufügen',
            test: 'Verbindung testen',
            sync_now: 'Jetzt synchronisieren',
            new_instance: 'Neu Konnektor Instanz',
            no_instances: 'Keine Konnektor Instanzen gefunden',
            instance_count: '{count} Instanz(en)',
            confirm_delete: 'Löschen Konnektor Instanz "{name}"?',
            all_connector_types: 'Alle Konnektor Typen',
            select_connector: 'Auswählen ein Konnektor...',
            fetch_error: 'Fehler beim fetch Konnektor Instanzen. Bitte versuchen Sie es erneut.',
            fetch_definitions_error: 'Fehler beim fetch Konnektor Definitionen. Bitte versuchen Sie es erneut.',
            create_error: 'Fehler beim create Konnektor Instanz',
            test_error: 'Fehler beim test Konnektor. Bitte versuchen Sie es erneut.',
            status_error: 'Fehler beim Aktualisieren des Konnektorstatus. Bitte versuchen Sie es erneut.',
            delete_error: 'Fehler beim delete Konnektor Instanz. Bitte versuchen Sie es erneut.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Status aktualisiert',
            reactivate: 'Reaktivieren',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Anmeldedatentresor',
            page_title: 'Konnektor-Anmeldedaten',
            add: 'Anmeldedaten hinzufügen',
            rotate: 'Rotieren',
            expired: 'Abgelaufen',
            expiring: 'Läuft bald ab',
            orphaned: 'Verwaist',
            new_credential: 'Neu Anmeldedaten',
            no_credentials: 'Keine credentials gefunden',
            no_expiry: 'No Ablauf',
            linked_connectors: 'Verknüpft Konnektoren',
            expires_at: 'Läuft ab am',
            value_label: 'Wert * (write-only)',
            confirm_delete_title: 'Löschen bestätigen',
            confirm_delete_message: 'Diese Anmeldedaten löschen? Verknüpfte Konnektoren verlieren den Zugriff.',
            rotate_title: 'Anmeldedaten rotieren',
            new_credential_value: 'Neuer Anmeldedatenwert',
            fetch_error: 'Fehler beim load credentials. Bitte versuchen Sie es erneut.',
            health_error: 'Fehler beim load Zustand. Bitte versuchen Sie es erneut.',
            create_error: 'Fehler beim create Anmeldedaten',
            rotate_error: 'Fehler beim rotate Anmeldedaten',
            delete_error: 'Fehler beim delete Anmeldedaten',
        },
        event_bus: {
            title: 'Ereignisbus',
            page_title: 'Event-Bus',
            events: 'Ereignisse',
            subscriptions: 'Abonnements',
            dlq: 'Warteschlange für fehlgeschlagene Nachrichten',
            publish: 'Ereignis veröffentlichen',
            retry: 'Erneut versuchen',
            dismiss: 'Verwerfen',
            search_events: 'Suche Ereignisse...',
            filter_event_type: 'Filtern nach Ereignis type...',
            event_type: 'Ereignis Typ',
            source_module: 'Quelle Modul',
            timestamp: 'Zeitstempel',
            delivery_status: 'Zustellung Status',
            correlation_id: 'Korrelation ID',
            tracking_id: 'Verfolgungs-ID',
            delivery_status_hint: 'Der Zustellstatus wird automatisch aktualisiert, wenn Ereignisse verarbeitet werden.',
            no_events: 'Keine Ereignisse gefunden',
            total_with_count: '{count} gesamt',
            prev: 'Zurück',
            new_subscription: '+ Neu Abonnement',
            subscriber_type: 'Abonnent Typ',
            subscriber_id: 'Abonnent ID',
            event_type_pattern: 'Ereignis Typ Muster',
            event_type_pattern_placeholder: 'z.B. incident.*',
            filter_expression: 'Filtern Ausdruck',
            filter_expression_placeholder: 'Optionaler Filter',
            subscriber_type_hint: 'Wählen Sie die Zustellmethode: Webhook sendet HTTP-Anfragen, E-Mail sendet Benachrichtigungen, Warteschlange leitet an eine Nachrichtenwarteschlange weiter.',
            subscriber_id_hint: 'Geben Sie das Ziel für Ereignisse ein. Für Webhooks verwenden Sie eine URL. Für E-Mail eine Adresse. Für Warteschlangen den Warteschlangennamen.',
            event_type_pattern_hint: 'Ereignisse nach Typ mit Platzhaltern abgleichen. Verwenden Sie * für beliebige Segmente (z.B. incident.* passt auf incident.created, incident.resolved).',
            filter_expression_hint: 'Optional. Ereignisse nach Inhalt weiter filtern (z.B. severity=high).',
            subscriber_id_placeholder_webhook: 'z.B. https://ihre-app.com/webhook',
            subscriber_id_placeholder_email: 'z.B. alerts@ihre-firma.com',
            subscriber_id_placeholder_queue: 'z.B. ereignis-warteschlange',
            event_pattern: 'Ereignis Muster',
            filter: 'Filtern',
            enabled: 'Aktiviert',
            delivery_stats: 'Zustellung Stats',
            no_subscriptions: 'Keine Abonnements gefunden',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} delivered / {failed} fehlgeschlagen',
            confirm_delete_subscription: 'Löschen dieses Abonnement?',
            unresolved: 'Ungelöst',
            resolved: 'Gelöst',
            event_id: 'Ereignis ID',
            subscription_id: 'Abonnement ID',
            error_code: 'Fehler Code',
            error_message: 'Fehler Message',
            attempts: 'Versuche',
            last_failed: 'Letzter Failed',
            no_dlq_entries: 'Keine dead letter Einträge gefunden',
            dlq_count: '{count} entry/entries gesamt',
            fetch_events_error: 'Fehler beim fetch Ereignisse. Bitte versuchen Sie es erneut.',
            fetch_subscriptions_error: 'Fehler beim fetch Abonnements. Bitte versuchen Sie es erneut.',
            fetch_dlq_error: 'Fehler beim fetch DLQ Einträge. Bitte versuchen Sie es erneut.',
            create_subscription_error: 'Fehler beim create Abonnement',
            toggle_subscription_error: 'Fehler beim toggle Abonnement',
            delete_subscription_error: 'Fehler beim delete Abonnement',
            retry_error: 'Wiederholen fehlgeschlagen',
            dismiss_error: 'Ablehnen fehlgeschlagen',
            event_published: 'Ereignis veröffentlicht',
            publish_event_error: 'Ereignis konnte nicht veröffentlicht werden. Bitte versuchen Sie es erneut.',
            priority_label: 'Priorität',
            priority_low: 'Niedrig',
            priority_normal: 'Normal',
            priority_high: 'Hoch',
            payload_helper_text: 'Geben Sie Ereignisdaten als JSON ein. Beispiel: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Abonnement erstellt',
            subscription_toggled: 'Abonnement umgeschaltet',
            subscription_deleted: 'Abonnement gelöscht',
            dlq_retried: 'DLQ-Nachricht erneut versucht',
            dlq_dismissed: 'DLQ-Nachricht verworfen',
        },
        definitions: {
            title: 'Connector-Definitionen',
            page_title: 'Konnektordefinitionen',
            add: 'Definition hinzufügen',
            new_definition: 'Neue Connector-Definition',
            edit_definition: 'Bearbeiten Sie die Connector-Definition',
            no_definitions: 'Keine Connector-Definitionen gefunden',
            definition_count: '{count} Definition(en)',
            all_categories: 'Alle Kategorien',
            select_category: 'Wählen Sie eine Kategorie aus...',
            capabilities: 'Fähigkeiten',
            capabilities_hint: 'Fähigkeiten (durch Kommas getrennt)',
            capabilities_placeholder: 'z.B. lesen, schreiben, synchronisieren',
            created: 'Connector-Definition erstellt',
            updated: 'Connector-Definition aktualisiert',
            fetch_error: 'Konnektordefinitionen konnten nicht abgerufen werden. Bitte versuchen Sie es erneut.',
            create_error: 'Konnektordefinition konnte nicht erstellt werden',
            update_error: 'Fehler beim Aktualisieren der Konnektordefinition',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD-Erfassung',
            page_title: 'BRD-Erfassung',
            create_brd: 'BRD erstellen',
            brd_title: 'BRD-Titel',
            description: 'Beschreibung',
            requirements: 'Anforderungen',
            status: 'Status',
            stakeholders: 'Beteiligte',
            connector_label: 'Konnektor',
            select_connector: 'Konnektor auswählen...',
            approval: 'Genehmigung',
            no_brds_found: 'Keine BRDs gefunden',
            decision_records: 'Entscheidungsprotokolle',
            create_decision: 'Entscheidung erstellen',
            alternatives_considered: 'Erwogene Alternativen',
            alternative_name: 'Alternativname',
            reason_rejected: 'Ablehnungsgrund',
            add_alternative: 'Alternative hinzufügen',
            remove_alternative: 'Alternative {name} entfernen',
            alternatives: 'Alternativen',
            rationale: 'Begründung',
            selected_connector: 'Ausgewählter Konnektor',
            comma_separated_names: 'Kommagetrennte Namen',
            brd_created: 'BRD erstellt',
            decision_record_created: 'Entscheidungsdatensatz erstellt',
            no_decision_records_found: 'Keine Entscheidungsdatensätze gefunden',
            aria_brds_table: 'BRDs',
            aria_decision_records_table: 'Entscheidungsdatensätze',
            aria_approve: '{name} genehmigen',
            aria_archive: '{name} archivieren',
            brd_title_help: 'Ein kurzer, für Menschen lesbarer Name für diese BRD – z. „Jira-Ticketing-Integration“, „Okta SSO-Rollout“.',
            brd_title_placeholder: 'z.B. Jira-Ticketing-Integration',
            description_help: 'Das Geschäftsproblem, das dieser Connector löst. Welches Ergebnis erwarten Sie, wenn es live ist?',
            description_placeholder: 'z.B. Automatisieren Sie die Erstellung von Vorfalltickets anhand von Überwachungswarnungen, sodass der Bereitschaftsdienst innerhalb von 60 Sekunden weitergeleitet wird.',
            connector_help: 'Optional. Wählen Sie einen Anschluss aus, für den diese BRD bestimmt ist. Lassen Sie das Feld leer, wenn der Anschluss noch nicht ausgewählt wurde.',
            requirements_help: 'Durch Kommas getrennte Liste der unverzichtbaren Funktionen – z. B. „OAuth2-Anmeldung, Webhook-Zustellung, Audit-Export“.',
            requirements_placeholder: 'OAuth2-Anmeldung, Webhook-Zustellung, Audit-Export',
            stakeholders_help: 'Durch Kommas getrennte Namen oder E-Mail-Adressen von Personen, die sich abmelden müssen.',
            selected_connector_help: 'Wählen Sie den Connector aus, für den diese Entscheidung gilt.',
            rationale_help: 'Warum dieser Anschluss gewählt wurde. Behandeln Sie die entscheidenden Faktoren, damit ein zukünftiger Leser den Aufruf versteht.',
            rationale_placeholder: 'z.B. Wir haben uns für Jira entschieden, weil es bereits unseren Ticketverlauf speichert und das Team darauf geschult ist.',
            alternatives_considered_help: 'Andere bewertete Steckverbinder und warum sie nicht ausgewählt wurden. Fügt die Verteidigungsfähigkeit für die Prüfung hinzu.',
            alternative_name_placeholder: 'z.B. ServiceNow',
            reason_rejected_placeholder: 'z.B. Kosten 3x höher, kein Vorteil für unseren Anwendungsfall',
        },
        security_reviews: {
            title: 'Sicherheitsprüfungen',
            page_title: 'Sicherheitsüberprüfungen',
            create_review: 'Prüfung erstellen',
            connector_name: 'Konnektorname',
            connector_label: 'Konnektor',
            select_connector: 'Konnektor auswählen...',
            reviewer: 'Prüfer',
            select_reviewer: 'Prüfer auswählen...',
            findings: 'Ergebnisse (eines pro Zeile)',
            risk_level: 'Risikostufe',
            risk_level_placeholder: 'z.B. hoch, mittel, niedrig',
            notes: 'Notizen',
            review_status: 'Prüfungsstatus',
            checklist: 'Checkliste',
            privacy_impact: 'Datenschutzauswirkung',
            approval_gate: 'Genehmigungstor',
            no_reviews_found: 'Keine Prüfungen gefunden',
            review_created: 'Sicherheitsüberprüfung erstellt',
            create_control_mapping: 'Kontrollzuordnung erstellen',
            control_mapping_created: 'Kontrollzuordnung erstellt',
            connector_id_label: 'Konnektor-ID',
            control_id_label: 'Kontroll-ID',
            control_name_label: 'Kontrollname',
            aria_approve: '{name} genehmigen',
            aria_reject: '{name} ablehnen',
            scan_imported: 'Scan erfolgreich importiert',
            import_scan: 'Scan importieren',
            scan_format: 'Scanformat',
            scan_data: 'Scandaten',
            import_results: 'Importergebnisse',
            recommendations: 'Empfehlungen',
            auto_populated_from: 'Automatisch ausgefüllt ab {tool}',
            manual_entry: 'Manuelle Eingabe',
            automation_type: 'Automatisierungstyp',
            select_automation_type: 'Automatisierungstyp auswählen...',
            automation_full: 'Vollständig – der Connector automatisiert diese Steuerung vollständig',
            automation_partial: 'Teilweise – Connector liefert Beweise; Menschen handeln immer noch',
            automation_monitoring: 'Nur Überwachung – Connector beobachtet, erzwingt nicht',
            control_id_help: 'Verwenden Sie den Code aus Ihrem Compliance-Schema (z. B. ISO-27001-A.12.1, SOC2-CC6.1 oder Ihre interne Kontroll-ID).',
            control_name_help: 'Kurzer, für Menschen lesbarer Name (z. B. „Netzwerkzugriffsbeschränkung“).',
            description_label: 'Beschreibung',
            load_sarif_example: 'SARIF-Beispiel laden',
            load_json_example: 'Laden Sie ein benutzerdefiniertes JSON-Beispiel',
            scan_format_help: 'SARIF ist der OASIS-Standard, der von den meisten Scannern (Snyk, CodeQL, Semgrep) ausgegeben wird. Verwenden Sie benutzerdefiniertes JSON für Tools, die kein SARIF ausgeben.',
            scan_data_help: 'Fügen Sie die vollständige Scan-Ausgabe ein, laden Sie eine .sarif/.json-Datei hoch oder klicken Sie auf die Schaltfläche „Beispiel laden“, um die Form anzuzeigen.',
            upload_file_label: 'Laden Sie die .sarif- oder .json-Datei hoch',
            invalid_scan_json: 'Scandaten konnten nicht als JSON geparst werden. Suchen Sie nach Syntaxfehlern in der Nähe des hervorgehobenen Zeichens.',
            save_as_review: 'Als Sicherheitsüberprüfung speichern',
            discard_preview: 'Vorschau verwerfen',
            preview_hint: 'Dies ist eine Vorschau – es ist noch nichts gespeichert. Klicken Sie auf „Als Sicherheitsüberprüfung speichern“, um eine ausstehende Überprüfung zu erstellen, die mit diesen Werten vorausgefüllt ist.',
            reviewer_required_for_save: 'Wählen Sie einen Prüfer aus, um diesen Scan als Sicherheitsüberprüfung zu speichern.',
        },
        poc_scorecards: {
            title: 'POC-Bewertungskarten',
            page_title: 'POC-Scorecards',
            create_scorecard: 'Bewertungskarte erstellen',
            connector_label: 'Konnektor',
            select_connector: 'Konnektor auswählen...',
            vendor_name: 'Anbietername',
            recommendation: 'Empfehlung',
            recommendation_placeholder: 'z.B. Fortfahren, Nicht fortfahren, Weitere Bewertung erforderlich',
            criteria: 'Bewertungskriterien',
            criterion_name: 'Kriteriumsname',
            score: 'Punktzahl',
            max_score: 'Maximalpunktzahl',
            notes: 'Notizen',
            add_criterion: 'Kriterium hinzufügen',
            remove_criterion: 'Kriterium {name} entfernen',
            total_score: 'Gesamtpunktzahl',
            api_quality: 'API-Qualität',
            reliability: 'Zuverlässigkeit',
            support_quality: 'Supportqualität',
            cost_score: 'Kostenbewertung',
            overall_score: 'Gesamtbewertung',
            evidence: 'Nachweise',
            no_scorecards_found: 'Keine Bewertungskarten gefunden',
            scorecard_created: 'POC-Scorecard erstellt',
            connector_help: 'Wählen Sie den Connector aus, für den diese POC-Bewertung gedacht ist.',
            vendor_name_help: 'Der zu bewertende Anbieter oder das zu bewertende Produkt – z. „Okta“, „Datadog“, „Snowflake“.',
            vendor_name_placeholder: 'z.B. Okta, Datadog, Schneeflocke',
            recommendation_help: 'Ihr letzter Anruf nach der Bewertung. Halten Sie es kurz und entschlossen.',
            criteria_help: 'Bewerten Sie den Anbieter nach jedem Bewertungskriterium. Fügen Sie eine Zeile pro Kriterium hinzu (API Qualität, Zuverlässigkeit, Support, Kosten usw.).',
            criterion_name_help: 'Was Sie bewerten – z.B. „API Qualität“, „Zuverlässigkeit“, „Support“, „Kosten“.',
            criterion_name_placeholder: 'z.B. API Qualität',
            score_help: 'Für dieses Kriterium wurde eine Punktzahl vergeben. Darf die maximale Punktzahl nicht überschreiten.',
            max_score_help: 'Höchstpunktzahl für dieses Kriterium verfügbar. Verwenden Sie in allen Zeilen die gleiche Skala, damit die Gesamtwerte vergleichbar sind.',
            notes_help: 'Optional. Fügen Sie Beweise, Vorbehalte oder Links hinzu – alles, was das Ergebnis erklärt.',
        },
        common_labels: {
            aria_filter_instances: 'Konnektorinstanzen filtern',
            aria_filter_definitions: 'Konnektordefinitionen filtern',
            aria_filter_credentials: 'Anmeldedaten filtern',
            aria_filter_events: 'Ereignisse filtern',
            aria_filter_dlq: 'Dead-Letter-Warteschlange filtern',
            aria_instance_summary: 'Konnektorinstanz-Zusammenfassung',
            aria_active_connectors: 'Aktive Konnektoren',
            aria_error_connectors: 'Fehlerhafte Konnektoren',
            aria_credential_health: 'Zusammenfassung des Anmeldedaten-Status',
            aria_items_per_page: 'Elemente pro Seite',
            show_password: 'Passwort anzeigen',
            hide_password: 'Passwort ausblenden',
            payload_json_label: 'Nutzlast (JSON)',
            payload_invalid_json: 'Nutzlast muss gültiges JSON sein',
            placeholder_event_type: 'z.B. incident.created',
            placeholder_source_module: 'z.B. monitoring',
            placeholder_version: 'z.B. 1.0',
        },
        statuses: {
            configuring: 'Konfigurieren',
            testing: 'Testen',
            active: 'Aktiv',
            paused: 'Angehalten',
            error: 'Fehler',
            disabled: 'Deaktiviert',
            draft: 'Entwurf',
            approved: 'Genehmigt',
            archived: 'Archiviert',
            pending: 'Ausstehend',
            rejected: 'Abgelehnt',
        },
        system_types: {
            application: 'Anwendung',
            database: 'Datenbank',
            service: 'Service',
            infrastructure: 'Infrastruktur',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Vor Ort',
            cloud: 'Wolke',
            hybrid: 'Hybrid',
            managed: 'Verwaltet',
        },
        system_statuses: {
            active: 'Aktiv',
            decommissioned: 'Außer Betrieb',
            planned: 'Geplant',
            under_review: 'In Überprüfung',
        },
        automation_statuses: {
            planning: 'Planung',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            on_hold: 'Pausiert',
        },
        gap_types: {
            functional: 'Funktional',
            technical: 'Technisch',
            security: 'Sicherheit',
            compliance: 'Compliance',
            process: 'Prozess',
        },
        severity_labels: {
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Niedrig',
        },
        gap_statuses: {
            open: 'Offen',
            in_progress: 'In Bearbeitung',
            mitigated: 'Mitigiert',
            closed: 'Geschlossen',
            accepted: 'Akzeptiert',
        },
        verification_statuses: {
            not_started: 'Nicht gestartet',
            in_progress: 'In Bearbeitung',
            verified: 'Verifiziert',
            failed: 'Fehlgeschlagen',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Intern',
            email: 'E-Mail',
            queue: 'Warteschlange',
        },
        credential_types: {
            password: 'Passwort',
            api_key: 'API-Schlüssel',
            oauth2_client: 'OAuth2-Client',
            certificate: 'Zertifikat',
            token: 'Token',
            ssh_key: 'SSH-Schlüssel',
            imap_credentials: 'IMAP-Anmeldedaten',
        },
        categories: {
            hr: 'Personalwesen',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Wolke',
            security: 'Sicherheit',
            itsm: 'ITSM',
            custom: 'Benutzerdefiniert',
        },
        delivery_statuses: {
            pending: 'Ausstehend',
            delivered: 'Zugestellt',
            partial: 'Teilweise',
            failed: 'Fehlgeschlagen',
        },
        automation_planning: {
            title: 'Automatisierungsplanung',
            page_title: 'Automatisierungsplanung',
            program_name: 'Programmname',
            program_status: 'Status',
            description: 'Beschreibung',
            linked_artifacts: 'Verknüpfte Artefakte',
            create_program: 'Programm erstellen',
            edit_program: 'Programm bearbeiten',
            no_programs_found: 'Keine Programme gefunden',
            program_created: 'Programm erstellt',
            program_updated: 'Programm aktualisiert',
            target_date: 'Zieldatum',
            owner: 'Eigentümer',
            select_status: 'Status auswählen...',
            aria_programs_table: 'Automatisierungsprogramme',
            aria_edit: '{name} bearbeiten',
            export_programs: 'Programme exportieren',
            title_label: 'Programmtitel',
            title_help: 'Ein kurzer, für Menschen lesbarer Name für das Automatisierungsprogramm – z. B. „Q3-Incident-to-Jira-Rollout“, „PDPL Zugriffsanfrage-Automatisierung“.',
            title_placeholder: 'z.B. Q3-Incident-zu-Jira-Rollout',
            description_help: 'Was dieses Programm abdeckt und welches Ergebnis Sie erwarten, wenn es live ist. Optional, aber empfohlen.',
            description_placeholder: 'z.B. Übertragen Sie Überwachungswarnungen in Jira, sodass Bereitschaftsdienste innerhalb von 60 Sekunden weitergeleitet werden.',
            owner_label: 'Programminhaber',
            owner_help: 'Die für dieses Programm verantwortliche Person. Sie erhalten Benachrichtigungen über Fortschritte und Tore.',
            select_owner: 'Wählen Sie einen Besitzer aus...',
            frameworks_in_scope: 'Frameworks im Geltungsbereich',
            frameworks_in_scope_help: 'Compliance-Frameworks, die dieses Programm unterstützt. Hilft dem Prüfpfad, zu zeigen, welchen Vorschriften die Automatisierung unterliegt.',
            select_frameworks: 'Frameworks auswählen...',
            status_draft: 'Entwurf',
            status_active: 'Aktiv',
            status_completed: 'Abgeschlossen',
            status_archived: 'Archiviert',
            change_status: 'Status ändern',
            status_updated: 'Status aktualisiert',
            aria_change_status: 'Status ändern für {name}',
        },
        system_inventory: {
            title: 'Systeminventar',
            page_title: 'Systeminventar',
            system_name: 'Systemname',
            system_type: 'Systemtyp',
            hosting: 'Hosting',
            status: 'Status',
            description: 'Beschreibung',
            create_system: 'System erstellen',
            edit_system: 'System bearbeiten',
            no_systems_found: 'Keine Systeme gefunden',
            system_created: 'System erstellt',
            system_updated: 'System aktualisiert',
            select_type: 'Typ auswählen...',
            select_hosting: 'Hosting auswählen...',
            select_status: 'Status auswählen...',
            owner: 'Eigentümer',
            department: 'Abteilung',
            data_categories: 'Datenkategorien',
            data_categories_placeholder: 'Kommagetrennt, z. B. PII, Finanzen, Gesundheit',
            jurisdiction: 'Gerichtsbarkeit',
            jurisdiction_placeholder: 'z. B. EU, US, Global',
            interfaces: 'Schnittstellen',
            interfaces_placeholder: 'Kommagetrennt, z. B. REST API, SFTP, JDBC',
            owner_placeholder: 'Wählen Sie einen verantwortlichen Eigentümer aus...',
            department_placeholder: 'Wählen Sie die verantwortliche Organisationseinheit aus...',
            system_name_hint: 'Verwenden Sie den geschäftlich anerkannten System- oder Anwendungsnamen.',
            system_type_hint: 'Klassifizieren Sie das System, damit das Reporting ähnliche Assets gruppieren kann.',
            hosting_hint: 'Identifizieren Sie, wo das System für Residenz, Sicherheit und Lieferantenbewertung ausgeführt wird.',
            status_hint: 'Legen Sie den Lebenszyklusstatus fest, der von Überprüfungen, Exporten und Audit-Nachweisen verwendet wird.',
            owner_hint: 'Wählen Sie den aktiven Benutzer aus, der für die System-Governance und Follow-up-Aufgaben verantwortlich ist.',
            department_hint: 'Verwenden Sie die Organisationseinheit, die für den Betrieb oder die Verwaltung dieses Systems verantwortlich ist.',
            data_categories_hint: 'Listen Sie die von diesem System verarbeiteten Datenklassen auf, getrennt durch Kommas.',
            jurisdiction_hint: 'Erfassen Sie die wichtigste Rechts- oder Hosting-Gerichtsbarkeit für das Compliance-Mapping.',
            interfaces_hint: 'Listen Sie Connector- oder Integrationspfade auf, die zum Datenaustausch verwendet werden.',
            description_hint: 'Fassen Sie zusammen, was das System tut und warum es für die Datenlandkarte wichtig ist.',
            owner_department_required: 'Eigentümer und Abteilung sind erforderlich.',
            aria_systems_table: 'Systeminventar',
            aria_edit: '{name} bearbeiten',
            export_systems: 'Systeme exportieren',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Rückverfolgbarkeitsmatrix',
            page_title: 'Rückverfolgbarkeitsmatrix',
            brd_selector: 'BRD auswählen',
            requirement: 'Anforderung',
            linked_artifact: 'Verknüpftes Artefakt',
            verification_status: 'Verifizierungsstatus',
            verified_by: 'Verifiziert von',
            create_entry: 'Eintrag erstellen',
            edit_entry: 'Eintrag bearbeiten',
            no_entries_found: 'Keine Einträge gefunden',
            entry_created: 'Eintrag erstellt',
            entry_updated: 'Eintrag aktualisiert',
            select_brd: 'BRD auswählen...',
            select_status: 'Status auswählen...',
            aria_matrix_table: 'Rückverfolgbarkeitsmatrix',
            aria_edit: 'Eintrag für {name} bearbeiten',
            export_matrix: 'Matrix exportieren',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Anforderungs-ID',
        },
        gap_register: {
            title: 'Lückenregister',
            page_title: 'Lückenregister',
            gap_title: 'Lückentitel',
            gap_type: 'Lückentyp',
            severity: 'Schweregrad',
            status: 'Status',
            description: 'Beschreibung',
            remediation_plan: 'Behebungsplan',
            assigned_to: 'Zugewiesen an',
            due_date: 'Fälligkeitsdatum',
            create_gap: 'Lücke erstellen',
            edit_gap: 'Lücke bearbeiten',
            no_gaps_found: 'Keine Lücken gefunden',
            gap_created: 'Lücke erstellt',
            gap_updated: 'Lücke aktualisiert',
            select_type: 'Typ auswählen...',
            select_severity: 'Schweregrad auswählen...',
            select_status: 'Status auswählen...',
            aria_gaps_table: 'Lückenregister',
            aria_edit: '{name} bearbeiten',
            export_gaps: 'Lücken exportieren',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Rahmenwerk-Verknüpfung',
            framework_linkage_placeholder: 'z.B. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Rahmenwerk-Umfang',
            framework_scope_placeholder: 'z.B. GDPR, PCI-DSS',
            procedure_linkage: 'Verfahrensverknüpfung',
            procedure_linkage_placeholder: 'z.B. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Regulierungsquelle',
            regulatory_source_placeholder: 'z.B. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Kontrollzuordnung',
            control_mappings_section: 'Kontrollzuordnungen',
            no_control_mappings: 'Noch keine Steuerungszuordnungen. Klicken Sie auf „Steuerungszuordnung erstellen“, um eine hinzuzufügen.',
            automation_type_col: 'Automatisierung',
        },
        gap_register_extra: {
            framework_filter: 'Rahmenwerk',
            framework_id: 'Rahmenwerk-ID',
            framework_id_placeholder: 'z.B. GDPR, ISO 27001',
            all_frameworks: 'Alle Rahmenwerke',
        },
        poc_scorecards_extra: {
            criteria_count: 'Kriterien',
        },
        export_labels: {
            export_brds: 'BRDs exportieren',
            export_decision_records: 'Entscheidungsregister exportieren',
            export_security_reviews: 'Sicherheitsüberprüfungen exportieren',
            export_poc_scorecards: 'POC-Scorekarten exportieren',
            export_instances: 'Instanzen exportieren',
            export_definitions: 'Definitionen exportieren',
            export_credentials: 'Anmeldeinformationen exportieren',
            export_events: 'Ereignisse exportieren',
            exporting: 'Wird exportiert...',
            export_success: 'Export abgeschlossen',
            export_error: 'Export fehlgeschlagen',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Verpflichtungen extrahieren',
            extracting: 'Extrahieren…',
            results_heading: 'Extrahierte Verpflichtungen ({count})',
            tokens_label: '≈ {count} Token',
            no_obligations: 'In diesem Vertragstext sind keine Verpflichtungsmuster erkennbar.',
            col_class: 'Klasse',
            col_summary: 'Zusammenfassung (bearbeitbar)',
            col_excerpt: 'Satzauszug',
            col_due_date: 'Fälligkeitsdatum (bearbeitbar)',
            col_priority: 'Priorität',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Kündigungsfrist',
            class_audit: 'Audit richtig',
            class_insurance: 'Versicherung',
            class_sla: 'Antwort SLA',
            class_breach: 'Benachrichtigung über Verstöße',
            class_renewal: 'Erneuerungsfenster',
            citations_label: 'Zitate',
            accept_hint: 'Akzeptieren erstellt eine Aufgabe pro Verpflichtung (Kapitel 8) mit angezeigter Priorität + Fälligkeitsdatum.',
            accept_label: 'Akzeptieren Sie Verpflichtungen',
            reject_label: 'Bericht ablehnen',
            resolve_summary: 'Lösung: {status} · {tasks} Aufgabe(n) erstellt',
            error_generic: 'Verpflichtungen konnten nicht extrahiert werden. Versuchen Sie es erneut oder überprüfen Sie den Vertragstext.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Begründungsentwurf',
            drafting: 'Begründung verfassen …',
            results_heading: 'AI-ausgearbeitete Begründung ({risk_level})',
            tokens_label: '≈ {count} Token',
            narrative_label: 'Begründungserzählung',
            controls_heading: 'Ausgleichskontrollen',
            no_controls: 'Es werden keine kompensierenden Kontrollen vorgeschlagen.',
            citations_label: 'Zitate',
            accept_label: 'Akzeptieren Sie die Argumentation',
            reject_label: 'Entwurf ablehnen',
            accept_hint: 'Accept zeichnet die Begründung des beibehaltenen Entwurfs für die IAT-Abstammung auf. Die Erzähl- und Steuerelemente aktualisieren den Annahmedatensatz nicht automatisch – ein Einfügen durch den Bediener ist erforderlich.',
            resolve_summary: 'Auflösung: {status}',
            error_generic: 'Begründung konnte nicht formuliert werden. Versuchen Sie es erneut oder überprüfen Sie den Risikoakzeptanzstatus.',
        },
        redline_assistant: {
            run_label: 'Redline mit Bibliothek',
            running: 'Redline läuft…',
            results_heading: 'Redline-Vorschläge ({count})',
            tokens_label: '≈ {count} Token',
            no_suggestions: 'Es wurden keine Lücken in den Standardklauseln festgestellt.',
            jurisdiction_label: 'Gerichtsstand',
            col_kind: 'Freundlich',
            col_severity: 'Schweregrad',
            col_title: 'Vorschlag',
            col_excerpt: 'Passender Auszug',
            col_apply: 'Bewerben',
            kind_missing: 'Fehlender Standard',
            kind_weaker: 'Schwächer als Standard',
            kind_jurisdiction: 'Zuständigkeitsüberlagerung',
            proposed_label: 'Vorgeschlagener Text',
            citations_label: 'Zitate',
            accept_label: 'Ausgewählte akzeptieren',
            reject_label: 'Bericht ablehnen',
            partial_label: 'Auswahl anwenden',
            accept_hint: '„Auswahl anwenden“ zeichnet die ausgewählten Vorschläge im Entwurf für die IAT-Abstammung auf. Der Vertragstext wird nicht automatisch bearbeitet – der Betreiber bestimmt die Redline.',
            resolve_summary: 'Lösung: {status} · {count} Vorschlag(e) angewendet',
            error_generic: 'Es konnte keine Redline erstellt werden. Versuchen Sie es erneut oder überprüfen Sie den Vertragsstatus.',
            clause_library_link: 'Klauselbibliothek verwalten',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Klauselbibliothek',
            add_label: 'Klausel hinzufügen',
            edit_label: 'Bearbeiten',
            delete_label: 'Löschen',
            slug_label: 'Schnecke',
            title_label: 'Titel',
            body_label: 'Körper',
            jurisdiction_label: 'Gerichtsstand',
            tags_label: 'Tags (durch Kommas getrennt)',
            must_contain_label: 'Muss Markierungen enthalten (durch Kommas getrennt)',
            severity_label: 'Schweregrad bei Fehlen',
            no_clauses: 'Noch keine Klauseln. Fügen Sie einen hinzu, um den Redline-Assistenten zu starten.',
            save_label: 'Speichern',
            cancel_label: 'Abbrechen',
            load_error: 'Klauselbibliothek konnte nicht geladen werden.',
            save_error: 'Klausel konnte nicht gespeichert werden.',
            delete_error: 'Klausel konnte nicht gelöscht werden.',
            delete_confirm: 'Klausel „{slug}“ löschen? Dies kann nicht rückgängig gemacht werden.',
        },
        subprocessor_impact: {
            assess_label: 'Bewerten Sie die Auswirkungen',
            assessing: 'Auswirkungen abschätzen…',
            results_heading: 'Auswirkungen auf den Unterprozessor ({severity})',
            tokens_label: '≈ {count} Token',
            risk_delta_label: 'Risikodelta',
            notification_heading: 'Kundenbenachrichtigung',
            subject_label: 'Betreff',
            deadline_label: 'Benachrichtigungsfrist',
            body_label: 'Benachrichtigungsstelle',
            required_fields_label: 'Felder der Mitteilung gemäß Artikel 28 Absatz 2',
            mitigation_heading: 'Abhilfemaßnahmen',
            citations_label: 'Zitate',
            accept_label: 'Beurteilung akzeptieren',
            reject_label: 'Beurteilung ablehnen',
            accept_hint: 'Accept erstellt eine Kundenbenachrichtigungsaufgabe mit der Frist SLA als Fälligkeitsdatum.',
            resolve_summary: 'Auflösung: {status}{task}',
            error_generic: 'Dieser Subprozessorwechsel konnte nicht beurteilt werden. Versuchen Sie es erneut oder überprüfen Sie den Änderungsdatensatz.',
        },
        onboarding_evidence: {
            collect_label: 'Führen Sie die Beweissammlung AI durch',
            collecting: 'Beweise sammeln…',
            results_heading: 'Beweissammlung ({tier})',
            tokens_label: '≈ {count} Token',
            findings_heading: 'Vorhandene Beweise',
            missing_heading: 'Fehlende Artefakte',
            no_findings: 'Es wurden keine vorhandenen Beweise gefunden.',
            no_missing: 'Für diese Risikostufe fehlen keine erforderlichen Artefakte.',
            next_step_label: 'Nächster Schritt',
            class_dpa: 'Datenverarbeitungsvereinbarung',
            class_soc2: 'SOC 2-Bericht',
            class_iso27001: 'ISO 27001-Zertifikat',
            class_insurance: 'Cyber-Versicherungspolice',
            class_incident_history: 'Vorheriger Vorfall',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Bericht annehmen',
            reject_label: 'Bericht ablehnen',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Die Beweissammlung AI konnte nicht ausgeführt werden. Versuchen Sie es erneut oder überprüfen Sie den Onboarding-Fallstatus.',
        },
        no_permission_title: 'Unzureichende Berechtigungen',
        no_permission_message: 'Sie sind nicht berechtigt, auf das Lieferantenrisikomanagement zuzugreifen.',
        unknown_vendor: 'Unbekannter Anbieter',
        nav: {
            dashboard: 'Risikoübersicht',
            scoring: 'Risikobewertung',
            questionnaires: 'Fragebögen',
            contracts: 'Verträge',
            gates: 'Genehmigungstore',
            reviews: 'Lieferantenbewertungen',
            signals: 'Risikosignale',
            reassessments: 'Neubewertungen',
            portfolio: 'Risikoportfolio',
            acceptances: 'Risikoakzeptanz',
            review_routing: 'Prüfungsweiterleitung',
            onboarding_cases: 'Onboarding-Fälle',
            sub_processor_register: 'Register der Unterauftragsverarbeiter',
        },
        dashboard: {
            title: 'Lieferantenrisikoübersicht',
            page_title: 'Lieferantenrisiko-Dashboard',
            loading: 'Lade Dashboard Daten...',
            risk_tier_header: 'Risikostufe',
            count_header: 'Zählen',
            percentage_header: 'Prozentsatz',
            sections_nav: 'Abschnitte zum Lieferantenrisiko',
            urgent_expiry: 'Dringend: läuft innerhalb von 14 Tagen ab',
            no_previous_tier: 'Keine vorherige Stufe',
            view_as_chart: 'Als Diagramm anzeigen',
            view_as_table: 'Als Tabelle anzeigen',
            showing_chart: 'Diagrammansicht',
            showing_table: 'Datentabellenansicht',
            load_error: 'Fehler beim load Anbieter Risiko Dashboard',
            risk_distribution: 'Risiko Distribution Overview',
            recent_tier_changes: 'Kürzliche Stufe Änderungen',
            no_tier_changes: 'No recent Stufe changes.',
            review_kpis: 'Überprüfen KPIs',
            total_vendors_reviewed: 'Gesamt Anbieter Überprüft',
            reviews_by_outcome: 'Überprüfungen nach Ergebnis',
            contract_coverage: 'Vertrag Abdeckung',
            total_vendors: 'Gesamt Anbieter',
            with_active_dpa: 'Mit Aktiv DPA',
            without_dpa: 'Ohne DPA',
            dpa_coverage: 'DPA Abdeckung',
            active_contracts_by_type: 'Aktiv Verträge nach Typ',
            expiring_contracts: 'Ablaufend Verträge (Next 90 Days)',
            no_expiring_contracts: 'No Verträge expiring within der next 90 Tage.',
            vendor: 'Anbieter',
            previous_tier: 'Vorherig Stufe',
            current_tier: 'Aktuell Stufe',
            changed_at: 'Geändert At',
            expires_at: 'Läuft ab am',
            days_remaining: 'Tage Verbleibend',
            days: 'Tage',
            vendors_label: 'Anbieter',
            no_data: 'No Daten',
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Niedrig',
            expiring_acceptances_heading: 'Ablaufende Risikoakzeptanzen',
            expiring_30d_label: 'Ablauf in 30 Tagen',
            expiring_60d_label: 'Ablauf in 60 Tagen',
            expiring_90d_label: 'Ablauf in 90 Tagen',
        },
        scoring: {
            title: 'Risikobewertung',
            page_title: 'Bewertung des Lieferantenrisikos',
            filter_scores: 'Risikobewertungen filtern',
            filter_by_tier: 'Filtern Sie nach Risikostufe',
            scores_pagination: 'Paginierung der Risikobewertung',
            config_dimensions: 'Konfiguration der Scoring-Dimensionen',
            scores_loaded: '{count} Risikobewertungen geladen',
            weight_must_equal_100: 'Das Gesamtgewicht muss 100 betragen (aktuell: {current})',
            out_of_10: 'von 10, bewertet mit {tier}',
            dimension_name: 'Name der Dimension {index}.',
            dimension_weight: '{name} Gewicht (Prozentsatz)',
            must_equal_100: 'muss gleich 100 sein',
            config_title: 'Bewertung Konfiguration',
            scores_title: 'Anbieter Risiko Bewertungen',
            new_score_title: 'Bewertung ein Anbieter',
            new: 'Neu Bewertung',
            vendor_id: 'Anbieter ID',
            risk_tier: 'Risiko Stufe',
            score: 'Bewertung',
            scored_at: 'Bewertet am',
            no_scores: 'Keine Risiko scores gefunden.',
            all_tiers: 'Alle Stufen',
            load_config_error: 'Fehler beim load Risiko Bewertung Konfiguration',
            update_config: 'Bewertungskonfiguration aktualisieren',
            update_config_error: 'Fehler beim update Risiko Bewertung Konfiguration',
            load_scores_error: 'Fehler beim load Anbieter Risiko scores',
            calculate_error: 'Fehler beim calculate Anbieter Risiko score',
            weight: 'Gewicht',
            score_range: 'Bewertung (1-5)',
            method: 'Methode',
        },
        questionnaires: {
            title: 'Fragebögen',
            page_title: 'Lieferantenfragebögen',
            filter_questionnaires: 'Fragebogenfilter',
            questionnaire_views: 'Ansichten im Fragebogen',
            templates_list: 'Liste der Fragebogenvorlagen',
            instances_list: 'Liste der Fragebogeninstanzen',
            filter_by_status: 'Filtern Sie nach Fragebogenstatus',
            templates_per_page: 'Vorlagen pro Seite',
            instances_per_page: 'Instanzen pro Seite',
            select_template: 'Fragebogenvorlage auswählen',
            template_category: 'Vorlagenkategorie',
            templates_tab: 'Vorlagen',
            instances_tab: 'Instanzen',
            new: 'Neu Questionnaire',
            new_template: 'Neue Vorlage',
            new_instance: 'Neue Instanz',
            vendor_id: 'Anbieter ID',
            template_id: 'Vorlage ID',
            assigned_to: 'Zugewiesen Zu',
            due_date: 'Due Datum',
            responses: 'Antworten',
            risk_score: 'Fertigstellung',
            no_questionnaires: 'Keine Fragebögen gefunden.',
            load_templates_error: 'Fehler beim load Fragebogen Vorlagen',
            load_instances_error: 'Fehler beim load Fragebögen',
            create_template_error: 'Fehler beim create Fragebogen Vorlage',
            initiate_error: 'Fehler beim initiate Fragebogen',
            submit_error: 'Fehler beim submit Fragebogen',
            review_error: 'Fehler beim Überprüfung Fragebogen',
            resubmit: 'Erneut einreichen',
            approve_error: 'Fragebogen konnte nicht genehmigt werden',
            reject_error: 'Fragebogen konnte nicht abgelehnt werden',
            resubmit_error: 'Fragebogen konnte nicht erneut eingereicht werden',
            templates_loaded: '{count} Fragebogenvorlagen geladen',
            instances_loaded: '{count} Fragebogeninstanzen geladen',
            fill_responses_cta: 'Antworten ausfüllen',
            fill_responses_title: 'Fragebogenantworten ausfüllen',
            template_missing: 'Vorlage nicht gefunden.',
            responses_saved: 'Antworten gespeichert.',
            load_template_error: 'Vorlage konnte nicht geladen werden.',
            version_history: 'Versionsverlauf',
            download_previous_version: 'Frühere Version herunterladen',
            trigger: 'Auslöser',
            template_label: 'Vorlage',
            reviewer: 'Rezensent',
            reviewer_required: 'Für {trigger} Fragebögen muss ein Gutachter zugewiesen werden (Aufgabentrennung).',
            select_option: 'Wählen Sie eine Option',
            multi_choice_hint: 'Wählen Sie alles aus, was zutrifft',
            category_initial_onboarding: 'Erstes Onboarding',
            category_annual_review: 'Jahresrückblick',
            category_incident_triggered: 'Durch einen Vorfall ausgelöst',
            category_contract_renewal: 'Vertragsverlängerung',
            template_editor: {
                page_title: 'Fragebogenvorlage bearbeiten',
                preview_page_title: 'Vorschau der Fragebogenvorlage',
                edit_structure: 'Struktur bearbeiten',
                preview: 'Vorschau',
                actions: 'Aktionen',
                back_to_list: 'Zurück zu den Vorlagen',
                load_error: 'Vorlage konnte nicht geladen werden',
                save_error: 'Die Vorlagenstruktur konnte nicht gespeichert werden',
                saved: 'Vorlagenstruktur gespeichert.',
                sections_heading: 'Abschnitte',
                questions_heading: 'Fragen',
                add_section: 'Abschnitt hinzufügen',
                edit_section: 'Abschnitt bearbeiten',
                delete_section: 'Abschnitt löschen',
                move_section_up: 'Abschnitt nach oben verschieben',
                move_section_down: 'Abschnitt nach unten verschieben',
                confirm_delete_section: 'Abschnitt „{title}“ löschen? Alle seine Fragen werden entfernt.',
                section_title: 'Abschnittstitel',
                section_description: 'Abschnittsbeschreibung',
                section_id: 'Abschnitts-ID',
                section_id_hint: 'Wird automatisch generiert, es sei denn, Sie legen eine explizit fest.',
                no_sections: 'Noch keine Abschnitte. Fügen Sie zunächst einen Abschnitt hinzu.',
                no_questions: 'In diesem Abschnitt gibt es noch keine Fragen.',
                add_question: 'Frage hinzufügen',
                edit_question: 'Frage bearbeiten',
                delete_question: 'Frage löschen',
                move_question_up: 'Frage nach oben verschieben',
                move_question_down: 'Frage nach unten verschieben',
                confirm_delete_question: 'Frage „{text}“ löschen?',
                question_text: 'Fragetext',
                question_type: 'Fragetyp',
                question_id: 'Frage-ID',
                evidence_required: 'Nachweis erforderlich',
                risk_weight: 'Risikogewicht',
                scoring_map: 'Wertungskarte',
                scoring_map_json_hint: 'JSON Objekt, das Antwortwerte numerischen Bewertungen zuordnet.',
                scoring_map_invalid_json: 'Die Wertungskarte muss gültig sein JSON.',
                options_heading: 'Antwortmöglichkeiten',
                add_option: 'Option hinzufügen',
                remove_option: 'Option entfernen',
                option_label: 'Etikett',
                option_value: 'Wert',
                option_risk_weight: 'Risikogewicht',
                min_options_error: 'Auswahlfragen müssen mindestens zwei Optionen haben.',
                option_label_required: 'Jede Option benötigt eine Beschriftung.',
                option_value_required: 'Jede Option benötigt einen Wert.',
                option_value_unique: 'Optionswerte müssen eindeutig sein.',
                branching_rules_heading: 'Verzweigungsregeln',
                branching_rules_hint: 'Diese Frage basierend auf der Antwort auf eine frühere Frage ein- oder ausblenden.',
                add_branching_rule: 'Verzweigungsregel hinzufügen',
                remove_branching_rule: 'Verzweigungsregel entfernen',
                branching_condition_question: 'Wenn Frage',
                branching_condition_value: 'hat Wert',
                branching_action: 'dann',
                branching_action_show: 'zeige diese Frage',
                branching_action_hide: 'verstecke diese Frage',
                branching_select_condition_question: 'Wählen Sie eine frühere Frage aus',
                branching_select_condition_value: 'Wert auswählen',
                save: 'Struktur speichern',
                saving: 'Sparen...',
                cancel: 'Abbrechen',
                close: 'Schließen',
                question_text_required: 'Fragetext ist erforderlich.',
                section_title_required: 'Abschnittstitel ist erforderlich.',
                type_single_choice: 'Einzelauswahl',
                type_multi_choice: 'Mehrfachauswahl',
                type_free_text: 'Freitext',
                type_numeric: 'Numerisch',
                type_date: 'Datum',
                type_file_upload: 'Datei-Upload',
                type_yes_no: 'Ja/Nein',
                preview_heading: 'Vorlagenvorschau',
                preview_subtitle: 'Schreibgeschützte Vorschau, wie der Fragebogen einem Anbieter angezeigt wird.',
                preview_no_content: 'Diese Vorlage enthält noch keine Abschnitte oder Fragen.',
                preview_branching_notice: 'Verzweigungsregeln werden beim Ausfüllen ausgewertet; Die Vorschau zeigt jede Frage.',
            },
            review_modal: {
                title: 'Überprüfen Sie die Antworten auf den Fragebogen',
                open_cta: 'Rezension',
                loading: 'Fragebogen wird geladen...',
                template_missing: 'Vorlage nicht gefunden.',
                load_error: 'Der Fragebogen konnte nicht zur Überprüfung geladen werden.',
                annotation_heading: 'Anmerkungen',
                annotation_placeholder: 'Fügen Sie eine Anmerkung eines Rezensenten zu dieser Frage hinzu...',
                annotation_status_label: 'Anmerkungsstatus',
                annotation_status_acceptable: 'Akzeptabel',
                annotation_status_concern: 'Sorge',
                annotation_status_requires_action: 'Erfordert Maßnahmen',
                review_comments_label: 'Gesamtbewertungskommentare',
                review_comments_placeholder: 'Fassen Sie Ihre Bewertungsergebnisse zusammen...',
                justification_label: 'Entscheidungsbegründung',
                justification_placeholder: 'Erläutern Sie die Gründe für Ihre Entscheidung ...',
                reject_requires_comments: 'Für die Ablehnung eines Fragebogens sind Überprüfungskommentare erforderlich.',
                decision_heading: 'Entscheidung',
                decision: {
                    approved: 'Genehmigen',
                    rejected: 'Ablehnen',
                    additional_info_requested: 'Fordern Sie zusätzliche Informationen an',
                },
                submit: 'Bewertung abgeben',
                submitting: 'Bewertung wird eingereicht...',
                cancel: 'Abbrechen',
                submit_success: 'Bewertung eingereicht.',
                submit_error: 'Bewertung konnte nicht eingereicht werden.',
                separation_of_duties_error: 'Sie können einen Fragebogen, mit dessen Ausfüllen Sie beauftragt wurden, nicht überprüfen. Die Aufgabentrennung erfordert einen anderen Gutachter.',
                decision_required: 'Bitte wählen Sie eine Bewertungsentscheidung aus.',
                announce_opened: 'Bewertungsmodalität geöffnet.',
                announce_submitted: 'Bewertung eingereicht.',
            },
            instance_detail: {
                submitted_at: 'Eingereicht',
                reviewed_at: 'Bewertet',
                reviewer: 'Rezensent',
                review_decision: 'Entscheidung',
                review_comments: 'Überprüfen Sie Kommentare',
                decision_approved: 'Genehmigt',
                decision_rejected: 'Abgelehnt',
                decision_additional_info_requested: 'Weitere Informationen erbeten',
                decision_pending: 'Ausstehende Überprüfung',
                expand_comments: 'Vollständige Kommentare anzeigen',
                collapse_comments: 'Vollständige Kommentare ausblenden',
                no_reviewer: 'Kein Prüfer zugewiesen',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Entwurf',
                in_progress: 'In Bearbeitung',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Genehmigt',
                rejected: 'Abgelehnt',
                expired: 'Abgelaufen',
            },
            export: {
                portfolio_label: 'Exportportfolio',
                instance_label: 'Exportieren',
                portfolio_success: 'Portfolio-Export bereit.',
                instance_success: 'Fragebogenexport bereit.',
                portfolio_error: 'Der Export des Fragebogenportfolios ist fehlgeschlagen.',
                instance_error: 'Der Fragebogen konnte nicht exportiert werden.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Einige Fragen werden durch Verzweigungsregeln ausgeblendet.',
                cycle_warning: 'In den Verzweigungsregeln wurde ein Zyklus erkannt. Betroffene Fragen sind standardmäßig sichtbar.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Verträge',
            page_title: 'Lieferantenverträge',
            new: 'Neu Vertrag',
            vendor_id: 'Anbieter ID',
            filter_contracts: 'Verträge filtern',
            filter_by_document_type: 'Filtern Sie nach Dokumenttyp',
            filter_by_status: 'Filtern Sie nach Vertragsstatus',
            per_page: 'Verträge pro Seite',
            expired_notice: 'Dieser Vertrag ist abgelaufen',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Dokument Typ',
            expiry_date: 'Ablauf Datum',
            no_contracts: 'Keine Verträge gefunden.',
            all_document_types: 'Alle Dokument Typen',
            load_error: 'Fehler beim load Verträge',
            create_error: 'Fehler beim create Vertrag',
            delete_error: 'Fehler beim delete Vertrag',
            update_error: 'Fehler beim update Status',
            confirm_delete: 'Löschen Vertrag "{title}"?',
            renewal_date: 'Erneuerung Datum',
            effective_date: 'Gültig Datum',
            parties: 'Parteien',
            summary: 'Zusammenfassung',
            n_contracts: '{count} Verträge',
            n_contract: '{count} Vertrag',
            contracts_loaded: '{count} Verträge geladen',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Sicherheitsnachtrag',
            doc_type_scc_annex: 'SCC-Anhang',
            doc_type_sub_processor_list: 'Unterauftragnehmer-Liste',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Sonstiges',
            status_draft: 'Entwurf',
            status_under_negotiation: 'In Verhandlung',
            status_pending_signature: 'Unterschrift ausstehend',
            status_active: 'Aktiv',
            status_expired: 'Abgelaufen',
            status_terminated: 'Beendet',
            status_superseded: 'Ersetzt',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Genehmigungstore',
            page_title: 'Gate-Bewertungen',
            new: 'Neu Gate Evaluation',
            vendor_id: 'Anbieter ID',
            gate_name: 'Gate-Name',
            outcome: 'Ergebnis',
            evaluator: 'Bewerter',
            no_gates: 'Keine Gate Bewertungen gefunden.',
            load_error: 'Fehler beim load Gate Bewertungen',
            evaluate_error: 'Fehler beim evaluate Anbieter Gate',
            detail_error: 'Fehler beim fetch Bewertung Detail',
            override_error: 'Fehler beim override Gate Bewertung',
            criteria_results: 'Kriterien Ergebnisse',
            evaluate_form: 'Bewerten Sie das Lieferantentor',
            per_page: 'Bewertungen pro Seite',
            passed: 'Bestanden',
            failed: 'Fehlgeschlagen',
            override: 'Überschreiben',
            override_justification: 'Überschreiben Begründung',
            override_placeholder: 'Eingeben Begründung für overriding dieses Gate Bewertung',
            approval_id: 'Genehmigung ID',
            overall_passed: 'Bestanden',
            overall_failed: 'Nicht bestanden',
            overall_overridden: 'Überschrieben',
            evaluations_loaded: '{count} Gate-Bewertungen geladen',
            create_approval_cta: 'Genehmigung erstellen',
        },
        reviews: {
            title: 'Lieferantenbewertungen',
            page_title: 'Anbieterbewertungen',
            new: 'Neu Überprüfen',
            vendor_id: 'Anbieter ID',
            filter_by_outcome: 'Filtern Sie nach Bewertungsergebnis',
            per_page: 'Bewertungen pro Seite',
            kpi_summary: 'Überprüfen Sie die Zusammenfassung von KPI.',
            review_outcome_label: 'Überprüfen Sie das Ergebnis',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Überprüfungsergebnis bearbeiten',
            reviews_loaded: '{count} Anbieterbewertungen geladen',
            reviewer: 'Prüfer',
            review_type: 'Überprüfen Typ',
            outcome: 'Ergebnis',
            findings: 'Ergebnisse',
            no_reviews: 'Keine Anbieter Überprüfungen gefunden.',
            load_kpis_error: 'Fehler beim fetch Anbieter Überprüfung KPIs',
            load_error: 'Fehler beim fetch Anbieter Überprüfungen',
            update_error: 'Lieferantenbewertung konnte nicht aktualisiert werden',
            create_error: 'Fehler beim create Anbieter Überprüfung',
            total_reviewed: 'Gesamt Anbieter Überprüft',
            total_reviews: 'Gesamt Überprüfungen',
            satisfactory: 'Zufriedenstellend',
            concerns_identified: 'Bedenken Identified',
            remediation_required: 'Behebung Erforderlich',
            escalated: 'Eskaliert',
            program_id: 'Programm ID',
            period_id: 'Zeitraum ID',
            task_id: 'Aufgabe ID',
            recommendations: 'Empfehlungen',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Risikosignale',
            page_title: 'Risikosignale',
            new_signal: 'Neues Risikosignal',
            signal_type: 'Signaltyp',
            severity: 'Schweregrad',
            description: 'Beschreibung',
            source: 'Quelle',
            vendor_id: 'Anbieter-ID',
            certification_expiry: 'Zertifizierungsablauf',
            breach_notification: 'Verstoßmeldung',
            financial_instability: 'Finanzielle Instabilität',
            compliance_change: 'Compliance-Änderung',
            custom: 'Benutzerdefiniert',
            active: 'Aktiv',
            acknowledged: 'Bestätigt',
            resolved: 'Gelöst',
            no_signals: 'Keine Risikosignale gefunden',
            source_external_feed: 'Externer Feed',
            source_manual: 'Manuell',
            source_integration: 'Integration',
            source_regulatory: 'Regulatorisch',
            source_media: 'Medien',
            trigger_reassessment_prompt: 'Dies ist ein Signal mit hoher Schwere. Möchten Sie eine Neubewertung des Anbieters starten?',
            reassessment_triggered: 'Neubewertung gestartet.',
            reassessment_trigger_error: 'Neubewertung konnte nicht gestartet werden.',
        },
        reassessments: {
            title: 'Neubewertungsaufgaben',
            page_title: 'Neubewertungsaufgaben',
            trigger: 'Neubewertung auslösen',
            trigger_success: 'Neubewertung erfolgreich ausgelöst',
            reason: 'Grund',
            deadline: 'Frist',
            assigned_to: 'Zugewiesen an',
            vendor_id: 'Anbieter-ID',
            outcome: 'Ergebnis',
            new_risk_level: 'Neues Risikoniveau',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Neubewertung abschließen',
            complete_success: 'Neubewertung erfolgreich abgeschlossen',
            pending: 'Ausstehend',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            cancelled: 'Abgebrochen',
            no_tasks: 'Keine Neubewertungsaufgaben gefunden',
            risk_trend: 'Risikotrend',
            notes: 'Notizen',
        },
        portfolio: {
            title: 'Risikoportfolio',
            page_title: 'Risikoportfolio',
            dashboard: 'Portfolioübersicht',
            vendor_id: 'Anbieter-ID',
            total_vendors: 'Gesamtzahl Lieferanten',
            average_score: 'Durchschnittliche Risikobewertung',
            high_risk_count: 'Hochrisiko-Lieferanten',
            trend_direction: 'Trendrichtung',
            improved: 'Verbessert',
            stable: 'Stabil',
            degraded: 'Verschlechtert',
            vendor_trend: 'Lieferantentrend',
            outstanding_questionnaires: 'Ausstehende Fragebögen',
            overdue_assessments: 'Überfällige Bewertungen',
            no_outstanding: 'Keine ausstehenden Fragebögen',
            no_overdue: 'Keine überfälligen Bewertungen',
            due_date: 'Fälligkeitsdatum',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Risikoakzeptanzregister',
            page_title: 'Risikoakzeptanzregister',
            new_acceptance: 'Neue Risikoakzeptanz',
            vendor_id: 'Anbieter-ID',
            risk_level: 'Risikoniveau',
            conditions: 'Bedingungen',
            justification: 'Begründung',
            expiry_date: 'Ablaufdatum',
            create_success: 'Risikoakzeptanz erfolgreich erstellt',
            review_condition: 'Bedingung prüfen',
            condition_met: 'Bedingung erfüllt',
            condition_not_met: 'Bedingung nicht erfüllt',
            review_success: 'Bedingung erfolgreich geprüft',
            export_register: 'Register exportieren',
            active: 'Aktiv',
            expired: 'Abgelaufen',
            revoked: 'Widerrufen',
            no_acceptances: 'Keine Risikoakzeptanzen gefunden',
            met_count: '{met}/{total} erfüllt',
        },
        review_routing: {
            title: 'Prüfungsweiterleitung',
            page_title: 'Überprüfen Sie die Weiterleitung',
            route_to_reviewers: 'An Prüfer weiterleiten',
            route_success: 'Erfolgreich an Prüfer weitergeleitet',
            vendor_id: 'Anbieter-ID',
            reviewer_id: 'Prüfer-ID',
            reviewer_role: 'Prüferrolle',
            questionnaire_id: 'Fragebogen-ID',
            review_status: 'Prüfungsstatus',
            reviewers_label: 'Gutachter (user_id, Rolle pro Zeile)',
            pending: 'Ausstehend',
            in_review: 'In Prüfung',
            completed: 'Abgeschlossen',
            no_routing: 'Keine Prüfungsweiterleitung gefunden',
            primary_reviewer: 'Hauptprüfer',
            secondary_reviewer: 'Zweitprüfer',
            approver: 'Genehmiger',
        },
        onboarding: {
            title: 'Lieferanten-Onboarding-Fälle',
            page_title: 'Lieferanten-Onboarding-Fälle',
            new_case: 'Neuer Onboarding-Fall',
            case_detail: 'Onboarding-Fall',
            open_mps_dossier_filing: 'MPS-Einreichung öffnen',
            dossier_filing_description: 'Erstellt eine neue MPS-Einreichung, vorbefüllt aus diesem Onboarding-Fall. Die Einreichung wird im Fall verlinkt.',
            dossier_filing_dpia_id: 'DPIA-ID',
            dossier_filing_dpia_id_placeholder: 'z. B. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Titel der Einreichung',
            dossier_filing_form_type: 'Formulartyp',
            dossier_filing_submit: 'Einreichung erstellen',
            dossier_filing_validation_error: 'DPIA-ID und Titel sind erforderlich.',
            dossier_filing_created: 'MPS-Einreichung erstellt',
            dossier_filing_error: 'MPS-Einreichung konnte nicht erstellt werden.',
            linked_questionnaire_due_date: 'Fälligkeitsdatum',
            linked_questionnaire_assignee: 'Zugewiesen an',
            linked_questionnaire_submitted_at: 'Eingereicht am',
            score_last_scored: 'Zuletzt bewertet',
            score_dimensions: 'Dimensionen',
            linked_contract_document_type: 'Dokumenttyp',
            linked_contract_effective_date: 'Wirksamkeitsdatum',
            linked_contract_expiry_date: 'Ablaufdatum',
            linked_sub_processor_countries: 'Verarbeitungsländer',
            linked_sub_processor_last_assessment: 'Letzte Bewertung',
            linked_sub_processor_next_assessment: 'Nächste Bewertung',
            case_id: 'Fall-ID',
            vendor: 'Lieferant',
            case_type: 'Falltyp',
            service_description: 'Dienstleistungsbeschreibung',
            data_categories: 'Datenkategorien',
            systems_accessed: 'Zugegriffene Systeme',
            jurisdiction: 'Rechtsordnung',
            hosting_model: 'Hosting-Modell',
            controller_role: 'Verantwortlicher/Auftragsverarbeiter-Rolle',
            status_pipeline: 'Status-Pipeline',
            tabs_label: 'Onboarding-Fallabschnitte',
            per_page: 'Fälle pro Seite',
            filter_cases: 'Onboarding-Fälle filtern',
            cases_count: 'Fälle',
            cases_loaded: 'Onboarding-Fälle geladen',
            no_cases: 'Keine Onboarding-Fälle gefunden.',
            vendor_lookup_placeholder: 'Lieferanten-ID eingeben',
            data_categories_placeholder: 'z.B. personenbezogene Daten, Finanzen, Gesundheit',
            systems_placeholder: 'z.B. CRM, ERP, HR-System',
            status_open: 'Offen',
            status_in_progress: 'In Bearbeitung',
            status_awaiting_approval: 'Warten auf Genehmigung',
            status_approved: 'Genehmigt',
            status_remediation_required: 'Behebung erforderlich',
            status_closed: 'Geschlossen',
            type_new_vendor: 'Neuer Lieferant',
            type_service_change: 'Dienstleistungsänderung',
            type_renewal: 'Verlängerung',
            type_reassessment: 'Neubewertung',
            tab_overview: 'Übersicht',
            tab_due_diligence: 'Sorgfaltsprüfung',
            tab_security_assessment: 'Sicherheitsbewertung',
            tab_contractual_controls: 'Vertragliche Kontrollen',
            tab_sub_processors: 'Unterauftragsverarbeiter',
            tab_approval_gates: 'Genehmigungstore',
            tab_evidence: 'Nachweise',
            tab_placeholder: 'Der Inhalt dieses Abschnitts wird während des Onboarding-Prozesses ausgefüllt.',
            completion_not_started: 'Nicht begonnen',
            completion_in_progress: 'In Bearbeitung',
            completion_completed: 'Abgeschlossen',
            completion_requiring_attention: 'Aufmerksamkeit erforderlich',
            gate_name: 'Tor-Name',
            approver: 'Genehmiger',
            decision: 'Entscheidung',
            comments: 'Kommentare',
            decide: 'Entscheidung treffen',
            decision_approved: 'Genehmigt',
            decision_rejected: 'Abgelehnt',
            decision_deferred: 'Zurückgestellt',
            decision_pending: 'Pending',
            no_approvals: 'Keine Genehmigungstore für diesen Fall konfiguriert.',
            approval_success: 'Genehmigungsentscheidung erfolgreich erfasst',
            approval_error: 'Genehmigungsentscheidung konnte nicht erfasst werden',
            transition_status: 'Status wechseln',
            confirm_transition: 'Fall von "{from}" nach "{to}" wechseln?',
            transition_success: 'Fallstatus erfolgreich gewechselt',
            transition_error: 'Fallstatus konnte nicht gewechselt werden',
            create_success: 'Onboarding-Fall erfolgreich erstellt',
            create_error: 'Onboarding-Fall konnte nicht erstellt werden',
            load_error: 'Onboarding-Fälle konnten nicht geladen werden',
            detail_error: 'Falldetails konnten nicht geladen werden',
            export: 'Exportieren',
            export_error: 'Onboarding-Fall konnte nicht exportiert werden',
            evidence_tab_description: 'Nachweise für den Onboarding-Fall hochladen und exportieren.',
            upload_evidence_label: 'Nachweis hochladen',
            attached_evidence_heading: 'Angehängte Nachweise',
            no_evidence_attached: 'Noch keine Nachweise angehängt.',
            export_bundle_button: 'Nachweispaket exportieren',
            load_attachments_error: 'Nachweise konnten nicht geladen werden.',
            linked_questionnaires_heading: 'Verknüpfte Fragebögen',
            no_linked_questionnaires: 'Keine verknüpften Fragebögen.',
            linked_scores_heading: 'Verknüpfte Bewertungen',
            no_linked_scores: 'Keine verknüpften Bewertungen.',
            latest_score_label: 'Neueste Bewertung',
            linked_contracts_heading: 'Verknüpfte Verträge',
            no_linked_contracts: 'Keine verknüpften Verträge.',
            linked_sub_processors_heading: 'Verknüpfte Unterauftragsverarbeiter',
            no_linked_sub_processors: 'Keine verknüpften Unterauftragsverarbeiter.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Als erledigt markieren',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Register der Unterauftragsverarbeiter',
            page_title: 'Register der Unterauftragsverarbeiter',
            new_record: 'Neuer Unterauftragsverarbeiter',
            edit_record: 'Unterauftragsverarbeiter bearbeiten',
            onboarding_case: 'Onboarding-Fall',
            services: 'Erbrachte Dienstleistungen',
            data_categories: 'Datenkategorien',
            processing_countries: 'Verarbeitungsländer',
            dpa_chain_status: 'DPA-Kettenstatus',
            certifications: 'Zertifizierungen',
            last_assessment: 'Letzte Bewertung',
            next_assessment: 'Nächste Bewertung',
            change_history: 'Änderungshistorie',
            history: 'Verlauf',
            changed_by: 'Geändert von',
            comma_separated: 'Kommagetrennte Werte',
            filter_records: 'Einträge filtern',
            filter_by_dpa: 'Nach DPA-Kettenstatus filtern',
            all_dpa_statuses: 'Alle DPA-Status',
            per_page: 'Einträge pro Seite',
            records_count: 'Unterauftragsverarbeiter',
            records_loaded: 'Einträge geladen',
            no_records: 'Keine Unterauftragsverarbeiter gefunden.',
            no_history: 'Keine Änderungshistorie verfügbar.',
            dpa_active: 'Aktiv',
            dpa_pending: 'Ausstehend',
            dpa_expired: 'Abgelaufen',
            dpa_not_required: 'Nicht erforderlich',
            create_success: 'Unterauftragsverarbeiter erfolgreich erstellt',
            create_error: 'Konnte nicht erstellt werden',
            update_success: 'Unterauftragsverarbeiter erfolgreich aktualisiert',
            update_error: 'Konnte nicht aktualisiert werden',
            load_error: 'Register konnte nicht geladen werden',
            history_error: 'Änderungshistorie konnte nicht geladen werden',
            export_csv: 'CSV exportieren',
            export_json: 'JSON exportieren',
            export_error: 'Daten konnten nicht exportiert werden',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Orchestrierungsübersicht',
            runs: 'Orchestrierungsläufe',
            tasks: 'Ausführungsaufgaben',
            templates: 'Checklistenvorlagen',
            vendor_requests: 'Lieferantenanfragen',
            proof_chain: 'Nachweiskette',
            dsr_integration: 'DSR-Integration',
            vendor_verification: 'Lieferantenverifizierung',
            completeness: 'Vollständigkeitsübersicht',
        },
        dashboard: {
            title: 'Orchestrierungsübersicht',
            loading: 'Lade Dashboard Daten...',
            active_runs: 'Aktiv Runs',
            completion_rate: 'Fertigstellung Rate',
            avg_completion_time: 'Avg Fertigstellung Zeit',
            overdue_tasks: 'Überfällig Aufgaben',
            vendor_compliance: 'Anbieter Konformität',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Kürzliche Runs',
            no_recent_runs: 'Keine kürzlichen Orchestrierungsdurchläufe.',
            run_id: 'Ausführen ID',
            ticket_id: 'Ticket-ID',
            progress: 'Fortschritt',
            systems: 'Systeme',
            vendors: 'Anbieter',
            load_error: 'Das Orchestrierungs-Dashboard konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
            aria_table: 'Aktuelle Orchestrierungsläufe',
        },
        runs: {
            title: 'Orchestrierungsläufe',
            new: 'Neu Ausführen',
            run_id: 'Ausführen ID',
            ticket_id: 'Ticket-ID',
            progress: 'Fortschritt',
            systems: 'Systeme',
            vendors: 'Anbieter',
            data_subject_ref: 'Daten Betreff Ref',
            data_categories: 'Daten Kategorien',
            enable_proof_chain: 'Aktivieren Nachweis Kette',
            start: 'Starten',
            started: 'Lauf gestartet',
            mark_ready: 'Als „Bereit“ markieren',
            marked_ready: 'Lauf als bereit markiert',
            complete: 'Komplett',
            complete_exceptions: 'Vollständig mit Ausnahmen',
            pause: 'Pause',
            resume: 'Lebenslauf',
            cancel: 'Abbrechen',
            no_runs: 'Keine orchestration runs gefunden.',
            load_error: 'Orchestrierungsläufe konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
            create_error: 'Orchestrierungslauf konnte nicht erstellt werden',
            aria_filter_status: 'Filtern Sie nach Ausführungsstatus',
            aria_table: 'Orchestrierungsläufe',
            placeholder_ticket_id: 'z. B. TKT-000123',
            placeholder_data_subject_ref: 'z. B. DS-REF-001',
            placeholder_data_categories: 'z. B. personal_data, Finanzen, Gesundheit',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Ausführungsaufgaben',
            task_id: 'Aufgabe ID',
            run_id: 'Ausführen ID',
            system_name: 'Systemname',
            assigned_to: 'Zugewiesen Zu',
            sla_deadline: 'SLA Frist',
            progress: 'Fortschritt',
            enter_run_id: 'Eingeben Ausführen ID zu filter...',
            select_run: 'Wählen Sie einen Lauf aus, um Aufgaben anzuzeigen',
            unassigned: 'Nicht zugewiesen',
            overdue: 'Überfällig',
            assign_task: 'Zuweisen Aufgabe',
            assignee_id: 'Zugewiesener ID',
            update_status: 'Aktualisieren Status',
            new_status: 'Neu Status',
            no_transitions: 'Diese Aufgabe befindet sich in einem Endzustand und kann nicht geändert werden.',
            select_status_placeholder: 'Neuen Status auswählen',
            skip_justification: 'Begründung für Überspringen',
            failure_reason: 'Fehlergrund',
            no_tasks: 'Keine execution Aufgaben gefunden.',
            new_task: 'Neue Aufgabe',
            system_id: 'System-ID',
            data_classification: 'Datenklassifizierung',
            load_error: 'execution tasks konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
            assign_error: 'Fehler beim assign task',
            status_error: 'Fehler beim Aktualisieren des Aufgabenstatus',
            no_tasks_for_run: 'Dieser Löschvorgang hat noch keine Ausführungsaufgaben.',
            aria_filter_run_id: 'Nach Lauf-ID filtern',
            aria_filter_status: 'Filtern Sie nach Aufgabenstatus',
            aria_table: 'Ausführungsaufgaben',
            view_steps: 'Schritte anzeigen',
            steps_modal_title: 'Ausführungsschritte',
            step_title: 'Schritt',
            step_status: 'Schrittstatus',
            step_notes: 'Notizen',
            step_evidence: 'Nachweisdateien',
            step_evidence_required: 'Für diesen Schritt ist ein Nachweis erforderlich',
            step_evidence_missing: 'Noch keine Nachweise hochgeladen',
            step_save: 'Schritt speichern',
            step_saved: 'Schritt aktualisiert',
            step_save_error: 'Schritt konnte nicht aktualisiert werden',
            create_success: 'Ausführungsaufgabe erstellt',
            create_error: 'Ausführungsaufgabe konnte nicht erstellt werden',
            step_upload_label: 'Nachweis anhängen',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Checklistenvorlagen',
            new: 'Neu Vorlage',
            template_id: 'Vorlage ID',
            system_type: 'System Typ',
            est_duration: 'Est. Dauer',
            steps: 'Schritte',
            action_type: 'Aktion Typ',
            instructions: 'Anweisungen',
            mandatory: 'Obligatorisch',
            add_step: 'Hinzufügen Schritt',
            step_n: 'Schritt',
            no_templates: 'Keine checklist Vorlagen gefunden.',
            all_system_types: 'Alle System Typen',
            load_error: 'Checklistenvorlagen konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
            create_error: 'Checklistenvorlage konnte nicht erstellt werden',
            aria_filter_system_type: 'Filtern Sie nach Systemtyp',
            aria_filter_status: 'Filtern Sie nach Vorlagenstatus',
            aria_table: 'Checklisten-Vorlagen',
            placeholder_name: 'z. B. Checkliste zum Löschen der Datenbank',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Vorlage erfolgreich aktualisiert',
            create_success_msg: 'Vorlage erfolgreich erstellt',
            update_error: 'Fehler beim Aktualisieren der Vorlage',
            activate_success: 'Vorlage erfolgreich aktiviert',
            activate_error: 'Fehler beim Aktivieren der Vorlage',
            archive_success: 'Vorlage erfolgreich archiviert',
            archive_error: 'Fehler beim Archivieren der Vorlage',
            clone_success: 'Vorlage erfolgreich geklont',
            clone_error: 'Fehler beim Klonen der Vorlage',
        },
        vendor_requests: {
            title: 'Anbieter Deletion Anfragen',
            new: 'Neu Anfrage',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Anfrage ID',
            vendor_name: 'Anbieter Name',
            vendor_id: 'Anbieter ID',
            data_subject_ref: 'Daten Betreff Ref',
            data_categories: 'Daten Kategorien',
            legal_basis: 'Rechtlich Grundlage',
            sla_days: 'SLA Tage',
            requested_completion: 'Angefordert Fertigstellung',
            communications: 'Kommunikationen',
            total_requests: 'Gesamt Anfragen',
            no_requests: 'Keine Anbieter Löschung requests gefunden.',
            filter_vendor_id: 'Filtern nach Anbieter ID...',
            change_status: 'Status Ändern',
            new_status: 'Neuer Status',
            actions: 'Aktionen',
            load_error: 'Die Löschanfragen des Anbieters konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
            create_error: 'Die Anforderung zum Löschen des Anbieters konnte nicht erstellt werden',
            dashboard_error: 'Das Dashboard zum Löschen des Anbieters konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
            aria_filter_status: 'Filtern Sie nach Anforderungsstatus',
            aria_filter_vendor_id: 'Filtern Sie nach Anbieter-ID',
            aria_table: 'Löschungsanfragen des Anbieters',
            placeholder_vendor_id: 'z. B. VND-001',
            placeholder_vendor_name: 'z. B. Acme Analytics',
            placeholder_data_subject_ref: 'z. B. DS-REF-001',
            placeholder_data_categories: 'z. B. personal_data, Analytik',
            placeholder_legal_basis: 'z. B. PDPL Artikel 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Nachweis Kette Betrachter',
            enter_run_id: 'Eingeben Ausführen ID...',
            load_chain: 'Laden Kette',
            verify_chain: 'Verifizieren Kette',
            verifying: 'Verifiziere...',
            chain_valid: 'Kette is valid',
            chain_invalid: 'Kette is broken',
            chain_length: 'Kette Length',
            broken_at_index: 'Unterbrochen bei index',
            no_proof_records: 'Keine Nachweis Datensätze gefunden für dieses Durchlauf..',
            proof_id: 'Nachweis ID',
            entity_type: 'Entität Typ',
            entity_id: 'Entität ID',
            event_hash: 'Ereignis Hash',
            previous_hash: 'Vorherig Hash',
            attestation: 'Attestierung',
            attestation_actor: 'Durchgeführt von',
            attestation_signed_at: 'Signiert am',
            attestation_signature: 'Signatur',
            attestation_evidence_refs: 'Nachweisverweise',
            run_field_label: 'Löschvorgang',
            select_run: 'Löschvorgang auswählen…',
            aria_select_run: 'Löschvorgang für die Nachweiskettensuche',
            chain_broken_here: 'Kette broken here',
            load_error: 'Die Prüfkette konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
            verify_error: 'Die Beweiskette konnte nicht überprüft werden. Bitte versuchen Sie es erneut.',
            aria_run_id: 'Führen Sie die ID für die Proof-Chain-Suche aus',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Status ändern',
            request_type: 'Anfragetyp',
            data_subject_hint: 'Kennung der betroffenen Person, deren Daten gelöscht werden (z. B. E-Mail oder Kunden-ID). Entspricht in der Regel dem Antragsteller des ausgewählten DSR-Falls.',
            title: 'DSR-Löschungsintegration',
            create_run: 'DSR-Löschungslauf erstellen',
            dsr_case_id: 'DSR-Fall-ID',
            data_subject_id: 'Betroffenen-ID',
            systems: 'Zielsysteme',
            route_to_department: 'An Abteilung weiterleiten',
            confirm_deletion: 'Löschung bestätigen',
            no_runs: 'Keine DSR-Löschungsläufe gefunden',
            status: 'Löschungsstatus',
            deadline: 'Frist',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Lieferantenlöschungsverifizierung',
            create_followup: 'Nachverfolgung erstellen',
            deadline: 'Bestätigungsfrist',
            escalation_contact: 'Eskalationskontakt',
            escalate: 'Eskalieren',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Keine Lieferantennachverfolgungen gefunden',
            vendor_request: 'Lieferantenanfrage',
            vendor: 'Lieferant',
            unknown_vendor: 'Unbekannter Lieferant',
            overdue_badge: 'Überfällig',
            mark_confirmed: 'Als bestätigt markieren',
            mark_confirmed_for: 'Löschung als bestätigt markieren für {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Lieferantenlöschung bestätigt',
            confirmation_failed: 'Lieferantenlöschung konnte nicht bestätigt werden',
            select_request: 'Lieferantenanfrage auswählen…',
            escalation_contact_placeholder: 'z. B. dpo@vendor.example',
            create_success: 'Lieferantennachverfolgung erfolgreich erstellt',
            create_failed: 'Lieferantennachverfolgung konnte nicht erstellt werden',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Dienstleisterlöschung erfolgreich eskaliert',
            escalation_failed: 'Eskalation der Dienstleisterlöschung fehlgeschlagen',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Löschungsvollständigkeit',
            check_conflicts: 'Aufbewahrungskonflikte prüfen',
            no_conflicts: 'Keine Aufbewahrungskonflikte gefunden',
            conflict_resolution: 'Konfliktlösung',
            extend_retention: 'Aufbewahrung verlängern',
            proceed_deletion: 'Mit Löschung fortfahren',
            escalate_conflict: 'Eskalieren',
            resolve: 'Konflikt lösen',
            data_subject_id: 'ID der betroffenen Person',
            placeholder_data_subject_id: 'Geben Sie die ID der betroffenen Person ein',
            data_subject_hint: 'Kennung der Person, deren Löschung Sie überprüfen (z. B. E-Mail, Kunden-ID oder DSR-Referenz). Wählen Sie eine bekannte Person oder geben Sie eine ein.',
            guidance: 'Geben Sie eine Datensubjekt-ID ein oder wählen Sie eine aus, wählen Sie optional Systeme und führen Sie die Prüfung aus, um Löschvollständigkeit und Aufbewahrungskonflikte anzuzeigen.',
            completion: 'Löschabschluss',
            pending_items: 'Ausstehende Elemente',
            system: 'System',
            policy: 'Richtlinie',
            retention_end: 'Aufbewahrungsende',
            completed_items: 'Abgeschlossene Elemente',
            last_review_label: 'Letzte Rezension',
            days_ago: 'vor Tagen',
            review_overdue: 'Überprüfungszyklus überschritten. Eine erneute Überprüfung ist erforderlich.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Ausstehend',
            planning: 'Planung',
            ready: 'Bereit',
            in_progress: 'In Bearbeitung',
            paused: 'Pausiert',
            completed: 'Abgeschlossen',
            completed_with_exceptions: 'Abgeschlossen mit Ausnahmen',
            cancelled: 'Abgebrochen',
        },
        task_status_labels: {
            pending: 'Ausstehend',
            assigned: 'Zugewiesen',
            in_progress: 'In Bearbeitung',
            awaiting_verification: 'Warte auf Verifizierung',
            verified: 'Verifiziert',
            failed: 'Fehlgeschlagen',
            skipped: 'Übersprungen',
        },
        system_type_labels: {
            database: 'Datenbank',
            saas_application: 'SaaS-Anwendung',
            file_storage: 'Dateispeicher',
            email_system: 'E-Mail-System',
            analytics_platform: 'Analyseplattform',
            backup_system: 'Backup-System',
            vendor_managed: 'Anbieterverwaltet',
            custom: 'Benutzerdefiniert',
        },
        template_status_labels: {
            draft: 'Entwurf',
            active: 'Aktiv',
        },
        action_type_labels: {
            manual_check: 'Manuelle Prüfung',
            api_call: 'API-Aufruf',
            sql_query: 'SQL-Abfrage',
            script_execution: 'Skriptausführung',
            file_deletion: 'Dateilöschung',
            verification: 'Verifizierung',
            notification: 'Benachrichtigung',
            approval: 'Genehmigung',
        },
        request_status_labels: {
            draft: 'Entwurf',
            sent: 'Gesendet',
            acknowledged: 'Bestätigt',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            overdue: 'Überfällig',
            escalated: 'Eskaliert',
            disputed: 'Bestritten',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Initiierung',
            data_deletion: 'Datenlöschung',
            completion: 'Abschluss',
            verification: 'Verifizierung',
            rollback: 'Rückgängig',
        },
        count_labels: {
            one_template: '1 Vorlage',
            n_templates: '{n} Vorlagen',
            one_run: '1 Durchlauf',
            n_runs: '{n} Durchläufe',
            one_task: '1 Aufgabe',
            n_tasks: '{n} Aufgaben',
            one_request: '1 Anfrage',
            n_requests: '{n} Anfragen',
        },
        aria: {
            completion_rate: 'Abschlussrate: {pct}%',
            vendor_compliance: 'Anbieter-Compliance: {pct}%',
            run_progress: 'Durchlauffortschritt: {pct}%',
            run_progress_systems: 'Fortschritt: {completed} von {total} Systemen verarbeitet',
            chain_broken: 'Kette an diesem Eintrag unterbrochen',
            chain_valid: 'Kette an diesem Eintrag gültig',
            chain_unknown: 'Kettenstatus unbekannt',
            escalate_vendor: 'Eskalieren {id}',
            resolve_conflict: 'Konflikt für {system} lösen',
        },
        regulatory: {
            annual_review_cycle_info: 'Die Vollständigkeit der Löschung muss mindestens alle 365 Tage überprüft werden. Datensätze, die diesen Schwellenwert überschreiten, werden zur obligatorischen erneuten Überprüfung markiert, um die Einhaltung gesetzlicher Vorschriften sicherzustellen.',
            runs_data_d1_lifecycle_info: 'Orchestrierungslaufzustände werden dem DATA-D1-Lebenszyklus zugeordnet: Planung = Katalog, bereit = klassifizieren, in_progress = Regeln anwenden, abgeschlossen = Beweise generiert, completed_with_exceptions = Überprüfung erforderlich. Jeder Übergang wird in der Beweiskette aufgezeichnet.',
            vendor_record_linkage_info: 'Jeder Löschantrag eines Lieferanten ist über vendor_id mit seinem Lieferantendatensatz verknüpft. Für die Abschlussbestätigung sind kryptografische Beweise aus der Beweiskette erforderlich, bevor die Anfrage als abgeschlossen markiert werden kann.',
            templates_internal_classification_info: 'Alle Lösch-Checklisten-Vorlagen werden als interne Organisationsdokumente eingestuft. Sie tragen keine offiziellen Zulassungsformular-IDs und sollten nicht als Zulassungsanträge eingereicht werden.',
            proof_chain_evidence_info: 'Jeder Beweisdatensatz erfasst die betroffene Person/Kategorie, den Zeitstempel, den Akteur, die Rechtsgrundlage und einen SHA-256-Hash. Zusammen ergeben diese Felder einen kryptografischen Löschnachweis, der die regulatorischen Prüfanforderungen erfüllt.',
            retention_data_law_info: 'Aufbewahrungspläne in diesem Dashboard werden aus der Datengesetzklassifizierung abgeleitet, die jeder Datenkategorie zugewiesen ist. Wenn sich eine Klassifizierung ändert, werden die verknüpften Aufbewahrungsregeln automatisch aktualisiert, um die Übereinstimmung mit den gesetzlichen Bestimmungen sicherzustellen.',
            dsr_erasure_route_info: 'DSR-Löschläufe sind mit dem ursprünglichen Rights Request-Fall verknüpft. Die regulatorische Frist (in der Regel 30 Tage gemäß GDPR Art. 17) wird automatisch verfolgt. Überfällige Fälle werden zur Eskalation markiert.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Erkennungsübersicht',
            scans: 'Erkennungsscans',
            staging_area: 'Bereitstellungsbereich',
            dedup_rules: 'Deduplizierungsregeln',
            classification_rules: 'Klassifizierungsregeln',
            suggestions: 'Vorschlagswarteschlange',
            analytics: 'Vorschlagsanalyse',
            import_templates: 'Importvorlagen',
            connectors: 'Konnektorverwaltung',
            discovery_cycles: 'Erkennungszyklen',
            owner_review: 'Eigentümerprüfung',
            classification_lineage: 'Klassifizierungshistorie',
        },
        dashboard: {
            title: 'Erkennungsübersicht',
            loading: 'Lade Dashboard Daten...',
            total_scans: 'Gesamt Scans',
            active_scans: 'Aktiv Scans',
            completed_30d: 'Abgeschlossen (30d)',
            failed_30d: 'Fehlgeschlagen (30T)',
            systems_discovered: 'Systeme Entdeckt',
            pending_staged_records: 'Ausstehend Staged Datensätze',
            recent_scans: 'Kürzliche Scans',
            no_recent_scans: 'No recent Scans.',
            scan_id: 'Scannen ID',
            discovered: 'Entdeckt',
            view_all_scans: 'Ansehen Alle Scans',
            staging_area: 'Staging-Bereich',
            suggestion_queue: 'Vorschlag Warteschlange',
            records_reviewed: 'Überprüfte Datensätze',
            records_approved: 'Genehmigte Datensätze',
            exceptions_pending: 'Ausstehende Ausnahmen',
            view_as_chart: 'Als Diagramm anzeigen',
            view_as_table: 'Als Tabelle anzeigen',
        },
        scans: {
            title: 'Erkennungsscans',
            new: 'Neu Scannen',
            scan_id: 'Scannen ID',
            scan_type: 'Scannen Typ',
            progress: 'Fortschritt',
            discovered: 'Entdeckt',
            no_scans: 'Keine discovery Scans gefunden.',
            all_scan_types: 'Alle Scannen Typen',
            source_config: 'Quelle Konfiguration (JSON)',
            invalid_json: 'Ungültig JSON in source Konfiguration',
            load_error: 'Fehler beim load discovery Scans. Bitte versuchen Sie es erneut.',
            create_error: 'Fehler beim create discovery Scan',
            change_status: 'Status ändern',
            new_status: 'Neuer Status',
            actions: 'Aktionen',
            type_endpoint: 'Endpunkt',
            type_spreadsheet_import: 'Tabellenimport',
            type_cmdb_sync: 'CMDB-Synchronisierung',
            type_cloud_sync: 'Cloud-Synchronisierung',
            status_pending: 'Ausstehend',
            status_running: 'Wird ausgeführt',
            status_completed: 'Abgeschlossen',
            status_failed: 'Fehlgeschlagen',
            status_cancelled: 'Abgebrochen',
            source_config_placeholder: '{"host": "...", "port": 443}',
            adapter_not_implemented_toast: 'Dieser Scan-Typ ist noch nicht verfügbar. Es können nur Endpunkt-Scans erstellt werden.',
            adapter_not_implemented_warning: 'Noch nicht verfügbar. Scans dieses Typs können erst erstellt werden, wenn die Unterstützung dafür freigegeben ist.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Bereitstellungsbereich',
            record_id: 'Datensatz ID',
            source_scan: 'Quelle Scannen',
            entity_type: 'Entität Typ',
            suggested_name: 'Vorgeschlagen Name',
            confidence: 'Konfidenz',
            no_records: 'Keine staged Datensätze gefunden.',
            promote: 'Fördern',
            discard: 'Verwerfen',
            review: 'Überprüfen',
            merge: 'Zusammenführen',
            merge_title: 'Datensatz zusammenführen',
            merge_target_id: 'Zusammenführen Ziel ID',
            reject_reason_placeholder: 'Grund für rejection...',
            promotion_status: 'Förderung Status',
            dedup_status: 'Deduplizierungsstatus',
            all_source_types: 'Alle Quelle Typen',
            all_promotion_statuses: 'Alle Förderung Statuses',
            all_dedup_statuses: 'Alle Dedup Statuses',
            load_error: 'Fehler beim load staged Datensätze. Bitte versuchen Sie es erneut.',
            promote_error: 'Fehler beim promote Datensatz',
            reject_error: 'Fehler beim reject Datensatz',
            merge_error: 'Fehler beim merge Datensatz',
            review_error: 'Fehler beim mark Datensatz für Überprüfung',
            bulk_promote_error: 'Fehler beim bulk promote Datensätze',
            bulk_reject_error: 'Fehler beim bulk reject Datensätze',
            service_type_web_server: 'Webserver',
            service_type_database: 'Datenbank',
            service_type_api_gateway: 'API-Gateway',
            service_type_storage: 'Speicher',
            service_type_authentication: 'Authentifizierung',
            service_type_analytics: 'Analytik',
            service_type_unknown: 'Unbekannt',
            source_type_endpoint_scan: 'Endpunktscan',
            source_type_spreadsheet: 'Tabelle',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Cloud-Inventar',
            promotion_status_new: 'Neu',
            promotion_status_under_review: 'In Überprüfung',
            promotion_status_promoted: 'Gefördert',
            promotion_status_rejected: 'Abgelehnt',
            promotion_status_merged: 'Zusammengeführt',
            dedup_status_unmatched: 'Keine Übereinstimmung',
            dedup_status_potential_match: 'Mögliche Übereinstimmung',
            dedup_status_exact_match: 'Exakte Übereinstimmung',
            dedup_status_resolved: 'Gelöst',
            confirm_bulk_reject: 'Sind Sie sicher, dass Sie die ausgewählten Datensätze ablehnen möchten?',
            scan_batch_id: 'Batch-ID',
            promote_to_registry_title: 'Zur Datenregistrierung hochstufen',
            promote_modal_instructions: 'Erstellt ein Datenregistrierungselement mit collection_method: beobachtet und einer Herkunftsverknüpfung mit diesem Entdeckungsdatensatz. Das Element befindet sich zunächst im Entwurfsstatus zur Überprüfung.',
            promote_label_category: 'Kategorie',
            promote_label_name_override: 'Name (optionale Überschreibung)',
            placeholder_name_override: 'Standardmäßig wird der Name/Hostname des Erkennungseintrags verwendet',
            promote_label_sensitivity: 'Vorgeschlagene Empfindlichkeit',
            inherit_from_category: 'Von der Kategorie erben',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Deduplizierungsregeln',
            new: 'Neu Regel',
            rule_name: 'Regel Name',
            match_fields: 'Abgleichen Felder',
            threshold: 'Schwellenwert',
            no_rules: 'Keine Deduplizierung Regeln gefunden.',
            seed_defaults: 'Initialisieren Standardwerte',
            match_type: 'Abgleichen Typ',
            similarity_threshold: 'Ähnlichkeit Schwellenwert',
            field_name: 'Feld Name',
            load_error: 'Fehler beim load Deduplizierung Regeln. Bitte versuchen Sie es erneut.',
            create_error: 'Fehler beim create Deduplizierung Regel',
            toggle_error: 'Fehler beim toggle Deduplizierung Regel',
            seed_error: 'Fehler beim seed Standardwerte',
            priority: 'Priorität',
            enabled_status: 'Aktiviert',
            disabled_status: 'Deaktiviert',
            default_badge: 'Standard',
            enable: 'Aktivieren',
            disable: 'Deaktivieren',
            match_type_exact: 'Exakt',
            match_type_fuzzy: 'Unscharf',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP-Bereich',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Klassifizierungsregeln',
            new: 'Neu Regel',
            rule_name: 'Regel Name',
            pattern: 'Muster',
            classification: 'Klassifizierung',
            no_rules: 'Keine classification Regeln gefunden.',
            seed_defaults: 'Initialisieren Standardwerte',
            run_rules_now: 'Regeln jetzt ausführen',
            run_rules_success: '{count} neue Klassifizierungsvorschläge generiert',
            run_rules_no_results: 'Es wurden keine neuen Vorschläge generiert – alle übereinstimmenden Regeln verfügen bereits über ausstehende Vorschläge',
            run_rules_error: 'Die Klassifizierungsregeln konnten nicht ausgeführt werden. Bitte versuchen Sie es erneut.',
            taxonomy_id: 'Taxonomie ID',
            level_id: 'Stufe ID',
            condition_logic: 'Bedingung Logic',
            weight: 'Gewicht',
            conditions: 'Bedingungen',
            condition_field: 'Feld',
            condition_operator: 'Betreiber',
            condition_value: 'Wert',
            effectiveness: 'Wirksamkeit',
            match_count: 'Übereinstimmungen',
            approval_count: 'Genehmigungen',
            rejection_count: 'Ablehnungen',
            filter_taxonomy: 'Filtern nach Taxonomie ID...',
            load_error: 'Fehler beim load classification Regeln. Bitte versuchen Sie es erneut.',
            create_error: 'Fehler beim create classification Regel',
            seed_error: 'Fehler beim seed Standardwerte',
            toggle_error: 'Fehler beim toggle Regel. Bitte versuchen Sie es erneut.',
            priority: 'Priorität',
            enabled_status: 'Aktiviert',
            disabled_status: 'Deaktiviert',
            enable: 'Aktivieren',
            disable: 'Deaktivieren',
            logic_and: 'UND',
            logic_or: 'ODER',
            operator_equals: 'Gleich',
            operator_contains: 'Enthält',
            operator_matches_regex: 'Entspricht Regex',
            operator_in_list: 'In Liste',
            operator_not_equals: 'Nicht gleich',
            operator_not_contains: 'Enthält nicht',
            enabled_label: 'Aktiviert',
            disabled_label: 'Deaktiviert',
            taxonomy_label: 'Taxonomie',
            operator_label: 'Betreiber',
            value_label: 'Wert',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Vorschlagswarteschlange',
            entity_type: 'Entität Typ',
            suggested_name: 'Vorgeschlagen Name',
            source: 'Quelle',
            confidence: 'Konfidenz',
            accept: 'Akzeptieren',
            reject: 'Ablehnen',
            no_suggestions: 'No Vorschläge in queue.',
            modify_approve: 'Ändern & Genehmigen',
            delegate: 'Delegierter',
            delegate_user_id: 'Delegierter Benutzer ID',
            new_level_id: 'Neu Stufe ID',
            reject_reason_placeholder: 'Grund für rejection...',
            current_level: 'Aktuell Stufe',
            suggested_level: 'Vorgeschlagen Stufe',
            filter_taxonomy: 'Filtern nach Taxonomie ID...',
            all_confidence: 'Alle Konfidenz Bands',
            load_error: 'Fehler beim load Vorschläge. Bitte versuchen Sie es erneut.',
            approve_error: 'Fehler beim approve Vorschlag',
            reject_error: 'Fehler beim reject Vorschlag',
            modify_error: 'Fehler beim modify & approve Vorschlag',
            delegate_error: 'Fehler beim delegate Vorschlag',
            bulk_approve_error: 'Fehler beim bulk approve Vorschläge',
            bulk_reject_error: 'Fehler beim bulk reject Vorschläge',
            suggestion_type_new_label: 'Neues Label',
            suggestion_type_validation: 'Validierung',
            suggestion_type_reclassification: 'Neuklassifizierung',
            status_pending: 'Ausstehend',
            status_approved: 'Genehmigt',
            status_rejected: 'Abgelehnt',
            status_superseded: 'Ersetzt',
            confidence_high: 'Hoch',
            confidence_medium: 'Mittel',
            confidence_low: 'Niedrig',
            confirm_bulk_reject: 'Sind Sie sicher, dass Sie die ausgewählten Vorschläge ablehnen möchten?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Alle Taxonomien',
        },
        analytics: {
            title: 'Vorschlagsanalyse',
            total_suggestions: 'Gesamt Vorschläge',
            accepted: 'Akzeptiert',
            rejected: 'Abgelehnt',
            pending: 'Ausstehend',
            acceptance_rate: 'Akzeptanzrate',
            by_entity_type: 'By Entität Typ',
            by_source: 'By Quelle',
            avg_decision_time: 'Avg Entscheidung Zeit',
            rule_effectiveness: 'Regel Wirksamkeit',
            by_status: 'Nach Status',
            by_confidence: 'By Konfidenz Band',
            load_error: 'Fehler beim load Vorschlag analytics. Bitte versuchen Sie es erneut.',
            confidence_high: 'Hoch',
            confidence_medium: 'Mittel',
            confidence_low: 'Niedrig',
            by_confidence_band: 'Nach Konfidenzstufe',
            approval_rate: 'Genehmigungsrate',
            by_label_state: 'Nach Label-Status',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Importvorlagen',
            new: 'Neu Vorlage',
            template_name: 'Vorlage Name',
            file_format: 'Datei Format',
            column_mappings: 'Spalte Zuordnungen',
            no_templates: 'Keine Import Vorlagen gefunden.',
            source_column: 'Quelle Spalte',
            target_field: 'Ziel Feld',
            transform: 'Transformieren',
            source_column_label: 'Quellspalte',
            target_field_label: 'Zielfeld',
            transform_label: 'Verwandeln',
            confirm_delete: 'Are you sure you want zu delete dieses Import Vorlage?',
            load_error: 'Fehler beim load Import Vorlagen. Bitte versuchen Sie es erneut.',
            save_error: 'Fehler beim save Import Vorlage',
            delete_error: 'Fehler beim delete Import Vorlage',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Konnektorverwaltung',
            new: 'Neu Konnektor',
            connector_name: 'Konnektor Name',
            connector_type: 'Typ',
            last_sync: 'Letzter Synchronisierung',
            no_connectors: 'Keine connectors gefunden.',
            cmdb_syncs: 'CMDB Synchronisierungen',
            cloud_syncs: 'Cloud Synchronisierungen',
            new_sync: 'Neu Synchronisierung',
            no_syncs: 'Keine syncs gefunden.',
            cmdb_error: 'Fehler beim load CMDB Scans. Bitte versuchen Sie es erneut.',
            cloud_error: 'Fehler beim load cloud Scans. Bitte versuchen Sie es erneut.',
            trigger_error: 'Fehler beim trigger Synchronisierung',
            trigger_sync_error: 'Fehler beim trigger {type}',
            cmdb_section_title: 'CMDB-Synchronisierungen',
            cloud_section_title: 'Cloud-Synchronisierungen',
            column_connector: 'Konnektor',
            action_start: 'Starten',
            confirm_cancel_sync: 'Diese Synchronisierung abbrechen?',
            start_error: 'Synchronisierung konnte nicht gestartet werden',
            cancel_error: 'Synchronisierung konnte nicht abgebrochen werden',
            type_endpoint: 'Endpunkt-Scan',
            type_spreadsheet_import: 'Tabellenimport',
            type_cmdb_sync: 'CMDB-Synchronisierung',
            type_cloud_sync: 'Cloud-Synchronisierung',
        },
        discovery_cycles: {
            title: 'Erkennungszyklen',
            new_cycle: 'Neuer Erkennungszyklus',
            cycle_name: 'Zyklusname',
            scope_type: 'Umfangstyp',
            scope_ids: 'Umfangs-IDs',
            cadence_days: 'Frequenz (Tage)',
            status: 'Status',
            no_cycles: 'Keine Erkennungszyklen gefunden',
            compare: 'Zyklen vergleichen',
            delta_report: 'Änderungsbericht',
            new_records: 'Neue Datensätze',
            changed_records: 'Geänderte Datensätze',
            removed_records: 'Entfernte Datensätze',
            schedule: 'Scan planen',
            scope_organization: 'Organisation',
            scope_department: 'Abteilung',
            scope_system: 'System',
            scope_systems: 'Systeme',
            bulk_actions: 'Massenaktionen',
            selected: '{count} ausgewählt',
            bulk_create: 'Massenerstellung',
            bulk_assign_scope: 'Massenbereich zuweisen',
            bulk_advance: 'Massenstatus voranschreiten',
            confirm_bulk_action: 'Massenaktion bestätigen',
            bulk_confirm_message: 'Sind Sie sicher, dass Sie diese Aktion ausführen möchten?',
            cycles_affected: '{count} Zyklen werden betroffen sein',
            select_all: 'Alle auswählen',
            select_cycle: 'Zyklus {name} auswählen',
            from_template: 'aus Vorlage',
            bulk_results: 'Massenergebnisse',
            failed_label: 'fehlgeschlagen',
            template_id: 'Vorlagen-ID',
            count: 'Anzahl',
            advance_to: 'Zielstatus',
            bulk_success: 'Massenvorgang abgeschlossen',
        },
        owner_review: {
            title: 'Eigentümerprüfungsaufgaben',
            assign_owner: 'Eigentümer zuweisen',
            owner_id: 'Eigentümer',
            decision: 'Entscheidung',
            approve: 'Genehmigen',
            modify: 'Ändern',
            reject: 'Ablehnen',
            escalate: 'Eskalieren',
            no_tasks: 'Keine Prüfungsaufgaben gefunden',
            bulk_resolve: 'Massenauflösung',
            exceptions: 'Ausnahmeposten',
            no_exceptions: 'Keine Ausnahmeposten gefunden',
            record_id_label: 'Datensatz-ID',
            selected: 'ausgewählt',
            notes: 'Notizen',
            select_all: 'Alle auswählen',
            select: 'Auswählen',
            confirm_resolve: 'Bestätigen',
        },
        classification_lineage: {
            title: 'Klassifizierungshistorie',
            link_suggestion: 'Mit vorgeschlagenem Label verknüpfen',
            suggestion_id: 'Vorschlag',
            proposed_label_id: 'Vorgeschlagenes Label',
            no_lineage: 'Keine Historiedaten gefunden',
            full_lineage: 'Vollständige Historie',
            record_id_label: 'Datensatz-ID',
            staged_record_label: 'Inszenierte Aufzeichnung',
            enter_staged_record_id: 'Geben Sie die ID des bereitgestellten Datensatzes ein',
            scan_id_label: 'Scan-ID',
            select_record: 'Datensatz auswählen',
            select_suggestion: 'Vorschlag auswählen',
            select_label: 'Bezeichnung auswählen',
        },
        common_labels: {
            n_rules: '{n} Regeln',
            one_rule: '1 Regel',
            n_scans: '{n} scannt',
            one_scan: '1 Scan',
            n_syncs: '{n} wird synchronisiert',
            one_sync: '1 Synchronisierung',
            n_records: '{n} Datensätze',
            one_record: '1 Datensatz',
            n_templates: '{n} Vorlagen',
            one_template: '1 Vorlage',
            n_suggestions: '{n} Vorschläge',
            one_suggestion: '1 Vorschlag',
            n_selected: '{n} ausgewählt',
            dashboard_loaded: 'Discovery-Dashboard geladen',
            n_scans_loaded: '{n} Scans geladen',
            n_staged_records_loaded: '{n} bereitgestellte Datensätze geladen',
            confirm_delete_import_template: 'Sind Sie sicher, dass Sie diese Importvorlage löschen möchten?',
            invalid_json_source_config: 'Ungültig JSON in source_config',
            scope_organization: 'Organisation',
            scope_department: 'Abteilung',
            scope_system: 'System',
            aria_filter_scans: 'Scans filtern',
            aria_filter_staged_records: 'Filtern Sie bereitgestellte Datensätze',
            aria_filter_suggestions: 'Filter',
            aria_filter_by_taxonomy_id: 'Nach Taxonomie-ID filtern',
            aria_filter_by_enabled_status: 'Filtern Sie nach aktiviertem Status',
            aria_filter_by_source_type: 'Filtern Sie nach Quelltyp',
            aria_filter_by_promotion_status: 'Filtern Sie nach Aktionsstatus',
            aria_filter_by_dedup_status: 'Filtern Sie nach Deduplizierungsstatus',
            aria_filter_by_scan_type: 'Filtern Sie nach Scantyp',
            aria_filter_by_status: 'Nach Status filtern',
            aria_filter_by_confidence: 'Nach Konfidenzband filtern',
            aria_select_all_records: 'Wählen Sie alle Datensätze aus',
            aria_select_record: 'Datensatz auswählen {id}',
            aria_select_all_suggestions: 'Wählen Sie alle Vorschläge aus',
            aria_select_suggestion: 'Vorschlag auswählen {id}',
            aria_promote_record: 'Datensatz hochstufen {id}',
            aria_discard_record: 'Datensatz verwerfen {id}',
            aria_merge_record: 'Datensatz zusammenführen {id}',
            aria_review_record: 'Überprüfungsdatensatz {id}',
            aria_accept_suggestion: 'Vorschlag annehmen {id}',
            aria_reject_record: 'Datensatz ablehnen',
            aria_merge_record_dialog: 'Datensatz zusammenführen',
            aria_create_new_scan: 'Neuen Scan erstellen',
            aria_sync_progress: 'Synchronisierungsfortschritt: {processed} von {total} Elementen verarbeitet',
            aria_scan_progress: 'Scanfortschritt: {processed} von {total} Elementen verarbeitet',
            aria_confidence: 'Vertrauen: {pct}%',
            aria_effectiveness: 'Wirksamkeit: {pct}',
            aria_promote_n_records: '{n} ausgewählte Datensätze hervorheben',
            aria_discard_n_records: '{n} ausgewählte Datensätze verwerfen',
            placeholder_pii_detection_rule: 'z. B. PII-Erkennungsregel',
            placeholder_tax_id: 'z. B. TAX-001',
            placeholder_lvl_id: 'z. B. LVL-001',
            placeholder_service_type: 'z. B. service_type',
            placeholder_database: 'z.B. Datenbank',
            placeholder_hostname_match: 'z. B. Hostname Exact Match',
            placeholder_hostname: 'z. B. Hostname',
            placeholder_hr_systems_csv: 'z. B. HR-Systeme CSV Importieren',
            placeholder_server_name: 'z. B. Servername',
            placeholder_hostname_field: 'z. B. Hostname',
            placeholder_lowercase: 'z. B. Kleinbuchstaben',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 'z. B. STG-000123',
            placeholder_lvl_002: 'z. B. LVL-002',
            placeholder_usr_id: 'z. B. USR-000123',
            decision_for: 'Entscheidung für {id}',
        },
        exports: {
            title: 'Exportieren',
            inventory: 'Dateninventar',
            classification_register: 'Klassifikationsregister',
            raci: 'RACI-Matrix',
            exceptions: 'Ausnahmebericht',
            evidence_pack: 'Nachweispaket',
            delta_report: 'Änderungsbericht',
            executive_summary: 'Zusammenfassung',
            export_button: 'Exportieren',
            downloading: 'Wird heruntergeladen...',
            download_complete: 'Download abgeschlossen.',
            download_error: 'Export-Download fehlgeschlagen. Bitte erneut versuchen.',
        },
        flags: {
            title: 'Nachgelagerte Markierungen',
            pending: 'Ausstehende Markierungen',
            cross_border_review_needed: 'Grenzüberschreitende Prüfung erforderlich',
            sharing_disclosure_needed: 'Weitergabe-Offenlegung erforderlich',
            dpia_review_needed: 'DSFA-Prüfung erforderlich',
            acknowledge: 'Bestätigen',
            resolve: 'Lösen',
            no_flags: 'Keine nachgelagerten Markierungen.',
        },
        delta: {
            title: 'Änderungsübersicht',
            new_count: '{n} neue',
            removed_count: '{n} entfernte',
            changed_count: '{n} geänderte',
            re_review_triggered: 'Erneute Prüfung ausgelöst',
            create_re_review: 'Erneute Prüfung erstellen',
            recent_alerts: 'Aktuelle Änderungswarnungen',
            no_alerts: 'Keine aktuellen Änderungswarnungen.',
            threshold_exceeded: 'Änderungsschwellenwert überschritten (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'Dieses Dashboard zeigt den Governance-Status auf Zyklusebene an: gescannte Systeme, bereitgestellte Datensätze, überprüfte Datensätze, genehmigte Datensätze und ausstehende Ausnahmen. Dies entspricht den Governance-Lead-Erfolgskriterien: „Live-Dashboard darüber, wer sein Inventar, seine Klassifizierung und seine Freigabeprotokolle aktualisiert hat“ (P-VN-05).',
            export_artifacts_info: 'Discovery-Exporte umfassen alle erforderlichen Artefakttypen gemäß UC-VN-10 und DATA-EVD-01: genehmigtes Dateninventar, Klassifizierungsregister, Eigentums-/RACI-Zuordnung, Ausnahmewarteschlange, Executive Summary, Beweispaket und Deltabericht.',
            scan_inventory_pipeline_info: 'Jeder abgeschlossene Scan erzeugt Datensätze, die mit der Vorlage vn_data_law_data_inventory_register kompatibel sind (Anforderung 88dd9877). Entdeckte Datenbestände werden automatisch in das Datenbestandsregister eingetragen, um der datenrechtlichen Verpflichtung zur Führung eines digitalen Datenbestands nachzukommen.',
            connector_linkage_info: 'Jede Connector-Konfiguration muss Folgendes umfassen: Systemtyp (z. B. CMDB, Cloud-Plattform), Datenspeicherreferenz (Datenbank, Dateispeicher, SaaS) und Geschäftsprozessverknüpfung (UC-VN-10 erforderliche unterstützende Datensätze). Diese Felder stellen sicher, dass entdeckte Assets auf ihr Quellsystem und ihren Geschäftskontext zurückverfolgt werden können.',
            owner_routing_info: 'Überprüfungsaufgaben werden basierend auf dem System-/Dateneigentum an die richtige Abteilung oder den richtigen Anwendungseigentümer weitergeleitet (UC-VN-10 Schritt 5). Beim Routing wird das Systemeigentumsregister verwendet, keine Ad-hoc-Zuweisung.',
            owner_actions_audit_info: 'Eigentümer können jede Klassifizierung genehmigen, ändern, ablehnen oder eskalieren (UC-VN-10 Schritt 6). Bei jeder Aktion werden Akteur, Zeitstempel und Grund erfasst, wodurch Governance-Audit-Nachweisereignisse gemäß der Anforderung f12fc090 (jährliche Governance-Bescheinigung) erstellt werden. Der Prüfpfad zeigt, wer überprüft hat, was wann geändert wurde.',
            escalation_timers_info: 'Eskalationstimer: Die Frist für die Überprüfung durch den Eigentümer beträgt 5 Werktage. Die Ausnahmeeskalation beträgt 48 Stunden (UC-VN-10 Timer). Ungelöste Elemente werden automatisch eskaliert, wenn die Frist abläuft. Timer können je nach Organisationsrichtlinie konfiguriert werden.',
            original_suggestion_preserved_info: 'Der ursprüngliche Maschinen-/Systemklassifizierungsvorschlag bleibt bei jeder Eigentümerüberschreibung erhalten (UC-VN-10 Nachweispflicht). Der ursprüngliche Vorschlag wird niemals überschrieben und bleibt für Prüfzwecke sichtbar.',
            dedup_lineage_info: 'Deduplizierungsregeln verhindern doppelte Inventareinträge, wenn derselbe Datenbestand über mehrere Scans oder Connectors hinweg erkannt wird. Die aktuellsten Scan-Metadaten werden als primärer Datensatz beibehalten, während Herkunftsverweise zu allen früheren Entdeckungen zur Nachverfolgbarkeit bei Audits beibehalten werden.',
            label_states_info: 'Analytics unterscheidet vier Label-Status für die Governance-Überprüfung: maschinell vorgeschlagen (ausstehend), vom Eigentümer genehmigt, vom Eigentümer geändert und abgelehnt. Mithilfe dieser Zustände können Prüfer die Automatisierungsgenauigkeit und das Engagement der Eigentümer beurteilen.',
            import_pipeline_info: 'Importvorlagen müssen Datensätze erzeugen, die mit der Discovery-Pipeline kompatibel sind. Importierte Datensätze gelangen mit denselben Metadatenanforderungen wie gescannte Datensätze in die Bereitstellung: Quellsystemidentifikation, Batch-ID und Zeitstempel. Spaltenzuordnungen sollten auf Felder abzielen, die in vn_data_law_data_inventory_register definiert sind.',
            lineage_full_path_info: 'Die Klassifizierungsherkunft verfolgt den vollständigen Pfad: Scannen/Importieren -> Staging -> Eigentümerüberprüfung -> genehmigtes Etikett -> Registereintrag (UC-VN-10 Schritte 1–10). In jeder Phase werden Datensatz-ID, Akteur, Zeitstempel und Zustandsübergang angezeigt, um eine vollständige Rückverfolgbarkeit der Prüfung zu gewährleisten.',
            staging_workflow_info: 'Gestaffelte Datensätze unterscheiden sich optisch von genehmigten Inventareinträgen durch ihr Promotion-Status-Badge (UC-VN-10 Schritt 3). Jeder Datensatz behält Herkunftsmetadaten bei: Quellsystem, Scan-Batch-ID und Erkennungszeitstempel für eine vollständige Rückverfolgbarkeit bei Audits.',
            suggestion_data_d1_info: 'Die Vorschläge stehen im Einklang mit dem Klassifizierungsverfahren des Datengesetzes DATA-D1: (1) Datenbestände katalogisieren, (2) nach Vertraulichkeitsgrad klassifizieren, (3) Handhabungsregeln definieren. Jeder Vorschlag enthält Sensibilitäts- und Wichtigkeitsdimensionen gemäß Anforderung 18e4bfd1.',
            downstream_flags_info: 'Elemente, die für die gemeinsame Nutzung, grenzüberschreitende Übertragung oder Behandlung mit hohem Risiko gekennzeichnet sind, lösen verknüpfte nachgelagerte Arbeitsabläufe aus: Bewertung der grenzüberschreitenden Übertragung, Risikobewertung der gemeinsamen Nutzung und DPIA Überprüfung (UC-VN-10 Schritt 8).',
            delta_detection_info: 'Erkennungszyklen unterstützen die Deltaerkennung gemäß UC-VN-10 Schritt 11: Jeder Zyklus zeigt, welche Datensätze seit der letzten genehmigten Baseline neu, geändert oder entfernt wurden. Materialänderungen (>10 % Delta) lösen automatisch Re-Review-Workflows aus.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Fehler beim load..',
            save: 'Fehler beim save..',
            fetch_escalation_log_failed: 'Fehler beim fetch Eskalation Protokoll..',
            fetch_activations_failed: 'Fehler beim fetch activations..',
            deactivate_failed: 'Fehler beim deactivate..',
            complete_activation_failed: 'Fehler beim abgeschlossen activation..',
            fetch_playbook_templates_failed: 'Fehler beim fetch Playbook Vorlagen..',
            create_playbook_template_failed: 'Fehler beim create Playbook Vorlage..',
            clone_template_failed: 'Fehler beim clone Vorlage..',
            update_status_failed: 'Fehler beim update Status..',
            deprecate_template_failed: 'Fehler beim deprecate Vorlage..',
            seed_defaults_failed: 'Fehler beim seed Standardwerte..',
            fetch_regulator_exports_failed: 'Fehler beim fetch regulator exports..',
            create_regulator_export_failed: 'Fehler beim create regulator Export..',
            update_submission_status_failed: 'Fehler beim update Einreichung Status..',
            fetch_response_metrics_failed: 'Fehler beim fetch response Metriken..',
            fetch_sla_dashboard_failed: 'Fehler beim fetch SLA Dashboard..',
            fetch_sla_definitions_failed: 'Fehler beim fetch SLA Definitionen..',
            create_sla_definition_failed: 'Fehler beim create SLA Definition..',
            seed_sla_defaults_failed: 'Fehler beim seed SLA Standardwerte..',
            fetch_vendor_coordination_failed: 'Fehler beim fetch Anbieter Koordination..',
            fetch_vendor_detail_failed: 'Fehler beim fetch Anbieter Detail..',
            create_vendor_coordination_failed: 'Fehler beim create Anbieter Koordination..',
            add_communication_failed: 'Fehler beim add communication..',
            add_info_request_failed: 'Fehler beim add Info request..',
            add_action_request_failed: 'Fehler beim add Aktion request..',
        },
        nav: {
            playbooks: 'Leitfadenvorlagen',
            sla_definitions: 'SLA-Definitionen',
            sla_dashboard: 'SLA-Übersicht',
            escalation_log: 'Eskalationsprotokoll',
            regulator_exports: 'Behördenexporte',
            deadlines: 'Benachrichtigung Deadlines',
            vendor_coordination: 'Lieferantenkoordination',
            metrics: 'Reaktionsmetriken',
            playbook_links: 'Playbook-Verknüpfungen',
            regulatory_sla: 'Regulatorische SLA',
            coordination_dashboard: 'Koordination',
            evidence_chain: 'Beweiskette',
            post_review: 'Nachvorfallprüfung',
        },
        playbooks: {
            page_title: 'Playbook-Vorlagen',
            activations_page_title: 'Playbook-Aktivierungen',
            title: 'Leitfadenvorlagen',
            new: 'Neu Vorlage',
            seed_defaults: 'Initialisieren Standardwerte',
            scenario_type: 'Szenario Typ',
            severity_applicability: 'Schweregrad Applicability',
            all_scenario_types: 'Alle Szenario Typen',
            new_playbook: 'Neu Playbook Vorlage',
            clone: 'Klonen',
            clone_template: 'Klonen Vorlage',
            template_name: 'Vorlage Name',
            deprecate: 'Veralten',
            confirm_deprecate: 'Veralten Vorlage "{name}"?',
            create_template: 'Erstellen Vorlage',
            update_status: 'Aktualisieren Status',
            no_templates: 'Keine Playbook Vorlagen gefunden',
            deactivation_reason_placeholder: 'Grund für deactivation...',
            description_placeholder: 'Playbook Beschreibung...',
            name_placeholder: 'Playbook Name...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA-Definitionen',
            title: 'SLA-Definitionen',
            new: 'Neu SLA Definition',
            sla_name: 'SLA-Name',
            target_hours: 'Ziel Hours',
            escalation_after: 'Eskalation After',
            applies_to: 'Applies Zu',
            no_definitions: 'Keine SLA Definitionen gefunden.',
            target_value_placeholder: 'Ziel value',
            name_placeholder: 'SLA Name...',
            threshold_hours_label: 'Schwellenwert-Stunden',
            severity_label: 'Schweregrad',
            all_types: 'Alle Typen',
            all_severities: 'Alle Schweregrade',
            default_label: 'Standard',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Konform',
            page_title: 'SLA-Übersicht',
            title: 'SLA-Übersicht',
            loading: 'Lade SLA Daten...',
            compliance_rate: 'Konformität Rate',
            breached: 'Verletzt',
            at_risk: 'At Risiko',
            on_track: 'On Verfolgen',
            by_severity: 'By Schweregrad',
            total_records: 'Anzahl Datensätze',
            met: 'Erfüllt',
            sla_records: 'SLA-Datensätze',
            sla_reference: 'SLA-Referenz',
            record_id: 'Datensatz-ID',
            metric_type: 'Metriktyp',
            severity: 'Schweregrad',
            elapsed: 'Verstrichen',
            clock_start: 'Uhrenstart',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Eskalationsprotokoll',
            title: 'Eskalationsprotokoll',
            incident_id: 'Vorfall ID',
            escalated_by: 'Eskaliert By',
            escalated_to: 'Eskaliert Zu',
            reason: 'Grund',
            escalated_at: 'Eskaliert At',
            no_escalations: 'Keine escalations gefunden.',
            filter_incident_id: 'Filtern nach Vorfall ID',
            escalation_id: 'Eskalations-ID',
            level: 'Stufe',
            level_range: 'Niveau (L1–L4)',
            create_escalation: 'Eskalation erstellen',
            incident_id_placeholder: 'z.B. INC-001',
            target_role_placeholder: 'z.B. dpo, incident_manager',
            select_placeholder: '— auswählen —',
        },
        regulator_exports: {
            page_title: 'Behördenexporte',
            title: 'Behördenexporte',
            new: 'Neu Exportieren',
            incident_id: 'Vorfall ID',
            regulator: 'Regulierungsbehörde',
            export_format: 'Dateiformat',
            exported_at: 'Exportiert At',
            no_exports: 'Keine regulator exports gefunden.',
            enter_incident_id: 'Eingeben Vorfall ID',
            export_id: 'Export-ID',
            watermark: 'Wasserzeichen',
            submission_status: 'Einreichungsstatus',
            all_jurisdictions: 'Alle Zuständigkeiten',
            no_transitions: 'Keine weiteren Übergänge verfügbar von',
            select_new_status: '— neuen Status auswählen —',
            sections_included: 'Enthaltene Abschnitte',
            form_classification: 'Klassifizierung',
            submission_channel: 'Kanal',
        },
        deadlines: {
            title: 'Benachrichtigung Deadlines',
            incident_id: 'Vorfall ID',
            deadline: 'Frist',
            regulator: 'Regulierungsbehörde',
            remaining: 'Verbleibend',
            no_deadlines: 'Keine Benachrichtigung deadlines gefunden.',
        },
        vendor_coordination: {
            page_title: 'Lieferantenkoordinierung',
            title: 'Lieferantenkoordination',
            new: 'Neu Koordination',
            incident_id: 'Vorfall ID',
            vendor_name: 'Anbieter Name',
            contact: 'Kontakt',
            coordination_status: 'Koordination Status',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Keine Anbieter Koordination items gefunden.',
            assessment_placeholder: 'Erstmalig Bewertung von Anbieter...',
            coordination_id: 'Koordinations-ID',
            communications: 'Kommunikation',
            info_requests: 'Infoanfragen',
            action_requests: 'Aktionsanfragen',
            direction: 'Richtung',
            channel_label: 'Kanal',
            no_transitions: 'Keine weiteren Übergänge verfügbar.',
            vendor_ref_number: 'Referenznummer',
            report_date: 'Berichtsdatum',
            contact_email: 'E-Mail',
            contact_phone: 'Telefon',
            channel_email: 'E-Mail',
            channel_meeting: 'Besprechung',
            channel_phone: 'Telefon',
            channel_portal: 'Portalbereich',
            incident_id_placeholder: 'Filtern nach Vorfall ID...',
            priority_critical: 'Kritisch',
            priority_high: 'Hoch',
            priority_low: 'Low',
            priority_medium: 'Mittel',
            vendor_id_placeholder: 'Filtern nach Anbieter ID...',
            description_placeholder: 'Beschreibung...',
            filter_vendor_id: 'Filtern nach Anbieter ID',
            inbound: 'Eingehend',
            outbound: 'Ausgehend',
            summary_placeholder: 'Zusammenfassung...',
            priority_label: 'Priorität',
            due_date_label: 'Fälligkeitsdatum',
            info_request_desc: 'Beschreibung der Informationsanfrage',
            action_request_desc: 'Beschreibung der Aktionsanforderung',
            content_label: 'Inhalt',
            select_incident: 'Vorfall auswählen',
            select_vendor: 'Dienstleister auswählen',
            discovery_date: 'Erkennung Datum',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Akteur',
        },
        metrics: {
            page_title: 'Antwortmetriken',
            title: 'Reaktionsmetriken',
            loading: 'Lade Metriken Daten...',
            avg_response_time: 'Avg Antwort Zeit',
            avg_resolution_time: 'Avg Lösung Zeit',
            total_incidents: 'Gesamt Vorfälle',
            by_severity: 'By Schweregrad',
            by_status: 'Nach Status',
            trend: 'Entwicklung',
            start_sla_clock: 'SLA-Uhr starten',
            sla_records: 'SLA Datensätze',
            no_sla_records: 'Noch keine SLA-Datensätze.',
            record_id: 'Datensatz-ID',
            severity: 'Schweregrad',
            progress: 'Fortschritt',
            incident_id_required: 'Vorfalls-ID ist erforderlich',
            sla_definition_required: 'SLA-Definition ist erforderlich',
            metric_type_required: 'Metriktyp ist erforderlich',
            threshold_required: 'Schwellenwert-Stunden sind erforderlich',
            sla_clock_started: 'SLA-Uhr gestartet',
            sla_definition: 'SLA-Definition',
            no_definitions: 'Keine SLA-Definitionen gefunden.',
            select_definition: 'SLA-Definition auswählen...',
            select_metric_type: 'Metriktyp auswählen...',
            select_severity: 'Schweregrad auswählen...',
            threshold_hours: 'Schwellenwert (Stunden)',
            warning_threshold_pct: 'Warnschwelle (%)',
            start_clock: 'Uhr starten',
            starting: 'Startet...',
            metric_label: 'Metrik',
            value_label: 'Wert',
            incident_id: 'Vorfalls-ID',
            threshold_placeholder: 'z.B. 24',
            metric_types: {
                triage_time: 'Sichtungszeit',
                containment_time: 'Eindämmungszeit',
                notification_time: 'Benachrichtigungszeit',
                resolution_time: 'Lösungszeit',
            },
            severities: {
                critical: 'Kritisch',
                high: 'Hoch',
                medium: 'Mittel',
                low: 'Low',
            },
            sla_reference: 'SLA-Referenz',
        },
        playbook_links: {
            page_title: 'Playbook-Verknüpfungen',
            title: 'Playbook-Benachrichtigungsverknüpfungen',
            new_link: 'Playbook mit Benachrichtigung verknüpfen',
            activation_id: 'Aktivierungs-ID',
            notification_id: 'Benachrichtigungs-ID',
            select_activation: 'Aktivierung auswählen...',
            select_notification: 'Benachrichtigung auswählen...',
            no_links: 'Keine Playbook-Verknüpfungen gefunden',
            seed_pdpl: 'Vorlagen-Playbook-Verknüpfungen hinzufügen',
            created: 'Playbook-Verknüpfung erstellt',
            seeded: 'Vorlagen-Playbook-Verknüpfungen hinzugefügt',
            create_failed: 'Playbook-Verknüpfung konnte nicht erstellt werden',
            seed_failed: 'Hinzufügen der Vorlagen-Playbook-Verknüpfungen fehlgeschlagen',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Kein installiertes Rahmenwerk regelt die Vorfallsreaktion, daher gibt es keine Fristen zum Hinzufügen.',
                declared_but_none_active: 'Ein Rahmenwerk zur Vorfallsreaktion ist installiert, aber nicht in Kraft.',
                no_declared_deadlines: 'Die installierten Rahmenwerke zur Vorfallsreaktion legen keine Meldefristen fest.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Frist',
            deadline_units: {
                hours: '{count} Stunden',
                working_days: '{count} Werktage',
                calendar_days: '{count} Kalendertage',
                days: '{count} Tage',
                weeks: '{count} Wochen',
                months: '{count} Monate',
                years: '{count} Jahre',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Regulatorische SLA-Zuordnungen',
            title: 'Regulatorische SLA-Zuordnungen',
            new_mapping: 'Neue SLA-Zuordnung',
            sla_definition: 'SLA-Definition',
            select_sla_definition: 'SLA-Definition auswählen...',
            framework_code: 'Rahmenwerk-Code',
            deadline_type: 'Fristtyp',
            deadline_hours: 'Frist in Stunden',
            seed_pdpl: 'Zuordnungen aus installierten Fristen hinzufügen',
            no_mappings: 'Keine regulatorischen SLA-Zuordnungen gefunden',
            dashboard: 'Regulatorische SLA-Übersicht',
            deadline_types: {
                initial_notification: 'Erste Benachrichtigung',
                supplement_response: 'Ergänzungsantwort',
                authority_response: 'Antwort der Behörde',
                remediation_report: 'Sanierungsbericht',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Gesetzliche Referenz',
        },
        coordination_dashboard: {
            page_title: 'Koordinierungsübersicht',
            title: 'Koordinationsübersicht',
            incident_id: 'Vorfall-ID',
            select_incident: 'Vorfall auswählen...',
            team_role: 'Teamrolle',
            members: 'Mitglieder',
            tasks: 'Aufgaben',
            sla_status: 'SLA-Status',
            no_teams: 'Keine Koordinationsteams gefunden',
            new_team: 'Neues Koordinationsteam',
            enter_incident_id: 'Geben Sie eine Vorfalls-ID ein, um Koordinierungsteams anzuzeigen.',
            member_ids_placeholder: 'Benutzer1, Benutzer2',
            edit_team: 'Team bearbeiten',
            task_count: 'Aufgabenanzahl',
            tasks_completed: 'Abgeschlossen',
            team_roles: {
                security: 'Sicherheit',
                legal: 'Recht',
                it: 'IT',
                management: 'Führung',
                department_owner: 'Abteilungsinhaber',
                external: 'Extern',
            },
        },
        evidence_chain: {
            page_title: 'Beweiskette',
            title: 'Beweiskette',
            new_entry: 'Neuer Beweis-Eintrag',
            select_incident: 'Vorfall auswählen...',
            actor: 'Akteur',
            select_actor: 'Akteur auswählen...',
            evidence_id: 'Beweis-ID',
            action: 'Aktion',
            recipient: 'Empfänger',
            no_entries: 'Keine Beweisketteneinträge gefunden',
            enter_incident_id: 'Geben Sie eine Vorfalls-ID ein, um die Beweiskette anzuzeigen.',
            evidence_id_placeholder: 'z. B. EVD-2026-0001',
            evidence_id_help: 'Eine eindeutige Referenz für das Beweisstück – eine Protokolldatei, ein Datenträgerabbild oder ein Screenshot. Ein Vorschlag ist vorausgefüllt; passen Sie ihn an Ihren Beweiskatalog an.',
            select_incident_first: 'Wählen Sie oben einen Vorfall aus, bevor Sie einen Beweiseintrag hinzufügen.',
            for_incident: 'Für Vorfall',
            actions: {
                collected: 'Gesammelt',
                shared: 'Geteilt',
                exported: 'Exportiert',
                archived: 'Archiviert',
            },
        },
        post_review: {
            page_title: 'Nachvorfallprüfung',
            title: 'Nachvorfallprüfung',
            new_review: 'Neue Prüfung',
            select_incident: 'Vorfall auswählen...',
            lessons_learned: 'Erfahrungen',
            recommendations: 'Empfehlungen',
            improvement_actions: 'Verbesserungsmaßnahmen',
            submit_for_review: 'Zur Prüfung einreichen',
            aria_submit_review: 'Prüfung {name} zur Überprüfung einreichen',
            aria_sign_off: 'Prüfung {name} freigeben',
            sign_off: 'Freigabe',
            no_reviews: 'Keine Nachvorfallprüfungen gefunden',
            enter_incident_id: 'Geben Sie eine Vorfalls-ID ein, um die Nachvorfallprüfung anzuzeigen.',
            one_per_line: 'Einer pro Zeile',
            statuses: {
                draft: 'Entwurf',
                in_review: 'In Prüfung',
                signed_off: 'Freigegeben',
                closed: 'Geschlossen',
            },
        },
        activations: {
            title: 'Playbook-Aktivierungen',
            activation_id: 'Aktivierungs-ID',
            phases: 'Phasen',
            activated_at: 'Aktiviert um',
            activated_by: 'Aktiviert von',
            deactivate: 'Deaktivieren',
            complete: 'Abschließen',
            deactivation_reason: 'Deaktivierungsgrund',
            evidence_button: 'Beweise',
            evidence_modal_title: 'Aktivierungsnachweis',
            no_evidence: 'Dieser Aktivierung liegen keine Beweise bei.',
            upload_evidence_label: 'Aktivierungsnachweis hochladen',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Führe mich durch Lücken',
            running: 'Gehlücken…',
            results_heading: 'Verbesserungs-Roadmap ({count} Lücke{plural})',
            tokens_label: '≈ {count} Token',
            no_gaps: 'Keine Lücken erkannt – alle Domänen erreichen oder über dem Zielwert.',
            col_priority: 'Priorität',
            col_domain: 'Domäne',
            col_levels: 'Aktuell → Ziel',
            col_severity: 'Schweregrad',
            col_actions: 'Sanierungsmaßnahmen',
            citations_label: 'Zitate',
            accept_label: 'Roadmap akzeptieren',
            reject_label: 'Entwurf ablehnen',
            export_label: 'Exportieren DOCX',
            exported_label: 'Roadmap exportiert',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Auflösung: {status}',
            resolve_status_accepted: 'Akzeptiert',
            resolve_status_rejected: 'Abgelehnt',
            error_generic: 'Lückenlösung konnte nicht ausgeführt werden. Versuchen Sie es erneut oder überprüfen Sie den Bewertungsstatus.',
        },
        policy_gap: {
            run_label: 'Erkennen Sie Richtlinienlücken',
            running: 'Lücken erkennen…',
            results_heading: 'Richtlinienlücken ({count})',
            tokens_label: '≈ {count} Token',
            no_gaps: 'Keine Deckungslücken festgestellt.',
            col_severity: 'Schweregrad',
            col_kind: 'Freundlich',
            col_clause: 'Klausel/Anforderung',
            kind_missing: 'Fehlende Klausel',
            kind_weak: 'Schwache Abdeckung',
            kind_stale: 'Veraltet (Rezension überfällig)',
            remediation_label: 'Vorgeschlagene Abhilfe',
            citations_label: 'Zitate',
            accept_label: 'Lückenbericht akzeptieren',
            reject_label: 'Bericht ablehnen',
            export_label: 'Exportieren DOCX',
            accept_hint: 'Akzeptieren zeichnet den Lückenbericht zum beibehaltenen Entwurf auf. Verwenden Sie Exportieren DOCX, um einen Richtlinien-Audit-Bericht zu erstellen. Der Bediener steuert die eigentliche Richtlinienbearbeitung.',
            resolve_summary: 'Auflösung: {status}',
            error_generic: 'Es konnten keine Richtlinienlücken festgestellt werden. Versuchen Sie es erneut oder überprüfen Sie den Dokumentstatus.',
        },
        remediation_plan_generator: {
            run_label: 'Plan aus Lücken generieren',
            running: 'Plan erstellen…',
            results_heading: 'Sanierungsplan ({phase_count} Phase{plural})',
            tokens_label: '≈ {count} Token',
            phase_label: 'Phase {index}',
            window_label: 'Fenster',
            owner_label: 'Eigentümerrolle',
            evidence_label: 'Nachweisarten erforderlich',
            tasks_label: 'Aufgaben',
            no_phases: 'Keine Phasen erzeugt – Eingabelückensatz ist möglicherweise leer.',
            citations_label: 'Zitate',
            accept_label: 'Plan akzeptieren',
            reject_label: 'Plan ablehnen',
            export_label: 'Exportieren DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Lösung: {status} · {tasks} Aufgabe(n) erstellt',
            error_generic: 'Der Behebungsplan konnte nicht erstellt werden. Versuchen Sie es erneut oder überprüfen Sie die Gap-Set-Eingabe.',
            source_picker_label: 'Quelle',
            source_all: 'Alle',
            source_consent: 'Analyse der Einwilligungslücke',
            source_policy: 'Erkennung von Richtlinienlücken',
            source_vendor: 'Lieferanten-Onboarding',
            load_upstream_label: 'Vom Upstream laden',
            loading_upstream: 'Laden…',
            upstream_count: '{count} Lücke(n) geladen',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Reifegradmodelle',
            maturity_assessments: 'Reifgradbewertungen',
            trend_dashboard: 'Trendübersicht',
            reporting_periods: 'Berichtszeiträume',
            executive_summaries: 'Zusammenfassungen',
            audit_readiness: 'Prüfungsbereitschaft',
            evidence_gaps: 'Nachweislücken',
            program_health: 'Programmzustand',
            obligation_mappings: 'Pflichtzuordnungen',
            governance_documents: 'Governance-Dokumente',
            remediation_plans: 'Maßnahmenpläne',
            governance_model: 'Governance-Modell',
            document_drafting: 'Dokumententwurf',
            document_issuance: 'Dokumentenausgabe',
            acknowledgment_tracking: 'Bestätigungsverfolgung',
            change_log: 'Änderungsprotokoll',
            review_cadence: 'Überprüfungskadenz',
        },
        maturity_models: {
            title: 'Reifegradmodelle',
            new: 'Neu Modell',
            seed_defaults: 'Initialisieren Standardwerte',
            domains: 'Domänen',
            activate: 'Aktivieren',
            no_models: 'Keine Reife models gefunden',
            new_maturity_model: 'Neu Reife Modell',
            create_model: 'Erstellen Modell',
            load_error: 'Reifegradmodelle konnten nicht geladen werden. Bitte erneut versuchen.',
            create_error: 'Reifegradmodell konnte nicht erstellt werden',
            status_error: 'Modellstatus konnte nicht aktualisiert werden',
            seed_error: 'Standardwerte konnten nicht generiert werden',
            aria_seed_defaults: 'Standard-Reifegradmodelle generieren',
            aria_create_new: 'Neues Reifegradmodell erstellen',
            loading_sr: 'Reifegradmodelle werden geladen',
            model_name_placeholder: 'z.B., Datenschutz-Reifegradmodell',
            status_changed: 'Modellstatus geändert auf {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Reifgradbewertungen',
            new: 'Neu Bewertung',
            model: 'Modell',
            assessor: 'Bewerter',
            overall_score: 'Gesamt Bewertung',
            overall_score_help: 'Eine gewichtete Reifebewertung von 0–5, die automatisch berechnet wird, wenn eine Bewertung abgeschlossen wird. Erhöhen Sie sie, indem Sie jede Domäne während der Evaluierung höher bewerten und die Bewertung anschließend über Zur Überprüfung einreichen → Abschließen führen.',
            assessed_at: 'Bewertet am',
            no_assessments: 'Keine Reife assessments gefunden.',
            load_error: 'Reifegradbeurteilungen konnten nicht geladen werden. Bitte erneut versuchen.',
            create_error: 'Bewertung konnte nicht erstellt werden',
            start_error: 'Bewertung konnte nicht gestartet werden',
            archive_error: 'Bewertung konnte nicht archiviert werden',
            eval_error: 'Bewertung zur Evaluierung konnte nicht geladen werden',
            eval_min_score: 'Bitte bewerten Sie mindestens eine Domäne',
            eval_save_error: 'Domänenbewertungen konnten nicht gespeichert werden',
            review_error: 'Konnte nicht zur Überprüfung eingereicht werden',
            finalize_error: 'Bewertung konnte nicht abgeschlossen werden',
            start: 'Starten',
            start_confirm_title: 'Bewertung starten',
            start_confirm_message: 'Sind Sie sicher, dass Sie diese Bewertung starten möchten?',
            archive_confirm_title: 'Bewertung archivieren',
            archive_confirm_message: 'Sind Sie sicher, dass Sie diese Bewertung archivieren möchten?',
            evaluate_domains: 'Evaluieren',
            evaluate_domains_title: 'Domänen evaluieren',
            submit_for_review: 'Zur Überprüfung einreichen',
            finalize: 'Abschließen',
            archive: 'Archivieren',
            all_reviewers: 'Alle Prüfer müssen genehmigen',
            any_reviewer: 'Jeder Prüfer kann genehmigen',
            select_level: 'Reifegrad auswählen...',
            findings_placeholder: 'Ergebnisse, Notizen, Evidenzreferenzen...',
            no_domains: 'Keine Domänen für dieses Modell gefunden.',
        },
        trend_dashboard: {
            title: 'Trendübersicht',
            loading: 'Lade trend Daten...',
            score_trend: 'Bewertung Trend',
            domain_comparison: 'Domäne Vergleich',
            period: 'Zeitraum',
            load_error: 'Trendzusammenfassung konnte nicht geladen werden. Bitte erneut versuchen.',
            metrics_load_error: 'Trendmetriken konnten nicht geladen werden. Bitte erneut versuchen.',
            module: 'Modul',
            metric: 'Kennzahl',
            value: 'Wert',
            trend: 'Trend',
            aria_filters: 'Filter für Trendmetriken',
            showing_chart: 'Diagrammansicht',
            showing_table: 'Datentabellenansicht',
            create_metric: 'Kennzahl erstellen',
            data_points: 'Datenpunkte',
            trend_direction: 'Trendrichtung',
            unit: 'Einheit',
        },
        reporting_periods: {
            title: 'Berichtszeiträume',
            new: 'Neu Zeitraum',
            period_name: 'Zeitraum Name',
            start_date: 'Starten Datum',
            end_date: 'Ende Datum',
            no_periods: 'Keine reporting Zeiträume gefunden.',
            load_error: 'Berichtszeiträume konnten nicht geladen werden. Bitte erneut versuchen.',
            create_error: 'Berichtszeitraum konnte nicht erstellt werden',
            auto_generate_error: 'Berichtszeiträume konnten nicht automatisch generiert werden',
            status_error: 'Periodenstatus konnte nicht aktualisiert werden',
            start_collecting: 'Erfassung starten',
            mark_as_draft: 'Als Entwurf markieren',
            submit_for_review: 'Zur Überprüfung einreichen',
            publish: 'Veröffentlichen',
            auto_generate: 'Berichtszeiträume automatisch generieren',
            period_label_placeholder: 'z.B., Q1 2026',
            status_updated: 'Periodenstatus aktualisiert',
        },
        executive_summaries: {
            title: 'Zusammenfassungen',
            new: 'Neu Zusammenfassung',
            period: 'Zeitraum',
            generated_at: 'Generiert At',
            generated_by: 'Generiert By',
            no_summaries: 'Keine executive summaries gefunden.',
            generate: 'Generieren',
            load_error: 'Führungszusammenfassungen konnten nicht geladen werden. Bitte erneut versuchen.',
            generate_error: 'Führungszusammenfassung konnte nicht generiert werden',
            save_error: 'Führungszusammenfassung konnte nicht gespeichert werden',
        },
        audit_readiness: {
            title: 'Prüfungsbereitschaft',
            loading: 'Lade Audit readiness Daten...',
            overall_readiness: 'Gesamt Readiness',
            by_domain: 'By Domäne',
            evidence_coverage: 'Nachweis Abdeckung',
            gaps: 'Lücken',
            load_error: 'Aktuelle Bereitschaft konnte nicht geladen werden. Bitte erneut versuchen.',
            history_error: 'Bereitschaftsverlauf konnte nicht geladen werden. Bitte erneut versuchen.',
            calculate_error: 'Auditbereitschaft konnte nicht berechnet werden',
            aria_recalculate: 'Auditbereitschaftsbewertung neu berechnen',
            aria_current_readiness: 'Aktuelle Auditbereitschaft',
            aria_history: 'Verlauf der Bereitschaftsbewertung',
            recalculated: 'Auditbereitschaft neu berechnet',
            loading_sr: 'Auditbereitschaftsdaten werden geladen',
        },
        evidence_gaps: {
            title: 'Nachweislücken',
            domain: 'Domäne',
            control: 'Kontrolle',
            gap_type: 'Lücke Typ',
            severity: 'Schweregrad',
            no_gaps: 'Keine Nachweis Lücken gefunden.',
            load_error: 'Evidenzlücken konnten nicht geladen werden. Bitte erneut versuchen.',
            stats_error: 'Lückenstatistiken konnten nicht geladen werden. Bitte erneut versuchen.',
            create_error: 'Evidenzlücke konnte nicht erstellt werden',
            update_error: 'Evidenzlücke konnte nicht aktualisiert werden',
            status_error: 'Status konnte nicht aktualisiert werden',
            aria_create_new: 'Neue Evidenzlücke erstellen',
            aria_statistics: 'Statistiken zu Evidenzlücken',
            loading_sr: 'Evidenzlücken werden geladen',
            status_updated: 'Lückenstatus aktualisiert',
            entity_type_placeholder: 'z.B., Lieferant, System',
            all_modules: 'Alle Domänen',
            all_severities: 'Alle Schweregrade',
        },
        program_health: {
            title: 'Programmzustand',
            loading: 'Lade Programm Zustand Daten...',
            overall_health: 'Gesamt Zustand',
            by_module: 'By Modul',
            risk_areas: 'Risiko Areas',
            recommendations: 'Empfehlungen',
            load_error: 'Programmgesundheitsdaten konnten nicht geladen werden. Bitte erneut versuchen.',
            aria_key_metrics: 'Kennzahlen der Programmgesundheit',
            aria_module_scorecard: 'Modul-Bewertungskarten-Aufschlüsselung',
            loading_sr: 'Programmgesundheitsdaten werden geladen',
            trending_up: 'Aufwärtstrend',
            trending_down: 'Abwärtstrend',
            trending_stable: 'Stabil',
            data_loaded: 'Programmgesundheitsdaten geladen',
        },
        obligation_mappings: {
            title: 'Pflichtzuordnungen',
            create_mapping: 'Pflichtzuordnung erstellen',
            framework: 'Rahmenwerk',
            select_framework: 'Rahmenwerk auswählen...',
            obligation: 'Pflicht',
            obligation_ref: 'Pflichtreferenz',
            obligation_description: 'Pflichtbeschreibung',
            role: 'Rolle',
            assigned_role: 'Zugewiesene Rolle',
            select_role: 'Rolle auswählen...',
            assigned_department: 'Zugewiesene Abteilung',
            select_department: 'Abteilung auswählen...',
            control_ids: 'Kontroll-IDs',
            control_ids_placeholder: 'z. B. A.5.1, CC6.1',
            control_ids_hint: 'Durch Kommas getrennte Kontroll-Referenzcodes.',
            raci_assignments: 'RACI-Zuordnungen',
            create_raci: 'RACI-Zuordnung erstellen',
            activity: 'Aktivität',
            activity_name: 'Aktivitätsname',
            activity_description: 'Aktivitätsbeschreibung',
            responsible: 'Verantwortlich',
            select_users: 'Benutzer auswählen...',
            accountable: 'Rechenschaftspflichtig',
            select_user: 'Benutzer auswählen...',
            consulted: 'Konsultiert',
            informed: 'Informiert',
            review_frequency: 'Überprüfungsfrequenz',
            frequency_monthly: 'Monatlich',
            frequency_quarterly: 'Vierteljährlich',
            frequency_semi_annual: 'Halbjährlich',
            frequency_annual: 'Jährlich',
            no_mappings_found: 'Keine Pflichtzuordnungen gefunden.',
            no_raci_found: 'Keine RACI-Zuordnungen gefunden.',
            mapping_created: 'Verpflichtungszuordnung erstellt',
            mapping_updated: 'Pflichtzuordnung aktualisiert',
            mapping_deleted: 'Pflichtzuordnung gelöscht',
            raci_created: 'RACI-Zuweisung erstellt',
            raci_updated: 'RACI-Zuordnung aktualisiert',
            raci_deleted: 'RACI-Zuordnung gelöscht',
            aria_edit_mapping: 'Zuordnung {name} bearbeiten',
            aria_delete_mapping: 'Zuordnung {name} löschen',
            aria_edit_raci: 'RACI {name} bearbeiten',
            aria_delete_raci: 'RACI {name} löschen',
            confirm_delete_mapping: 'Diese Pflichtzuordnung löschen?',
            confirm_delete_raci: 'Diese RACI-Zuordnung löschen?',
            table_view: 'Tabellenansicht',
            matrix_view: 'Matrixansicht',
            print_export: 'Drucken / Exportieren',
            filter_framework: 'Nach Rahmenwerk filtern',
            no_matrix_data: 'Keine RACI-Zuweisungen zur Anzeige in der Matrixansicht.',
            aria_print_export: 'RACI-Matrix drucken oder exportieren',
        },
        governance_documents: {
            title: 'Governance-Dokumente',
            create_document: 'Governance-Dokument erstellen',
            document_title: 'Dokumenttitel',
            document_type: 'Dokumenttyp',
            content_url: 'Inhalts-URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Rahmencodes',
            select_frameworks: 'Rahmenwerke auswählen...',
            effective_date: 'Gültigkeitsdatum',
            review_date: 'Überprüfungsdatum',
            status: 'Status',
            version: 'Version',
            acknowledgments: 'Bestätigungen',
            acknowledge: 'Bestätigen',
            acknowledged_by: 'Bestätigt von',
            department: 'Abteilung',
            no_documents_found: 'Keine Governance-Dokumente gefunden.',
            draft: 'Entwurf',
            review: 'Überprüfung',
            approved: 'Genehmigt',
            issued: 'Ausgestellt',
            superseded: 'Abgelöst',
            document_created: 'Dokument erstellt',
            document_acknowledged: 'Dokument bestätigt',
            policy: 'Richtlinie',
            procedure: 'Verfahren',
            standard: 'Standard',
            guideline: 'Leitlinie',
        },
        governance_model: {
            title: 'Governance-Modell',
            create_model: 'Governance-Modell erstellen',
            edit_model: 'Governance-Modell bearbeiten',
            model_name: 'Modellname',
            description: 'Beschreibung',
            governance_type: 'Governance-Typ',
            type_centralized: 'Zentralisiert',
            type_federated: 'Föderiert',
            type_hybrid: 'Hybrid',
            framework_codes: 'Rahmenwerk-Codes',
            select_frameworks: 'Rahmenwerke auswählen...',
            review_frequency: 'Überprüfungshäufigkeit',
            freq_monthly: 'Monatlich',
            freq_quarterly: 'Vierteljährlich',
            freq_semi_annual: 'Halbjährlich',
            freq_annual: 'Jährlich',
            committees: 'Ausschüsse',
            committee_name: 'Ausschussname',
            committee_members: 'Mitglieder (kommagetrennt)',
            committee_mandate: 'Mandat',
            add_committee: 'Ausschuss hinzufügen',
            no_committees: 'Keine Ausschüsse definiert.',
            delegation_rules: 'Delegationsregeln',
            from_role: 'Von Rolle',
            to_role: 'An Rolle',
            scope: 'Umfang',
            add_delegation_rule: 'Delegationsregel hinzufügen',
            control_owner_summary: 'Kontrollinhaber-Zusammenfassung',
            no_raci_data: 'Keine RACI-Daten verknüpft.',
            activity: 'Aktivität',
            responsible: 'Verantwortlich',
            accountable: 'Rechenschaftspflichtig',
            consulted: 'Konsultiert',
            informed: 'Informiert',
            no_models_found: 'Keine Governance-Modelle gefunden.',
            model_created: 'Governance-Modell erstellt',
            model_updated: 'Governance-Modell aktualisiert',
            model_deleted: 'Governance-Modell gelöscht',
            confirm_delete: 'Dieses Governance-Modell löschen?',
            expand: 'Erweitern',
            collapse: 'Einklappen',
            aria_expand: 'Details für {name} erweitern',
            aria_edit: 'Governance-Modell {name} bearbeiten',
            aria_delete: 'Governance-Modell {name} löschen',
            aria_export_csv: '{name} als CSV exportieren',
            aria_export_json: '{name} als JSON exportieren',
            framework_read_permission_warning: 'Sie benötigen die Berechtigung regulatory_framework:read, um Frameworks auszuwählen. Bitten Sie einen Administrator, es zu gewähren.',
            select_members: 'Wählen Sie Ausschussmitglieder aus',
            select_from_role: 'Wählen Sie die delegierende Rolle aus',
            select_to_role: 'Wählen Sie die Empfangsrolle aus',
            mandate_markdown_hint: 'Markdown unterstützt: **fett**, Listen, Links.',
            scope_tab_obligation: 'Verpflichtung',
            scope_tab_framework: 'Rahmen',
            scope_tab_free_text: 'Freitext',
            select_obligation: 'Wählen Sie eine Verpflichtung aus',
            select_framework: 'Wählen Sie ein Framework aus',
            aria_remove_committee: 'Ausschuss {n} entfernen',
            aria_remove_delegation_rule: 'Delegationsregel {n} entfernen',
            no_frameworks_available: 'Keine Rahmenwerke verfügbar. Fügen Sie zuerst regulatorische Rahmenwerke hinzu.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Dokumententwurf & Genehmigung',
            document_title: 'Dokumenttitel',
            document_type: 'Dokumenttyp',
            status: 'Status',
            drafter: 'Verfasser',
            deadline: 'Frist',
            assign_drafter: 'Verfasser zuweisen',
            select_drafter: 'Verfasser auswählen...',
            assign: 'Zuweisen',
            submit_for_review: 'Zur Überprüfung einreichen',
            legal_review: 'Rechtliche Überprüfung',
            reviewer: 'Prüfer',
            select_reviewer: 'Prüfer auswählen...',
            comments: 'Kommentare',
            review_action: 'Überprüfungsaktion',
            approve: 'Genehmigen',
            request_changes: 'Änderungen anfordern',
            submit_review: 'Überprüfung einreichen',
            executive_approval: 'Leitungsgenehmigung',
            no_drafts_found: 'Keine Dokumente in Entwurfs-, Überprüfungs- oder Genehmigungsphase.',
            drafter_assigned: 'Verfasser zugewiesen',
            status_updated: 'Dokumentstatus aktualisiert',
            review_approved: 'Überprüfung genehmigt',
            changes_requested: 'Änderungen angefordert',
            document_approved: 'Dokument genehmigt',
            aria_assign: 'Verfasser für {name} zuweisen',
            aria_submit_review: '{name} zur Überprüfung einreichen',
            aria_review: '{name} überprüfen',
            aria_approve: '{name} genehmigen',
        },
        document_issuance: {
            title: 'Dokumentenausgabe & Verteilung',
            document_title: 'Dokumenttitel',
            document_type: 'Dokumenttyp',
            status: 'Status',
            version: 'Version',
            effective_date: 'Gültigkeitsdatum',
            distribution_scope: 'Verteilungsbereich',
            distribution_placeholder: 'Z.B.: Alle Abteilungen',
            issuance_number: 'Ausgabenummer',
            issuance_number_placeholder: 'Z.B.: ISS-2026-001',
            supersedes: 'Ersetzt Dokument',
            none: 'Keines',
            issue_document: 'Dokument ausgeben',
            ready_for_issuance: 'Bereit zur Ausgabe',
            issued_documents: 'Ausgegebene Dokumente',
            no_issued_documents: 'Keine ausgegebenen Dokumente gefunden.',
            view_lineage: 'Abstammung anzeigen',
            supersession_lineage: 'Ersetzungsabstammung',
            document_issued: 'Dokument ausgegeben',
            document_superseded: 'Dokument ersetzt',
            aria_issue: '{name} ausgeben',
            aria_view_lineage: 'Abstammung von {name} anzeigen',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Bestätigungsverfolgung',
            document_title: 'Dokument',
            acknowledged_count: 'Bestätigt',
            pending_count: 'Ausstehend',
            percentage: 'Abschluss',
            details: 'Einzelheiten',
            send_reminder: 'Erinnerung senden',
            acknowledgment_details: 'Bestätigungsdetails',
            user: 'Benutzer',
            department: 'Abteilung',
            acknowledged_at: 'Bestätigt am',
            status: 'Status',
            acknowledged: 'Bestätigt',
            no_issued_documents: 'Keine Dokumente zu verfolgen.',
            no_acknowledgments: 'Keine Bestätigungen erfasst.',
            reminder_sent: 'Erinnerung gesendet',
            aria_expand: 'Details für {name} anzeigen',
            aria_send_reminder: 'Erinnerung für {name} senden',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Änderungsprotokoll',
            date: 'Datum',
            document: 'Dokument',
            change_type: 'Änderungstyp',
            actor: 'Akteur',
            summary: 'Zusammenfassung',
            export_csv: 'CSV exportieren',
            filters: 'Protokollfilter',
            filter_document: 'Nach Dokument filtern',
            all_documents: 'Alle Dokumente',
            filter_change_type: 'Nach Typ filtern',
            all_types: 'Alle Typen',
            date_from: 'Datum von',
            date_to: 'Datum bis',
            version_history: 'Versionshistorie',
            version_archive: 'Versionsarchiv',
            no_entries: 'Keine Einträge gefunden.',
            no_history: 'Keine Historie verfügbar.',
            exported: 'Protokoll exportiert',
            aria_export: 'Protokoll als CSV exportieren',
            aria_view_history: 'Historie von {name} anzeigen',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Überprüfungskadenz',
            configure_cadence: 'Kadenz konfigurieren',
            edit_cadence: 'Kadenz bearbeiten',
            document: 'Dokument',
            select_document: 'Dokument auswählen...',
            review_frequency: 'Überprüfungshäufigkeit',
            freq_30: '30 Tage',
            freq_60: '60 Tage',
            freq_90: '90 Tage',
            freq_180: '180 Tage',
            freq_365: '365 Tage',
            last_reviewed: 'Zuletzt überprüft',
            next_review: 'Nächste Überprüfung',
            status: 'Status',
            overdue: 'Überfällig',
            on_track: 'Im Plan',
            days: 'Tage',
            horizon: 'Überprüfungshorizont',
            overdue_warning: 'Überfällige Überprüfungen',
            overdue_count: '{count} Dokument(e) überfällig.',
            no_cadences: 'Keine Kadenzen konfiguriert.',
            cadence_created: 'Kadenz erstellt',
            cadence_updated: 'Kadenz aktualisiert',
            aria_edit: 'Kadenz von {name} bearbeiten',
        },
        remediation_plans: {
            title: 'Maßnahmenpläne',
            create_plan: 'Maßnahmenplan erstellen',
            gap_id: 'Lückenreferenz',
            select_gap: 'Lücke auswählen...',
            plan_title: 'Titel',
            description: 'Beschreibung',
            priority: 'Priorität',
            priority_critical: 'Kritisch',
            priority_high: 'Hoch',
            priority_medium: 'Mittel',
            priority_low: 'Niedrig',
            assigned_to: 'Zugewiesen an',
            select_user: 'Benutzer auswählen...',
            target_date: 'Zieldatum',
            framework_code: 'Rahmenwerk',
            select_framework: 'Rahmenwerk auswählen...',
            gap_finding: 'Lückenfeststellung',
            action_items: 'Maßnahmenpunkte',
            owner: 'Verantwortlicher',
            deadline: 'Frist',
            planned: 'Geplant',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            verified: 'Verifiziert',
            no_plans_found: 'Keine Maßnahmenpläne gefunden.',
            evidence: 'Nachweis',
            plan_created: 'Abhilfeplan erstellt',
            action_items_hint: 'Maßnahmen (eine pro Zeile)',
        },
        statuses: {
            identified: 'Identifiziert',
            assigned: 'Zugewiesen',
            in_remediation: 'In der Sanierung',
            resolved: 'Gelöst',
            accepted_risk: 'Akzeptiertes Risiko',
            planned: 'Geplant',
            in_progress: 'In Bearbeitung',
            under_review: 'Wird überprüft',
            finalized: 'Abgeschlossen',
            archived: 'Archiviert',
            draft: 'Entwurf',
            active: 'Aktiv',
            upcoming: 'Demnächst',
            collecting: 'Sammeln',
            published: 'Veröffentlicht',
            review: 'Rezension',
            approved: 'Genehmigt',
            issued: 'Ausgestellt',
            superseded: 'Abgelöst',
            completed: 'Abgeschlossen',
            verified: 'Verifiziert',
        },
        severities: {
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Low',
        },
        priorities: {
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Low',
        },
        gap_types: {
            missing: 'Fehlt',
            expired: 'Abgelaufen',
            insufficient: 'Unzureichend',
        },
        document_types: {
            policy: 'Politik',
            procedure: 'Vorgehensweise',
            standard: 'Standard',
            guideline: 'Richtlinie',
            template: 'Vorlage (intern)',
        },
        maturity_levels: {
            level_1: 'Stufe 1 – Anfänglich',
            level_2: 'Ebene 2 – Entwicklung',
            level_3: 'Level 3 – Definiert',
            level_4: 'Stufe 4 – verwaltet',
            level_5: 'Stufe 5 – Optimierung',
        },
        grades: {
            excellent: 'Ausgezeichnet',
            good: 'Gut',
            fair: 'Fair',
            poor: 'Arm',
            critical: 'Kritisch',
            needs_improvement: 'Verbesserungsbedarf',
            not_assessed: 'Nicht bewertet',
        },
        modules: {
            data_mapping: 'Datenzuordnung',
            consent: 'Zustimmung',
            governance: 'Regierungsführung',
            incidents: 'Vorfälle',
            monitoring: 'Überwachung',
            classification: 'Klassifizierung',
            vendor_risk: 'Lieferantenrisiko',
            discovery: 'Entdeckung',
            deletion: 'Löschung',
        },
        cadences: {
            monthly: 'Monatlich',
            quarterly: 'Vierteljährlich',
            semi_annual: 'Halbjährlich',
            annual: 'Jährlich',
        },
        section_labels: {
            opening: 'Eröffnung',
            key_metrics: 'Schlüsselkennzahlen',
            highlights: 'Höhepunkte',
            concerns: 'Bedenken',
            recommendations: 'Empfehlungen',
            outlook: 'Ausblick',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} Lücke',
        count_gaps_plural: '{count} Lücken',
        count_summaries: '{count} Zusammenfassung',
        count_summaries_plural: '{count} Zusammenfassungen',
        count_assessments: '{count} Bewertung',
        count_assessments_plural: '{count} Bewertungen',
        count_models: '{count} Modell',
        count_models_plural: '{count} Modelle',
        count_periods: '{count} Zeitraum',
        count_periods_plural: '{count} Zeiträume',
        count_metrics: '{count} Metrik',
        count_metrics_plural: '{count} Metriken',
        unknown_user: 'Unbekannter Benutzer',
        unknown_gap: 'Unbekannte Lücke',
        no_permission_title: 'Unzureichende Berechtigungen',
        no_permission_message: 'Sie haben keine Berechtigung, auf diese Seite zuzugreifen.',
        ac_vn_frameworks_title: 'VN Framework-Abdeckung',
        ac_vn_frameworks_body: 'Reifegradbewertungen decken alle sechs vietnamesischen Regulierungsrahmen ab: PDPL, Datenrecht, Telekommunikationsrecht, Cybersicherheitsrecht, AI Recht und E-Commerce-Recht – jeweils mit rahmenspezifischen Bewertungskriterien.',
        ac_controller_processor_title: 'Controller vs. Prozessor',
        ac_controller_processor_body: 'Bei den Pflichtzuordnungen wird zwischen den Verantwortlichkeiten des Datenverantwortlichen und des Datenverarbeiters unterschieden. Weisen Sie mithilfe der RACI-Matrix Rollen zu, um sicherzustellen, dass jede Verpflichtung einen eindeutigen Eigentümer hat, unabhängig davon, ob die Organisation als Verantwortlicher oder Auftragsverarbeiter fungiert.',
        ac_versioned_evidence_title: 'Versionierter Beweispfad',
        ac_versioned_evidence_body: 'Bei jedem Statusübergang (Entwurf, Überprüfung, Genehmigt, Ausgestellt) wird ein zeitgestempelter, versionierter Datensatz erstellt, der als überprüfbarer Beweis dient. Das Änderungsprotokoll erfasst den gesamten Lebenszyklusverlauf des Dokuments.',
        ac_remediation_gap_link_title: 'Verbunden mit Bewertungslücken',
        ac_remediation_gap_link_body: 'Jeder Sanierungsplan ist direkt mit einer identifizierten Bewertungslücke verknüpft. Weisen Sie einen Eigentümer zu und legen Sie eine Zielfrist fest, um den Lösungsfortschritt von der Lücke bis zur Schließung zu verfolgen.',
        ac_annual_attestation_title: 'Jährlicher Zertifizierungszyklus',
        ac_annual_attestation_body: 'Die Berichtszeiträume richten sich nach der jährlichen Bescheinigungspflicht des vietnamesischen Datengesetzes. Nutzen Sie den Rhythmus „Jährlich“ (365-Tage-Zyklus), um attestierungsgerechte Zeiträume automatisch zu generieren.',
        ac_evidence_per_obligation_title: 'Nachweis gemäß Rahmenverpflichtung',
        ac_evidence_per_obligation_body: 'Beweislücken identifizieren fehlende, abgelaufene oder unzureichende Nachweise für jede Rahmenverpflichtung. Filtern Sie nach Domäne, um zu sehen, für welche regulatorischen Anforderungen keine unterstützenden Beweise vorliegen.',
        ac_cross_framework_title: 'Rahmenübergreifende Vollständigkeit',
        ac_cross_framework_body: 'Die Audit-Bereitschaft bietet eine rahmenübergreifende Vollständigkeitsansicht. Die Komponentenbewertungen spiegeln die Abdeckung aller abgebildeten Frameworks wider und heben Bereiche hervor, die vor einer Prüfung Aufmerksamkeit erfordern.',
        ac_cadence_default_title: 'Standard-Überprüfungsrhythmus',
        ac_cadence_default_body: 'Der standardmäßige Überprüfungsrhythmus beträgt 180 Tage. Überfällige Bewertungen werden automatisch gekennzeichnet. Passen Sie die Häufigkeit pro Dokument nach Bedarf an – die Optionen reichen von 30 bis 365 Tagen.',
        ac_templates_internal_title: 'Vorlagenklassifizierung',
        ac_templates_internal_body: 'Vorlagen werden als interne Dokumente klassifiziert. Verwenden Sie den Dokumenttyp „Vorlage“, um wiederverwendbare Governance-Artefakte mit ausschließlich internem Verteilungsbereich zu kennzeichnen.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Ausstehende Schreibvorgänge',
        subtitle: 'Externe Claude-Clients schlagen hier Schreibvorgänge vor. Genehmigen oder lehnen Sie jeden Eintrag ab — Forseti führt erst nach Admin-Freigabe aus.',
        no_pending: 'Keine ausstehenden Schreibvorgänge.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Werkzeug',
        col_preview: 'Vorschau',
        col_key: 'API-Taste',
        col_requested: 'Angefordert',
        col_expires: 'Läuft ab',
        col_status: 'Status',
        status_pending: 'Ausstehend',
        status_approved: 'Genehmigt',
        status_declined: 'Abgelehnt',
        status_expired: 'Abgelaufen',
        status_executed: 'Ausgeführt',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Genehmigen',
        decline_label: 'Ablehnen',
        refresh_label: 'Aktualisieren',
        error_generic: 'Ausstehende Schreibvorgänge konnten nicht geladen werden.',
        confirm_approve: 'Bei Genehmigung wird das Werkzeug für Ihre Organisation mit den Berechtigungen des API key-Inhabers ausgeführt. Fortfahren?',
        confirm_decline: 'Diesen ausstehenden Schreibvorgang ablehnen? Dies kann nicht rückgängig gemacht werden.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Ausstehende Schreibvorgänge laufen 24 Stunden nach Erstellung ab. Der stündliche GC-Sweep lehnt abgelaufene Einträge automatisch ab.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Aktionen',
    },
    audit_walkthrough: {
        title: 'Forseti — Generator für Audit-Begehungen',
        subtitle: 'Erstellen Sie eine strukturierte Begehung für ein Audit-Kickoff: Eröffnungsfrage je Kontrolle, erwartete Nachweise, Folgefragen, potenzielle Lücken.',
        audit_picker_label: 'Prüfung',
        audit_picker_hint: 'Wählen Sie ein geplantes Audit, um eine Begehung zu erstellen.',
        run_label: 'Begehung erstellen',
        running: 'Erstellung läuft…',
        results_heading: 'Begehung — {count} Kontrolle(n)',
        tokens_label: '≈ {count} Token',
        no_controls: 'Keine Kontrollen im Geltungsbereich. Fügen Sie dem Audit-Datensatz Kontrollen hinzu.',
        col_control: 'Kontrolle',
        col_question: 'Eröffnungsfrage',
        col_evidence: 'Nachweis',
        col_gaps: 'Potenzielle Lücken',
        expected_evidence_label: 'Erwartete Nachweise',
        followup_probes_label: 'Folgefragen',
        accept_label: 'Begehung akzeptieren',
        reject_label: 'Ablehnen',
        export_label: 'DOCX exportieren',
        resolve_summary: 'Ergebnis: {status}',
        resolve_status_accepted: 'Akzeptiert',
        resolve_status_rejected: 'Abgelehnt',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Begehung konnte nicht erstellt werden. Versuchen Sie es erneut oder prüfen Sie den Audit-Status.',
    },
    privilege_creep: {
        title: 'Forseti — Erkennung schleichender Rechteausweitung',
        subtitle: 'Periodische Prüfung von Rollenzuweisungen. Befunde sind beratend — Forseti widerruft niemals automatisch.',
        run_label: 'Jetzt prüfen',
        running: 'Prüfung läuft…',
        findings_heading: '{count} Befund(e)',
        no_findings: 'Keine schleichende Rechteausweitung festgestellt.',
        col_user: 'Benutzer',
        col_job_function: 'Jobfunktion',
        col_severity: 'Schweregrad',
        col_unexpected: 'Unerwartete Berechtigungen',
        col_remediation: 'Vorgeschlagene Behebung',
        col_status: 'Status',
        severity_critical: 'Kritisch',
        severity_high: 'Hoch',
        severity_medium: 'Mittel',
        severity_low: 'Niedrig',
        status_open: 'Offen',
        status_dismissed: 'Verworfen',
        status_remediated: 'Behoben',
        dismiss_label: 'Verwerfen',
        remediate_label: 'Als behoben markieren',
        note_label: 'Notiz (optional)',
        error_generic: 'Schleichende Rechteausweitung konnte nicht geprüft werden. Versuchen Sie es später erneut.',
        advisory_banner: 'Diese Befunde sind beratend. Forseti widerruft niemals automatisch Berechtigungen — besprechen Sie sie mit dem Rolleninhaber, bevor Sie handeln.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Automatische Zuordnung von Compliance-Regeln',
            subtitle: 'Forseti geht ein installiertes Regulierungspaket durch und schlägt neue oder aktualisierte Compliance-Regeln vor, die auf den Quellartikeln basieren.',
            pack_picker_label: 'Regulierungspaket',
            pack_picker_hint: 'Wählen Sie ein installiertes Paket zum Zuordnen aus.',
            run_label: 'Karte aus der Packung',
            running: 'Kartierung…',
            results_heading: '{count} Vorschlag(e)',
            tokens_label: '≈ {count} Token aus Ihrem monatlichen Bucket',
            no_suggestions: 'Keine Vorschläge – der Inhalt des Pakets ist möglicherweise bereits vollständig von den bestehenden Regeln abgedeckt.',
            kind_new: 'Neue Regel',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Keine Änderung erforderlich',
            col_kind: 'Freundlich',
            col_title: 'Vorgeschlagener Titel',
            col_existing: 'Bestehende Regel',
            col_source: 'Quelle',
            proposed_body_label: 'Vorgeschlagener Körper',
            source_quote_label: 'Quellenangabe',
            accept_selected: 'Ausgewählte akzeptieren',
            reject_all: 'Alles ablehnen',
            accept_all: 'Akzeptiere alles',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Die Regelzuordnung konnte nicht ausgeführt werden. Versuchen Sie es erneut oder überprüfen Sie den Paketcode.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Sitzungsübersicht',
            access_log: 'Zugriffsprotokoll',
            justification_review: 'Begründungsprüfung',
            investigations: 'Untersuchungen',
            pattern_analysis: 'Musteranalyse',
            anchoring_status: 'Verankerungsstatus',
            configuration: 'Konfiguration',
            access_policies: 'Zugriffsrichtlinien',
            review_campaigns: 'Überprüfungskampagnen',
            evidence_packages: 'Nachweispakete',
        },
        session_dashboard: {
            title: 'Sitzungsübersicht',
            loading: 'Übersichtsdaten werden geladen...',
            total_sessions: 'Sitzungen gesamt',
            active_sessions: 'Aktive Sitzungen',
            avg_duration: 'Durchschn. Dauer',
            peak_day_sessions: 'Aktivster Tag',
            auth_method_breakdown: 'Aufschlüsselung nach Authentifizierungsmethode',
            geo_country_breakdown: 'Aufschlüsselung nach Land',
            recent_session_events: 'Letzte Sitzungsereignisse',
            user_id: 'Benutzer-ID',
            session_id: 'Sitzungs-ID',
            ip_address: 'IP-Adresse',
            auth_method: 'Authentifizierungsmethode',
            started_at: 'Gestartet am',
            event_type: 'Ereignistyp',
            all_event_types: 'Alle Ereignistypen',
            filter_by_user: 'Nach Benutzer filtern',
            no_active_sessions: 'Keine aktiven Sitzungen',
            no_session_events: 'Keine Sitzungsereignisse gefunden',
            revoke: 'Widerrufen',
            revoke_session: 'Sitzung widerrufen',
            aria_revoke_session: 'Sitzung {user} widerrufen',
            revoke_reason: 'Grund',
            revoke_placeholder: 'Geben Sie einen Grund für den Widerruf dieser Sitzung an...',
            session_label: 'Sitzung:',
            load_failed: 'Sitzungsübersicht konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
            events_load_failed: 'Sitzungsereignisse konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
            revoke_failed: 'Sitzung konnte nicht widerrufen werden',
            session_events_loaded: '{n} Sitzungsereignisse geladen',
            event_types: {
                session_started: 'Sitzung gestartet',
                session_refreshed: 'Sitzung aktualisiert',
                session_expired: 'Sitzung abgelaufen',
                session_revoked: 'Sitzung widerrufen',
                session_terminated: 'Sitzung beendet',
            },
        },
        access_log: {
            title: 'Zugriffsprotokoll',
            user_id: 'Benutzer ID',
            resource: 'Ressource',
            action: 'Aktion',
            ip_address: 'IP Adresse',
            timestamp: 'Zeitstempel',
            no_logs: 'Keine Zugriff Protokoll Einträge gefunden.',
            filter_by_user: 'Filtern nach Benutzer ID...',
            load_failed: 'Fehler beim load Zugriff Protokoll. Bitte versuchen Sie es erneut.',
            export_success: 'Exportieren initiated successfully',
            export_failed: 'Fehler beim Export Zugriff Protokoll',
            record_count: 'Datensatz',
            record_count_plural: 'Datensätze',
            unclassified: 'Nicht klassifiziert',
            classification_missing: 'Klassifizierung fehlt',
            classification_level: 'Klassifizierungsstufe',
            response_code: 'Antwortcode',
            access_surface: 'Zugriffskanal',
            api_key: 'API-Schlüssel',
            classification_levels: {
                public: 'Oeffentlich',
                internal: 'Intern',
                confidential: 'Vertraulich',
                restricted: 'Eingeschraenkt',
                unclassified: 'Nicht klassifiziert',
            },
        },
        justification_review: {
            title: 'Begründungsprüfung',
            user_id: 'Benutzer ID',
            resource: 'Ressource',
            justification: 'Begründung',
            submitted_at: 'Eingereicht At',
            reviewer: 'Prüfer',
            no_reviews: 'Keine Begründung Überprüfungen gefunden.',
            load_failed: 'Fehler beim load Begründungen. Bitte versuchen Sie es erneut.',
            review_failed: 'Fehler beim Überprüfung Begründung',
            decline_failed: 'Fehler beim decline Zugriff',
            justification_count: 'Begründung',
            justification_count_plural: 'Begründungen',
            reviewed: 'Überprüft',
            concerning: 'Besorgniserregend',
            escalated: 'Eskaliert',
            link_investigation_placeholder: 'Verknüpfen zu ein Untersuchung (optional)',
            review_queue: 'Überprüfen Warteschlange',
            access_id_label: 'Zugangs-ID',
            access_id_placeholder: 'Zugriffsprotokoll-Eintrags-ID',
            resource_type_label: 'Ressourcentyp',
            resource_type_placeholder: 'z.B. document, database',
            resource_id_label: 'Ressourcen-ID',
            resource_id_placeholder: 'Ressourcenkennung',
            reason_placeholder: 'Grund für den Zugriff auf diese Ressource...',
            create_failed: 'Fehler beim create justification',
            unclassified: 'Nicht klassifiziert',
            review_statuses: {
                pending: 'Ausstehend',
                reviewed: 'Bewertet',
                concerning: 'Betreffend',
                escalated: 'Eskaliert',
            },
            reason_codes: {
                business_need: 'Geschäftsbedarf',
                legal_obligation: 'Gesetzliche Verpflichtung',
                data_subject_request: 'Anfrage einer betroffenen Person',
                audit_requirement: 'Audit-Anforderung',
                incident_response: 'Reaktion auf Vorfälle',
                research: 'Forschung',
                administrative: 'Administrativ',
                other: 'Andere',
            },
            classification_level: 'Klassifizierungsstufe',
            review_decision: 'Überprüfungsentscheidung',
        },
        investigations: {
            title: 'Untersuchung Workspace',
            new: 'Neu Untersuchung',
            subject: 'Betreff',
            investigator: 'Ermittler',
            opened_at: 'Geöffnet At',
            findings: 'Ergebnisse',
            no_investigations: 'Keine Untersuchungen gefunden.',
            load_failed: 'Fehler beim load Untersuchungen. Bitte versuchen Sie es erneut.',
            create_failed: 'Fehler beim create Untersuchung',
            timeline_add_failed: 'Fehler beim add Zeitverlauf Eintrag',
            link_access_failed: 'Fehler beim link Zugriff IDs',
            link_justification_failed: 'Fehler beim link Begründung IDs',
            close_failed: 'Fehler beim close Untersuchung',
            export_success: 'Exportieren initiated successfully',
            export_failed: 'Fehler beim Export Untersuchung',
            refresh_failed: 'Fehler beim refresh Untersuchung Detail. Bitte versuchen Sie es erneut.',
            update_failed: 'Fehler beim Aktualisieren der Untersuchung',
            investigation_count: 'Untersuchung',
            investigation_count_plural: 'Untersuchungen',
            title_placeholder: 'Untersuchung title',
            description_placeholder: 'Detailed Beschreibung von der Untersuchung...',
            assignee_placeholder: 'Benutzer ID von assignee (optional)',
            timeline_action_placeholder: 'e.g., Überprüft Zugriff logs',
            timeline_note_placeholder: 'Optionaler Hinweis',
            access_ids_placeholder: 'Zugriffs-ID-1, Zugriffs-ID-2',
            justification_ids_placeholder: 'Begründungs-ID-1, Begründungs-ID-2',
            findings_placeholder: 'Zusammenfassen der Untersuchung Ergebnisse...',
            by_actor: 'by',
            close_investigation: 'Schließen Untersuchung',
            add_timeline_failed: 'Fehler beim add Zeitverlauf Eintrag..',
            fetch_failed: 'Fehler beim fetch Untersuchungen..',
            change_status: 'Untersuchungsstatus ändern',
            details: 'Untersuchungsdetails',
            link_evidence: 'Beweis verknüpfen',
            link_justifications: 'Begründungen verknüpfen',
            access_ids_label: 'Zugangs-IDs',
            justification_ids_label: 'Begründungs-IDs',
            access_ids_link_placeholder: 'Zugriffs-ID-1, Zugriffs-ID-2',
            justification_ids_link_placeholder: 'Begründungs-ID-1, Begründungs-ID-2',
            investigations_loaded: '{n} investigations geladen',
            status_changed_to: 'Status geändert auf {status}',
            add_timeline_entry: 'Zeitleisteneintrag hinzufügen',
            add_access_ids: 'Zugangs-IDs hinzufügen',
            add_justification_ids: 'Begründungs-IDs hinzufügen',
            investigation_types: {
                anomaly_review: 'Anomalieüberprüfung',
                complaint_driven: 'Beschwerdeorientiert',
                scheduled_review: 'Geplante Überprüfung',
                whistleblower_report: 'Whistleblower-Bericht',
            },
            severity_labels: {
                low: 'Niedrig',
                medium: 'Mittel',
                high: 'Hoch',
                critical: 'Kritisch',
            },
            status_labels: {
                open: 'Offen',
                in_progress: 'In Bearbeitung',
                pending_review: 'Ausstehende Überprüfung',
                closed_substantiated: 'Abgeschlossen (begründet)',
                closed_unsubstantiated: 'Geschlossen (unbegründet)',
                closed_inconclusive: 'Geschlossen (nicht schlüssig)',
            },
            disposition_labels: {
                substantiated: 'Begründet',
                unsubstantiated: 'Unbegründet',
                inconclusive: 'Nicht schlüssig',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Musteranalyse',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Lade Muster Daten...',
            anomalies_detected: 'Anomalien Erkannt',
            patterns: 'Muster',
            by_user: 'By Benutzer',
            by_resource: 'By Ressource',
            time_range: 'Zeit Range',
            load_failed: 'Fehler beim analyze patterns. Bitte versuchen Sie es erneut.',
            top_resources: 'Top 10',
            access_by_hour: 'Zugriff nach Hour von Tag',
            access_by_day: 'Zugriff nach Tag von Week',
            fetch_failed: 'Fehler beim fetch Muster analysis..',
            analysis_loaded: 'Musteranalyse für {user} geladen, {n} Sitzungen gefunden',
            date_format_placeholder: 'TT/MM/JJJJ',
            access_by_hour_heading: 'Zugriff nach Tageszeit',
            access_by_day_heading: 'Zugriff nach Wochentag',
            by_classification: 'Nach Klassifizierungsstufe',
            top_accessed_resources: 'Am häufigsten aufgerufene Ressourcen',
            user_access_summary: 'Benutzerzugriffszusammenfassung',
            days: {
                sunday: 'Sonntag',
                monday: 'Montag',
                tuesday: 'Dienstag',
                wednesday: 'Mittwoch',
                thursday: 'Donnerstag',
                friday: 'Freitag',
                saturday: 'Samstag',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Verankerungsstatus',
            loading: 'Lade anchoring Daten...',
            anchored: 'Verankert',
            pending: 'Ausstehend',
            failed: 'Fehlgeschlagen',
            last_anchor: 'Letzter Anker',
            no_records: 'Keine anchoring Datensätze gefunden.',
            status_load_failed: 'Fehler beim load anchoring Status. Bitte versuchen Sie es erneut.',
            proofs_load_failed: 'Fehler beim load Zugriff Nachweise. Bitte versuchen Sie es erneut.',
            verify_failed: 'Fehler beim verify Nachweis Kette. Bitte versuchen Sie es erneut.',
            anchor_failed: 'Fehler beim Anker Zugriff Nachweis',
            batch_anchor_failed: 'Fehler beim batch Anker Zugriff Nachweise',
            proof_count: 'Nachweis',
            proof_count_plural: 'Nachweise',
            anchor_single: 'Anker Single',
            anchor_batch: 'Anker Stapel',
            access_id_placeholder: 'Eingeben Zugriff ID zu Anker',
            batch_ids_placeholder: 'Zugriffs-ID-1, Zugriffs-ID-2, Zugriffs-ID-3',
            verify_chain: 'Verifizieren Kette',
            chain_breaks: 'Kette Breaks',
            total_checked: 'Gesamt Checked',
            anchor_placeholder: 'Eingeben Zugriff ID zu Anker',
            fetch_failed: 'Fehler beim fetch anchoring Status..',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Fehler beim fetch Nachweise..',
            access_id: 'Zugangs-ID',
            chain_sequence: 'Kettensequenz',
            proof_hash_label: 'Beweis-Hash',
            batch_id: 'Batch-ID',
            proofs_loaded: '{n} access proofs geladen',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Ausstehend',
                confirmed: 'Bestätigt',
                failed: 'Fehlgeschlagen',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Kette',
                blockchain_anchor: 'Blockchain-Anker',
                zkp_stub: 'ZKP Stub',
            },
        },
        configuration: {
            title: 'Konfiguration',
            session_settings: 'Sitzung Einstellungen',
            log_retention: 'Protokoll Aufbewahrung (days)',
            anomaly_threshold: 'Anomalie Schwellenwert',
            anchoring_enabled: 'Verankerung Aktiviert',
            save_config: 'Speichern Konfiguration',
            load_failed: 'Fehler beim load Konfiguration. Bitte versuchen Sie es erneut.',
            save_success: 'Konfiguration saved successfully',
            save_failed: 'Fehler beim save Konfiguration. Bitte versuchen Sie es erneut.',
            justification_rules: 'Begründung Regeln',
            reason_codes: 'Grund Codes',
            anchoring_settings: 'Verankerung Einstellungen',
            anchoring_method: 'Verankerung Methode',
            anchoring_frequency: 'Verankerung Häufigkeit',
            batch_interval: 'Stapel Interval (minutes)',
            eligibility_criteria: 'Berechtigung Kriterien',
            add_criterion_placeholder: 'Hinzufügen eligibility criterion...',
            retention_settings: 'Aufbewahrung Einstellungen',
            standard_retention: 'Standard Aufbewahrung (days)',
            sensitive_retention: 'Sensibel Aufbewahrung (days)',
            working_hours: 'Arbeits Hours',
            work_start: 'Starten Zeit',
            work_end: 'Ende Zeit',
            timezone: 'Zeitzone',
            timezone_placeholder: 'z.B. Asia/Ho_Chi_Minh',
            working_days: 'Arbeits Tage',
            concurrent_sessions: 'Gleichzeitig Sitzung Limits',
            concurrent_threshold: 'Gleichzeitig Sitzung Schwellenwert',
            classification_level: 'Klassifizierung Stufe',
            prompt_mode: 'Aufforderung Modus',
            grace_period: 'Toleranz Zeitraum (s)',
            code_placeholder: 'Code',
            is_custom: 'Benutzerdefiniert',
            label_placeholder: 'Bezeichnung',
            description_placeholder: 'Beschreibung',
            custom: 'Benutzerdefiniert',
            fetch_failed: 'Fehler beim fetch Konfiguration..',
            classification_levels: {
                public: 'Öffentlich',
                internal: 'Intern',
                confidential: 'Vertraulich',
                restricted: 'Eingeschränkt',
                unclassified: 'Nicht klassifiziert',
            },
            prompt_modes: {
                reason_only: 'Nur Grund',
                reason_text: 'Grund + Text',
                reason_text_supervisor: 'Grund + Text + Vorgesetzter',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Kette',
                blockchain_anchor: 'Blockchain-Anker',
                zkp_stub: 'ZKP Stub',
            },
            anchoring_frequencies: {
                realtime: 'Echtzeit',
                batched: 'Gestapelt',
                on_demand: 'Auf Anfrage',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Zugriffsrichtlinien',
            create_policy: 'Zugriffsrichtlinie erstellen',
            policy_name: 'Richtlinienname',
            framework_code: 'Rahmenwerk',
            select_framework: 'Rahmenwerk auswählen...',
            review_frequency: 'Überprüfungshäufigkeit',
            scope: 'Geltungsbereich',
            no_policies_found: 'Keine Zugriffsrichtlinien gefunden.',
            policy_created: 'Zugriffsrichtlinie erstellt',
            frequency_labels: {
                monthly: 'Monatlich',
                quarterly: 'Vierteljährlich',
                semi_annual: 'Halbjährlich',
                annual: 'Jährlich',
            },
        },
        review_campaigns: {
            title: 'Zugriffsüberprüfungskampagnen',
            create_campaign: 'Überprüfungskampagne erstellen',
            reviewer: 'Prüfer',
            due_date: 'Fälligkeitsdatum',
            certify: 'Zertifizieren',
            no_campaigns_found: 'Keine Zugriffsüberprüfungskampagnen gefunden.',
            status_open: 'Offen',
            status_in_review: 'In Überprüfung',
            status_completed: 'Abgeschlossen',
            campaign_created: 'Prüfungskampagne erstellt',
            review_certified: 'Zugangsprüfung zertifiziert',
            certify_campaign: 'Kampagne {id} zertifizieren',
            policy_id: 'Richtlinien-ID',
            select_policy: 'Richtlinie auswählen...',
            reviewer_ids_label: 'Prüfer',
            select_reviewers: 'Prüfer auswählen...',
            status_labels: {
                open: 'Offen',
                in_review: 'In Überprüfung',
                in_progress: 'In Arbeit',
                cancelled: 'Abgebrochen',
                completed: 'Abgeschlossen',
            },
        },
        evidence_packages: {
            title: 'Nachweispakete',
            generate_package: 'Nachweispaket generieren',
            export_type: 'Exporttyp',
            package_type: 'Pakettyp',
            framework_scope: 'Rahmenwerksumfang',
            period: 'Zeitraum',
            generated_by: 'Generiert von',
            generated_at: 'Generiert am',
            no_packages_found: 'Keine Nachweispakete gefunden.',
            package_generated: 'Beweispaket generiert',
            export_type_full: 'Vollständiger Export',
            export_type_summary: 'Zusammenfassung',
            export_type_certification_only: 'Nur Zertifizierung',
            package_type_access_review: 'Zugangsprüfung',
            package_type_investigation: 'Untersuchung',
            package_type_certification: 'Zertifizierung',
            package_type_authority_request: 'Behördenanfrage',
            period_start: 'Beginn des Zeitraums',
            period_end: 'Ende des Zeitraums',
            select_frameworks: 'Rahmenwerke auswählen...',
            completeness_col: 'Vollständigkeit',
            status_col: 'Status',
            generated_by_col: 'Erstellt von',
        },
        evidence_package_detail: {
            title: 'Beweispaketdetails',
            not_found: 'Beweispaket nicht gefunden.',
            package_metadata: 'Paketmetadaten',
            status: 'Status',
            item_count: 'Anzahl Elemente',
            disposition: 'Disposition',
            closed_at: 'Geschlossen am',
            completeness_dashboard: 'Vollständigkeitsübersicht',
            no_completeness_data: 'Keine Vollständigkeitsdaten verfügbar.',
            controls_covered: 'Kontrollen abgedeckt',
            missing: 'Fehlend',
            sign_off_tracker: 'Freigabeverfolgung',
            stage_framework_owner: 'Rahmenwerkeigentümer',
            stage_legal_compliance: 'Recht und Compliance',
            stage_internal_audit: 'Interne Revision',
            approved: 'Genehmigt',
            returned: 'Zurückgegeben',
            pending: 'Ausstehend',
            approve: 'Genehmigen',
            return_btn: 'Zurückgeben',
            sign_off_submitted: 'Freigabe eingereicht',
            artifacts: 'Artefakte',
            no_artifacts: 'Keine Artefakte gefunden.',
            template_id: 'Vorlagen-ID',
            description: 'Beschreibung',
            classification: 'Klassifikation',
            evidence_type: 'Beweistyp',
            framework_section: 'Rahmenabschnitt',
            history: 'Genehmigungshistorie',
            expand: 'Erweitern',
            collapse: 'Zuklappen',
            action: 'Aktion',
            actor: 'Akteur',
            timestamp: 'Zeitstempel',
            comment: 'Kommentar',
            close_case: 'Fall schließen',
            confirm_close: 'Schließen bestätigen',
            closure_notes: 'Abschlussnotizen',
            disposition_compliant: 'Konform',
            disposition_non_compliant: 'Nicht konform',
            disposition_partial: 'Teilweise',
            case_closed: 'Beweispaket geschlossen',
            export_package: 'Paket exportieren',
            export_complete: 'Beweispaket exportiert',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Übersicht',
            properties: 'Eigenschaften',
            groups: 'Gruppen',
            bindings: 'Bindungen',
            change_requests: 'Änderungsanträge',
            comparison: 'Vergleich',
            inconsistencies: 'Inkonsistenzen',
            snapshot_schedules: 'Momentaufnahmepläne',
            golden_templates: 'Goldene Vorlagen',
            evidence_packs: 'Nachweispakete',
            audit: 'Prüfung',
        },
        select_property: 'Eigenschaft auswählen…',
        select_policy: 'Richtlinie auswählen…',
        select_properties: 'Eigenschaften auswählen…',
        select_target_properties: 'Zieleigenschaften auswählen…',
        select_owner: 'Eigentümer auswählen…',
        select_technical_contact: 'Technischen Kontakt auswählen…',
        property_statuses: {
            active: 'Aktiv',
            inactive: 'Inaktiv',
            pending: 'Ausstehend',
            decommissioned: 'Stillgelegt',
        },
        dashboard: {
            title: 'Übergreifende Eigenschaftsübersicht',
            loading: 'Lade Dashboard Daten...',
            total_properties: 'Gesamt Eigenschaften',
            active_properties: 'Aktiv Eigenschaften',
            snapshot_coverage: 'Snapshot-Abdeckung',
            properties_covered: 'Eigenschaften abgedeckt',
            total_bindings: 'Bindungen insgesamt',
            deployed_bindings: 'Bereitgestellt',
            pending_bindings: 'Ausstehende Bereitstellung',
            deployment_progress: 'Bereitstellungsfortschritt',
            on_target: 'Im Ziel',
            below_target: 'Unter Ziel',
            target_label: 'Ziel',
            total_groups: 'Gesamt Gruppen',
            pending_changes: 'Ausstehend Änderungen',
            inconsistencies: 'Inkonsistenzen',
            recent_activity: 'Kürzliche Aktivität',
            no_recent_activity: 'Keine kürzlichen Aktivitäten.',
            fetch_error: 'Fehler beim load Dashboard. Bitte versuchen Sie es erneut.',
        },
        properties: {
            title: 'Eigenschaftsregister',
            new: 'Neu Eigenschaft',
            edit: 'Bearbeiten Eigenschaft',
            property_name: 'Bezeichnung',
            property_type: 'Typ',
            identifier: 'Kennung',
            environment: 'Umgebung',
            owner_id: 'Eigentümer ID',
            technical_contact_id: 'Technisch Kontakt ID',
            decommission: 'Außerbetriebnahme',
            confirm_decommission: 'Are you sure you want zu decommission dieses property?',
            no_properties: 'Keine Eigenschaften gefunden.',
            all_environments: 'Alle Environments',
            all_types: 'Alle Typen',
            cmp_instance_id: 'CMP Instanz-ID',
            deployment_date: 'Bereitstellungsdatum',
            identifier_placeholder: 'z. B. www.example.com oder com.example.app',
            owner_name: 'Eigentümer',
            technical_contact_name: 'Technischer Kontakt',
            sensitive_data_categories: 'Kategorien sensibler Daten',
            sensitive_data_categories_placeholder: 'Wählen Sie sensible Datenkategorien aus',
            requires_enhanced_review: 'Erfordert eine erweiterte Überprüfung',
            sensitive_review_status: 'Überprüfung sensibler Daten',
            sensitive_review_pending: 'Ausstehende Überprüfung',
            sensitive_review_approved: 'Genehmigt',
            sensitive_review_rejected: 'Abgelehnt',
            sensitive_review_submit: 'Bewertung abgeben',
            sensitive_review_approve: 'Genehmigen',
            sensitive_review_reject: 'Ablehnen',
            sensitive_review_notes: 'Überprüfen Sie die Notizen',
            sensitive_review_notes_placeholder: 'Bewertungsnotizen eingeben...',
            record_withdrawal: 'Auszahlung aufzeichnen',
            withdrawal_subject_id: 'Betreff-ID',
            withdrawal_consent_id: 'Einwilligungs-ID',
            withdrawal_reason: 'Grund',
            withdrawal_downstream_systems: 'Nachgelagerte Systeme',
            withdrawal_downstream_placeholder: 'System-1, System-2',
            withdrawal_success: 'Der Widerruf wurde erfolgreich weitergegeben.',
            withdrawal_error: 'Die Auszahlung konnte nicht verbreitet werden.',
            withdrawal_data_subject_id: 'ID der betroffenen Person',
            withdrawal_data_subject_id_placeholder: 'z. B. usr_abc123',
            withdrawal_consent_record_id: 'Einwilligungsdatensatz-ID',
            withdrawal_consent_record_id_placeholder: 'z. B. consent_xyz789',
            withdrawal_purposes: 'Zurückgezogene Zwecke',
            withdrawal_purposes_placeholder: 'Wählen Sie die Zwecke aus, zu denen Sie Ihre Einwilligung widerrufen möchten...',
            withdrawal_withdrawn_at: 'Auszahlungsdatum',
            withdrawal_tasks_created: 'Aufgaben erstellt',
            withdrawal_affected_bindings: 'Betroffene Bindungen',
            withdrawal_affected_activities: 'Betroffene Verarbeitungsaktivitäten',
            withdrawal_history: 'Auszahlungsverlauf',
        },
        groups: {
            title: 'Eigenschaftsgruppen',
            new: 'Neu Gruppe',
            group_name: 'Gruppe Name',
            member_count: 'Mitglieder',
            no_groups: 'Keine property groups gefunden.',
            policies: 'Richtlinien',
            confirm_delete: 'Sind Sie sicher, dass Sie diese Gruppe löschen möchten?',
        },
        bindings: {
            title: 'Bindungsmatrix',
            property: 'Eigenschaft',
            purpose: 'Zweck',
            legal_basis: 'Rechtlich Grundlage',
            bound: 'Gebunden',
            no_bindings: 'Keine bindings gefunden.',
            bind_policy: 'Bindungsrichtlinie',
            bulk_bind: 'Massenbindung',
            bind_policy_to_property: 'Richtlinie an Eigentum binden',
            bulk_bind_policy: 'Massenbindungsrichtlinie',
            unbound: 'Ungebunden',
            policy_id: 'Richtlinien-ID',
            version_placeholder: 'z. B. 1.0.0',
            property_ids_placeholder: 'Prop-ID-1, Prop-ID-2, Prop-ID-3',
            binding_name: 'Bindung',
            property_name: 'Eigenschaftsname',
            policy_name: 'Richtlinienname',
            bindings_list: 'Bindungsliste',
            binding_actions: {
                start_deploy: 'Bereitstellung starten',
                mark_deployed: 'Als bereitgestellt markieren',
                rollback: 'Zurückrollen',
            },
        },
        change_requests: {
            title: 'Änderungsantragsprozess',
            new: 'Neu Ändern Anfrage',
            requester: 'Antragsteller',
            change_type: 'Ändern Typ',
            property: 'Eigenschaft',
            submitted_at: 'Eingereicht At',
            no_requests: 'Keine change requests gefunden.',
            fetch_pipeline_error: 'Fehler beim fetch Pipeline. Bitte versuchen Sie es erneut.',
            fetch_list_error: 'Fehler beim fetch change requests. Bitte versuchen Sie es erneut.',
            create_error: 'Fehler beim create change request. Bitte versuchen Sie es erneut.',
            action_error: 'Fehler beim perform Aktion. Bitte versuchen Sie es erneut.',
            approver_name: 'Genehmiger',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Alle',
            view_my_properties: 'Meine Eigenschaften',
            stage_requests: '{stage}-Anfragen',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Vergleichsarbeitsbereich',
            select_properties: 'Auswählen Eigenschaften zu Vergleichen',
            compare: 'Vergleichen',
            no_comparison: 'Auswählen bei least two Eigenschaften zu compare.',
            fetch_error: 'Fehler beim fetch comparison reports. Bitte versuchen Sie es erneut.',
            run_error: 'Fehler beim Durchlauf comparison. Bitte versuchen Sie es erneut.',
            save_notes_error: 'Fehler beim save Hinweise. Bitte versuchen Sie es erneut.',
            scan_complete: 'Inkonsistenz Scan completed successfully.',
            scan_error: 'Fehler beim Durchlauf inconsistency Scan. Bitte versuchen Sie es erneut.',
            min_two_properties: 'Bitte provide bei least 2 property IDs für comparison.',
            view_inconsistencies: 'Inkonsistenzen anzeigen',
            click_to_select: 'Klicken Sie, um zwei oder mehr Eigenschaften auszuwählen',
            no_properties_available: 'Keine Eigenschaften verfügbar',
            no_matching_properties: 'Keine übereinstimmenden Eigenschaften',
            select_at_least_two_props: 'Wählen Sie mindestens zwei Eigenschaften aus der Dropdown-Liste.',
            no_differences_found: 'Keine Unterschiede zwischen den ausgewählten Eigenschaften gefunden.',
            found_inconsistencies: '{count} Inkonsistenzen in {properties} Eigenschaften und {bindings} Bindungen gefunden.',
        },
        inconsistencies: {
            title: 'Inkonsistenzliste',
            property: 'Eigenschaft',
            field: 'Feld',
            expected: 'Erwartet',
            actual: 'Tatsächlich',
            severity: 'Schweregrad',
            no_inconsistencies: 'Keine inconsistencies gefunden.',
            trend: 'Inkonsistenztrend',
            trend_chart_label: 'Inkonsistenz-Trenddiagramm, das die Anzahl im Zeitverlauf zeigt',
            acknowledge: 'Bestätigen',
            resolve: 'Lösen',
            suppress: 'Unterdrücken',
            cr_id_placeholder: 'CR-Kennung',
            view_all: 'Alle',
            view_my_properties: 'Meine Eigenschaften',
            view_dpo_review: 'DPO Überprüfungswarteschlange',
            change_request_id_required: 'Um diese Inkonsistenz zu beheben, ist eine Änderungsanforderungs-ID erforderlich.',
        },
        snapshot_schedules: {
            title: 'Momentaufnahmepläne',
            new: 'Neu Planen',
            schedule_name: 'Planen Name',
            frequency: 'Häufigkeit',
            next_run: 'Nächster Ausführen',
            last_run: 'Letzter Ausführen',
            no_schedules: 'Keine Snapshot Zeitpläne gefunden.',
            target_type: 'Zieltyp',
            target_id: 'Ziel-ID',
            custom_frequency_days: 'Benutzerdefinierte Häufigkeit (Tage)',
            time_of_day: 'Tageszeit',
            time: 'Zeit',
            retention_days: 'Aufbewahrung (Tage)',
            execute_now: 'Jetzt ausführen',
            placeholder_days: 'z.B. 10',
            target_name: 'Zielname',
        },
        golden_templates: {
            title: 'Goldene Vorlagen',
            new: 'Neu Vorlage',
            template_name: 'Vorlage Name',
            property_type: 'Eigenschaft Typ',
            no_templates: 'Keine golden Vorlagen gefunden.',
            applicable_group_ids: 'Anwendbare Gruppen-IDs (durch Kommas getrennt)',
            applicable_groups: 'Anwendbare Gruppen',
            purposes: 'Zwecke',
            legal_bases: 'Rechtliche Grundlagen',
            banner_config: 'Bannerkonfiguration',
            consent_settings: 'Einwilligungseinstellungen',
            group_ids_placeholder: 'Gruppen-ID-1, Gruppen-ID-2',
            apply_to_property: 'Auf Eigenschaft anwenden',
            apply_to_group: 'Auf Gruppe anwenden',
            apply_success: 'Vorlage erfolgreich angewendet.',
            applications: 'Anwendungsverlauf',
            no_applications: 'Noch keine Vorlagenanwendungen.',
            no_configuration_details: 'Vorlage hat keine Konfigurationsdetails. Bearbeiten Sie die Vorlage, um Zwecke, Rechtsgrundlagen, Banner-Einstellungen oder Einwilligungseinstellungen hinzuzufügen.',
        },
        rollouts: {
            title: 'Vorlagen-Rollouts',
            rollout_status: 'Rollout-Status',
            confirmed: 'Bestätigt',
            pending: 'Ausstehend',
            failed: 'Fehlgeschlagen',
            confirm: 'Eigenschaft bestätigen',
            confirm_success: 'Eigenschaft im Rollout bestätigt.',
            can_close: 'Alle Eigenschaften bestätigt — Rollout abgeschlossen',
            cannot_close: 'Warte auf Eigenschaftsbestätigungen',
            no_rollouts: 'Keine Rollouts gefunden.',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            cancelled: 'Abgebrochen',
            reject: 'Eigenschaft ablehnen',
            reject_success: 'Eigenschaft im Rollout abgelehnt.',
            confirm_all: 'Alle bestätigen',
            reject_all: 'Alle ablehnen',
            confirm_label: 'Rollout bestätigen für',
            reject_label: 'Rollout ablehnen für',
            confirm_all_label: 'Alle ausstehenden Rollouts bestätigen',
            reject_all_label: 'Alle ausstehenden Rollouts ablehnen',
            no_properties_in_rollout: 'Diese Einführung hat keine Eigenschaften.',
        },
        analytics: {
            title: 'Eigenschaftsanalyse',
            opt_in_rate: 'Opt-in-Rate',
            opt_out_rate: 'Opt-out-Rate',
            withdrawal_rate: 'Widerrufsrate',
            total_consents: 'Gesamte Einwilligungen',
            total_withdrawals: 'Gesamte Widerrufe',
            compliance_score: 'Compliance-Bewertung',
            non_compliant: 'Nicht-konforme Eigenschaften',
            no_non_compliant: 'Alle Eigenschaften sind konform.',
            threshold: 'Compliance-Schwellenwert',
            property_id: 'Immobilien-ID',
            compliant: 'Konform',
            non_compliant_status: 'Nicht konform',
            compliance_help_summary: 'Wie wird der Compliance-Score berechnet und wie behebe ich ihn?',
            compliance_help_intro: 'Eine Eigenschaft wird als nicht konform gekennzeichnet, wenn ihre Bewertung unter 80 % liegt. Die Punktzahl wird wie folgt berechnet:',
            compliance_help_with_template_label: 'Wenn ein Golden Template angewendet wird:',
            compliance_help_with_template_text: '% der Konfigurationsschlüssel (Zwecke, legal_bases, banner_config, consent_settings), die genau mit der Vorlage übereinstimmen.',
            compliance_help_no_template_label: 'Wenn kein Golden Template angewendet wird:',
            compliance_help_no_template_text: '% der Bindungen (der Eigenschaft zugewiesene Einwilligungstypen) mit deployment_status = bereitgestellt. Eine Eigenschaft ohne Bindungen hat standardmäßig den Wert 100 %.',
            compliance_help_fix_intro: 'Um eine Eigenschaft aus dieser Liste zu entfernen, führen Sie einen der folgenden Schritte aus:',
            compliance_help_fix_deploy_label: 'Stellen Sie seine Bindungen bereit',
            compliance_help_fix_deploy_text: 'Öffnen Sie die Seite „Bindungsmatrix“ und setzen Sie den Bereitstellungsstatus jeder Bindung auf „Bereitgestellt“.',
            compliance_help_fix_template_label: 'Wenden Sie eine goldene Vorlage an',
            compliance_help_fix_template_text: 'Verwenden Sie „Auf Gruppe anwenden“ in der Vorlage und bestätigen Sie dann das Rollout für diese Eigenschaft. Stellen Sie sicher, dass die Konfiguration der Eigenschaft mit der Vorlage übereinstimmt.',
            compliance_help_fix_decommission_label: 'Entsorgen Sie die Immobilie',
            compliance_help_fix_decommission_text: 'Verwenden Sie die Schaltfläche „Außerbetriebnahme“ im Detailfenster der Immobilie, wenn sie nicht mehr verwendet wird.',
            compliance_help_fix_no_bindings_label: 'Entfernen Sie alle Bindungen',
            compliance_help_fix_no_bindings_text: 'Wenn eine Eigenschaft keine Vorlage und keine Bindungen hat, wird sie standardmäßig mit 100 % bewertet.',
        },
        evidence_packs: {
            title: 'Nachweispakete',
            new: 'Neu Paket',
            pack_name: 'Paket Name',
            properties: 'Eigenschaften',
            generated_at: 'Generiert At',
            no_packs: 'Keine Nachweis packs gefunden.',
            generate: 'Generieren',
            content_type_consent_records: 'Einwilligung Datensätze',
            content_type_policy_versions: 'Richtlinie Versionen',
            content_type_binding_history: 'Bindung Verlauf',
            content_type_change_requests: 'Ändern Anfragen',
            content_type_inconsistency_reports: 'Inkonsistenz Berichte',
            content_type_snapshot_data: 'Snapshot Daten',
            content_type_audit_trail: 'Prüfprotokoll',
        },
        audit: {
            title: 'Einwilligungseigenschaftsprüfung',
            event_type: 'Ereignis Typ',
            entity_type: 'Entität Typ',
            entity_id: 'Entität ID',
            actor: 'Akteur',
            timestamp: 'Zeitstempel',
            no_events: 'Keine Audit Ereignisse gefunden.',
            entity_type_property: 'Eigenschaft',
            entity_type_group: 'Gruppe',
            entity_type_binding: 'Bindung',
            entity_type_change_request: 'Änderungsanfrage',
            entity_type_golden_template: 'Goldene Vorlage',
            entity_type_snapshot_schedule: 'Snapshot-Zeitplan',
            entity_type_inconsistency: 'Inkonsistenz',
            entity_type_evidence_pack: 'Nachweispaket',
            action_created: 'Erstellt',
            action_updated: 'Aktualisiert',
            action_deleted: 'Gelöscht',
            action_decommissioned: 'Außer Betrieb',
            action_status_changed: 'Status geändert',
            action_submitted: 'Eingereicht',
            action_approved: 'Genehmigt',
            action_rejected: 'Abgelehnt',
            action_deployed: 'Bereitgestellt',
            action_rolled_back: 'Zurückgesetzt',
            action_paused: 'Pausiert',
            action_resumed: 'Fortgesetzt',
            action_cancelled: 'Abgebrochen',
            action_acknowledged: 'Bestätigt',
            action_resolved: 'Gelöst',
            action_suppressed: 'Unterdrückt',
            action_executed: 'Ausgeführt',
        },
        property_types: {
            website: 'Website',
            mobile_app: 'Mobile App',
            ott: 'OTT',
            iot_device: 'IoT-Gerät',
            kiosk: 'Kiosk',
            api_service: 'API-Dienst',
            sdk: 'SDK',
        },
        environments: {
            production: 'Produktion',
            staging: 'Inszenierung',
            development: 'Entwicklung',
            testing: 'Test',
        },
        group_types: {
            brand: 'Marke',
            region: 'Region',
            product_line: 'Produktlinie',
            business_unit: 'Geschäftseinheit',
            custom: 'Benutzerdefiniert',
        },
        target_types: {
            property: 'Eigenschaft',
            group: 'Gruppe',
            all: 'Alle',
        },
        frequencies: {
            hourly: 'Stündlich',
            daily: 'Täglich',
            weekly: 'Wöchentlich',
            monthly: 'Monatlich',
            custom: 'Benutzerdefiniert',
        },
        binding_statuses: {
            current: 'Aktuell',
            outdated: 'Veraltet',
            pending: 'Ausstehend',
            deploying: 'Wird bereitgestellt',
            rolled_back: 'Zurückgesetzt',
        },
        sensitive_categories: {
            health: 'Gesundheitsdaten',
            biometric: 'Biometrische Daten',
            genetic: 'Genetische Daten',
            racial_ethnic: 'Rasse/ethnische Herkunft',
            political_opinions: 'Politische Meinungen',
            religious_beliefs: 'Religiöse Überzeugungen',
            trade_union: 'Gewerkschaftsmitgliedschaft',
            sexual_orientation: 'Sexuelle Orientierung',
            criminal_records: 'Strafregister',
            financial: 'Finanzdaten',
            children: 'Kinderdaten',
            geolocation: 'Präzise Geolokalisierung',
        },
        export_csv: 'CSV exportieren',
        exporting: 'Exportiere...',
        page_x_of_y: 'Seite {page} von {total}',
        regulatory: {
            capture_method_guidance: 'PDPL erfordert einen Einwilligungsnachweis, der mit dem Erfassungskanal übereinstimmt. Eigenschaftstypen werden den Einwilligungserfassungsmethoden zugeordnet: Website/OTT = Banner-basierte Einwilligung, mobile App/SDK = In-App-Zustimmungsfluss, Kiosk = physische/Bildschirm-Zustimmungserfassung, API-Dienst = programmatische Einwilligung, IoT-Gerät = Einwilligungsmechanismus auf Geräteebene.',
            dashboard_metrics_info: 'Dieses Dashboard bietet dem DPO eine einzige Ansicht des Einwilligungsstatus über alle digitalen Eigenschaften hinweg. Zu den wichtigsten Kennzahlen gehören: Prozentsatz der Bindungsabdeckung (bereitgestellt im Vergleich zur Gesamtzahl der Bindungen), Einhaltung der Vorlage über das Inkonsistenzen-Widget, ausstehende Änderungsanforderungen, die überprüft werden müssen, und Aufschlüsselung des Eigenschaftsstatus, die Eigenschaften mit aktivem, inaktivem oder stillgelegtem Status anzeigt. Immobilien mit sensiblen Datenkategorien werden über das Immobilienregister verfolgt.',
            change_request_impact_info: 'Änderungsanträge, die sich auf Einwilligungseigenschaften auswirken, müssen die regulatorischen Auswirkungen dokumentieren: welche PDPL Anforderungen betroffen sind, ob die Änderung Auswirkungen auf die Verarbeitung sensibler Daten hat (Art. 2.4) und ob die Änderung eine erneute Einwilligung der betroffenen Personen erfordert. In der Zusammenfassung der Auswirkungen in jeder Änderungsanforderung sind die PDPL regulatorischen Referenzen für Prüfnachweise aufgeführt.',
            outdated_binding_warning: 'Bindungen mit dem Status „veraltet“ weisen darauf hin, dass eine Eigenschaft eine Richtlinienversion ausführt, die nicht aktuell ist. Diese Eigenschaften entsprechen möglicherweise nicht den neuesten PDPL-Anforderungen oder Aktualisierungen der Organisationsrichtlinien. Überprüfen und aktualisieren Sie veraltete Bindungen umgehend, um die PDPL-Konformität in allen Einwilligungseigenschaften aufrechtzuerhalten.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Übersicht',
            market_packs: 'Marktpakete',
            language_packs: 'Sprachpakete',
            frameworks: 'Regulatorische Rahmenwerke',
            mappings: 'Modulzuordnungen',
            submissions: 'Paketeinreichungen',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Übersetzungsvorschau',
            audit: 'Prüfprotokoll',
        },
        dashboard: {
            title: 'Lokalisierungsübersicht',
            loading: 'Lade Dashboard Daten...',
            active_market_packs: 'Aktiv Markt Pakete',
            published_packs_available: 'Veröffentlicht market packs available',
            language_packs: 'Sprache Pakete',
            total_language_packs: 'Gesamt language packs registered',
            active_regulatory_frameworks: 'Aktiv Regulatorisch Rahmenwerke',
            frameworks_active: 'Rahmenwerke mit aktiv Status',
            pending_pack_submissions: 'Ausstehend Paket Einreichungen',
            submissions_awaiting_review: 'Einreichungen awaiting Überprüfung',
            recent_audit_events: 'Kürzliche Audit Ereignisse',
            no_recent_audit: 'No recent Audit Ereignisse.',
            event_type: 'Ereignis Typ',
            entity_type: 'Entität Typ',
            entity_id: 'Entität ID',
            actor: 'Akteur',
            timestamp: 'Zeitstempel',
        },
        market_packs: {
            title: 'Marktpaketkatalog',
            new: 'Neu Markt Paket',
            pack_name: 'Paket Name',
            market: 'Markt',
            locale: 'Sprache',
            no_packs: 'Keine market packs gefunden.',
        },
        language_packs: {
            title: 'Sprachpakete',
            new: 'Neu Sprache Paket',
            language: 'Sprache',
            locale_code: 'Sprache Code',
            completeness: 'Vollständigkeit',
            completeness_detail: 'Vollständigkeitsdetails',
            overall_completeness: 'Gesamtvollständigkeit',
            total_keys: 'Gesamtschlüssel',
            translated_keys: 'Übersetzte Schlüssel',
            missing_keys: 'Fehlende Schlüssel',
            namespace_breakdown: 'Namensraumaufschlüsselung',
            namespace: 'Namensraum',
            translated: 'Übersetzt',
            no_packs: 'Keine language packs gefunden.',
            install_language_pack: 'Sprachpaket installieren',
            available_packs: 'Verfügbare Sprachpakete',
            install: 'Installieren',
            all_installed: 'Alle Sprachpakete sind bereits installiert.',
            install_success: 'Sprachpaket erfolgreich installiert.',
            market: 'Markt',
            featured: 'Hervorgehoben',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Regulatorische Rahmenwerke',
            new: 'Neu Rahmenwerk',
            framework_name: 'Rahmenwerk Name',
            jurisdiction: 'Gerichtsbarkeit',
            effective_date: 'Gültig Datum',
            no_frameworks: 'Keine regulatory frameworks gefunden.',
            view_requirements: 'Anforderungen anzeigen',
            hide_requirements: 'Anforderungen ausblenden',
            show_legacy: 'Legacy-Rahmenwerke anzeigen',
            effective_until: 'Gültig bis',
            effective_on: 'Gültig ab',
            replaced_by: 'Ersetzt durch',
            replaces_label: 'Ersetzt',
            status_active: 'Aktiv',
            status_draft: 'Entwurf',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Veraltet',
            status_upcoming: 'Bevorstehend',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Beobachtungsliste',
            watchlist_empty: 'Kein Entwurf oder bevorstehende Frameworks',
            watchlist_empty_desc: 'Zur Überwachung werden hier Frameworks mit dem Status „Entwurf“ oder „bevorstehend“ angezeigt.',
            expected_effective: 'Wirksam erwartet',
            effective_date_tbd: 'Datum des Inkrafttretens: TBD',
            activate: 'Aktivieren',
            ai_law_title: 'Vietnam AI Gesetz',
            ai_risk_classification: 'AI Risikoklassifizierung',
            ai_system_dossier: 'AI Systemdossier',
            ai_labeling_checklist: 'AI Kennzeichnung und Offenlegung',
            ai_incident_report: 'AI Vorfallbericht',
            ai_impact_assessment: 'AI Folgenabschätzung',
            ai_sandbox_application: 'Sandbox-Anwendung',
            ai_sandbox_monitoring: 'Sandbox-Überwachung',
            ai_sandbox_completion: 'Sandbox-Abschluss',
            ai_submission_tracker: 'Einreichungs-Tracker',
            ai_evidence_capture: 'Beweiserfassung',
            risk_level_minimal: 'Minimales Risiko',
            risk_level_limited: 'Begrenztes Risiko',
            risk_level_high: 'Hohes Risiko',
            risk_level_unacceptable: 'Inakzeptables Risiko',
            submission_status_draft: 'Entwurf',
            submission_status_submitted: 'Eingereicht',
            submission_status_returned: 'Zur Änderung zurückgeschickt',
            submission_status_accepted: 'Akzeptiert',
            submission_status_rejected: 'Abgelehnt',
            submission_status_closed: 'Geschlossen',
            ecommerce_law_title: 'Vietnamesisches E-Commerce-Gesetz',
            ecommerce_platform_classification: 'Plattformklassifizierung',
            ecommerce_seller_register: 'Verkäuferregister',
            ecommerce_complaint_register: 'Beschwerderegister',
            ecommerce_takedown_register: 'Takedown-Register',
            ecommerce_submission_tracking: 'Einreichungsverfolgung',
            ecommerce_dossier_checklist: 'Dossier-Checkliste',
            ecommerce_cross_border: 'Grenzüberschreitende Compliance',
            ecommerce_trustmark: 'Trustmark-Programm',
            ecommerce_notification: 'Benachrichtigung',
            ecommerce_registration: 'Registrierung',
            ecommerce_termination: 'Kündigung',
            ecommerce_reporting_readiness: 'Meldebereitschaft',
            ecommerce_evidence_capture: 'Beweiserfassung',
            submission_status_approved: 'Genehmigt',
            cybersec_law_2025_title: 'Vietnamesisches Cybersicherheitsgesetz (2025)',
            cybersec_system_classification: 'Systemklassifizierung',
            cybersec_risk_assessment: 'Risikobewertung',
            cybersec_incident_report: 'Vorfallbericht',
            cybersec_authority_cooperation: 'Behördenkooperation',
            cybersec_data_localization: 'Datenlokalisierung',
            cybersec_vendor_security: 'Anbietersicherheit',
            cybersec_submission_tracker: 'Einreichungs-Tracker',
            cybersec_evidence_capture: 'Beweiserfassung',
            cybersec_dossier_checklist: 'Dossier-Checkliste',
            cybersec_classification_general: 'Allgemein',
            cybersec_classification_important: 'Wichtig',
            cybersec_classification_critical: 'Kritisch',
            data_law_title: 'Vietnamesisches Datengesetz',
            data_inventory: 'Dateninventar',
            data_classification: 'Datenklassifizierung',
            data_sharing: 'Datenaustausch',
            data_governance: 'Daten-Governance',
            data_submission_tracker: 'Einreichungs-Tracker',
            data_evidence_capture: 'Beweiserfassung',
            data_dossier_checklist: 'Dossier-Checkliste',
            telecom_law_title: 'Vietnamesisches Telekommunikationsgesetz',
            telecom_authority_request: 'Anfrage der Behörde',
            telecom_disclosure: 'Offenlegung',
            telecom_confidentiality: 'Vertraulichkeit',
            telecom_submission_tracker: 'Einreichungs-Tracker',
            telecom_evidence_capture: 'Beweiserfassung',
            telecom_dossier_checklist: 'Dossier-Checkliste',
            pdpl_law_title: 'Vietnamesisches Gesetz zum Schutz personenbezogener Daten',
            pdpl_breach_notification: 'Benachrichtigung über Verstöße',
            pdpl_dpo_notification: 'DPO Terminbenachrichtigung',
            pdpl_data_subject_notification: 'Benachrichtigung der betroffenen Person',
            pdpl_submission_tracker: 'Einreichungs-Tracker',
            pdpl_evidence_capture: 'Beweiserfassung',
            pdpl_dossier_checklist: 'Dossier-Checkliste',
            pdpl_authority_filing: 'Einreichung bei der Behörde',
            pdpl_mops_filing: 'MoPS-Einreichung',
        },
        mappings: {
            title: 'Modulzuordnungen',
            new: 'Neu Zuordnung',
            module: 'Modul',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Zustimmung',
            module_dpia: 'DPIA',
            module_incident: 'Vorfall',
            module_rights_request: 'Rechteanfrage',
            module_data_mapping: 'Datenzuordnung',
            module_classification: 'Klassifizierung',
            module_monitoring: 'Überwachung',
            module_governance: 'Regierungsführung',
            module_vendor: 'Anbieter',
            module_risk: 'Risiko',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Markt Paket',
            mapping_status: 'Zuordnung Status',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Keine Modul Zuordnungen gefunden.',
            incomplete_entries_warning: '{count} Zuordnung(en) ausgeblendet wegen fehlender Framework-ID, Anforderungs-ID oder Modul.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Paketeinreichungen',
            new: 'Neu Einreichung',
            submitter: 'Einreicher',
            pack: 'Paket',
            submitted_at: 'Eingereicht At',
            no_submissions: 'Keine Paket Einreichungen gefunden.',
            installed_sector_overlays_title: 'Sektor-Overlays ({count})',
            installed_sector_overlays_aria: 'Installierte Sektor-Overlays',
            installed_col_name: 'Name',
            installed_col_code: 'Code',
            installed_col_sector: 'Sektor',
            installed_col_mode: 'Modus',
            installed_col_status: 'Status',
            installed_refresh: 'Aktualisieren',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Übersetzungsvorschau',
            select_locale: 'Auswählen Sprache',
            select_module: 'Auswählen Modul',
            key: 'Key',
            value: 'Wert',
            no_translations: 'Keine translations gefunden.',
        },
        audit: {
            title: 'Lokalisierung Audit Trail',
            event_type: 'Ereignis Typ',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Entität Typ',
            entity_id: 'Entität ID',
            entity_name: 'Entitätsname',
            actor: 'Akteur',
            timestamp: 'Zeitstempel',
            payload: 'Nutzlast',
            collapse_payload: 'Nutzlastdetails einklappen',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Nutzlastdetails ausklappen',
            collapse: 'Einklappen',
            expand: 'Ausklappen',
            all_entity_types: 'Alle Entitätstypen',
            no_events: 'Keine Audit Ereignisse gefunden.',
        },
        market_packs_detail: {
            installed_at: 'Installiert am',
            pack_details_label: 'Paketdetails',
            market_code: 'Marktcode',
            supported_locales: 'Unterstützte Gebietsschemata',
            min_platform_version: 'Mindest-Plattformversion',
            pack_information: 'Paketinformation',
            pack_contents: 'Packungsinhalt',
            included_frameworks: 'Enthaltene Regelwerke',
            pack_artifacts: 'Paket-Artefakte',
            no_artifacts: 'Keine Artefakte für dieses Paket gefunden',
            pack_not_found: 'Paket nicht gefunden',
        },
        mappings_form: {
            framework_id: 'Framework-ID',
            requirement_id: 'Anforderungs-ID',
            module_label: 'Modul',
            mapping_type: 'Zuordnungstyp',
            configuration_json: 'Konfiguration (JSON)',
            conflict_resolution: 'Konfliktlösung',
            is_override: 'Ist Überschreibung',
            all_modules: 'Alle Module',
            all_mapping_types: 'Alle Zuordnungstypen',
        },
        submissions_detail: {
            submission_id: 'Einreichungs-ID',
            validation: 'Validierung',
            reviewers: 'Prüfer',
            start_review: 'Überprüfung starten',
            hide_comments: 'Kommentare ausblenden',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Prüfungskommentare',
            loading_comments: 'Kommentare werden geladen...',
            no_comments: 'Noch keine Kommentare.',
            add_comment: 'Kommentar hinzufügen',
            reject_submission: 'Einreichung ablehnen',
            reason_for_rejection: 'Ablehnungsgrund',
            reviewer_ids: 'Prüfer-IDs (kommagetrennt)',
            submit_new_pack: 'Neues Paket einreichen',
            pack_type: 'Pakettyp',
            market_code: 'Marktcode',
            target_locales: 'Zielgebietsschemata',
            pack_file: 'Paketdatei',
            pack_format_info: 'Paketformat: Marktpakete sollten gebietsschemaspezifische JSON-Dateien mit Namespace-/Schlüsselübersetzungen enthalten. Sprachpakete sollten Übersetzungen für die angegebenen Zielgebietsschemas bereitstellen. Ein detailliertes Schema finden Sie in der Dokumentation.',
            accepted_formats: 'Akzeptierte Formate: .json oder .zip. JSON-Dateien sollten ein flaches oder namespaced-Objekt aus übersetzten Schlüssel-Wert-Paaren enthalten. ZIP-Dateien sollten eine oder mehrere JSON-Gebietsschemadateien enthalten.',
            comma_separated_locales: 'Kommagetrennte Gebietsschemacodes für dieses Sprachpaket',
            confirm_approval: 'Genehmigung bestätigen',
            approve_message: 'Diese Paketeinreichung genehmigen?',
            submission_label: 'Einreichung',
            all_pack_types: 'Alle Pakettypen',
            passed: 'bestanden',
            failed_label: 'fehlgeschlagen',
            market_code_required: 'Marktcode ist erforderlich',
            version_required: 'Version ist erforderlich',
            pack_type_regulatory: 'Regulatorisch',
            framework_code: 'Rahmenwerk-Code',
            jurisdiction_code: 'Zuständigkeitscode',
            pack_source: 'Quelle',
            pack_source_manual: 'Manuell',
            pack_version: 'Paketversion',
            install_regulatory_pack: 'Regulatorisches Paket installieren',
            regulatory_pack_desc: 'Laden Sie ein regulatorisches Rahmenwerk-Paket hoch (.zip)',
            framework_required: 'Rahmenwerk-Code ist erforderlich',
            jurisdiction_required: 'Zuständigkeitscode ist erforderlich',
        },
        catalog_detail: {
            packs_installed_of: 'von {total} Paketen installiert',
            install_all: 'Alle installieren',
            installing: 'Wird installiert...',
            removing: 'Wird entfernt...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Erfordert',
            tab_catalog: 'Katalog',
            tab_installed: 'Installiert',
            details: 'Einzelheiten',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Quellgebietsschema',
            target_locale: 'Zielgebietsschema',
            locales_must_differ: 'Quell- und Zielgebietsschema müssen unterschiedlich sein.',
            all_keys_translated: 'Alle Schlüssel übersetzt',
            keys_missing: '{count} Schlüssel fehlen',
            show_missing_only: 'Nur fehlende anzeigen',
            namespace: 'Namensraum',
            source: 'Quelle',
            target: 'Ziel',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Rahmenwerke',
            tab_watchlist: 'Beobachtungsliste',
            authority: 'Behörde',
            requirements_count: 'Anforderungen',
            all_types: 'Alle Typen',
            collapse_all: 'Alle einklappen',
            expand_all: 'Alle ausklappen',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Gefiltert auf',
            clear_filter: 'Filter löschen',
            no_requirements: 'Keine Anforderungen für dieses Framework gefunden.',
            requirements_label: 'Anforderungen',
            status_updated: 'Status erfolgreich aktualisiert',
            overview_tab: 'Übersicht',
            forms_tab: 'Formulare',
            procedures_tab: 'Verfahren',
            fines_risks_tab: 'Bußgelder & Risiken',
            deadlines_tab: 'Fristen',
            guidance_tab: 'Anleitung',
            guidance_empty: 'Für dieses Framework gibt es keine Anleitungsinhalte.',
            guidance_loading: 'Anleitung wird geladen…',
            guidance_section: 'Abschnitt „Anleitung“.',
            guidance_view: 'Anleitung anzeigen',
            guidance_map_to_workflow: 'Dem Workflow zuordnen',
            guidance_link_evidence: 'Beweise verknüpfen',
            guidance_filter_type: 'Typ',
            guidance_filter_category: 'Kategorie',
            guidance_filter_module: 'Modul',
            guidance_filter_status: 'Status',
            guidance_filter_search: 'Suchanleitung',
            guidance_filter_all: 'Alle',
            guidance_table_reference: 'Referenz',
            guidance_table_name: 'Führungsname',
            guidance_table_type: 'Typ',
            guidance_table_category: 'Kategorie',
            guidance_table_effective_date: 'Datum des Inkrafttretens',
            guidance_table_modules: 'Verwandte Module',
            guidance_table_actions: 'Aktionen',
            guidance_reference_only_banner: 'Nur Referenz – gilt für den Sektor {{sector}}, jedoch nicht für Ihre Organisation.',
            guidance_open_official_source: 'Offizielle Quelle öffnen',
            guidance_no_detail: 'Keine weiteren Details für diese Leitlinie verfügbar.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Verwandte Anleitungen',
            deadlines_empty: 'Keine regulatorischen Fristen für dieses Regelwerk gefunden.',
            deadlines_sla_type: 'SLA-Typ',
            deadlines_value: 'Frist',
            deadlines_unit: 'Einheit',
            deadlines_extension: 'Verlängerung',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Rechtsgrundlage',
            framework_stack: 'Rechtsrahmen-Struktur',
            layer_parent_law: 'Stammgesetz',
            layer_implementing_decree: 'Durchführungsverordnung',
            layer_enforcement_decree: 'Vollstreckungsverordnung',
            layer_admin_procedures: 'Verwaltungsverfahren',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Untergeordnete Rechtsakte',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Aktiv',
            status_draft: 'Entwurf',
            draft_enforcement_badge: 'Entwurf — noch nicht erlassen',
            effective_date_draft: 'Entwurf — noch nicht in Kraft',
            effective_date: 'Inkrafttreten',
            forms_table_id: 'Formular-ID',
            forms_table_name: 'Formularname',
            forms_table_source: 'Quelle',
            forms_table_category: 'Kategorie',
            forms_table_type: 'Typ',
            fill_form: 'Formular ausfüllen',
            view_reference: 'Referenz anzeigen',
            forms_empty: 'Keine Formulare für diesen Rechtsrahmen gefunden.',
            forms_loading: 'Formulare werden geladen...',
            filter_by_source_layer: 'Nach Quellebene filtern',
            all_layers: 'Alle Ebenen',
            procedures_id: 'Verfahrens-ID',
            procedures_name: 'Verfahren',
            procedures_trigger: 'Auslöser',
            procedures_steps: 'Schritte',
            procedures_outputs: 'Ergebnisse',
            procedures_related_forms: 'Zugehörige Formulare',
            procedures_submission_channels: 'Übermittlungskanäle',
            procedures_empty: 'Keine Verfahren für diesen Rechtsrahmen gefunden.',
            fines_area: 'Verstoßbereich',
            fines_articles: 'Artikel',
            fines_individual: 'Bußgeld (Einzelperson)',
            fines_organization: 'Bußgeld (Organisation)',
            fines_revenue: 'Umsatzeskalation',
            fines_deadlines: 'Fristen',
            fines_remediation: 'Abhilfemaßnahmen',
            fines_related: 'Zugehörige Verfahren',
            fines_empty: 'Keine Vollstreckungsdaten für diesen Rechtsrahmen verfügbar.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Risikoart',
            fines_risk_triggers: 'Auslöser',
            fines_risk_only_note: 'Diese Einträge beschreiben Compliance- und Betriebsrisiken, keine Geldstrafen. Sofern Bußgelder gelten, sind sie im übergeordneten Rahmenwerk festgelegt.',
            fines_enforcement_pending: 'Vollstreckungsrahmen noch nicht veröffentlicht. Sanktionsdaten werden ergänzt, wenn das Durchführungsinstrument erlassen wird.',
            view_details: 'Details anzeigen',
            back_to_list: 'Zurück zur Liste',
            description: 'Beschreibung',
            jurisdiction: 'Zuständigkeit',
            lineage: 'Abstammung',
            replaces: 'Ersetzt',
            replaced_by: 'Ersetzt durch',
            requirements: 'Anforderungen',
            tab_navigation: 'Rechtsrahmen-Detail-Tabs',
            forms_table_actions: 'Aktionen',
            create_form: 'Formular erstellen',
            mark_promulgated: 'Als verkündet markieren',
            status_promulgated: 'Verkündet',
            promulgated_success: 'Schichtstatus auf verkündet aktualisiert',
            transition_review: 'Übergangsüberprüfung',
            transition_review_desc: 'Dieses Rahmenwerk hat eine ausstehende Übergangsüberprüfung für die Übernahme des Nachfolgegesetzes.',
            transition_review_by: 'Überprüfungsfrist',
            transition_review_status: 'Überprüfungsstatus',
            transition_review_notes: 'Anmerkungen',
            transition_review_pending: 'Ausstehend',
            transition_review_in_progress: 'In Bearbeitung',
            transition_review_completed: 'Abgeschlossen',
            transition_review_successor: 'Nachfolge-Rahmenwerk',
            transition_review_lead_time: 'Vorlaufzeit',
            transition_review_lead_time_days: '{days} Tage vor Inkrafttreten',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Formular-Übergangsüberprüfungen',
            transition_review_forms_desc: 'Überprüfen Sie jede operative Vorlage auf Relevanz unter dem Nachfolge-Rahmenwerk.',
            transition_review_outcome: 'Ergebnis',
            transition_review_outcome_carry_forward: 'Übernehmen',
            transition_review_outcome_carry_forward_revised: 'Übernehmen (überarbeitet)',
            transition_review_outcome_retire: 'Stilllegen',
            transition_review_outcome_split: 'Aufteilen',
            transition_review_outcome_awaiting_decree: 'Warten auf Verordnung',
            transition_review_no_forms: 'Keine Formular-Übergangsüberprüfungen konfiguriert.',
            transition_review_update_success: 'Übergangsüberprüfung erfolgreich aktualisiert',
            transition_review_operational_note: 'Dies sind prozessunterstützende operative Vorlagen, keine gesetzlichen Formularzähler. Die rechtliche Zuordnung verbleibt beim alten Rahmenwerk, bis das Nachfolgegesetz in Kraft tritt.',
            rights_tab: 'Rechte',
            rights_empty: 'Für dieses Rahmenwerk sind keine Betroffenenrechte definiert.',
            rights_code: 'Code',
            rights_name: 'Recht',
            rights_legal_basis: 'Rechtsgrundlage',
            rights_elaborating_articles: 'Detailartikel',
            rights_description: 'Beschreibung',
            requirements_reference: 'Referenz',
            requirements_mandatory: 'Obligatorisch',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Quelle',
            category_legal_basis: 'Rechtsgrundlage',
            category_data_subject_rights: 'Rechte der betroffenen Person',
            category_notification: 'Benachrichtigung',
            category_assessment: 'Bewertung',
            category_governance: 'Regierungsführung',
            category_record_keeping: 'Aufzeichnungen führen',
            category_cross_border: 'Grenzüberschreitend',
            category_technical_measures: 'Technische Maßnahmen',
            category_incident_reporting: 'Meldung von Vorfällen',
            category_risk_management: 'Risikomanagement',
            category_supply_chain: 'Lieferkette',
            category_certification: 'Zertifizierung',
            child_frameworks: '{count} untergeordnetes Framework',
            child_frameworks_plural: '{count} Untergeordnete Frameworks',
            replaces_prefix: 'Ersetzt:',
            replaced_by_prefix: 'Ersetzt durch:',
            responsible_prefix: 'Verantwortlich:',
            watchlist_empty_hint: 'Zur Überwachung werden hier Frameworks mit dem Status „Entwurf“ oder „bevorstehend“ angezeigt.',
            framework_count: '{count}-Framework',
            framework_count_plural: '{count} Frameworks',
            hiding_legacy: '({count} Vermächtnis verbergen)',
            label_VN_PDPD_DECREE_13: 'PDPD-Dekret 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Gesetz 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Dekret 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Durchsetzungsentwurf 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Datenrecht',
            label_VN_DATA_LAW_DECREE_165_2025: 'Datenrechtsdekret 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Entwurf für Datenbörsen 2026',
            label_VN_TELECOM_LAW_2023: 'Telekommunikationsgesetz 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Telekommunikationsdekret 163/2024',
            label_VN_CYBERSEC_LAW: 'Cybersicherheitsgesetz (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec-Dekret 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Cybersicherheitsgesetz 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'E-Commerce-Gesetz 122/2025',
            label_VN_AI_LAW_2026: 'AI Gesetz 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Unternehmen',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Autorität',
            sla_type_rights_request_ack: 'Rechteanfrage-Bestätigung',
            'sla_type_Access control review': 'Überprüfung der Zugangskontrolle',
            'sla_type_Annual governance attestation': 'Jährliche Governance-Bescheinigung',
            'sla_type_Audit review cycle': 'Audit-Überprüfungszyklus',
            'sla_type_Authority request response': 'Antwort auf die Anfrage der Behörde',
            'sla_type_Change notification processing': 'Bearbeitung von Änderungsmeldungen',
            'sla_type_Complaint acknowledgment': 'Reklamationsbestätigung',
            'sla_type_Complaint resolution': 'Beschwerdelösung',
            'sla_type_Confidentiality policy review': 'Überprüfung der Vertraulichkeitsrichtlinie',
            'sla_type_Cybersecurity incident full report': 'Vollständiger Bericht zu Cybersicherheitsvorfällen',
            'sla_type_Cybersecurity incident initial report': 'Erster Bericht über Cybersicherheitsvorfälle',
            'sla_type_Data inventory review': 'Überprüfung des Datenbestands',
            'sla_type_Disclosure log review cycle': 'Überprüfungszyklus des Offenlegungsprotokolls',
            'sla_type_Incident classification decision': 'Entscheidung zur Einstufung des Vorfalls',
            'sla_type_Information system classification review': 'Überprüfung der Klassifizierung von Informationssystemen',
            'sla_type_Notification processing (sales website/app)': 'Benachrichtigungsverarbeitung (Verkaufswebsite/-App)',
            'sla_type_Pen test cycle': 'Pen-Test-Zyklus',
            'sla_type_Registration processing (service platform)': 'Registrierungsabwicklung (Serviceplattform)',
            'sla_type_Risk assessment cycle': 'Risikobewertungszyklus',
            'sla_type_Takedown decision (after review)': 'Takedown-Entscheidung (nach Prüfung)',
            'sla_type_Termination notice (public)': 'Kündigungsmitteilung (öffentlich)',
            sla_type_ai_impact_assessment_completion: 'AI Abschluss der Folgenabschätzung',
            sla_type_ai_sandbox_application_review: 'AI Überprüfung der Sandbox-Anwendung',
            sla_type_ai_serious_incident_report: 'AI Bericht über schwerwiegenden Vorfall',
            sla_type_ai_system_registration: 'AI Systemregistrierung',
            sla_type_authority_request_response: 'Antwort auf die Anfrage der Behörde',
            sla_type_certificate_issuance_review: 'Überprüfung der Zertifikatsausstellung',
            sla_type_complaint_handling_approach_notice: 'Hinweis zum Umgang mit Beschwerden',
            sla_type_complaint_incomplete_info_request: 'Beschwerde unvollständige Informationsanfrage',
            sla_type_data_governance_attestation: 'Bescheinigung über die Datenverwaltung',
            sla_type_dsr_acknowledgment: 'DSR-Bestätigung',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Kl.2) – Zurückziehen / Einschränken / Einspruch',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Kl.2) – Dritter',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Kl.3) – Zugriff/Korrektur/Bereitstellung',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Kl.3) – Dritter',
            sla_type_dsr_cl4_deletion: 'DSR (Kl.4) – Streichung',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Kl.4) – Löschung, Dritter',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Kl.5) – Schutzmaßnahmen',
            sla_type_telecom_authority_request_response: 'Antwort auf Anfrage der Telekommunikationsbehörde',
            sla_type_rights_request_completion: 'Abschluss der Rechteanfrage',
            sla_type_incident_authority_notification: 'Benachrichtigung der Vorfallbehörde',
            sla_type_incident_subject_notification: 'Benachrichtigung über den Vorfall',
            sla_type_dpia_completion: 'DPIA Abschluss',
            sla_type_data_retention_review: 'Überprüfung der Datenspeicherung',
            sla_type_authority_filing_review: 'Überprüfung der Einreichung durch die Behörde',
            sla_type_authority_supplement_response: 'Antwort der Behörde nach Ergänzung',
            sla_type_authority_resubmission: 'Wiedervorlage der Behörde',
            sla_type_authority_filing_supplement_response: 'Antwort der Behörde auf Ergänzungsantrag',
            sla_type_authority_filing_resubmission: 'Behörde reicht Wiedervorlage ein',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'Arbeitstage',
            unit_calendar_days: 'Kalendertage',
            unit_hours: 'Stunden',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Zustimmung',
            form_category_privacy: 'Privatsphäre',
            form_category_breach: 'Verstoß',
            form_category_dpia: 'DPIA',
            form_category_register: 'Registrieren',
            form_category_monitoring: 'Überwachung',
            form_category_vendor: 'Anbieter',
            form_category_org: 'Organisatorisch',
            form_category_sector: 'Sektor',
            form_category_data_mapping: 'Datenzuordnung',
            form_category_governance: 'Regierungsführung',
            form_category_incident: 'Vorfall',
            form_category_authority_submission: 'Einreichung bei der Behörde',
            form_category_notice: 'Hinweis',
            form_category_assessment_report: 'Bewertungsbericht',
            form_category_request: 'Anfrage',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Einreichung',
            module_consent: 'Zustimmung',
            module_rights_request: 'Rechteanfrage',
            module_classification: 'Klassifizierung',
            module_dpia: 'DPIA',
            module_governance: 'Regierungsführung',
            module_monitoring: 'Überwachung',
            module_register: 'Registrieren',
            module_risk: 'Risiko',
            module_data_mapping: 'Datenzuordnung',
            module_incident: 'Vorfall',
            module_assessment: 'Bewertung',
            module_vendor: 'Anbieter',
            module_policy: 'Politik',
            module_audit: 'Prüfung',
            module_training: 'Ausbildung',
            module_discovery: 'Entdeckung',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Berechnungsanleitung',
            enum_guidance_type_implementation_guidance: 'Implementierungsleitfaden',
            enum_guidance_type_interpretive_note: 'Interpretationshinweis',
            enum_guidance_type_legal_guidance: 'Rechtliche Hinweise',
            enum_guidance_type_procedural_guidance: 'Verfahrenshinweise',
            enum_guidance_type_sector_guidance: 'Branchenberatung',
            enum_guidance_type_technical_methodology: 'Technische Methodik',
            enum_guidance_category_annual_reporting: 'Jährliche Berichterstattung',
            enum_guidance_category_authority_cooperation: 'Behördenkooperation',
            enum_guidance_category_authority_coordination: 'Behördenkoordination',
            enum_guidance_category_business_continuity: 'Geschäftskontinuität',
            enum_guidance_category_capital_adequacy: 'Kapitaladäquanz',
            enum_guidance_category_complaint_handling: 'Beschwerdebearbeitung',
            enum_guidance_category_consent: 'Zustimmung',
            enum_guidance_category_consent_management: 'Einwilligungsmanagement',
            enum_guidance_category_consumer_protection: 'Verbraucherschutz',
            enum_guidance_category_control_environment: 'Kontrollumgebung',
            enum_guidance_category_cross_border: 'Grenzüberschreitend',
            enum_guidance_category_cross_border_transfers: 'Grenzüberschreitende Überweisungen',
            enum_guidance_category_data_classification: 'Datenklassifizierung',
            enum_guidance_category_data_localization: 'Datenlokalisierung',
            enum_guidance_category_data_protection_impact_assessment: 'Datenschutz-Folgenabschätzung',
            enum_guidance_category_direct_marketing: 'Direktmarketing',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Regierungsführung',
            enum_guidance_category_ict_risk_management: 'IKT-Risikomanagement',
            enum_guidance_category_incident_management: 'Vorfallmanagement',
            enum_guidance_category_internal_audit: 'Interne Revision',
            enum_guidance_category_isms_documentation: 'ISMS-Dokumentation',
            enum_guidance_category_lawful_basis: 'Rechtsgrundlage',
            enum_guidance_category_licensing: 'Lizenzierung',
            enum_guidance_category_local_presence: 'Lokale Präsenz',
            enum_guidance_category_logging_and_retention: 'Protokollierung und Aufbewahrung',
            enum_guidance_category_logical_and_physical_access: 'Logischer und physischer Zugriff',
            enum_guidance_category_operational_resilience_testing: 'Prüfung der betrieblichen Belastbarkeit',
            enum_guidance_category_outsourcing: 'Outsourcing',
            enum_guidance_category_platform_registration: 'Plattformregistrierung',
            enum_guidance_category_quality_of_service: 'Servicequalität',
            enum_guidance_category_risk_classification: 'Risikoklassifizierung',
            enum_guidance_category_risk_management: 'Risikomanagement',
            enum_guidance_category_scope_definition: 'Geltungsbereichsdefinition',
            enum_guidance_category_sectoral_localisation: 'Sektorale Lokalisierung',
            enum_guidance_category_subscriber_protection: 'Abonnentenschutz',
            enum_guidance_category_supply_chain: 'Lieferkette',
            enum_guidance_category_system_classification: 'Systemklassifizierung',
            enum_guidance_category_system_operations: 'Systembetrieb',
            enum_guidance_category_telecommunications_data: 'Telekommunikationsdaten',
            enum_guidance_category_third_party_risk: 'Risiko Dritter',
            enum_guidance_category_transitional_provisions: 'Übergangsbestimmungen',
            enum_guidance_category_vulnerability_management: 'Schwachstellenmanagement',
            enum_guidance_status_active: 'Aktiv',
            enum_guidance_status_draft: 'Entwurf',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Zugriffsverwaltung',
            enum_module_ai_governance: 'AI Governance',
            enum_module_asset_management: 'Vermögensverwaltung',
            enum_module_audit_trail: 'Prüfpfad',
            enum_module_breach_notification: 'Benachrichtigung über Verstöße',
            enum_module_capital_adequacy: 'Kapitaladäquanz',
            enum_module_classification: 'Klassifizierung',
            enum_module_consent: 'Zustimmung',
            enum_module_consent_management: 'Einwilligungsmanagement',
            enum_module_cross_border_transfers: 'Grenzüberschreitende Überweisungen',
            enum_module_data_localization: 'Datenlokalisierung',
            enum_module_data_mapping: 'Datenzuordnung',
            enum_module_data_protection: 'Datenschutz',
            enum_module_data_subject_rights: 'Rechte der betroffenen Person',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Verschlüsselung',
            enum_module_governance: 'Regierungsführung',
            enum_module_incident: 'Vorfall',
            enum_module_incident_management: 'Vorfallmanagement',
            enum_module_internal_audit: 'Interne Revision',
            enum_module_isms_documentation: 'ISMS-Dokumentation',
            enum_module_isms_governance: 'ISMS-Governance',
            enum_module_logging_and_monitoring: 'Protokollierung und Überwachung',
            enum_module_monitoring: 'Überwachung',
            enum_module_policy_management: 'Richtlinienverwaltung',
            enum_module_risk: 'Risiko',
            enum_module_risk_management: 'Risikomanagement',
            enum_module_training: 'Ausbildung',
            enum_module_vendor: 'Anbieter',
            enum_module_vendor_management: 'Lieferantenmanagement',
            enum_module_vendor_risk: 'Lieferantenrisiko',
            enum_module_vulnerability_management: 'Schwachstellenmanagement',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Zugang',
            category_availability: 'Verfügbarkeit',
            category_change_management: 'Änderungsmanagement',
            category_classification: 'Klassifizierung',
            category_communication_and_information: 'Kommunikation & Information',
            category_compliance: 'Compliance',
            category_confidentiality: 'Vertraulichkeit',
            category_control_activities: 'Kontrollaktivitäten',
            category_control_environment: 'Kontrollumgebung',
            category_incident: 'Vorfall',
            category_internal_audit: 'Interne Revision',
            category_internal_control: 'Interne Kontrolle',
            category_logical_and_physical_access: 'Logischer und physischer Zugriff',
            category_mis_data: 'Daten des Managementinformationssystems',
            category_monitoring: 'Überwachung',
            category_monitoring_activities: 'Überwachungsaktivitäten',
            category_outsourcing: 'Outsourcing',
            category_privacy: 'Privatsphäre',
            category_processing_integrity: 'Verarbeitungsintegrität',
            category_reporting: 'Berichterstattung',
            category_risk_assessment: 'Risikobewertung',
            category_risk_mitigation: 'Risikominderung',
            category_sector_applicability: 'Branchenbezogene Anwendbarkeit',
            category_system_operations: 'Systembetrieb',
            category_training: 'Ausbildung',
            category_vendor: 'Anbieter',
            guidance_related_forms_advisory: 'Für einige Formulare gibt es entsprechende Anleitungen – siehe im',
            guidance_related_procedures_advisory: 'Für einige Verfahren gibt es entsprechende Anleitungen – siehe im',
            guidance_related_requirements_advisory: 'Für einige Anforderungen gibt es entsprechende Anleitungen – siehe im',
        },
        channels: {
            title: 'Einreichungskanäle und Fristen',
            channels: 'Einreichungskanäle',
            add_channel: 'Kanal hinzufügen',
            channel_type: 'Typ',
            name: 'Name',
            instructions: 'Anweisungen',
            active: 'Aktiv',
            no_channels: 'Keine Einreichungskanäle konfiguriert',
            portal: 'Portal',
            in_person: 'Persönlich',
            postal: 'Post',
            email: 'E-Mail',
            add: 'Hinzufügen',
            deadlines: 'Regulatorische Fristen',
            add_deadline: 'Frist hinzufügen',
            label: 'Bezeichnung',
            days: 'Tage',
            day_type: 'Tagestyp',
            calendar_days: 'Kalendertage',
            working_days: 'Arbeitstage',
            no_deadlines: 'Keine Fristen konfiguriert',
            description: 'Beschreibung',
            failed_load: 'Fehler beim load channel data',
            channel_added: 'Kanal hinzugefügt',
            channel_added_announce: 'Einreichungskanal hinzugefügt',
            failed_add_channel: 'Fehler beim add channel',
            deadline_added: 'Frist hinzugefügt',
            deadline_added_announce: 'Regulatorische Frist hinzugefügt',
            failed_add_deadline: 'Fehler beim add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Framework-Verknüpfung',
            modules: 'Verknüpfte Module',
            add_module: 'Modul hinzufügen',
            module_code: 'Modulcode',
            required: 'Erforderlich',
            link: 'Verknüpfen',
            no_modules: 'Keine Module verknüpft',
            notes: 'Notizen',
            personas: 'Verknüpfte Personas',
            add_persona: 'Persona hinzufügen',
            persona_id: 'Persona-ID',
            role_description: 'Rollenbeschreibung',
            no_personas: 'Keine Personas verknüpft',
            failed_load: 'Fehler beim load framework links',
            module_linked: 'Modul verknüpft',
            module_linked_announce: 'Modul erfolgreich verknüpft',
            failed_link_module: 'Fehler beim link module',
            persona_linked: 'Persona verknüpft',
            persona_linked_announce: 'Persona erfolgreich verknüpft',
            failed_link_persona: 'Fehler beim link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Ausstehend',
                assessed: 'Bewertet',
                applied: 'Angewendet',
                dismissed: 'Verworfen',
            },
            title: 'Regulatorische Aktualisierungen und Framework-Dashboard',
            page_description: 'Überblick über die Abdeckung aller installierten Regulierungsrahmen sowie das Änderungsprotokoll regulatorischer Aktualisierungen im Produkt: Änderungen, Wirksamkeitsdaten und Folgenabschätzungen.',
            coverage_dashboard: 'Framework-Abdeckungs-Dashboard',
            no_frameworks: 'Keine Frameworks gefunden',
            framework: 'Rahmen',
            modules: 'Module',
            templates: 'Vorlagen',
            forms: 'Formulare',
            channels: 'Kanäle',
            deadlines: 'Fristen',
            pending: 'Ausstehende Aktualisierungen',
            updates: 'Regulatorische Aktualisierungen',
            create_update: 'Aktualisierung erstellen',
            framework_id: 'Framework-ID',
            update_title: 'Titel',
            description: 'Beschreibung',
            effective_date: 'Inkrafttreten',
            amendment_ref: 'Änderungsreferenz',
            create: 'Erstellen',
            no_updates: 'Keine regulatorischen Aktualisierungen',
            status: 'Status',
            impact: 'Auswirkung',
            change_summary: 'Änderungszusammenfassung',
            assess: 'Bewerten',
            failed_load: 'Fehler beim load data',
            update_created: 'Aktualisierung erstellt',
            update_created_announce: 'Regulatorische Aktualisierung erstellt',
            failed_create: 'Fehler beim create update',
            update_assessed: 'Aktualisierung bewertet',
            failed_assess: 'Fehler beim assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Verwandte Rahmenwerke',
            panel_intro: 'Wie dieses Rahmenwerk mit anderen zusammenhängt. Ein Overlay kann gleichzeitig unter mehreren übergeordneten Rahmenwerken gelten; jedes erscheint hier genau einmal.',
            outgoing_heading: 'Dieses Rahmenwerk gilt für',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Gilt unter diesem Rahmenwerk',
            outgoing_caption: 'Rahmenwerke, auf die dieses Rahmenwerk verweist',
            incoming_caption: 'Rahmenwerke, die diesem Rahmenwerk untergeordnet sind',
            col_framework: 'Rahmenwerk',
            col_relationship: 'Beziehung',
            col_scope: 'Quelle',
            scope_global: 'Aus Paketinhalt',
            scope_org: 'Organisationsspezifisch',
            load_failed: 'Verwandte Rahmenwerke konnten nicht geladen werden',
            relationship_kinds: {
                parent: 'Übergeordnetes Rahmenwerk',
                applies_to: 'Gilt für',
                cross_reference: 'Querverweis',
                supersedes: 'Ersetzt',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Bankwesen',
                insurance: 'Versicherungen',
                healthcare: 'Gesundheitswesen',
                education: 'Education',
                ict_critical: 'Kritische IKT',
                telecom: 'Telekommunikation',
                energy: 'Energie',
                transport: 'Transport',
                digital_services: 'Digitale Dienste',
                digital_technology_industry: 'Digitale Technologiebranche',
                manufacturing: 'Manufacturing',
                public_administration: 'Öffentliche Verwaltung',
                payments: 'Zahlungsverkehr',
                publishing: 'Verlagswesen',
                crypto_assets: 'Kryptowerte',
                other: 'Sonstige',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sektor',
            direct_scope_label: 'Unmittelbarer rechtlicher Geltungsbereich',
            chip_direct: 'Unmittelbar anwendbar',
            chip_reference: 'Nur als Referenz',
            banner_direct_copy: 'Unmittelbar auf Ihre Organisation anwendbar. Sektorberichte und Fristen sind aktiv.',
            banner_reference_copy: 'Nur als Referenz — Ihre Organisation ist diesem Sektor nicht zugeordnet.',
            edit_mode_button: 'Modus bearbeiten',
            edit_form_title: 'Installationsmodus ändern',
            mode_direct_label: 'Direkt — rechtlich anwendbar',
            mode_direct_help: 'Aktiviert Sektorfristen und Dashboard-Widgets.',
            mode_reference_label: 'Nur als Referenz',
            mode_reference_help: 'Kann eingesehen werden, aber ohne Fristen oder Widgets.',
            reason_note_label: 'Begründung (optional)',
            reason_note_placeholder: 'z. B. Tochtergesellschaft einer Bank übernommen',
            cancel: 'Abbrechen',
            confirm: 'Bestätigen',
            confirming: 'Wird gespeichert…',
            save: 'Speichern',
            saving: 'Wird gespeichert…',
            toast_mode_changed: 'Der Modus des Sektor-Overlays wurde aktualisiert.',
            toast_installed: 'Sektor-Overlay installiert.',
            toast_sectors_saved: 'Sektor-Selbsteinstufung gespeichert.',
            install_modal_title: 'Sektor-Overlay installieren',
            install_modal_intro: 'Sektor-Overlays sind optionale Regulierungspakete für eine einzelne Branche.',
            install_modal_footer: 'Sie können den Installationsmodus später auf der Framework-Detailseite ändern.',
            install_button: 'Installieren',
            installed_label: 'Installiert',
            uninstall_button: 'Deinstallieren',
            uninstall_confirm: 'Bestätigen Sie die Deinstallation',
            uninstall_cancel: 'Abbrechen',
            suggested_label: 'Empfohlen',
            catalog_page_title: 'Sektor-Overlays',
            catalog_page_intro: 'Optionale Regulierungspakete für jeweils einen bestimmten Industriesektor.',
            catalog_empty_title: 'Keine Sektor-Overlays verfügbar',
            catalog_empty_description: 'Hier erscheinen Sektor-Overlay-Pakete, die auf Ihrer Plattform veröffentlicht wurden.',
            authority_label: 'Behörde',
            org_sectors_title: 'Branchenzuordnung',
            org_sectors_intro: 'Wählen Sie die Branchen aus, in denen Ihre Organisation tätig ist.',
            widget_title: 'Fällige Berichte für Sektor-Overlays',
            widget_aria_label: 'Fällige Berichte für Sektor-Overlays',
            widget_empty: 'Keine anstehenden Fristen für Sektor-Overlays.',
            audit_event_mode_changed: 'Installationsmodus des Sektor-Overlays geändert',
            audit_event_installed: 'Sektor-Overlay installiert',
            view_details_button: 'Details anzeigen →',
            banner_aria_label: 'Status des Sektor-Overlays',
            toast_load_failed: 'Sektor-Overlays konnten nicht geladen werden',
            toast_install_failed: 'Sektor-Overlay konnte nicht installiert werden',
            toast_uninstall_confirm_required: 'Geben Sie zur Bestätigung der Deinstallation "{packCode}" ein.',
            toast_uninstalled: '{packCode} wurde deinstalliert.',
            toast_uninstall_failed: 'Sektor-Overlay konnte nicht deinstalliert werden',
            uninstall_warning_destructive_label: 'Diese Aktion ist destruktiv.',
            uninstall_warning_body: 'Bei der Deinstallation werden der Framework-Datensatz und alle gespeicherten regulatorischen Fristen zu {frameworkCode} aus dieser Organisation entfernt.',
            uninstall_warning_type_to_confirm: 'Geben Sie zur Bestätigung {packCode} ein.',
            uninstall_in_progress: 'Deinstallation läuft…',
            widget_view_all: 'Alle Sektor-Overlays anzeigen →',
            widget_due_in_days: 'Fällig in {days} Tagen',
            widget_overdue_days: 'Seit {days} Tagen überfällig',
            widget_due_today: 'Heute fällig',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
                product_identification_certificate_receipt: 'Certificate synchronisation receipt',
                product_identification_connection_readiness: 'Connection and transition readiness record',
                product_identification_event_record: 'Significant supply-chain event record',
                product_identification_provider_agreement: 'Service-provider data-responsibility agreement',
                product_identification_scope_determination: 'Product scope and risk-tier determination',
                product_identification_uid_register: 'UID and master-data register entry',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_identification_scope_determinations: 'Product Scope Determinations',
                product_identification_sector_watch: 'Sector Watch',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Gestaffelte Pflichten',
            panel_aria_label: 'Gestaffelte Pflichten',
            panel_intro: 'Einige Klauseln dieses Frameworks treten später in Kraft als das Framework selbst.',
            phased_effective_date_label: 'Gestaffeltes Inkrafttreten:',
            status_pending: 'Ausstehend',
            status_active: 'Aktiv',
            status_early: 'Aktiv (vorzeitig umgesetzt)',
            notice_window_label: 'Mitteilungsfrist an die Behörde:',
            days_label: 'Tage',
            recipient_label: 'Empfänger:',
            early_implement_button: 'Als vorzeitig umgesetzt markieren',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Gestaffelte Pflicht als vorzeitig umgesetzt markieren',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'Wirksam ab',
            reason_label: 'Begründung (optional)',
            reason_placeholder: 'z. B. Vorzeitige Umsetzung vom Vorstand am 2026-09-01 beschlossen',
            confirm: 'Bestätigen',
            toast_early_implemented: 'Vorzeitige Umsetzung erfasst.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Gestaffelte Pflicht als vorzeitig umgesetzt markiert',
            widget_rolling_window_label: 'Gestaffelte Aktivierungen innerhalb von 90 Tagen',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Übersicht',
            partner_orgs: 'Partnerorganisationen',
            service_tiers: 'Servicestufen',
            deliverable_templates: 'Liefervorlagen',
            deliverables: 'Liefergegenstände',
            brand_profiles: 'Markenprofile',
            support_tickets: 'Supportanfragen',
            workflow_templates: 'Arbeitsablaufvorlagen',
            workflows: 'Arbeitsabläufe',
            escalation: 'Eskalation',
            reports: 'Berichte',
            audit: 'Prüfprotokoll',
        },
        dashboard: {
            title: 'Partnerübersicht',
            loading: 'Lade Dashboard Daten...',
            total_managed_tenants: 'Gesamt Verwaltet Mandanten',
            avg_health_score: 'Avg Zustand Bewertung',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Offen Tickets',
            upcoming_deliverables: 'Bevorstehend Lieferobjekte',
            escalated_tickets: 'Eskaliert Tickets',
            managed_tenants: 'Verwaltet Mandanten',
            partner_tier: 'Partner Stufe',
            managed_tenants_count: 'Verwaltet Mandanten Anzahl',
            onboarding_date: 'Onboarding Datum',
            view_details: 'Ansehen Details',
            no_partner_orgs: 'Keine partner Organisationen gefunden.',
        },
        partner_orgs: {
            title: 'Partnerorganisationen',
            new: 'Neu Partner Org',
            contact_name: 'Kontakt Name',
            contact_email: 'Kontakt E-Mail',
            partner_tier: 'Partner Stufe',
            managed_orgs: 'Verwaltet Orgs',
            onboarding_date: 'Onboarding Datum',
            no_orgs: 'Keine partner Organisationen gefunden.',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Kontakt Telefon',
            metadata_json: 'Metadaten (JSON)',
            change_status: 'Ändern Status',
            new_status: 'Neu Status',
            invalid_json_metadata: 'Ungültig JSON in metadata field',
            suspended: 'Gesperrt',
            offboarded: 'Ausgeschieden',
            all_tiers: 'Alle Stufen',
            silver: 'Silber',
            gold: 'Goldstufe',
            platinum: 'Platin',
            status_offboarded: 'Ausgeschieden',
            status_suspended: 'Gesperrt',
            tier_gold: 'Goldstufe',
            tier_platinum: 'Platin',
            tier_silver: 'Silber',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Servicestufen',
            new: 'Neu Stufe',
            tier_name: 'Stufe Name',
            monthly_fee: 'Monatlich Fee',
            features: 'Funktionen',
            no_tiers: 'Keine service Stufen gefunden.',
            tier_code: 'Stufe Code',
            response_time_hrs: 'Antwort Zeit (hrs)',
            resolution_time_hrs: 'Lösung Zeit (hrs)',
            review_frequency: 'Überprüfen Häufigkeit',
            sla_response_time: 'SLA Antwort Zeit (hours)',
            sla_resolution_time: 'SLA Lösung Zeit (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Vergleiche...',
            compare: 'Vergleichen',
            comparison: 'Dienst Stufe Vergleich',
            attribute: 'Attribut',
            deprecate: 'Veralten',
            deprecated: 'Veraltet',
            select_compare: 'Auswählen bei least 2 Stufen zu compare',
            comparison_title: 'Dienst Stufe Vergleich',
            status_deprecated: 'Veraltet',
            activate: 'Aktivieren',
            resolution_time: 'Lösung Zeit',
            response_time: 'Antwort Zeit',
            select_at_least_two: 'Auswählen bei least 2 Stufen',
        },
        deliverable_templates: {
            title: 'Liefervorlagen',
            new: 'Neu Vorlage',
            template_name: 'Vorlage Name',
            deliverable_type: 'Lieferobjekt Typ',
            no_templates: 'Keine deliverable Vorlagen gefunden.',
            frequency: 'Häufigkeit',
            est_effort_hrs: 'Est. Aufwand (hrs)',
            estimated_effort: 'Geschätzt Aufwand (hours)',
            associated_module: 'Associated Modul',
            linked_tiers_count: 'Verknüpft Stufen Anzahl',
            evidence_requirements: 'Nachweis Anforderungen (comma-separated)',
            tier_ids: 'Stufe IDs (comma-separated)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'Ein Zeit',
            frequency_monthly: 'Monatlich',
            frequency_quarterly: 'Vierteljährlich',
            frequency_annually: 'Jährlich',
            type_report: 'Bericht',
            type_review: 'Überprüfen',
            type_assessment: 'Bewertung',
            type_meeting: 'Besprechung',
            type_configuration: 'Konfiguration',
            freq_one_time: 'Ein Zeit',
            freq_monthly: 'Monatlich',
            freq_quarterly: 'Vierteljährlich',
            freq_annually: 'Jährlich',
            status_deprecated: 'Veraltet',
        },
        deliverables: {
            title: 'Liefergegenstände',
            new: 'Neu Lieferobjekt',
            partner: 'Geschäftspartner',
            deliverable_type: 'Lieferobjekt Typ',
            due_date: 'Due Datum',
            no_deliverables: 'Keine deliverables gefunden.',
            assigned_to: 'Zugewiesen Zu',
            completion_date: 'Fertigstellung Datum',
            update_status: 'Aktualisieren Status',
            hide_details: 'Ausblenden Details',
            view_details: 'Ansehen Details',
            evidence_ids: 'Nachweis IDs',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Client Genehmigung',
            bulk_update: 'Massen Aktualisieren',
            bulk_update_status: 'Massen Aktualisieren Status',
            generate_schedule: 'Generieren Planen',
            generate_deliverable_schedule: 'Generieren Lieferobjekt Planen',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organisation ID',
            service_tier_id: 'Dienst Stufe ID',
            filter_assigned_to: 'Filtern nach Zugewiesen Zu',
            select_status: 'Auswählen Status...',
            new_status: 'Neu Status',
            upcoming: 'Bevorstehend',
            overdue: 'Überfällig',
            status_deferred: 'Aufgeschoben',
            status_overdue: 'Überfällig',
            status_upcoming: 'Bevorstehend',
        },
        brand_profiles: {
            title: 'Markenprofile',
            new: 'Neu Marke Profil',
            partner: 'Geschäftspartner',
            brand_name: 'Marke Name',
            primary_color: 'Primär Farbe',
            logo_url: 'Logo-URL',
            no_profiles: 'Keine brand profiles gefunden.',
            secondary_color: 'Sekundär Farbe',
            header_text: 'Kopfzeile Text',
            footer_text: 'Fußzeile Text',
            contact_email: 'Kontakt E-Mail',
            contact_phone: 'Kontakt Telefon',
            contact_website: 'Kontakt Website',
            contact_address: 'Kontakt Adresse',
            is_default: 'Is Standard',
            set_default: 'Festlegen Standard',
            preview: 'Vorschau',
            report_header_preview: 'Bericht Kopfzeile Vorschau',
            report_footer_preview: 'Bericht Fußzeile Vorschau',
            preview_description: 'This is ein preview von der report header mit der ausgewählt brand colors.',
            contact_website_short: 'Webseite',
            logo_preview: 'Logo-Vorschau',
        },
        support_tickets: {
            title: 'Supportanfragen',
            new: 'Neu Ticket',
            partner: 'Geschäftspartner',
            subject: 'Betreff',
            assigned_to: 'Zugewiesen Zu',
            no_tickets: 'Keine support Tickets gefunden.',
            category: 'Kategorie',
            priority: 'Priorität',
            escalation_tier: 'Eskalation Stufe',
            sla_status: 'SLA-Status',
            update_status: 'Aktualisieren Status',
            escalate: 'Eskalieren',
            add_note: 'Hinzufügen Hinweis',
            escalate_ticket: 'Eskalieren Ticket',
            escalate_to_tier: 'Eskalieren Zu Stufe',
            notes_timeline: 'Hinweise Zeitverlauf',
            no_notes: 'No Hinweise yet.',
            attachments: 'Anhänge',
            no_attachments: 'Keine Anhänge.',
            client_org_id: 'Kunden-Organisations-ID',
            content: 'Inhalt',
            note_type: 'Typ',
            view_detail: 'Ansehen Detail',
            hide: 'Ausblenden',
            new_status: 'Neu Status',
            all_categories: 'Alle Kategorien',
            all_priorities: 'Alle Priorities',
            all_tiers: 'Alle Stufen',
            ticket_not_found: 'Ticket not gefunden',
            ticket_id: 'Ticket-ID',
            client_org: 'Kunden-Organisation',
            deescalate: 'Deeskalieren',
            deescalate_ticket: 'Ticket deeskalieren',
            deescalate_to_tier: 'De-escalate Zu Stufe',
            add_attachment: 'Hinzufügen Anhang',
            file: 'Datei',
            linked_entity: 'Verknüpft Entität',
            sla_target: 'Ziel',
            breached: 'Verletzt',
            remaining_hours: 'verbleibend',
            na: 'N/A',
            tickets_found: 'Tickets gefunden',
            reason: 'Grund',
            target: 'Ziel',
            upload: 'Hochladen',
        },
        workflow_templates: {
            title: 'Arbeitsablaufvorlagen',
            new: 'Neu Vorlage',
            template_name: 'Vorlage Name',
            steps: 'Schritte',
            no_templates: 'Keine Workflow Vorlagen gefunden.',
            category: 'Kategorie',
            view_steps: 'Ansehen Schritte',
            hide_steps: 'Ausblenden Schritte',
            step: 'Schritt',
            step_title: 'Titel',
            step_description: 'Beschreibung',
            assignee_role: 'Zugewiesener Rolle',
            sla_hours: 'SLA-Stunden',
            evidence_required: 'Nachweis Erforderlich',
            guidance_notes: 'Leitfaden Hinweise',
            add_step: 'Hinzufügen Schritt',
            no_steps: 'Keine Schritte definiert.',
            no_steps_defined: 'Keine Schritte definiert.',
            templates_found: 'Vorlagen gefunden',
            all_categories: 'Alle Kategorien',
        },
        workflows: {
            client_org: 'Kunden-Organisation',
            title: 'Workflow Instanzen',
            new: 'Neu Workflow',
            template: 'Vorlage',
            partner: 'Geschäftspartner',
            current_step: 'Aktuell Schritt',
            progress: 'Fortschritt',
            no_workflows: 'Keine Workflow Instanzen gefunden.',
            instances_found: 'Instanzen gefunden',
        },
        escalation: {
            title: 'Eskalation Dashboard',
            ticket: 'Anfrage',
            partner: 'Geschäftspartner',
            escalated_at: 'Eskaliert At',
            reason: 'Grund',
            no_escalations: 'Keine escalations gefunden.',
            time_in_tier: 'Zeit in Stufe',
            auto_evaluate: 'Auto Bewerten',
            priority_critical: 'Kritisch',
            priority_high: 'Hoch',
            priority_medium: 'Mittel',
            priority_low: 'Low',
            deescalated_from_dashboard: 'De-escalated von Dashboard',
            sla_breached: 'SLA Verletzt',
            sla_remaining: 'SLA Verbleibend: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Kunden-Organisation',
            title: 'Berichte',
            partner: 'Geschäftspartner',
            report_type: 'Bericht Typ',
            generated_at: 'Generiert At',
            no_reports: 'Keine reports gefunden.',
            generate: 'Generieren Bericht',
        },
        audit: {
            title: 'Partner-Prüfprotokoll',
            event_type: 'Ereignis Typ',
            entity_type: 'Entität Typ',
            entity: 'Entität',
            entity_id: 'Entität ID',
            actor: 'Akteur',
            timestamp: 'Zeitstempel',
            no_events: 'Keine Audit Ereignisse gefunden.',
            events_found: 'Ereignisse gefunden',
            client_org: 'Kundenorganisation',
            all_actors: 'Alle Akteure',
            event_type_search: 'Ereignistyp suchen',
            event_type_created: 'Erstellt',
            event_type_updated: 'Aktualisiert',
            event_type_deleted: 'Gelöscht',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Partnerorganisation',
            entity_type_service_tier: 'Serviceebene',
            entity_type_deliverable_template: 'Lieferbare Vorlage',
            entity_type_deliverable_instance: 'Lieferbare Instanz',
            entity_type_brand_profile: 'Markenprofil',
            entity_type_support_ticket: 'Support-Ticket',
            entity_type_workflow_template: 'Workflow-Vorlage',
            entity_type_workflow_instance: 'Workflow-Instanz',
            entity_type_generated_report: 'Generierter Bericht',
            entity_type_audit_export: 'Audit-Export',
        },
        no_permission_title: 'Unzureichende Berechtigungen',
        no_permission_message: 'Sie haben keine Berechtigung, auf den Partnerarbeitsbereich zuzugreifen.',
        status_active: 'Aktiv',
        status_suspended: 'Suspendiert',
        status_offboarded: 'Offboarded',
        status_completed: 'Abgeschlossen',
        status_cancelled: 'Abgesagt',
        status_deprecated: 'Veraltet',
        status_new: 'Neu',
        status_triaged: 'Triagiert',
        status_in_progress: 'In Bearbeitung',
        status_waiting_on_client: 'Warten auf den Kunden',
        status_waiting_on_vendor: 'Warten auf den Verkäufer',
        status_escalated: 'Eskaliert',
        status_resolved: 'Gelöst',
        status_closed: 'Geschlossen',
        status_pending: 'Ausstehend',
        status_skipped: 'Übersprungen',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Onboarding',
        category_incident_escalation: 'Eskalation des Vorfalls',
        category_config_change: 'Konfigurationsänderung',
        category_periodic_review: 'Regelmäßige Überprüfung',
        category_offboarding: 'Offboarding',
        category_custom: 'Benutzerdefiniert',
        category_configuration: 'Konfiguration',
        category_incident: 'Vorfall',
        category_compliance_question: 'Compliance-Frage',
        category_feature_request: 'Funktionsanfrage',
        category_bug_report: 'Fehlerbericht',
        priority_critical: 'Kritisch',
        priority_high: 'Hoch',
        priority_medium: 'Mittel',
        priority_low: 'Niedrig',
        tier_silver: 'Silber',
        tier_gold: 'Gold',
        tier_platinum: 'Platin',
        report_type_compliance_summary: 'Compliance-Zusammenfassung',
        report_type_risk_overview: 'Risikoübersicht',
        report_type_incident_report: 'Vorfallbericht',
        report_type_consent_status: 'Einwilligungsstatus',
        report_type_program_maturity: 'Programmreife',
        report_type_executive_pack: 'Executive-Paket',
        note_type_internal: 'Intern',
        note_type_client_visible: 'Kunde sichtbar',
        status_updated: 'Status erfolgreich aktualisiert',
        deliverable_status_updated: 'Lieferstatus erfolgreich aktualisiert',
        ticket_status_updated: 'Ticketstatus erfolgreich aktualisiert',
        ticket_escalated: 'Das Ticket wurde erfolgreich eskaliert',
        note_added: 'Notiz erfolgreich hinzugefügt',
        attachment_uploaded: 'Anhang erfolgreich hochgeladen',
        ticket_deescalated: 'Das Ticket wurde erfolgreich deeskaliert',
        auto_evaluation_completed: 'Automatische Bewertung abgeschlossen',
        template_created: 'Workflow-Vorlage erfolgreich erstellt',
        template_updated: 'Workflow-Vorlage erfolgreich aktualisiert',
        brand_set_default: 'Markenprofil erfolgreich als Standard festgelegt',
        brand_created: 'Markenprofil erfolgreich erstellt',
        brand_updated: 'Markenprofil erfolgreich aktualisiert',
        tier_created: 'Serviceebene erfolgreich erstellt',
        tier_updated: 'Die Serviceebene wurde erfolgreich aktualisiert',
        tier_deprecated: 'Die Dienstebene wurde erfolgreich eingestellt',
        tier_activated: 'Servicestufe erfolgreich aktiviert',
        period: 'Zeitraum',
        format: 'Formatieren',
        period_start: 'Periodenbeginn',
        period_end: 'Periodenende',
        reports_count: '{count} Berichte',
        reports_count_one: '{count} Bericht',
        metric: 'Metrisch',
        value: 'Wert',
        showing_chart_view: 'Diagrammansicht wird angezeigt',
        showing_table_view: 'Datentabellenansicht wird angezeigt',
        evidence_ids_placeholder: 'z.B. ev-001, ev-002',
        confirm_cancel_workflow: 'Möchten Sie diesen Workflow wirklich abbrechen? Diese Aktion kann nicht rückgängig gemacht werden.',
        export_csv: 'Exportieren CSV',
        compliance_qualifications_title: 'Qualifikationen für den Compliance-Service',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Regulierungsausgabeausrichtung',
        regulatory_output_desc: 'Jeder Vorlagentyp ist einer regulatorischen Ausgabekategorie zugeordnet: Berichte erstellen Compliance-Dokumentation, Überprüfungen validieren die Wirksamkeit von Kontrollen, Bewertungen decken Risikobewertungen ab (DPIA, Lückenanalyse), Besprechungen dokumentieren Governance-Sitzungen. Nachweisanforderungen definieren die Artefakte, auf die sich jeder Liefergegenstand beziehen muss, um für die Prüfung bereit zu sein.',
        sla_compliance_title: 'SLA Verfolgung der Compliance-Lieferung',
        sla_compliance_desc: 'SLAs für Reaktions- und Lösungszeiten sind vertraglich bindend für die Bereitstellung von Compliance-Services. Die Überprüfungshäufigkeit bestimmt, wie oft die Compliance-Ergebnisse der Partner bewertet werden. SLA-Verstöße bei Compliance-kritischen Tickets lösen eine automatische Eskalation aus und werden im Partner Audit Trail für regulatorische Beweise protokolliert.',
        regulatory_engagement_title: 'Muster des regulatorischen Engagements',
        regulatory_engagement_desc: 'Workflow-Kategorien werden den Arten von regulatorischen Eingriffen zugeordnet: Onboarding umfasst die anfängliche Compliance-Einrichtung und Lückenanalyse, die regelmäßige Überprüfung übernimmt wiederkehrende Compliance-Bewertungen, Incident Escalation verwaltet die Reaktion auf regulatorische Verstöße, Offboarding stellt sicher, dass die Datenaufbewahrungspflichten erfüllt werden. Für jeden Schritt kann die Sammlung von Nachweisen zur Vollständigkeit des Prüfprotokolls erforderlich sein.',
        compliance_escalation_title: 'Compliance-kritische Eskalationsverfahren',
        compliance_escalation_desc: 'L1 kümmert sich um Standardbetriebsprobleme. L2 deckt Compliance-relevante Fehler ab (Versäumung regulatorischer Fristen, Kontrolllücken). L3 ist für Szenarien von Verstößen gegen Vorschriften reserviert, die eine sofortige Aufmerksamkeit der Geschäftsleitung erfordern. SLA Verstoß gegen L2/L3-Tickets generiert automatisch Audit-Trail-Einträge. Alle Eskalations- und Deeskalationsaktionen werden permanent protokolliert.',
        audit_regulatory_title: 'Regulatorischer Beweispfad',
        audit_regulatory_desc: 'Dieser Prüfpfad liefert manipulationssichere Aufzeichnungen zur Einhaltung gesetzlicher Vorschriften. Jede Partneraktion (Erstellen, Aktualisieren, Löschen, Eskalation, Statusänderung) wird mit Akteuridentität, Zeitstempel und vollständiger Nutzlastdifferenz protokolliert. Verwenden Sie Entitätstypfilter, um Beweise nach Compliance-Domäne zu isolieren. Exportieren Sie gefilterte Ergebnisse, um eine revisionssichere Dokumentation für behördliche Inspektionen zu erstellen.',
        evidence_trail_title: 'Modulübergreifender Beweispfad',
        evidence_trail_desc: 'Jeder Workflow-Schritt kann Beweis-IDs sammeln, die modulübergreifend mit Liefergegenständen, Prüfereignissen und Compliance-Artefakten verknüpft sind. Durch das Vorrücken eines Schritts mit Beweis-IDs entsteht eine überprüfbare Kette, die die Arbeitsabläufe der Partner mit der behördlichen Dokumentation verbindet. Abgeschlossene Arbeitsabläufe mit vollständigen Beweisketten dienen als Nachweis für die Erfüllung des Compliance-Engagements.',
        evidence_ids_label: 'Beweis-IDs',
    },
    org_groups: {
        title: 'Organisationsgruppen',
        new: 'Erstellen Gruppe',
        group_name: 'Gruppe Name',
        member_count: 'Mitglied Anzahl',
        root_org: 'Stamm Org',
        no_groups: 'Keine org groups gefunden.',
        add_organization: 'Organisation hinzufügen',
        select_organization: 'Organisation auswählen...',
        no_available_orgs: 'Keine verfügbaren Organisationen zum Hinzufügen.',
        no_members: 'Keine Mitgliedsorganisationen in dieser Gruppe.',
        member_added: 'Organisation zur Gruppe hinzugefügt.',
        member_removed: 'Organisation aus der Gruppe entfernt.',
        already_in_group: 'Diese Organisation ist bereits in einer Gruppe.',
        org_not_found: 'Organisation nicht gefunden.',
        add_member_error: 'Organisation konnte nicht zur Gruppe hinzugefügt werden.',
        remove_member_error: 'Organisation konnte nicht aus der Gruppe entfernt werden.',
        confirm_remove_member: 'Sind Sie sicher, dass Sie "{name}" aus dieser Gruppe entfernen möchten?',
        load_error: 'Org-Gruppendetails konnten nicht geladen werden.',
        page_title_list: 'Organisationsgruppen',
        page_title_detail: 'Details zur Organisationsgruppe',
        page_title_create: 'Organisationsgruppe erstellen',
        page_title_settings: 'Organisationsgruppeneinstellungen',
        page_title_users: 'Gruppenbenutzer',
        page_title_reports: 'Gruppenberichte',
        page_title_dashboard: 'Gruppen-Dashboard',
        page_title_audit: 'Gruppenprüfung',
        page_title_org_audit: 'Audit der Organisationsgruppe',
        page_title_shared_vendors: 'Geteilte Anbieter',
        page_title_shared_frameworks: 'Gemeinsame Frameworks',
        page_title_shared_forms: 'Freigegebene Formularvorlagen',
        page_title_merged_vendors: 'Zusammengeschlossene Anbieter',
        page_title_settings_inheritance: 'Vererbung der Einstellungen',
        announce_loaded_groups: '{count} Organisationsgruppen geladen',
        announce_loaded_group: 'Geladene Gruppe {name}',
        announce_loaded_users: 'Geladene {count} Gruppenbenutzer',
        announce_loaded_reports: '{count} Berichte geladen',
        announce_loaded_vendors: 'Geladen {count} freigegebene Anbieter',
        announce_loaded_frameworks: '{count} freigegebene Frameworks geladen',
        announce_loaded_templates: '{count} freigegebene Formularvorlagen geladen',
        announce_loaded_audit: '{count} Prüfereignisse geladen',
        announce_loaded_merged: 'Geladene {shared} freigegebene und {local} lokale Anbieter',
        announce_dashboard_loaded: 'Dashboard geladen.',
        announce_dashboard_refreshed: 'Dashboard aktualisiert.',
        announce_dashboard_refresh_failed: 'Das Dashboard konnte nicht aktualisiert werden.',
        announce_refreshing: 'Erfrischendes Dashboard...',
        announce_settings_loaded: 'Einstellungsvererbung geladen.',
        announce_settings_saved: 'Einstellungen erfolgreich gespeichert.',
        announce_saving_settings: 'Einstellungen speichern...',
        announce_error_saving: 'Fehler beim Speichern der Einstellungen.',
        announce_group_dissolved: 'Gruppe aufgelöst.',
        announce_creating_group: 'Organisationsgruppe wird erstellt...',
        announce_group_created: 'Organisationsgruppe erfolgreich erstellt.',
        announce_error_creating: 'Fehler beim Erstellen der Organisationsgruppe.',
        announce_vendor_published: 'Anbieter erfolgreich veröffentlicht.',
        announce_vendor_retired: 'Der Anbieter ist im Ruhestand.',
        announce_framework_published: 'Framework erfolgreich veröffentlicht.',
        announce_framework_retired: 'Framework im Ruhestand.',
        announce_template_published: 'Formularvorlage erfolgreich veröffentlicht.',
        announce_template_retired: 'Formularvorlage wurde eingestellt.',
        announce_role_assigned: 'Rolle erfolgreich zugewiesen.',
        announce_error_assign_role: 'Fehler beim Zuweisen der Rolle.',
        announce_role_revoked: 'Rolle erfolgreich widerrufen.',
        announce_report_generating: 'Bericht wird erstellt...',
        announce_report_requested: 'Berichterstellung angefordert.',
        announce_error_report: 'Fehler beim Anfordern eines Berichts.',
        announce_report_deleted: 'Bericht gelöscht.',
        announce_search_applied: 'Suche angewendet.',
        announce_search_cleared: 'Suche gelöscht.',
        announce_tab_selected: 'Registerkarte {tab} ausgewählt',
        announce_error_add_member: 'Fehler beim Hinzufügen eines Mitglieds zur Gruppe.',
        announce_category_reverted: '{category} wurde auf vom übergeordneten Element geerbt zurückgesetzt.',
        announce_category_overridden: '{category} auf lokale Überschreibung gesetzt.',
        announce_override_saved: '{category} Überschreibung gespeichert.',
        announce_export_started: 'Audit wird exportiert als {format}...',
        announce_export_completed: 'Export als {format} abgeschlossen.',
        announce_export_failed: 'Der Export ist fehlgeschlagen.',
        announce_filter_org: 'Gefiltert nach Organisation {name}',
        announce_filter_org_cleared: 'Organisationsfilter gelöscht',
        announce_filter_event: 'Gefiltert nach Ereignistyp {type}',
        announce_filter_cleared: 'Filter gelöscht',
        announce_download: 'Herunterladen von {name} als {format}',
        error_load_groups: 'Organisationsgruppen konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
        error_load_users: 'Gruppenbenutzer konnten nicht geladen werden.',
        error_load_reports: 'Gruppenberichte konnten nicht geladen werden.',
        error_load_dashboard: 'Das Gruppen-Dashboard konnte nicht geladen werden.',
        error_load_settings: 'Die Gruppeneinstellungen konnten nicht geladen werden.',
        error_load_vendors: 'Zusammengeführte Anbieter konnten nicht geladen werden.',
        error_save_settings: 'Einstellungen konnten nicht gespeichert werden.',
        error_dissolve_group: 'Die Gruppe konnte nicht aufgelöst werden.',
        error_assign_role: 'Rolle konnte nicht zugewiesen werden.',
        error_create_group: 'Die Organisationsgruppe konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
        error_request_report: 'Fehler beim Anfordern eines Berichts.',
        error_group_name_required: 'Gruppenname ist erforderlich.',
        success_settings_saved: 'Einstellungen erfolgreich gespeichert.',
        confirm_retire_vendor: 'Sind Sie sicher, dass Sie den Anbieter „{name}“ aus dem Verkehr ziehen möchten?',
        confirm_retire_framework: 'Sind Sie sicher, dass Sie das Framework „{name}“ außer Betrieb nehmen möchten?',
        confirm_retire_template: 'Sind Sie sicher, dass Sie die Formularvorlage „{name}“ zurückziehen möchten?',
        confirm_dissolve_group: 'Sind Sie sicher, dass Sie „{name}“ auflösen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
        confirm_revoke_role: 'Sind Sie sicher, dass Sie die Rolle „{role}“ von „{name}“ widerrufen möchten?',
        confirm_delete_report: 'Sind Sie sicher, dass Sie den Bericht „{name}“ löschen möchten?',
        dissolve_group: 'Gruppe auflösen',
        dissolve_warning: 'Dadurch wird die Organisationsgruppe dauerhaft aufgelöst. Alle Mitgliedsorganisationen werden entfernt.',
        count_groups: '{count} Gruppe',
        count_groups_plural: '{count} Gruppen',
        count_users: '{count} Benutzer',
        count_users_plural: '{count} Benutzer mit Gruppenrollen',
        count_events: '{count} Ereignis',
        count_events_plural: '{count} Ereignisse',
        count_reports: '{count} Bericht',
        count_reports_plural: '{count} Berichte',
        count_vendors: '{count} Anbieter, der in der gesamten Gruppe geteilt wird',
        count_vendors_plural: '{count} Anbieter, die in der gesamten Gruppe geteilt werden',
        count_frameworks: '{count} Framework, das in der gesamten Gruppe geteilt wird',
        count_frameworks_plural: '{count} Frameworks, die in der gesamten Gruppe geteilt werden',
        count_templates: '{count} Vorlage, die in der gesamten Gruppe geteilt wird',
        count_templates_plural: '{count} Vorlagen, die in der gesamten Gruppe geteilt werden',
        count_shared_vendors: '{count} geteilte Anbieter',
        count_local_vendors: '{count} lokale Anbieter',
        merged_combined_view: 'Kombinierte Ansicht von {shared} freigegebenen und {local} lokalen Anbietern',
        dashboard: {
            title: 'Gruppenübersicht',
            loading: 'Lade Dashboard Daten...',
            total_groups: 'Gesamt Gruppen',
            total_members: 'Gesamt Mitglieder',
            active_groups: 'Aktiv Gruppen',
            overall_compliance_score: 'Gesamt-Compliance-Score',
            group_risk_level: 'Gruppenrisikostufe',
            sla_compliance: 'SLA Compliance',
            compliance_posture: 'Compliance-Haltung',
            risk_heatmap: 'Risiko-Heatmap',
            sla_performance: 'SLA Leistung',
            recent_incidents: 'Aktuelle Vorfälle',
            recent_activity: 'Letzte Aktivität',
            refresh_label: 'Dashboard-Daten aktualisieren',
            score_good: 'Gut',
            score_needs_attention: 'Braucht Aufmerksamkeit',
            score_critical: 'Kritisch',
            trend_up: 'Tendenz steigend',
            trend_down: 'Abwärtstrend',
            trend_stable: 'Stabil',
            risk_low: 'Low',
            risk_medium: 'Mittel',
            risk_high: 'Hoch',
            risk_critical: 'Kritisch',
            risk_unknown: 'Unbekannt',
            col_organization: 'Organisation',
            col_score: 'Punktzahl',
            col_trend: 'Trend',
            col_open_issues: 'Offene Probleme',
            col_critical_issues: 'Kritische Probleme',
            col_health: 'Gesundheit',
            col_vendor_risk: 'Lieferantenrisiko',
            col_data_risk: 'Datenrisiko',
            col_compliance_risk: 'Compliance-Risiko',
            col_total_cases: 'Gesamtzahl der Fälle',
            col_on_time: 'Pünktlich',
            col_breached: 'Durchbrochen',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Durchschnittliche Tage',
            sr_vendor_risk: 'Risikobewertung des Anbieters: {score}',
            sr_data_risk: 'Datenrisikobewertung: {score}',
            sr_compliance_risk: 'Compliance-Risikobewertung: {score}',
            sr_sla_breaches: 'Warnung: {count} SLA Verstöße',
            sr_severity: 'Schweregrad: {severity}',
        },
        detail: {
            title: 'Gruppendetails',
            members: 'Mitglieder',
            settings: 'Einstellungen',
            audit: 'Prüfung',
            sr_loading: 'Details zur Organisationsgruppe werden geladen',
            sr_loading_hierarchy: 'Ladehierarchie',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'Mitgliedsorganisation',
            root_org_label: 'Wurzelorganisation',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Gruppenbenutzer',
            add_user: 'Hinzufügen Benutzer',
            no_users: 'No users in dieses Gruppe.',
            role_group_admin: 'Gruppenadministrator',
            role_group_reader: 'Gruppenleser',
            sr_loading: 'Gruppenbenutzer werden geladen',
        },
        reports: {
            title: 'Gruppenberichte',
            no_reports: 'Keine reports gefunden.',
            generate_report: 'Neuen Bericht erstellen',
            report_type_compliance: 'Compliance-Zusammenfassung',
            report_type_risk: 'Risikoübersicht',
            report_type_sla: 'SLA Leistung',
            report_type_incident: 'Vorfallbericht',
            report_type_executive: 'Executive Brief',
            report_type_board: 'Vorstandsbericht',
            status_queued: 'In der Warteschlange',
            status_generating: 'Generieren',
            status_completed: 'Abgeschlossen',
            status_failed: 'Fehlgeschlagen',
            period_start: 'Periodenbeginn',
            period_end: 'Periodenende',
            sr_loading: 'Gruppenberichte werden geladen',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Gemeinsame Lieferanten',
            no_vendors: 'Keine shared Anbieter gefunden.',
            merged_vendors: 'Zusammengeführt Anbieter',
            publish_form_label: 'Gemeinsamen Anbieter veröffentlichen',
            sr_loading: 'Geteilte Anbieter werden geladen',
            sr_loading_merged: 'Zusammengeführte Anbieter werden geladen',
            local_vendors: 'Lokale Anbieter',
            source_shared: 'Von der Gruppe geteilt',
            source_local: 'Lokaler Anbieter',
            category_technology: 'Technologie',
            category_consulting: 'Beratung',
            category_outsourcing: 'Outsourcing',
            category_cloud: 'Wolke',
            category_data_processing: 'Datenverarbeitung',
            category_other: 'Andere',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Mittel',
            risk_tier_high: 'Hoch',
            risk_tier_critical: 'Kritisch',
            compliance_compliant: 'Konform',
            compliance_non_compliant: 'Nicht konform',
            compliance_pending: 'Ausstehend',
            compliance_under_review: 'Wird überprüft',
            status_active: 'Aktiv',
            status_retired: 'Im Ruhestand',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Framework-Code',
            title: 'Gemeinsame Rahmenwerke',
            no_frameworks: 'Keine shared frameworks gefunden.',
            publish_form_label: 'Gemeinsames Framework veröffentlichen',
            sr_loading: 'Gemeinsam genutzte Frameworks werden geladen',
            status_active: 'Aktiv',
            status_retired: 'Im Ruhestand',
        },
        shared_forms: {
            title: 'Gemeinsame Formularvorlagen',
            no_forms: 'Keine shared Formular Vorlagen gefunden.',
            publish_form_label: 'Freigegebene Formularvorlage veröffentlichen',
            sr_loading: 'Laden freigegebener Formularvorlagen',
            status_active: 'Aktiv',
            status_retired: 'Im Ruhestand',
        },
        audit: {
            title: 'Gruppenprüfung',
            consolidated_title: 'Konsolidiert Audit Trail',
            consolidated_subtitle: 'Cross-organization Audit Ereignisse für dieses Gruppe',
            audit_log_title: 'Gruppe Audit Protokoll',
            event_type: 'Ereignis Typ',
            actor: 'Akteur',
            timestamp: 'Zeitstempel',
            source_org: 'Quelle Org',
            org: 'Org',
            summary: 'Zusammenfassung',
            details: 'Einzelheiten',
            no_events: 'Keine Audit Ereignisse gefunden.',
            all_events: 'Alle Ereignisse',
            all_orgs: 'Alle Orgs',
            organization: 'Organisation',
            from: 'Von',
            to: 'To',
            search_events: 'Suche Ereignisse...',
            clear: 'Löschen',
            events_24h: 'Ereignisse (24h)',
            events_7d: 'Ereignisse (7d)',
            active_orgs: 'Aktiv Orgs',
            exporting: 'Exportiere...',
            export_csv: 'Exportieren CSV',
            export_json: 'Exportieren JSON',
            back_to_group: 'Back zu Gruppe',
            back_to_detail: 'Back zu Detail',
            sr_loading: 'Audit-Ereignisse werden geladen',
            sr_loading_summary: 'Audit-Zusammenfassung wird geladen',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Gruppe Einstellungen',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Einstellungen Inheritance',
            inheritance_subtitle: 'Konfigurieren Sie, welche Einstellungskategorien von der Gruppe erben oder lokale Überschreibungen verwenden',
            sla_settings: 'SLA Einstellungen',
            calendar_settings: 'Kalender Einstellungen',
            notification_settings: 'Benachrichtigung Einstellungen',
            intake_settings: 'Aufnahme Einstellungen',
            cmp_integration: 'CMP-Integration',
            overridden: 'Überschrieben',
            inheriting: 'Vererbung',
            hide_defaults: 'Ausblenden Standardwerte',
            show_group_defaults: 'Anzeigen Gruppe Standardwerte',
            group_defaults: 'Gruppe Standardwerte',
            inherit_from_group: 'Erben von Gruppe',
            use_local_override: 'Use Local Überschreiben',
            local_override_json: 'Local Überschreiben (JSON)',
            save_override: 'Speichern Überschreiben',
            saving: 'Speichere...',
            sr_loading: 'Vererbung der Ladeeinstellungen',
            sr_loading_settings: 'Gruppeneinstellungen werden geladen',
            sr_enabled: 'Aktiviert',
            sr_disabled: 'Deaktiviert',
            sr_override_active: 'Lokale Übersteuerung aktiv',
            sr_inherited: 'Vom Elternteil geerbt',
            danger_zone: 'Gefahrenzone',
            sharing_options: 'Freigabeoptionen',
            advanced_options: 'Erweiterte Optionen',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Aktiv',
        status_dissolved: 'Aufgelöst',
        status_pending: 'Ausstehend',
        status_suspended: 'Suspendiert',
    },
    integrations: {
        nav: {
            webhooks: 'Webhook-Endpunkte',
            domains: 'Domänen',
            export_schedules: 'Exportzeitpläne',
            email_log: 'E-Mail-Protokoll',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Benutzer',
            roles: 'Rollen',
            profile: 'Mein Profil',
        },
        security: {
            mfa: 'MFA-Einstellungen',
            sso: 'SSO-Konfiguration',
            api_keys: 'API-Schlüssel',
            service_accounts: 'Dienstkonten',
            ip_allowlist: 'IP-Positivliste',
            delegations: 'Delegierungen',
            break_glass: 'Notfallzugriff',
            session_settings: 'Sitzungseinstellungen',
            active_sessions: 'Aktive Sitzungen',
            access_reviews: 'Zugriffsüberprüfungen',
            my_reviews: 'Meine Überprüfungen',
            role_analytics: 'Rollenanalysen',
            attestation_schedules: 'Bestätigungszeitpläne',
            sod_rules: 'SoD-Regeln',
            sod_violations: 'SoD-Verstöße',
        },
        module_config: {
            title: 'Modulkonfiguration',
            subtitle: 'Aktivieren oder deaktivieren Sie Funktionsmodule für Ihre Organisation. Deaktivierte Module werden in der Navigation ausgeblendet.',
            enabled: 'Aktiviert',
            disabled: 'Deaktiviert',
            save: 'Konfiguration speichern',
            saving: 'Speichern...',
            saved: 'Konfiguration erfolgreich gespeichert',
            nav_label: 'Modulkonfiguration',
            settings: 'Einstellungen',
            webhooks: 'Webhook-Endpunkte',
            domains: 'Domänen',
            export_schedules: 'Exportzeitpläne',
            email_log: 'E-Mail-Protokoll',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Dateien',
            configuration: 'Konfiguration',
            browser: 'Dateibrowser',
            dashboard: 'Dateien-Dashboard',
        },
        immutable_audit_trails: {
            nav_group_label: 'Prüfung und Nachweis',
            nav_settings: 'Einstellungen',
            nav_status: 'Beweisstatus',
            nav_integrity_center: 'Integritätszentrum',
            nav_usage: 'Nutzung & Credits',
            settings_title: 'Unveränderliche Prüfprotokolle – Einstellungen',
            settings_subtitle: 'Organisationsspezifische Einstellungen für das Immutable Audit Trails-Add-on.',
            settings_banner: 'Das Add-on „Immutable Audit Trails“ ist für Ihre Organisation nicht aktiviert. Aktivieren Sie es hier, um mit der Verankerung von Nachweisen für Tier-A- und Tier-B-Prüfungsereignisse zu beginnen.',
            status_title: 'Unveränderliche Prüfprotokolle – Beweisstatus',
            status_subtitle: 'Modulübergreifende Proof-Beziehungen pro Ereignis.',
            status_banner: 'Das Add-on „Immutable Audit Trails“ ist für Ihre Organisation nicht aktiviert. Besuchen Sie die Einstellungen, um es zu aktivieren.',
            integrity_center_title: 'Unveränderliche Prüfprotokolle – Integrity Center',
            integrity_center_subtitle: 'Überprüfen Sie Beweise und exportieren Sie Integrity Packs.',
            integrity_center_banner: 'Das Add-on „Immutable Audit Trails“ ist für Ihre Organisation nicht aktiviert. Besuchen Sie die Einstellungen, um es zu aktivieren, bevor Sie die Überprüfung durchführen oder Integrity Packs exportieren.',
            usage_title: 'Unveränderliche Audit Trails – Nutzung und Credits',
            usage_subtitle: 'Guthaben, Paketkäufe und Nutzungshistorie.',
            usage_banner: 'Aktivieren Sie unveränderliche Prüfprotokolle in den Einstellungen, um Proof-Credits zu verbrauchen.',
            module_tips_placeholder: 'Tipps und Anleitungen für diese Oberfläche finden Sie im Hilfecenter.',
            column_label: 'Beweis',
            no_proof_aria: 'Kein Beweis aufgezeichnet',
            proof_status_receipt_label: 'Beleg {id}',
            in_progress_hint: 'In Bearbeitung – wird automatisch aktualisiert',
            status_pills: {
                pending: 'Ausstehend',
                submitted: 'Eingereicht',
                anchored: 'Verankert',
                confirmed: 'Bestätigt',
                retrying: 'Erneuter Versuch',
                paused_cap: 'Pausiert (Kappe)',
                failed: 'Fehlgeschlagen',
                failed_auth: 'Authentifizierung fehlgeschlagen',
            },
            status_tooltips: {
                pending: 'Ausstehend – in der Warteschlange für den Proof-Service. Wird in Kürze zu „Eingereicht“ verschoben; Es sind keine Maßnahmen erforderlich.',
                submitted: 'Eingereicht – Ihr Ereignis wurde an den Korrekturdienst gesendet. Im Batch-Modus wartet es darauf, versiegelt und in der Kette verankert zu werden. Automatisch aktualisiert; Es sind keine Maßnahmen erforderlich.',
                anchored: 'Verankert – der Stapel wurde in die Kette geschrieben und wartet auf die endgültige Bestätigung. Automatisch aktualisiert; Es sind keine Maßnahmen erforderlich.',
                confirmed: 'Bestätigt – der Beweis ist in der Kette verankert und überprüfbar.',
                retrying: 'Erneuter Versuch – ein vorübergehender Fehler ist aufgetreten. Der Abgleicher versucht es automatisch erneut. Es sind keine Maßnahmen erforderlich.',
                paused_cap: 'Pausiert – Ihre monatliche Proof-Credit-Obergrenze wurde erreicht. Lösen Sie weitere Credits ein oder warten Sie bis zum nächsten Abrechnungsmonat.',
                failed: 'Fehlgeschlagen – die Verankerung war nicht erfolgreich. Der Abgleicher wird es automatisch erneut versuchen; Wenden Sie sich an den Support, wenn das Problem weiterhin besteht.',
                failed_auth: 'Authentifizierung fehlgeschlagen – der Prüfdienst hat unsere Anmeldeinformationen abgelehnt. Überprüfen Sie die Seite „Einstellungen für unveränderliche Audit-Trails“.',
            },
            mode_pills: {
                direct: 'Direkt',
                batch: 'Charge',
            },
            drawer: {
                title: 'Beweisdetails',
                close: 'Schließen',
                section_proof: 'Beweis',
                section_batch: 'Batch-Mitgliedschaft',
                status: 'Status',
                proof_reference: 'Beweisreferenz',
                receipt_id: 'Empfangs-ID',
                taxonomy_class: 'Taxonomie-Kurs',
                units_consumed: 'Verbrauchte Einheiten',
                created_at: 'Erstellt',
                verification_ref: 'Referenz zur Kettenverifizierung',
                chain: 'Kette',
                tx_hash: 'Tx-Hash',
                block_ref: 'Blockreferenz',
                batch_reference: 'Chargenreferenz',
                leaf_index: 'Blattindex',
                local_event_hash: 'Lokaler Ereignis-Hash',
                show_inclusion: 'Einschlusspfad anzeigen',
                hide_inclusion: 'Einschlusspfad ausblenden',
                privacy_note: 'Nutzlast wird gehasht; Für diese Veranstaltung wurden von ComplianceOne keine personenbezogenen Daten hinterlassen.',
                verify_now: 'Jetzt überprüfen',
                verify_pending: 'Verifizierung läuft…',
                verify_verified: 'Verifiziert – lokale und kettenseitige Prüfungen bestanden.',
                verify_issue_prefix: 'Verifizierungsproblem: ',
                verify_failed: 'Der Bestätigungsaufruf ist fehlgeschlagen.',
                verify_result_prefix: 'Überprüfungsurteil: ',
                verify_pending_note: 'Überprüfung ausstehend: Dieser Beweis wartet noch auf die Verankerung in der Kette. Versuchen Sie es erneut, nachdem die Charge versiegelt wurde.',
                awaiting_anchor_note: 'Dieser Beweis ist Teil einer offenen Charge. Die Verifizierung wird verfügbar, sobald der Batch versiegelt und in der Kette verankert ist – normalerweise innerhalb des konfigurierten Batch-Fensters der Organisation. Das Statusabzeichen wird automatisch aktualisiert.',
                terminal_error_note: 'Dieser Beweis konnte nicht verankert werden. Der Proof-Status-Abgleicher wird es automatisch erneut versuchen; Bei der manuellen Überprüfung gibt es noch nichts zu überprüfen.',
            },
            integrity_center: {
                section_run: 'Führen Sie die Überprüfung durch',
                section_progress: 'Lauffortschritt',
                section_issues: 'Probleme',
                period_start: 'Beginn der Periode',
                period_end: 'Periodenende',
                module_filter: 'Module (durch Kommas getrennt, optional)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Führen Sie die Überprüfung durch',
                export_button: 'Integrity Pack exportieren',
                export_success: 'Integrity Pack exportiert.',
                export_failed: 'Der Export ist fehlgeschlagen.',
                empty_disabled: 'Aktivieren Sie unveränderliche Prüfprotokolle in den Einstellungen, um Verifizierungsdurchgänge durchzuführen.',
                error_period_required: 'Wählen Sie ein Start- und Enddatum.',
                error_start_run: 'Der Lauf konnte nicht gestartet werden.',
                processed: 'Verarbeitet: {processed} von {total}',
                col_verdict: 'Urteil',
                col_count: 'Zählen',
                col_proof_link: 'Beweislink',
                col_module: 'Modul',
                col_event_type: 'Ereignistyp',
                col_expected: 'Erwarteter Hash',
                col_recomputed: 'Neu berechneter Hash',
                col_raised_at: 'Aufgewachsen bei',
                any_verdict: 'Irgendein Urteil',
                no_issues: 'Keine Probleme gefunden.',
            },
            settings_page: {
                load_error: 'Die Einstellungen konnten nicht geladen werden.',
                save: 'Speichern',
                save_success: 'Einstellungen gespeichert.',
                save_error: 'Speichern fehlgeschlagen.',
                discard: 'Änderungen verwerfen',
                read_only_banner: 'Sie haben nur Lesezugriff. Wenden Sie sich an einen Plattformadministrator, um Änderungen vorzunehmen.',
                section_status: 'Status und Aktivierung',
                enabled_label: 'Add-on aktiviert',
                enabled_help: 'Wenn diese Option aktiviert ist, wird jedes Tier-A- und Tier-B-Prüfungsereignis gehasht und verankert.',
                section_mode: 'Standard-Proof-Modus',
                mode_direct: 'Direkt (ein Anker pro Veranstaltung)',
                mode_batch: 'Batch (Merkle-Root-Anker pro Gruppe)',
                mode_hybrid: 'Hybrid (Registrierungsstandard pro Klasse)',
                batch_window_label: 'Batch-Fenster (Minuten, 1–60)',
                section_taxonomy: 'Taxonomie geschützter Ereignisse',
                taxonomy_help: 'Überschreibungen pro Ereignisklasse. Stufe A → Direktmodus, Stufe B → Stapelmodus, Aus → nicht geprüft.',
                taxonomy_search: 'Suchüberschreibungen',
                taxonomy_reset: 'Auf Standardeinstellungen zurücksetzen',
                taxonomy_event_class: 'Veranstaltungsklasse',
                taxonomy_override: 'Überschreiben',
                taxonomy_actions: 'Aktionen',
                taxonomy_clear: 'Klar',
                taxonomy_no_overrides: 'Keine Überschreibungen – Verwendung der Registrierungsstandardwerte.',
                taxonomy_add_class: 'event_class.code',
                taxonomy_add: 'Überschreibung hinzufügen',
                section_caps: 'Obergrenzen und Limits',
                cap_label: 'Monatliche Obergrenze (Einheiten; leer = unbegrenzt)',
                warn_label: 'Warnschwellenwert (Einheiten; muss kleiner als die Obergrenze sein)',
                auto_pause_label: 'Neue Proofs werden automatisch angehalten, sobald die Obergrenze erreicht ist',
                section_credentials: 'Anmeldeinformationen',
                credential_id: 'Anmeldeinformations-ID',
                credential_rotated_at: 'Zuletzt gedreht',
                rotate_button: 'Anmeldeinformationen rotieren',
                rotate_success: 'Berechtigungsnachweis geändert. Alte Anmeldeinformationen sind noch {n} Minuten gültig.',
                rotate_failed: 'Die Rotation der Anmeldedaten ist fehlgeschlagen.',
                rotate_modal_title: 'Anmeldeinformationen AesirX rotieren?',
                rotate_modal_body: 'Die Rotation stellt einen neuen Ausweis aus. Der alte Berechtigungsnachweis wird von AesirX für ein 60-minütiges Kulanzfenster weiterhin akzeptiert, damit Mitarbeiter während des Flugs ihre aktuellen Aufgaben erledigen können. Nach 60 Minuten wird der alte Ausweis widerrufen.',
                rotate_modal_confirm: 'Jetzt drehen',
                tier_a_off_modal_title: 'Proofing für eine Ereignisklasse deaktivieren?',
                tier_a_off_modal_body: 'Wenn Sie eine Ereignisklasse auf „Aus“ setzen, verhindert der Proof-Tap, dass diese Ereignisse verankert werden. Architektur §6 erläutert die regulatorische Implikation: Tier-A-Ereignisse sind regulierungsrelevant; Wenn Sie sie deaktivieren, kann dies die Verteidigungsfähigkeit Ihres Audit-Trails beeinträchtigen.',
                tier_a_off_modal_confirm: 'Ich verstehe – Override anwenden',
                error_warn_lt_cap: 'Der Warnschwellenwert muss unter der monatlichen Obergrenze liegen.',
                error_window_range: 'Das Batch-Fenster muss zwischen 1 und 60 Minuten liegen.',
                error_run_in_progress: 'Ein Verifizierungslauf ist im Gange; Das Add-on kann derzeit nicht deaktiviert werden.',
            },
            status_page: {
                filter_status: 'Status',
                filter_mode: 'Proof-Modus',
                filter_module: 'Modul',
                filter_source_collection: 'Quellensammlung',
                filter_from_date: 'Von',
                filter_to_date: 'Zu',
                filter_search: 'Suchen',
                any_status: 'Beliebiger Status',
                any_mode: 'Jeder Modus',
                col_module: 'Modul',
                col_event_type: 'Ereignistyp',
                col_proof_status: 'Status',
                col_proof_mode: 'Modus',
                col_created_at: 'Erstellt',
                col_units: 'Einheiten',
                empty_disabled: 'Das Add-on „Immutable Audit Trails“ ist für Ihre Organisation nicht aktiviert. Besuchen Sie die Einstellungen, um es zu aktivieren.',
                empty_enabled_no_rows: 'Keine Proof-Links entsprechen den aktuellen Filtern.',
                export_csv: 'Exportieren CSV',
                export_xlsx: 'Exportieren XLSX',
                export_success: 'Export abgeschlossen',
                export_failed: 'Der Export ist fehlgeschlagen',
            },
            usage_page: {
                section_alerts: 'Warnungen',
                alert_dismiss: 'Entlassen',
                alert_dismiss_aria: 'Warnung schließen {id}',
                alert_dismissed: 'Warnung abgewiesen',
                alert_dismiss_failed: 'Warnung konnte nicht geschlossen werden',
                section_balance: 'Guthaben',
                balance_label: 'Verfügbare Credits',
                balance_hint: 'Verbleibende Einheiten (1 Einheit = 1 geschützter Ereignisnachweis).',
                section_current_month: 'Aktueller Monat',
                consumed_label: 'Verbraucht',
                granted_label: 'Zugegeben',
                refunded_label: 'Erstattet',
                net_label: 'Netz',
                section_history: 'Nutzungshistorie',
                history_kind: 'Freundlich',
                history_units: 'Einheiten',
                history_balance_after: 'Ausgleich danach',
                history_notes: 'Notizen',
                history_at: 'Wann',
                cap_warning_banner: 'Sie haben Ihren Warnschwellenwert für diesen Abrechnungsmonat überschritten. Erwägen Sie die Einlösung zusätzlicher Credits.',
                cap_paused_banner: 'Monatsobergrenze erreicht. Die Einreichung von Nachweisen wird bis zum nächsten Monat oder bis zur Erhöhung der Obergrenze ausgesetzt.',
                section_redeem: 'Guthabenpaket einlösen',
                redeem_purchase_id: 'Rechnungsnummer',
                redeem_package: 'Paket',
                redeem_button: 'Einlösen',
                redeem_pending: 'Einlösen…',
                redeem_success: 'Credits eingelöst',
                redeem_failed: 'Einlösen fehlgeschlagen',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'Einheiten',
                alert_dismissing: 'Entlassen…',
                alert_kind: {
                    cap_reached: 'Monatsobergrenze erreicht',
                    low_balance: 'Geringes Gleichgewicht',
                    auth_failure: 'Authentifizierungsfehler',
                    merkle_mismatch: 'Merkle-Nichtübereinstimmung',
                    batch_terminal_failure: 'Fehler im Batch-Terminal',
                    direct_terminal_failure: 'Fehler am Terminal für direkte Übermittlung',
                },
                usage_kind: {
                    consume: 'Konsumieren',
                    refund: 'Rückerstattung',
                    grant: 'Grant',
                    adjustment: 'Anpassung',
                    reservation: 'Reservierung',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Anlasser',
                package_growth: 'Wachstum',
                package_enterprise: 'Unternehmen',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Mehr Guthaben kaufen',
                    purchase_title: 'Mehr Guthaben kaufen',
                    purchase_subtitle: 'Wählen Sie ein Paket. Sie werden zur sicheren Stripe-Kasse weitergeleitet, und Ihre Rechnung erhalten Sie per E-Mail.',
                    checkout_button: 'Weiter zur Kasse',
                    checkout_pending: 'Kasse wird geöffnet…',
                    cancel_button: 'Abbrechen',
                    purchase_error: 'Die Kasse konnte nicht gestartet werden. Bitte versuchen Sie es in Kürze erneut.',
                    redeem_subtitle: 'Bereits bezahlt? Wählen Sie das gekaufte Paket aus und geben Sie die Rechnungs-ID aus Ihrer E-Mail ein, um das Guthaben hinzuzufügen.',
                    error_not_found: 'Es wurde keine Rechnung mit dieser Nummer gefunden. Überprüfen Sie die Rechnungsnummer und versuchen Sie es erneut.',
                    error_not_paid: 'Diese Rechnung ist noch nicht als bezahlt markiert.',
                    error_org_mismatch: 'Diese Rechnung wurde nicht an Ihre Organisation ausgestellt.',
                    error_package_mismatch: 'Diese Rechnung stimmt nicht mit dem ausgewählten Paket überein.',
                    error_billing_unavailable: 'Der Abrechnungsdienst konnte nicht erreicht werden. Bitte versuchen Sie es in Kürze erneut.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Dateikonfiguration',
            subtitle: 'Konfigurieren Sie Datei-Upload-Limits, Aufbewahrungsrichtlinien und Außerkraftsetzungen je Besitzertyp für die Plattformanhangsoberfläche.',
            section_global: 'Globale Standardwerte',
            section_global_help: 'Diese Grenzwerte gelten für jede von Benutzern in Ihrer Organisation hochgeladene Datei, es sei denn, sie werden unten durch eine Überschreibung pro Besitzertyp geändert.',
            section_overrides: 'Überschreibungen je Besitzertyp',
            section_overrides_help: 'Überschreiben Sie die globalen Grenzwerte für einen bestimmten Eigentümertyp. Werte pro Eigentümertyp haben immer Vorrang vor der globalen Einstellung.',
            max_file_size_label: 'Maximale Dateigröße',
            max_file_size_hint: 'Pro einzelnem Upload (MiB)',
            max_total_attachments_label: 'Maximale Gesamtanzahl an Anhängen',
            max_total_attachments_hint: 'Über die gesamte Organisation hinweg',
            max_total_size_label: 'Maximaler Gesamtspeicher',
            max_total_size_hint: 'In der gesamten Organisation (MiB)',
            allowed_mime_types_label: 'Zulässige Dateitypen',
            allowed_mime_types_placeholder: 'Wählen Sie MIME-Typen aus',
            retention_days_label: 'Aufbewahrungsdauer (Tage)',
            retention_days_hint: 'Wie lange müssen Dateien aufbewahrt werden, bevor sie gelöscht werden können? Für keine Aufbewahrungsrichtlinie leer lassen.',
            legal_hold_label: 'Rechtlicher Aufbewahrungszeitraum',
            legal_hold_hint: 'Wenn diese Option aktiviert ist, erfordert das endgültige Löschen einer Datei dieses Eigentümertyps die Berechtigung zum Überschreiben der gesetzlichen Aufbewahrungsfrist und eine ausdrückliche Zustimmung des Betreibers.',
            save: 'Änderungen speichern',
            saving: 'Sparen…',
            reset: 'Zurücksetzen',
            save_success: 'Einstellungen gespeichert',
            empty_state_title: 'Plattformstandards verwenden',
            empty_state_body: 'Ihre Organisation verwendet derzeit die Standarddateirichtlinie der Plattform. Bearbeiten Sie einen beliebigen Wert unten, um eine Überschreibung auf Organisationsebene zu erstellen.',
            read_only_banner: 'Sie haben nur Lesezugriff auf die Dateikonfiguration. Wenden Sie sich an einen Plattformadministrator, um Änderungen vorzunehmen.',
            owner_type_default_badge: 'Standard',
            owner_type_overridden_badge: 'Überschrieben',
            owner_type_label: 'Besitzertyp',
            owner_type_collapse: 'Zusammenbruch',
            owner_type_expand: 'Erweitern',
            override_save: 'Außerkraftsetzung speichern',
            override_remove: 'Überschreibung entfernen',
            mime_labels: {
                'application/pdf': 'PDF Dokument',
                'image/png': 'PNG-Bild',
                'image/jpeg': 'JPEG-Bild',
                'image/jpg': 'JPG-Bild',
                'application/msword': 'Word-Dokument (Legacy)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word-Dokument',
                'application/vnd.ms-excel': 'Excel-Tabelle (alt)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel-Tabelle',
                'text/csv': 'CSV-Datei',
                'text/plain': 'Klartext',
                'message/rfc822': 'E-Mail-Nachricht',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Dateibrowser',
            subtitle: 'Durchsuchen, filtern und verwalten Sie alle im gesamten Unternehmen hochgeladenen Anhänge.',
            filter_owner_type: 'Besitzertyp',
            filter_uploader: 'Hochgeladen von',
            filter_from: 'Hochgeladen nachher',
            filter_to: 'Vorher hochgeladen',
            filter_size_min: 'Mindestgröße (MiB)',
            filter_size_max: 'Maximale Größe (MiB)',
            filter_mime: 'Dateityp',
            filter_deleted_state: 'Löschstatus',
            filter_deleted_any: 'Alle Dateien',
            filter_deleted_only: 'Nur gelöscht',
            filter_deleted_exclude: 'Ausschließen gelöscht',
            filter_orphaned: 'Nur verwaist',
            filter_search_placeholder: 'Dateiname suchen…',
            filter_apply: 'Wenden Sie Filter an',
            filter_clear: 'Filter löschen',
            col_filename: 'Dateiname',
            col_owner_type: 'Besitzertyp',
            col_owner_entity: 'Besitzer',
            col_uploader: 'Hochgeladen von',
            col_uploaded_at: 'Hochgeladen',
            col_size: 'Größe',
            col_mime: 'Typ',
            col_state: 'Staat',
            col_actions: 'Aktionen',
            state_active: 'Aktiv',
            state_deleted: 'Gelöscht',
            state_orphaned: 'Verwaist',
            action_view_detail: 'Details anzeigen',
            action_download: 'Herunterladen',
            action_ai_edits: 'AI Änderungen',
            action_soft_delete: 'Vorläufiges Löschen',
            action_restore: 'Wiederherstellen',
            action_hard_delete: 'Hartes Löschen',
            bulk_soft_delete: 'Vorläufiges Löschen ausgewählt',
            bulk_export_csv: 'Exportieren CSV',
            bulk_select_all: 'Alles auswählen',
            reason_label: 'Grund',
            reason_min_10_chars: 'Begründung ist erforderlich (mindestens 10 Zeichen)',
            reason_min_20_chars: 'Begründung ist erforderlich (mindestens 20 Zeichen)',
            legal_hold_override_label: 'Gesetzliche Aufbewahrungsfrist außer Kraft setzen',
            legal_hold_override_warning: 'Diese Datei unterliegt einer rechtlichen Aufbewahrungsfrist. Das endgültige Löschen wird im Audit-Trail mit Ihrem Grund und der Überschreibungsmarkierung aufgezeichnet.',
            empty_state_title: 'Keine Dateien entsprechen Ihren Filtern',
            empty_state_body: 'Passen Sie Ihre Filter an, um Ergebnisse anzuzeigen, oder löschen Sie sie, um alle Dateien anzuzeigen.',
            pagination_prev: 'Zurück',
            pagination_next: 'Als nächstes',
            pagination_page: 'Seite {page} von {total}',
            pagination_size_label: 'Pro Seite',
            pagination_total: 'Gesamt: {count}',
            toast_soft_deleted: 'Datei vorläufig gelöscht',
            toast_restored: 'Datei wiederhergestellt',
            toast_hard_deleted: 'Datei dauerhaft gelöscht',
            toast_bulk_deleted: '{count} Datei(en) gelöscht',
            toast_error: 'Der Vorgang ist fehlgeschlagen',
            confirm_soft_delete_title: 'Soft-Delete-Datei',
            confirm_restore_title: 'Datei wiederherstellen',
            confirm_hard_delete_title: 'Datei endgültig löschen',
            confirm_bulk_soft_delete_title: 'Ausgewählte Dateien vorläufig löschen',
            confirm_submit: 'Bestätigen',
            confirm_cancel: 'Abbrechen',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Dateidetails',
            close: 'Schließen',
            meta_filename: 'Dateiname',
            meta_size: 'Größe',
            meta_mime: 'Typ',
            meta_owner_type: 'Besitzertyp',
            meta_owner_entity: 'Eigentümereinheit',
            meta_uploader: 'Hochgeladen von',
            meta_uploaded_at: 'Hochgeladen',
            meta_storage_path: 'Speicherpfad',
            meta_deleted_state: 'Löschstatus',
            audit_section_title: 'Audit-Trail',
            audit_load_more: 'Weitere Veranstaltungen laden',
            audit_empty: 'Für diese Datei wurden keine Prüfereignisse aufgezeichnet.',
            audit_config_changed: 'Konfiguration geändert',
            audit_soft_deleted: 'Vorläufig gelöscht',
            audit_restored: 'Restauriert',
            audit_hard_deleted: 'Endgültig gelöscht',
            audit_downloaded: 'Heruntergeladen',
            audit_quota_breached: 'Versuch einer Quotenverletzung',
            audit_owner_type_download_denied: 'Abruf verweigert',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} von {actor} am {at}',
            action_download: 'Herunterladen',
            action_soft_delete: 'Vorläufiges Löschen',
            action_restore: 'Wiederherstellen',
            action_hard_delete: 'Hartes Löschen',
            effective_settings_title: 'Effektive Politik',
            effective_max_file_size: 'Maximale Dateigröße',
            effective_retention_days: 'Aufbewahrung',
            effective_legal_hold: 'Rechtlicher Aufbewahrungszeitraum',
            effective_legal_hold_yes: 'Aktiv',
            effective_legal_hold_no: 'Keine',
        },
        files_dashboard: {
            title: 'Dateien-Dashboard',
            subtitle: 'Speichernutzung, Wachstum und verwaiste Dateien im gesamten Unternehmen.',
            kpi_total_files: 'Gesamtdateien',
            kpi_total_size: 'Gesamtspeicher',
            kpi_near_quota: 'Kontingentnutzung',
            kpi_orphaned: 'Verwaiste Dateien',
            chart_by_owner_type: 'Dateien nach Eigentümertyp',
            chart_growth_30d: '30-Tage-Wachstum',
            toggle_count: 'Nach Zählung',
            toggle_size: 'Nach Größe',
            orphaned_panel_title: 'Verwaiste Dateien (Top 20)',
            orphaned_view_all: 'Alle verwaisten Dateien anzeigen',
            orphaned_empty: 'Keine verwaisten Dateien gefunden.',
            growth_empty: 'Keine Upload-Aktivität in den letzten 30 Tagen.',
            retention_n_days: '{days} Tag(e)',
            retention_none: 'Keine Richtlinie',
        },
    },
    category_labels: {
        'user-guide': 'Benutzerhandbuch',
        'admin-guide': 'Admin-Handbuch',
        'getting-started': 'Erste Schritte',
        faq: 'FAQ',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Schlüssel',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Zwecke',
        preferences: 'Präferenzen',
        onboarding: 'Onboarding',
        'first-steps': 'Erste Schritte',
        login: 'Anmelden',
        navigation: 'Navigation',
        users: 'Benutzer',
        user: 'Benutzer',
        roles: 'Rollen',
        role: 'Rolle',
        'access-control': 'Zugangskontrolle',
        'access-management': 'Zugriffsverwaltung',
        permissions: 'Berechtigungen',
        permission: 'Erlaubnis',
        consent: 'Zustimmung',
        'consent-management': 'Einwilligungsmanagement',
        privacy: 'Privatsphäre',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Prüfung',
        auditing: 'Wirtschaftsprüfung',
        breach: 'Verstoß',
        incident: 'Vorfall',
        incidents: 'Vorfälle',
        notification: 'Benachrichtigung',
        notifications: 'Benachrichtigungen',
        reporting: 'Berichterstattung',
        risk: 'Risiko',
        vendor: 'Anbieter',
        vendors: 'Anbieter',
        'vendor-risk': 'Lieferantenrisiko',
        assessment: 'Bewertung',
        assessments: 'Beurteilungen',
        dpia: 'DPIA',
        'cross-border': 'Grenzüberschreitend',
        transfer: 'Übertragen',
        transfers: 'Überweisungen',
        deletion: 'Löschung',
        retention: 'Aufbewahrung',
        'data-mapping': 'Datenzuordnung',
        discovery: 'Entdeckung',
        classification: 'Klassifizierung',
        workflow: 'Arbeitsablauf',
        workflows: 'Arbeitsabläufe',
        template: 'Vorlage',
        templates: 'Vorlagen',
        catalog: 'Katalog',
        monitoring: 'Überwachung',
        tasks: 'Aufgaben',
        task: 'Aufgabe',
        'task-management': 'Aufgabenverwaltung',
        organizations: 'Organisationen',
        org: 'Organisation',
        governance: 'Regierungsführung',
        compliance: 'Compliance',
        forms: 'Formulare',
        form: 'Formular',
        connectors: 'Anschlüsse',
        connector: 'Stecker',
        integration: 'Integration',
        integrations: 'Integrationen',
        security: 'Sicherheit',
        encryption: 'Verschlüsselung',
        backup: 'Sicherung',
        recovery: 'Erholung',
        maintenance: 'Wartung',
        settings: 'Einstellungen',
        configuration: 'Konfiguration',
        sla: 'SLA',
        escalation: 'Eskalation',
        remediation: 'Sanierung',
        evidence: 'Beweise',
        'case-management': 'Fallmanagement',
        profile: 'Profil',
        delegation: 'Delegation',
        'service-accounts': 'Dienstkonten',
        license: 'Lizenz',
        'license-management': 'Lizenzverwaltung',
        plan: 'Planen',
        plans: 'Pläne',
        dashboard: 'Armaturenbrett',
        'web-consent': 'Web-Einwilligung',
        'partner-marketplace': 'Partnermarktplatz',
        partner: 'Partner',
        partners: 'Partner',
        deliverables: 'Leistungen',
        'service-tiers': 'Servicestufen',
        'support-tickets': 'Support-Tickets',
        'audit-trail': 'Prüfpfad',
        'first-time-setup': 'Erstmalige Einrichtung',
        setup: 'Einrichtung',
        quickstart: 'Schnellstart',
        tour: 'Rundgang',
        tours: 'Touren',
        'sample-data': 'Beispieldaten',
        'data-subject': 'Betroffene Person',
        'data-subjects': 'Betroffene Personen',
        pseudonymous: 'Pseudonym',
        identified: 'Identifiziert',
        'help-center': 'Hilfecenter',
        article: 'Artikel',
        articles: 'Artikel',
        search: 'Suchen',
        'platform-settings': 'Plattformeinstellungen',
        'platform-config': 'Plattformkonfiguration',
        'module-config': 'Modulkonfiguration',
        'module-configuration': 'Modulkonfiguration',
        'rights-requests': 'Rechteanfragen',
        'access-accountability': 'Zugriff auf Verantwortlichkeit',
        'data-discovery': 'Datenermittlung',
        'incident-ops': 'Vorfalloperationen',
        'deletion-orchestration': 'Löschorchestrierung',
        'compliance-forms': 'Compliance-Formulare',
        'program-governance': 'Programm-Governance',
        localization: 'Lokalisierung',
        'privacy-scanner': 'Datenschutzscanner',
        'consent-properties': 'Einwilligungseigenschaften',
        acceptances: 'Annahmen',
        access: 'Zugang',
        'access-review': 'Zugriffsüberprüfung',
        'access-reviews': 'Zugriff auf Bewertungen',
        accountability: 'Verantwortung',
        acknowledgment: 'Danksagung',
        'action-plans': 'Aktionspläne',
        activation: 'Aktivierung',
        activations: 'Aktivierungen',
        'active-response': 'Aktive Reaktion',
        activity: 'Aktivität',
        adequacy: 'Angemessenheit',
        'admin-guide': 'Admin-Handbuch',
        aggregation: 'Aggregation',
        agreements: 'Vereinbarungen',
        ai: 'AI',
        alerts: 'Warnungen',
        analytics: 'Analytik',
        anchoring: 'Verankerung',
        approval: 'Zustimmung',
        approvals: 'Zulassungen',
        apps: 'Apps',
        assets: 'Vermögenswerte',
        assignments: 'Aufgaben',
        attestation: 'Bescheinigung',
        'audit-log': 'Audit-Protokoll',
        'audit-pack': 'Audit-Paket',
        authentication: 'Authentifizierung',
        authority: 'Autorität',
        'auto-play': 'Automatische Wiedergabe',
        automated: 'Automatisiert',
        automation: 'Automatisierung',
        baseline: 'Grundlinie',
        batches: 'Chargen',
        bidirectional: 'Bidirektional',
        bilingual: 'Zweisprachig',
        bindings: 'Bindungen',
        blockchain: 'Blockchain',
        board: 'Vorstand',
        brand: 'Marke',
        brds: 'BRDs',
        'break-glass': 'Glasbruch',
        builder: 'Baumeister',
        bulk: 'Masse',
        'bulk-sync': 'Massensynchronisierung',
        bundles: 'Bündel',
        calendar: 'Kalender',
        campaigns: 'Kampagnen',
        cases: 'Fälle',
        categories: 'Kategorien',
        category: 'Kategorie',
        certification: 'Zertifizierung',
        chain: 'Kette',
        change: 'Veränderung',
        'change-awareness': 'Bewusstsein ändern',
        'change-log': 'Änderungsprotokoll',
        'change-management': 'Änderungsmanagement',
        'change-requests': 'Änderungswünsche',
        'cmp-validation': 'CMP Validierung',
        collection: 'Sammlung',
        comparison: 'Vergleich',
        completeness: 'Vollständigkeit',
        completion: 'Fertigstellung',
        'compliance-posture': 'Compliance-Haltung',
        'compliance-proof': 'Compliance-Nachweis',
        'compliance-reviews': 'Compliance-Überprüfungen',
        config: 'Konfig',
        confirmation: 'Bestätigung',
        conflicts: 'Konflikte',
        consolidation: 'Konsolidierung',
        continuous: 'Kontinuierlich',
        contracts: 'Verträge',
        controls: 'Kontrollen',
        cookies: 'Kekse',
        cooperation: 'Zusammenarbeit',
        coordination: 'Koordination',
        'corporate-governance': 'Unternehmensführung',
        'corrective-actions': 'Korrekturmaßnahmen',
        coverage: 'Abdeckung',
        'credential-vault': 'Tresor für Anmeldeinformationen',
        credentials: 'Anmeldeinformationen',
        cycles: 'Zyklen',
        dashboards: 'Dashboards',
        'data-assets': 'Datenbestände',
        'data-flows': 'Datenflüsse',
        'data-sensitivity': 'Datensensibilität',
        'data-steward': 'Datenverwalter',
        deadlines: 'Fristen',
        'decree-356': 'Dekret 356',
        dedup: 'Dedup',
        definitions: 'Definitionen',
        deliverable: 'Lieferbar',
        departments: 'Abteilungen',
        'dependency-order': 'Abhängigkeitsordnung',
        deployment: 'Bereitstellung',
        detection: 'Erkennung',
        diff: 'Diff',
        'digital-properties': 'Digitale Eigenschaften',
        dimensions: 'Abmessungen',
        disable: 'Deaktivieren',
        'disaster-recovery': 'Notfallwiederherstellung',
        disclosure: 'Offenlegung',
        documentation: 'Dokumentation',
        documents: 'Dokumente',
        domains: 'Domänen',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Drift',
        'drift-detection': 'Drifterkennung',
        'due-diligence': 'Due Diligence',
        email: 'Email',
        emergency: 'Notfall',
        enable: 'Aktivieren',
        'enterprise-map': 'Unternehmenskarte',
        erasure: 'Löschung',
        evaluation: 'Bewertung',
        'event-bus': 'Veranstaltungsbus',
        exceptions: 'Ausnahmen',
        execution: 'Ausführung',
        executive: 'Exekutive',
        exercises: 'Übungen',
        export: 'Exportieren',
        exports: 'Exporte',
        filing: 'Einreichung',
        filter: 'Filtern',
        filtering: 'Filtern',
        findings: 'Erkenntnisse',
        flows: 'Fließt',
        'follow-up': 'Nachverfolgung',
        forensic: 'Forensisch',
        forensics: 'Forensik',
        'form-filler': 'Formularausfüller',
        framework: 'Rahmen',
        frameworks: 'Rahmenwerke',
        'gap-analysis': 'Lückenanalyse',
        'gap-report': 'Lückenbericht',
        gaps: 'Lücken',
        gates: 'Tore',
        'getting-started': 'Erste Schritte',
        'golden-templates': 'Goldene Vorlagen',
        grc: 'GRC',
        'group-management': 'Gruppenleitung',
        groups: 'Gruppen',
        'guided-tours': 'Geführte Touren',
        guides: 'Führer',
        handling: 'Handhabung',
        'hands-on': 'Praktisch',
        health: 'Gesundheit',
        help: 'Hilfe',
        hierarchy: 'Hierarchie',
        history: 'Geschichte',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Identitätsanbieter',
        implementation: 'Umsetzung',
        import: 'Importieren',
        improvement: 'Verbesserung',
        'incident-response': 'Reaktion auf Vorfälle',
        inconsistencies: 'Inkonsistenzen',
        instances: 'Instanzen',
        intake: 'Aufnahme',
        'intake-key': 'Einlassschlüssel',
        interactive: 'Interaktiv',
        international: 'International',
        inventory: 'Inventar',
        investigation: 'Untersuchung',
        investigations: 'Untersuchungen',
        'ip-allowlist': 'IP-Zulassungsliste',
        issues: 'Probleme',
        justification: 'Begründung',
        justifications: 'Begründungen',
        kanban: 'Kanban',
        keyboard: 'Tastatur',
        keys: 'Schlüssel',
        'knowledge-base': 'Wissensdatenbank',
        koncernstyring: 'Unternehmensführung',
        kpi: 'KPI',
        labeling: 'Beschriftung',
        language: 'Sprache',
        'language-packs': 'Sprachpakete',
        languages: 'Sprachen',
        lawfulness: 'Rechtmäßigkeit',
        layout: 'Layout',
        'legal-basis': 'Rechtsgrundlage',
        'lessons-learned': 'Gelernte Lektionen',
        levels: 'Ebenen',
        library: 'Bibliothek',
        lifecycle: 'Lebenszyklus',
        lineage: 'Abstammung',
        links: 'Links',
        listings: 'Einträge',
        live: 'Lebe',
        locale: 'Gebietsschema',
        log: 'Log',
        logging: 'Protokollierung',
        'managed-service': 'Managed-Service',
        mapping: 'Kartierung',
        mappings: 'Zuordnungen',
        'market-packs': 'Marktpakete',
        marketplace: 'Marktplatz',
        maturity: 'Reife',
        metrics: 'Metriken',
        models: 'Modelle',
        module: 'Modul',
        modules: 'Module',
        mps: 'MPS',
        'multi-factor': 'Multifaktor',
        'multi-market': 'Multi-Markt',
        'multi-org': 'Multi-Org',
        'multi-property': 'Mehrere Objekte',
        'my-tasks': 'Meine Aufgaben',
        obligations: 'Verpflichtungen',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orchestrierung',
        'org-groups': 'Organisationsgruppen',
        organization: 'Organisation',
        overdue: 'Überfällig',
        overview: 'Übersicht',
        'owner-review': 'Eigentümerbewertung',
        package: 'Paket',
        packages: 'Pakete',
        packs: 'Packungen',
        pagination: 'Paginierung',
        'parent-child': 'Eltern-Kind',
        patterns: 'Muster',
        performance: 'Leistung',
        periodic: 'Periodisch',
        periods: 'Perioden',
        personal: 'Persönlich',
        'personal-data': 'Persönliche Daten',
        personas: 'Personas',
        planning: 'Planung',
        platform: 'Plattform',
        playbooks: 'Spielbücher',
        poc: 'PoC',
        'point-in-time': 'Zeitpunkt',
        policies: 'Richtlinien',
        portfolio: 'Portfolio',
        'post-incident': 'Nach dem Vorfall',
        'power-user': 'Power-User',
        practice: 'Üben',
        pricing: 'Preise',
        'privacy-impact': 'Auswirkungen auf die Privatsphäre',
        procedures: 'Verfahren',
        processes: 'Prozesse',
        processing: 'Verarbeitung',
        'processing-activities': 'Verarbeitungstätigkeiten',
        procurement: 'Beschaffung',
        productivity: 'Produktivität',
        profiles: 'Profile',
        programs: 'Programme',
        progress: 'Fortschritt',
        proof: 'Beweis',
        propagation: 'Vermehrung',
        properties: 'Eigenschaften',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Öffentliche Form',
        quality: 'Qualität',
        questionnaires: 'Fragebögen',
        'quick-links': 'Quicklinks',
        'quick-start': 'Schnellstart',
        'quick-starts': 'Schnellstarts',
        raci: 'RACI',
        readiness: 'Bereitschaft',
        'real-time': 'Echtzeit',
        reassessments: 'Neubewertungen',
        reconciliation: 'Versöhnung',
        record: 'Aufnahme',
        records: 'Aufzeichnungen',
        recurring: 'Wiederkehrend',
        register: 'Registrieren',
        registry: 'Registrierung',
        regulations: 'Vorschriften',
        regulator: 'Regulator',
        regulatory: 'Regulatorisch',
        'regulatory-frameworks': 'Regulatorische Rahmenbedingungen',
        remap: 'Neu zuordnen',
        reminders: 'Erinnerungen',
        reports: 'Berichte',
        requests: 'Anfragen',
        requirements: 'Anforderungen',
        response: 'Antwort',
        'response-plans': 'Reaktionspläne',
        'response-team': 'Reaktionsteam',
        'response-templates': 'Antwortvorlagen',
        'response-time': 'Reaktionszeit',
        responses: 'Antworten',
        responsibility: 'Verantwortung',
        results: 'Ergebnisse',
        review: 'Rezension',
        reviews: 'Rezensionen',
        'right-to-delete': 'Recht auf Löschung',
        rights: 'Rechte',
        'risk-tiers': 'Risikostufen',
        rollouts: 'Rollouts',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Routenführung',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Regeln',
        runs: 'Läuft',
        saml: 'SAML',
        scan: 'Scannen',
        scanning: 'Scannen',
        scc: 'SCC',
        schedule: 'Zeitplan',
        scheduled: 'Geplant',
        schedules: 'Zeitpläne',
        scheduling: 'Terminplanung',
        scores: 'Partituren',
        scoring: 'Wertung',
        scripts: 'Skripte',
        'security-reviews': 'Sicherheitsüberprüfungen',
        'sensitive-data': 'Sensible Daten',
        sensitivity: 'Empfindlichkeit',
        'separation-of-duties': 'Aufgabentrennung',
        service: 'Service',
        sessions: 'Sitzungen',
        'setup-wizard': 'Setup-Assistent',
        severity: 'Schweregrad',
        'shared-governance': 'Gemeinsame Governance',
        'shared-settings': 'Gemeinsame Einstellungen',
        shortcuts: 'Verknüpfungen',
        sidebar: 'Seitenleiste',
        signals: 'Signale',
        'single-sign-on': 'Single Sign-On',
        snapshots: 'Schnappschüsse',
        sod: 'SoD',
        sorting: 'Sortieren',
        sources: 'Quellen',
        staging: 'Inszenierung',
        standards: 'Standards',
        status: 'Status',
        structure: 'Struktur',
        'sub-processor': 'Unterauftragsverarbeiter',
        'sub-processors': 'Unterauftragsverarbeiter',
        submissions: 'Einsendungen',
        subprocessors: 'Unterauftragsverarbeiter',
        subscription: 'Abonnement',
        suggestions: 'Vorschläge',
        summaries: 'Zusammenfassungen',
        supplements: 'Ergänzungen',
        support: 'Unterstützung',
        sync: 'Synchronisieren',
        system: 'System',
        systems: 'Systeme',
        tables: 'Tische',
        tagging: 'Markieren',
        'tamper-proof': 'Manipulationssicher',
        taxonomies: 'Taxonomien',
        taxonomy: 'Taxonomie',
        tenants: 'Mieter',
        termination: 'Kündigung',
        'third-party': 'Drittanbieter',
        'ticket-sync': 'Ticketsynchronisierung',
        tickets: 'Tickets',
        tiers: 'Ebenen',
        tokens: 'Token',
        totp: 'TOTP',
        traceability: 'Rückverfolgbarkeit',
        tracking: 'Verfolgung',
        trail: 'Weg',
        training: 'Ausbildung',
        translation: 'Übersetzung',
        translations: 'Übersetzungen',
        transparency: 'Transparenz',
        trends: 'Trends',
        unified: 'Einheitlich',
        updates: 'Aktualisierungen',
        validation: 'Validierung',
        vault: 'Tresor',
        'vendor-requests': 'Lieferantenanfragen',
        verification: 'Überprüfung',
        versioning: 'Versionierung',
        versions: 'Versionen',
        vietnam: 'Vietnam',
        violations: 'Verstöße',
        visualization: 'Visualisierung',
        waivers: 'Verzichtserklärungen',
        'web-records': 'Webaufzeichnungen',
        website: 'Website',
        websites: 'Websites',
        weights: 'Gewichte',
        'white-label': 'White-Label',
        withdrawal: 'Rückzug',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Auskunftsersuchen',
        data_mapping: 'Datenverzeichnis',
        governance: 'Steuerung',
        consent: 'Einwilligungssteuerung',
        consent_properties: 'Einwilligungseigenschaften',
        assessments: 'DSFA & Bewertungen',
        classification: 'Datenklassifizierung',
        monitoring: 'Überwachungsprogramme',
        program: 'Überwachungsprogramm',
        incidents: 'Vorfallreaktion',
        connectors: 'Konnektoren',
        vendor_risk: 'Lieferantenrisiko',
        deletion_orchestration: 'Löschorchestrierung',
        discovery: 'Datenentdeckung',
        incident_ops: 'Vorfallsbetrieb',
        program_governance: 'Programmsteuerung',
        access_accountability: 'Zugriffsverantwortung',
        localization: 'Lokalisierung',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Partner-Arbeitsbereich',
        privacy_scanner: 'Datenschutz-Scanner',
        onboarding: 'Onboarding',
        compliance_forms: 'Compliance-Formulare',
        audit_trail: 'Prüfprotokoll',
        data_subjects: 'Betroffene Personen',
        organizations: 'Organisationen',
        task_management: 'Aufgabenverwaltung',
        deletion_orch: 'Löschorchestrierung',
        forms: 'Compliance-Formulare',
        partner_marketplace: 'Partner-Marktplatz',
        dashboard: 'Armaturenbrett',
        platform_settings: 'Plattformeinstellungen',
        help_center: 'Hilfezentrum',
    },
    module_descriptions: {
        rights_requests: 'Verwalten Sie Auskunftsersuchen betroffener Personen einschließlich Zugang, Löschung und Datenübertragbarkeit. Verfolgen Sie den Anfragezyklus, weisen Sie Bearbeiter zu und halten Sie gesetzliche Fristen ein.',
        data_mapping: 'Bilden Sie Datenflüsse in Ihrer Organisation ab. Verfolgen Sie Verarbeitungstätigkeiten, Datenkategorien, Zwecke, Aufbewahrungsrichtlinien und Systemintegrationen.',
        governance: 'Aufsicht und die Nachweise dahinter. Verwalten Sie das Lieferantenregister und seinen Lebenszyklus, genehmigen Sie die Entscheidungen, die dieses Modul absichert, überwachen Sie Compliance-Abweichungen und führen Sie die Register für Vertrauensdienste und Identität, Regulierungsartefakte, Zahlungen und eKYC sowie Plattformnachweise.',
        consent: 'Verwalten Sie die Einholung, Speicherung und den Widerruf von Einwilligungen. Verfolgen Sie Einwilligungsdatensätze, Zwecke und die Integration mit Einwilligungsplattformen.',
        consent_properties: 'Konfigurieren Sie Einwilligungseigenschaften, Gruppen und goldene Vorlagen. Definieren Sie wiederverwendbare Einwilligungskonfigurationen für eine einheitliche Bereitstellung.',
        assessments: 'Führen Sie Datenschutz-Folgenabschätzungen (DSFA) und Datenschutz-Risikobewertungen durch. Bewerten Sie Verarbeitungstätigkeiten auf Compliance-Risiken.',
        classification: 'Klassifizieren Sie Datenbestände nach Sensibilitätsstufe. Wenden Sie Taxonomie-Labels an, verfolgen Sie Lebenszyklus-Einträge und identifizieren Sie Klassifizierungslücken.',
        monitoring: 'Richten Sie Überwachungsprogramme ein, um Compliance-Metriken, zentrale Risikoindikatoren und Kontrollwirksamkeit über die Zeit zu verfolgen.',
        incidents: 'Melden und verwalten Sie Datenschutzvorfälle und Datenschutzverletzungen. Verfolgen Sie Untersuchungs-, Benachrichtigungs- und Abhilfe-Workflows.',
        connectors: 'Konfigurieren Sie Datenquellen-Konnektoren und Integrationen. Verbinden Sie externe Systeme für automatisierte Datenentdeckung und Überwachung.',
        vendor_risk: 'Bewerten und überwachen Sie Risiken von Drittanbietern. Bewerten Sie Lieferanten, verfolgen Sie Risikominderung und verwalten Sie Lieferanten-Risikokonfigurationen.',
        deletion_orchestration: 'Orchestrieren Sie die Datenlöschung über Systeme hinweg. Verfolgen Sie Löschanfragen, überprüfen Sie Nachweisketten und stellen Sie vollständige Datenentfernung sicher.',
        discovery: 'Entdecken Sie personenbezogene Daten in verbundenen Systemen. Scannen Sie Datenquellen, um ein umfassendes Dateninventar aufzubauen.',
        incident_ops: 'Operatives Vorfallmanagement mit Playbooks, Eskalations-Workflows und SLA-Verfolgung für die Vorfallbehebung.',
        program_governance: 'Verfolgen Sie den Reifegrad des gesamten Datenschutzprogramms, Compliance-Bewertungen, Nachweislücken und die Berichterstattung an die Geschäftsleitung in Ihrer Organisation.',
        access_accountability: 'Untersuchen und prüfen Sie Benutzerzugriffsmuster. Verfolgen Sie Zugriffskonfigurationen, überwachen Sie verdächtige Aktivitäten und setzen Sie Rechenschaftspflicht durch.',
        localization: 'Installieren und verwalten Sie Markt-Lokalisierungspakete. Fügen Sie regulatorische Rahmenwerke, Sprachpakete und regionsspezifische Compliance-Inhalte hinzu.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Arbeiten Sie mit externen Partnern zusammen. Teilen Sie Compliance-Daten, verfolgen Sie Partnerverpflichtungen und verwalten Sie Partnerbeziehungen.',
        privacy_scanner: 'Scannen Sie Websites auf Datenschutz-Compliance-Probleme. Überwachen Sie Cookie-Nutzung, Tracking-Skripte und das Vorhandensein von Datenschutzrichtlinien in Echtzeit.',
        onboarding: 'Führen Sie neue Organisationen durch die Plattformeinrichtung. Schritt-für-Schritt-Onboarding-Assistent für die Erstkonfiguration und den Datenimport.',
        compliance_forms: 'Erstellen und verwalten Sie Compliance-Formulare aus einem Vorlagenkatalog. Füllen Sie regulatorische Formulare aus, verfolgen Sie Einreichungen und erstellen Sie Berichte.',
        audit_trail: 'Sehen Sie ein vollständiges Prüfprotokoll aller Systemaktionen ein. Verfolgen Sie, wer was, wann und wo über alle Module hinweg getan hat.',
        organizations: 'Verwalten Sie Organisationen und Mandantenfähigkeits-Einstellungen. Konfigurieren Sie Organisationsprofile, Hierarchien und organisationsübergreifende Beziehungen.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Erklären Sie Anomalien',
            explaining: 'Erklären…',
            results_heading: 'Erklärungen zur Anomalie ({severity})',
            tokens_label: '≈ {count} Token',
            items_label: '{count} Elemente',
            remediation_label: 'Sanierungsschritte',
            items_detail_label: 'Betroffene Artikel',
            accept_label: 'Erklärung akzeptieren',
            reject_label: 'Erklärung ablehnen',
            error_generic: 'Konnte die Anomalien nicht erklären. Versuchen Sie es erneut oder überprüfen Sie, ob der Scan erfolgreich abgeschlossen wurde.',
        },
        cookie_insight: {
            insight_label: 'Einblick in Cookies und Tracker AI.',
            producing: 'Erkenntnisse schaffen…',
            results_heading: 'Cookie- und Tracker-Einblick ({severity})',
            tokens_label: '≈ {count} Token',
            items_label: '{count} Elemente',
            classification_label: 'Klassifizierung',
            severity_label: 'Schweregrad',
            banner_copy_label: 'Empfohlene Bannerkopie',
            retention_label: 'Hinweise zur Aufbewahrung',
            citations_label: 'Zitate',
            kind_cookie: 'Keks',
            kind_tracker: 'Tracker',
            class_necessary: 'Notwendig',
            class_analytics: 'Analytik',
            class_marketing: 'Marketing',
            class_social: 'Sozial',
            accept_label: 'Akzeptiere Einsicht',
            reject_label: 'Einsicht ablehnen',
            no_items: 'Bei diesem Scan wurden keine Cookies oder Tracker erkannt.',
            error_generic: 'Die Cookie-Einsicht konnte nicht erstellt werden. Versuchen Sie es erneut oder überprüfen Sie, ob der Scan erfolgreich abgeschlossen wurde.',
        },
        monitoring: {
            title: 'Datenschutzüberwachung',
            new_scan: 'Neuer Scan',
            scan_url: 'Zu scannende URL',
            scan_frequency: 'Scan-Häufigkeit',
            last_scan: 'Letzter Scan',
            score: 'Datenschutzbewertung',
            issues: 'Gefundene Probleme',
            history: 'Scan-Verlauf',
            settings: 'Überwachungseinstellungen',
        },
        real_time: {
            title: 'Echtzeit-Scanner',
            scan_now: 'Jetzt scannen',
            scanning: 'Wird gescannt...',
            results: 'Scan-Ergebnisse',
            no_results: 'Noch keine Scan-Ergebnisse.',
            cookies: 'Browser-Cookies',
            trackers: 'Tracker',
            consent_banners: 'Einwilligungsbanner',
            third_party: 'Drittanbieter-Skripte',
            archive: 'Ergebnisarchiv',
            settings: 'Scanner-Einstellungen',
        },
        dashboard: {
            page_title: 'Dashboard zur Datenschutzüberwachung',
            privacy_monitoring: 'Datenschutzüberwachung',
            plan_label: '{package} Plan',
            expires_label: 'läuft ab {date}',
            settings: 'Einstellungen',
            refresh: 'Aktualisieren',
            refreshing_dashboard: 'Erfrischendes Überwachungs-Dashboard',
            refresh_aria: 'Überwachungs-Dashboard aktualisieren',
            not_configured_title: 'Datenschutzüberwachung nicht konfiguriert',
            not_configured_desc: 'Verbinden Sie Ihren AesirX Datenschutzüberwachungsdienst, um mit der Verfolgung der Website-Compliance zu beginnen.',
            configure_monitoring: 'Konfigurieren Sie die Überwachung',
            license_expired_title: 'Lizenz abgelaufen',
            license_expired_desc: 'Ihre AesirX Privacy Monitoring-Lizenz ist abgelaufen. Bitte erneuern Sie es, um eine Dienstunterbrechung zu vermeiden.',
            compliance_score: 'Compliance-Score',
            cookies_detected: 'Cookies erkannt',
            trackers_label: 'Tracker',
            open_issues: 'Offene Probleme',
            score_good: 'Gut',
            score_needs_improvement: 'Verbesserungsbedarf',
            score_critical: 'Kritisch',
            action_required: 'Aktion erforderlich',
            cookies_sr: 'Cookies erkannt',
            trackers_sr: 'Tracker gefunden',
            open_issues_sr: 'offene Fragen',
            action_required_sr: 'Handlungsbedarf',
            metric_label: 'Metrisch',
            value_label: 'Wert',
            status_label: 'Status',
            aria_metrics: 'Metriken zur Datenschutzüberwachung',
            plan_limit_warning: 'Scannen von URL: {current}/{max} – Sie haben Ihr Planlimit erreicht.',
            manage_domains: 'Verwalten Sie Domains oder aktualisieren Sie Ihren Plan.',
            monitored_domains: 'Überwachte Domänen',
            domain_added: 'Domäne {domain} hinzugefügt',
            domain_removed: 'Domäne {domain} entfernt',
            failed_add_domain: 'Das Hinzufügen der Domäne ist fehlgeschlagen',
            failed_remove_domain: 'Die Domäne konnte nicht entfernt werden',
            failed_load: 'Das Monitoring-Dashboard konnte nicht geladen werden',
            new_domain_aria: 'Neue Domain URL',
            add_domain: 'Domäne hinzufügen',
            adding_domain: 'Hinzufügen…',
            no_domains: 'Es sind noch keine überwachten Domänen konfiguriert.',
            aria_domains_table: 'Überwachte Domänen',
            col_domain: 'Domäne',
            col_compliance_score: 'Compliance-Score',
            col_status: 'Status',
            col_cookies: 'Kekse',
            col_trackers: 'Tracker',
            col_issues: 'Probleme',
            col_last_scan: 'Letzter Scan',
            col_action: 'Aktion',
            compliance_status_sr: 'Compliance-Status',
            showing_chart: 'Diagrammansicht wird angezeigt',
            showing_table: 'Datentabellenansicht wird angezeigt',
            loading_sr: 'Überwachungs-Dashboard wird geladen',
            col_framework_tags: 'Framework-Tags',
            col_last_monitored: 'Zuletzt überwacht',
            framework_tags_sr: 'geltenden Regulierungsrahmen',
            last_monitored_sr: 'Zeitstempel der kontinuierlichen Überwachung',
            audit_trail_heading: 'Prüfpfad',
            audit_trail_info: 'Jeder Überwachungsscan wird mit einem Zeitstempel versehen und protokolliert. Compliance-Änderungen auf Domänenebene führen zu einem unveränderlichen Prüfdatensatz, der zur behördlichen Überprüfung exportiert werden kann.',
            cybersecurity_law_info_heading: 'Erkennung von Cybersicherheitsgesetzen',
            cybersecurity_law_info: 'Überwachte Domänen werden automatisch auf die Anforderungen des Cybersicherheitsgesetzes überprüft (z. B. Vietnamesisches Cybersicherheitsgesetz, NIS2-Richtlinie). Framework-Tags geben an, welche Vorschriften gelten. Probleme werden nach regulatorischen Rahmenbedingungen für eine gezielte Behebung kategorisiert.',
        },
        scan: {
            page_title: 'Echtzeit-Scan',
            title: 'Echtzeit-Datenschutzscanner',
            settings: 'Einstellungen',
            results_archive: 'Ergebnisarchiv',
            launch_scan: 'Starten Sie den Scan',
            target_url: 'Ziel URL',
            protocol_aria: 'Protokoll',
            url_required: 'URL ist erforderlich',
            url_whitespace: 'Bitte entfernen Sie Leerzeichen aus dem URL',
            url_invalid: 'Bitte geben Sie eine gültige Domain ein (z. B. example.com)',
            add_subpages: 'Unterseiten hinzufügen',
            add_subpages_aria: 'Unterseiten hinzufügen, {count} hinzugefügt',
            start_scan: 'Starten Sie den Scan',
            launching: 'Starten…',
            scan_launched: 'Der Scan wurde erfolgreich gestartet',
            scan_failed: 'Scan konnte nicht gestartet werden',
            scan_progress: 'Scan-Fortschritt',
            scanning_in_progress: 'Der Scanvorgang läuft…',
            scan_id: 'Scan-ID',
            status_label: 'Status',
            status_unknown: 'unknown',
            progress_label: 'Fortschritt',
            pages_label: 'Seiten',
            refresh_status: 'Status aktualisieren',
            new_scan: 'Neuer Scan',
            scan_completed: 'Scan abgeschlossen, Ergebnisse verfügbar',
            failed_check_status: 'Der Scanstatus konnte nicht überprüft werden',
            scan_results: 'Scan-Ergebnisse',
            main_page: 'Hauptseite',
            page_n: 'Seite {n}',
            result_pages_nav: 'Ergebnisseiten',
            page_url: 'Seite URL',
            risk_score: 'Risikobewertung',
            risk_score_sr: 'Risikobewertung',
            cookies_label: 'Kekse',
            cookies_sr: 'Cookies erkannt',
            scripts_label: 'Skripte',
            scripts_sr: 'Skripte erkannt',
            issues_label: 'Probleme',
            issues_sr: 'Probleme gefunden',
            issues_found: 'Gefundene Probleme',
            aria_issues_table: 'Beim Scan gefundene Probleme',
            col_severity: 'Schweregrad',
            col_category: 'Kategorie',
            col_title: 'Titel',
            col_remediation: 'Sanierung',
            severity_sr: 'severity',
            scan_result_metrics: 'Scan-Ergebnismetriken',
            view_full_detail: 'Vollständiges Detail anzeigen',
            col_consent_category: 'Einwilligungskategorie',
            consent_category_sr: 'Einwilligungskategorisierung',
            compliance_evidence_heading: 'Compliance-Nachweis',
            compliance_evidence_info: 'Scan-Ergebnisse werden automatisch mit Compliance-Fällen verknüpft. Jedes Problem kann einer Einwilligungskategorie (wesentlich, funktional, Analyse, Marketing) zugeordnet werden und liefert revisionssichere Beweise für die behördliche Überprüfung.',
            rule_config_heading: 'Regelkonfiguration',
            rule_config_info: 'Scanregeln können je nach Regulierungsrahmen konfiguriert werden. Cookie-Prüfungen, Skripterkennung und Anbieteranalyse können über die Einstellungen aktiviert oder deaktiviert werden. Es können benutzerdefinierte Regeln definiert werden, die den Compliance-Anforderungen Ihres Unternehmens entsprechen.',
            case_linkage_heading: 'Verknüpfung von Compliance-Fällen',
            case_linkage_info: 'Bei Echtzeitscans gefundene Probleme können zur Nachverfolgung und Behebung mit Compliance-Fällen verknüpft werden. Navigieren Sie zur Seite „Scan-Ergebnisdetails“, um Fallzuordnungen anzuzeigen.',
            subpages_modal_title: 'Unterseiten hinzufügen',
            subpages_modal_desc: 'Fügen Sie bis zu 5 Unterseiten-URLs hinzu, die neben der Hauptdomäne gescannt werden sollen.',
            subpage_n: 'Unterseite {n}',
        },
        result_detail: {
            page_title: 'Details zum Scanergebnis',
            title: 'Scan-Ergebnis',
            back_to_archive: 'Zurück zum Archiv',
            failed_load: 'Scan-Ergebnis konnte nicht geladen werden',
            loading_sr: 'Scanergebnis wird geladen…',
            scan_id: 'Scan-ID',
            target_url: 'Ziel URL',
            status_label: 'Status',
            status_sr: 'status',
            started: 'Begonnen',
            completed: 'Abgeschlossen',
            pages_scanned: 'Gescannte Seiten',
            https_label: 'HTTPS',
            highest_risk_score: 'Höchster Risikowert',
            most_cookies: 'Die meisten Kekse',
            most_vendors: 'Die meisten Drittanbieter',
            most_issues: 'Die meisten Probleme',
            across_pages: 'seitenübergreifend',
            highest_values_aria: 'Höchste Werte auf allen Seiten',
            scanned_pages_nav: 'Gescannte Seiten',
            main_page: 'Hauptseite',
            page_n: 'Seite {n}',
            page_url: 'Seite URL',
            risk_score: 'Risikobewertung',
            overall_risk_sr: 'Gesamtrisikobewertung',
            cookies_label: 'Kekse',
            cookies_sr: 'Cookies erkannt',
            vendors_label: 'Drittanbieter',
            vendors_sr: 'Drittanbieter erkannt',
            issues_label: 'Probleme',
            issues_sr: 'Probleme gefunden',
            risk_breakdown_aria: 'Aufschlüsselung der Risikobewertung',
            issues_found: 'Gefundene Probleme',
            aria_issues_table: 'Beim Scan gefundene Probleme',
            col_severity: 'Schweregrad',
            col_category: 'Kategorie',
            col_title: 'Titel',
            col_remediation: 'Sanierung',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies erkannt',
            col_name: 'Name',
            col_domain: 'Domäne',
            col_path: 'Pfad',
            col_secure: 'Sicher',
            col_httponly: 'HttpOnly',
            col_samesite: 'SameSite',
            vendors_heading: 'Drittanbieter ({count})',
            aria_vendors_table: 'Drittanbieter erkannt',
            col_hostname: 'Hostname',
            scripts_heading: 'Skripte / Beacons ({count})',
            aria_scripts_table: 'Skripte und Beacons erkannt',
            col_url: 'URL',
            col_type: 'Typ',
            links_heading: 'Links',
            first_party: 'Erstanbieter',
            third_party: 'Drittanbieter',
            col_risk_category: 'Risikokategorie',
            col_regulatory_framework: 'Regulierungsrahmen',
            col_actionable_detail: 'Umsetzbares Detail',
            risk_category_sr: 'regulatorische Risikokategorie',
            risk_category_mapping_heading: 'Zuordnung von Risikokategorien',
            risk_category_mapping_info: 'Jeder Scanbefund wird einer regulatorischen Risikokategorie zugeordnet (Datenerfassung, grenzüberschreitende Übertragung, Einwilligungsverwaltung, Datenaufbewahrung, Sicherheitskontrollen). Diese Zuordnung ermöglicht eine gezielte Sanierung, die nach den Auswirkungen der Vorschriften priorisiert wird.',
            compliance_case_heading: 'Compliance Case Association',
            compliance_case_info: 'Scan-Ergebnisse werden zur durchgängigen Nachverfolgung mit Compliance-Fällen verknüpft. Probleme können direkt aus den Scan-Ergebnissen in den Compliance-Fall-Workflow eskaliert werden.',
        },
        results_archive: {
            page_title: 'Scan-Ergebnisarchiv',
            title: 'Scan-Ergebnisarchiv',
            back_to_scanner: 'Zurück zum Scanner',
            failed_load: 'Scan-Ergebnisse konnten nicht geladen werden',
            results_loaded: 'Scan-Ergebnisse geladen, {count} Datensätze',
            total_label: 'Gesamt: {count}',
            aria_table: 'Scan-Ergebnisarchiv',
            col_scan_date: 'Scandatum',
            col_target_url: 'Ziel URL',
            col_duration: 'Dauer',
            col_issues_found: 'Gefundene Probleme',
            col_risk_score: 'Risikobewertung',
            col_status: 'Status',
            col_actions: 'Aktionen',
            scan_status_sr: 'Scanstatus',
            view_result: 'Ansicht',
            no_results: 'Keine Scan-Ergebnisse gefunden.',
            loading_sr: 'Scan-Ergebnisse werden geladen',
            pagination_aria: 'Paginierung der Scanergebnisse',
            col_risk_category: 'Risikokategorie',
            risk_category_sr: 'regulatorische Risikokategorie',
            archive_retention_heading: 'Archivaufbewahrung',
            archive_retention_info: 'Scanergebnisse werden für die in der Datenaufbewahrungsrichtlinie Ihrer Organisation angegebene Dauer aufbewahrt. Die archivierten Ergebnisse umfassen den vollständigen Scan-Kontext (Framework-Tags, Risikokategorien, Abhilfemaßnahmen) und können als CSV für behördliche Nachweise exportiert werden.',
            export_context_info: 'Exportierte CSV-Dateien enthalten regulatorischen Kontext: Risikokategorie, anwendbare Rahmenbedingungen und umsetzbare Abhilfedetails für jedes Ergebnis.',
        },
        scanner_settings: {
            page_title: 'Scannereinstellungen',
            title: 'Scannereinstellungen',
            back_to_scanner: 'Zurück zum Scanner',
            failed_load: 'Die Scannerkonfiguration konnte nicht geladen werden',
            endpoint_url: 'Endpunkt URL',
            save_success: 'Scannerkonfiguration erfolgreich gespeichert.',
            save_failed: 'Die Scannerkonfiguration konnte nicht gespeichert werden',
            test_connection: 'Testverbindung',
            testing: 'Testen...',
            saving: 'Sparen...',
            loading_sr: 'Laden der Scannereinstellungen',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Datenschutzüberwachungseinstellungen',
            title: 'Überwachungseinstellungen',
            back_to_dashboard: 'Zurück zum Dashboard',
            failed_load: 'Die Überwachungskonfiguration konnte nicht geladen werden',
            credentials_heading: 'AesirX Anmeldeinformationen',
            connected_as: 'Verbunden als {username}',
            credentials_desc: 'Geben Sie Ihre AesirX-Kontoanmeldeinformationen ein, um Privacy Monitoring zu verbinden. Finden Sie Ihre Mitglieds-ID auf der Profilseite Ihres AesirX-Kontos.',
            client_id: 'Kunden-ID',
            client_secret: 'Client-Geheimnis',
            username: 'AesirX Benutzername (E-Mail)',
            password: 'AesirX Passwort',
            placeholder_keep: '(Leer lassen, um vorhanden zu bleiben)',
            credential_form_aria: 'AesirX Anmeldeformular',
            test_connection: 'Testverbindung',
            testing: 'Testen…',
            test_aria: 'Testen Sie die AesirX-Verbindung',
            testing_aria: 'Verbindung testen',
            save_configuration: 'Konfiguration speichern',
            saving: 'Sparen…',
            save_aria: 'Anmeldedaten speichern',
            saving_aria: 'Anmeldeinformationen speichern',
            credentials_saved: 'AesirX Anmeldeinformationen erfolgreich gespeichert.',
            credentials_saving: 'AesirX Anmeldeinformationen gespeichert. Konfiguration wird neu geladen…',
            credentials_save_failed: 'Anmeldeinformationen konnten nicht gespeichert werden',
            client_id_required: 'Kunden-ID und Benutzername sind erforderlich.',
            all_fields_required: 'Alle Anmeldedatenfelder sind erforderlich.',
            email_heading: 'E-Mail-Benachrichtigungen',
            receive_emails: 'Erhalten Sie E-Mails mit Scanberichten',
            email_toggle_aria: 'E-Mails mit Scanberichten ein-/ausschalten',
            only_on_change: 'Senden Sie nur eine E-Mail, wenn sich der Risikostatus ändert',
            only_on_change_aria: 'E-Mail nur bei Risikostatusänderung umschalten',
            emails_enabled: 'Scan-Berichts-E-Mails aktiviert',
            emails_disabled: 'Scan-Berichts-E-Mails deaktiviert',
            only_change_on: 'Nur E-Mail bei Risikoänderung',
            only_change_off: 'E-Mail bei jedem Scan',
            email_failed: 'E-Mail-Einstellung konnte nicht aktualisiert werden',
            email_settings_aria: 'Einstellungen für E-Mail-Benachrichtigungen',
            loading_sr: 'Überwachungseinstellungen werden geladen',
        },
        entity_types: {
            user: 'Benutzer',
            case: 'Fall',
            incident: 'Vorfall',
            assessment: 'Bewertung',
            vendor: 'Anbieter',
            consent: 'Zustimmung',
            policy: 'Politik',
            data_map: 'Datenkarte',
        },
        actions: {
            create: 'Erstellen',
            update: 'Aktualisieren',
            delete: 'Löschen',
            view: 'Ansicht',
            export: 'Exportieren',
            status_change: 'Statusänderung',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Formularübersicht',
            total_templates: 'Vorlagen gesamt',
            installed: 'Installiert',
            available: 'Verfügbar',
            by_market: 'Nach Markt',
            back_dashboard: 'Back zu Dashboard',
            browse_templates: 'Durchsuchen Vorlagen',
            catalog_coverage: 'Katalog Abdeckung',
            quick_start: 'Schnell Starten',
            recent_instances: 'Kürzliche Instanzen',
            total_instances: 'Gesamt Instanzen',
            fetch_failed: 'Fehler beim fetch Dashboard Daten..',
            no_instances: 'Keine Instanzen gefunden.',
            form_metrics: 'Formularmetriken-Übersicht',
            templates_total: 'Vorlagen insgesamt',
            instances_total: 'Instanzen insgesamt',
            installed_of_available: '{installed} von {available} installiert',
            templates_in_category: 'Vorlagen in {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Formularvorlagen',
            new: 'Neue Vorlage',
            edit: 'Vorlage bearbeiten',
            name: 'Vorlagenname',
            form_type: 'Formulartyp',
            market: 'Markt',
            language: 'Sprache',
            schema: 'Formularschema',
            preview: 'Vorschau',
            duplicate: 'Vorlage duplizieren',
            grid_view: 'Raster Ansehen',
            list_view: 'Liste Ansehen',
            fetch_failed: 'Fehler beim fetch Vorlagen..',
            filter_templates: 'Vorlagen filtern',
            version_history: 'Versionshistorie',
            no_version_history: 'Keine Versionshistorie — dies ist die Erstversion.',
            form_id: 'Formular-ID',
            source_instrument: 'Quellinstrument',
            issuer: 'Aussteller',
            all_frameworks: 'Alle Frameworks',
            view_mode: 'Ansichtsmodus',
            classification: 'Klassifizierung',
        },
        catalog: {
            title: 'Formularkatalog',
            install: 'Installieren',
            uninstall: 'Deinstallieren',
            open_form: 'Open form',
            install_all: 'Alle installieren',
            installed_tab: 'Installiert',
            catalog_tab: 'Katalog',
            search_placeholder: 'Vorlagen durchsuchen...',
            no_templates: 'Keine Vorlagen gefunden.',
            template_count: '{count} Vorlagen',
            all_markets: 'Alle Markets',
            sections: 'Abschnitte',
            all_categories: 'Alle Kategorien',
            category: 'Kategorie',
            category_install_failed: 'Fehler beim install category Vorlagen..',
            category_install_success: 'Kategorie Vorlagen installed.',
            fetch_failed: 'Fehler beim fetch Vorlagen..',
            fetch_installed_failed: 'Fehler beim fetch installed Vorlagen..',
            fields: 'Felder',
            install_failed: 'Fehler beim install Vorlage..',
            install_success: 'Vorlage installed successfully.',
            installing: 'Installiere...',
            removing: 'Entferne...',
            uninstall_confirm: 'Are you sure you want zu uninstall dieses Vorlage?',
            uninstall_failed: 'Fehler beim uninstall Vorlage..',
            uninstall_success: 'Vorlage uninstalled successfully.',
            installation_summary: 'Installationsübersicht',
            filter_catalog: 'Katalog filtern',
            all_frameworks: 'Alle Frameworks',
            templates_installed_count: '{count} Vorlage(n) erfolgreich installiert',
            all_already_installed: 'Alle Vorlagen in dieser Kategorie sind bereits installiert',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Formular-ID',
            issuer: 'Aussteller',
            catalog_tabs: 'Formularkatalog-Tabs',
        },
        filler: {
            back_templates: 'Back zu Vorlagen',
            no_sections: 'Keine Abschnitte gefunden.',
            draft_created: 'Entwurf created.',
            draft_saved: 'Entwurf saved.',
            fetch_template_failed: 'Fehler beim fetch Vorlage..',
            save_draft: 'Speichern Entwurf',
            save_draft_failed: 'Fehler beim save Entwurf..',
            saving: 'Speichere...',
            select_option: 'Auswählen ein Option',
            submit: 'Absenden',
            submit_failed: 'Fehler beim submit..',
            submitting: 'Sende...',
            title: 'Formular-Assistent',
            form_id_label: 'Formular-ID',
            schema_label: 'Schema',
            form_number_label: 'Form',
            framework_label: 'Rahmen',
            issuer_label: 'Aussteller',
            source_label: 'Quelle',
            market_label: 'Markt',
            section_of: 'Abschnitt {current} von {total}',
            form_navigation: 'Formularnavigation',
            form_progress: 'Formularfortschritt: Abschnitt {current} von {total}',
            removed_item: 'Element {index} aus {field} entfernt',
            added_item: 'Neues Element zu {field} hinzugefügt',
            go_to_previous: 'Zum vorherigen Abschnitt, Abschnitt {current} von {total}',
            go_to_next: 'Zum nächsten Abschnitt, Abschnitt {current} von {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Einreichungsstatus',
            advance: 'Einreichungsstatus aktualisieren...',
            not_started: 'Nicht begonnen',
            drafting: 'Entwurf',
            internal_review: 'Interne Prüfung',
            approved_for_submission: 'Zur Einreichung genehmigt',
            submitted_to_authority: 'Bei Behörde eingereicht',
            supplement_requested: 'Ergänzung angefordert',
            supplement_in_progress: 'Ergänzung in Bearbeitung',
            resubmitted: 'Erneut eingereicht',
            accepted: 'Angenommen',
            rejected: 'Abgelehnt',
            closed: 'Geschlossen',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Genehmigt By',
            form_answers: 'Formular Antworten',
            column_header: 'Form',
            no_answers: 'Keine answers gefunden.',
            view_outputs: 'Ansehen Ausgaben',
            approve: 'Genehmigen',
            approve_failed: 'Fehler beim approve..',
            approving: 'Genehmige...',
            detail_title: 'Instanz Detail',
            fetch_failed: 'Fehler beim fetch Instanzen..',
            generate_failed: 'Fehler beim generate Ausgabe..',
            generate_output: 'Generieren Ausgabe',
            generating: 'Generiere...',
            submitted_at: 'Eingereicht At',
            instance_metadata: 'Formularinstanz-Metadaten',
            filing_status_steps: 'Archivierungsstatus-Schritte',
            current_status: 'aktueller Status',
            filing_status_update_failed: 'Fehler beim Aktualisieren des Einreichungsstatus',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Back zu Instanz',
            no_outputs_msg: 'No Ausgaben have been generated yet.',
            no_outputs_title: 'No Ausgaben',
            output_name: 'Ausgabe Name',
            download: 'Herunterladen',
            download_failed: 'Fehler beim download..',
            fetch_failed: 'Fehler beim fetch Ausgaben..',
            format: 'Dateiformat',
            generated_at: 'Generiert At',
            generated_by: 'Generiert By',
            size: 'Größe',
            title: 'Formular Ausgaben',
            print: 'Drucken',
            print_outputs: 'Formularausgaben drucken',
            printing: 'Formularausgaben werden gedruckt',
            pdf_preview: 'PDF-Vorschau',
            downloading: '{name} wird heruntergeladen',
            preview_in_new_window: '{name} ({format}) in neuem Fenster anzeigen',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Umfang und Anwendbarkeit',
            risk: 'Risikobewertung und Kontrollzuordnung',
            policy: 'Organisatorische Sicherheitsrichtlinien',
            localization: 'Datenlokalisierung und lokale Präsenz',
            vendor: 'Drittanbieter und Lieferkette',
            authority: 'Bearbeitung behördlicher Anfragen',
            incident: 'Vorfallreaktion und Beweissicherung',
            training: 'Schulung und Bereitschaft',
        },
        market_finalization_domains: {
            data_law: 'Datengesetz — Governance und operative Kontrollen',
            pdpl_2025: 'PDPL 2025 — Datenschutz-Governance und Betroffenenrechte',
            decree_356: 'Dekret 356/2025 — Lieferanten- und grenzüberschreitende Kontrollen',
            telecom: 'Telekommunikationsgesetz — Vertraulichkeit und Behördenanfragen',
        },
        nav: {
            filing_case_links: 'Einreichungsfallverknüpfungen',
            section_routing: 'Abschnittsweiterleitung',
            submission_packages: 'Einreichungspakete',
        },
        filing_case_links: {
            title: 'Einreichungsfallverknüpfungen',
            link_form: 'Formular mit Einreichungsfall verknüpfen',
            filing_case_type: 'Einreichungsfalltyp',
            form_instance: 'Formularinstanz',
            select_form_instance: 'Formularinstanz auswählen...',
            select_filing_case: 'Einreichungsfall auswählen...',
            no_links_found: 'Keine Einreichungsfallverknüpfungen gefunden.',
            linked: 'Verknüpft',
            filing_case_id: 'Archivierungsfall-ID',
            link_created: 'Archivierungsfall-Verknüpfung erstellt',
            link_deleted: 'Einreichungsfallverknüpfung gelöscht',
            aria_delete_link: 'Verknüpfung {name} löschen',
            case_type_dpia_dossier: 'DSFA-Dossier',
            case_type_cross_border_filing: 'Grenzüberschreitende Einreichung',
            case_type_breach_notification: 'Meldung einer Datenschutzverletzung',
            no_instances_guidance: 'Noch keine Formularinstanzen. Erstellen Sie eine aus Formularvorlagen.',
            no_filings_guidance: 'Noch keine Einreichungsfälle. Erstellen Sie einen aus Bewertungen.',
        },
        section_routing: {
            title: 'Abschnittsweiterleitung',
            assign_section: 'Abschnitt zuweisen',
            department: 'Abteilung',
            select_department: 'Abteilung auswählen...',
            assignee: 'Zugewiesene Person',
            select_assignee: 'Zugewiesene Person auswählen...',
            section: 'Abschnitt',
            select_section: 'Abschnitt auswählen...',
            select_form_instance: 'Formularinstanz auswählen...',
            no_assignments_found: 'Keine Abschnittszuweisungen gefunden.',
            select_instance_prompt: 'Formularinstanz auswählen, um Abschnittszuweisungen anzuzeigen.',
            complete_section: 'Abschnitt abschließen',
            status_pending: 'Ausstehend',
            status_in_progress: 'In Bearbeitung',
            status_completed: 'Abgeschlossen',
            status_reviewed: 'Überprüft',
            form_instance_id: 'Formularinstanz-ID',
            assignment_created: 'Abschnittszuweisung erstellt',
            assignment_deleted: 'Abschnittszuweisung gelöscht',
            status_updated: 'Abschnittsstatus aktualisiert',
            aria_delete_assignment: 'Zuweisung {name} löschen',
            deadline: 'Frist',
            deadline_help: 'Optional. Zuweisungen nach diesem Datum werden als überfällig angezeigt.',
            overdue: 'Überfällig',
            days_remaining: 'Tage verbleibend',
            approval_requested: 'Zur Überprüfung eingereicht',
            form_approved: 'Formular genehmigt',
            form_rejected: 'Formular abgelehnt',
            no_departments_guidance: 'Keine Abteilungen konfiguriert. Richten Sie diese in den Organisationseinstellungen ein.',
            no_sections_guidance: 'Keine Abschnitte in der Vorlage definiert. Stellen Sie sicher, dass die Formularvorlage Abschnitte enthält.',
            select_instance_first: 'Wählen Sie zuerst eine Formularinstanz aus, um verfügbare Abschnitte zu laden.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Einreichungspakete',
            assemble_package: 'Einreichungspaket zusammenstellen',
            dossier: 'Dossier',
            select_dossier: 'Dossier auswählen...',
            package_label: 'Paketbezeichnung',
            cover_letter: 'Anschreiben',
            checklist: 'Checkliste',
            evidence_index: 'Beweisverzeichnis',
            completeness: 'Vollständigkeit',
            incomplete_warning: 'Zusammenstellung nicht möglich: Formular hat unvollständige Abschnitte.',
            no_packages_found: 'Keine Einreichungspakete gefunden.',
            version: 'Version',
            package_assembled: 'Einreichungspaket zusammengestellt',
            package_deleted: 'Einreichungspaket gelöscht',
            aria_delete_package: 'Paket {name} löschen',
            status_draft: 'Entwurf',
            status_complete: 'Vollständig',
            status_submitted: 'Eingereicht',
            status_accepted: 'Akzeptiert',
            status_rejected: 'Abgelehnt',
            package_updated: 'Einreichungspaket aktualisiert',
            document_generated: 'Dokument erstellt',
            documents: 'Dokumente',
            generate: 'Generieren',
            preview: 'Vorschau',
            download: 'Herunterladen',
            download_all: 'Alle herunterladen',
            no_dossiers_guidance: 'Noch keine Dossiers. Erstellen Sie eines aus Bewertungen.',
            package_plan_help: 'In Paketplan aufnehmen (automatische Dokumentenerstellung verfügbar):',
            cover_letter_help: 'Offizielles Anschreiben an die zuständige Behörde',
            checklist_help: 'Compliance-Checkliste zur Überprüfung aller Einreichungsanforderungen',
            evidence_index_help: 'Index der im Einreichungsdokument referenzierten Nachweisdokumente',
            completeness_skipped: 'Vollständigkeitsprüfung übersprungen — keine Formularinstanz mit diesem Dossier verknüpft.',
            completeness_unavailable: 'Vollständigkeitsprüfung nicht verfügbar — Paketzusammenstellung wird fortgesetzt.',
            duplicate_package_error: 'Für dieses Dossier existiert bereits ein Einreichungspaket.',
            doc_cover_letter: 'Anschreiben',
            doc_checklist: 'Checkliste',
            doc_evidence_index: 'Nachweisverzeichnis',
            export_csv: 'CSV exportieren',
            export_success: 'Verknüpfungen der Einreichungsfälle erfolgreich exportiert',
            no_assignments_guidance: 'Noch keine Abschnittszuweisungen. Weisen Sie Abschnitte Abteilungen und Teammitgliedern zu, um die Weiterleitung zu starten.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Vorlage auswählen',
            hint: 'Wählen Sie eine regulatorische Formularvorlage oder beginnen Sie mit einem leeren Formular.',
            market_filter: 'Nach Markt filtern',
            framework_filter: 'Nach Rahmenwerk filtern',
            all_markets: 'Alle Märkte',
            all_frameworks: 'Alle Rahmenwerke',
            search_placeholder: 'Vorlagen suchen...',
            blank_form: 'Leeres / allgemeines Formular',
            blank_form_desc: 'Ohne Vorlage erstellen — Felder manuell ausfüllen.',
            no_templates: 'Keine Vorlagen für diesen Filter gefunden.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Auswählen',
            link_error: 'Vorlagenverknüpfung fehlgeschlagen — Sie können eine Vorlage später in der Bearbeitungsansicht verknüpfen.',
            internal_badge: 'Interne Vorlage',
            legacy_badge: 'Veraltet',
            draft_badge: 'Entwurf',
            upcoming_badge: 'Kommend',
            hidden_badge: 'Ausgeblendet',
            show_hidden_toggle: 'Ausgeblendete / veraltete Vorlagen anzeigen',
            show_hidden_toggle_hint: 'Administratoransicht: umfasst Vorlagen, die als internal_only, hidden, legacy oder draft markiert sind.',
        },
        cover_letter_modal: {
            title: 'Anschreiben: Vorschau und Bearbeitung',
            iframe_title: 'Anschreiben-Vorschau',
            stage_label: 'Phase',
            stage_initial: 'Erste Einreichung',
            stage_supplement: 'Ergänzungsantwort',
            stage_resubmission: 'Erneute Einreichung',
            subject_label: 'Betreff',
            body_label: 'Hauptabsatz',
            notes_label: 'Zusätzliche Notizen',
            refresh_preview: 'Vorschau aktualisieren',
            confirm: 'Bestätigen und speichern',
            regenerate: 'Neu generieren',
            snapshot_saved: 'Anschreiben gespeichert',
        },
        related_panel: {
            title: 'Zugehörige Regulierungsformulare',
            open_form: 'Formular ausfüllen',
            no_forms: 'Keine zugehörigen Formulare gefunden.',
            hint_vn: 'VN-spezifische Regulierungsformulare verfügbar — wählen Sie ein Formular, um den Compliance-Assistenten zu öffnen.',
            internal_badge: 'Intern',
            official_badge: 'Offiziell',
            loading: 'Zugehörige Formulare werden geladen...',
            error: 'Zugehörige Formulare konnten nicht geladen werden.',
            form_id: 'Formular-ID',
            form_name: 'Formularname',
            framework: 'Rahmenwerk',
        },
        view_form: 'Formular anzeigen',
        edit_form: 'Formular bearbeiten',
        save_draft: 'Entwurf speichern',
        module_settings: 'Moduleinstellungen',
        validation_errors: 'Bitte beheben Sie die Validierungsfehler',
        custom_name_help: 'Optional — Standard ist der Vorlagenname, falls leer',
        select_placeholder: 'Auswählen...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Zurück',
        next_section: 'Weiter',
        loading: 'Wird geladen...',
        array_add: 'Hinzufügen',
        array_remove: 'Entfernen',
        array_item_label: '{label} Element {index}',
        array_add_item: 'Element zu {label} hinzufügen',
        array_remove_item: 'Element {index} aus {label} entfernen',
        legacy_banner: 'Dieser Datensatz wurde vor den vereinheitlichten Formularen erstellt. Einige Daten können zwischen Entität und Formularinstanz abweichen.',
        field_required: '{label} ist erforderlich',
        section_announcement: 'Abschnitt {current} von {total}: {title}',
        sections_nav_label: 'Formularabschnitte',
        no_sections: 'Keine Vorlagenabschnitte verfügbar.',
        section_label_tpl: 'Abschnitt {index}: {title}',
        field_shown_announcement: '{field} wird jetzt angezeigt.',
        field_required_announcement: '{field} ist jetzt erforderlich.',
        submit_blocked_hint_one: '1 Pflichtfeld benötigt noch eine Antwort.',
        submit_blocked_hint_many: '{count} Pflichtfelder benötigen noch eine Antwort.',
        module_settings_auto_applied: 'Modulspezifische Einstellungen werden automatisch übernommen.',
        operation_failed: 'Der Vorgang ist fehlgeschlagen',
        failed_to_load_form: 'Formular konnte nicht geladen werden',
        failed_to_save_form: 'Formular konnte nicht gespeichert werden',
        failed_to_submit_form: 'Formular konnte nicht gesendet werden',
        failed_to_load_template: 'Vorlage konnte nicht geladen werden',
        failed_to_save_draft: 'Entwurf konnte nicht gespeichert werden',
        failed_to_create: 'Erstellung fehlgeschlagen',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Paketkatalog',
        install: 'Paket installieren',
        uninstall: 'Paket deinstallieren',
        install_batch: 'Paketgruppe installieren',
        installed_tab: 'Installiert',
        catalog_tab: 'Katalog',
        pack_details: 'Paketdetails',
        frameworks: 'Rahmenwerke',
        requirements: 'Anforderungen',
        artifacts: 'Artefakte',
        language_pack: 'Sprachpaket',
        dependencies: 'Abhängigkeiten',
        no_packs: 'Keine Pakete gefunden.',
    },
    platform_pages: {
        users: {
            title: 'Benutzer',
            new: 'Neuer Benutzer',
            invite: 'Benutzer einladen',
            edit: 'Benutzer bearbeiten',
            name: 'Bezeichnung',
            email: 'E-Mail',
            role: 'Rolle',
            status: 'Zustand',
            identity_type: 'Identitätstyp',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Letzte Anmeldung',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Deaktivieren',
            reactivate: 'Reaktivieren',
            unlock: 'Entsperren',
            reset_password: 'Passwort zurücksetzen',
            remove: 'Entfernen',
            remove_confirm: '"{name}" entfernen? Diese Aktion ist dauerhaft und der Benutzer verliert jeglichen Zugriff. Der Datensatz wird zu Prüfzwecken aufbewahrt.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Rollen',
            new: 'Neue Rolle',
            edit: 'Rolle bearbeiten',
            name: 'Rollenname',
            description: 'Beschreibung',
            permissions: 'Berechtigungen',
            users_count: 'Benutzer',
            detail: 'Rollendetails',
            all_tiers: 'Alle Stufen',
            tier: 'Stufe',
            type_system: 'System',
            type_custom: 'Benutzerdefiniert',
            role_name_placeholder: 'z. B. regionaler Compliance-Leiter',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Mein Profil',
            name: 'Bezeichnung',
            display_name: 'Anzeigename',
            email: 'E-Mail',
            phone: 'Telefon',
            department: 'Abteilung',
            job_title: 'Berufsbezeichnung',
            language: 'Sprache',
            role: 'Rolle',
            persona: 'Erkannte Persona',
            last_login: 'Letzte Anmeldung',
            save: 'Änderungen speichern',
            save_success: 'Profil erfolgreich aktualisiert',
            module_tips_section: 'Modultipps',
            tips_toggle: 'Regulatorische Tipps in Modulen anzeigen',
            dismissed_tips: 'Ausgeblendete Tipps',
            reset_dismissed: 'Alle ausgeblendeten Tipps zurücksetzen',
            notification_prefs: 'Benachrichtigungseinstellungen',
            notification_prefs_link: 'Benachrichtigungseinstellungen verwalten →',
            change_password: 'Passwort ändern',
            current_password: 'Aktuelles Passwort',
            new_password: 'Neues Passwort',
            confirm_password: 'Passwort bestätigen',
            accessibility: 'Barrierefreiheit',
            high_contrast: 'Hoher Kontrast Modus',
            high_contrast_desc: 'Kontrast erhoehen fuer bessere Lesbarkeit. Erfuellt WCAG 2.1 AAA.',
            passwords_no_match: 'Passwörter stimmen nicht überein',
            password_changed_logout: 'Passwort geändert. Sie werden abgemeldet.',
            failed_update: 'Profil konnte nicht aktualisiert werden',
            failed_change_password: 'Passwort konnte nicht geändert werden',
            none: 'Keine',
            never: 'Nie',
            password_requirements: 'Min. 12 Zeichen, Großbuchstabe, Kleinbuchstabe, Ziffer, Sonderzeichen',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA-Einstellungen',
            mfa_enable: 'MFA aktivieren',
            mfa_disable: 'MFA deaktivieren',
            mfa_setup: 'Scannen Sie diesen QR-Code mit Ihrer Authentifizierungs-App',
            sso_title: 'SSO-Konfiguration',
            sso_provider: 'Anbieter',
            sso_client_id: 'Client-ID',
            sso_redirect: 'Weiterleitungs-URI',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API-Schlüssel',
            api_keys_new: 'Neuer API-Schlüssel',
            api_keys_name: 'Schlüsselname',
            api_keys_last_used: 'Zuletzt verwendet',
            api_keys_expires: 'Ablaufdatum',
            api_keys_revoke: 'Widerrufen',
            service_accounts_title: 'Dienstkonten',
            ip_allowlist_title: 'IP-Positivliste',
            ip_add: 'IP-Adresse hinzufügen',
            ip_address: 'IP-Adresse',
            ip_description: 'Beschreibung',
            delegations_title: 'Delegierungen',
            delegations_new: 'Neue Delegierung',
            delegations_from: 'Von Benutzer',
            delegations_to: 'An Benutzer',
            delegations_expires: 'Ablaufdatum',
            break_glass_title: 'Notfallzugriff',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Zugriff anfordern',
            break_glass_reason: 'Grund für den Zugriff',
            break_glass_duration: 'Dauer',
            session_settings_title: 'Sitzungseinstellungen',
            eviction_policy_section: 'Verdrängungsrichtlinie',
            session_timeout: 'Sitzungszeitüberschreitung (Minuten)',
            session_max_concurrent: 'Maximale gleichzeitige Sitzungen',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Aktive Sitzungen',
            active_sessions_device: 'Gerät',
            active_sessions_ip: 'IP-Adresse',
            active_sessions_started: 'Gestartet',
            active_sessions_revoke: 'Sitzung widerrufen',
            active_sessions_confirm_terminate: 'Möchten Sie diese Sitzung wirklich beenden?',
            active_sessions_confirm_terminate_current: 'Dies ist Ihre aktuelle Sitzung. Wenn Sie es beenden, werden Sie sofort abgemeldet. Weitermachen?',
            active_sessions_confirm_terminate_all: 'Sind Sie sicher, dass Sie alle anderen Sitzungen beenden möchten? Nur Ihre aktuelle Sitzung bleibt aktiv.',
            active_sessions_terminated: 'Sitzung erfolgreich beendet.',
            active_sessions_all_terminated: 'Alle anderen Sitzungen wurden beendet.',
            active_sessions_terminate_all: 'Beenden Sie alle anderen Sitzungen',
            active_sessions_last_activity: 'Letzte Aktivität',
            active_sessions_current: 'Aktuelle Sitzung',
            active_sessions_revoke_selected: 'Ausgewählte widerrufen',
            active_sessions_confirm_terminate_selected: 'Möchten Sie die ausgewählten Sitzungen wirklich beenden?',
            active_sessions_selected_terminated: 'Ausgewählte Sitzungen erfolgreich beendet.',
            ip_allowlist_desc: 'Verwalten Sie zulässige IP-Adressen für den Zugriff auf API.',
            ip_no_entries: 'Keine IP-Adressen konfiguriert.',
            session_settings_desc: 'Konfigurieren Sie das Sitzungszeitlimit und die Beschränkungen für gleichzeitige Sitzungen.',
            session_settings_save_policy: 'Sitzungsrichtlinie speichern',
            session_settings_saved: 'Sitzungseinstellungen erfolgreich aktualisiert.',
            session_settings_idle_timeout: 'Leerlaufzeitüberschreitung (Minuten)',
            session_settings_absolute_timeout: 'Absolutes Timeout (Minuten)',
            session_settings_help_idle: 'Die Sitzung läuft nach diesem Zeitraum der Inaktivität ab.',
            session_settings_help_absolute: 'Die Sitzung läuft unabhängig von der Aktivität nach dieser Gesamtdauer ab.',
            session_settings_help_max: 'Maximale Anzahl gleichzeitiger Sitzungen pro Benutzer.',
            sso_note: 'Änderungen an der SSO-Konfiguration erfordern möglicherweise eine erneute Authentifizierung der Benutzer.',
            service_accounts_warning: 'Dieser API-Schlüssel wird nur einmal angezeigt. Kopieren Sie es jetzt und bewahren Sie es sicher auf.',
            service_accounts_new: 'Neues Dienstkonto',
            service_accounts_name: 'Kontoname',
            service_accounts_role: 'Rolle',
            service_accounts_created: 'Erstellt',
            service_accounts_desc: 'Verwalten Sie Maschine-zu-Maschine-Dienstkonten für API Integrationen.',
            break_glass_warning: 'Der Zugriff durch Glasbruch wird überwacht und protokolliert. Nur für echte Notfälle verwenden.',
            break_glass_min_chars: '{count} / {min} Mindestzeichen',
            break_glass_permission_count: '{count} Berechtigung(en) ausgewählt',
            break_glass_submitted: 'Glasbruchantrag eingereicht. Warten auf Genehmigung.',
            break_glass_approved: 'Antrag auf Glasbruch genehmigt.',
            break_glass_denied: 'Glasbruchanforderung abgelehnt.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Bestätigen Sie die Aktivierung',
            break_glass_pending: 'Ausstehende Anfragen',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Verbleibende Zeit',
            break_glass_expired: 'Abgelaufen',
            break_glass_approved_denied_by: 'Genehmigt/Abgelehnt von',
            delegations_desc: 'Verwalten Sie temporäre Berechtigungsdelegierungen zwischen Benutzern.',
            delegations_my: 'Meine Delegationen',
            delegations_to_me: 'An mich delegiert',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Alle Berechtigungen',
            delegations_scope_specific: 'Spezifische Berechtigungen',
            delegations_created: 'Delegation erfolgreich erstellt.',
            delegations_revoked: 'Delegation widerrufen.',
            delegations_confirm_revoke: 'Sind Sie sicher, dass Sie diese Delegation widerrufen möchten?',
            delegations_confirm_revoke_title: 'Bestätigen Sie „Widerrufen“.',
            delegations_permission_count: '{count} Berechtigung(en)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Status',
            api_keys_prefix: 'Schlüsselpräfix',
            api_keys_permissions_count: 'Berechtigungen',
            api_keys_ip_count: 'IP-Einschränkungen',
            api_keys_created: 'Erstellt',
            api_keys_confirm_revoke_title: 'Bestätigen Sie „Widerrufen“.',
            api_keys_confirm_revoke_message: 'Sind Sie sicher, dass Sie den API-Schlüssel „{name}“ widerrufen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
            api_keys_confirm_rotate_title: 'Bestätigen Sie die Drehung',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API Schlüssel erfolgreich erstellt.',
            api_keys_revoked_success: 'Der API-Schlüssel „{name}“ wurde widerrufen.',
            api_keys_rotated_success: 'Der API-Schlüssel „{name}“ wurde gedreht. Kopieren Sie den neuen Schlüssel unten.',
            api_keys_load_error: 'API-Schlüssel konnten nicht geladen werden.',
            api_keys_create_error: 'Der Schlüssel API konnte nicht erstellt werden.',
            api_keys_revoke_error: 'Der Schlüssel API konnte nicht widerrufen werden.',
            api_keys_rotate_error: 'Der Schlüssel API konnte nicht gedreht werden.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 Minuten',
            break_glass_duration_30: '30 Minuten',
            break_glass_duration_60: '60 Minuten (1h)',
            break_glass_duration_120: '120 Minuten (2h)',
            break_glass_duration_240: '240 Minuten (4h)',
            sod_rules_title: 'Regeln zur Aufgabentrennung',
            sod_violations_report: 'Bericht über Verstöße',
            sod_create_rule: 'Regel erstellen',
            sod_col_name: 'Name',
            sod_col_description: 'Beschreibung',
            sod_col_conflict_count: 'Konfliktanzahl',
            sod_col_severity: 'Schweregrad',
            sod_col_status: 'Status',
            sod_col_actions: 'Aktionen',
            sod_no_rules: 'Keine SoD-Regeln gefunden',
            sod_severity_warning: 'Warnung',
            sod_severity_blocking: 'Blockieren',
            sod_active: 'Aktiv',
            sod_inactive: 'Inaktiv',
            sod_edit: 'Bearbeiten',
            sod_exceptions: 'Ausnahmen',
            sod_deactivate: 'Deaktivieren',
            sod_confirm_deactivate_title: 'Bestätigen Sie die Deaktivierung',
            sod_confirm_deactivate_message: 'Regel „{name}“ deaktivieren? Es werden keine Verstöße mehr gemeldet.',
            sod_deactivate_error: 'Regel konnte nicht deaktiviert werden',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Zugriffsüberprüfungen',
            campaigns: 'Überprüfungskampagnen',
            new_campaign: 'Neue Kampagne',
            campaign_name: 'Kampagnenname',
            scope: 'Umfang',
            reviewers: 'Prüfer',
            due_date: 'Fälligkeitsdatum',
            progress: 'Fortschritt',
            my_reviews: 'Meine Überprüfungen',
            approve: 'Genehmigen',
            revoke: 'Widerrufen',
            analytics: 'Rollenanalysen',
            analytics_title: 'Rollenanalysen',
            permission_usage: 'Berechtigungsnutzung',
            role_coverage: 'Rollenabdeckung',
            campaign_detail: 'Kampagnendetails',
            campaign_description: 'Beschreibung',
            campaign_status: 'Status',
            campaign_deadline: 'Frist',
            campaign_scope_type: 'Bereichstyp',
            campaign_completion: 'Fertigstellung',
            campaign_total_items: 'Gesamtzahl der Artikel',
            campaign_pending_items: 'Ausstehend',
            campaign_confirmed_items: 'Bestätigt',
            campaign_revoked_items: 'Widerrufen',
            campaign_escalated_items: 'Eskaliert',
            campaign_launch: 'Kampagne starten',
            campaign_cancel: 'Kampagne abbrechen',
            campaign_confirm_launch: 'Sind Sie sicher, dass Sie diese Kampagne starten möchten?',
            campaign_confirm_cancel: 'Möchten Sie diese Kampagne wirklich abbrechen?',
            campaign_launched: 'Kampagne erfolgreich gestartet.',
            campaign_cancelled: 'Kampagne abgebrochen.',
            review_items: 'Überprüfen Sie die Elemente',
            review_item_user: 'Benutzer',
            review_item_role: 'Rolle',
            review_item_reviewer: 'Rezensent',
            review_item_decision: 'Entscheidung',
            review_item_justify: 'Begründung',
            review_item_confirm: 'Bestätigen',
            review_item_revoke: 'Widerrufen',
            review_item_escalate: 'Eskalieren',
            all_decisions: 'Alle Entscheidungen',
            bulk_action: 'Massenaktion',
            bulk_confirm: 'Massenbestätigung',
            bulk_revoke: 'Massenwiderruf',
            selected_count: '{count} ausgewählt',
            justify_required: 'Eine Begründung ist erforderlich',
            scope_all_users: 'Alle Benutzer',
            scope_role_owner: 'Rolleninhaber',
            scope_department: 'Abteilung',
            back_to_campaigns: 'Zurück zu Kampagnen',
            recommendations: 'Empfehlungen',
            refreshing: 'Erfrischend...',
            decide: 'Entscheide',
            decision_by: 'Entscheidung von',
            loading_items: 'Artikel werden geladen...',
            loading_review_items: 'Bewertungsartikel werden geladen...',
            campaign_not_found: 'Kampagne nicht gefunden',
            loading_campaign: 'Kampagnendetails werden geladen...',
            campaign_completed: 'Kampagne erfolgreich abgeschlossen.',
            confirm_cancel_title: 'Bestätigen Sie „Abbrechen“.',
            justify_bulk_confirm: 'Begründung für die Massenbestätigung:',
            justify_bulk_revoke: 'Begründung für den Massenwiderruf:',
            items_selected: '{count} Element(e) ausgewählt',
            justify_bulk_required: 'Für Massenaktionen ist eine Begründung erforderlich',
            create_campaign_title: 'Erstellen Sie eine Kampagne zur Zugriffsüberprüfung',
            name_required: 'Name ist erforderlich',
            scope_type_required: 'Bereichstyp ist erforderlich',
            reviewer_strategy_required: 'Eine Gutachterstrategie ist erforderlich',
            deadline_required: 'Eine Frist ist erforderlich',
            reviewer_id_required: 'Für eine bestimmte Benutzerstrategie ist eine Prüfer-ID erforderlich',
            invalid_json: 'Ungültig JSON',
            scope_filter_label: 'Bereichsfilter (JSON)',
            reviewer_strategy_label: 'Reviewer-Strategie',
            reviewer_id_label: 'Rezensenten-ID',
            deadline_label: 'Frist',
            create_campaign_btn: 'Kampagne erstellen',
            scope_type_label: 'Bereichstyp',
            scope_by_role: 'Nach Rolle',
            scope_by_department: 'Nach Abteilung',
            scope_custom: 'Benutzerdefiniert',
            reviewer_manager: 'Manager',
            reviewer_specific_user: 'Spezifischer Benutzer',
            enter_json_filter: 'Geben Sie den Filter JSON ein',
            hint_label: 'Hinweis:',
            role_usage: 'Rollennutzung',
            over_privileged_users: 'Überprivilegierte Benutzer',
            role_name_col: 'Rollenname',
            assigned_users: 'Zugewiesene Benutzer',
            active_users_30d: 'Aktive Benutzer (30 Tage)',
            total_perms: 'Gesamtdauerwellen',
            used_perms: 'Gebrauchte Dauerwellen',
            unused_perms: 'Unbenutzte Dauerwellen',
            no_data_available: 'Keine Daten verfügbar',
            roles_col: 'Rollen',
            total_col: 'Insgesamt',
            used_col: 'Gebraucht',
            unused_pct: 'Unbenutzt %',
            current_count: 'Aktueller Zählerstand',
            recommended_count: 'Empfohlene Anzahl',
            rationale: 'Begründung',
            loading_analytics: 'Analyse wird geladen...',
            loading_role_analytics: 'Rollenanalysedaten werden geladen...',
            computed_at: 'Berechnet bei:',
            form_errors: 'Das Formular weist {count} Fehler auf. Bitte korrigieren Sie sie.',
            scope_filter_invalid_json: 'Bereichsfilter enthält ungültige JSON',
            scope_hint_all_users: 'Kein Filter erforderlich – alle Benutzer werden einbezogen.',
            review_type_label: 'Überprüfungstyp',
            review_type_required: 'Überprüfungstyp ist erforderlich',
            reviewer_ids_label: 'Prüfer-IDs',
            reviewer_ids_hint: 'Benutzer-IDs durch Kommas getrennt eingeben',
            reviewer_ids_required: 'Mindestens eine Prüfer-ID ist erforderlich',
            start_date_label: 'Startdatum',
            start_date_required: 'Startdatum ist erforderlich',
            end_date_label: 'Enddatum',
            end_date_required: 'Enddatum ist erforderlich',
            review_type_user_access: 'Benutzerzugriff',
            review_type_role_permissions: 'Rollenberechtigungen',
            review_type_service_accounts: 'Dienstkonten',
            review_type_privileged_access: 'Privilegierter Zugriff',
            review_type_application_access: 'Anwendungszugriff',
            auto_revoke_label: 'Automatisch widerrufen bei Ablehnung',
            campaigns_title: 'Greifen Sie auf Bewertungskampagnen zu',
            create_campaign_label: '+ Kampagne erstellen',
            no_campaigns_found: 'Keine Kampagnen gefunden',
            loading_campaigns: 'Kampagnen werden geladen...',
            no_reviews_assigned: 'Ihnen wurden keine Bewertungen zugewiesen',
            loading_reviews: 'Bewertungen werden geladen...',
            review_decision_title: 'Überprüfungsentscheidung',
            justification_label: 'Begründung',
            announce_loaded_review_items: '{count} Bewertungselemente geladen',
            announce_loaded_campaigns: 'Geladene {count} Kampagnen',
            announce_decision_submitted: 'Entscheidung „{decision}“ zur Überprüfung eingereicht',
            announce_decision_submitted_for: 'Entscheidung „{decision}“ eingereicht für {name}',
            announce_bulk_decision: 'Bulk {decision} wird auf {count} Artikel angewendet',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Bestätigungszeitpläne',
            new: 'Neuer Zeitplan',
            edit: 'Zeitplan bearbeiten',
            create_title: 'Bestätigungszeitplan erstellen',
            edit_title: 'Bestätigungszeitplan bearbeiten',
            schedule_history: 'Zeitplanverlauf',
            next_run_date: 'Nächstes Ausführungsdatum',
            schedule_name: 'Zeitplanname',
            frequency: 'Häufigkeit',
            next_run: 'Nächste Ausführung',
            last_run: 'Letzte Ausführung',
            history: 'Bestätigungsverlauf',
            description: 'Beschreibung',
            scope: 'Umfang',
            status: 'Status',
            created_at: 'Erstellt',
            updated_at: 'Aktualisiert',
            trigger_now: 'Jetzt auslösen',
            back_to_schedules: 'Zurück zu den Zeitplänen',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Zeitplan erfolgreich ausgelöst.',
            deleted: 'Zeitplan gelöscht.',
            saved: 'Zeitplan erfolgreich gespeichert.',
            campaigns_generated: 'Generierte Kampagnen',
            run_date: 'Ausführungsdatum',
            run_status: 'Ausführungsstatus',
            create_schedule: 'Zeitplan erstellen',
            view_history: 'Verlauf anzeigen',
            deactivate: 'Deaktivieren',
            overdue: 'OVERDUE',
            overdue_sr: 'Der Zeitplan ist überfällig',
            confirm_trigger_title: 'Aktion bestätigen',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Bestätigen Sie die Deaktivierung',
            confirm_deactivate_message: 'Möchten Sie diesen Zeitplan wirklich deaktivieren?',
            triggered_success: 'Zeitplan erfolgreich ausgelöst.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Zeitplan deaktiviert.',
            name_label: 'Name *',
            frequency_label: 'Häufigkeit *',
            scope_type_label: 'Umfangstyp *',
            scope_filter_label: 'Bereichsfilter (JSON)',
            reviewer_strategy_label: 'Reviewer-Strategie *',
            reviewer_id_label: 'Rezensenten-ID *',
            frequency_quarterly: 'Vierteljährlich',
            frequency_semi_annual: 'Halbjährlich',
            frequency_annual: 'Jährlich',
            scope_all_users: 'Alle Benutzer',
            scope_by_role: 'Nach Rolle',
            scope_by_department: 'Nach Abteilung',
            scope_custom: 'Benutzerdefiniert',
            reviewer_manager: 'Manager',
            reviewer_role_owner: 'Rolleninhaber',
            reviewer_specific_user: 'Spezifischer Benutzer',
            computed_next_run_date: 'Berechnetes Datum für die nächste Ausführung',
            current_scheduled_date: 'Aktuelles geplantes Datum',
            recomputed_frequency_changed: '(neu berechnet – Frequenz geändert)',
            estimated_days_hint: 'Geschätzt basierend auf {days} Tagen ab heute',
            validation_name_required: 'Name ist erforderlich',
            validation_frequency_required: 'Häufigkeit ist erforderlich',
            validation_scope_type_required: 'Bereichstyp ist erforderlich',
            validation_reviewer_strategy_required: 'Eine Gutachterstrategie ist erforderlich',
            validation_reviewer_id_required: 'Für eine bestimmte Benutzerstrategie ist eine Prüfer-ID erforderlich',
            validation_invalid_json: 'Ungültig JSON',
            validation_scope_filter_invalid_json: 'Bereichsfilter enthält ungültige JSON',
            form_errors: 'Das Formular weist {count} Fehler auf. Bitte korrigieren Sie sie.',
            create_schedule_btn: 'Zeitplan erstellen',
            schedule_history_title: 'Zeitplanverlauf',
            campaign_name_col: 'Kampagnenname',
            items_col: 'Artikel',
            no_campaigns_yet: 'Es wurden noch keine Kampagnen generiert',
            loading_history: 'Verlauf wird geladen...',
            loading_schedule: 'Zeitplan wird geladen...',
            total_campaigns: 'Kampagnen insgesamt: {count}',
            results_per_page: 'Ergebnisse pro Seite',
            page_size_option: '{size} / Seite',
        },
        sod: {
            title: 'Funktionstrennung (SoD-Regeln)',
            new: 'Neue Regel',
            edit: 'Regel bearbeiten',
            create_title: 'SoD-Regel erstellen',
            edit_title: 'SoD-Regel bearbeiten',
            rule_name: 'Regelname',
            conflicting_permissions: 'Kollidierende Berechtigungen',
            permission_a: 'Berechtigung A',
            permission_b: 'Berechtigung B',
            violations: 'SoD-Verstöße',
            violation_user: 'Benutzer',
            violation_rule: 'Verletzte Regel',
            violation_date: 'Erkannt am',
            exceptions: 'SoD-Ausnahmen',
            exception_reason: 'Ausnahmegrund',
            exception_approver: 'Genehmigt von',
            exception_status: 'Ausnahmestatus',
            has_exception: 'Hat eine Ausnahme',
            no_exception: 'Keine Ausnahme',
            grant_exception: 'Ausnahme gewähren',
            granting: 'Gewähren...',
            grant_new_exception: 'Neue Ausnahme gewähren',
            exceptions_for: 'Ausnahmen für: {name}',
            back_to_rules: 'Zurück zu den Regeln',
            no_violations: 'Es wurden keine Verstöße festgestellt',
            no_exceptions_found: 'Für diese Regel wurden keine Ausnahmen gefunden',
            all_exception_statuses: 'Alle Ausnahmestatus',
            user_id_label: 'Benutzer-ID',
            justification_label: 'Begründung',
            compensating_controls_label: 'Kompensierende Kontrollen',
            expires_at_label: 'Läuft ab um',
            granting_exception_for: 'Gewähren einer Ausnahme für Regel: {name}',
            severity: 'Schweregrad',
            severity_warning: 'Warnung',
            severity_blocking: 'Blockieren',
            violation_count: '{count} Verstoß(e)',
            exception_count: '{count} Ausnahme(n)',
            rule_description: 'Beschreibung',
            confirm_delete: 'Sind Sie sicher, dass Sie diese Regel löschen möchten?',
            deleted: 'Regel gelöscht.',
            framework_code: 'Rahmenwerk',
            name_label: 'Name *',
            severity_label: 'Schweregrad *',
            conflict_pairs_legend: 'Konfliktberechtigungspaare',
            conflict_pairs_hint: 'Jedes Paar definiert zwei Berechtigungen, die nicht von demselben Benutzer gehalten werden sollten.',
            placeholder_permission_a: '-- Erlaubnis A --',
            placeholder_permission_b: '-- Erlaubnis B --',
            permission_a_for_pair: 'Berechtigung A für Paar {index}',
            permission_b_for_pair: 'Berechtigung B für Paar {index}',
            conflict_pair_label: 'Konfliktpaar {index}',
            remove_conflict_pair: 'Konfliktpaar entfernen {index}',
            add_pair: '+ Paar hinzufügen',
            placeholder_name: 'z.B. Trennung der Zahlungsgenehmigung',
            create_rule: 'Regel erstellen',
            loading_rule: 'SoD-Regel wird geladen...',
            failed_load_rule: 'Die Regel konnte nicht geladen werden.',
            failed_create_rule: 'Die SoD-Regel konnte nicht erstellt werden.',
            failed_update_rule: 'SoD-Regel konnte nicht aktualisiert werden.',
            validation_name_required: 'Name ist erforderlich.',
            validation_pair_required: 'Es ist mindestens ein Konfliktberechtigungspaar erforderlich.',
            validation_pair_both_required: 'Paar {index}: Beide Berechtigungen müssen ausgewählt werden.',
            validation_pair_must_differ: 'Paar {index}: Berechtigung A und Berechtigung B müssen unterschiedlich sein.',
            added_conflict_pair: 'Konfliktpaar hinzugefügt {index}',
            removed_conflict_pair: 'Konfliktpaar {index} entfernt',
            announce_loaded_violations: '{count} Verstöße geladen',
            announce_loaded_exceptions: '{count} Ausnahmen geladen',
            announce_loaded_rules: '{count} SoD-Regeln geladen',
            announce_loading_exceptions: 'Ausnahmen werden geladen…',
            regulatory_info: {
                sod_context: 'SoD-Regeln setzen die von der PDPL geforderte regulatorische Rollentrennung durch. Verstoesse mit framework_code koennen in rahmenspezifische Nachweispakete aufgenommen werden (UC-VN-09).',
                violation_export_context: 'SoD-Verletzungsexporte enthalten framework_code, widerspruchliche Rollen, Schweregrad, Ersteller und Erkennungszeitpunkt fuer Nachweispakete (P-VN-10).',
                exception_context: 'SoD-Ausnahmen erzeugen pruefungsfaehige Nachweise: Genehmiger, Begruendung, kompensierende Kontrollen und Ablaufdatum. Abgelaufene Ausnahmen bleiben sichtbar (UC-VN-09 Schritt 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP-Integration',
            status: 'Verbindungsstatus',
            endpoint: 'CMP-Endpunkt',
            last_sync: 'Letzte Synchronisierung',
            configure: 'Konfigurieren',
        },
        purpose_mapping: {
            title: 'Zweckzuordnung',
            cmp_purpose: 'CMP-Zweck',
            grc_purpose: 'GRC-Zweck',
            mapping_status: 'Zuordnungsstatus',
            auto_map: 'Automatisch zuordnen',
            new_mapping: 'Neue Zuordnung',
        },
        webhook: {
            title: 'Webhook-Konfiguration',
            webhook_url: 'Webhook-URL',
            secret: 'Webhook-Geheimnis',
            events: 'Ereignistypen',
            test_webhook: 'Webhook testen',
        },
        bulk_sync: {
            title: 'Massenabgleich',
            sync_now: 'Jetzt synchronisieren',
            last_run: 'Letzte Ausführung',
            records_synced: 'Synchronisierte Datensätze',
            sync_history: 'Synchronisierungsverlauf',
            syncing: 'Synchronisierung...',
        },
        sync_settings: {
            sync_failures: 'Synchronisierung Failures',
            sync_secret_warning: 'Synchronisierung Geheimnis (save dieses now — it will not be shown again):',
            push_description: 'Konfigurieren der GRC Suite zu push case Status updates zu AesirX CMP.',
            technical_description: 'Diese Konfiguration steuert, wie die GRC Suite Fallstatusaktualisierungen an AesirX CMP verschiebt.',
            direction_label: 'Richtung',
            direction_value: 'GRC Suite → AesirX CMP (ausgehend)',
            auth_label: 'Authentifizierung',
            auth_value: 'HMAC-SHA256-Signatur im X-GRC-Signature-Header',
            retry_label: 'Wiederholen Richtlinie',
            retry_value: '3 retries mit exponential backoff (30s, 2min, 10min)',
            inbound_label: 'Eingehend Konfiguration',
            inbound_value: 'Eingehend Webhook (CMP → GRC) is configured in',
            cmp_webhook_settings: 'CMP Webhook Einstellungen',
            page_title: 'CMP Synchronisierungseinstellungen',
            outbound_sync_configured: 'Ausgehende Synchronisierung konfiguriert.',
            configure_sync_failed: 'Synchronisierung konnte nicht konfiguriert werden',
            configuration_updated: 'Konfiguration aktualisiert.',
            update_configuration_failed: 'Konfiguration konnte nicht aktualisiert werden',
            connection_test_failed: 'Verbindungstest fehlgeschlagen',
            test_result_message: 'Test an {endpoint} gesendet. Status: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Neu → Unter Überprüfen (assigned)',
                under_review_to_identity_verification: 'Unter Überprüfen → Identity Verifizierung',
                under_review_to_in_progress: 'Unter Überprüfen → In Fortschritt',
                in_progress_to_completed: 'In Fortschritt → Abgeschlossen',
                in_progress_to_denied: 'In Fortschritt → Denied',
                any_to_closed: 'Any → Geschlossen',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhooks',
            new: 'Neuer Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Ereignisse',
            secret: 'Geheimnis',
            active: 'Aktiv',
            test: 'Testen',
            create_failed: 'Fehler beim create Webhook..',
            create_success: 'Webhook erfolgreich erstellt.',
            deactivate_failed: 'Fehler beim deactivate Webhook..',
            deactivate_success: 'Webhook deaktiviert.',
            fetch_failed: 'Fehler beim fetch Webhooks..',
            send_test_failed: 'Fehler beim send test..',
            test_failed: 'Webhook test fehlgeschlagen.',
            test_success: 'Webhook-Test erfolgreich.',
            edit_title: 'Webhook bearbeiten',
            announce_loaded: 'Webhooks geladen',
            update_success: 'Webhook erfolgreich aktualisiert.',
            update_failed: 'Fehler beim Aktualisieren des Webhooks.',
            activate_success: 'Webhook aktiviert.',
            activate_failed: 'Fehler beim Aktivieren des Webhooks.',
        },
        domains: {
            title: 'Domänen',
            new: 'Neue Domain',
            domain_name: 'Domainname',
            verified: 'Verifiziert',
            verify: 'Verifizieren',
            add_failed: 'Fehler beim add Domäne..',
            add_success: 'Domäne added successfully.',
            domain_placeholder: 'z.B. grc.example.com',
            fetch_failed: 'Fehler beim fetch Domänen..',
            remove_failed: 'Fehler beim remove Domäne..',
            remove_success: 'Domäne removed successfully.',
            txt_record: 'TXT Datensatz',
            verify_dns_failed: 'DNS verification fehlgeschlagen.',
            verify_failed: 'Fehler beim verify Domäne..',
            verify_success: 'Domäne verified successfully.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Exportzeitpläne',
            new: 'Neuer Zeitplan',
            schedule_name: 'Bezeichnung',
            frequency: 'Häufigkeit',
            format: 'Dateiformat',
            last_run: 'Letzte Ausführung',
            next_run: 'Nächste Ausführung',
            create_failed: 'Fehler beim create Zeitplan..',
            create_success: 'Planen created successfully.',
            day_of_month: 'Tag von Monat',
            day_of_week: 'Tag von Week',
            deactivate_failed: 'Fehler beim deactivate Zeitplan..',
            deactivate_success: 'Planen deactivated.',
            fetch_failed: 'Fehler beim fetch Zeitpläne..',
            freq_daily: 'Täglich',
            freq_monthly: 'Monatlich',
            freq_weekly: 'Wöchentlich',
            recipients: 'Empfänger',
            time_utc: 'Zeit (UTC)',
            trigger_failed: 'Fehler beim trigger Export..',
            trigger_success: 'Exportieren triggered successfully.',
            type_audit_trail: 'Prüfprotokoll',
            type_compliance_report: 'Konformität Bericht',
            type_custom: 'Benutzerdefiniert',
            type_incident_report: 'Vorfall Bericht',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'E-Mail-Protokoll',
            recipient: 'Empfänger',
            subject: 'Betreff',
            sent_at: 'Gesendet am',
            status: 'Zustand',
            resend: 'Erneut senden',
            fetch_failed: 'Fehler beim fetch E-Mail logs..',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organisationen',
        create: 'Erstellen Organisation',
        edit: 'Bearbeiten Organisation',
        name: 'Bezeichnung',
        slug: 'URL-Kennung',
        timezone: 'Zeitzone',
        locale: 'Standard Sprache',
        parent: 'Übergeordnet Organisation',
        group: 'Gruppe',
        org_group: 'Organisationsgruppe',
        no_group: '(Keine – nicht in einer Gruppe)',
        created: 'Erstellt',
        no_parent: '(Keine – eigenständig)',
        no_orgs: 'Keine Organisationen gefunden',
        load_error: 'Fehler beim load Organisationen',
        save_error: 'Fehler beim save Organisation',
        created_msg: 'Organisation created',
        updated: 'Organisation updated',
        deactivate: 'Deaktivieren',
        deactivated: 'Organisation deactivated',
        confirm_deactivate: 'Are you sure you want zu deactivate dieses Organisation?',
        loaded_results: 'Organizations geladen, {count} Ergebnisse',
        form_loaded: 'Organization-Formular geladen',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Englisch (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Alle Rahmenwerke',
        assigned_at: 'Zugewiesen am',
    },
    intake_configs: {
        title: 'Öffentlich Aufnahme Configs',
        create: 'Erstellen Konfiguration',
        edit: 'Bearbeiten Aufnahme Konfiguration',
        label_col: 'Bezeichnung',
        key: 'Aufnahme Schlüssel',
        key_hint: 'Öffentlich URL will be: /rights-request?key=',
        mapped_org: 'Ziel Organisation',
        status: 'Zustand',
        enabled: 'Aktiviert',
        disabled: 'Deaktiviert',
        request_types: 'Aktiviert Anfrage Typen',
        language: 'Standard Sprache',
        public_url: 'Öffentlich URL',
        no_configs: 'Keine Aufnahme configs gefunden Erstellen one zu enable public rights request forms..',
        load_error: 'Fehler beim load Aufnahme configs',
        save_error: 'Fehler beim save',
        delete_error: 'Fehler beim delete',
        created_msg: 'Konfiguration created',
        updated: 'Konfiguration updated',
        deleted: 'Aufnahme Konfiguration deleted',
        confirm_delete: 'Löschen dieses Aufnahme Konfiguration?',
        select_types: 'Auswählen bei least one request type',
        select_org: 'Auswählen ein target Organisation',
        routing_mode: 'Routingmodus',
        single_org: 'Einzelne Organisation',
        org_group_mode: 'Organisationsgruppe',
        org_group: 'Organisationsgruppe',
        select_group: 'Organisationsgruppe auswählen...',
        group_root_hint: 'Wird automatisch auf die Stammorganisation der Gruppe gesetzt, wenn eine Gruppe ausgewählt wird.',
        loaded_results: 'Intake configurations geladen, {count} Ergebnisse',
        form_loaded: 'Intake configuration-Formular geladen',
    },
    frequency: {
        weekly: 'Wöchentlich',
        monthly: 'Monatlich',
        quarterly: 'Vierteljährlich',
        semi_annually: 'Halbjährlich',
        annually: 'Jährlich',
        custom: 'Benutzerdefiniert',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Kalender',
            all_tasks: 'Alle Aufgaben',
            my_tasks: 'Meine Aufgaben',
            overdue: 'Überfällig',
            board: 'Vorstand',
            workflows: 'Arbeitsabläufe',
        },
        list: {
            title: 'Alle Aufgaben',
            my_title: 'Meine Aufgaben',
            overdue_title: 'Überfällige Aufgaben',
            new_task: 'Aufgabe erstellen',
            no_tasks: 'Keine Aufgaben gefunden.',
            empty_state: 'Keine Aufgaben gefunden. Aufgaben werden aus Modulworkflows wie Bewertungen, Vorfällen und Überprüfungskampagnen generiert.',
            total: 'Aufgaben gesamt',
            search_placeholder: 'Aufgaben suchen...',
            bulk_actions: 'Massenaktionen',
            change_status: 'Status ändern',
            change_priority: 'Priorität ändern',
            assign: 'Zuweisen',
            sr_my_tasks_count: '{count} Aufgaben Ihnen zugewiesen',
            sr_days_overdue: '{count} Tage überfällig',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Aufgabendetail',
            description: 'Beschreibung',
            workflow_progress: 'Workflow-Fortschritt',
            checklist: 'Checkliste',
            dependencies: 'Abhängigkeiten',
            comments: 'Kommentare',
            activity_timeline: 'Aktivitätszeitachse',
            assignees: 'Zugewiesene',
            add_assignee: 'Zugewiesenen hinzufügen',
            remove_assignee: 'Zugewiesenen entfernen',
            due_date: 'Fälligkeitsdatum',
            start_date: 'Startdatum',
            sla_status: 'SLA-Status',
            sla_on_track: 'Im Zeitplan',
            sla_at_risk: 'Gefährdet',
            sla_breached: 'Verletzt',
            tags: 'Schlagworte',
            source_entity: 'Quellentität',
            view_source: 'Quelle anzeigen',
            add_dependency: 'Abhängigkeit hinzufügen',
            remove_dependency: 'Abhängigkeit entfernen',
            write_comment: 'Kommentar schreiben...',
            post_comment: 'Senden',
            edit_comment: 'Bearbeiten',
            delete_comment: 'Löschen',
            add_checklist_item: 'Element hinzufügen',
            no_dependencies: 'Keine Abhängigkeiten',
            no_comments: 'Noch keine Kommentare',
            blocks: 'Blockiert',
            blocked_by: 'Blockiert durch',
            related: 'Verwandt',
            working_days_remain: 'Arbeitstage verbleibend',
            created_by: 'Erstellt von',
            completed_at: 'Abgeschlossen am',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Keine Zugewiesenen',
            add_assignee_placeholder: 'Benutzer-ID eingeben...',
            edited: 'bearbeitet',
            timeline: 'Aktivitätszeitachse',
            no_timeline: 'Noch keine Aktivität',
            timeline_reason: 'Reason',
            no_workflow: 'Kein Workflow zugewiesen',
            add_dependency_placeholder: 'Aufgaben-ID eingeben...',
            sr_status_updated: 'Status aktualisiert auf {status}',
            sr_priority_updated: 'Priorität aktualisiert auf {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Als Workflow ausführen',
            convert_modal_title: 'Aufgabe in Workflow-Ausführung umwandeln',
            convert_workflow_label: 'Arbeitsablauf',
            convert_workflow_placeholder: 'Wählen Sie einen Workflow aus …',
            convert_attachments_label: 'Anhänge',
            convert_attachments_empty: 'Keine Anhänge zu dieser Aufgabe.',
            convert_persona_label: 'Persona-Überschreibung (optional)',
            convert_submit: 'Workflow-Lauf starten',
            convert_starting: 'Workflow-Ausführung wird gestartet…',
            convert_started: 'Workflow-Lauf gestartet – Eröffnungslauf…',
            convert_disabled_in_flight: 'Ein von dieser Aufgabe ausgeführter Workflow wird bereits ausgeführt.',
            linked_workflow_chip: 'Von {workflow_title}',
            linked_chat_chip: 'Von AI Chat',
            linked_more: '+{count} mehr',
        },
        calendar: {
            title: 'Aufgabenkalender',
            page_title: 'Kalender',
            create_task: 'Aufgabe erstellen',
            month: 'Monat',
            week: 'Woche',
            day: 'Tag',
            today: 'Heute',
            more_tasks: '+{count} mehr',
            no_tasks_day: 'Keine Aufgaben an diesem Tag',
            all_day: 'Ganztägig',
            quick_create_tooltip: 'Aufgabe für diesen Tag erstellen',
            view_changed: 'Kalenderansicht geändert auf {view}',
            tasks_in_view: '{count} Aufgaben in Ansicht',
            task_in_view: '{count} Aufgabe im Blick',
            filter_my_tasks: 'Meine Aufgaben',
            filter_overdue: 'Überfällig',
            filter_high_priority: 'Hohe Priorität',
            filter_all: 'Alle',
        },
        filters: {
            assignee: 'Zugewiesener',
            me: 'Ich',
            role: 'Rolle',
            status: 'Status',
            priority: 'Priorität',
            task_type: 'Aufgabentyp',
            source_module: 'Quellmodul',
            organization: 'Organisation',
            sla_breached_only: 'Nur SLA-verletzt anzeigen',
            clear_filters: 'Filter löschen',
            apply_filters: 'Anwenden',
            source_label: 'Quelle',
            source_manual: 'Handbuch',
            source_module_kind: 'Modulgeneriert',
            source_chat: 'Von AI Chat',
            source_workflow_run: 'Von AI Workflow-Ausführung',
        },
        status: {
            pending: 'Ausstehend',
            in_progress: 'In Bearbeitung',
            blocked: 'Blockiert',
            completed: 'Abgeschlossen',
            overdue: 'Überfällig',
            escalated: 'Eskaliert',
            cancelled: 'Storniert',
        },
        priority: {
            critical: 'Kritisch',
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Niedrig',
        },
        task_types: {
            manual_task: 'Manuelle Aufgabe',
            dsr_case: 'DSR-Fall',
            dsr_review: 'DSR-Prüfung',
            dsr_escalation: 'DSR-Eskalation',
            dpia_review: 'DSFA-Prüfung',
            dpia_approval: 'DSFA-Genehmigung',
            transfer_review: 'Transferprüfung',
            incident_task: 'Vorfallsaufgabe',
            incident_notification: 'Vorfallsbenachrichtigung',
            playbook_step: 'Playbook-Schritt',
            regulator_deadline: 'Behördenfrist',
            monitoring_task: 'Überwachungsaufgabe',
            kpi_alert: 'KPI-Alarm',
            vendor_review: 'Lieferantenprüfung',
            vendor_questionnaire: 'Lieferantenfragebogen',
            governance_review: 'Steuerungsprüfung',
            drift_alert: 'Abweichungsalarm',
            governance_approval: 'Steuerungsgenehmigung',
            deletion_task: 'Löschaufgabe',
            deletion_verification: 'Löschverifizierung',
            access_review: 'Zugriffsprüfung',
            sod_alert: 'SoD-Alarm',
            consent_workflow: 'Einwilligungs-Workflow',
            consent_alert: 'Einwilligungsalarm',
            property_review: 'Eigenschaftsprüfung',
            inconsistency_alert: 'Inkonsistenzalarm',
            form_review: 'Formularprüfung',
            dossier_deadline: 'Dossierfrist',
            submission_task: 'Einreichungsaufgabe',
            translation_review: 'Übersetzungsprüfung',
            governance_task: 'Steuerungsaufgabe',
            reporting_deadline: 'Berichtsfrist',
            scanner_alert: 'Scanner-Alarm',
            classification_review: 'Klassifizierungsprüfung',
            lifecycle_task: 'Lebenszyklusaufgabe',
            discovery_review: 'Entdeckungsprüfung',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Schulungszuweisung',
        task_type_groups: {
            training: 'Schulung',
            manual: 'Manuell',
            dsr: 'DSR / Betroffenenrechte',
            assessments: 'Bewertungen',
            incidents: 'Vorfälle',
            incident_ops: 'Vorfallsbetrieb',
            monitoring: 'Überwachung',
            vendor_risk: 'Lieferantenrisiko',
            governance: 'Steuerung',
            deletion: 'Löschung',
            access_review: 'Zugriffsprüfung',
            consent: 'Einwilligung',
            consent_properties: 'Einwilligungseigenschaften',
            forms: 'Formulare',
            localization: 'Lokalisierung',
            program_governance: 'Programmsteuerung',
            privacy_scanner: 'Datenschutz-Scanner',
            classification: 'Klassifizierung',
            discovery: 'Entdeckung',
            forseti: 'Forseti',
        },
        create: {
            title: 'Aufgabe erstellen',
            task_title: 'Titel',
            task_description: 'Beschreibung',
            task_type: 'Aufgabentyp',
            task_type_label: 'Aufgabentyp',
            task_type_placeholder: 'Aufgabentyp wählen',
            priority: 'Priorität',
            assignees: 'Zugewiesene',
            assignee_search_placeholder: 'Benutzer suchen...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: '{name} entfernen',
            due_date: 'Fälligkeitsdatum',
            start_date: 'Startdatum',
            checklist: 'Checkliste',
            tags: 'Schlagworte',
            add_checklist_item: 'Checklistenpunkt hinzufügen',
            validation: {
                title_required: 'Titel ist erforderlich',
                title_max_length: 'Titel darf maximal 500 Zeichen haben',
                description_max_length: 'Beschreibung darf maximal 5000 Zeichen haben',
                due_date_before_start: 'Fälligkeitsdatum muss nach dem Startdatum liegen',
                max_reminders: 'Maximal 5 Erinnerungen pro Aufgabe',
            },
            reminders: {
                title: 'Erinnerungen',
                add: 'Erinnerung hinzufügen',
                at_due_date: 'Am Fälligkeitsdatum',
                minutes_before: '{count} Minuten zuvor',
                hours_before: '{count} Stunden vorher',
                days_before: '{count} Tage zuvor',
                weeks_before: '{count} Wochen vorher',
                custom: 'Benutzerdefiniert',
                channel_platform: 'Plattform',
                channel_email: 'E-Mail',
                channel_both: 'Beides',
                offset_label: 'Erinnerung {n}: Zeitpunkt',
                channel_label: 'Erinnerung {n}: Kanal',
                remove_label: 'Erinnerung {n} entfernen',
            },
            workflow: {
                label: 'Arbeitsablauf',
                none: 'Keine (manuelle Statusverwaltung)',
                steps_count: '{count} Schritte',
                preview_label: 'Workflow-Schritte',
            },
        },
        errors: {
            fetch_failed: 'Aufgaben konnten nicht geladen werden.',
            save_failed: 'Aufgabe konnte nicht gespeichert werden.',
            delete_failed: 'Aufgabe konnte nicht gelöscht werden.',
            status_change_failed: 'Aufgabenstatus konnte nicht geändert werden.',
            assign_failed: 'Aufgabe konnte nicht zugewiesen werden.',
            comment_failed: 'Kommentar konnte nicht gespeichert werden.',
            checklist_failed: 'Fehler beim Aktualisieren der Checkliste.',
            dependency_failed: 'Fehler beim Aktualisieren der Abhängigkeit.',
            workflow_failed: 'Workflow konnte nicht gespeichert werden.',
            workflow_activate_failed: 'Workflow konnte nicht aktiviert werden.',
            workflow_delete_failed: 'Workflow konnte nicht gelöscht werden.',
            template_install_failed: 'Vorlage konnte nicht installiert werden.',
            search_failed: 'Aufgabensuche fehlgeschlagen.',
            convert_failed: 'Der Workflow-Lauf konnte nicht gestartet werden.',
            convert_quota_exhausted: 'Token-Bucket niedrig. Füllen Sie vor dem erneuten Ausführen auf.',
        },
        dependency_types: {
            blocks: 'Blockiert',
            blocked_by: 'Blockiert durch',
            related: 'Verwandt',
        },
        workflow: {
            current_step: 'Aktueller Schritt',
            transition: 'Übergang',
            history: 'Schritthistorie',
            no_workflow: 'Kein Workflow zugewiesen',
            detail_title: 'Workflow-Details',
            list_title: 'Workflow-Definitionen',
            create_workflow: 'Workflow erstellen',
            browse_templates: 'Vorlagen durchsuchen',
            template_catalog_title: 'Workflow-Vorlagenkatalog',
            name: 'Name',
            description: 'Beschreibung',
            task_types: 'Aufgabentypen',
            steps: 'Schritte',
            version: 'Version',
            created: 'Erstellt',
            actions: 'Aktionen',
            status_draft: 'Entwurf',
            status_active: 'Aktiv',
            status_archived: 'Archiviert',
            activate: 'Aktivieren',
            archive: 'Archivieren',
            save: 'Speichern',
            edit: 'Bearbeiten',
            delete: 'Löschen',
            clone: 'Klonen',
            install: 'Installieren',
            installed: 'Installiert',
            system: 'System',
            general_settings: 'Allgemeine Einstellungen',
            default_assignment: 'Standardzuweisung',
            default_sla: 'Standard-SLA',
            workflow_steps: 'Workflow-Schritte',
            add_step: 'Schritt hinzufügen',
            step_name: 'Schrittname',
            step_status: 'Statuswert',
            assignment_strategy: 'Zuweisungsstrategie',
            assignment_role: 'Zuweisungsrolle',
            sla_hours: 'SLA-Stunden',
            sla_calendar_type: 'Kalendertyp',
            working_days: 'Arbeitstage',
            calendar_days: 'Kalendertage',
            is_terminal: 'Terminalschritt',
            required_fields: 'Pflichtfelder',
            checklist_template: 'Checklistenvorlage',
            transitions: 'Übergänge',
            add_transition: 'Übergang hinzufügen',
            from_step: 'Von',
            to_step: 'Nach',
            permission: 'Berechtigung',
            sla_action: 'SLA-Aktion',
            notify: 'Benachrichtigen',
            escalation_rules: 'Eskalationsregeln',
            add_rule: 'Regel hinzufügen',
            trigger: 'Auslöser',
            threshold: 'Schwellenwert',
            action: 'Aktion',
            target_role: 'Zielrolle',
            trigger_sla_threshold: 'SLA-Schwellenwert',
            trigger_sla_breach: 'SLA-Verletzung',
            trigger_blocked_duration: 'Blockierte Dauer',
            trigger_manual: 'Manuell',
            action_notify: 'Benachrichtigen',
            action_escalate: 'Eskalationsstufe',
            action_reassign: 'Neu zuweisen',
            sla_start: 'Starten',
            sla_pause: 'Pause',
            sla_resume: 'Fortsetzen',
            sla_stop: 'Stopp',
            strategy_manual: 'Manuell',
            strategy_role_based: 'Rollenbasiert',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Am wenigsten belastet',
            strategy_creator: 'Ersteller',
            confirm_delete: 'Sind Sie sicher, dass Sie diesen Workflow löschen möchten?',
            confirm_archive: 'Sind Sie sicher, dass Sie diesen Workflow archivieren möchten?',
            cannot_delete_active: 'Aktive Workflows können nicht gelöscht werden. Erst archivieren.',
            validation_errors: 'Workflow-Validierungsfehler',
            step_count: '{count} Schritte',
            no_workflows: 'Keine Workflows gefunden.',
            no_templates: 'Keine Vorlagen verfügbar.',
            sr_templates_count: '{count} Vorlagen verfügbar',
            template_installed: 'Vorlage erfolgreich installiert.',
            activate_success: 'Workflow erfolgreich aktiviert.',
            archive_success: 'Workflow erfolgreich archiviert.',
            save_success: 'Workflow erfolgreich gespeichert.',
            delete_success: 'Workflow erfolgreich gelöscht.',
            applicable_task_types: 'Anwendbare Aufgabentypen',
            steps_column: 'Schritte',
            status_value_hint: 'Workflow-Statusschlüssel für Übergänge. Kleinbuchstaben, Zahlen und Unterstriche.',
            from_status: 'Von Status',
            to_status: 'Zu Status',
            required_permissions: 'Erforderliche Berechtigungen',
            transition_required_fields: 'Pflichtfelder (Übergang)',
            auto_assign: 'Automatisch zuweisen',
            auto_assign_role: 'Automatisch zuweisende Rolle',
            threshold_percent: 'Schwellenwert (%)',
            blocked_hours: 'Blockierte Stunden',
            notify_roles: 'Benachrichtigungsrollen',
            escalate_to_role: 'Eskalieren an Rolle',
            notification_template: 'Benachrichtigungsvorlage',
            action_escalate_tier: 'Eskalationsstufe',
            create_modal_title: 'Workflow erstellen',
            create_modal_description: 'Definieren Sie Schritte, Übergänge und Eskalationsregeln. Der Workflow wird als Entwurf erstellt.',
            create_modal_footer_note: 'Als Entwurf erstellt. Aktivieren Sie ihn, sobald der Graph validiert wurde.',
            validation_name_required: 'Ein Workflow-Name ist erforderlich.',
            validation_task_types_required: 'Wählen Sie mindestens einen anwendbaren Aufgabentyp aus.',
            validation_min_one_step: 'Fügen Sie mindestens einen Schritt hinzu.',
            validation_min_one_transition: 'Fügen Sie mindestens einen Übergang hinzu.',
            validation_step_name_required: 'Ein Schrittname ist erforderlich.',
            validation_status_value_required: 'Ein Statuswert ist erforderlich.',
            validation_status_value_duplicate: 'Der Statuswert muss über alle Schritte hinweg eindeutig sein.',
            validation_transition_endpoints_required: 'Wählen Sie sowohl einen Von- als auch einen Zu-Status aus.',
            validation_transition_unknown_status: 'Dieser Übergang verweist auf einen Status, den kein Schritt definiert.',
            validation_sla_positive: 'Die SLA-Stunden müssen größer als null sein. Leer lassen für kein SLA.',
            validation_threshold_percent_range: 'Der Schwellenwert muss zwischen 0 und 100 liegen.',
            validation_blocked_hours_positive: 'Die blockierten Stunden müssen größer als null sein.',
            warning_no_terminal_step: 'Kein Endschritt. Die Aktivierung schlägt fehl, bis ein Schritt als Endschritt markiert ist.',
            warning_step_no_outgoing: 'Nicht-terminale Schritte ohne ausgehende Übergänge: {statuses}',
            warning_unreachable_steps: 'Vom ersten Schritt aus nicht erreichbare Schritte: {statuses}',
            orphaned_transitions_warning: '{count} Übergänge verweisen auf einen gelöschten Schritt.',
            remove_orphaned_transitions: 'Verwaiste Übergänge entfernen',
            transitions_renamed_announcement: '{count} Übergänge auf den neuen Statuswert aktualisiert.',
            add_checklist_item: 'Checklistenpunkt hinzufügen',
            checklist_item_label: 'Checklistenpunkt',
        },
        columns: {
            priority: 'Priorität',
            title: 'Titel',
            status: 'Status',
            type: 'Typ',
            assignees: 'Zugewiesene(r)',
            due_date: 'Fälligkeitsdatum',
            source: 'Quelle',
            actions: 'Aktionen',
        },
        groups: {
            overdue: 'Überfällig',
            due_today: 'Heute fällig',
            due_this_week: 'Diese Woche fällig',
            due_later: 'Später fällig',
            no_due_date: 'Kein Fälligkeitsdatum',
            closed: 'Abgeschlossen oder abgebrochen',
        },
        board: {
            title: 'Aufgaben-Board',
            drag_hint: 'Aufgaben zwischen Spalten ziehen, um den Status zu ändern',
            empty_column: 'Keine Aufgaben',
            keyboard_hint: 'Tastatur: Pfeiltasten zum Navigieren, Leertaste zum Aufnehmen, Links/Rechts zur Spaltenauswahl, Enter zum Ablegen, Escape zum Abbrechen.',
            sr_moving: '{title} wird verschoben. Über {column}. Enter zum Ablegen, Escape zum Abbrechen.',
            sr_tasks_shown: '{count} Aufgaben angezeigt',
            sr_picked_up: '{title} aufgenommen',
            sr_returned: '{title} zurückgegeben an {column}',
            sr_dropped: '{title} in {column} abgelegt, Position {position} von {total}',
            sr_move_cancelled: 'Verschiebung abgebrochen',
            sr_over_column: 'Über {column}',
            sr_moved_to: '{title} verschoben nach {column}',
            invalid_transition: 'Aufgabe kann nicht von {from} nach {to} verschoben werden.',
            terminal_status: 'Diese Aufgabe hat einen Endstatus und kann nicht verschoben werden.',
            invalid_drop_target: 'Ungueltiges Ablageziel',
        },
        team: {
            title: 'Teamauslastung',
            assignee: 'Zugewiesener',
            active: 'Aktiv',
            overdue: 'Überfällig',
            blocked: 'Blockiert',
            completed_7d: 'Abgeschlossen (7 Tage)',
            load: 'Auslastung',
            load_high: 'Hoch',
            load_medium: 'Mittel',
            load_normal: 'Normal',
        },
        graph: {
            title: 'Abhängigkeitsdiagramm',
            list_view: 'Listenansicht',
            graph_view: 'Diagrammansicht',
            zoom_in: 'Vergrößern',
            zoom_out: 'Herauszoomen',
            reset_zoom: 'Zurücksetzen',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Keine Abhängigkeiten zur Visualisierung.',
        },
        add_dependency_modal: {
            title: 'Abhängigkeit hinzufügen',
            search_placeholder: 'Suchen Sie Aufgaben nach Titel oder ID...',
            no_results: 'Keine Aufgaben gefunden.',
            this_blocks: 'Diese Aufgabe blockiert ausgewählte',
            this_blocked_by: 'Diese Aufgabe ist durch Ausgewählte blockiert',
            related: 'Verwandt',
            max_dependencies: 'Maximal 10 Abhängigkeiten pro Aufgabe.',
            circular_error: 'Kann nicht hinzugefügt werden: würde eine zirkuläre Abhängigkeit erzeugen.',
        },
        conflict_detection: {
            title: 'Konflikterkennung',
            unresolved: 'ungelöst',
            no_conflicts: 'Keine Konflikte erkannt',
            section: 'Abschnitt',
            field: 'Feld',
            dept_a: 'Abteilung A',
            value_a: 'Wert A',
            dept_b: 'Abteilung B',
            value_b: 'Wert B',
            severity: 'Schweregrad',
            status: 'Status',
            resolved: 'Gelöst',
            open: 'Offen',
            resolve: 'Lösen',
            load_failed: 'Fehler beim load conflicts',
            resolve_success: 'Konflikt erfolgreich gelöst',
            resolve_failed: 'Fehler beim resolve conflict',
            severity_critical: 'Kritisch',
            severity_warning: 'Warnung',
            severity_info: 'Infos',
        },
        consolidation: {
            title: 'Konsolidierungsfortschritt',
            add_child: 'Unteraufgabe hinzufügen',
            child_task_id: 'Unteraufgabe-ID',
            department_id: 'Abteilungs-ID',
            link: 'Verknüpfen',
            overall_completion: 'Gesamtfertigstellung',
            departments_complete: 'Abteilungen abgeschlossen',
            overdue: 'Überfällig',
            ready: 'Bereit',
            not_ready: 'Nicht bereit',
            master_status: 'Hauptstatus',
            department_breakdown: 'Abteilungsaufschlüsselung',
            department: 'Abteilung',
            status: 'Status',
            completion: 'Fertigstellung',
            due_date: 'Fälligkeitsdatum',
            overdue_flag: 'Überfällig',
            child_tasks: 'Unteraufgaben',
            task: 'Aufgabe',
            load_failed: 'Fehler beim load consolidation data',
            child_linked: 'Unteraufgabe erfolgreich verknüpft',
            link_failed: 'Fehler beim link child task',
            confirm_unlink: 'Möchten Sie die Verknüpfung dieser Unteraufgabe wirklich aufheben?',
            child_unlinked: 'Unteraufgabe entkoppelt',
            unlink_failed: 'Entkopplung der Unteraufgabe fehlgeschlagen',
            unlink: 'Entkoppeln',
            status_completed: 'Abgeschlossen',
            status_in_progress: 'In Bearbeitung',
            status_pending: 'Ausstehend',
            select_child_task: 'Unteraufgabe auswählen',
            select_department: 'Abteilung auswählen',
        },
        section_assignments: {
            title: 'Abschnittszuweisungen',
            assign_section: 'Abschnitt zuweisen',
            section_ref: 'Abschnittsreferenz',
            section_label: 'Abschnittsbezeichnung',
            department_id: 'Abteilungs-ID',
            department: 'Abteilung',
            assignee_id: 'Zugewiesene-ID',
            assignee: 'Bevollmächtigter',
            mandatory: 'Pflichtfeld',
            assign: 'Zuweisen',
            no_assignments: 'Keine Abschnittszuweisungen',
            status: 'Status',
            completion: 'Fertigstellung',
            mark_complete: 'Abschließen',
            load_failed: 'Fehler beim load section assignments',
            assign_success: 'Abschnitt erfolgreich zugewiesen',
            assign_failed: 'Fehler beim assign section',
            status_updated: 'Status aktualisiert',
            status_update_failed: 'Fehler beim Aktualisieren des Status',
            section_ref_placeholder: 'z.B. 3.2',
            status_completed: 'Abgeschlossen',
            status_in_progress: 'In Bearbeitung',
            status_pending: 'Ausstehend',
            select_department: 'Abteilung auswählen',
            select_assignee: 'Wählen Sie den Beauftragten aus',
        },
        overdue_columns: {
            days_overdue: 'Tage ueberfaellig',
        },
        regulatory_info: {
            deadline_context: 'Beim Erstellen von DPIA-Aufgaben sollten Faelligkeitsdaten den PDPL-Einreichungsfristen entsprechen: Abteilungseingabe = 10 Arbeitstage. Cybersicherheits- und Telekommunikationsfristen gelten ebenfalls.',
            overdue_deadline_context: 'Regulatorische Fristen aus Paketdaten: PDPL Abteilungseingabe = 10 Arbeitstage, Ergaenzungsantwort = 15 Arbeitstage, Wiedereinreichung = 10 Arbeitstage. SLA-verletzte Aufgaben werden visuell hervorgehoben.',
            section_assignment_context: 'DPIA Multi-Abteilungs-Workflow (PDPL-P1): DPO weist Dossierabschnitte Abteilungen zu. Abteilungseingabefrist: 10 Arbeitstage.',
            template_catalog_context: 'Systemvorlagen decken rahmenspezifische Prozesse ab: PDPL DPIA, Datenschutzgesetz-Attestierung, Cybersicherheits-Vorfallreaktion, Telekom-Behoerdenanfragen und KI-Registrierung. Systemvorlagen sind nicht bearbeitbar.',
            workflow_instantiation_context: 'Installierte Vorlagen koennen zu aktiven Workflows instanziiert werden. Aufgaben werden automatisch mit berechneten Faelligkeitsdaten erstellt.',
        },
        overdue: {
            days_overdue: 'Tage überfällig',
        },
    },
    notifications: {
        bell: {
            title: 'Benachrichtigungen',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Benachrichtigungen',
            mark_all_read: 'Alle als gelesen markieren',
            no_notifications: 'Keine Benachrichtigungen',
            filter: {
                all: 'All',
                unread: 'Ungelesen',
                tasks: 'Aufgaben',
                approvals: 'Zulassungen',
                alerts: 'Warnungen',
            },
        },
        preferences: {
            title: 'Benachrichtigungseinstellungen',
            description: 'Konfigurieren Sie, wie Sie Benachrichtigungen erhalten',
            category: {
                label: 'Kategorie',
                task_assigned: 'Aufgabe zugewiesen',
                task_sla_approaching: 'SLA Nähert sich',
                task_overdue: 'Aufgabe überfällig',
                task_completed: 'Aufgabe abgeschlossen',
                task_escalated: 'Aufgabe eskaliert',
                workflow_transition: 'Workflow-Übergang',
                approval_requested: 'Genehmigung beantragt',
                comment_added: 'Kommentar hinzugefügt',
                weekly_digest: 'Wöchentliche Zusammenfassung',
                sla_breach_report: 'SLA Verstoßbericht',
                system_alert: 'Systemwarnung',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plattform',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Ruhige Stunden',
                start: 'Starten',
                end: 'End',
                timezone: 'Zeitzone',
            },
            digest: {
                title: 'Häufigkeit der Verdauung',
                daily: 'Täglich',
                weekly: 'Wöchentlich',
                none: 'Keine',
            },
            watched: {
                title: 'Beobachtete Entitäten',
                empty: 'Keine beobachteten Entitäten',
            },
            save: 'Einstellungen speichern',
            saving: 'Sparen...',
            saved_success: 'Einstellungen erfolgreich gespeichert',
            load_error: 'Gespeicherte Einstellungen konnten nicht geladen werden. Standardeinstellungen werden angezeigt – Änderungen werden gespeichert, wenn Sie auf Speichern klicken.',
            enabled: 'Aktiviert',
            disabled: 'Deaktiviert',
            unwatch: 'Unwatch',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Benachrichtigungs-Administrationswarteschlange',
            description: 'E-Mail-Zustellstatus überwachen und fehlgeschlagene Nachrichten erneut senden',
            load_error: 'E-Mail-Warteschlangendaten konnten nicht geladen werden',
            retry_error: 'E-Mail konnte nicht erneut gesendet werden',
            filter_status: 'Nach Status filtern',
            all_statuses: 'Alle',
            refresh: 'Aktualisieren',
            status_queued: 'In Warteschlange',
            status_sent: 'Gesendet',
            status_failed: 'Fehlgeschlagen',
            status_bounced: 'Zurückgewiesen',
            status_sending: 'Sending',
            col_recipient: 'Empfänger',
            col_category: 'Kategorie',
            col_template: 'Vorlage',
            col_status: 'Status',
            col_retries: 'Wiederholungen',
            col_created: 'Erstellt',
            col_sent: 'Gesendet am',
            col_actions: 'Aktionen',
            no_items: 'Keine E-Mail-Warteschlangeneinträge gefunden',
            retry: 'Erneut versuchen',
            retrying: 'Wird erneut versucht...',
            prev: 'Zurück',
            next: 'Weiter',
            access_denied_title: 'Administratorzugang erforderlich',
            access_denied: 'Diese Seite ist nur für Administratoren verfügbar. Bitte kontaktieren Sie Ihren Administrator, wenn Sie Zugang benötigen.',
            announce_queue_loaded: 'E-Mail-Warteschlange geladen, {count} Elemente',
            announce_processed: 'Verarbeitete {count} E-Mail(s)',
            process_now: 'Jetzt verarbeiten',
            processing_ellipsis: 'Verarbeitung…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'UI-Assistent',
            activate: 'Aktivieren',
            deactivate: 'Deaktivieren',
            connected: 'Verbunden',
            disconnected: 'Nicht verbunden',
            disconnected_hint: 'Nicht verbunden – führen Sie „npm run Relay“ aus, um eine Verbindung zur Claude-CLI herzustellen',
            open_panel: 'Öffnen Sie das UI Assist-Panel',
            history: 'Geschichte',
            settings: 'Einstellungen',
        },
        prompt: {
            placeholder: 'Beschreiben Sie die Änderung, die Sie vornehmen möchten ...',
            send: 'Senden',
            cancel: 'Abbrechen',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Verarbeitung…',
            complete: 'Komplett',
            error: 'Fehler',
            abort: 'Abbruch',
            abort_confirm: 'Sind Sie sicher, dass Sie den Vorgang abbrechen möchten?',
            aborted_hint: 'Sitzung wurde unterbrochen — Sie können von hier aus fortfahren.',
            undo: 'Rückgängig machen',
            redo: 'Wiederholen',
            retry: 'Versuchen Sie es noch einmal',
            dismiss: 'Entlassen',
        },
        label: {
            component: 'Komponente',
            element: 'Element',
            file: 'Datei',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Noch keine Nachrichten. Wählen Sie Elemente aus und senden Sie eine Aufforderung, um loszulegen.',
        },
        panel: {
            tab_conversation: 'Chatten',
            tab_tasks: 'Aufgaben',
            tab_sprint: 'Sprint',
            tab_sessions: 'Sitzungen',
            tab_lobby: 'Lobby',
            follow_up: 'Follow-up',
            followup_placeholder: 'Stellen Sie eine Folgefrage ...',
            interrupt_placeholder: 'Eingabe zum Unterbrechen und Umleiten…',
            send_interrupt: 'Senden & Unterbrechen',
            will_interrupt: 'Bricht die aktuelle Antwort ab und sendet stattdessen diese',
            expand: 'Auf Vollbild erweitern',
            collapse: 'Auf Seitenleiste verkleinern',
            no_sessions: 'Keine vorherigen Sitzungen. Wählen Sie Elemente aus und senden Sie eine Anweisung.',
            session_active: 'Aktiv',
            session_ended: 'Beendet',
            session_messages: '{count} Nachrichten',
            switch_session: 'Zu dieser Sitzung wechseln',
            load_session: 'Sitzungsverlauf laden',
            session_restored: 'Sitzung aus dem Verlauf wiederhergestellt',
            archive_session: 'Archivsitzung',
            archive_confirm: 'Diese Sitzung archivieren? Die Sitzungsdatei wird gelöscht.',
            new_session: 'Neue Sitzung',
            new_session_desc: 'Verwenden Sie „Aktivieren“, um ein UI-Element auszuwählen und eine neue Sitzung zu starten',
            start_new_session: 'Neue Sitzung starten',
            no_active_session_chat: 'Starten Sie eine neue Sitzung oder wählen Sie mit „Aktivieren“ ein UI-Element auf der Seite aus, um zu beginnen.',
            no_active_session_tasks: 'Starten Sie eine neue Sitzung oder wählen Sie eine bestehende Sitzung aus, um Aufgaben zu erstellen.',
            no_active_session_sprint: 'Starten Sie eine neue Sitzung oder wählen Sie eine bestehende Sitzung aus, um einen Sprint zu planen.',
            previous_sessions: 'Frühere Sitzungen',
        },
        tasks: {
            none_found: 'In der Antwort wurden keine Aufgaben gefunden. Bitten Sie Claude, Aufgaben vorzuschlagen.',
            selected: 'selected',
            create_selected: 'Erstellen Sie Aufgaben',
            execute_tasks: 'Aufgaben ausführen',
            creating: 'Erstellen…',
            created: 'Aufgaben erstellt',
            create_error: 'Aufgabe konnte nicht erstellt werden',
            plan_sprint: 'Sprint planen',
            priority_critical_desc: 'Blocker — sofort beheben',
            priority_high_desc: 'Wichtig — in diesem Sprint lösen',
            priority_medium_desc: 'Normal — für kommenden Sprint planen',
            priority_low_desc: 'Optional — Backlog-Kandidat',
            add_custom: 'Aufgabe hinzufügen',
            add_title_placeholder: 'Aufgabentitel…',
            add_desc_placeholder: 'Beschreibung (optional)',
            add_save: 'Hinzufügen',
            add_cancel: 'Abbrechen',
        },
        sprint: {
            title: 'Sprint-Planer',
            assigned: 'assigned',
            new_placeholder: 'Sprintname (z. B. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Rückstand',
            move_to: 'Umzug nach…',
            empty: 'Erstellen Sie eine Sprintgruppe und weisen Sie ihr Aufgaben zu.',
            create_all: 'Alle Aufgaben erstellen',
            remove: 'Entfernen',
            drop_here: 'Weisen Sie Aufgaben aus dem Backlog unten zu',
            add_task: 'Aufgabe aus Backlog hinzufügen…',
        },
        feature_disabled: 'UI Assist ist nicht aktiviert',
        permission_denied: 'Sie sind nicht berechtigt, UI Assist zu verwenden',
    },
    license: {
        title: 'Lizenz & Paket',
        current_license: 'Aktuelle Lizenz',
        license_key: 'Lizenzschlüssel',
        activate: 'Lizenz aktivieren',
        deactivate: 'Lizenz deaktivieren',
        validate_now: 'Jetzt validieren',
        status: 'Lizenzstatus',
        active: 'Aktiv',
        expired: 'Abgelaufen',
        suspended: 'Suspendiert',
        invalid: 'Ungültig',
        pending: 'Ausstehend',
        no_license: 'Keine Lizenz',
        expires_at: 'Läuft ab',
        last_validated: 'Zuletzt validiert',
        domain: 'Lizenzierte Domain',
        activation_success: 'Lizenz erfolgreich aktiviert.',
        activation_failed: 'Die Lizenzaktivierung ist fehlgeschlagen.',
        domain_mismatch: 'Die Lizenzdomäne stimmt nicht mit der aktuellen Domäne überein.',
        deactivation_success: 'Lizenz deaktiviert.',
        validation_success: 'Lizenz erfolgreich validiert.',
        validation_failed: 'Die Lizenzvalidierung ist fehlgeschlagen.',
        enter_key: 'Geben Sie Ihren Lizenzschlüssel ein',
        confirm_deactivate: 'Sind Sie sicher, dass Sie diese Lizenz deaktivieren möchten? Modulbeschränkungen gelten basierend auf der Standardstufe.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Paket',
        module_column: 'Modul',
        current_tier: 'Aktuelles Paket',
        light: 'Licht',
        standard: 'Standard',
        enterprise_unlimited: 'Enterprise Unlimited',
        change_package: 'Paket ändern',
        upgrade: 'Upgrade',
        downgrade: 'Downgrade',
        target_tier: 'Zielpaket',
        new_license_key: 'Lizenzschlüssel für Zielpaket',
        confirm_change: 'Bestätigen Sie die Paketänderung',
        change_success: 'Paket erfolgreich geändert.',
        change_failed: 'Paketänderung fehlgeschlagen.',
        modules_included: 'Module enthalten',
        modules_restricted: 'Im aktuellen Paket nicht verfügbar',
        upgrade_to_enable: 'Führen Sie ein Upgrade durch, um dieses Modul zu aktivieren.',
        module_availability: 'Modulverfügbarkeit',
        comparison: 'Paketvergleich',
        power_users: 'Power-User',
        unlimited: 'Unbegrenzt',
        impact_warning: 'Durch ein Downgrade werden die folgenden Module deaktiviert:',
        invalid_license: 'Der bereitgestellte Lizenzschlüssel ist für dieses Paket nicht gültig.',
        tier_mismatch: 'Der Lizenzschlüssel gilt für eine andere Paketstufe.',
    },
    board_report: {
        title: 'Vorstandsbericht zur Compliance',
        subtitle: 'Zusammenfassung der Compliance über alle Module',
        date_from: 'Von Datum',
        date_to: 'Bis Datum',
        language: 'Berichtssprache',
        generate: 'Bericht erstellen',
        generating: 'Bericht wird erstellt...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'PDF herunterladen',
        close: 'Bericht schließen',
        open_board_report: 'Vorstandsbericht erstellen',
        close_board_report: 'Vorstandsbericht schließen',
        report_period: 'Berichtszeitraum',
        generated_at: 'Erstellt am',
        generated_by: 'Erstellt von',
        organization: 'Organisation',
        section_rights_requests: 'Rechtsanfragen',
        section_vendors: 'Lieferanten',
        section_dpias: 'DSFAs',
        section_consent: 'Einwilligungsrichtlinien',
        section_data_mapping: 'Datenzuordnung',
        section_incidents: 'Vorfälle',
        section_classification: 'Datenklassifizierung',
        section_governance: 'Regierungsführung',
        open: 'Offen',
        closed: 'Geschlossen',
        sla_compliance: 'SLA-Einhaltung',
        total: 'Gesamt',
        expired_dpa: 'Abgelaufene DPA',
        high_risk: 'Hohes Risiko',
        completed: 'Abgeschlossen',
        overdue: 'Überfällig',
        policies_active: 'Aktive Richtlinien',
        coverage: 'Abdeckung',
        systems: 'Systeme',
        completeness: 'Vollständigkeit',
        avg_resolution_days: 'Durchschn. Lösungszeit (Tage)',
        labeled: 'Gekennzeichnet',
        unlabeled_systems: 'Ungekennzeichnete Systeme',
        compliance_score: 'Compliance-Bewertung',
        drift_events_open: 'Offene Abweichungen',
        no_data: 'Keine Daten für den ausgewählten Zeitraum verfügbar.',
        error_date_range: 'Bitte wählen Sie sowohl Start- als auch Enddatum.',
        table_category: 'Kategorie',
        table_metric: 'Kennzahl',
        table_value: 'Wert',
        showing_chart_view: 'Diagrammansicht wird angezeigt',
        showing_table_view: 'Datentabellenansicht wird angezeigt',
        error_generate: 'Vorstandsbericht konnte nicht erstellt werden.',
        playbook_evidence_title: 'Nachweise für Playbook-Ausführungen',
        playbook_evidence_description: 'Ausführungsaktivität pro Playbook und angehängte Nachweisdateien für den Berichtszeitraum.',
        playbook_name: 'Spielbuch',
        playbook_execution_count: 'Ausführungen',
        playbook_steps_total: 'Schritte',
        playbook_steps_required: 'Nachweis erforderlich',
        playbook_steps_attached: 'Nachweis angehängt',
        playbook_coverage_pct: 'Abdeckung',
        playbook_evidence_files: 'Nachweisdateien',
        playbook_no_evidence: 'Keine Playbook-Ausführungen in diesem Zeitraum.',
        playbook_download_file: 'Datei herunterladen',
    },
    framework_module_links: {
        title: 'Rahmenverknüpfung',
        modules: 'Verknüpfte Module',
        add_module: 'Modul hinzufügen',
        module_code: 'Modulcode',
        required: 'Erforderlich',
        notes: 'Notizen',
        link: 'Verknüpfen',
        no_modules: 'Keine Module verknüpft',
        personas: 'Verknüpfte Personas',
        add_persona: 'Persona hinzufügen',
        persona_id: 'Persona-ID',
        role_description: 'Rollenbeschreibung',
        no_personas: 'Keine Personas verknüpft',
    },
    framework_channels: {
        title: 'Einreichungskanäle & Fristen',
        channels: 'Einreichungskanäle',
        add_channel: 'Kanal hinzufügen',
        channel_type: 'Typ',
        name: 'Name',
        instructions: 'Anweisungen',
        active: 'Aktiv',
        no_channels: 'Keine Einreichungskanäle konfiguriert',
        portal: 'Portal',
        in_person: 'Persönlich',
        postal: 'Post',
        email: 'E-Mail',
        add: 'Hinzufügen',
        deadlines: 'Regulatorische Fristen',
        add_deadline: 'Frist hinzufügen',
        label: 'Bezeichnung',
        days: 'Tage',
        day_type: 'Tagestyp',
        calendar_days: 'Kalendertage',
        working_days: 'Arbeitstage',
        description: 'Beschreibung',
        no_deadlines: 'Keine Fristen konfiguriert',
    },
    regulatory_updates: {
        title: 'Regulatorische Aktualisierungen & Rahmen-Dashboard',
        coverage_dashboard: 'Rahmenabdeckungs-Dashboard',
        no_frameworks: 'Keine Rahmenwerke gefunden',
        framework: 'Rahmenwerk',
        status: 'Status',
        modules: 'Module',
        templates: 'Vorlagen',
        forms: 'Formulare',
        channels: 'Kanäle',
        deadlines: 'Fristen',
        pending: 'Ausstehende Aktualisierungen',
        updates: 'Regulatorische Aktualisierungen',
        create_update: 'Aktualisierung erstellen',
        framework_id: 'Rahmenwerk-ID',
        update_title: 'Titel',
        description: 'Beschreibung',
        effective_date: 'Gültigkeitsdatum',
        create: 'Erstellen',
        no_updates: 'Keine regulatorischen Aktualisierungen',
        impact: 'Auswirkung',
        assess: 'Bewerten',
    },
    vehicle_monitoring: enVehicleMonitoring,
    record_links: enRecordLinks,
    module_tips: deModuleTips,
    framework_rights: {
        be_informed: 'Recht auf Information',
        consent: 'Recht auf Einwilligung',
        refuse_consent: 'Recht auf Verweigerung der Einwilligung',
        withdraw_consent: 'Recht auf Widerruf der Einwilligung',
        access: 'Auskunftsrecht',
        rectification: 'Recht auf Berichtigung',
        data_provision: 'Recht auf Datenbereitstellung',
        deletion: 'Recht auf Löschung',
        restriction_of_processing: 'Recht auf Einschränkung der Verarbeitung',
        object_to_processing: 'Widerspruchsrecht',
        complaint: 'Recht auf Beschwerde',
        denunciation: 'Recht auf Anzeige',
        lawsuit: 'Recht auf Klageerhebung',
        claim_damages: 'Recht auf Schadensersatz',
        request_protection_measures: 'Recht auf Schutzmaßnahmen',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Richtlinie 2002/58/EG (ePrivacy-Richtlinie)',
            description: 'Übergeordnete Richtlinie für nationale ePrivacy-Umsetzungen. Deckt Cookies, Datenschutz bei elektronischer Kommunikation und Direktmarketing ab. Jeder Mitgliedsstaat muss es in nationales Recht umsetzen.',
        },
        EU_GDPR: {
            display_name: 'Datenschutz-Grundverordnung (EU) 2016/679',
            description: 'Die Datenschutz-Grundverordnung (GDPR) ist eine EU-Verordnung zum Datenschutz und zur Privatsphäre. Es gilt unmittelbar in allen EU-/EWR-Mitgliedsstaaten.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Richtlinie (EU) 2022/2555 (NIS2)',
            description: 'Übergeordnete Richtlinie für nationale NIS2-Umsetzungen. Deckt das Cybersicherheitsrisikomanagement, die Meldung von Vorfällen und die Sicherheit der Lieferkette für wesentliche und wichtige Unternehmen ab.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnamesisches AI Gesetz (134/2025/QH15) – gültig ab 1. März 2026',
            description: 'Vietnam AI Gesetz (Luật Trí tuệ nhân tạo 134/2025/QH15), das die Entwicklung, den Einsatz und die Nutzung von Systemen der künstlichen Intelligenz regelt. Legt Risikoklassifizierung, Transparenz- und Kennzeichnungspflichten, Anforderungen zur Meldung von Vorfällen, Verpflichtungen zur Folgenabschätzung und einen Mechanismus für kontrollierte Tests (Sandbox) für AI-Aktivitäten in Vietnam fest.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnamesisches Cybersicherheitsgesetz (Ära 24/2018)',
            description: 'Vietnams Gesetz zur Cybersicherheit (Gesetz Nr. 24/2018/QH14), das Anforderungen für Cybersicherheit, Datenlokalisierung und Vorfallmeldung festlegt. Aktiv bis zur Aufhebung durch Gesetz 116/2025/QH15 mit Wirkung zum 01.07.2026.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Dekret 53 – Umsetzung des Cybersicherheitsgesetzes',
            description: 'Dekret 53/2022/ND-CP zur Umsetzung des Cybersicherheitsgesetzes, das die Festlegung des Geltungsbereichs, Auslöser für die Datenlokalisierung, Anforderungen an die lokale Präsenz und Arbeitsabläufe bei der Zusammenarbeit mit Behörden umfasst.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Gesetz zur Cybersicherheit (116/2025/QH15)',
            description: 'Vietnams konsolidiertes Gesetz zur Cybersicherheit (Gesetz 116/2025/QH15), das das Gesetz 24/2018/QH14 ersetzt und am 1. Juli 2026 in Kraft tritt. Konsolidiert den Geltungsbereich des Dekrets 53/2022/NĐ-CP (siehe VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Vietnamesisches Datengesetz',
            description: 'Das vietnamesische Datengesetz (Gesetz 60/2024/QH15) legt umfassende Daten-Governance-Anforderungen fest, einschließlich Dateninventur, -klassifizierung, Sharing-Governance und grenzüberschreitender Datentransfer-Governance. Überschneidungen mit VN_PDPL_LAW_2025 (Aspekte personenbezogener Daten) und VN_CYBERSEC_LAW_2025 (Datenlokalisierung/-klassifizierung).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Dekret 165/2025/NĐ-CPĐ-CP (Umsetzung des Datengesetzes)',
            description: 'Dekret 165/2025/NĐ-CP zur Umsetzung des vietnamesischen Datengesetzes (Gesetz 60/2024/QH15). Bietet offizielle Formulare für die Folgenabschätzung der grenzüberschreitenden Datenübertragung, die jährliche Risikobewertungsberichterstattung und den Zugriff auf nationale Datenbanken. Definiert 11 offizielle Mẫu-Số-Formen.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Entwurf eines Dekrets über den Betrieb von Datenbörsen 2026',
            description: 'Entwurf eines Erlasses über den Betrieb von Datenaustauschplattformen (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Reguliert die Plattformklassifizierung, die Teilnehmerberechtigung, die Kontrolle der Auflistung von Datenprodukten und -diensten, Sandbox-Tests, den Transaktionsablauf, elektronische Vertragsabschlüsse, Bescheinigungen von Eigentumsrechten an Daten und die Bearbeitung von Beschwerden/Streitigkeiten. Derzeit in öffentlicher Konsultation; noch nicht verkündet. Zitiert das Gesetz über Daten 60/2024/QH15, das Gesetz über den Schutz personenbezogener Daten 91/2025/QH15, das Gesetz über Cybersicherheit 116/2025/QH15 und das Gesetz über elektronische Transaktionen 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Gesetz zum elektronischen Geschäftsverkehr (122/2025/QH15)',
            description: 'Vietnamesisches Gesetz über den elektronischen Handel (Luật Thương mại điện tử 122/2025/QH15), das ein einheitliches nationales System für den elektronischen Handel festlegt und den inländischen und grenzüberschreitenden Plattformbetrieb, Plattform-Governance und -Rechenschaftspflicht, Verbraucherschutz, Verkäufer- und Affiliate-Kontrollen, Beschwerdebearbeitung, Inhaltsmoderation und Durchsetzung der Löschung sowie die Vorbereitung von Verwaltungsverfahren für das staatliche E-Commerce-Managementsystem abdeckt.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Dekret 13/2023/NĐ-CP',
            description: 'Dekret 13/2023/ND-CP, das operative Instrument für PDPL Aufgaben, einschließlich Dossiereinreichung, Governance-Artefakte, grenzüberschreitende Mechanismen und MoPS-Verwaltungsverfahren.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Gesetz zum Schutz personenbezogener Daten (91/2025/QH15)',
            description: 'Das vietnamesische Gesetz zum Schutz personenbezogener Daten (Gesetz 91/2025/QH15) legt einen umfassenden Rahmen für den Schutz personenbezogener Daten fest und tritt am 1. Januar 2026 in Kraft. Deckt die Compliance-Themen A–J ab: rechtmäßige Verarbeitungsgrundlagen, Einwilligung zu sensiblen Daten, Rechte betroffener Personen (Zugriff/Berichtigung/Löschung/Einschränkung/Einspruch/Übertragbarkeit), Benachrichtigung von Verstößen an Behörden und betroffene Personen, DPIA für die Verarbeitung mit hohem Risiko, Steuerung der grenzüberschreitenden Übertragung, DPO Benennung, Aufzeichnungen über Verarbeitungstätigkeiten, vertragliche Pflichten des Auftragsverarbeiters. Behördliche Einreichungspflichten über MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Querverweise: VN_PDPL_DECREE_356_2025 (Durchführungsverordnung), VN_CYBERSEC_LAW_2025 (Cybersicherheitsüberschneidungen), VN_DATA_LAW (Datenverwaltung).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Dekret 356/2025/ND-CP (PDPL Umsetzung)',
            description: 'Dekret 356/2025/ND-CP zur Umsetzung des Gesetzes zum Schutz personenbezogener Daten (Gesetz 91/2025/QH15). Deckt die Einreichung von Dossiers, Governance-Artefakte, grenzüberschreitende Mechanismen und die Zertifizierung von Dienstleistern ab. Anhang Formularverzeichnis: Abschnitte I–VIII (Folgenabschätzung, grenzüberschreitende Übertragung, Zertifizierung). MoPS-Verwaltungsverfahren über VN_PDPL_ADMIN_PROCEDURES_778_2026 (Entscheidung 778/QĐ-BCA-A05). Zertifizierungsablauf: Antrag → Ausstellung → Änderungsantrag → Neuausstellung → Widerruf. Querverweise: VN_PDPL_LAW_2025 (übergeordnetes Recht), VN_CYBERSEC_LAW_2025 (Datenlokalisierung), VN_DATA_LAW (Überschneidungen bei der Datenverwaltung).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Verwaltungsverfahren (Entscheidung 778/QĐ-BCA-A05)',
            description: 'Entscheidung 778/QĐ-BCA-A05 (Februar 2026) – Verwaltungsverfahren zum Schutz personenbezogener Daten gemäß A05 (MPS). Definiert Verfahren zur Einreichung, Verarbeitung und Ausgabe von Dossiers, einschließlich DLCN-HC-Formularen und gesetzlich vorgeschriebenen Formularen.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Entwurf des Vollstreckungsbeschlusses 2026',
            description: 'Entwurf eines Dekrets zur Durchsetzung und Sanktionierung von Verstößen gegen das Gesetz zum Schutz personenbezogener Daten (Gesetz 91/2025/QH15). Definiert Strafrahmen, erschwerende/mildernde Faktoren und Durchsetzungsverfahren. Derzeit im Entwurfsstatus, bis zur Veröffentlichung.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnamesisches Telekommunikationsgesetz 2023',
            description: 'Vietnamesisches Telekommunikationsgesetz 2023 (Luật Viễn thông), ein Sektor-Overlay für Telekommunikations-/ISP-/Plattformunternehmen mit Verpflichtungen zur Vertraulichkeit, Offenlegungskontrolle und zur Bearbeitung behördlicher Anfragen. Querverweise: VN_CYBERSEC_LAW_2025 (Informationssystemsicherheit), VN_PDPL_LAW_2025 (Benutzerdatenverarbeitung), VN_DATA_LAW (Datenverwaltung).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Dekret 163/2024/NĐ-CPĐ-CP (Umsetzung des Telekommunikationsgesetzes)',
            description: 'Dekret 163/2024/NĐ-CP zur Umsetzung des Telekommunikationsgesetzes (Gesetz 24/2023/QH15). Bietet offizielle Formulare für die Meldung von Telekommunikationsdiensten, die Lizenzierung, die Registrierung und die Entwicklungspläne für Rechenzentren. Definiert 7 offizielle Mẫu-Số-Formen.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Beschwerden, Streitigkeiten und Risikomanagement',
            description: 'Plattformbetreiber müssen die Aufnahme von Beschwerden, einen Arbeitsablauf zur Streitbeilegung mit Beweissicherung, Risikoerkennung und Frühwarnkontrollen sowie eine regelmäßige Überprüfung der Datenschutz-, Cybersicherheits- und Zugriffsverwaltungsrisiken gewährleisten.',
        },
        vn_dp_draft_e: {
            title: 'Transaktionsablauf und Bescheinigung der Eigentumsrechte an Daten',
            description: 'Für Transaktionen sind eine Kontoregistrierung, eine Auflistungsgenehmigung, ein elektronischer Vertragsabschluss, eine Zahlungsbestätigung, ein Lieferprotokoll und gegebenenfalls die Ausstellung einer Bescheinigung über Eigentumsrechte an den Daten erforderlich. Der Transaktionsverlauf und die Rückverfolgbarkeit müssen in einem Transaktionsregister aufbewahrt werden.',
        },
        vn_dp_draft_d: {
            title: 'Sandbox- und Testumgebungskontrollen',
            description: 'Testumgebungen müssen isoliert, zugriffsbeschränkt, sicherheitsüberwacht und überprüft werden, bevor eine Ausgabe extrahiert wird. Es dürfen keine Quelldaten aus der Testumgebung extrahiert werden und der Abschluss/die Bereinigung von Testsitzungen muss aufgezeichnet werden.',
        },
        vn_dp_draft_c: {
            title: 'Steuerelemente für die Datenprodukt- und Serviceauflistung',
            description: 'Listings müssen durch rechtmäßige Quellen- und Eigentums-/Nutzungsnachweise, technische Validierungsaufzeichnungen, Qualitätsoffenlegungen sowie plattformseitige Listing-Überprüfungs- und Genehmigungsaufzeichnungen gestützt werden. Unzulässige Daten müssen vor der Auflistung gefiltert werden.',
        },
        vn_dp_draft_b: {
            title: 'Teilnehmerberechtigung und Onboarding',
            description: 'Plattformbetreiber müssen den Rechtsstatus, die Geschäftsfähigkeit und die Geschäftsvoraussetzungen für Verkäufer und Käufer überprüfen, Transaktions- und Zahlungskonten registrieren und ein Teilnehmerregister führen.',
        },
        vn_dp_draft_a: {
            title: 'Klassifizierung und Betriebsmodell der Datenplattform',
            description: 'Plattformbetreiber müssen eine Klassifizierung des Plattformtyps festlegen (nationale Datenplattform im Vergleich zu anderen Datenplattformen), die Berechtigung und Verantwortlichkeiten der Betreiber festlegen sowie Preise, Gebühren, Betriebsregeln und technische Governance dokumentieren.',
        },
        d165_r5: {
            title: 'Zugriff auf nationale Datenbankinformationen',
            description: 'Für Anträge auf Extraktion/Nutzung von Informationen aus der nationalen Datenbank für digitale Daten sind vorgeschriebene Formulare erforderlich (siehe Abschnitt 07a/07b).',
        },
        d165_r4: {
            title: 'Jährliche Berichterstattung über die Risikobewertung',
            description: 'Jährliche Risikobewertungsberichte müssen unter Verwendung der vorgeschriebenen Formulare eingereicht werden (siehe Abschnitt 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Kündigungsmitteilung für grenzüberschreitende Überweisungen',
            description: 'Die Beendigung des grenzüberschreitenden Transfers muss unter Verwendung der vorgeschriebenen Formulare (siehe Abschnitt 04a/04b) mitgeteilt werden.',
        },
        d165_r2: {
            title: 'Benachrichtigung über das Bewertungsergebnis',
            description: 'Die Behörde stellt Bescheide über die Beurteilungsergebnisse unter Verwendung des vorgeschriebenen Formulars aus (siehe Abschnitt 03).',
        },
        d165_r1: {
            title: 'Einreichung einer Folgenabschätzung für die grenzüberschreitende Datenübertragung',
            description: 'Organisationen müssen Dossiers zur Folgenabschätzung für grenzüberschreitende Datenübermittlungen/-verarbeitungen unter Verwendung vorgeschriebener Formulare einreichen (siehe Abschnitt 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Einwilligung für Cookies und ähnliche Technologien',
            description: 'Das Speichern von oder der Zugriff auf Informationen auf einem Endgerät ist nur mit vorheriger Einwilligung nach Aufklärung gestattet, es sei denn, es handelt sich um eine technisch notwendige Speicherung.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Vertraulichkeit der Kommunikation',
            description: 'Die Mitgliedstaaten gewährleisten die Vertraulichkeit der Kommunikation und der damit verbundenen Verkehrsdaten über ein öffentliches Kommunikationsnetz und öffentlich zugängliche elektronische Kommunikationsdienste.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Einwilligung zum Direktmarketing (Opt-in)',
            description: 'Die Nutzung elektronischer Kommunikationssysteme für Direktmarketing bedarf der vorherigen Zustimmung des Abonnenten.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Soft-Opt-In für Bestandskunden',
            description: 'Direktmarketing an Bestandskunden für ähnliche Produkte/Dienstleistungen ist ohne Zustimmung gestattet, wenn ein Opt-out vorgesehen ist.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Benachrichtigung bei Verstößen für Telekommunikationsanbieter',
            description: 'Anbieter öffentlich zugänglicher elektronischer Kommunikationsdienste müssen der zuständigen nationalen Behörde Verstöße gegen den Schutz personenbezogener Daten melden.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Widerspruchsrecht gegen Direktmarketing',
            description: 'Den Abonnenten wird die Möglichkeit eingeräumt, dem Erhalt von Direktmarketing-Nachrichten jederzeit und kostenfrei zu widersprechen.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Speicherung und Löschung von Verkehrsdaten',
            description: 'Verkehrsdaten von Teilnehmern und Nutzern müssen gelöscht oder anonymisiert werden, wenn sie für die Übertragung nicht mehr benötigt werden, mit Ausnahme von Abrechnungs- oder Zusammenschaltungszahlungen.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Bedingungen für die Verarbeitung von Standortdaten',
            description: 'Andere Standortdaten als Verkehrsdaten dürfen nur in anonymisierter Form oder mit Einwilligung des Nutzers im erforderlichen Umfang und für die erforderliche Dauer verarbeitet werden.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Rechtmäßigkeit der Verarbeitung – Einwilligung',
            description: 'Die Verarbeitung ist nur dann rechtmäßig, wenn die betroffene Person der Verarbeitung ihrer personenbezogenen Daten für einen oder mehrere bestimmte Zwecke zugestimmt hat.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Rechtmäßigkeit der Verarbeitung – Vertrag',
            description: 'Die Verarbeitung ist für die Erfüllung eines Vertrages, dessen Vertragspartei die betroffene Person ist, erforderlich.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Rechtmäßigkeit der Verarbeitung – gesetzliche Verpflichtung',
            description: 'Die Verarbeitung ist zur Erfüllung einer rechtlichen Verpflichtung erforderlich, der der Verantwortliche unterliegt.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Rechtmäßigkeit der Verarbeitung – lebenswichtige Interessen',
            description: 'Die Verarbeitung ist erforderlich, um lebenswichtige Interessen der betroffenen Person oder einer anderen natürlichen Person zu schützen.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Rechtmäßigkeit der Verarbeitung – öffentliches Interesse',
            description: 'Die Verarbeitung ist für die Wahrnehmung einer Aufgabe erforderlich, die im öffentlichen Interesse liegt oder in Ausübung öffentlicher Gewalt erfolgt.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Rechtmäßigkeit der Verarbeitung – berechtigte Interessen',
            description: 'Die Verarbeitung ist zur Wahrung berechtigter Interessen des Verantwortlichen oder eines Dritten erforderlich.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Verarbeitung besonderer Kategorien (sensible Daten)',
            description: 'Die Verarbeitung besonderer Kategorien personenbezogener Daten (Rassenherkunft, politische Meinungen, Gesundheit usw.) ist untersagt, sofern keine besonderen Voraussetzungen in Art. 9(2) erfüllt sind.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Bedingungen für die Einwilligung',
            description: 'Der Verantwortliche muss nachweisen, dass die Einwilligung erteilt wurde. Die Einwilligung muss freiwillig, konkret, informiert und eindeutig erfolgen. Der Widerruf muss so einfach sein wie die Erteilung der Einwilligung.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Einwilligung des Kindes – digitale Dienste',
            description: 'Für die Verarbeitung personenbezogener Daten eines Kindes unter 16 Jahren (oder einem vom Mitgliedstaat festgelegten niedrigeren Alter, nicht unter 13 Jahren) ist für die Nutzung von Diensten der Informationsgesellschaft die Zustimmung der Eltern erforderlich.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Transparente Information und Kommunikation',
            description: 'Der Verantwortliche stellt Informationen über die Verarbeitung in prägnanter, transparenter, verständlicher und leicht zugänglicher Form zur Verfügung.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Informationen zur Direktabholung',
            description: 'Werden personenbezogene Daten von der betroffenen Person erhoben, stellt der Verantwortliche zum Zeitpunkt der Erhebung konkrete Informationen bereit.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Informationen zur indirekten Erhebung',
            description: 'Sofern die personenbezogenen Daten nicht von der betroffenen Person erhoben wurden, muss der Verantwortliche innerhalb einer angemessenen Frist bestimmte Informationen bereitstellen.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Zugangsrecht',
            description: 'Die betroffene Person hat das Recht, eine Bestätigung der Verarbeitung und Zugang zu den personenbezogenen Daten sowie ergänzende Informationen zu erhalten.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Recht auf Berichtigung',
            description: 'Die betroffene Person hat das Recht, unverzüglich die Berichtigung unrichtiger personenbezogener Daten zu verlangen.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Recht auf Löschung (Recht auf Vergessenwerden)',
            description: 'Die betroffene Person hat das Recht, bei Vorliegen bestimmter Gründe die unverzügliche Löschung personenbezogener Daten zu verlangen.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Recht auf Einschränkung der Verarbeitung',
            description: 'Die betroffene Person hat das Recht, unter bestimmten Voraussetzungen eine Einschränkung der Verarbeitung zu verlangen.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Mitteilungspflicht – Berichtigung/Löschung/Einschränkung',
            description: 'Der Verantwortliche teilt allen Empfängern, denen die Daten mitgeteilt wurden, jede Berichtigung, Löschung oder Einschränkung mit.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Recht auf Datenübertragbarkeit',
            description: 'Die betroffene Person hat das Recht, ihre personenbezogenen Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Widerspruchsrecht',
            description: 'Die betroffene Person hat das Recht, der Verarbeitung aufgrund von öffentlichem Interesse oder berechtigten Interessen, einschließlich Profiling, zu widersprechen.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Automatisierte individuelle Entscheidungsfindung, einschließlich Profiling',
            description: 'Die betroffene Person hat das Recht, nicht einer ausschließlich auf einer automatisierten Verarbeitung beruhenden Entscheidung unterworfen zu werden, die rechtliche oder ähnlich erhebliche Wirkung entfaltet.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Verstoßmeldung an Aufsichtsbehörde (72h)',
            description: 'Im Falle einer Verletzung des Schutzes personenbezogener Daten muss der Verantwortliche die Aufsichtsbehörde innerhalb von 72 Stunden nach Kenntniserlangung benachrichtigen, es sei denn, es ist unwahrscheinlich, dass dies zu einem Risiko führt.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Meldung von Verstößen an betroffene Personen (hohes Risiko)',
            description: 'Wenn ein Verstoß voraussichtlich ein hohes Risiko für Rechte und Freiheiten mit sich bringt, teilt der Verantwortliche der betroffenen Person den Verstoß unverzüglich mit.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Antwort auf Anfragen betroffener Personen (30 Tage)',
            description: 'Der Verantwortliche antwortet auf Anfragen betroffener Personen unverzüglich und innerhalb eines Monats nach Erhalt, bei komplexen Anfragen um zwei weitere Monate verlängerbar.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Datenschutz-Folgenabschätzung',
            description: 'Wenn die Verarbeitung voraussichtlich ein hohes Risiko für Rechte und Freiheiten mit sich bringt, führt der Verantwortliche vor der Verarbeitung eine DPIA durch.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Vorherige Rücksprache mit der Aufsichtsbehörde',
            description: 'Der Verantwortliche muss die Aufsichtsbehörde konsultieren, wenn ein DPIA darauf hinweist, dass die Verarbeitung ohne Abhilfemaßnahmen zu einem hohen Risiko führen würde.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA Pflichtverarbeitungsliste',
            description: 'DPIA wird für die systematische Bewertung/Profilierung, die groß angelegte Verarbeitung spezieller Kategorien und die systematische Überwachung öffentlicher Bereiche benötigt.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Aufzeichnungen über Verarbeitungstätigkeiten (Verantwortlicher)',
            description: 'Jeder Verantwortliche führt ein Verzeichnis der Verarbeitungsaktivitäten in seinem Verantwortungsbereich, einschließlich Zwecken, Kategorien, Empfängern, Übermittlungen und Aufbewahrung.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Aufzeichnungen über Verarbeitungstätigkeiten (Auftragsverarbeiter)',
            description: 'Jeder Auftragsverarbeiter muss ein Verzeichnis aller Kategorien von Verarbeitungstätigkeiten führen, die im Auftrag eines Verantwortlichen durchgeführt werden.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Sicherheit der Verarbeitung',
            description: 'Verantwortlicher und Auftragsverarbeiter ergreifen geeignete technische und organisatorische Maßnahmen, um eine dem Risiko angemessene Sicherheit zu gewährleisten.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Datenschutz durch Technikgestaltung und durch datenschutzfreundliche Voreinstellungen',
            description: 'Der Verantwortliche muss geeignete Maßnahmen ergreifen, um sicherzustellen, dass nur personenbezogene Daten verarbeitet werden, die für den jeweiligen Zweck erforderlich sind (Datenminimierung).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Benennung des Datenschutzbeauftragten',
            description: 'Verantwortlicher und Auftragsverarbeiter benennen einen DPO, wenn die Verarbeitung von einer Behörde durchgeführt wird, eine regelmäßige und systematische Überwachung oder eine groß angelegte Verarbeitung besonderer Kategorien umfasst.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Allgemeiner Grundsatz für Überweisungen',
            description: 'Eine Übermittlung personenbezogener Daten in ein Drittland erfolgt nur unter Einhaltung der Voraussetzungen des Kapitels V.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Übertragungen auf der Grundlage einer Angemessenheitsentscheidung',
            description: 'Eine Übermittlung kann erfolgen, wenn die Kommission entschieden hat, dass das Drittland ein angemessenes Schutzniveau gewährleistet.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Übermittlungen unterliegen angemessenen Sicherheitsvorkehrungen',
            description: 'Liegt kein Angemessenheitsbeschluss vor, können Übermittlungen stattfinden, sofern geeignete Sicherheitsvorkehrungen getroffen werden, einschließlich Standardvertragsklauseln oder BCRs.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Standardvertragsklauseln',
            description: 'Von der Kommission oder einer Aufsichtsbehörde angenommene Standardvertragsklauseln bieten angemessene Garantien für internationale Übermittlungen.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Verbindliche Unternehmensregeln',
            description: 'Verbindliche Unternehmensregeln, die von einer zuständigen Aufsichtsbehörde genehmigt wurden, bieten angemessene Schutzmaßnahmen für konzerninterne internationale Transfers.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Ausnahmen für bestimmte Situationen',
            description: 'In Ermangelung angemessener oder sicherer Maßnahmen können Übermittlungen auf der Grundlage einer ausdrücklichen Einwilligung, einer vertraglichen Notwendigkeit, eines öffentlichen Interesses, eines Rechtsanspruchs, lebenswichtiger Interessen oder eines öffentlichen Registers erfolgen.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Verantwortung und Schulung des Leitungsorgans',
            description: 'Die Leitungsorgane wesentlicher und wichtiger Einrichtungen müssen Maßnahmen zum Risikomanagement im Bereich der Cybersicherheit genehmigen und sich regelmäßigen Schulungen unterziehen.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Maßnahmen zum Risikomanagement im Bereich der Cybersicherheit',
            description: 'Wesentliche und wichtige Einrichtungen müssen geeignete und verhältnismäßige technische, betriebliche und organisatorische Maßnahmen ergreifen, um Cybersicherheitsrisiken zu bewältigen.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Richtlinien und Verfahren zur Behandlung von Vorfällen',
            description: 'Unternehmen müssen Richtlinien zur Behandlung von Vorfällen implementieren, einschließlich Erkennungs-, Analyse-, Eindämmungs- und Reaktionsverfahren.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Geschäftskontinuität und Krisenmanagement',
            description: 'Unternehmen müssen Maßnahmen zur Geschäftskontinuität umsetzen, einschließlich Backup-Management, Notfallwiederherstellung und Krisenmanagement.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Sicherheit der Lieferkette',
            description: 'Unternehmen müssen sich mit sicherheitsrelevanten Aspekten der Beziehungen zu Direktlieferanten und Dienstleistern befassen, einschließlich der Risikobewertung der Lieferkette.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Sicherheit in Netzwerk- und Informationssystemen',
            description: 'Unternehmen müssen Sicherheit bei der Beschaffung, Entwicklung und Wartung von Netzwerk- und Informationssystemen implementieren, einschließlich der Handhabung von Schwachstellen.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Umgang mit und Offenlegung von Sicherheitslücken',
            description: 'Unternehmen müssen Richtlinien und Verfahren für die koordinierte Offenlegung und Handhabung von Schwachstellen implementieren.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Wirksamkeit der Cybersicherheitsrisikobewertung',
            description: 'Unternehmen müssen Richtlinien und Verfahren implementieren, um die Wirksamkeit von Maßnahmen zum Risikomanagement im Bereich der Cybersicherheit zu bewerten.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Kryptografie und Verschlüsselungsrichtlinien',
            description: 'Unternehmen müssen Richtlinien und Verfahren für den Einsatz von Kryptografie und gegebenenfalls Verschlüsselung implementieren.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'Personalsicherheit, Zugangskontrolle, Asset-Management',
            description: 'Die Unternehmen müssen Personalsicherheit, Zugangskontrollrichtlinien und Vermögensverwaltung implementieren.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Multi-Faktor-Authentifizierung',
            description: 'Unternehmen müssen Multi-Faktor-Authentifizierungs- oder kontinuierliche Authentifizierungslösungen, gesicherte Sprach-/Video-/Textkommunikation und gesicherte Notfallkommunikationssysteme verwenden.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Frühwarnung an CSIRT (24h)',
            description: 'Unternehmen müssen dem zuständigen CSIRT innerhalb von 24 Stunden, nachdem sie von einem erheblichen Vorfall Kenntnis erlangt haben, eine Frühwarnung übermitteln.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Vorfallmeldung an CSIRT (72h)',
            description: 'Unternehmen müssen dem zuständigen CSIRT innerhalb von 72 Stunden nach Kenntnisnahme eines erheblichen Vorfalls eine Vorfallmeldung übermitteln.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Abschlussbericht an CSIRT (1 Monat)',
            description: 'Unternehmen müssen dem zuständigen CSIRT spätestens einen Monat nach der Meldung des Vorfalls einen Abschlussbericht vorlegen.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Anwendbarkeitsbestimmung für AI Aktivitäten',
            description: 'Stellen Sie fest, ob die Organisation AI Aktivitäten durchführt, die dem vietnamesischen AI-Gesetz unterliegen, und dokumentieren Sie die Entscheidung über die Anwendbarkeit.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AI Klassifizierung des Systemrisikos nach Risikostufe',
            description: 'Klassifizieren Sie jedes AI-System nach Risikostufe (minimal, begrenzt, hoch, inakzeptabel) mithilfe der geführten Klassifizierungsmethode.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI Pflege des Systemregistereintrags',
            description: 'Führen Sie ein Register von AI Systemen mit Systemname, Eigentümer, Zweck, Bereitstellungskontext, Risikostufe und Anbieterbeteiligung.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI Systemdossier mit Artefakt-Checkliste und Beweisen',
            description: 'Erstellen und pflegen Sie ein Systemdossier mit der erforderlichen Artefakt-Checkliste, der Beweisbibliothek und dem Versionsverlauf.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Transparenzpflichten für AI Systembetreiber',
            description: 'Implementieren Sie Transparenzmaßnahmen für AI-Systeme, einschließlich Benutzerbenachrichtigung, Offenlegung der Beteiligung von AI und Systemdokumentation.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AI Ausgabekennzeichnung, Wasserzeichen und Metadatensignatur',
            description: 'Implementieren Sie Kennzeichnung und Wasserzeichen für von AI generierte Inhalte, einschließlich ggf. Metadatensignierung.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Offenlegungspflichten für von AI generierte Inhalte',
            description: 'Teilen Sie Benutzern mit, wenn Inhalte von AI generiert werden, und stellen Sie Mechanismen bereit, mit denen Benutzer die Beteiligung von AI identifizieren können.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Erklärbarkeit und nutzerorientierte Transparenzpflichten',
            description: 'Bereitstellung erklärbarer Ergebnisse für AI Entscheidungen, die Einzelpersonen betreffen, und Pflege einer benutzerorientierten Transparenzdokumentation.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Menschliche Aufsichtspflichten für Hochrisiko-AI-Systeme',
            description: 'Stellen Sie sicher, dass menschliche Aufsichtsmechanismen für Systeme mit hohem Risiko AI vorhanden sind, einschließlich Eskalations- und Außerkraftsetzungsfunktionen.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI Überwachung und Erkennung von Vorfällen',
            description: 'Implementieren Sie Überwachungsfunktionen, um AI Systemvorfälle, Fehlfunktionen und unerwünschte Ergebnisse zu erkennen.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Schwerwiegender AI Vorfall, der der Behörde gemeldet wird',
            description: 'Melden Sie schwerwiegende AI Vorfälle innerhalb der vorgeschriebenen Frist (72 Stunden) der zuständigen Behörde.',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI Einhaltung des Zeitplans für Vorfallbenachrichtigungen',
            description: 'Halten Sie die Benachrichtigungsfristen für AI Vorfälle ein, einschließlich Erstbenachrichtigung, Aktualisierungen und Abschlussberichte.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI Folgenabschätzung für Hochrisikosysteme',
            description: 'Führen Sie Folgenabschätzungen für Systeme mit hohem Risiko AI durch, die Risiken, Abhilfemaßnahmen, menschliche Aufsicht und Überwachungspläne abdecken.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Kontrollierte Tests/Sandbox-Berechtigungsbestimmung',
            description: 'Bestimmen Sie die Eignung für die Teilnahme an kontrollierten Tests (Sandbox) und dokumentieren Sie die Eignungsbewertung.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Sandbox-Antragseinreichung und Genehmigungsverfolgung',
            description: 'Senden Sie Sandbox-Anträge an die zuständige Behörde und verfolgen Sie den Genehmigungsstatus während des gesamten Einreichungslebenszyklus.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Sandbox-Überwachungskontrollpunkte und Abschluss/Beendigung',
            description: 'Führen Sie während der Sandbox-Teilnahme regelmäßige Überwachungskontrollpunkte durch und erstellen Sie Abschluss-/Abschlussberichte.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Kennzeichnung der Ausnahme-/Ausnahmedokumentation mit Begründung',
            description: 'Dokumentieren Sie etwaige Befreiungen oder Ausnahmen von der Kennzeichnung mit Begründung und Belegen.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Datenlokalisierung für vietnamesische Benutzer',
            description: 'Daten vietnamesischer Nutzer über in Vietnam bereitgestellte Dienste müssen auf dem Territorium Vietnams gespeichert werden.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Meldung von Cybersicherheitsvorfällen',
            description: 'Cybersicherheitsvorfälle müssen den zuständigen Behörden rechtzeitig gemeldet werden.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Klassifizierung von Informationssystemen',
            description: 'Informationssysteme müssen nach Wichtigkeitsstufen klassifiziert und geeignete Sicherheitsmaßnahmen angewendet werden.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Risikobewertung der Cybersicherheit',
            description: 'Für Informationssysteme müssen regelmäßig Risikobewertungen zur Cybersicherheit durchgeführt werden.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Bestimmung innerhalb des Geltungsbereichs (Entscheidungsbaum)',
            description: 'Organisationen müssen mithilfe des vorgeschriebenen Entscheidungsbaums feststellen, ob sie in den Geltungsbereich des Dekrets 53 fallen.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Bewertung und Vorbereitung der Datenlokalisierung',
            description: 'Organisationen müssen die Anforderungen an die Datenlokalisierung bewerten und ihre Bereitschaft nachweisen.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Checkliste für die Speicher- und Aufbewahrungsbereitschaft',
            description: 'Zur Einhaltung der Datenlokalisierung muss eine Checkliste für die Speicher- und Aufbewahrungsbereitschaft ausgefüllt werden.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Beurteilung des örtlichen Präsenzbedarfs',
            description: 'Organisationen müssen beurteilen, ob lokale Präsenzanforderungen gelten, und entsprechend planen.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Erhalt und Überprüfung der Anforderung durch die Behörde',
            description: 'Behördenanfragen müssen gemäß den vorgeschriebenen Verfahren entgegengenommen, überprüft und bearbeitet werden.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Antwort der Behörde auf Anfrage innerhalb der Frist',
            description: 'Auf behördliche Anfragen muss innerhalb der vorgeschriebenen Frist geantwortet werden.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Protokoll zur Bearbeitung von Behördenanfragen',
            description: 'Es muss ein Protokoll aller Aktivitäten zur Bearbeitung behördlicher Anfragen geführt werden.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Anwendbarkeit und In-Scope-Bestimmung (konsolidiert)',
            description: 'Organisationen müssen feststellen, ob sie in den Geltungsbereich des konsolidierten Cybersicherheitsgesetzes fallen, das sowohl den Geltungsbereich des alten Gesetzes 24/2018 als auch des Dekrets 53/2022 abdeckt.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Klassifizierung des Informationssystems nach Wichtigkeitsstufe',
            description: 'Informationssysteme müssen nach Wichtigkeitsstufen (allgemein, wichtig, kritisch) mit entsprechenden Sicherheitspflichten klassifiziert werden.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Governance-Struktur für Cybersicherheit und verantwortliches Personal',
            description: 'Organisationen müssen Governance-Strukturen für Cybersicherheit einrichten und verantwortliches Personal benennen.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Regelmäßige Risikobewertung der Cybersicherheit',
            description: 'Für alle Verschlusssachensysteme müssen regelmäßige Cybersicherheitsrisikobewertungen durchgeführt werden.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Sicherheitsmaßnahmen pro Informationssystemklassifizierung',
            description: 'Für jede Klassifizierungsstufe des Informationssystems müssen geeignete technische Sicherheitsmaßnahmen angewendet werden.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Zugangskontrolle und Identitätsmanagement',
            description: 'Für Informationssysteme müssen Maßnahmen zur Zugangskontrolle und zum Identitätsmanagement implementiert werden.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Protokollierung, Überwachung und Audit-Trail',
            description: 'Informationssysteme müssen über Protokollierungs-, Überwachungs- und Audit-Trail-Funktionen verfügen.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Schwachstellenmanagement und Patching',
            description: 'Schwachstellenmanagement- und Patching-Prozesse müssen eingerichtet und aufrechterhalten werden.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Datenlokalisierung für vietnamesische Benutzerdaten',
            description: 'Daten vietnamesischer Nutzer über in Vietnam bereitgestellte Dienste müssen auf dem Territorium Vietnams gespeichert werden.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Lokale Präsenzanforderungen für ausländische Dienstleister',
            description: 'Ausländische Dienstleister, die in Vietnam tätig sind, müssen wie vorgeschrieben eine lokale Präsenz aufrechterhalten.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Lokale Speicherarchitektur und grenzüberschreitende Flussdokumentation',
            description: 'Die lokale Speicherarchitektur muss dokumentiert und grenzüberschreitende Datenflüsse abgebildet und kontrolliert werden.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Erkennung und Reaktion auf Cybersicherheitsvorfälle',
            description: 'Organisationen müssen über die Fähigkeit verfügen, Cybersicherheitsvorfälle zu erkennen und darauf zu reagieren.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Obligatorische Vorfallmeldung an die zuständige Behörde',
            description: 'Qualifizierte Cybersicherheitsvorfälle müssen innerhalb der vorgeschriebenen Fristen an MPS/MIC gemeldet werden.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Einhaltung des Zeitplans für die Meldung von Vorfällen',
            description: 'Vorfallmeldungen müssen den vorgeschriebenen Fristen entsprechen (Erstmeldung innerhalb von 24 Stunden, vollständige Meldung innerhalb von 72 Stunden).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Bearbeitung und Zusammenarbeit von Behördenanfragen',
            description: 'Behördenanfragen müssen zeitnah bearbeitet werden und die Zusammenarbeit muss gemäß den vorgeschriebenen Verfahren erfolgen.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Fallregister und Offenlegungsprotokoll für behördliche Anfragen',
            description: 'Alle behördlichen Anfragen und Offenlegungen müssen in einem Fallregister mit vollständigem Prüfprotokoll protokolliert werden.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Sicherheitsbewertung von Anbietern und Dritten',
            description: 'Drittanbieter mit Zugriff auf Informationssysteme müssen sich einer Sicherheitsbewertung unterziehen.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Kontrollen für den Zugriff auf Lieferantendaten und die Benachrichtigung über Vorfälle',
            description: 'Der Zugriff auf Lieferantendaten muss dokumentiert und vertragliche Pflichten zur Meldung von Vorfällen festgelegt werden.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Schulungen und Übungen zum Sicherheitsbewusstsein',
            description: 'Es müssen regelmäßig Schulungen zur Sensibilisierung für Cybersicherheit und Tabletop-Übungen durchgeführt werden.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Regelmäßiger Pentest und Sicherheitsaudit',
            description: 'Für vertrauliche Informationssysteme müssen regelmäßige Penetrationstests und Sicherheitsüberprüfungen durchgeführt werden.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Pflegen Sie den digitalen Datenbestand',
            description: 'Unternehmen müssen ein umfassendes digitales Dateninventar führen, in dem alle Datenbestände katalogisiert werden.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Datenklassifizierung nach Sensibilität und Wichtigkeit',
            description: 'Daten müssen entsprechend der im Datengesetz festgelegten Sensibilitäts- und Wichtigkeitsstufen klassifiziert werden.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Definieren Sie Handhabungsregeln nach Klassifizierungsstufe',
            description: 'Für jede Datenklassifizierungsebene müssen Handhabungsregeln festgelegt werden.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Verfolgen Sie den Datenaustausch und die Offenlegung mit Genehmigungen',
            description: 'Alle Datenaustausch- und Offenlegungsaktivitäten müssen mit entsprechenden Genehmigungen nachverfolgt werden.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Governance der grenzüberschreitenden Datenübertragung',
            description: 'Grenzüberschreitende Datenübermittlungen müssen den Governance-Anforderungen des Datengesetzes entsprechen.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Behalten Sie Governance-Richtlinien und Verantwortlichkeiten bei',
            description: 'Organisationen müssen Daten-Governance-Richtlinien einhalten und Governance-Verantwortlichkeiten zuweisen.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Jährliche Governance-Bescheinigung',
            description: 'Zur Bestätigung der fortlaufenden Einhaltung muss eine jährliche Governance-Bescheinigung ausgefüllt werden.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Datenrisikobewertung',
            description: 'Um Daten-Governance-Risiken zu identifizieren und zu mindern, muss eine Datenrisikobewertung durchgeführt werden.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Verantwortungs- und Governance-Verpflichtungen des Plattformbetreibers',
            description: 'Richten Sie Rechenschaftsstrukturen für Plattformbetreiber ein, einschließlich Governance-Richtlinien, Überwachungsverantwortlichkeiten und Durchsetzungspflichten.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Plattformregeln für die Veröffentlichung und Transparenz von Informationen',
            description: 'Veröffentlichen Sie Plattformregeln, Handelsbedingungen und betriebliche Offenlegungen auf transparente und zugängliche Weise.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'E-Commerce-Verkaufswebsite/App-Benachrichtigung an MOIT',
            description: 'Senden Sie eine Benachrichtigung an MOIT für E-Commerce-Verkaufswebsites und -anwendungen über Online.gov.vn oder bestimmte Kanäle.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'E-Commerce-Dienstplattform-/Marktplatzregistrierung bei MOIT',
            description: 'Registrieren Sie E-Commerce-Dienstleistungsplattformen und Marktplätze bei MOIT, einschließlich Dossiereinreichung und Genehmigungsverfolgung.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Führung des Verkäuferregisters (Inland und Ausland)',
            description: 'Führen Sie ein Register aller Verkäufer auf der Plattform, einschließlich in- und ausländischer Verkäufer, mit Onboarding, Verifizierung und Lebenszyklusverfolgung.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Livestream-Verkäuferregister und Compliance-Kontrollen',
            description: 'Führen Sie ein Verzeichnis der Livestream-Verkäufer mit Identitätsprüfung, Aktivitätsverfolgung und Compliance-Kontrollen.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Affiliate- und Marketingpartnerregister',
            description: 'Führen Sie ein Verzeichnis von Affiliate-Vermarktern und Empfehlungspartnern mit Vertragsstatus, Inhaltsrichtlinien und Compliance-Bestätigungen.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Produktauflistung und Durchsetzung verbotener/bedingter Waren',
            description: 'Implementieren Sie Produktlistenkontrollen, einschließlich der Überprüfung, Überwachung und Durchsetzung verbotener und bedingter Waren.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Verbraucherbeschwerden werden mit Eingang, Maßnahmen und Prüfpfad registriert',
            description: 'Führen Sie ein Beschwerderegister mit Aufnahmeaufzeichnungen, Untersuchungsmaßnahmen, Lösungsergebnissen und einem vollständigen Prüfprotokoll.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Beschwerdebearbeitungsverfahren und Lösungsfristen',
            description: 'Richten Sie Verfahren zur Beschwerdebearbeitung mit definierten Zeitplänen für die Bestätigung, Untersuchung und Lösung ein.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Moderation, Überwachung und Durchsetzung von Deaktivierungen von Inhalten',
            description: 'Implementieren Sie Verfahren zur Moderation, Überwachung und Entfernung von Inhalten für illegale, verbotene oder rechtsverletzende Inhalte.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Registrierung von Lösch- und Durchsetzungsmaßnahmen mit Beweissicherung',
            description: 'Führen Sie ein Register der Lösch- und Durchsetzungsmaßnahmen mit Entscheidungsaufzeichnungen, Beweissicherung und Verkäuferbenachrichtigungen.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Plattformrichtlinien und Offenlegungsregister (Versionen, Veröffentlichungsdaten, Akzeptanz)',
            description: 'Führen Sie ein Register der Plattformrichtlinien und Offenlegungen mit Versionsverlauf, Veröffentlichungsdaten und Akzeptanznachweisen.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Verbraucherinformationspflichten – genaue Produktinformationen und Schutz vor Täuschung',
            description: 'Sorgen Sie für genaue, klare Produkt- und Serviceinformationen und ergreifen Sie Maßnahmen, um betrügerische Praktiken auf der Plattform zu verhindern.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Betriebsbedingungen und Registrierung/Benachrichtigung für ausländische Plattformen',
            description: 'Halten Sie die Betriebsbedingungen für ausländische Plattformen ein, die vietnamesische Benutzer bedienen, einschließlich der Registrierungs- und Meldepflichten.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Lokale Vertretung oder Autorisierung für ausländische Plattformen',
            description: 'Benennen Sie gemäß den gesetzlichen Bestimmungen einen lokalen Vertreter oder eine autorisierte Stelle in Vietnam für ausländische Plattformen.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Grenzüberschreitende Koordination der Verkäufer- und Beschwerdebearbeitung',
            description: 'Koordinieren Sie die grenzüberschreitende Verkäuferbearbeitung und Beschwerdelösung, einschließlich Eskalation und Beweismanagement.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Interaktion und Berichtsbereitschaft des staatlichen E-Commerce-Managementsystems',
            description: 'Bereiten Sie sich auf die Interaktion mit dem staatlichen E-Commerce-Managementsystem vor, einschließlich der Zuordnung von Datenfeldern und der Berichtsfunktion.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Benachrichtigung über Materialänderungen nach der Genehmigung',
            description: 'Senden Sie Benachrichtigungen über wesentliche Änderungen an Plattformdetails, Richtlinien oder Abläufen nach der ersten Genehmigung.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Kündigungs- und Deaktivierungsbenachrichtigung',
            description: 'Senden Sie Kündigungs- oder Deaktivierungsbenachrichtigungen, wenn Sie den E-Commerce-Betrieb einstellen, einschließlich öffentlicher Bekanntmachung und Verbraucherschutzabwicklung.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Berechtigung und Aufrechterhaltung des Gütesiegel- und Glaubwürdigkeitsbewertungsprogramms',
            description: 'Bewerten Sie die Eignung für Gütesiegel- und Glaubwürdigkeitsbewertungsprogramme und bewahren Sie Compliance-Nachweise für die fortlaufende Teilnahme auf.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Einwilligung zur Verarbeitung personenbezogener Daten',
            description: 'Für die Verarbeitung personenbezogener Daten ist die Einwilligung der betroffenen Person erforderlich, es sei denn, es gilt eine Ausnahmeregelung.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Zugangsrecht',
            description: 'Betroffene Personen haben das Recht auf Auskunft über ihre personenbezogenen Daten, die beim Verantwortlichen gespeichert sind.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Recht auf Berichtigung',
            description: 'Betroffene Personen haben das Recht, die Berichtigung unrichtiger personenbezogener Daten zu verlangen.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Recht auf Löschung',
            description: 'Betroffene Personen haben das Recht, die Löschung ihrer personenbezogenen Daten zu verlangen.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Recht auf Einschränkung der Verarbeitung',
            description: 'Betroffene Personen haben das Recht, eine Einschränkung der Verarbeitung ihrer personenbezogenen Daten zu verlangen.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Widerspruchsrecht',
            description: 'Betroffene Personen haben das Recht, der Verarbeitung ihrer personenbezogenen Daten zu widersprechen.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Recht auf Widerruf der Einwilligung',
            description: 'Betroffene Personen haben das Recht, eine einmal erteilte Einwilligung jederzeit zu widerrufen.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Meldung eines Verstoßes an die Behörde (72 Stunden)',
            description: 'Der Datenverantwortliche muss das Ministerium für öffentliche Sicherheit innerhalb von 72 Stunden nach Feststellung einer Verletzung des Schutzes personenbezogener Daten benachrichtigen.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Benachrichtigung bei Verstößen an betroffene Personen',
            description: 'Der Datenverantwortliche muss betroffene Personen unverzüglich über eine Verletzung des Schutzes personenbezogener Daten informieren.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA für die Verarbeitung mit hohem Risiko',
            description: 'Für Verarbeitungstätigkeiten, die voraussichtlich ein hohes Risiko mit sich bringen, muss eine Datenschutz-Folgenabschätzung durchgeführt werden.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Beurteilung des grenzüberschreitenden Transfers',
            description: 'Vor der Übermittlung personenbezogener Daten außerhalb Vietnams muss eine Folgenabschätzung durchgeführt werden.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Verzeichnis der Verarbeitungstätigkeiten',
            description: 'Datenverantwortliche müssen ein Verzeichnis der Verarbeitungsaktivitäten (RoPA) führen, das alle Verarbeitungsaktivitäten dokumentiert.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Aufzeichnungen der Datenverarbeitungsvereinbarung',
            description: 'Es müssen Aufzeichnungen über Datenverarbeitungsverträge mit Auftragsverarbeitern und Unterauftragsverarbeitern geführt werden.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Schutzmaßnahmen für grenzüberschreitende Übermittlungen',
            description: 'Für die Übermittlung personenbezogener Daten außerhalb Vietnams sind angemessene Sicherheitsvorkehrungen und dokumentierte Bewertungen erforderlich.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Bestätigung innerhalb von 2 Werktagen',
            description: 'Anträge betroffener Personen müssen innerhalb von 2 Arbeitstagen nach Eingang bestätigt werden.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Fertigstellung innerhalb von 15 Werktagen',
            description: 'Anträge betroffener Personen müssen innerhalb von 15 Arbeitstagen bearbeitet werden, mit einer möglichen Verlängerung um 5 Arbeitstage.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'Einreichung des Dossiers zur MoPS-Folgenabschätzung',
            description: 'Organisationen, die Hochrisikoverarbeitungen durchführen, müssen beim Ministerium für öffentliche Sicherheit im vorgeschriebenen Verwaltungsverfahren ein Folgenabschätzungsdossier einreichen.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'Einreichung von MoPS-Dossiers für grenzüberschreitende Transfers',
            description: 'Organisationen, die personenbezogene Daten außerhalb Vietnams übermitteln, müssen beim Ministerium für öffentliche Sicherheit ein Dossier zur Folgenabschätzung für die grenzüberschreitende Übertragung einreichen.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Zertifizierung von Verarbeitungsdienstleistern',
            description: 'Organisationen, die die Verarbeitung personenbezogener Daten als Dienstleistung anbieten, müssen vom Ministerium für öffentliche Sicherheit eine Dienstleistungsverarbeitungsbescheinigung erhalten.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Bearbeitung des Dossiers zur Folgenabschätzung, wenn die Auslöser erfüllt sind',
            description: 'Wenn vorgeschriebene Auslöser erfüllt sind, muss ein Dossier zur Folgenabschätzung der Verarbeitung eingereicht werden.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Dossier über die Auswirkungen grenzüberschreitender Transfers',
            description: 'Für internationale Datenübermittlungen muss ein Dossier über die Auswirkungen einer grenzüberschreitenden Übertragung erstellt und eingereicht werden.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Benennung des Datenschutzbeauftragten',
            description: 'Gemäß Verordnung 13 muss ein Datenschutzbeauftragter benannt werden.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Governance-Artefakte (Richtlinien, Rollen, Verfahren)',
            description: 'Governance-Artefakte, einschließlich Richtlinien, Rollen und Verfahren, müssen gepflegt werden.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Vollständigkeit des Beweispakets und unveränderlicher Prüfpfad',
            description: 'Die Beweispakete müssen vollständig sein und ein unveränderlicher Prüfpfad muss aufrechterhalten werden.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Unterstützung bei der Einreichung von Fallabläufen',
            description: 'Für Folgenabschätzungen und grenzüberschreitende Dossiers müssen die Arbeitsabläufe bei der Einreichung von Fällen unterstützt werden.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Vertragliche Anforderungen des Auftragsverarbeiters',
            description: 'Vertragliche Anforderungen an Auftragsverarbeiter müssen festgelegt und überwacht werden.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Jährliche Compliance-Überprüfung',
            description: 'Um die fortlaufende Einhaltung zu überprüfen, muss eine jährliche Compliance-Überprüfung durchgeführt werden.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Einreichungsunterlagen der Behörde',
            description: 'Es müssen Aufzeichnungen über alle behördlichen Einreichungen geführt werden.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Rechtsgrundlagen für die Verarbeitung personenbezogener Daten',
            description: 'Die Verarbeitung personenbezogener Daten muss auf rechtmäßigen Grundlagen im Sinne der PDPL basieren.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Rechte der betroffenen Person (Auskunft, Berichtigung, Löschung, Einschränkung, Widerspruch, Übertragbarkeit)',
            description: 'Betroffene Personen haben umfassende Rechte, einschließlich Auskunft, Berichtigung, Löschung, Einschränkung, Widerspruch und Übertragbarkeit.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Meldung eines Verstoßes an die Behörde',
            description: 'Datenverantwortliche müssen die zuständige Behörde innerhalb der vorgeschriebenen Frist über Verstöße gegen den Schutz personenbezogener Daten informieren.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Benachrichtigung bei Verstößen an betroffene Personen',
            description: 'Datenverantwortliche müssen betroffene Personen über Verstöße gegen den Schutz personenbezogener Daten informieren.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Datenschutz-Folgenabschätzung',
            description: 'Für Verarbeitungstätigkeiten mit hohem Risiko muss eine Datenschutz-Folgenabschätzung durchgeführt werden.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Grenzüberschreitende Transfer-Governance',
            description: 'Grenzüberschreitende Übermittlungen personenbezogener Daten müssen den Governance-Anforderungen entsprechen.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Benennung des Datenschutzbeauftragten',
            description: 'Organisationen müssen bei Bedarf einen Datenschutzbeauftragten benennen.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Aufzeichnungen über Verarbeitungstätigkeiten',
            description: 'Organisationen müssen Aufzeichnungen über alle Verarbeitungsaktivitäten führen.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Vertragliche Pflichten des Auftragsverarbeiters',
            description: 'Mit den Auftragsverarbeitern müssen vertragliche Verpflichtungen vereinbart werden.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Einreichung des Dossiers zur Folgenabschätzung',
            description: 'Dossiers zur Folgenabschätzung müssen wie vorgeschrieben bei der zuständigen Behörde eingereicht werden.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Dossier über die Auswirkungen grenzüberschreitender Transfers',
            description: 'Für internationale Datenübermittlungen muss ein Dossier über die Auswirkungen einer grenzüberschreitenden Übertragung erstellt und eingereicht werden.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO Termin und Benachrichtigung',
            description: 'Es ist ein Datenschutzbeauftragter zu bestellen und der Behörde mitzuteilen.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Governance-Richtlinien und -Verfahren',
            description: 'Governance-Richtlinien und -Verfahren müssen festgelegt und aufrechterhalten werden.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Beweispaket und unveränderlicher Prüfpfad',
            description: 'Die Beweispakete müssen vollständig sein und ein unveränderlicher Prüfpfad muss aufrechterhalten werden.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Lebenszyklus eines Aktenfalls',
            description: 'Der Lebenszyklus eines Aktenfalls muss in vorgeschriebenen Phasen verwaltet werden.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Vertragliche Anforderungen des Auftragsverarbeiters',
            description: 'Vertragliche Anforderungen an Auftragsverarbeiter müssen festgelegt und überwacht werden.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Zertifizierung als Service-Processing-Anbieter',
            description: 'Organisationen, die Datenverarbeitungsdienste anbieten, müssen eine Zertifizierung erhalten.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Einreichungsunterlagen der Behörde',
            description: 'Es müssen Aufzeichnungen über alle behördlichen Einreichungen geführt werden.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Jährliche Compliance-Überprüfung',
            description: 'Um die fortlaufende Einhaltung zu überprüfen, muss eine jährliche Compliance-Überprüfung durchgeführt werden.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Technische Maßnahmen zur Datensicherheit',
            description: 'Technische Maßnahmen zur Datensicherheit müssen umgesetzt und aufrechterhalten werden.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Dekret 356 offizielle Formularanforderungen für Dokumente',
            description: 'Für die entsprechenden Einreichungen beim Ministerium für öffentliche Sicherheit sind die 13 offiziellen Mẫu số-Verwaltungsverfahrensformulare (Mẫu số 01a–10) zu verwenden, die im Dekret 356/2025/NĐ-CPĐ-CP vorgeschrieben sind. Bei den Elementen I–VIII handelt es sich um interne Datenstrukturzuordnungen, nicht um offizielle Regulierungsformulare.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA Dossiereinreichung an A05',
            description: 'Organisationen müssen DPIA-Dossiers an A05/MPS für Verarbeitungsaktivitäten mit sensiblen personenbezogenen Daten oder umfangreiche Verarbeitungen einreichen.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Einreichung des grenzüberschreitenden Transferdossiers',
            description: 'Organisationen müssen Dossiers zur grenzüberschreitenden Datenübermittlung zur Überprüfung und Genehmigung gemäß A05/MPS einreichen.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Bearbeitung des Antrags auf ein Servicezertifikat',
            description: 'Datenverarbeitungsdienstleister müssen Zertifikate ab A05/MPS beantragen.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Benachrichtigung über die Verarbeitung personenbezogener Daten',
            description: 'Verantwortliche müssen A05/MPS über die Verarbeitung personenbezogener Daten unter Verwendung der vorgeschriebenen Formulare informieren (siehe Abschnitt 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Jährliche Lageberichterstattung',
            description: 'Verantwortliche müssen jährliche Situationsberichte zur Verarbeitung personenbezogener Daten vorlegen (Stand 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Vertraulichkeit der Benutzerinformationen standardmäßig',
            description: 'Benutzerinformationen müssen standardmäßig vertraulich behandelt werden.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Minimale organisatorische und technische Schutzkontrollen',
            description: 'Es müssen minimale organisatorische und technische Schutzmaßnahmen umgesetzt werden.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Offenlegung nur auf geeigneter Grundlage',
            description: 'Die Weitergabe von Nutzerdaten ist nur auf gesetzlicher Grundlage zulässig.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Obligatorische Offenlegungsprotokollierung und Überprüfbarkeit',
            description: 'Alle Offenlegungen müssen protokolliert und überprüfbar sein.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Interner Genehmigungsworkflow für Offenlegungen',
            description: 'Für alle Offenlegungen muss ein interner Genehmigungsworkflow eingerichtet werden.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Bearbeitung der Antwort auf Autoritätsanfragen',
            description: 'Behördenanfragen müssen gemäß den vorgeschriebenen Verfahren bearbeitet und beantwortet werden.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'Die betroffene Person hat das Recht, über die Verarbeitung personenbezogener Daten informiert zu werden.',
        consent: 'Die betroffene Person hat das Recht auf Einwilligung in die Verarbeitung ihrer personenbezogenen Daten.',
        refuse_consent: 'Die betroffene Person hat das Recht, der Verarbeitung ihrer personenbezogenen Daten nicht zuzustimmen.',
        withdraw_consent: 'Die betroffene Person hat das Recht, den Widerruf einer zuvor erteilten Einwilligung zu verlangen.',
        access: 'Die betroffene Person hat das Recht, ihre beim Verantwortlichen gespeicherten personenbezogenen Daten einzusehen und darauf zuzugreifen.',
        rectification: 'Die betroffene Person hat das Recht, unrichtige personenbezogene Daten zu korrigieren oder deren Berichtigung zu verlangen.',
        data_provision: 'Die betroffene Person hat das Recht, die Bereitstellung ihrer personenbezogenen Daten zu verlangen (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'Vorbehaltlich gesetzlicher Ausnahmen hat die betroffene Person das Recht, die Löschung oder Vernichtung personenbezogener Daten zu verlangen.',
        restriction_of_processing: 'Die betroffene Person hat das Recht, eine Einschränkung der Verarbeitung ihrer personenbezogenen Daten zu verlangen.',
        object_to_processing: 'Die betroffene Person hat das Recht, einen Widerspruchsantrag gegen die Verarbeitung ihrer personenbezogenen Daten zu stellen.',
        complaint: 'Die betroffene Person hat das Recht, eine Beschwerde (khiếu nại) bezüglich der Verarbeitung personenbezogener Daten einzureichen.',
        denunciation: 'Die betroffene Person hat das Recht, bei Verstößen gegen den Schutz personenbezogener Daten Anzeige zu erstatten.',
        lawsuit: 'Die betroffene Person hat das Recht, bei Verstößen gegen ihre Datenschutzrechte rechtliche Schritte einzuleiten.',
        claim_damages: 'Die betroffene Person hat das Recht, Schadensersatz für Schäden zu verlangen, die durch Verstöße gegen den Schutz personenbezogener Daten verursacht wurden.',
        request_protection_measures: 'Die betroffene Person hat das Recht, von den zuständigen Behörden oder relevanten Organisationen/Einzelpersonen die Umsetzung von Maßnahmen zum Schutz ihrer personenbezogenen Daten zu verlangen.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Risikomanagement und -überwachung (Entwurf)',
            steps: {
                '1': 'Risikokategorien identifizieren',
                '2': 'Führen Sie Frühwarnkontrollen durch',
                '3': 'Risiken bei der Erfassung, Speicherung, Verarbeitung, Weitergabe und Transaktion von Bewertungen',
                '4': 'Erfassen Sie Datenschutz-, Cybersicherheits- und Zugriffsverwaltungsrisiken',
                '5': 'Lösen Sie Abhilfemaßnahmen aus',
                '6': 'Behalten Sie den Bewertungsverlauf bei',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Beschwerden und Streitigkeiten (Entwurf)',
            steps: {
                '1': 'Nehmen Sie Beschwerden/Streitigkeiten entgegen',
                '2': 'Überprüfen Sie die Vollständigkeit der erforderlichen Informationen. Bei Unvollständigkeit innerhalb von 24 Stunden Nachtrag anfordern',
                '3': 'Benachrichtigen Sie die Parteien innerhalb von 3 Arbeitstagen nach Erhalt einer vollständigen Anfrage über den Bearbeitungsansatz und den voraussichtlichen Zeitplan',
                '4': 'Fordern Sie Antworten und unterstützende Dokumente an',
                '5': 'Bewerten Sie den Streitfall und die Plattformverantwortung',
                '6': 'Auflösungsergebnis aufzeichnen',
                '7': 'Beweise sichern und Fall abschließen',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Lieferung und Eigentumsbescheinigung an Daten (Entwurf)',
            steps: {
                '1': 'Liefern Sie das Produkt/die Dienstleistung gemäß der Vertragsmethode',
                '2': 'Notieren Sie das Lieferereignis und die Lieferzeit',
                '3': 'Erstellen Sie eine Bescheinigung über Eigentumsrechte an Daten',
                '4': 'Registrieren Sie den Abschluss der Transaktion',
                '5': 'Aktivieren Sie bei Bedarf eine spätere Suche/Verifizierung',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Transaktionsanbahnung und Vertragsabschluss (Entwurf)',
            steps: {
                '1': 'Rezensionsauflistung und Bewertungsergebnis',
                '2': 'Initiieren Sie den Kauf-, Verhandlungs- oder Auktionsworkflow',
                '3': 'Elektronischen Vertrag erstellen',
                '4': 'Sichern Sie sich die Akzeptanz beider Parteien',
                '5': 'Erfassen Sie den Zahlungsabschluss',
                '6': 'Lieferung genehmigen',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Sandbox-/Testumgebungszugriff (Entwurf)',
            steps: {
                '1': 'Senden Sie eine Sandbox-Anfrage',
                '2': 'Definieren Sie den Umfang, die zulässigen Daten, den Zeitraum und die Verarbeitungsberechtigungen',
                '3': 'Bereitstellung einer isolierten technischen Umgebung',
                '4': 'Überwachen Sie Sicherheit und Zugriff während des Tests',
                '5': 'Überprüfen Sie die Ausgabe vor der Extraktion',
                '6': 'Genehmigen Sie die Entnahme nur dort, wo dies zulässig ist',
                '7': 'Schließen Sie die Testsitzung und bereinigen Sie die Umgebung',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Datenprodukt-/Dienstleistungsliste (Entwurf)',
            steps: {
                '1': 'Listungsantrag erstellen',
                '2': 'Erfassen Sie Quellen- und Eigentums-/Nutzungsnachweise',
                '3': 'Notieren Sie technische Merkmale, Format, API Details und Einschränkungen',
                '4': 'Erfassen Sie qualitativ hochwertige Offenlegungen',
                '5': 'Reichen Sie das Listungsdossier zur Plattformprüfung ein',
                '6': 'Die Plattform führt Vollständigkeits-, Rechtmäßigkeits- und technische Prüfungen durch',
                '7': 'Eintrag genehmigen oder ablehnen',
                '8': 'Bei Genehmigung die Auflistung mit beigefügtem Bewertungsergebnis veröffentlichen',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Teilnehmer-Onboarding (Entwurf)',
            steps: {
                '1': 'Bestimmen Sie den Teilnehmertyp: Verkäufer oder Käufer',
                '2': 'Überprüfen Sie den Rechtsstatus und die Geschäftsfähigkeit',
                '3': 'Überprüfen Sie zusätzliche Bedingungen, wenn bedingte Geschäftsbereiche gelten',
                '4': 'Transaktionskonto registrieren',
                '5': 'Zahlungskonto verknüpfen',
                '6': 'Genehmigen Sie die Teilnahme an der Plattform',
                '7': 'Speichern Sie Teilnehmeraufzeichnungen und Prüfprotokolle',
            },
        },
        AI_A1: {
            name: 'AI Systemregistrierung/Benachrichtigung',
            steps: {
                '1': 'Anwendbarkeitsprüfung und AI Systemklassifizierung',
                '2': 'Erstellen Sie ein Registrierungs-/Benachrichtigungsdossier',
                '3': 'Interne Genehmigung',
                '4': 'Über den Kanal einreichen',
                '5': 'Änderungsschleife – bei Rückgabe aktualisieren und erneut einreichen',
                '6': 'Bestätigung schließen und speichern',
            },
        },
        AI_A2: {
            name: 'AI Risikoklassifizierungs-Workflow',
            steps: {
                '1': 'Erstellen Sie einen AI Systemregistereintrag',
                '2': 'Führen Sie den Klassifizierungsassistenten aus',
                '3': 'Beweise beifügen',
                '4': 'Klassifizierung genehmigen',
                '5': 'Verknüpfen Sie die Ausgaben mit dem Systemdossier',
            },
        },
        AI_A3: {
            name: 'Kennzeichnungs- und Offenlegungskonformität',
            steps: {
                '1': 'Kennzeichnungspflichten nach System und Anwendungsfall identifizieren',
                '2': 'Kennzeichnungshinweise implementieren',
                '3': 'Erfassen Sie Beweise – Screenshots, UI-Kopie, technische Metadaten',
                '4': 'Behandeln Sie Ausnahmen und zeichnen Sie Begründungen auf',
                '5': 'Genehmigen und Version',
            },
        },
        AI_A4: {
            name: 'Meldung schwerwiegender Vorfälle',
            steps: {
                '1': 'Erstellen Sie AI Vorfalldatensatz',
                '2': 'Schweregrad klassifizieren',
                '3': 'Führen Sie Eindämmungsmaßnahmen und Beweiserfassung durch',
                '4': 'Generieren Sie den Export einer Vorlage für einen Bericht über schwerwiegende Vorfälle',
                '5': 'Übermitteln Sie es über den Kanal an die Behörde',
                '6': 'Verfolgen Sie die Antworten und Schließungen der Behörden',
            },
        },
        AI_A5: {
            name: 'AI Folgenabschätzung',
            steps: {
                '1': 'Bewertungsprotokoll erstellen',
                '2': 'Füllen Sie die erforderlichen Abschnitte aus',
                '3': 'Fügen Sie Beweise bei – Datenflüsse, Modelldokumentation, Kontrollen',
                '4': 'Interne Genehmigung',
                '5': 'Bericht exportieren und im Dossier speichern',
            },
        },
        AI_A6: {
            name: 'Kontrollierte Tests/Sandbox-Teilnahme',
            steps: {
                '1': 'Vollständige Berechtigungs- und Bereitschaftsaufzeichnung',
                '2': 'Anwendung vorbereiten und exportieren',
                '3': 'Über den Kanal einreichen',
                '4': 'Verfolgen Sie die Genehmigung',
                '5': 'Führen Sie Überwachungskontrollpunkte und Protokolle aus',
                '6': 'Vollständiger Fertigstellungsbericht und Abschlusspaket',
            },
        },
        CYBERSEC_C1: {
            name: 'Bewertung der Sicherheit kritischer Informationssysteme',
            steps: {
                '1': 'Klassifizieren Sie Informationssysteme gemäß den nationalen Cybersicherheitsanforderungen',
                '2': 'Führen Sie Sicherheitsbewertungen und Risikobewertungen für klassifizierte Systeme durch',
                '3': 'Implementieren Sie die erforderlichen Sicherheitsmaßnahmen und grundlegenden Kontrollen',
                '4': 'Dokumentieren Sie den Nachweis der Einhaltung und planen Sie regelmäßige Überprüfungen',
            },
        },
        CYBERSEC_C2: {
            name: 'Benachrichtigung über Cybersicherheitsvorfälle',
            steps: {
                '1': 'Erkennen, klassifizieren und bewerten Sie den Cybersicherheitsvorfall',
                '2': 'Führen Sie sofortige Reaktionsmaßnahmen und Beweissicherung durch',
                '3': 'Melden Sie sich bei der Behörde und beteiligen Sie sich an der nationalen Koordinierung der Reaktion auf Vorfälle',
                '4': 'Führen Sie nach dem Vorfall eine Überprüfung durch und dokumentieren Sie die gewonnenen Erkenntnisse',
            },
        },
        CYBERSEC_C3: {
            name: 'Überprüfung der Datenlokalisierungskonformität',
            steps: {
                '1': 'Bewerten Sie anhand des Diensttyps und der Datenkategorien, ob Auslöser für die Datenlokalisierung gelten',
                '2': 'Überprüfen Sie, ob die lokale Speicherarchitektur und grenzüberschreitende Flusskontrollen vorhanden sind',
                '3': 'Bewerten Sie gegebenenfalls die Anforderungen an die Präsenz vor Ort (Zweigstelle/Repräsentanz).',
                '4': 'Dokumentieren Sie Konformitätsnachweise und bewahren Sie sie zur behördlichen Überprüfung auf',
            },
        },
        CYBERSEC_C4: {
            name: 'Antwort auf die Kooperationsanfrage der Behörde',
            steps: {
                '1': 'Erhalten und überprüfen Sie die Legitimität und den Umfang der behördlichen Anfrage',
                '2': 'Sammeln und bereiten Sie angeforderte Informationen vor oder führen Sie erforderliche Aktionen aus',
                '3': 'Senden Sie die Antwort innerhalb der erforderlichen Frist an die Behörde',
                '4': 'Protokollieren Sie die Offenlegung und schließen Sie den Fall',
            },
        },
        CYBERSEC_C5: {
            name: 'Überprüfung der Qualifikation des Cybersicherheitspersonals',
            steps: {
                '1': 'Bewerten Sie die Sicherheitslage des Anbieters mithilfe des Cybersecurity-Assessment-Frameworks',
                '2': 'Überprüfen Sie den Umfang des Anbieterdatenzugriffs und registrieren Sie sich im Anbieterdatenzugriffsprotokoll',
                '3': 'Überprüfen Sie die Klausel zur Benachrichtigung über Vorfälle des Anbieters und die vertraglichen Cybersicherheitsanforderungen',
                '4': 'Planen Sie regelmäßige Sicherheitsüberprüfungen der Anbieter und überwachen Sie die Einhaltung',
            },
        },
        DATA_D1: {
            name: 'Einrichtung des Dateninventars und der Klassifizierung',
            steps: {
                '1': 'Katalogisieren Sie alle Datenbestände mit Typen und Quellen im Datenbestandsregister',
                '2': 'Klassifizieren Sie jeden Vermögenswert nach Sensitivitätsstufe',
                '3': 'Definieren Sie Handhabungsregeln pro Klassifizierungsstufe',
                '4': 'Erfassen Sie Beweise – Screenshots, Architekturdiagramme, Zugangskontrollen',
                '5': 'Planen Sie regelmäßige Überprüfungen',
            },
        },
        DATA_D2: {
            name: 'Datenaustausch-Governance',
            steps: {
                '1': 'Bewerten Sie neue Vereinbarungen zur Datenfreigabe anhand einer Risikobewertung für die Datenfreigabe',
                '2': 'Vollständiger interner Genehmigungsworkflow',
                '3': 'Zeichnen Sie das Freigabeereignis im Datenfreigabe-/Offenlegungsprotokoll auf',
                '4': 'Erfassen Sie Beweise – Genehmigungsunterlagen, Vertragsdokumente',
                '5': 'Regelmäßige Überprüfung der Sharing-Vereinbarungen',
            },
        },
        DATA_D3: {
            name: 'Bewertung der grenzüberschreitenden Datenübertragung',
            steps: {
                '1': 'Identifizieren Sie grenzüberschreitende Übertragungen im Datenbestand',
                '2': 'Führen Sie eine Bewertung des Übertragungsrisikos durch',
                '3': 'Wenden Sie Schutzmaßnahmen und Governance-Maßnahmen an',
                '4': 'Dokument im grenzüberschreitenden Abschnitt der Governance-Bescheinigung',
                '5': 'Exportieren Sie das Bewertungspaket für das Audit',
            },
        },
        DATA_D4: {
            name: 'Jährliche Governance-Bescheinigung',
            steps: {
                '1': 'Überprüfen Sie die Governance-RACI-Matrix',
                '2': 'Überprüfen Sie den Richtlinienstatus (Governance, Klassifizierung, Freigabe, Aufbewahrung).',
                '3': 'Vollständiges Attestierungspaket',
                '4': 'Holen Sie die interne Genehmigung ein',
                '5': 'Export- und Archivbescheinigung',
            },
        },
        DATA_D5: {
            name: 'Umgang mit Datenvorfällen',
            steps: {
                '1': 'Klassifizieren Sie den Vorfall mithilfe des Entscheidungsdatensatzes',
                '2': 'Führen Sie eine Reaktion auf Vorfälle gemäß Playbook durch',
                '3': 'Erfassen Sie Beweise – Protokolle, Screenshots, Kommunikation',
                '4': 'Führen Sie eine Überprüfung nach dem Vorfall durch',
                '5': 'Wenn eine behördliche Benachrichtigung erforderlich ist: Einreichungsfall erstellen und an MIC senden',
                '6': 'Schließen und archivieren',
            },
        },
        ECOM_P1: {
            name: 'Benachrichtigung (Verkaufswebsite/App)',
            steps: {
                '1': 'Bestimmen Sie die Klassifizierung: Verkaufsseite/App vs. Marktplatz/Serviceplattform',
                '2': 'Erstellen Sie einen internen Dossierdatensatz mit Unternehmensidentität, Website-/App-Informationen und erforderlichen Offenlegungen',
                '3': 'Erstellen Sie ein Beweispaket – Screenshots von Offenlegungen, Richtlinien-URLs, Versionsverlauf',
                '4': 'Über Online.gov.vn einreichen – Dokumente hochladen, Zeitstempel und Bestätigung aufzeichnen',
                '5': 'Verfolgen Sie die Verarbeitung – bei Rücksendung zur Änderung die Gründe erfassen und erneut einreichen',
                '6': 'Schließen – Bestätigung speichern, Nachweise verknüpfen und Prüfprotokoll erstellen',
            },
        },
        ECOM_P2: {
            name: 'Registrierung (Serviceplattformen / Marktplätze)',
            steps: {
                '1': 'Bestimmen Sie das Plattformmodell und die Verpflichtungen – Marktplatz vs. gemischter vs. Social Commerce',
                '2': 'Build-Registrierungsdossier – Antragsformular, Dienstbereitstellungsplan, AGB, Plattformregeln',
                '3': 'Interne Genehmigungen – Rechts-/Compliance-Eigentümer zuweisen, zur Einreichung genehmigen',
                '4': 'Über Online.gov.vn einreichen – Dossier hochladen, Zeitstempel aufzeichnen',
                '5': 'Verarbeitungsschleife – bei Rückgabe zur Änderung, Aktualisierung und erneuten Übermittlung',
                '6': 'Genehmigung und Operationalisierung – Bestätigung speichern, laufende Compliance-Register führen',
            },
        },
        ECOM_P3: {
            name: 'Aktualisierungs-/Änderungsbenachrichtigung',
            steps: {
                '1': 'Änderungsauslöser identifizieren – wesentliche Änderung an Dossierelementen',
                '2': 'Änderungspaket generieren – Änderungszusammenfassung, aktualisierte Dokumente, Beweisaktualisierungen',
                '3': 'Senden Sie die Aktualisierung und zeichnen Sie den Übermittlungsstatus auf',
                '4': 'Bearbeitung verfolgen und abschließen',
            },
        },
        ECOM_P4: {
            name: 'Kündigung/Deaktivierung',
            steps: {
                '1': 'Bereiten Sie das Kündigungspaket vor – Kündigungsdatum, Grund, Verbraucherschutz-Handhabungsplan, Nachweis der öffentlichen Bekanntmachung',
                '2': 'Kündigung einreichen – Zeitstempel und Bestätigung aufzeichnen',
                '3': 'Schließen – Endgültigen Status und Beweise speichern',
            },
        },
        ECOM_P5: {
            name: 'Programm zur Vertrauenswürdigkeits-/Glaubwürdigkeitsbewertung',
            steps: {
                '1': 'Überprüfung der Eignung und Bereitschaft',
                '2': 'Bereiten Sie das Dossier mit dem offiziellen Formular vor',
                '3': 'Senden und verfolgen',
                '4': 'Bewahren Sie kontinuierliche Compliance-Nachweise auf',
            },
        },
        PDPL_P1: {
            name: 'DPIA Einreichung der Einreichung bei MPS',
            steps: {
                '1': 'Füllen Sie DPIA mithilfe des Formularpfads gemäß Dekret 356 aus (Version 02a/02b)',
                '2': 'Stellen Sie Begleitdokumente und Belege zusammen',
                '5': 'Bewahren Sie das Dossier und den Nachweis der Einreichung für den Prüfpfad auf',
            },
        },
        PDPL_P2: {
            name: 'Antrag auf grenzüberschreitende Überweisung',
            steps: {
                '1': 'Füllen Sie das Dossier für grenzüberschreitende Transfers mit den Formularen des Dekrets 356 aus (Version 01a/01b, Version 09)',
            },
        },
        PDPL_P3: {
            name: 'Antrag auf ein Datenverarbeitungsdienstzertifikat',
            steps: {
                '1': 'Bereiten Sie ein Zertifizierungsantragsdossier gemäß den Anforderungen des Dekrets 356 vor',
                '2': 'Senden Sie den Antrag mit den erforderlichen Belegen an MPS.',
                '3': 'Reagieren Sie auf MPS Rezensionskommentare und Ergänzungsanfragen',
            },
        },
        PDPL_P4: {
            name: 'Bearbeitung von Anfragen zu Rechten betroffener Personen',
            steps: {
                '1': 'Anfrage empfangen und protokollieren; Überprüfung der Identität der betroffenen Person innerhalb von 2 Werktagen',
                '2': 'Bewerten Sie die Art der Anfrage und legen Sie die anwendbare Frist fest (10/15/20 Tage nach Typ).',
                '3': 'Führen Sie Fulfillment-Aktionen über alle relevanten Systeme und Abteilungen hinweg aus',
                '4': 'Bereiten Sie eine Antwort mit Ergebnis und Begründung vor und senden Sie sie an die betroffene Person',
                '5': 'Den Fall abschließen und Beweise für die Prüfung aufbewahren; Dokumentieren Sie etwaige Ablehnungsgründe',
            },
        },
        PDPL_P5: {
            name: 'Verstoßmeldung an MPS',
            steps: {
                '1': 'Den Verstoß erkennen und bewerten; feststellen, ob die Meldepflicht besteht, und die geltende Meldefrist aus den installierten Fristdatensätzen ermitteln',
                '2': 'Bereiten Sie das Benachrichtigungspaket gemäß den PDPL-Anforderungen vor',
                '3': 'Die Benachrichtigung innerhalb der aus den installierten Fristdatensätzen ermittelten Meldefrist, gerechnet ab der Entdeckung, an MPS senden',
                '4': 'Auf MPS Folgeanfragen antworten; Nachträge und Koordinationsnachweise einreichen',
            },
        },
        TELECOM_T1: {
            name: 'Bearbeitung von Behördenanfragen',
            steps: {
                '1': 'Empfangen und protokollieren Sie die behördliche Anfrage per Aufnahmeprotokoll',
                '2': 'Überprüfen Sie die Identität der Behörde, die Rechtsgrundlage und den Umfang der Anfrage',
                '3': 'Führen Sie eine interne rechtliche Prüfung durch und holen Sie die Genehmigung des Managements ein',
                '4': 'Bereiten Sie ein Antwortpaket mit gesammelten Beweisen vor',
                '5': 'Antwortschreiben entwerfen und genehmigen',
                '6': 'Erstellen Sie einen Einreichungsfall und senden Sie die Antwort über den Kanal',
                '7': 'Protokollieren Sie die Offenlegung und verfolgen Sie die Bestätigung der Autorität',
                '8': 'Schließen Sie den Fall ab und bewahren Sie Beweise für den Prüfpfad auf',
            },
        },
        TELECOM_T2: {
            name: 'Offenlegungsmanagement',
            steps: {
                '1': 'Offenlegungsanfrage erhalten (Benutzereinwilligung, rechtliche Verpflichtung oder behördliche Anfrage)',
                '2': 'Kompletter Offenlegungsgenehmigungs-Workflow',
                '3': 'Offenlegung durchführen und im Protokollregister eintragen',
                '4': 'Erfassen Sie Beweise – Genehmigungsaufzeichnungen, Kommunikationsaufzeichnungen',
                '5': 'Regelmäßige Überprüfung des Offenlegungsprotokolls',
            },
        },
        TELECOM_T3: {
            name: 'Vertraulichkeitsrichtlinie und Einrichtung der Zugriffskontrolle',
            steps: {
                '1': 'Vertraulichkeitsrichtlinie entwerfen und genehmigen',
                '2': 'Entwerfen und genehmigen Sie Zugriffskontroll- und Protokollierungsrichtlinien',
                '3': 'Dokumentverarbeitungsaktivitäten im Benutzerinformationsverarbeitungsregister',
                '4': 'Erfassen Sie Beweise – Richtliniendokumente, Umsetzungsnachweise',
                '5': 'Planen Sie regelmäßige Überprüfungen',
            },
        },
        TELECOM_T4: {
            name: 'Bewertung von Telekommunikationsvorfällen',
            steps: {
                '1': 'Den Vorfall einordnen und bewerten',
                '2': 'Bestimmen Sie die Benachrichtigungsanforderungen',
                '3': 'Wenn eine behördliche Mitteilung erforderlich ist: Einreichungsfall erstellen',
                '4': 'Beweise erfassen und sichern',
                '5': 'Auflösung und Abschluss',
            },
        },
        TELECOM_T5: {
            name: 'Aufbewahrungs- und Löschverwaltung',
            steps: {
                '1': 'Definieren Sie einen Aufbewahrungs- und Löschplan pro Datenkategorie',
                '2': 'Implementieren Sie Löschverfahren',
                '3': 'Beweise erfassen – Aufzeichnungen löschen',
                '4': 'Regelmäßige Überprüfung der Zeitpläne',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registrierung / Benachrichtigung / Verwaltungsbereitschaft',
            'ECOM-FINE-B': 'Beschwerden und Verbraucherschutz',
            'ECOM-FINE-C': 'Überwachung und Durchsetzung von Takedowns',
            'ECOM-FINE-D': 'Grenzüberschreitende Operationen',
            'ECOM-FINE-E': 'Plattformregeln und Transparenz',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Fehler bei der Bearbeitung von Behördenanfragen',
            'TELECOM-FINE-B': 'Vertraulichkeit und unbefugte Offenlegung',
            'TELECOM-FINE-C': 'Zugriffskontroll- und Protokollierungsfehler',
            'TELECOM-FINE-D': 'Verstöße gegen die Aufbewahrungs- und Löschvorschriften',
            'TELECOM-FINE-E': 'Fehler bei der Bewertung von Telekommunikationsvorfällen',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Bis zu 100 Millionen VND',
                fine_org: 'Bis zu 200 Millionen VND',
                deadlines: 'Sofort nach Erkennung',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Bis zu 100 Millionen VND',
                fine_org: 'Bis zu 200 Millionen VND',
                deadlines: 'Dauerhafte Verpflichtung',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Bis zu 100 Millionen VND',
                fine_org: 'Bis zu 200 Millionen VND',
                deadlines: 'Dauerhafte Verpflichtung',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Bis zu 100 Millionen VND',
                fine_org: 'Bis zu 200 Millionen VND',
                deadlines: 'Dauerhafte Verpflichtung',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Bis zu 100 Millionen VND',
                fine_org: 'Bis zu 200 Millionen VND',
                deadlines: 'Wie im Antrag der Behörde angegeben',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Bis zu 80 Millionen VND',
                fine_org: 'Bis zu 160 Millionen VND',
                deadlines: 'Dauerhafte Verpflichtung',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Bis zu 80 Millionen VND',
                fine_org: 'Bis zu 160 Millionen VND',
                deadlines: 'Dauerhafte Verpflichtung',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Bis zu 80 Millionen VND',
                fine_org: 'Bis zu 160 Millionen VND',
                deadlines: 'Dauerhafte Verpflichtung',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Bis zu 80 Millionen VND',
                fine_org: 'Bis zu 160 Millionen VND',
                deadlines: 'Gemäß den Pflichten zur Beschwerdebearbeitung',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Bis zu 80 Millionen VND',
                fine_org: 'Bis zu 160 Millionen VND',
                deadlines: 'Vor Beginn des E-Commerce-Betriebs',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Laufender Betrieb der Plattform',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformbetreiber / Risiko',
                '2': 'Plattformbetreiber / Sicherheit',
                '3': 'Plattformbetreiber / Risiko',
                '4': 'Plattformbetreiber / Sicherheit',
                '5': 'Plattformbetreiber',
                '6': 'Compliance',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Ein Teilnehmer reicht eine Beschwerde oder einen Streitbeilegungsantrag ein',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformbetreiber',
                '2': 'Plattformbetreiber',
                '3': 'Plattformbetreiber',
                '4': 'Plattformbetreiber',
                '5': 'Plattformbetreiber / Recht',
                '6': 'Plattformbetreiber',
                '7': 'Compliance',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Die Lieferung erfolgt nach Erfüllung der Vertrags- und Zahlungsbedingungen',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Verkäufer / Plattformbetreiber',
                '2': 'Plattformbetreiber',
                '3': 'Plattformbetreiber',
                '4': 'Plattformbetreiber',
                '5': 'Plattformbetreiber',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Ein Käufer initiiert eine Transaktion für Daten, Produkte oder Dienstleistungen',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Käufer',
                '2': 'Käufer / Plattformbetreiber',
                '3': 'Plattformbetreiber',
                '4': 'Käufer / Verkäufer',
                '5': 'Plattformbetreiber / Zahlung',
                '6': 'Plattformbetreiber',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'Ein Teilnehmer möchte Datenprodukte/-dienste in der isolierten Umgebung bewerten',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Teilnehmer',
                '2': 'Plattformbetreiber / Teilnehmer',
                '3': 'Plattformbetreiber / IT',
                '4': 'Plattformbetreiber / Sicherheit',
                '5': 'Plattformbetreiber',
                '6': 'Plattformbetreiber',
                '7': 'Plattformbetreiber / IT',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Ein Verkäufer möchte Daten, Produkte oder Datendienste zum Handel anbieten',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Verkäufer',
                '2': 'Verkäufer/Rechtsanwalt',
                '3': 'Verkäufer / IT',
                '4': 'Verkäufer',
                '5': 'Verkäufer',
                '6': 'Plattformbetreiber',
                '7': 'Plattformbetreiber',
                '8': 'Plattformbetreiber',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'Ein Verkäufer oder Käufer tritt der Datenplattform bei',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformbetreiber',
                '2': 'Plattformbetreiber / Recht',
                '3': 'Plattformbetreiber / Recht',
                '4': 'Plattformbetreiber',
                '5': 'Plattformbetreiber',
                '6': 'Plattformbetreiber',
                '7': 'Compliance',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Beginn der Verarbeitung personenbezogener Daten, die eine Folgenabschätzung erfordert',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Datenschutzteam',
                '2': 'Datenschutzteam',
                '3': 'Recht / Compliance',
                '4': 'DPO / Datenschutzteam',
                '5': 'Compliance-Team',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Absicht, personenbezogene Daten außerhalb Vietnams zu übertragen',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Datenschutzteam',
                '2': 'Recht / Compliance',
                '3': 'DPO / Datenschutzteam',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organisation, die zertifizierungspflichtige Datenverarbeitungsdienste anbietet',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Recht / Compliance',
                '2': 'Recht / Compliance',
                '3': 'DPO / Datenschutzteam',
                '4': 'Compliance-Team',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Eingang einer Anfrage zu den Rechten der betroffenen Person',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Datenschutzteam / Kundenservice',
                '2': 'DPO / Datenschutzteam',
                '3': 'Abteilungsleiter / IT',
                '4': 'DPO / Datenschutzteam',
                '5': 'Compliance-Team',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Feststellung einer Verletzung oder Verletzung des Schutzes personenbezogener Daten',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Incident Response Team / DPO',
                '2': 'DPO / Datenschutzteam',
                '3': 'Recht / Compliance',
                '4': 'DPO / Incident Response Team',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Dekret 13 Art. 24',
        'MoPS Admin Procedures': 'MoPS-Administrationsverfahren',
        'Annual cycle': 'Jahreszyklus',
        'Decree 13/2023/NĐ-CP Art. 24': 'Dekret 13/2023/NĐ-CPĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Entwurf eines Dekrets über Datenplattformen 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Gesetz 122/2025/QH15 Kap. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Gesetz 122/2025/QH15 Kap. IV (Verbraucherschutz)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Gesetz 122/2025/QH15 – Online.gov.vn Betriebsverfahren',
        'Law 134/2025/QH15 Article 12': 'Gesetz 134/2025/QH15 Artikel 12',
        'Law 134/2025/QH15 Article 21': 'Gesetz 134/2025/QH15 Artikel 21',
        'Law 134/2025/QH15 Article 27': 'Gesetz 134/2025/QH15 Artikel 27',
        'Law 134/2025/QH15 Articles 9–10': 'Gesetz 134/2025/QH15 Artikel 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Gesetz 24/2023/QH15 – Artikelzuordnung steht aus',
        'Law 60/2024/QH15 (internal control target)': 'Gesetz 60/2024/QH15 (internes Kontrollziel)',
        'Law 91/2025/QH15 Art. 21': 'Gesetz 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Gesetz 91/2025/QH15 Art. 23',
        'Policy-defined': 'Richtliniendefiniert',
        Quarterly: 'Vierteljährlich',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 Verwaltungsverfahren',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'internes Kontrollziel (Gesetz 116/2025/QH15 Art. 24(2)(b), 41(5) – genauer Zeitplan bis zur Umsetzung des Instruments)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'internes Kontrollziel (Gesetz 116/2025/QH15 Art. 41(3) – genauer Zeitplan bis zur Umsetzung des Instruments)',
        'source verification pending': 'Quellenüberprüfung steht noch aus',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad-hoc-Berichterstattung der Internen Revision an die SBV (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Jährlicher ICAAP-Bericht (Phụ lục III) (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Jährliches Phụ lục I (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Jährliches Phụ lục IV (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Jährlicher Risikomanagementbericht (Phụ lục II) (Layer 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Verantwortung für das Asset-Management (Layer 2, theme C)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": 'Circular 83/2025/TT-NHNN — Aufsicht durch Vorstand / Mitgliederbeirat (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Vorstandsausschüsse (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Vorstandsbeschlüsse und Protokolle (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Kontrollverfahren für Niederlassungen (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance-Bewertung interner Regeln (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance-Funktion (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Überwachung von Compliance-Nachbesserungen (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Konzentrationsrisiko (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Interessenkonflikte (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Kontrollkultur (Layer 2, theme A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Kreditrisiko (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegierte Entscheidungsbefugnisse (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Mitteilung über vorzeitige Umsetzung (Layer 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Verantwortung des Generaldirektors (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Zinsänderungsrisiko im Bankbuch (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Revisionsordnung der Internen Revision (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Prüfungsmandate der Internen Revision (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Kommunikation von Prüfungsfeststellungen der Internen Revision (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Feststellungen der Internen Revision und Korrekturmaßnahmen (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Nachverfolgung der Internen Revision (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Governance der Internen Revision',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Unabhängigkeit der Internen Revision (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Prüfungsplan der Internen Revision (Layer 2/Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Berichterstattung der Internen Revision an die SBV (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Governance des Internen Kontrollsystems (Layer 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Verfahren für interne Berichterstattung (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register interner Berichte (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Überwachung von Rechtsänderungen (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Rechts- und Reputationsrisiko (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Eskalation bei Limitverletzungen (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Liquiditätsrisiko (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Jährliche MIS-Überprüfung (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-Backup und -Kontinuität (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-Eskalationsmechanismen (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-Zuverlässigkeit und Vertraulichkeit (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-Risikodatenquellen (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Management-Informationssystem (Layer 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Marktrisiko (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Register wesentlicher Risiken (Layer 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Modellrisiko (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Prüfung neuer Produkte (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operationelles Risiko (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Business-Continuity-Planung für Outsourcing (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing-Verträge (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Kontrollen für Outsourcing',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Sorgfaltsprüfung für Outsourcing (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Ersatzanbieter für Outsourcing (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Wesentlichkeit von Outsourcing (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Überwachung von Outsourcing (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Technische Referenzen Phụ lục V/VI (Layer 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Richtlinienregister (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Berufsethik (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risikoappetit (Layer 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Koordination des Risikoausschusses (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Governance von Risikodaten (Layer 3, theme E/F) — gestaffelt bis 2028-01-01',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Überprüfung von Risikolimits (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Rahmenwerk für Risikolimits (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikomanagement-Funktion (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikomethodik und Modelle (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikoberichterstattung (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Taktung der Risikoberichterstattung (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — Einreichung von Nachbesserungen an die SBV (Layer 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — Einreichungskanäle zur SBV (Layer 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sektorale Anwendbarkeit (Layer 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Funktionstrennung (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Aufsicht durch das Senior Management (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategisches Risiko (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stresstests (Layer 3, theme E) — gestaffelt bis 2028-01-01',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Aufsichtsrat (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register aufsichtsrechtlicher Feststellungen (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Rollen und Eskalation im Drei-Linien-Modell (Layer 2, theme B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Drei-Linien-Modell (Layer 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Eskalation bei Verstößen (Layer 2, theme D)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Nationalversammlung)',
        'Ministry of Public Security / A05 Department': 'Ministerium für öffentliche Sicherheit / Abteilung A05',
        'Ministry of Public Security': 'Ministerium für öffentliche Sicherheit',
        'Ministry of Information and Communications': 'Ministerium für Information und Kommunikation',
        'Government of Vietnam': 'Regierung von Vietnam',
        'European Data Protection Board (EDPB)': 'Europäischer Datenschutzausschuss (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / Nationale CSIRTs',
        'State Bank of Vietnam': 'Staatsbank von Vietnam',
    },
    tour_titles: {
        access_accountability: 'Nehmen Sie an der Accountability-Tour teil',
        assessments: 'Bewertungstour',
        audit_trail: 'Audit-Trail-Tour',
        classification: 'Klassifizierungstour',
        compliance_forms: 'Tour zu Compliance-Formularen',
        connectors: 'Steckverbinder-Tour',
        consent_properties: 'Consent-Eigenschaften-Tour',
        consent: 'Einwilligungstour',
        dashboard: 'Dashboard-Tour',
        data_mapping: 'Daten-Mapping-Tour',
        data_subjects: 'Tour zu betroffenen Personen',
        deletion_orchestration: 'Tour zur Löschorchestrierung',
        discovery: 'Entdeckungstour',
        governance: 'Governance-Tour',
        help_center: 'Hilfe-Center-Tour',
        incident_ops: 'Incident Operations Tour',
        incidents: 'Tour zu Vorfällen',
        localization: 'Lokalisierungstour',
        monitoring: 'Überwachungstour',
        onboarding: 'Onboarding-Tour',
        organizations: 'Organisationstour',
        partner_workspace: 'Partner-Workspace-Tour',
        platform_settings: 'Tour zu den Plattformeinstellungen',
        privacy_scanner: 'Privatsphäre-Scanner-Tour',
        program_governance: 'Programm-Governance-Tour',
        rights_requests: 'Tour zu Rechteanfragen',
        task_management: 'Aufgabenverwaltungstour',
        vendor_risk: 'Anbieter-Risikotour',
    },
    tour_descriptions: {
        access_accountability: 'Überprüfen Sie als Leiter der Internen Revision (P-VN-10) Zugriffsnachweise, prüfen Sie die Nutzung von Berechtigungen und untersuchen Sie anomale Muster, um die Integrität und Vertraulichkeit von GDPR Artikel 5(1)(f) nachzuweisen.',
        assessments: 'Führen Sie als Unternehmen DPO (P-VN-01) DPIAs gemäß Dekret 356 Mau so 02a durch, identifizieren Sie Compliance-Lücken und bewerten Sie grenzüberschreitende Übertragungsrisiken gemäß PDPL.',
        audit_trail: 'Fragen Sie als Leiter der internen Revision (P-VN-10) modulübergreifende Beweise ab, stellen Sie Prüfpakete zusammen und exportieren Sie manipulationssichere Protokolle für behördliche Einreichungen.',
        classification: 'Definieren Sie als Datenverwalter (P-VN-05) Datenklassifizierungstaxonomien, weisen Sie Vertraulichkeitsbezeichnungen zu, setzen Sie Aufbewahrungsrichtlinien durch und schließen Sie Abdeckungslücken, die für die Einhaltung von PDPL erforderlich sind.',
        compliance_forms: 'Durchsuchen, vervollständigen und übermitteln Sie als Unternehmen DPO (P-VN-01) die offiziellen vietnamesischen Regulierungsformulare, einschließlich Phu luc I, Phu luc II und Mau so-Vorlagen für PDP-Komitee-Einreichungen.',
        connectors: 'Erfassen Sie als IT-Systemleiter (P-VN-05) Geschäftsanforderungen für Integrationen, ordnen Sie Konnektoren den Compliance-Datenflüssen zu und überprüfen Sie die POC-Bereitschaft, bevor Sie in Betrieb gehen.',
        consent_properties: 'Konfigurieren Sie als Unternehmen DPO (P-VN-01) die Bereitstellung von CMP: digitale Eigentumsregistrierung, goldene Vorlagen für konsistente PDPL Einwilligungsbanner und verbindliche Matrizen, die Eigenschaften mit Verarbeitungsaktivitäten verknüpfen.',
        consent: 'Als Unternehmen DPO (P-VN-01) verwalten Sie den Lebenszyklus der Einwilligungsrichtlinie: Definieren Sie Erfassungszwecke, setzen Sie PDPL Widerrufsrechte durch und integrieren Sie sie in Ihre Consent Management Platform.',
        dashboard: 'Orientieren Sie sich als Unternehmen DPO (P-VN-01) an der Compliance-Positions-Kommandozentrale, die PDPL Fristen, Verstöße gegen SLAs und Audit-Bereitschafts-KPIs anzeigt.',
        data_mapping: 'Erstellen Sie als Datenverwalter (P-VN-05) Ihr ROPA-Inventar, bilden Sie grenzüberschreitende Datenflüsse ab und führen Sie das gemäß PDPL erforderliche Verarbeitungsaktivitätsregister.',
        data_subjects: 'Als Enterprise DPO oder Education Compliance Lead verwalten Sie einheitliche Profile betroffener Personen, die Einwilligungsdatensätze, Rechteanfragen und Zeitleistenaktivitäten auf der gesamten Plattform verknüpfen.',
        deletion_orchestration: 'Überprüfen Sie als DPO (P-VN-01) die durchgängige Datenlöschung über interne Systeme und Drittanbieter hinweg mit kryptografischen Beweisketten für die Einhaltung von GDPR Artikel 17 des Rechts auf Löschung.',
        discovery: 'Führen Sie als Datenverwalter (P-VN-05) automatisierte Scans durch, um personenbezogene Daten in verbundenen Systemen zu ermitteln, prüfen Sie bereitgestellte Ergebnisse und geben Sie die Ergebnisse an das Klassifizierungsmodul zur PDPL-Kennzeichnung weiter.',
        governance: 'Pflegen Sie als Vendor Risk Manager (P-VN-08) das Lieferantenregister, verfolgen Sie Unterauftragsverarbeiterketten, bewerten Sie grenzüberschreitende Transferrisiken und setzen Sie Governance-Playbooks gemäß PDPL durch.“',
        help_center: 'Für alle Personen – Datenschutzbeauftragte, Prüfer, IT-Leiter und Compliance-Manager – finden Sie Antworten, Anleitungen und modulspezifische Dokumentation zur Unterstützung Ihrer täglichen Compliance-Workflows.',
        incident_ops: 'Aktivieren Sie als Incident Commander (P-VN-09) Reaktions-Playbooks, verfolgen Sie die Einhaltung von SLA anhand des 72-Stunden-Benachrichtigungsfensters PDPL, verwalten Sie Eskalationen und koordinieren Sie die Reaktion des Anbieters.',
        incidents: 'Erkennen und melden Sie als Incident Commander (P-VN-09) Datenverstöße mit 72-Stunden-Benachrichtigungspflichten (PDPL) an das PDP-Komitee, Triage nach Schweregrad und erstellen Sie Vorfalldossiers in Audit-Qualität.',
        localization: 'Verwalten Sie als Unternehmen DPO (P-VN-01) den Lebenszyklus des Regulierungsrahmens: Installieren Sie vietnamesische Marktpakete, aktivieren Sie die Rahmen PDPL und Dekret 13, ordnen Sie Anforderungen den Plattformmodulen zu und verwalten Sie Übersetzungen.',
        monitoring: 'Richten Sie als Unternehmen DPO (P-VN-01) fortlaufende Compliance-Überwachungsprogramme ein, verfolgen Sie regulatorische Änderungen, messen Sie KPIs und verwalten Sie regulatorische Interaktionsfälle unter PDPL.',
        onboarding: 'Als Compliance-Trainer (P-VN-02) führen Sie Schulungen über geführte Touren durch, laden realistische Beispieldaten für praktische Übungen und führen den Einrichtungsassistenten für Schnellstartszenarien aus.',
        organizations: 'Konfigurieren Sie als DPO (P-VN-01) Compliance-Strukturen für mehrere Organisationen, richten Sie Abteilungshierarchien ein und leiten Sie Anfragen betroffener Personen innerhalb Ihrer Unternehmensgruppe weiter.',
        partner_workspace: 'Als Partner Account Manager binden Sie Partnerorganisationen ein, konfigurieren Serviceebenen, verfolgen Compliance-Ergebnisse und verwalten den Partnermarktplatz.',
        platform_settings: 'Als Plattformadministrator verwalten Sie die gesamte Betriebskonfiguration – Identität, Sicherheit, Integrationen und Modulzugriff, die Ihrem Compliance-Programm zugrunde liegen.',
        privacy_scanner: 'Führen Sie als Datenschutzanalyst (P-VN-06) kontinuierliche Überwachungsscans durch, um Verstöße gegen die Cookie-Einwilligung, nicht autorisierte Tracker und Richtlinienlücken zu erkennen und so Beweise für die Einhaltung von ePrivacy und GDPR zu generieren.',
        program_governance: 'Navigieren Sie zwischen Reifegradmodellen, Bewertungen, Evidenzlücken, Zusammenfassungen und mehr. Jedes Untermodul trägt zu einer ganzheitlichen Sicht auf den Zustand Ihres internen Programms bei.',
        rights_requests: 'Als Unternehmen DPO (P-VN-01) verwalten Sie Anträge auf Rechte betroffener Personen gemäß PDPL mit gesetzlichen Fristen von 15 Tagen, Identitätsüberprüfung und Lösungspfaden auf Prüfniveau.',
        task_management: 'Leiten Sie Compliance-Aufgaben über Personas hinweg, verfolgen Sie SLA Fristen und automatisieren Sie Arbeitsabläufe, die Ihr Team zur Verantwortung ziehen.',
        vendor_risk: 'Bewerten Sie als Vendor Risk Manager (P-VN-08) Risiken Dritter, erzwingen Sie Gate-Reviews und veranlassen Sie Neubewertungen – im Gegensatz zur Program Governance, die Ihre interne Reife verfolgt.',
    },
    consolidation: {
        title: 'Konsolidierungsfaelle',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Konsolidierungsfall erstellen',
        case_count: 'case',
        created: 'Konsolidierungsfall erstellt',
        create: 'Fall erstellen',
        creating: 'Erstellen...',
        cancel: 'Abbrechen',
        no_cases: 'Keine Konsolidierungsfälle gefunden.',
        all_statuses: 'Alle Status',
        all_purposes: 'Alle Zwecke',
        filter_status: 'Nach Status filtern',
        filter_purpose: 'Nach Zweck filtern',
        col_title: 'Titel',
        col_purpose: 'Zweck',
        col_status: 'Status',
        col_progress: 'Fortschritt',
        col_deadline: 'Frist',
        col_department: 'Abteilung',
        col_version: 'Version',
        col_evidence: 'Beweise',
        sections_accepted: 'accepted',
        prev: 'Zurück',
        next: 'Als nächstes',
        page: 'Seite',
        prev_page: 'Vorherige Seite',
        next_page: 'Nächste Seite',
        field_title: 'Titel',
        field_purpose: 'Zweck',
        field_framework: 'Rahmencode',
        field_deadline: 'Frist',
        field_reviewer: 'Benutzer-ID des Prüfers',
        title_placeholder: 'Geben Sie den Falltitel ein',
        framework_placeholder: 'z. B. PDPD-2023',
        reviewer_placeholder: 'Geben Sie die Benutzer-ID des Prüfers ein',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Einreichung',
            cross_border_filing: 'Grenzüberschreitende Einreichung',
            annual_governance_attestation: 'Jährliche Governance-Bescheinigung',
            breach_package: 'Verstoßpaket',
            audit_inspection_pack: 'Audit-Inspektionspaket',
        },
        statuses: {
            draft: 'Entwurf',
            collecting: 'Sammeln',
            consolidating: 'Konsolidierung',
            review: 'Rezension',
            corrections: 'Korrekturen',
            pending_approval: 'Ausstehende Genehmigung',
            approved: 'Genehmigt',
            completed: 'Abgeschlossen',
            closed: 'Geschlossen',
            rejected: 'Abgelehnt',
        },
        section_statuses: {
            pending: 'Ausstehend',
            in_progress: 'In Bearbeitung',
            submitted: 'Eingereicht',
            accepted: 'Akzeptiert',
            needs_correction: 'Muss korrigiert werden',
            corrected: 'Korrigiert',
        },
        flag_types: {
            missing_section: 'Fehlender Abschnitt',
            conflicting_entry: 'Widersprüchlicher Eintrag',
            stale_entry: 'Veralteter Eintrag',
            missing_evidence: 'Fehlende Beweise',
            incomplete_data: 'Unvollständige Daten',
            duplicate_entry: 'Doppelter Eintrag',
        },
        flag_severities: {
            critical: 'Kritisch',
            warning: 'Warnung',
            info: 'Infos',
        },
        sections_title: 'Abschnitte',
        no_sections: 'Noch keine Abschnitte zugewiesen.',
        flags_title: 'Flaggen',
        no_flags: 'Keine Flags erkannt.',
        open: 'open',
        approval_chain: 'Genehmigungskette',
        pending: 'Ausstehend',
        audit_trail: 'Prüfpfad',
        no_audit: 'Keine Prüfereignisse.',
        auto_assign: 'Abschnitte automatisch zuweisen',
        start_collection: 'Sammlung starten',
        review_case: 'Fall überprüfen',
        generate_export: 'Export generieren',
        review_title: 'Konsolidierungsüberprüfung',
        advance_approval: 'Vorab zur Genehmigung',
        back_to_case: 'Zurück zum Fall',
        accept: 'Akzeptiere',
        return_section: 'Rückkehr',
        return_comment: 'Kommentar zurückgeben...',
        resolve: 'Lösen',
        dismiss: 'Entlassen',
        resolution_notes: 'Lösungshinweise...',
        notes: 'Notizen',
        section_accepted: 'Abschnitt akzeptiert',
        section_returned: 'Abschnitt zur Korrektur zurückgegeben',
        advanced_to_approval: 'Der Fall wurde zur Genehmigung weitergeleitet',
        section_data: 'Abschnittsdaten',
        save_progress: 'Fortschritt speichern',
        submit_section: 'Abschnitt „Senden“.',
        submitting: 'Einreichen...',
        resubmit: 'Erneut einreichen',
        progress_saved: 'Fortschritt gespeichert',
        section_submitted: 'Abschnitt eingereicht',
        correction_required: 'Korrektur erforderlich',
        evidence_title: 'Beweise',
        no_evidence: 'Keine Beweise beigefügt.',
        add_evidence: 'Beweise hinzufügen',
        evidence_added: 'Beweise hinzugefügt',
        evidence_files: 'Beweisakten',
        uploaded_by: 'hochgeladen von',
        comments_title: 'Kommentare',
        no_comments: 'Noch keine Kommentare.',
        comment_placeholder: 'Kommentar hinzufügen...',
        add_comment: 'Kommentar hinzufügen',
        comment_added: 'Kommentar hinzugefügt',
        view_data: 'Eingereichte Daten anzeigen',
        export_title: 'Dossier-Export',
        export_generated: 'Exportpaket generiert',
        regenerate: 'Regenerieren',
        regenerating: 'Regenerierend...',
        generated_at: 'Erstellt am',
        artifacts_title: 'Artefakte exportieren',
        artifact_type: 'Artefakt',
        file_name: 'Dateiname',
        file_size: 'Größe',
        generated: 'Generiert',
        download: 'Herunterladen',
        artifacts: {
            master_report: 'Masterbericht',
            evidence_bundle: 'Beweisbündel (ZIP)',
            contributor_lineage: 'Abstammung des Mitwirkenden',
            conflict_resolution: 'Konfliktlösung',
            approval_history: 'Genehmigungshistorie',
        },
        official_forms: 'Offizielle Formulare',
        forms_description: 'Aus den konsolidierten Daten können folgende amtliche Formulare generiert werden. Offizielle Formulare sind von internen Arbeitspapieren getrennt.',
        announce_loaded_cases: 'Geladene {count} Konsolidierungsfälle',
        announce_case_detail_loaded: 'Falldetail geladen',
        announce_case_transitioned: 'Fall wurde auf {status} umgestellt',
        announce_flag_resolved: 'Flagge gelöst',
        announce_flag_dismissed: 'Flagge abgewiesen',
        case_detail_loaded: 'Falldetail geladen',
        sections_auto_assigned: 'Abschnitte werden automatisch aus der Abteilungshierarchie zugewiesen',
        errors: {
            load_failed: 'Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
            create_failed: 'Fall konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
            required_fields: 'Bitte füllen Sie alle erforderlichen Felder aus.',
            not_found: 'Nicht gefunden.',
            comment_required: 'Bei der Rückgabe eines Abschnitts ist ein Kommentar erforderlich.',
            notes_required: 'Beim Lösen oder Verwerfen einer Meldung sind Notizen erforderlich.',
        },
    },
    rights_requests: {
        import: {
            title: 'Fälle importieren',
            nav_title: 'Massenimport',
            description: 'Laden Sie eine CSV-Datei hoch, um Rechtsanfragefälle in Massen zu importieren.',
            select_file: 'CSV-Datei auswählen',
            file_help: 'CSV mit Überschriften, die den Fallfeldern entsprechen.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Datei zu groß. Maximale Größe ist 2MB.',
            preview: 'Vorschau',
            columns_detected: 'Erkannte Spalten: {count}',
            column_mapping: 'Spaltenzuordnung',
            import_button: 'Importieren',
            import_complete: 'Import abgeschlossen',
            import_error: 'Import fehlgeschlagen',
            result: 'Importergebnis',
            total: 'Gesamt',
            imported: 'Importiert',
            skipped: 'Übersprungen',
            failed: 'Fehlgeschlagen',
            errors: 'Fehler',
            error_row: 'Zeile',
            error_field: 'Feld',
            error_message: 'Nachricht',
            history: 'Importverlauf',
            no_history: 'Kein Importverlauf.',
            history_file: 'Dateiname',
            history_date: 'Datum',
        },
        batch: {
            nav_title: 'Batch-Prozess',
            page_title: 'DSR-Batch-Prozess',
            page_subtitle: 'Laden Sie eine CSV ausstehender DSRs hoch. Forseti entwirft eine Bestätigungsantwort pro Anfrage und stellt pro Zeile eine Überprüfungsaufgabe in die Warteschlange. Der DPO überprüft und akzeptiert jeden vor dem Senden.',
            upload_heading: 'Hochladen',
            csv_hint: 'Erforderliche Spalten: request_id, requester_email, request_type. Optional: Frist (ISO-8601), Notizen.',
            rows_heading: '{count} Zeilen analysiert',
            submit_label: 'Batch ausführen',
            submitting: 'Ausarbeitung…',
            col_request_id: 'Anfrage-ID',
            col_email: 'E-Mail des Antragstellers',
            col_type: 'Typ',
            col_status: 'Status',
            col_actions: 'Aktionen',
            status_queued: 'In der Warteschlange',
            status_drafted: 'Ausgearbeitet',
            status_failed: 'Fehlgeschlagen',
            status_accepted: 'Akzeptiert',
            status_rejected: 'Abgelehnt',
            status_parsing_error: 'Parse-Fehler',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Akzeptiere',
            reject: 'Ablehnen',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Bewertungsauslöser',
        subtitle: 'Konfigurieren Sie die automatische Erstellung von Bewertungen und DPO-Benachrichtigungen für Datenschutz-Workflows.',
        auto_create_dpia_label: 'DPIA automatisch erstellen, wenn die Verarbeitung einer Aktivität erforderlich ist, die als DPIA markiert ist',
        auto_notify_new_purpose_label: 'Automatische Benachrichtigung DPO, wenn ein neuer Zweck mit sensiblen Datenkategorien erstellt wird',
        auto_notify_cross_border_label: 'Automatische Benachrichtigung DPO, wenn grenzüberschreitende Übertragung zur Verarbeitungsaktivität hinzugefügt wird',
        default_dpo_label: 'Standard DPO',
        default_dpo_placeholder: 'Wählen Sie den Standard DPO...',
        notification_channels_label: 'Benachrichtigungskanäle',
        channel_in_app: 'In-App',
        channel_email: 'Email',
        save_success: 'Einstellungen für Bewertungsauslöser gespeichert.',
        save_error: 'Die Einstellungen für den Bewertungsauslöser konnten nicht gespeichert werden.',
        fetch_error: 'Die Einstellungen für den Bewertungsauslöser konnten nicht geladen werden.',
        fetch_users_error: 'Benutzer konnten nicht geladen werden.',
        announce_loaded: 'Einstellungen für Bewertungsauslöser geladen',
        dpia_status: 'DPIA Status',
        dpia_linked: 'DPIA Verknüpft',
        dpia_required_no_dpia: 'DPIA Erforderlich – nicht erstellt',
        create_dpia: 'Erstellen Sie DPIA',
        create_dpia_success: 'DPIA erstellt und mit Aktivität verknüpft.',
        create_dpia_error: 'DPIA für Aktivität konnte nicht erstellt werden.',
        view_dpia: 'Anzeigen DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Profile betroffener Personen',
        nav_title: 'Betroffene Personen',
        nav: {
            profiles: 'Profile',
        },
        create: 'Profil erstellen',
        edit: 'Profil bearbeiten',
        delete: 'Profil löschen',
        delete_confirm: 'Sind Sie sicher, dass Sie dieses Profil der betroffenen Person löschen möchten?',
        display_name: 'Anzeigename',
        email: 'Email',
        phone: 'Telefon',
        employee_id: 'Mitarbeiter-ID',
        student_id: 'Studentenausweis',
        notes: 'Notizen',
        external_refs: 'Externe Referenzen',
        cases_count: 'Fälle',
        workflows_count: 'Einwilligungsaufzeichnungen',
        last_activity: 'Letzte Aktivität',
        timeline: 'Zeitleiste',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Es wurden keine Profile der betroffenen Personen gefunden.',
        search_placeholder: 'Suchen Sie nach Name, E-Mail, Telefon oder ID ...',
        create_success: 'Betroffenenprofil erstellt.',
        create_error: 'Das Profil der betroffenen Person konnte nicht erstellt werden.',
        update_success: 'Profil der betroffenen Person aktualisiert.',
        update_error: 'Das Profil der betroffenen Person konnte nicht aktualisiert werden.',
        delete_success: 'Betroffenenprofil gelöscht.',
        delete_error: 'Das Profil der betroffenen Person konnte nicht gelöscht werden.',
        link_case_success: 'Fall mit Profil verknüpft.',
        link_case_error: 'Fall konnte nicht mit Profil verknüpft werden.',
        link_workflow_success: 'Mit dem Profil verknüpfter Workflow.',
        link_workflow_error: 'Workflow konnte nicht mit Profil verknüpft werden.',
        load_error: 'Das Laden der Profile der betroffenen Personen ist fehlgeschlagen.',
        detail_load_error: 'Das Profil der betroffenen Person konnte nicht geladen werden.',
        timeline_load_error: 'Zeitleiste konnte nicht geladen werden.',
        tab_cases: 'DSR-Fälle',
        tab_consent: 'Einwilligungsaufzeichnungen',
        tab_timeline: 'Zeitleiste',
        tab_consent_workflows: 'Einwilligungsworkflows',
        tab_web_consent: 'CMP Einwilligung',
        web_consent_empty: 'Für dieses Thema wurden keine CMP Einwilligungsdatensätze gefunden.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'CMP Einwilligungsdatensätze konnten nicht geladen werden.',
        col_policy_version: 'Richtlinienversion',
        col_versions: 'Versionen',
        col_consent_version: 'Zustimmung',
        col_cookie_declaration_version: 'Cookie-Erklärung',
        col_purposes: 'Zwecke',
        purpose_granted: 'Zugegeben',
        purpose_denied: 'Abgelehnt',
        granted_domains: 'Zugegeben',
        denied_domains: 'Abgelehnt',
        col_action: 'Aktion',
        col_timestamp: 'Zeitstempel',
        col_domain: 'Domäne',
        no_cases: 'Keine verknüpften Fälle.',
        no_workflows: 'Keine verknüpften Einwilligungsworkflows.',
        no_timeline: 'Keine Timeline-Ereignisse.',
        case_id: 'Fall-ID',
        case_status: 'Status',
        case_type: 'Anfragetyp',
        case_created: 'Erstellt',
        workflow_id: 'Workflow-ID',
        workflow_type: 'Typ',
        workflow_status: 'Status',
        workflow_created: 'Erstellt',
        timeline_date: 'Datum',
        timeline_event: 'Veranstaltung',
        timeline_details: 'Einzelheiten',
        timeline_time: 'Zeit',
        timeline_type: 'Typ',
        timeline_summary: 'Zusammenfassung',
        type_case: 'DSR-Gehäuse',
        type_consent_workflow: 'Einwilligungsworkflow',
        profile_info: 'Profilinformationen',
        announce_loaded: 'Profile der betroffenen Personen geladen',
        announce_detail_loaded: 'Profil der betroffenen Person geladen',
        select_case: '— Fall auswählen —',
        select_workflow: '— Workflow auswählen —',
        no_cases_hint: 'Verknüpfen Sie Fälle, um sie hier zu sehen.',
        no_workflows_hint: 'Verknüpfen Sie Einwilligungs-Workflows, um sie hier zu sehen.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} Systeme · {flows} Datenflüsse · {crossBorder} grenzüberschreitend · {highRisk} hohes/kritisches Risiko',
            switch_to_list: 'Zur Listenansicht wechseln',
            switch_to_list_aria: 'Zur Tabellenansicht wechseln',
            svg_aria: 'Datenflusskarte: {systems} Systeme, {flows} Datenflüsse, {crossBorder} grenzüberschreitend',
            selected_system: 'Ausgewähltes System',
            drill_hint: 'Öffnen Sie den Reiter „Liste", um verknüpfte Datenflüsse, DSFAs und Dienstleisterzuordnungen für dieses System zu bearbeiten.',
        },
    },
    packages: {
        light: {
            name: 'Licht',
            description: 'Wesentliche Datenschutz-Compliance-Tools für kleine Teams',
        },
        standard: {
            name: 'Standard',
            description: 'Voll ausgestattete Compliance-Plattform für wachsende Organisationen',
        },
        enterprise_unlimited: {
            name: 'Enterprise Unlimited',
            description: 'Compliance auf Enterprise-Niveau mit Multi-Org-Unterstützung und dedizierten Diensten',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Anbietereinstellungen',
            subtitle: 'Konfigurieren Sie den Claude-Anbieter für den Forseti AI Legal Advisor.',
            healthHeading: 'Gesundheit des Anbieters',
            statusLabel: 'Status',
            statusOk: 'Okay',
            statusFailed: 'Nicht verfügbar',
            providerLabel: 'Anbieter',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI-Relais',
            providerAesirManaged: 'Von Aesir verwaltete Token',
            lastCheckedLabel: 'Zuletzt überprüft',
            reasonLabel: 'Grund',
            refreshButton: 'Aktualisieren',
            apiKeyHeading: 'Claude API-Taste',
            apiKeyHint: 'Das Setzen einer API-Taste aktiviert den Claude-API-Modus. Lassen Sie die Option deaktiviert, um das lokale CLI-Relay zu verwenden (Entwickler-/selbstgehosteter Modus).',
            apiKeyLabel: 'API-Taste',
            apiKeyMinLength: 'Mindestens 32 Zeichen. Im Ruhezustand verschlüsselt; nie wieder gezeigt.',
            apiKeyTooShort: 'Der Schlüssel API muss mindestens 32 Zeichen lang sein.',
            apiKeySaved: 'API Schlüssel gespeichert. Erfrischende Gesundheit…',
            apiKeyCleared: 'API-Taste gelöscht. Forseti verwendet jetzt das lokale CLI-Relay.',
            setKeyButton: 'Speichern Sie die Taste API.',
            clearKeyButton: 'Taste API löschen (CLI-Relais verwenden)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Nutzung',
            recentHeading: 'Aktuelle Aktivität',
            historyHeading: 'Letzte 12 Perioden',
            noActivity: 'In diesem Zeitraum noch keine Aktivität.',
            featureLabel: 'Funktion',
            whenLabel: 'Wann',
            periodLabel: 'Zeitraum',
            byFeatureHeading: 'Diesen Monat nach Feature',
            byFeatureEmpty: 'In diesem Zeitraum noch keine Feature-Aktivität.',
            featureColumn: 'Funktion',
            featureUnitsColumn: 'Einheiten',
            featureRunsColumn: 'Läuft',
            byUserHeading: 'Diesen Monat nach Benutzer (Administratoransicht)',
            byUserEmpty: 'In diesem Zeitraum noch keine Aktivität pro Benutzer.',
            userColumn: 'Benutzer',
            reconciliationLinkLabel: 'Bericht zur Abstimmungsdrift anzeigen',
            reconciliationLinkHint: 'Nur Aesir-interner Administrator.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Chatten',
                workflow: 'Workflow-Ausführung',
                tabular: 'Tabellarische Extraktion',
                redline: 'Redline-Rezension',
                title: 'Gesprächstitel',
                unknown: 'Andere',
            },
        },
        reconciliation: {
            title: 'Forseti-Quotenabstimmung',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Zurück zur Forseti-Nutzung',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Diese Seite ist auf Aesir-interne Administratoren beschränkt. Wenn Sie der Meinung sind, dass Sie Zugriff haben sollten, wenden Sie sich an Ihren AesirX-Vertreter.',
        },
        projects: {
            title: 'Projekte',
            subtitle: 'Arbeitsbereiche für Dokumente, Gespräche, Arbeitsabläufe und Überprüfungen.',
            modeLabel: 'Ansicht',
            tabMine: 'Meine Projekte',
            tabShared: 'Mit mir geteilt',
            searchLabel: 'Projekte suchen',
            searchPlaceholder: 'Suchen…',
            newButton: 'Neues Projekt',
            colName: 'Name',
            colVisibility: 'Sichtbarkeit',
            colDocs: 'Dokumente',
            colMembers: 'Mitglieder',
            colUpdated: 'Aktualisiert',
            visibilityOwn: 'Nur besitzen',
            visibilityShared: 'Geteilt',
            emptyMine: 'Noch keine Projekte. Erstellen Sie eines, um loszulegen.',
            emptyShared: 'Es wurden keine Projekte mit Ihnen geteilt.',
            pageHint: 'Zeigt {shown} von {total}.',
            createTitle: 'Neues Projekt',
            createSubmit: 'Projekt erstellen',
            fieldName: 'Name',
            fieldDescription: 'Beschreibung (optional)',
            fieldCmNumber: 'Fall-/Angelegenheitsnummer (optional)',
            fieldVisibility: 'Sichtbarkeit',
            errorNameRequired: 'Name ist erforderlich.',
            errorSelectorRequired: 'Wählen Sie einen Benutzer oder eine Organisationsgruppe aus, die Sie hinzufügen möchten.',
            renameButton: 'Umbenennen',
            renamePrompt: 'Projekt umbenennen',
            deleteButton: 'Löschen',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Diesen Ordner löschen? Die darin enthaltenen Dokumente bleiben im Projekt und werden lediglich nicht abgelegt.',
            removeMemberConfirm: 'Dieses Mitglied entfernen?',
            foldersHeading: 'Ordner',
            foldersEmpty: 'Noch keine Ordner.',
            addFolderButton: 'Neuer Ordner',
            addFolderTitle: 'Neuer Ordner',
            documentsHeading: 'Dokumente',
            documentsEmpty: 'Noch keine Dokumente beigefügt.',
            colFilename: 'Dateiname',
            colAdded: 'Hinzugefügt',
            unattachButton: 'Trennen',
            membersHeading: 'Mitglieder',
            membersEmpty: 'Nur Eigentümer.',
            addMemberButton: 'Mitglied hinzufügen',
            addMemberTitle: 'Mitglied hinzufügen',
            addMemberSubmit: 'Mitglied hinzufügen',
            colMember: 'Mitglied',
            colRole: 'Rolle',
            selectorTypeLabel: 'Hinzufügen von',
            selectorUser: 'Benutzer-ID',
            selectorGroup: 'Organisationsgruppen-ID',
            fieldUserId: 'Benutzer',
            fieldGroupId: 'Org-Gruppe',
            fieldRole: 'Rolle',
            roleAdmin: 'Admin',
            roleEditor: 'Herausgeber',
            roleViewer: 'Zuschauer',
            notFound: 'Projekt nicht gefunden.',
            selectUserPlaceholder: 'Wählen Sie einen Benutzer aus…',
            selectGroupPlaceholder: 'Wählen Sie eine Organisationsgruppe aus…',
            noUsersAvailable: 'Keine Benutzer verfügbar.',
            noGroupsAvailable: 'Keine Organisationsgruppen verfügbar.',
            loadFailed: 'Optionen konnten nicht geladen werden.',
            deleteProjectTitle: 'Projekt löschen?',
            deleteFolderTitle: 'Ordner löschen?',
            removeMemberTitle: 'Mitglied entfernen?',
            removeMemberConfirmGeneric: 'Dieses Mitglied aus dem Projekt entfernen?',
            addDocumentButton: 'Dokument hinzufügen',
            addDocumentTitle: 'Dokument zum Projekt hinzufügen',
            fieldDocument: 'Dokument auswählen',
            selectDocPlaceholder: 'Nach einem Dokument suchen...',
            ownerLabel: 'Systemeigentümer',
        },
        chat: {
            sidebarLabel: 'Chats',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Ihr AI Rechts- und Compliance-Berater – basierend auf Ihren Mieterdaten und Ihren installierten Regulierungspaketen.',
            personaSelectorLabel: 'Persona',
            personaHeading: 'Wählen Sie eine Person',
            startChatButton: 'Starten Sie einen neuen Chat',
            startingChat: 'Chat wird gestartet…',
            recentHeading: 'Aktuelle Chats',
            recentEmpty: 'Noch keine Chats – starten Sie oben einen.',
            untitledChat: 'Neuer Chat',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Gespräch',
            detailSubtitle: 'Persona: {persona}',
            composeHeading: 'Senden Sie eine Nachricht',
            composeLabel: 'Nachricht Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persona',
            presetPersona: 'Voreingestellte Persona',
            changePersona: 'Persona ändern',
            hidePersonaOptions: 'Personaoptionen ausblenden',
            starterHeading: 'Beginnen Sie mit einer dieser Aktionen',
            renameButton: 'Umbenennen',
            renamePrompt: 'Chat umbenennen',
            deleteButton: 'Löschen',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chat nicht gefunden.',
            emptyChat: 'Noch keine Nachrichten – sagen Sie „Hallo“, um ein Gespräch zu beginnen.',
            roleUser: 'Du',
            roleAssistant: 'Forseti',
            roleSystem: 'System',
            thinkingLabel: 'Denken',
            toolCallPlaceholder: 'Werkzeugaufruf:',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Vorgeschlagene Dokumentänderungen (V{version}).',
            disclaimer: 'Forseti unterstützt Ihr Urteil – es ersetzt keinen zugelassenen Anwalt.',
            sendButton: 'Senden',
            sending: 'Senden…',
            composePlaceholder: 'Fragen Sie Forseti…',
            composingPlaceholder: 'Forseti antwortet…',
            streamingPlaceholder: 'Forseti tippt…',
            promptInjectionTitle: 'Forseti ließ einige Kontrolltoken fallen.',
            promptInjectionBody: 'Entfernte Muster: {patterns}. Forseti sieht nur Ihren Nachrichtentext.',
            rateLimitTitle: 'Sie senden Nachrichten zu schnell.',
            rateLimitRetryAfter: 'Versuchen Sie es in {seconds}s noch einmal.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti ist hier unsicher.',
            counselReviewBody: 'Lassen Sie sich von einem zugelassenen Anwalt beraten, bevor Sie dieser Antwort folgen.',
            legalConclusionRefused: 'Forseti weigerte sich, ohne Begründung eine rechtliche Schlussfolgerung zu ziehen. Versuchen Sie es zuerst mit search_regulatory oder eskalieren Sie es an einen Anwalt.',
            toolCallDone: 'Erledigt.',
            toolCallArgsLabel: 'Argumente',
            toolCallResultLabel: 'Ergebnis',
            toolResultLogged: 'Tool-Ergebnis im Audit-Protokoll erfasst.',
            toolResultAria: 'Werkzeugergebnis',
            documentEditAria: 'Dokument bearbeiten',
            createTaskFromMessage: 'Erstellen Sie daraus eine Aufgabe',
            createTaskHeading: 'Erstellen Sie eine Aufgabe aus dieser Nachricht',
            createTaskTitleLabel: 'Titel',
            createTaskTitleRequired: 'Titel ist erforderlich.',
            createTaskDescriptionLabel: 'Beschreibung',
            createTaskAssigneeLabel: 'Bevollmächtigter',
            createTaskAssigneePlaceholder: 'Beauftragten auswählen…',
            createTaskAssigneeNone: '– nicht zugewiesen –',
            createTaskDueDateLabel: 'Fälligkeitsdatum',
            createTaskWorkflowLabel: 'Verknüpfter Workflow (optional)',
            createTaskWorkflowPlaceholder: 'Wählen Sie einen Workflow aus…',
            createTaskWorkflowNone: '– keine –',
            renameModalSubmit: 'Umbenennen',
            renameModalSaving: 'Sparen…',
            deleteConfirmTitle: 'Chat löschen?',
            createTaskSubmit: 'Aufgabe erstellen',
            createTaskSubmitting: 'Erstellen…',
            cancelButton: 'Abbrechen',
            autoRouteHintMessage: 'Diese Frage liegt eher im Steuerhaus von {persona}.',
            autoRouteSwitchButton: 'Wechseln Sie zu {persona}',
            autoRouteSwitching: 'Wechseln…',
            handoffSuggestionMessage: 'Oder eröffnen Sie einen neuen Chat mit {persona} und tragen Sie diese Frage weiter.',
            handoffSuggestionButton: 'Neuen Chat starten mit {persona}',
            handoffSuggestionWorking: 'Eröffnung…',
            handoffSuggestionDismiss: 'Nicht jetzt',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Compliance-Spezialist',
                'forseti-legal': 'Rechtsberater',
                'forseti-dpo': 'DSB-Berater',
                'forseti-risk': 'Risikoanalyst',
                'forseti-auditor': 'Auditorenperspektive',
            },
            exportButton: 'PDF exportieren',
            exportRunning: 'Exportiert…',
            exportSuccess: 'Exportiert: {filename} (Datei-Browser öffnen).',
            exportOpenFile: 'Im Datei-Browser öffnen',
            exportDownload: 'PDF herunterladen',
            citation: {
                verifyBadge: '[VERIFIZIEREN]',
                verifyAria: 'Zitat konnte nicht überprüft werden – überprüfen Sie es, bevor Sie sich darauf verlassen',
                popoverAria: 'Quelle für {label}',
                openSource: 'Open-Source-Seite',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-generiertes Ereignis aus dem Forseti-Prüfpfad.',
        },
        rowAction: {
            ask: 'Fragen Sie Forseti',
            tooltipOpen: 'Fragen Sie Forseti danach {recordType}',
            tooltipLocked: 'Dieses {recordType} ist gesperrt – Forseti kann Fragen beantworten, aber keine Änderungen vornehmen.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti – über diese Platte',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Zusammenfassung',
            suggestedPersona: 'Vorgeschlagene Person',
            askForseti: 'Fragen Sie Forseti',
            runWorkflow: 'Führen Sie einen Workflow aus',
            viewLineage: 'Audit-Herkunft anzeigen',
            createPdplEvidencePack: 'PDPL-Nachweispaket erstellen',
            prepareBreachDraft: 'Benachrichtigungsentwurf vorbereiten',
            primaryActions: {
                projectSummary: 'Projektdokument zusammenfassen',
                supplementSummary: 'Ergänzungsanfrage zusammenfassen',
                authoritySummary: 'Behördenanfrage zusammenfassen',
                rightsRequestResponse: 'DSR-Antwort entwerfen',
                dpiaQuickReview: 'DPIA-Schnellprüfung ausführen',
                taskControlWalkthrough: 'Kontrollnachweise durchgehen',
                vendorRiskNarrative: 'Lieferantenrisiko-Narrativ erstellen',
                incidentLetter: 'PDPL-Verstoßbrief entwerfen',
                ropaSufficiency: 'RoPA-Vollständigkeitsprüfung ausführen',
                vendorDpaReview: 'Lieferanten-DPA-Prüfung ausführen',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti erstellt nur Entwurfsinhalte für das verknüpfte Mẫu số 08. Es entscheidet nicht über die Meldepflicht und reicht oder sendet das Formular nicht ein.',
            breachDraftReady: 'Mẫu số 08-Entwurf ist zur Prüfung bereit',
            breachDraftReviewBeforeSelecting: 'Vor der Auswahl prüfen',
            workflowHeading: 'Relevante Workflows',
            workflowLoading: 'Relevante Workflows werden geladen…',
            workflowEmpty: 'Für diesen Datensatz sind keine relevanten Workflows verfügbar.',
            workflowError: 'Relevante Workflows konnten nicht geladen werden.',
            selectWorkflow: '{title} auswählen',
            workflowSelected: 'Workflow ausgewählt. Der eingebettete Runner öffnet sich im nächsten Schritt.',
            workflowRunSelected: 'Ausgewählten Workflow ausführen',
            workflowStarting: 'Workflow wird gestartet…',
            workflowRunError: 'Dieser Workflow konnte nicht gestartet werden.',
            workflowCreateGuided: 'Geführten Workflow für diesen Datensatz erstellen',
            lineageHeading: 'Audit-Verlauf für diesen Datensatz',
            lineageLoading: 'Audit-Verlauf wird geladen…',
            lineageEmpty: 'Für diesen Datensatz sind keine Forseti-Verlaufsereignisse erfasst.',
            lineageError: 'Audit-Verlauf konnte nicht geladen werden.',
            retry: 'Wiederholen',
            chatContextLoading: 'Datensatzkontext wird geladen…',
            chatGuidance: {
                general: 'Forseti kann diesen Datensatz zusammenfassen, fehlende Informationen erkennen und Prüfschritte vorschlagen.',
                privacy: 'Forseti kann Datenschutzangaben prüfen, Pflichten aus installierten Regelwerken abgleichen und einen Entwurf zur menschlichen Prüfung vorbereiten.',
                compliance: 'Forseti kann Compliance-Lücken erkennen, Anforderungen mit installierten Quellen belegen und verantwortliche nächste Schritte vorschlagen.',
                legal: 'Forseti kann den Datensatz zusammenfassen, belegte Quellen auffinden und einen Entwurf zur anwaltlichen Prüfung vorbereiten.',
                risk: 'Forseti kann dokumentierte Risiken bewerten, fehlende Angaben kennzeichnen und angemessene Prüfschritte vorschlagen.',
                audit: 'Forseti kann Nachweislücken untersuchen, den Datensatz installierten Kontrollen zuordnen und auditfähige Folgemaßnahmen vorschlagen.',
            },
            starterPrompts: {
                identifyGaps: 'Welche Informationen oder Nachweise fehlen in diesem Datensatz?',
                reviewFrameworks: 'Welche Pflichten aus unseren installierten Regelwerken sollte ich für diesen Datensatz prüfen?',
                prepareDraft: 'Helfen Sie mir, einen Entwurf ausschließlich auf Grundlage dieses Datensatzes und belegter installierter Quellen zu erstellen.',
                nextSteps: 'Welche Prüfschritte sollte ich als Nächstes durchführen?',
                summarizeRecord: 'Fassen Sie diesen Datensatz zusammen und kennzeichnen Sie Punkte, die menschlich geprüft werden müssen.',
                assessRisk: 'Bewerten Sie die dokumentierten Risiken, erkennen Sie fehlende Angaben und schlagen Sie Prüfschritte vor.',
            },
            disclaimer: 'Forseti unterstützt Ihr Urteil – es ersetzt keinen zugelassenen Anwalt.',
            recordType: {
                project: 'Projekt',
                dpia: 'DPIA',
                dossier_filing: 'Dossiereinreichung',
                transfer_assessment: 'Grenzüberschreitende Übertragung',
                vendor: 'Anbieter',
                vendor_contract: 'Lieferantenvertrag',
                vendor_acceptance: 'Akzeptanz des Lieferantenrisikos',
                incident: 'Vorfall',
                incident_op: 'Einsatz bei Vorfällen',
                breach_notification: 'Benachrichtigung über Verstöße',
                authority_request: 'Anfrage der Behörde',
                supplement_request: 'Ergänzungsanfrage',
                rights_request: 'Rechteanfrage',
                contract: 'Vertrag',
                task: 'Aufgabe',
                data_mapping_activity: 'RoPA-Aktivität',
                compliance_rule: 'Compliance-Regel',
                program: 'Programm',
                governance_document: 'Governance-Dokument',
                remediation_plan: 'Sanierungsplan',
                cross_border_filing: 'Grenzüberschreitende Einreichung',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Bitten Sie Forseti, dies zu verfassen',
            drafting: 'Ausarbeitung…',
            hint_pick_activity: 'Wählen Sie eine verknüpfte Verarbeitungsaktivität aus, um Forseti Drafting zu aktivieren.',
            draft_origin_label: 'Forseti-Entwurf – Überprüfung pro Bereich',
            tokens_label: '≈ {count} Token aus Ihrem monatlichen Bucket',
            accept_selected: 'Ausgewählte akzeptieren',
            reject_all: 'Entwurf ablehnen',
            field_checkbox_label: 'Forseti-Entwurf für {field} anwenden',
            field_labels: {
                description: 'Beschreibung',
                purpose: 'Zweck',
                data_subjects: 'Betroffene Personen',
                data_types: 'Datentypen',
                necessity_justification: 'Notwendigkeit und Verhältnismäßigkeit',
                consultation_notes: 'Beratungshinweise',
                dpo_opinion: 'DPO Meinung',
                system_ids: 'Vernetzte Systeme',
                vendor_ids: 'Verlinkte Anbieter',
            },
            error_generic: 'Forseti konnte dieses DPIA nicht entwerfen. Versuchen Sie es erneut oder verfeinern Sie den Aktivitätsdatensatz.',
            variant_legend: 'Rahmenrahmen',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Kein installiertes Framework verfügbar',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Achtung: {count} Framework-übergreifende Konflikte gelten für diese Variante. Überprüfen Sie es, bevor Sie es akzeptieren.',
        },
        formDraft: {
            button_label: 'Forseti bitten, dieses Formular zu entwerfen',
            drafting: 'Entwurf wird erstellt…',
            draft_origin_label: 'Forseti-Entwurf — jedes Feld prüfen',
            tokens_label: '≈ {count} Tokens aus Ihrem Monatskontingent',
            accept_selected: 'Auswahl übernehmen',
            reject_all: 'Entwurf verwerfen',
            field_checkbox_label: 'Forseti-Entwurf für {field} übernehmen',
            excluded_note: '{count} Feld(er) können nicht entworfen werden (Verweise, Anhänge oder schreibgeschützt).',
            error_generic: 'Forseti konnte dieses Formular nicht entwerfen. Bitte erneut versuchen.',
            empty_draft: 'Forseti hat keine Feldentwürfe für dieses Formular erstellt.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Bitten Sie Forseti, diese TIA zu entwerfen',
            drafting: 'Ausarbeitung der TIA…',
            hint_pick_destination: 'Legen Sie das Zielland im Übertragungsdatensatz fest, bevor Forseti einen Entwurf erstellen kann.',
            draft_origin_label: 'Forseti TIA-Entwurf – Überprüfung pro Bereich',
            tokens_label: '≈ {count} Token aus Ihrem monatlichen Bucket',
            accept_selected: 'Ausgewählte akzeptieren',
            reject_all: 'Entwurf ablehnen',
            field_checkbox_label: 'Forseti-Entwurf für {field} anwenden',
            error_generic: 'Forseti konnte diese TIA nicht entwerfen. Versuchen Sie es erneut oder verfeinern Sie den Übertragungsdatensatz.',
            usage_details: 'KI-Nutzungsdetails',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Empfohlene Datensatzaktualisierungen',
            mechanism_recommendation: 'Übermittlungsmechanismus: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Legen Sie eine Überprüfungsfrist fest, bevor Sie diese sensible Übermittlung aktualisieren.',
            processing_activity_warning: 'Verknüpfen Sie mindestens eine Verarbeitungstätigkeit / einen ROPA-Datensatz, bevor Sie die TIA abschließen.',
            go_to_field: 'Zum Feld springen',
            adequacy_label: 'Zielregime',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Übergangs-/EWR-Abkürzung',
            adequacy_status_unknown: 'Unbekannt – Ziel festlegen',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Achtung: {count} Framework-übergreifende Konflikte gelten für dieses Übertragungsregime. Überprüfen Sie es, bevor Sie es akzeptieren.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Fehlende erforderliche Angaben',
            missing_facts_hint: 'Forseti hat Angaben markiert, die nicht im Datensatz enthalten sind. Davon abhängige Felder sind nicht vorausgewählt — ergänzen Sie die Angaben oder akzeptieren Sie das Feld bewusst.',
            risk_label: 'Kalibriertes Risiko',
            risk_low: 'Niedrig',
            risk_medium: 'Mittel',
            risk_high: 'Hoch',
            jurisdiction_label: 'Auslöser der Rechtsordnung',
            jurisdiction_none_installed: 'Kein installiertes Regelwerk deckt diese Bewertung ab — es wurde keine Rechtsordnung bewertet. Installieren Sie ein passendes Regulierungspaket, bevor Sie sich auf die rechtlichen Schlussfolgerungen dieses Entwurfs stützen.',
            jurisdiction_none_assessed: 'Forseti hat keine Bewertung der Rechtsordnung zurückgegeben. Es wurde nichts bewertet — das ist kein Befund, dass keine Rechtsordnung gilt.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Namen der bewerteten Rechtsordnungen werden geladen…',
            trigger_applicable: 'Anwendbar',
            trigger_not_applicable: 'Nicht ausgelöst',
            trigger_unknown: 'Unbekannt — Angabe fehlt',
            recipient_role_label: 'Rolle des Empfängers',
            recipient_role_controller: 'Verantwortlicher',
            recipient_role_processor: 'Auftragsverarbeiter',
            recipient_role_joint_controller: 'Gemeinsam Verantwortlicher',
            recipient_role_unknown: 'Unbekannt — nicht angegeben',
            opt_in_hint: 'Rechtliche Schlussfolgerung — nur auswählen, wenn Sie sie mit den vorliegenden Fakten akzeptieren.',
        },
        mcp: {
            pageTitle: 'MCP-Anschlussschlüssel',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Neuen Schlüssel ausstellen',
            issueModalTitle: 'Geben Sie den MCP-Schlüssel API aus',
            labelField: 'Etikett',
            labelHint: 'Eine kurze Beschreibung (z. B. „AI Capability Lab – Regulierungsforschung“).',
            permittedToolsField: 'Zugelassene Werkzeuge',
            expiresAtField: 'Läuft ab (optional)',
            expiresAtHint: 'Lassen Sie das Feld leer, wenn es niemals abläuft.',
            submit: 'Ausgabeschlüssel',
            cancel: 'Abbrechen',
            keyDisplayedOnce: 'Kopieren Sie diesen Schlüssel jetzt – Sie können ihn NICHT noch einmal sehen. Speichern Sie es in Ihrem Secret Manager (1Password, AWS Secrets Manager, Vault usw.).',
            keyDisplayedOnceClose: 'Ich habe es kopiert – nah dran',
            colLabel: 'Etikett',
            colPrefix: 'Präfix',
            colStatus: 'Status',
            colTools: 'Werkzeuge',
            colCreatedAt: 'Erstellt',
            colLastUsed: 'Zuletzt verwendet',
            colCalls: 'Anrufe',
            colActions: 'Aktionen',
            statusActive: 'Aktiv',
            statusRevoked: 'Widerrufen',
            statusExpired: 'Abgelaufen',
            revokeAction: 'Widerrufen',
            rotateAction: 'Drehen',
            confirmRevokeTitle: 'Diesen MCP-Schlüssel widerrufen?',
            confirmRevokeBody: 'Externe Claude-Clients, die diesen Schlüssel verwenden, verlieren sofort den Zugriff. Dies kann nicht rückgängig gemacht werden.',
            confirmRevoke: 'Schlüssel widerrufen',
            rotatedTitle: 'Schlüssel gedreht',
            rotatedBody: 'Der neue Klartextschlüssel ist unten. Der alte Schlüssel funktioniert noch 5 Minuten lang, sodass alle Anrufe während des Flugs beendet werden.',
            emptyState: 'Es wurden noch keine MCP-Schlüssel ausgegeben.',
            errorIssue: 'Der MCP-Schlüssel konnte nicht ausgegeben werden.',
            errorList: 'MCP-Schlüssel konnten nicht geladen werden.',
            errorRevoke: 'Der Schlüssel konnte nicht widerrufen werden.',
            errorRotate: 'Der Schlüssel konnte nicht gedreht werden.',
            neverUsed: '—',
            neverExpires: 'Niemals',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Forseti-Erinnerung',
            pageSubtitle: 'Durchsuchen und kuratieren Sie den organisationsspezifischen Kontext, den Forseti in Gesprächen und Entscheidungen gesammelt hat. Kuratierte Einträge sind vor nächtlicher Aufbewahrung geschützt und bleiben für jede Person verfügbar.',
            filterCategory: 'Kategorie',
            filterAllCategories: 'Alle Kategorien',
            totalCount: '{count} Einträge',
            colAnchor: 'Anker',
            colCategory: 'Kategorie',
            colSource: 'Quelle',
            colContent: 'Inhalt',
            colCreatedAt: 'Erstellt',
            colActions: 'Aktionen',
            editButton: 'Bearbeiten',
            deleteButton: 'Löschen',
            promoteButton: 'Bewerben Sie sich als kuratiert',
            emptyState: 'Noch keine Erinnerungen – Forseti sammelt beim Chatten Kontext.',
            categories: {
                framework_interpretation: 'Rahmeninterpretation',
                process_preference: 'Prozesspräferenz',
                escalation_threshold: 'Eskalationsschwelle',
                decision_context: 'Entscheidungskontext',
                glossary_term: 'Glossarbegriff',
                auto_inferred: 'Automatisch abgeleitet',
            },
            sources: {
                admin_curated: 'Kuratiert',
                user_explicit: 'Explizit',
                auto: 'Abgeleitet',
            },
            deleteConfirmTitle: 'Diesen Speicher löschen?',
            deleteConfirmBody: 'Durch das Löschen wird dieser Anker aus jedem zukünftigen Forseti-Chat-Boot entfernt. Anker: {anchor}',
            deleteConfirmLabel: 'Löschen',
            deleteCancelLabel: 'Abbrechen',
            editModalTitle: 'Speicher bearbeiten',
            editAnchorLabel: 'Anker',
            editCategoryLabel: 'Kategorie',
            editContentLabel: 'Inhalt',
            editContentCount: '{n}/{max} Zeichen',
            editEmptyError: 'Der Inhalt darf nicht leer sein.',
            editTooLongError: 'Der Inhalt muss ≤1000 Zeichen lang sein.',
            editAutoPromoteHint: 'Durch das Speichern dieses automatisch abgeleiteten Speichers wird dieser als vom Benutzer bestätigt markiert und aus dem Aufbewahrungs-Sweep entfernt.',
            editCancelButton: 'Abbrechen',
            editSaveButton: 'Speichern',
            editSaving: 'Sparen…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Audit-Abstammung',
            subtitle: 'Verfolgen Sie jedes von AI generierte Artefakt, das an einen Datensatz angehängt ist. Chats, Tool-Aufrufe, Aufgaben, Beweisentwürfe und Dokumentversionen.',
            filterForm: 'Filter',
            recordType: 'Datensatztyp',
            recordId: 'Datensatz-ID',
            dateStart: 'Von',
            dateEnd: 'Zu',
            search: 'Suchen',
            loading: 'Laden…',
            exportPdf: 'Exportieren PDF',
            exportCsv: 'Exportieren CSV',
            nodesHeading: 'Abstammungsknoten',
            colType: 'Typ',
            colLabel: 'Etikett',
            colOccurredAt: 'Geschehen um',
            colActor: 'Schauspieler',
            colId: 'Ausweis',
            noResults: 'Keine Berateraktivität für diesen Datensatz.',
            draftBadge: 'AI-generierter Entwurf',
            prev: 'Zurück',
            next: 'Als nächstes',
            viewToggleTable: 'Als Tabelle anzeigen',
            viewToggleGraph: 'Als Diagramm anzeigen',
            graphAriaLabel: 'Abstammungsdiagramm (visuell). Nutzen Sie die Tabellenansicht für unterstützende Technik.',
            graphLegendDraft: 'Beweisentwurf AI.',
            graphEmpty: 'Keine Knoten zum Diagramm.',
            paginationAriaLabel: 'Paginierung',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Export fehlgeschlagen: {message}',
            truncatedNotice: 'Diese Herkunftskette ist nicht vollständig. Die Plattform liest aus jeder Quelle höchstens eine feste Anzahl von Zeilen, und das Limit wurde erreicht für: {sources}. Zeilen jenseits eines Limits, falls vorhanden, werden nicht angezeigt. Grenzen Sie den Datumsbereich ein, um sie einzubeziehen.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Projekt',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Anbieter',
                incident: 'Vorfall',
                incident_op: 'Einsatz bei Vorfällen',
                rights_request: 'Rechteanfrage',
                contract: 'Vertrag',
                task: 'Aufgabe',
                data_mapping_activity: 'Datenzuordnungsaktivität',
                compliance_rule: 'Compliance-Regel',
                program: 'Programm',
            },
            nodeTypes: {
                chat: 'Chatten',
                tool_call: 'Werkzeugaufruf',
                workflow_run: 'Workflow-Ausführung',
                task: 'Aufgabe',
                evidence: 'Beweise',
                document_version: 'Dokumentversion',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'Okay',
                drifted: 'Driftete',
                hash_mismatch: 'Hash-Nichtübereinstimmung',
                merkle_mismatch: 'Merkle-Nichtübereinstimmung',
                source_missing: 'Quelle fehlt',
                not_found: 'Nicht gefunden',
                chain_unreachable: 'Kette nicht erreichbar',
                service_unreachable: 'Dienst nicht erreichbar',
                pending: 'Bis zur Verankerung',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI-generierter ENTWURF',
            pendingReviewBadge: 'Ausstehende Überprüfung',
            approvedBadge: 'Genehmigt',
            rejectedBadge: 'Abgelehnt',
            moveToReviewButton: 'Zur Überprüfung als bereit markieren',
            approveButton: 'Genehmigen',
            rejectButton: 'Ablehnen',
            rejectReasonLabel: 'Ablehnungsgrund',
            humanSignoffBanner: 'Forseti unterstützt Ihr Urteil – es ersetzt keinen zugelassenen Anwalt. Von AI generierte Beweise erfordern die Zustimmung eines Menschen, bevor sie als formelle Beweise gelten.',
            pageTitle: 'AI Beweisentwürfe',
            pageSubtitle: 'Überprüfen, genehmigen oder lehnen Sie von AI generierte Beweise ab. Entwürfe müssen von einem Menschen genehmigt werden, bevor sie als formelle Beweise gelten.',
            filterAll: 'Alles umsetzbar',
            filterDrafts: 'Nur Entwürfe',
            filterPending: 'Ausstehende Überprüfung',
            filterApproved: 'Kürzlich genehmigt',
            filterRejected: 'Kürzlich abgelehnt',
            colId: 'Beweis-ID',
            colStatus: 'Status',
            colSource: 'Quelle',
            colCreatedAt: 'Erstellt',
            colActions: 'Aktionen',
            empty: 'Keine AI Beweisentwürfe in diesem Status.',
            confirmApprove: 'Diesen von AI generierten Beweis als formell genehmigen?',
            confirmReject: 'Lehnen Sie diesen Beweisentwurf ab. Dies kann nicht rückgängig gemacht werden.',
            reasonRequired: 'Es ist ein Ablehnungsgrund erforderlich.',
            rejectModalTitle: 'Beweisentwurf ablehnen',
            approveModalTitle: 'Beweisentwürfe genehmigen',
            operationFailed: 'Der Vorgang ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
            moveToReviewSuccess: 'Als zur Überprüfung bereit markiert.',
            approveSuccess: 'Genehmigt.',
            rejectSuccess: 'Abgelehnt.',
            sourceLabelWorkflowRunTitle: 'Arbeitsablauf: {title}',
            sourceLabelWorkflowRunId: 'Workflow-Ausführung {id}',
            sourceLabelChatTitle: 'Chat: {title}',
            sourceLabelChatId: 'Forseti-Chat {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Filterstatus',
        },
        workflows: {
            pageTitle: 'KI-Workflows',
            pageSubtitle: 'Gespeicherte KI-Prompt-Vorlagen, die Aufgaben und Nachweisentwürfe erzeugen.',
            newWorkflow: 'Neuer Workflow',
            newWorkflowHeading: 'Neuer KI-Workflow',
            tabAll: 'Alle',
            tabBuiltin: 'Integriertes Paket',
            tabMine: 'Meine Workflows',
            tabDetail: 'Einzelheiten',
            tabLinkedTasks: 'Verknüpfte Aufgaben',
            builtinBadge: 'Integriert',
            builtinSubtitle: 'Integrierter Workflow.',
            userSubtitle: 'Benutzerdefinierter Workflow.',
            emptyHint: 'Noch keine Workflows.',
            loadTimeoutError: 'Workflows konnten nach 10 Sekunden nicht geladen werden. Erneut versuchen oder im Netzwerk-Tab des Browsers nach einer hängenden Anfrage suchen.',
            loadError: 'Workflows konnten nicht geladen werden.',
            hideError: 'Ausblenden fehlgeschlagen.',
            unhideError: 'Einblenden fehlgeschlagen.',
            retry: 'Wiederholen',
            producesTasks: 'Erzeugt Aufgaben',
            producesEvidence: 'Erzeugt Nachweisentwürfe',
            hideButton: 'Aus meiner Liste ausblenden',
            unhideButton: 'Einblenden',
            runButton: 'Ausführen',
            editButton: 'Bearbeiten',
            shareButton: 'Teilen',
            backToList: 'Zurück zur Liste',
            requireTitleAndPrompt: 'Titel und Prompt sind erforderlich.',
            fieldTitle: 'Titel',
            fieldDescription: 'Beschreibung',
            fieldType: 'Typ',
            fieldPersona: 'Persona-Affinität',
            fieldPractice: 'Praxis',
            fieldPrompt: 'Eingabeaufforderung (Markdown)',
            fieldProducesTasks: 'Erzeugt Aufgaben',
            fieldProducesEvidence: 'Erzeugt Nachweisentwürfe',
            fieldProjectId: 'Projekt (optional)',
            fieldAttachmentIds: 'Anhänge',
            fieldParentRecord: 'Übergeordneter Datensatz (optional)',
            fieldPersonaOverride: 'Persona-Überschreibung (optional)',
            typeAssistant: 'Assistent',
            typeTabular: 'Tabellarisch',
            personaNone: '— keine —',
            parentNone: '— keine —',
            parentIdPlaceholder: 'ID des übergeordneten Datensatzes',
            selectProjectPlaceholder: 'Projekt auswählen…',
            selectProjectFirst: 'Wählen Sie ein Projekt, um Anhänge auszuwählen.',
            selectAttachmentsPlaceholder: 'Dokumente auswählen…',
            noAttachmentsInProject: 'Dieses Projekt hat keine angehängten Dokumente.',
            selectParentRecordPlaceholder: 'Datensatz auswählen…',
            selectParentRecordFirst: 'Wählen Sie zuerst einen Datensatztyp.',
            noRecordsForType: 'Keine Datensätze dieses Typs verfügbar.',
            shareUserSelectorLabel: 'Empfänger (Benutzer)',
            shareGroupSelectorLabel: 'Empfänger (Organisationsgruppen)',
            selectUsersPlaceholder: 'Benutzer auswählen…',
            selectOrgGroupsPlaceholder: 'Organisationsgruppen auswählen…',
            loadingOptions: 'Lädt…',
            loadFailed: 'Optionen konnten nicht geladen werden.',
            parentRecordTypes: {
                dpia: 'DSFA',
                vendor: 'Lieferant',
                incident: 'Vorfall',
                rights_request: 'Rechtsantrag',
                contract: 'Vertrag',
                data_mapping_activity: 'RoPA-Aktivität',
                compliance_rule: 'Compliance-Regel',
                program: 'Programm',
            },
            cancelButton: 'Abbrechen',
            closeButton: 'Schließen',
            createButton: 'Erstellen',
            creating: 'Erstellt…',
            saveButton: 'Speichern',
            saving: 'Speichert…',
            shareWorkflowHeading: 'Workflow teilen',
            shareIntro: 'Teilen: {title}',
            shareUserIds: 'Benutzer-IDs (durch Leerzeichen oder Komma getrennt)',
            shareGroupIds: 'Organisationsgruppen-IDs (durch Leerzeichen oder Komma getrennt)',
            shareAllowEdit: 'Empfängern Bearbeitung erlauben',
            shareRequireRecipient: 'Geben Sie mindestens eine Benutzer- oder Gruppen-ID an.',
            shareSuccess: 'Geteilt.',
            sharing: 'Teilt…',
            runWorkflowHeading: 'Ausführen: {title}',
            runStarting: 'Startet…',
            runStatusLabel: 'Status: {status}',
            runProducedCounts: 'Aufgaben: {tasks} · Nachweisentwürfe: {evidence}',
            runPanelHeading: 'Workflow-Lauf',
            runPanelHeadingWithTitle: 'Workflow-Lauf: {title}',
            runViewTasks: 'Erstellte Aufgaben anzeigen',
            runViewEvidence: 'Beleg-Entwürfe anzeigen',
            runResultHeading: 'Ergebnis des Beraters',
            runNoResult: 'Der Lauf wurde beendet, hat aber kein prüfbares Ergebnis geliefert. Es wurden keine Aufgaben, Belege oder Anhänge erstellt. Wiederholen Sie den Vorgang mit dem erforderlichen Datensatz oder den ausgewählten Dokumenten.',
            runCitationWarning: 'Einige Zitate konnten nicht automatisch überprüft werden. Prüfen Sie sie, bevor Sie sich auf dieses Ergebnis verlassen.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Der Workflow-Lauf konnte nicht geladen werden.',
            runStreamFailed: 'Die Verbindung zum Workflow-Fortschritt ist fehlgeschlagen.',
            runStatusValues: {
                pending: 'Ausstehend',
                running: 'Läuft',
                completed: 'Abgeschlossen',
                succeeded: 'Erfolgreich',
                failed: 'Fehlgeschlagen',
                cancelled: 'Abgebrochen',
            },
            runProgressStatus: 'Status: {status}',
            runProgressTaskCreated: 'Aufgabe erstellt.',
            runProgressEvidenceDrafted: 'Nachweisentwurf erstellt.',
            runProgressAttachmentGenerated: 'Anlage erstellt.',
            guidedWorkflowTitle: 'Geführter Workflow für {recordType}',
            guidedWorkflowDescription: 'Für Datensätze vom Typ {recordType} erstellt. Prüfen und bearbeiten Sie diesen Workflow vor dem Speichern.',
            guidedWorkflowPrompt: 'Prüfen Sie den bereitgestellten Datensatz vom Typ {recordType}, erkennen Sie fehlende Angaben und schlagen Sie belegte nächste Schritte zur menschlichen Prüfung vor.',
            promptHeading: 'Prompt',
            howItWorksHeading: 'Was passiert, wenn Sie diesen Workflow ausführen',
            builtinGuidance: 'Forseti prüft die von Ihnen ausgewählten Datensätze und Dokumente, liefert ein vom Bediener prüfbares Ergebnis und erstellt Entwurfsaufgaben oder Belege nur, wenn das Ergebnis dies unterstützt. Nichts wird automatisch genehmigt oder eingereicht.',
            editWorkflowHeading: 'Workflow bearbeiten',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Workflow nicht gefunden.',
            schedules: {
                tabTitle: 'Zeitpläne',
                sectionHeading: 'Geplante Läufe',
                empty: 'Noch keine Zeitpläne. Erstellen Sie einen, um diesen Workflow automatisch auszuführen.',
                addButton: 'Zeitplan hinzufügen',
                addHeading: 'Workflow planen',
                editHeading: 'Zeitplan bearbeiten',
                fieldFrequency: 'Häufigkeit',
                fieldDayOfWeek: 'Wochentag',
                fieldDayOfMonth: 'Tag des Monats',
                fieldTime: 'Uhrzeit (UTC)',
                fieldTimezone: 'Zeitzone',
                fieldProject: 'Projekt (optional)',
                fieldAttachments: 'Standard-Anhänge',
                fieldPersonaOverride: 'Persona-Überschreibung (optional)',
                frequencyDaily: 'Täglich',
                frequencyWeekly: 'Wöchentlich',
                frequencyMonthly: 'Monatlich',
                weekdaySun: 'Sonntag',
                weekdayMon: 'Montag',
                weekdayTue: 'Dienstag',
                weekdayWed: 'Mittwoch',
                weekdayThu: 'Donnerstag',
                weekdayFri: 'Freitag',
                weekdaySat: 'Samstag',
                statusActive: 'Aktiv',
                statusPaused: 'Pausiert',
                nextRunLabel: 'Nächster Lauf',
                lastRunLabel: 'Letzter Lauf',
                pauseButton: 'Pausieren',
                resumeButton: 'Fortsetzen',
                deleteButton: 'Löschen',
                triggerButton: 'Jetzt ausführen',
                savingButton: 'Speichert…',
                saveButton: 'Speichern',
                triggered: 'Lauf gestartet — Lauf-Verlauf prüfen.',
            },
            branching: {
                helpHeading: 'Bedingte Prompt-Syntax',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Frameworkübergreifende Konflikte',
            pageSubtitle: 'Kuratierter Katalog der Orte, an denen Ihre installierten Regulierungspakete auf widersprüchliche Weise interagieren. Forseti-Oberflächen; Der Mensch entscheidet.',
            noInstalledPacks: 'Für diese Organisation sind keine Regulierungspakete installiert. Installieren Sie mindestens zwei Pakete, um Konflikte zwischen Frameworks aufzudecken.',
            noConflicts: 'Keine Konflikte entsprechen den aktuellen Filtern.',
            allAcknowledged: 'Jeder sichtbare Konflikt wurde anerkannt. Schöne Arbeit.',
            columnSeverity: 'Schweregrad',
            columnTitle: 'Titel',
            columnFrameworks: 'Rahmenwerke',
            columnTopic: 'Thema',
            columnStatus: 'Status',
            filterFrameworkPlaceholder: 'Alle Frameworks',
            filterTopicPlaceholder: 'Alle Themen',
            filterSeverityPlaceholder: 'Alle Schweregrade',
            filterStatusPlaceholder: 'Alle Status',
            filterStatusAcknowledged: 'Anerkannt',
            filterStatusUnacknowledged: 'Unbestätigt',
            severityCritical: 'Kritisch',
            severityHigh: 'Hoch',
            severityMedium: 'Mittel',
            severityLow: 'Niedrig',
            statusAcknowledged: 'Anerkannt',
            statusInReview: 'Im Rückblick',
            statusResolved: 'Gelöst',
            statusWontFix: 'Lässt sich nicht beheben',
            statusUnacknowledged: 'Unbestätigt',
            drawerSummaryHeading: 'Zusammenfassung',
            drawerSideAHeading: 'Seite A',
            drawerSideBHeading: 'Seite B',
            drawerRequirementsLabel: 'Anforderungen zitiert',
            drawerProvisionsLabel: 'Zitierte Bestimmungen',
            drawerSuggestedResolutionHeading: 'Vorgeschlagene Lösung',
            drawerLegalCounselBadge: 'Prüfung durch einen Rechtsbeistand erforderlich',
            drawerAckHeading: 'Danksagung',
            drawerAckStatusLabel: 'Status',
            drawerAckAssigneeLabel: 'Zugewiesen an',
            drawerAckDueDateLabel: 'Fälligkeitsdatum',
            drawerAckNotesLabel: 'Notizen',
            drawerAckButtonAcknowledge: 'Bestätigen',
            drawerAckButtonUpdate: 'Aktualisieren',
            drawerAckButtonUnacknowledge: 'Nicht bestätigen',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Titel oder Zusammenfassung suchen…',
            searchButton: 'Suchen',
            searchButtonAriaLabel: 'Führen Sie eine Konfliktsuche durch',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Export fehlgeschlagen: {message}',
            exportAriaLabel: 'Exportkonflikte',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Tabellarische Reviews',
            pageSubtitle: 'Vergleichen Sie N Dokumente über M Attribute mit fundierten Zitaten.',
            newReview: 'Neuer Review',
            newReviewHeading: 'Neuer tabellarischer Review',
            emptyHint: 'Noch keine tabellarischen Reviews.',
            docsLabel: '{n} Dokumente',
            colsLabel: '{n} Spalten',
            detailSubtitle: '{docs} Dokumente · {cols} Spalten',
            addColumn: 'Spalte hinzufügen',
            addColumnHeading: 'Spalte hinzufügen',
            columnInfo: 'Prompt & Format anzeigen',
            columnInfoAria: 'Prompt und Format für Spalte {name} anzeigen',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Rückgabeformat',
            columnInfoPresets: 'Zulässige Werte',
            columnInfoCitations: 'Zitate',
            columnInfoCitationsRequired: 'Erforderlich',
            removeColumn: 'Spalte entfernen',
            removeColumnAria: 'Spalte {name} entfernen',
            removeColumnConfirm: 'Spalte "{name}" entfernen? Ihre extrahierten Zellen werden gelöscht.',
            deleteReview: 'Löschen',
            deleteReviewAria: 'Überprüfung {title} löschen',
            deleteReviewConfirm: 'Überprüfung "{title}" löschen? Dadurch werden ihre Spalten, Zellen und Diskussionen dauerhaft entfernt.',
            runAll: 'Alle ausführen',
            running: 'Läuft…',
            cancel: 'Abbrechen',
            exportXlsx: 'Excel exportieren',
            exportCsv: 'CSV exportieren',
            exporting: 'Exportiert…',
            backToList: 'Zurück zur Liste',
            notFound: 'Tabellarischer Review nicht gefunden.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Hängen Sie Dokumente an, um den Review zu starten.',
            noColumns: 'Fügen Sie Spalten hinzu, um zu definieren, was extrahiert werden soll.',
            tableAriaLabel: 'Extraktionsraster',
            threadsCountAria: '{n} Diskussionsbeitrag/-beiträge',
            startDiscussionAria: 'Diskussion starten',
            docColumn: 'Dokument',
            statusPending: 'Ausstehend',
            statusRunning: 'Läuft…',
            statusFailed: 'Fehlgeschlagen: {msg}',
            statusCancelled: 'Abgebrochen',
            citationsCount: '{n} Zitat(e)',
            citationsView: 'Quellenstellen anzeigen',
            citationPage: 'Seite {n}',
            titleRequired: 'Titel ist erforderlich.',
            fieldTitle: 'Titel',
            fieldProjectId: 'Projekt (optional)',
            fieldAttachmentIds: 'Anhänge',
            fieldColumnName: 'Spaltenname',
            fieldColumnNameHelp: 'Ein kurzes, aussagekräftiges Label für diese Spalte im Extraktionsraster.',
            columnNamePlaceholder: 'z. B. Vertragsbeginn',
            fieldExtractionPrompt: 'Extraktions-Prompt',
            fieldExtractionPromptHelp: 'Beschreiben Sie die Informationen, die Sie extrahieren möchten. Formulieren Sie präzise, um bessere Ergebnisse zu erzielen.',
            columnPromptPlaceholder: 'Wonach soll Forseti in den Dokumenten suchen?',
            fieldReturnFormat: 'Rückgabeformat',
            fieldReturnFormatHelp: 'Steuert, wie die KI ihre Antwort formatiert (z. B. als Datum oder Zahl).',
            fieldPresets: 'Erlaubte Werte (einer pro Zeile)',
            fieldPresetsPlaceholder: 'ja\nnein\nteilweise',
            fieldCitationsRequired: 'Zitat erforderlich',
            formatShortText: 'Kurzer Text',
            formatLongText: 'Langer Text',
            formatEnum: 'Aufzählung',
            formatNumber: 'Zahl',
            formatDate: 'Datum',
            formatList: 'Liste',
            columnRequireFields: 'Name und Prompt sind erforderlich.',
            addColumnError: 'Spalte hinzufügen fehlgeschlagen.',
            cancelButton: 'Abbrechen',
            createButton: 'Erstellen',
            creating: 'Erstellt…',
            addButton: 'Hinzufügen',
            adding: 'Fügt hinzu…',
            runConfirmHeading: 'Extraktion ausführen',
            estimateBody: 'Geschätzt ~{units} abrechenbare Einheiten ({confidence} Konfidenz).',
            starting: 'Startet…',
            confirmRun: 'Lauf bestätigen',
            charts: {
                title: 'Spalten-Diagramme',
                tabsAria: 'Diagramm-Spalten',
                enumAria: 'Verteilung der Enum-Werte',
                numberAria: 'Histogramm der Zahlenverteilung',
                dateAria: 'Datums-Zeitachse',
                enumEmpty: 'Keine abgeschlossenen Zellen.',
                numberEmpty: 'Keine numerischen Werte.',
                numberExcluded: '{n} Zelle(n) ausgeschlossen (leer / nicht-numerisch / nicht abgeschlossen).',
                dateEmpty: 'Keine Datumswerte.',
                dateExcluded: '{n} Zelle(n) ausgeschlossen.',
                unsupportedFormat: 'Nicht unterstütztes Spaltenformat.',
            },
            threads: {
                drawerTitle: 'Diskussion',
                loading: 'Lädt…',
                empty: 'Noch keine Beiträge. Starten Sie das Gespräch.',
                composerLabel: 'Neuer Kommentar',
                composerPlaceholder: 'Kommentar hinzufügen…',
                post: 'Senden',
                posting: 'Sendet…',
                edit: 'Bearbeiten',
                editLabel: 'Beitrag bearbeiten',
                delete: 'Löschen',
                save: 'Speichern',
                cancel: 'Abbrechen',
                close: 'Schließen',
                editedBadge: 'bearbeitet',
                loadError: 'Thread konnte nicht geladen werden',
                postError: 'Senden fehlgeschlagen',
                editError: 'Bearbeiten fehlgeschlagen',
                deleteError: 'Löschen fehlgeschlagen',
            },
            compare: {
                pageTitle: 'Reviews vergleichen',
                leftReviewLabel: 'Linker Review',
                rightReviewLabel: 'Rechter Review',
                pickReview: 'Wählen Sie einen Review zum Vergleich',
                noSharedDocuments: 'Diese Reviews teilen keine Dokumente — der Vergleich erfordert mindestens einen gemeinsamen Anhang.',
                sharedDocumentsHeading: 'Geteilte Dokumente',
                leftOnlyHeading: 'Spalten nur im linken Review',
                rightOnlyHeading: 'Spalten nur im rechten Review',
                differs: 'Abweichend',
                identical: 'Identisch',
                emptyValue: '—',
                compareButton: 'Vergleichen mit…',
                documentHeading: 'Dokument',
                errorMissingIds: 'Sowohl linke als auch rechte Review-IDs sind erforderlich.',
                errorComparisonFailed: 'Vergleich fehlgeschlagen.',
                errorLoadFailed: 'Reviews konnten nicht geladen werden.',
                pickerLoading: 'Lädt…',
                pickerNoOtherReviews: 'Keine anderen Reviews zum Vergleich verfügbar.',
                pickerCancel: 'Abbrechen',
            },
        },
        documentReview: {
            pageTitle: 'Dokumentenprüfung',
            pageSubtitle: 'Akzeptieren oder lehnen Sie die von AI vorgeschlagenen Änderungen an diesem Dokument ab.',
            sidePanelLabel: 'AI Redlines',
            sidePanelHeading: 'AI Redlines',
            noPendingEdits: 'Keine ausstehenden Änderungen.',
            pendingCount: '{count} ausstehend',
            acceptAllButton: 'Akzeptiere alles',
            rejectAllButton: 'Alles ablehnen',
            acceptAllTitle: 'Alle ausstehenden Änderungen akzeptieren?',
            acceptAllConfirm: 'Dadurch werden {count} ausstehende Änderungen akzeptiert und eine oder mehrere neue Versionen erstellt. Weitermachen?',
            editsHeading: 'Änderungen',
            editsEmpty: 'Keine Änderungen zur Überprüfung.',
            versionsHeading: 'Versionsgeschichte',
            versionsEmpty: 'Noch keine Versionen.',
            editLabel: 'Bearbeiten',
            editLabelWithId: 'Bearbeiten #{id}',
            acceptButton: 'Akzeptiere',
            rejectButton: 'Ablehnen',
            accepting: 'Akzeptieren…',
            rejecting: 'Ablehnen…',
            diffDeletedAria: 'Gelöscht',
            diffInsertedAria: 'Eingefügt',
            statusPending: 'Ausstehend',
            statusAccepted: 'Akzeptiert',
            statusRejected: 'Abgelehnt',
            versionLabel: 'Version',
            versionAriaLabel: 'Version {n}',
            versionAriaLabelCurrent: 'Version {n} ({current})',
            versionCurrentTag: 'aktuell',
            versionSourceLabel: 'Quelle',
            versionCreatedLabel: 'Erstellt',
            versionModelLabel: 'Modell',
            sourceUpload: 'Hochladen',
            sourceUserUpload: 'Benutzer-Upload',
            sourceAssistantEdit: 'Bearbeitungsassistent',
            sourceUserAccept: 'Der Benutzer hat die Bearbeitung akzeptiert',
            sourceUserReject: 'Der Benutzer hat die Bearbeitung abgelehnt',
            sourceGenerated: 'Generiert',
            extractionExtracted: 'Text extrahiert',
            extractionFailed: 'Textextraktion fehlgeschlagen',
            extractionUnsupported: 'Text konnte nicht extrahiert werden',
            extractionPages: '{n} Seiten',
            extractionWords: '{n} Wörter',
            extractionReasonScanned: 'Dies scheint ein gescanntes oder reines Bilddokument zu sein. Forseti kann es noch nicht lesen — laden Sie eine textbasierte Datei hoch oder fügen Sie den Text direkt ein.',
            extractionReasonEncrypted: 'Die Datei ist passwortgeschützt. Entfernen Sie den Schutz und laden Sie sie erneut hoch.',
            extractionReasonMalformed: 'Die Datei konnte nicht geparst werden und ist möglicherweise beschädigt.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Die Datei ist zu groß für die Verarbeitung.',
            extractionReasonTimeout: 'Die Verarbeitung hat zu lange gedauert. Versuchen Sie eine kleinere oder einfachere Datei.',
            extractionReasonUnknown: 'Es sind keine weiteren Details verfügbar.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Dokumenttext',
            textPaneTitle: 'Dokumenttext',
            textPaneVersion: 'Version {n}',
            textPaneEmpty: 'Für diese Version ist kein Text verfügbar. Gescannte PDFs und einige Dokumenttypen enthalten keinen extrahierbaren Text.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Dokumenten-Review',
            pageTitle: 'Dokumenten-Review',
            pageSubtitle: 'Dokumente in Ihrer Organisation, die KI-vorgeschlagene Bearbeitungen zur Überprüfung haben oder Forseti-basierte Versionen.',
            colDocument: 'Dokument',
            colLinkedRecord: 'Verknüpfter Datensatz',
            colPendingEdits: 'Ausstehende Bearbeitungen',
            colLastActivity: 'Letzte Aktivität',
            colOpen: 'Öffnen',
            open: 'Öffnen',
            empty: 'Noch keine Dokumente unter Forseti-Review. Dokumente kommen in diese Warteschlange, wenn Forseti während eines Chats eine Bearbeitung vorschlägt, wenn ein KI-Workflow eine Redline erzeugt oder wenn Sie ein Dokument über die Schaltfläche oben zur Überprüfung senden.',
            loading: 'Lädt…',
            loadError: 'Dokumente unter Überprüfung konnten nicht geladen werden.',
            retryButton: 'Wiederholen',
            noLinkedRecord: '—',
            pendingZero: 'Keine',
            sendForReviewButton: 'Dokument zur Überprüfung senden',
            sendForReviewTitle: 'Dokument zur Überprüfung senden',
            sendForReviewSubtitle: 'Laden Sie ein .txt-, .md-, .pdf- oder .docx-Dokument hoch (bis zu 50 MB). Es wird zur Überprüfungswarteschlange hinzugefügt und Forseti wird einen Redline-Lauf darauf ausführen.',
            sendForReviewTabUpload: 'Neues hochladen',
            sendForReviewPickerLabel: 'Anhänge suchen',
            sendForReviewPickerPlaceholder: 'Zum Suchen tippen…',
            sendForReviewPickerEmpty: 'Keine passenden Anhänge. Versuchen Sie eine andere Suche.',
            sendForReviewSubmit: 'Zur Überprüfung senden',
            sendForReviewCancel: 'Abbrechen',
            sendForReviewSubmitting: 'Sendet…',
            sendForReviewError: 'Dokument konnte nicht zur Überprüfung gesendet werden. Bitte erneut versuchen.',
            sendForReviewSuccess: 'Dokument zur Überprüfung gesendet.',
            sendForReviewAcceptedExt: 'Akzeptiert: {ext}.',
            sendForReviewMaxSize: 'Maximale Größe {mb} MB.',
        },
        chats: {
            exportError: 'Export fehlgeschlagen.',
        },
    },
    pdplEvidencePacks: {
        title: 'PDPL-Nachweispakete',
        subtitle: 'Kundengesteuerte Einreichung an MoPS A05',
        nav: { emailProviders: 'E-Mail-Anbieter' },
        providers: {
            pageTitle: 'E-Mail-Anbieter',
            pageSubtitle: 'Konfigurieren Sie den ausgehenden Mail-Anbieter Ihrer Organisation für die Übermittlung von PDPL-Nachweispaketen an MoPS A05.',
            architectureRuleHeading: 'Architekturregel (gesperrt)',
            architectureRuleBody: 'AesirX ist kein Absender für Nachweis-E-Mails. Jede Übermittlung nutzt einen Anbieter, der Ihrer Organisation gehört und von ihr kontrolliert wird. AesirX versendet niemals in Ihrem Namen.',
            empty: 'Bislang sind keine Anbieter konfiguriert.',
            createCta: 'Anbieter anlegen',
            statusPendingConsent: 'Zustimmung ausstehend',
            statusPendingConnectionCheck: 'Verbindungstest ausstehend',
            statusActive: 'Aktiv',
            statusConnectionCheckFailed: 'Verbindungstest fehlgeschlagen',
            statusDisabled: 'Deaktiviert',
            acceptConsentCta: 'Zustimmung erteilen',
            runConnectionCheckCta: 'Verbindungstest ausführen',
            disableCta: 'Deaktivieren',
            disableConfirm: 'Beim Deaktivieren dieses Anbieters werden Direktsendungen gestoppt, bis ein neuer Anbieter konfiguriert und die Zustimmung erneut bestätigt wurde. Fortfahren?',
            columnDisplayName: 'Name',
            columnFromAddress: 'Absenderadresse',
            columnStatus: 'Status',
            columnLastCheck: 'Letzter Test',
            columnActions: 'Aktionen',
            createModalTitle: 'Neuer SMTP-Anbieter',
            createDisplayNameLabel: 'Anzeigename',
            createFromAddressLabel: 'Absenderadresse',
            createReplyToLabel: 'Antwortadresse (optional)',
            createSmtpHostLabel: 'SMTP-Server',
            createSmtpPortLabel: 'SMTP-Port',
            createSmtpUsernameLabel: 'SMTP-Benutzername',
            createSmtpPasswordLabel: 'SMTP-Passwort',
            createSmtpEncryptionLabel: 'Verschlüsselung',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implizit)',
            createSmtpEncryptionNone: 'Keine',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Bald verfügbar',
            createOauthDisabledHint: 'OAuth-Anbieter sind noch nicht aktiviert. Bitte verwenden Sie vorerst SMTP.',
            createSubmitCta: 'Anbieter anlegen',
            createCancelCta: 'Abbrechen',
            createErrorInvalidFromAddress: 'Die Absenderadresse muss ein gültiges Postfach sein.',
            createErrorDuplicateFromAddress: 'Es ist bereits ein anderer Anbieter mit dieser Absenderadresse eingerichtet.',
            createErrorGeneric: 'Der Anbieter konnte nicht angelegt werden. Bitte Formular prüfen und erneut versuchen.',
            consentModalTitle: 'Architekturregel bestätigen',
            consentLocaleToggleEn: 'Englisch',
            consentLocaleToggleVi: 'Vietnamesisch',
            consentAcknowledgeCta: 'Ich bestätige — Zustimmung erteilen',
            consentCancelCta: 'Abbrechen',
            consentErrorMismatch: 'Der Zustimmungstext wurde geändert und stimmt nicht mehr mit dem kanonischen Wortlaut überein. Bitte den Dialog erneut öffnen und einreichen.',
            testModalTitle: 'Verbindungstest',
            testRunningCta: 'Verbindung wird getestet…',
            testRunCta: 'Verbindungstest ausführen',
            testRetryCta: 'Verbindungstest wiederholen',
            testSuccessHeading: 'Verbindung erfolgreich',
            testSuccessBody: 'Authentifizierung gegen {host}:{port} in {latencyMs} ms. Der Anbieter ist bereit für Direktsendungen.',
            testFailureHeading: 'Verbindung fehlgeschlagen',
            testFailureBody: 'Der Test konnte nicht abgeschlossen werden. Bitte die Ursache unten prüfen und nach Behebung erneut versuchen.',
            testFailureReasonLabel: 'Fehlerursache',
            testCloseCta: 'Schließen',
            detailDrawerTitle: 'Anbieterdetails',
            detailDrawerHistoryHeading: 'Verlauf der Verbindungstests',
            detailDrawerConsentHeading: 'Zustimmungserklärung',
            detailDrawerNoHistory: 'Bislang keine Verbindungstests erfasst.',
            detailDrawerNoConsent: 'Zustimmung wurde noch nicht bestätigt.',
            outcomeSuccess: 'Erfolgreich',
            outcomeFailure: 'Fehlgeschlagen',
        },
        builder: {
            pageTitle: 'Nachweispaket zusammenstellen',
            pageSubtitle: 'Laden Sie die 6 Teil-Dateien hoch, wählen Sie den Pakettyp und stellen Sie das deterministische 8-teilige ZIP für die Einreichung zusammen.',
            packTypeLabel: 'Pakettyp',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Teil 01 — Anschreiben (PDF)',
            part02AuthorityForm: 'Teil 02 — Behördenformular (PDF)',
            part03Supporting: 'Teil 03 — Unterstützende Nachweise (≥1)',
            part04Safeguards: 'Teil 04 — Schutzmaßnahmen',
            part04SafeguardsRequiredOtia: '(für OTIA erforderlich)',
            part05PriorCorrespondence: 'Teil 05 — Frühere Korrespondenz (optional)',
            part06Signature: 'Teil 06 — Unterschriften- und Stempelnachweis (≥1)',
            uploadPickFile: 'Datei wählen',
            uploadPickFiles: 'Dateien wählen',
            uploadRemoveFile: 'Entfernen',
            requiredMissing: 'Erforderlicher Teil fehlt',
            assembleCta: 'Paket zusammenstellen',
            assembling: 'Stelle zusammen…',
            assembleSuccessHeading: 'Paket zusammengestellt',
            assembleSuccessSummary: '{fileCount} Dateien · {bytes} Byte · SHA-256 {shaPrefix}…',
            composeNextCta: 'E-Mail-Entwurf verfassen',
            warningsHeading: 'Validierungswarnungen',
            assembleError: 'Das Paket konnte nicht zusammengestellt werden. Bitte Eingaben prüfen und erneut versuchen.',
            forsetiAssistHeading: 'Forseti KI-Unterstützung (optional)',
            forsetiAssistHint: 'Führen Sie den PDPL Evidence Pack Assembly Advisor aus, um fehlende Teile aufzuzeigen, Anschreibensprosa zu entwerfen und Schutzlücken zu kennzeichnen. AesirX ist nicht der Absender — die KI berät nur; Sie genehmigen jedes Artefakt.',
            forsetiAssistCta: 'Forseti Workflow-Bibliothek öffnen',
            forsetiAiRunIdLabel: 'Verknüpfte Forseti KI-Lauf-ID (einfügen, um KI-Unterstützung zu erfassen)',
        },
        draft: {
            pageTitle: 'E-Mail-Entwurf',
            pageSubtitle: 'Verfassen und bearbeiten Sie den E-Mail-Entwurf. Direktversand folgt in einer späteren Version.',
            composeCta: 'Entwurf verfassen',
            composing: 'Verfasse…',
            noDraftYet: 'Noch kein Entwurf. Verfassen Sie einen, um zu beginnen.',
            localeToggleLabel: 'Sprache',
            localeEn: 'Englisch',
            localeVi: 'Vietnamesisch',
            subjectLabel: 'Betreff',
            bodyLabel: 'Inhalt',
            saveCta: 'Änderungen speichern',
            saving: 'Speichere…',
            saveError: 'Der Entwurf konnte nicht gespeichert werden. Bitte erneut versuchen.',
            manifestSummaryLabel: 'Manifest-Zusammenfassung',
            coverLetterLabel: 'Anschreiben (PDF)',
            coverLetterDownloadCta: 'PDF herunterladen',
            editHistoryHeading: 'Bearbeitungsverlauf',
            editHistoryEmpty: 'Noch keine Bearbeitungen.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 Hinweis zum inoffiziellen Kanal',
            uploadSentProofCta: 'Manuellen Versandnachweis hochladen',
            versionLabel: 'Version',
            statusDraft: 'Entwurf',
            statusEdited: 'Bearbeitet',
            statusFinalized: 'Abgeschlossen',
        },
        sentProof: {
            modalTitle: 'Manuellen Versandnachweis hochladen',
            architectureLockNote: 'AesirX ist nicht der Absender. Dieser Nachweis erfasst den manuellen Versand, den Sie über Ihr eigenes Mailprogramm durchgeführt haben.',
            fileLabel: 'Nachweisdatei (.eml, .png, .jpg)',
            envelopeFromLabel: 'Von',
            envelopeToLabel: 'An',
            envelopeSubjectLabel: 'Betreff (beim Versand)',
            envelopeSentAtLabel: 'Versandzeit',
            envelopeAttachmentShaLabel: 'Anhang SHA-256 (beim Versand)',
            uploadCta: 'Nachweis hochladen',
            uploading: 'Lade hoch…',
            uploadSuccess: 'Versandnachweis hochgeladen.',
            uploadErrorInvalidMime: 'Dateityp nicht erlaubt. Bitte .eml, .png, .jpg oder .jpeg verwenden.',
            uploadErrorGeneric: 'Der Nachweis konnte nicht hochgeladen werden. Bitte erneut versuchen.',
            existingProofsHeading: 'Erfasste Versandnachweise',
            existingProofsEmpty: 'Noch keine Versandnachweise erfasst.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Nachweispaket',
            notFoundMessage: 'Paket nicht gefunden – es wurde möglicherweise entfernt oder der Link ist falsch.',
            manifestHeading: 'Manifest',
            dossierLabel: 'Vorgang',
            packShaLabel: 'Paket-SHA-256',
            fileCountLabel: 'Dateien',
            bytesLabel: 'Größe',
            composeDraftCta: 'Entwurf verfassen',
            sendComingSoonCta: 'Direktversand: bald verfügbar',
            sendComingSoonHint: 'Der Direktversand wird aktiv entwickelt. Nutzen Sie bis dahin „Manuellen Versandnachweis hochladen" auf der Entwurfsseite.',
            sendCta: 'Über konfigurierten Anbieter versenden',
            sendDisabledNoProviderHint: 'Konfigurieren Sie zunächst einen kundenseitig betriebenen E-Mail-Anbieter. AesirX versendet niemals in Ihrem Namen.',
            timelineHeading: 'Versandhistorie',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Nachweispaket versenden',
            providerPickerLabel: 'Konfigurierter E-Mail-Anbieter',
            providerPickerHint: 'AesirX versendet niemals in Ihrem Namen. Wählen Sie unten einen Ihrer konfigurierten Anbieter aus.',
            providerPickerEmpty: 'Es sind keine aktiven Anbieter konfiguriert. Konfigurieren Sie zunächst einen auf der Seite E-Mail-Anbieter, bevor Sie versenden.',
            providerPickerEmptyCta: 'E-Mail-Anbieter öffnen',
            draftPickerLabel: 'E-Mail-Entwurf',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Für dieses Paket wurden noch keine Entwürfe verfasst. Verfassen Sie zunächst einen auf der Entwurfsseite.',
            recipientToLabel: 'An',
            recipientToHint: 'Eine oder mehrere E-Mail-Adressen, durch Kommas getrennt.',
            recipientCcLabel: 'Cc (komma-getrennt)',
            recipientCcHint: 'Optional. Kommagetrennte Liste von E-Mail-Adressen.',
            recipientBccLabel: 'Bcc (komma-getrennt)',
            recipientBccHint: 'Optional. Kommagetrennte Liste von E-Mail-Adressen.',
            consentReaffirmHeading: 'Einwilligung zum Anbieter erneut bestätigen',
            submitCta: 'Jetzt versenden',
            sending: 'Versand läuft…',
            cancelCta: 'Abbrechen',
            sendSuccess: 'Versand protokolliert. Das Paket wurde über Ihren konfigurierten Anbieter übermittelt.',
            sendErrorGeneric: 'Das Paket konnte nicht versendet werden. Bitte prüfen Sie den nachstehenden Fehler und versuchen Sie es erneut.',
            sendErrorAlreadyFinalized: 'Dieser Entwurf wurde bereits für einen erfolgreichen Versand verwendet. Verfassen Sie einen neuen Entwurf, bevor Sie erneut versenden.',
            sendErrorInvalidRecipients: 'Eine oder mehrere Empfängeradressen sind keine gültigen E-Mail-Postfächer.',
            // Timeline
            timelineEmpty: 'Bisher keine Versendungen protokolliert. Nutzen Sie oben „Über konfigurierten Anbieter versenden", um über Ihren kundenseitig betriebenen SMTP- / Microsoft 365- / Google Workspace-Anbieter zu übermitteln.',
            timelineProviderLabel: 'Über Ihren konfigurierten Anbieter versendet',
            timelineSentAtLabel: 'Versendet',
            timelineRecipientLabel: 'Empfänger',
            timelineOutcomeSent: 'Versendet',
            timelineOutcomeFailed: 'Fehlgeschlagen',
            timelineViewDetailsCta: 'Details anzeigen',
            // Authority-response chips
            authorityChipNone: 'Antwort ausstehend',
            authorityChipAcknowledgement: 'Eingangsbestätigung',
            authorityChipAcceptance: 'Annahme',
            authorityChipRejection: 'Ablehnung',
            authorityChipSupplementRequest: 'Aufforderung zur Ergänzung',
            authorityChipNoResponse: 'Keine Rückmeldung',
            // Hard-copy chips
            hardCopyChipNone: 'Keine postalische Nachreichung',
            hardCopyChipPending: 'Verschickt — Zustellung ausstehend',
            hardCopyChipDelivered: 'Zugestellt',
            // Drawer
            drawerTitle: 'Versandeintrag',
            drawerEnvelopeHeading: 'Umschlag',
            drawerEnvelopeFromLabel: 'Von',
            drawerEnvelopeToLabel: 'An',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Bcc',
            drawerEnvelopeSubjectLabel: 'Betreff',
            drawerEnvelopeMessageIdLabel: 'Nachrichten-ID',
            drawerOutcomeHeading: 'Ergebnis',
            drawerSnapshotHeading: 'Momentaufnahme der Beweismittelkette',
            drawerSnapshotPackShaLabel: 'Paket-SHA-256 zum Versandzeitpunkt',
            drawerSnapshotDraftVersionLabel: 'Entwurfsversion zum Versandzeitpunkt',
            drawerSnapshotAttachmentLabel: 'Übermittelte ZIP',
            drawerSnapshotAttachmentDownloadCta: 'ZIP herunterladen',
            drawerAuthorityResponseHeading: 'Behördenantwort',
            drawerHardCopyHeading: 'Postalische Nachreichung',
            drawerSentByLabel: 'Versendet von',
            drawerSendIdLabel: 'Versandeintrags-ID',
            drawerSmtpResponseLabel: 'SMTP-Antwort',
            drawerFailureReasonLabel: 'Fehlerursache',
            drawerLatencyLabel: 'Latenz',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Akzeptiert',
            drawerRejectedLabel: 'Abgelehnt',
            drawerCloseCta: 'Schließen',
            // Authority-response panel
            authorityRecordCta: 'Antwort erfassen',
            authorityReclassifyCta: 'Neu klassifizieren',
            authorityClassificationLabel: 'Klassifizierung',
            authorityReceivedAtLabel: 'Eingegangen am',
            authorityFreeTextLabel: 'Anmerkungen (optional)',
            authorityEvidenceLabel: 'Nachweisdatei (.eml / .pdf / .png / .jpg — optional)',
            authorityReasonLabel: 'Begründung der Neuklassifizierung',
            authorityHistoryHeading: 'Verlauf der Neuklassifizierungen',
            authorityHistoryEmpty: 'Keine Neuklassifizierungen erfasst.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Antwort speichern',
            authoritySubmitting: 'Speichern läuft…',
            authorityErrorInvalidMime: 'Dateityp des Nachweises nicht zulässig. Verwenden Sie .eml, .pdf, .png oder .jpg.',
            authorityErrorGeneric: 'Die Antwort konnte nicht gespeichert werden. Bitte erneut versuchen.',
            authorityRecordedAtLabel: 'Erfasst am',
            authorityRecordedByLabel: 'Erfasst von',
            authorityEvidenceFilenameLabel: 'Nachweis',
            authorityEvidenceDownloadCta: 'Beweis herunterladen',
            // Hard-copy panel
            hardCopyRecordCta: 'Postalische Nachreichung protokollieren',
            hardCopyConfirmCta: 'Zustellung bestätigen',
            hardCopyEditNotesCta: 'Zustellnotizen bearbeiten',
            hardCopyPostedAtLabel: 'Verschickt am',
            hardCopyCourierLabel: 'Zustelldienst',
            hardCopyTrackingLabel: 'Sendungsnummer',
            hardCopyRecipientLabel: 'Empfänger laut Etikett',
            hardCopyReceiptLabel: 'Quittungsnachweis (.pdf / .png / .jpg — optional)',
            hardCopyReceiptDownloadCta: 'Quittung herunterladen',
            hardCopyDeliveredAtLabel: 'Zugestellt am',
            hardCopyDeliveryNotesLabel: 'Zustellnotizen (optional)',
            hardCopySubmitCta: 'Nachreichung speichern',
            hardCopySubmitting: 'Speichern läuft…',
            hardCopyErrorInvalidMime: 'Dateityp der Quittung nicht zulässig. Verwenden Sie .pdf, .png oder .jpg.',
            hardCopyErrorGeneric: 'Die Nachreichung konnte nicht gespeichert werden. Bitte erneut versuchen.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.47.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    product_identification: {
        common: {
            retry: 'Retry',
            loading: 'Loading…',
            cancel: 'Cancel',
            close: 'Close',
            no_access_title: 'No access',
            no_access_message: 'Your role cannot read this register.',
            install_direct: 'Installed Direct',
            install_reference: 'Installed for reference',
            reference_mode_body: 'The overlay that declares this register is installed for reference. Scope determinations can still be recorded and read here, and the sector watch read; no duty and no clock is run from them until the overlay is installed Direct from the Overlay Catalog.',
        },
        participation: {
            compulsory: 'Compulsory',
            voluntary: 'Voluntary',
            outside_scope: 'Outside the scope',
            compulsory_rules_disapplied: 'Compulsory rules disapplied',
            not_determined: 'Open determination',
            unclassified: 'No class declared',
        },
        scope: {
            title: 'Product Scope Determinations',
            subtitle: 'A person’s recorded reading, per product line, of whether the identification and traceability regime reaches it, with the instrument relied on and a second person’s review. This register records the reading; it never decides it.',
            page_guide: 'Record one determination per product line from the options the installed pack declares. Nothing is preselected or suggested. Recording again keeps the earlier version, and compulsory and voluntary lines are always counted separately.',
            load_failed: 'The scope determinations could not be loaded.',
            none_declared: 'No installed framework declares the scope-determination register.',
            applicability_heading: 'Who the regime reaches, as the installed pack states it',
            mandatory_scope: 'Compulsory',
            voluntary_scope: 'Voluntary',
            out_of_scope: 'Outside the scope',
            mandatory_rules_disapplied: 'Compulsory rules disapplied',
            list_status_note: 'Status of the lists',
            counts_heading: 'Product lines by class',
            filter_label: 'Class',
            filter_all: 'All classes',
            table_caption: 'Current determination per product line',
            col_product: 'Product line',
            col_class: 'Class',
            col_option: 'Determination',
            col_instrument: 'Instrument relied on',
            col_determined_by: 'Determined by',
            col_reviewed_by: 'Reviewed by',
            col_determined_on: 'Determined on',
            col_version: 'Version',
            col_actions: 'Actions',
            empty: 'No determination is recorded yet.',
            empty_filtered: 'No product line is in this class.',
            record_button: 'Record a determination',
            record_again: 'Record again',
            history_button: 'History',
            duties_button: 'Duties',
            form_title: 'Record a scope determination',
            field_product: 'Product line',
            field_framework: 'Framework',
            choose: 'Choose…',
            no_default_note: 'Nothing is preselected and nothing is suggested: the reading is yours.',
            instrument_required: 'Required for the determination chosen.',
            cited_basis_note_heading: 'The installed pack’s reading of the lists',
            reviewer_same: 'The reviewer must be a person other than the one who determined it.',
            field_required: 'This field is required.',
            save: 'Save',
            saving: 'Saving…',
            save_failed: 'The determination could not be saved.',
            saved: 'Determination recorded.',
            products_failed: 'The product lines could not be loaded.',
            no_products: 'No product line is in the product register yet.',
            conflict: 'Someone recorded a determination for this line meanwhile. Reload the register and try again.',
            history_title: 'Determination history — {product}',
            history_failed: 'The history could not be loaded.',
            status_current: 'Current',
            status_superseded: 'Superseded',
            duties_title: 'Duties for {product}',
            duties_failed: 'The duties could not be loaded.',
            duties_no_task: "No task or deadline is created here from these duties. Where the decree states a period, the requirement's own text states it.",
            duties_reference: 'Installed for reference: no duty is shown. Install the overlay Direct to run them.',
            duties_compulsory_heading: 'Compulsory',
            duties_voluntary_heading: 'Voluntary',
            duties_role_heading: 'Attached to a role',
            duties_open: 'Open determination: the scope of this line is still to be determined. This is not a finding that no duty applies.',
            duties_counts: 'Compulsory: {applies} · Applies to how you take part (voluntary): {voluntary}',
            duties_none: 'No requirement is declared.',
            col_requirement: 'Requirement',
            col_state: 'Status for this line',
            col_reference: 'Reference',
        },
        duty_state: {
            applies: 'Applies (compulsory)',
            applies_voluntarily: 'Applies to how you take part (voluntary)',
            not_raised_by_option: 'Not raised by this determination',
            open_determination: 'Open determination',
            role_attached: 'Attached to a role; read its condition',
            voluntary_practice: 'Voluntary practice',
        },
        watch: {
            title: 'Sector Watch',
            subtitle: 'What the installed pack tracks as still to be issued by an authority, with the status it records and when it must be checked again, and the clocks the pack places on authorities. Nothing here is owed by your organisation.',
            page_guide: 'Read each tracked output as the installed pack states it. A recheck date that has passed reads as recheck overdue; nothing is re-dated. The clocks on authorities run on the authority, not on your organisation.',
            load_failed: 'The sector watch could not be loaded.',
            items_heading: 'Outputs still to be issued',
            items_caption: 'Tracked outputs, as the installed pack states them',
            col_output: 'Output',
            col_status: 'Status',
            col_basis: 'Basis of the status',
            col_checked: 'Checked on',
            col_recheck: 'Recheck due',
            col_install: 'Install mode',
            status_not_issued: 'Not issued',
            status_not_retrieved: 'Named, text not retrieved',
            status_announced_operational: 'Announced operational',
            status_issued: 'Issued',
            recheck_overdue: 'Recheck overdue',
            existing_basis: 'Instruments recorded as the existing basis',
            note_label: 'Note',
            items_none: 'No installed framework tracks an output for this register.',
            clocks_heading: 'Clocks on authorities and periods not stated',
            clocks_caption: 'Records the installed pack places on authorities, or whose period it does not state',
            col_clock: 'Record',
            col_kind: 'What it means for your organisation',
            col_period: 'Period as stated',
            col_binds: 'Applies from',
            authority_clock: 'Clock on the ministry — nothing is owed by the organisation',
            set_by_instrument: 'Set by the ministry instrument or sector law',
            no_period: 'No period stated',
            period_months: '{n} months',
            period_days: '{n} days',
            period_other: '{n} ({unit})',
            not_stated: 'Not stated',
            clocks_none: 'No clock on an authority is declared.',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Fertiggestellte Berichte und Mitteilungen zum Dossier',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=de.js.map